import { createFileRoute } from "@tanstack/react-router";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";

export type ReleaseEntry = {
  id: string;
  timestamp: string;
  title: string;
  summary: string;
  userHighlights: string[];
  technicalNotes: string[];
};

type ReleaseCardEntry = ReleaseEntry & {
  entryNumber: number;
};

export const releaseEntries: ReleaseEntry[] = [
  {
    id: "rabbit-hole-edition-covers-now-show-generated-descriptions",
    timestamp: "2026-07-31T11:31:13+02:00",
    title: "Rabbit Hole now shows only the topic and summary on the manage page",
    summary:
      "Rabbit Hole now shows the backend journey-plan explanation as plain text beneath the topic heading, so learners can verify the intended topic without a separate next-topic section.",
    userHighlights: [
      "The manage page now shows only the topic and one generated summary for the whole week.",
      "The page no longer shows the separate `Nächste 7 Tage` section or next-topic input.",
    ],
    technicalNotes: [
      "Threaded the stored `journey_plan.why_this_is_interesting` into a plain-text Rabbit Hole week-summary block that sits under the topic heading on the manage page.",
      "Removed the separate `Nächste 7 Tage` section, next-topic input, and next-week save flow from the manage page.",
      "Added a static regression for the week-summary block and updated the Rabbit Hole QA docs and traceability matrices.",
    ],
  },
  {
    id: "text-details-word-preview-now-keeps-clear-space-under-the-word",
    timestamp: "2026-07-31T11:24:31+02:00",
    title: "Text-details word previews now keep clear space under the word",
    summary:
      "The vocabulary preview flyout on generated-text details now keeps more breathing room between the saved word and the translation row, so the card reads as a separate preview instead of a cramped label stack.",
    userHighlights: [
      "The tapped word now has a clearer visual gap before the translation line in the flyout.",
      "The explanation fallback still stays visible for THINKERA words when the short meaning would otherwise be too literal.",
    ],
    technicalNotes: [
      "Adjusted the saved-word flyout spacing and line-height in `GeneratedTextDetailsTextPane` so the translation block no longer collapses tightly under the title word.",
      "Kept the shared THINKERA display-meaning fallback in place for the generated-text flyout.",
      "Updated the QA notes for the text-details vocabulary preview interaction.",
    ],
  },
  {
    id: "saved-word-preview-flyout-now-sits-farther-from-the-anchor",
    timestamp: "2026-07-31T11:21:54+02:00",
    title: "Saved-word previews now sit farther from the highlighted word",
    summary:
      "The saved-word popup in text details now opens with more vertical spacing and extra top padding, so the selected token no longer feels crowded by the popup header.",
    userHighlights: [
      "The popup now clears the selected word more comfortably on desktop widths.",
      "The edition-style redesign stays intact, but the word and the card now breathe more clearly apart from each other.",
    ],
    technicalNotes: [
      "Increased the flyout offset from the anchor and added more top padding inside the saved-word card in `GeneratedTextDetailsTextPane`.",
      "Kept the immediate translation, edition-style surface, and calm control treatment unchanged.",
      "Recorded the spacing adjustment in the generated-text release log so the new placement remains tracked.",
    ],
  },
  {
    id: "saved-word-preview-flyout-now-matches-overview-editions",
    timestamp: "2026-07-31T11:17:38+02:00",
    title: "Saved-word previews now match the overview editions style",
    summary:
      "The saved-word popup in text details now uses the same paper-like framing and calmer hierarchy as the Rabbit Hole edition cards on the overview page.",
    userHighlights: [
      "The popup now feels like part of the overview edition family instead of a separate utility panel.",
      "The translation still appears immediately, but the card now uses the quieter cover-style surface and button treatment.",
    ],
    technicalNotes: [
      "Reworked the saved-word flyout in `GeneratedTextDetailsTextPane` to use a flat paper-like surface, subtle spine, and edition-style spacing inspired by the overview cards.",
      "Kept the existing immediate-translation behavior and adjusted the control styling to stay calm and compact without uppercase treatment.",
      "Recorded the redesign in the generated-text QA notes so the flyout now has an explicit overview-style visual target.",
    ],
  },
  {
    id: "overview-own-topic-now-asks-for-rabbit-hole-refinement-before-create",
    timestamp: "2026-07-31T11:16:59+0200",
    title: "The overview own-topic flow now asks for refinement before creating a Rabbit Hole",
    summary:
      "Starting a manual Rabbit Hole from the overview now pauses on ambiguous topics first, so learners can narrow the idea or keep it broad before the week is created.",
    userHighlights: [
      "The `Eigenes Thema` entry on the overview now uses the same clarification step as the dedicated Rabbit Hole planner.",
      "Ambiguous topics now offer narrower choices before the week is created, while broad topics can still be kept intentionally broad.",
    ],
    technicalNotes: [
      "Reused the Rabbit Hole refinement helper in `barkguide-ui/src/utils/rabbitHoleTopicRefinement.ts` from the overview custom-topic form in `barkguide-ui/src/routes/app/index.tsx`.",
      "Added an overview browser regression in `barkguide-ui/e2e/app-overview.spec.ts` for the custom-topic refinement path and confirmation create flow.",
      "Updated the Rabbit Hole QA docs and traceability matrices to include the overview own-topic refinement entry point.",
    ],
  },
  {
    id: "saved-word-preview-now-sits-clear-of-the-selected-word",
    timestamp: "2026-07-31T11:15:10+02:00",
    title: "Saved-word previews now sit clear of the selected word",
    summary:
      "The saved-word popup in text details now opens with a larger gap from the clicked token, so the translated word no longer visually collides with the reading text behind it.",
    userHighlights: [
      "The selected word stays readable and separate from the popup instead of appearing to overlap the panel.",
      "The translation still opens immediately, but the flyout now sits a bit farther below the clicked token.",
    ],
    technicalNotes: [
      "Increased the vertical offset for the saved-word flyout in `GeneratedTextDetailsTextPane` so the popup opens farther below the anchor element.",
      "Kept the immediate-translation behavior from the previous pass and left the pronunciation and close controls unchanged.",
      "Recorded the layout adjustment in the generated-text QA notes so the overlap risk stays tracked.",
    ],
  },
  {
    id: "thinkera-vocabulary-preview-now-shows-an-explanatory-meaning",
    timestamp: "2026-07-31T11:10:14+02:00",
    title: "THINKERA vocabulary previews now keep an explanatory meaning visible",
    summary:
      "THINKERA vocabulary cards and saved-word previews now show an explanatory meaning instead of going blank when the short same-language translation is too literal.",
    userHighlights: [
      "THINKERA vocabulary now keeps a readable meaning visible in the card and preview instead of showing an empty translation line.",
      "The direct translated word still stays available in the underlying vocabulary details for fallback and reference.",
    ],
    technicalNotes: [
      "Added a shared THINKERA display-meaning resolver on top of the existing level-aware vocabulary helpers so the card and flyout can fall back from `contextual_meaning` to `core_idea` and still avoid the literal label.",
      "Updated the vocabulary card and saved-word preview in `GeneratedTextDetailsTextPane` to use the new display resolver instead of the raw primary translation helper.",
      "Added unit regressions for the explanation fallback path and updated the THINKERA QA traceability notes.",
    ],
  },
  {
    id: "german-generated-texts-now-get-a-repair-pass",
    timestamp: "2026-07-31T11:09:07+02:00",
    title: "German generated texts now get a repair pass before publish",
    summary:
      "German generated texts are now rewritten once before they are shown, so translated-sounding or awkward grammar is corrected before the learner reads it.",
    userHighlights: [
      "German texts now get an automatic natural-language rewrite before they appear in the app.",
      "The rewritten version keeps the original meaning and topic, but reads more like native German.",
    ],
    technicalNotes: [
      "Added a German-specific rewrite pass in `barkguide/reeprise/tasks.py` before generated text is staged for playback.",
      "Tightened the German language-quality prompt so the model is told to prefer idiomatic word order, case, and verb frames.",
      "Added backend coverage for the new German repair path and the updated prompt guardrails.",
    ],
  },
  {
    id: "saved-word-preview-translation-now-shows-immediately",
    timestamp: "2026-07-31T10:52:48+02:00",
    title: "Saved-word previews now show the translation immediately",
    summary:
      "The saved-word popup in text details now shows the translation as soon as it opens, so the learner does not have to click through an extra reveal step.",
    userHighlights: [
      "Saved highlighted words now open directly to the translation instead of hiding it behind a second tap.",
      "The pronunciation button and close control still stay in place, but the meaning is visible right away.",
    ],
    technicalNotes: [
      "Removed the reveal/hide state from the saved-word flyout in `GeneratedTextDetailsTextPane` so the translation line renders immediately on open.",
      "Updated the Playwright regression for highlighted-word previews to assert that no reveal button is present and that the translation is visible on first open.",
      "Recorded the UX change in the generated-text QA notes and release log.",
    ],
  },
  {
    id: "numeric-periods-no-longer-cut-off-vocabulary-sentences",
    timestamp: "2026-07-31T10:50:47+02:00",
    title: "Numbered sentences now stay intact when saving vocabulary",
    summary:
      "A period after a number no longer makes the vocabulary capture logic stop early, so texts like `17.` keep their full sentence context when saved from the reading view.",
    userHighlights: [
      "Vocabulary saved from sentences with numbered references now keeps the full surrounding sentence instead of stopping at the first numeric period.",
      "The reading flow still highlights the same clicked word, but the stored context is no longer truncated by `17.`-style patterns.",
    ],
    technicalNotes: [
      "Adjusted the sentence-boundary scanner in the generated-text details flow to treat numeric periods as part of the sentence unless a real terminal boundary follows.",
      "Mirrored the same rule in the frontend tokenization helper so the clickable sentence spans and the saved vocabulary sentence stay aligned.",
      "Added a backend regression test covering the `17.` case and updated the QA notes for generated text vocabulary capture.",
    ],
  },
  {
    id: "number-saving-keeps-the-original-digit-surface",
    timestamp: "2026-07-31T10:49:10+02:00",
    title: "Saved numbers now keep the original digit surface",
    summary:
      "Number previews still show the spelled-out form, but saving one now stores the original clicked digits as the vocabulary word so the text can highlight it again later.",
    userHighlights: [
      "A tapped number now behaves like a normal vocabulary item after saving, including the highlight state in the reading text.",
      "The preview still shows the spoken-out form and translation before you save it.",
    ],
    technicalNotes: [
      "Changed the number-save path in `GeneratedTextDetailsTextPane` to persist the original displayed number surface instead of the written-out preview form.",
      "Added a regression helper and unit test so the saved vocabulary word stays aligned with the clicked digits rather than the spoken rendering.",
      "Updated the QA notes for the number-to-words feature to reflect the new save/highlight behavior.",
    ],
  },
  {
    id: "staff-voice-review-now-loads-real-session-rows",
    timestamp: "2026-07-30T15:57:31+02:00",
    title: "The staff voice review now loads real session rows",
    summary:
      "The voice review page now pulls its session list from the backend instead of hardcoded rows, so each new speaking session appears in the staff list with the user name, duration, and session metadata that matter for review.",
    userHighlights: [
      "Every new voice conversation now appears in the staff review list as soon as the session is created.",
      "Staff can see the user name, duration, language, and live session status for each entry.",
    ],
    technicalNotes: [
      "Added a staff-only `/reeprise/staff/voice-sessions/` backend endpoint that serializes `SpeakingSession` records with user metadata and computed duration.",
      "Switched `barkguide-ui/src/routes/app/staff_voice_review.tsx` to fetch live session rows while keeping the review-pattern and suggested-change scaffolding in place.",
      "Added backend and browser regression coverage so the live voice-session list and the staff-only guard stay stable.",
    ],
  },
  {
    id: "check-your-email-route-removed",
    timestamp: "2026-07-30T15:54:44+02:00",
    title: "The Check Your Email page has been removed",
    summary:
      "The temporary Check Your Email surface is no longer exposed in the app, so the route and sidebar entry are gone.",
    userHighlights: [
      "The Check Your Email page no longer appears in navigation.",
      "The route has been removed instead of being left as a hidden stub.",
    ],
    technicalNotes: [
      "Deleted `barkguide-ui/src/routes/app/check_your_email.tsx` and removed the matching `/app/check_your_email` route from the generated router tree.",
      "Removed the experimental sidebar entry from `barkguide-ui/src/routes/app/route.tsx` and deleted the orphaned translation keys.",
      "Updated the release notes and visibility cleanup docs so the app no longer treats the page as a live surface.",
    ],
  },
  {
    id: "maison-memoire-route-and-endpoints-removed",
    timestamp: "2026-07-30T15:40:18+02:00",
    title: "The Maison Memoire page has been removed",
    summary:
      "The Maison Memoire route, backend endpoint, and helper utilities are no longer part of the app, so the surface no longer appears in navigation or QA coverage.",
    userHighlights: [
      "Maison Memoire no longer appears as a user-facing page.",
      "The image-reading mockup endpoint and helper utilities have been removed with it.",
    ],
    technicalNotes: [
      "Deleted `barkguide-ui/src/routes/app/maison_memoire.tsx`, the related Playwright spec, and the Maison Memoire helper utilities.",
      "Removed the `/app/maison_memoire` route and the backend `reeprise/maison-memoire/generate/` endpoint wiring.",
      "Updated the QA registry, risk register, and release documentation to stop treating Maison Memoire as a live feature.",
    ],
  },
  {
    id: "staff-surface-labels-now-say-operations",
    timestamp: "2026-07-30T15:45:00+02:00",
    title: "The staff surface now says operations instead of console",
    summary:
      "The staff navigation, page title, and related back links now use Operations wording instead of Console, so the internal area reads like a working operations tool rather than a generic support console.",
    userHighlights: [
      "The staff area name is now Operations in both navigation and page title copy.",
      "The related back links and empty-state copy now use the same term for consistency.",
    ],
    technicalNotes: [
      "Updated the shared staff-console translation keys in `barkguide-ui/src/assets/translations.json`.",
      "Kept the staff-console regression and translation guard passing after the copy rename.",
      "Left the underlying route names unchanged; only the visible labels changed.",
    ],
  },
  {
    id: "staff-console-user-page-now-shows-voice-agent-usage",
    timestamp: "2026-07-30T15:41:37+02:00",
    title: "The staff console user page now shows voice agent usage",
    summary:
      "The dedicated user page now includes voice-agent time and voice-agent token totals, so staff can inspect conversational usage next to the rest of the account metrics.",
    userHighlights: [
      "Staff can see how long the selected user spent with the voice agent.",
      "Staff can see how many tokens those voice-agent turns used.",
    ],
    technicalNotes: [
      "Added a duration field to the shared speaking-turn demo data in `barkguide-ui/src/lib/staff_console.shared.ts`.",
      "Aggregated speaking-turn duration and token usage on `barkguide-ui/src/routes/app/staff_console_user/$userId.tsx` and added the matching translation keys.",
      "Updated the staff-console regression, release notes, and QA traceability entries after the detail-page update.",
    ],
  },
  {
    id: "staff-console-user-page-now-lists-active-language-spaces",
    timestamp: "2026-07-30T15:36:08+02:00",
    title: "The staff console user page now lists active language spaces",
    summary:
      "The dedicated user page now shows every active language space for the selected user, and the activity timestamp is labeled more precisely, so staff can see the full scope of that account instead of only its primary space.",
    userHighlights: [
      "Staff can see all active language spaces for the selected user in one place.",
      "The last-activity timestamp now has a clearer label, so the detail table reads more directly.",
    ],
    technicalNotes: [
      "Extended the shared staff-console user data with an `activeLanguageSpaces` list in `barkguide-ui/src/lib/staff_console.shared.ts`.",
      "Rendered the new list on `barkguide-ui/src/routes/app/staff_console_user/$userId.tsx` and renamed the activity label from `Zuletzt gesehen` to `Letzte Aktivität` / `Last activity`.",
      "Kept the staff-console regression, translation guard, and build checks passing after the detail-page update.",
    ],
  },
  {
    id: "staff-console-user-page-now-shows-monthly-token-usage",
    timestamp: "2026-07-30T15:32:44+02:00",
    title: "The staff console user page now shows monthly token usage",
    summary:
      "The dedicated user page now includes a separate `Tokens this month` metric so staff can compare the current monthly token load with the longer-running lifetime total.",
    userHighlights: [
      "Staff can see the current monthly token total for the selected user at a glance.",
      "The monthly metric sits next to the lifetime token total for easier comparison.",
    ],
    technicalNotes: [
      "Added a `tokensThisMonth` field to the shared staff-console demo users in `barkguide-ui/src/lib/staff_console.shared.ts`.",
      "Surfaced the new value on `barkguide-ui/src/routes/app/staff_console_user/$userId.tsx` with its own translation key.",
      "Kept the existing staff-console regression and build checks passing after the data and copy update.",
    ],
  },
  {
    id: "staff-console-voice-review-now-shows-duration-and-token-usage",
    timestamp: "2026-07-30T15:11:28+02:00",
    title: "The staff console voice review now shows duration and token usage",
    summary:
      "The voice-feedback review surface now shows how long each voice-agent session ran and how many tokens it used, so staff can compare conversational cost and load alongside the failure review itself.",
    userHighlights: [
      "Voice review rows now show both elapsed time and token usage per conversation.",
      "Staff can compare expensive or slow sessions while reviewing the same redacted conversation set.",
    ],
    technicalNotes: [
      "Extended the voice-feedback demo rows in `barkguide-ui/src/routes/app/staff_console.tsx` with per-conversation duration and token fields.",
      "Surfaced the new values in the voice review table and selected-conversation detail panel.",
      "Added the corresponding translation keys in `barkguide-ui/src/assets/translations.json` and kept the existing staff-console regression intact.",
    ],
  },
  {
    id: "staff-console-user-list-now-opens-a-dedicated-user-page",
    timestamp: "2026-07-30T14:57:55+02:00",
    title: "The staff console user list now opens a dedicated user page",
    summary:
      "Clicking a user in the staff console now opens a separate user detail page with the same key metrics, event history, and review notes instead of keeping the detail view trapped inside the list screen.",
    userHighlights: [
      "The user list now leads to a dedicated page for the selected user.",
      "The separate page shows the same billing and audit data in a quieter, focused layout.",
    ],
    technicalNotes: [
      "Added a dedicated `/app/staff_console_user/$userId` route that reuses the shared staff-console demo dataset for the selected user detail view.",
      "Updated the user-list open action in `barkguide-ui/src/routes/app/staff_console.tsx` to navigate to the new detail route.",
      "Extended the staff-console regression so the open action is verified as a route transition instead of an inline section toggle.",
    ],
  },
  {
    id: "staff-console-now-includes-a-voice-feedback-review-surface",
    timestamp: "2026-07-30T14:53:51+02:00",
    title: "The staff console now includes a voice feedback review surface",
    summary:
      "Staff can now inspect recurring voice-agent failures, review redacted conversation details, and compare candidate prompt changes inside the existing staff console instead of treating conversation improvement as an automatic self-training loop.",
    userHighlights: [
      "Staff now have one place to review recurring voice failures and suggested improvements.",
      "The new review surface stays inside the existing staff console and keeps the workflow manual and controlled.",
    ],
    technicalNotes: [
      "Extended `barkguide-ui/src/routes/app/staff_console.tsx` with a voice-feedback review section that shows weekly summary metrics, redacted conversation review rows, failure patterns, suggested changes, and review actions.",
      "Added new translation keys in `barkguide-ui/src/assets/translations.json` and extended the translation guardrail test to cover the new staff-console copy.",
      "Documented the new staff-console scope in the QA feature notes so the voice review flow has an explicit ownership trail.",
    ],
  },
  {
    id: "staff-console-now-uses-flat-minimal-table-surfaces",
    timestamp: "2026-07-30T14:49:16+02:00",
    title: "The staff console now uses flatter minimal table surfaces",
    summary:
      "Staff can still review the same operations data, but the console now removes the remaining card-like framing so the tables, detail view, ledger, pricing, and operations panels read as one flat scientific surface instead of a set of rounded boxes.",
    userHighlights: [
      "The staff console is easier to scan because the data now sits on a flatter surface.",
      "The remaining rounded card shells were removed from the table sections and detail panels.",
    ],
    technicalNotes: [
      "Updated `barkguide-ui/src/routes/app/staff_console.tsx` to remove the rounded container shells around the users, language, detail, ledger, pricing, and operations sections.",
      "Tightened the remaining controls so the console keeps the minimal operational look without changing the underlying data or route behavior.",
      "Kept the existing browser regression in `barkguide-ui/e2e/staff-console.spec.ts` green after the surface cleanup.",
    ],
  },
  {
    id: "staff-console-now-bundles-dashboard-user-ledger-pricing-and-ops-views",
    timestamp: "2026-07-30T12:27:24+02:00",
    title: "The staff console now opens users into a detail view",
    summary:
      "Staff now have a single in-app operations console with a minimal scientific table layout, searchable user list, language breakdown, selected-user detail view, event ledger, feature breakdown, pricing table, and operations panel so token spend, activity, generated texts, vocabulary additions, and failures can be inspected together, while test-generated accounts are clearly highlighted.",
    userHighlights: [
      "The user list now stays as the navigation surface and opens a selected user into a detail view.",
      "A language breakdown shows how many visible users are active in each language space.",
      "Each user now shows how many texts they generated and how many vocabulary items they added.",
      "The user list now shows the full visible staff dataset instead of a narrowed slice.",
      "Test-generated accounts are highlighted so software-created users are easy to spot during review.",
      "A searchable user list, per-feature breakdown, and detailed request ledger make investigation and billing review easier.",
      "The pricing and operations sections keep model cost and system health visible without opening the Django admin.",
    ],
    technicalNotes: [
      "Expanded the structured demo dataset in `barkguide-ui/src/routes/app/staff_console.tsx` so the user list now shows multiple distinct users, including explicitly test-generated accounts.",
      "Removed the standalone intro card and promoted the staff-console title into a plain page header so the surface opens directly into the working sections.",
      "Replaced the pill-style user switching with an explicit open action per table row so the selected-user summary and drill-down cards stay tied to one opened user.",
      "Derived per-user generated-text and vocabulary-item counts from the visible demo dataset and surfaced them both in the table and the detail cards.",
      "Added a highlighted test-generated badge and kept the selected-user summary as the source of truth for the drill-down cards while leaving the full user table visible.",
      "Added a sidebar entry for the staff console in `barkguide-ui/src/routes/app/route.tsx` and new translation keys for the staff-only navigation and section labels.",
      "Flattened the staff-console surfaces by removing the heavy card strokes and then removed the left navigation rail entirely so the page now reads as a single main column.",
      "Reworked the summary and detail blocks into compact tables with thin labels and reduced helper copy so the page reads more like an ordered data sheet.",
      "Added a browser regression that checks staff users can reach the console and non-staff users are redirected away from it.",
    ],
  },
  {
    id: "rabbit-hole-refresh-now-shows-a-single-loading-rectangle-inside-the-cover",
    timestamp: "2026-07-29T16:34:40+02:00",
    title: "Rabbit Hole refresh now shows a single loading rectangle inside the cover",
    summary:
      "When you ask for another Rabbit Hole idea on the overview, the recommendation card now keeps the full edition surface visible and shows one calm animated loading block inside the cover instead of adding a second placeholder panel underneath it.",
    userHighlights: [
      "The recommendation stays visible while the next idea loads.",
      "The loading state now uses one simple animated rectangle inside the cover.",
    ],
    technicalNotes: [
      "Updated the inactive Rabbit Hole recommendation branch in `barkguide-ui/src/routes/app/index.tsx` so refresh sets a local pending flag and reuses the cover's `titlePending` rendering instead of showing a separate loading block below the card.",
      "Added an E2E assertion in `barkguide-ui/e2e/app-overview.spec.ts` that checks the recommendation title loading placeholder appears immediately after regenerating.",
    ],
  },
  {
    id: "rabbit-hole-read-cards-now-stay-colored-while-inactive-cards-remain-grayscale",
    timestamp: "2026-07-29T15:37:42+02:00",
    title: "Rabbit Hole read cards now stay colored while inactive cards remain grayscale",
    summary:
      "Opened Rabbit Hole chapters now use a quieter colored cover treatment instead of the same grayscale draft look as inactive recommendations, so read and unread states are easier to tell apart at a glance.",
    userHighlights: [
      "Already opened Rabbit Hole chapters now look different from unread ones without losing the calm editorial style.",
      "Inactive recommendation cards still keep the grayscale draft treatment.",
    ],
    technicalNotes: [
      "Split the shared Rabbit Hole cover tone into active, read, and inactive states in `barkguide-ui/src/utils/rabbitHoleEditionDesign.ts`, keeping inactive cards grayscale while giving read cards a muted but still colored palette.",
      "Updated `barkguide-ui/src/components/RabbitHoleEditionCover.tsx` so the overview and weekly cover surfaces pass the read tone through the shared cover renderer, and added a regression test that checks read covers are not grayscale.",
    ],
  },
  {
    id: "generate-text-card-now-matches-overview-and-top-bar-chrome",
    timestamp: "2026-07-29T15:04:09+02:00",
    title: "The generate-text card now matches the overview and top bar chrome",
    summary:
      "The main generate-text card on each language-space page now uses a calmer framed surface and tighter control row so it reads like part of the overview and mobile top-bar system instead of a separate floating panel.",
    userHighlights: [
      "The generate card now feels like it belongs to the same page chrome as the overview and mobile top bar.",
      "The idea helper, import tools, and send action stay in place but read as smaller secondary controls.",
    ],
    technicalNotes: [
      "Reworked `barkguide-ui/src/components/GenerateTextCard.tsx` to use a quieter overview-style container, tighter action row, and mobile-friendly control grouping without changing the creation flow.",
      "Added a browser regression in `barkguide-ui/e2e/reeprise-generation.spec.ts` that checks the framed surface, idea helper behavior, and narrow-phone overflow safety.",
    ],
  },
  {
    id: "rabbit-hole-overview-now-shows-one-ai-topic-with-regenerate",
    timestamp: "2026-07-29T14:26:45+02:00",
    title: "Rabbit Hole overview now shows one AI topic with regenerate",
    summary:
      "The Rabbit Hole overview now shows a single AI-generated topic on each inactive language card and lets you regenerate it for a fresh model-backed idea, instead of rotating through premade topics.",
    userHighlights: [
      "You now see one real recommendation right away on every inactive Rabbit Hole language card.",
      "The regenerate action asks the model for a new topic instead of cycling through a fixed list.",
    ],
    technicalNotes: [
      "Switched the overview inactive-card branch in `barkguide-ui/src/routes/app/index.tsx` to render the backend-provided single suggestion and call the refresh mutation for regenerate.",
      "Removed the curated backend fallback path in `barkguide/reeprise/topic_intelligence.py` so recommendation generation stays prompt-driven instead of inventing premade topics.",
      "The compact custom-topic controls stay directly underneath the recommendation card without a separate visible `Eigenes Thema` label, so the inactive state reads like one replacement surface instead of two stacked cards.",
    ],
  },
  {
    id: "idea-helper-font-uses-the-original-display-face-again",
    timestamp: "2026-07-29T14:03:18+02:00",
    title: "The idea helper uses its original display font again",
    summary:
      "The compact `Idee` button now keeps its original font treatment instead of inheriting the heavier fallback, so the helper looks closer to the rest of the calm UI again.",
    userHighlights: [
      "The `Idee` button no longer looks unusually bold.",
      "The helper keeps the same behavior while the original font treatment is restored.",
    ],
    technicalNotes: [
      "Restored `--font-TikTok` in `barkguide-ui/src/index.css` so the existing display face is used again for the helper styling.",
      "Kept the topic-idea endpoint fix intact in `barkguide/reeprise/topic_intelligence.py`.",
    ],
  },
  {
    id: "topic-idea-endpoint-no-longer-crashes-on-similarity-check",
    timestamp: "2026-07-29T13:59:12+02:00",
    title: "The idea endpoint now returns suggestions instead of crashing on similarity checks",
    summary:
      "The compact `Idee` helper can now fetch a topic suggestion without hitting a backend type error, so the button no longer gets stuck behind a 500 response when the topic queue checks prior ideas.",
    userHighlights: [
      "Tapping `Idee` now keeps the helper responsive instead of failing in the background.",
      "The topic suggestion endpoint no longer crashes while checking for near-duplicate ideas.",
    ],
    technicalNotes: [
      "Fixed the topic-idea similarity path in `barkguide/reeprise/topic_intelligence.py` so it normalizes token collections before set intersection.",
      "Added a regression in `barkguide/reeprise/tests.py` that exercises the similarity check with normal list-based tokenization and keeps the endpoint path covered.",
    ],
  },
  {
    id: "word-card-related-examples-now-show-learning-language-first",
    timestamp: "2026-07-29T13:12:26+02:00",
    title: "Word card related examples now show the learning-language line first",
    summary:
      "Vocabulary example lines now render the sentence in the learning language first and keep the translation in parentheses, so the example reads in the same direction as the source text.",
    userHighlights: [
      "Example lines now start with the learning-language sentence.",
      "The translation stays attached in parentheses on the same line.",
    ],
    technicalNotes: [
      "Added a shared `formatBilingualExample` helper in `barkguide-ui/src/components/WordCard/wordCardSectionData.ts` and reused it across the word card, preview card, item card, and vocabulary progress surfaces.",
      "Added a regression test for the new bilingual example formatting so `target (translation)` stays stable for related examples.",
    ],
  },
  {
    id: "topic-idea-button-stays-usable-while-ideas-prefetch",
    timestamp: "2026-07-29T13:03:56+02:00",
    title: "The idea button now stays usable without a loading label",
    summary:
      "The small `Idee` button on the generate-text card now stays calm and clickable instead of showing a loading label, so it no longer feels stuck behind shared suggestion state.",
    userHighlights: [
      "You can tap `Idee` without seeing a loading label on the button.",
      "The helper now feels calm even when it fetches a suggestion on demand.",
    ],
    technicalNotes: [
      "Removed the background loading label from `barkguide-ui/src/components/GenerateTextCard.tsx` so the visible helper no longer mirrors the mutation pending state.",
      "Added a Playwright regression in `barkguide-ui/e2e/reeprise-idea-button.spec.ts` that verifies the button still fills the topic field from the returned suggestion.",
    ],
  },
  {
    id: "vocabulary-card-spacing-tightened-on-text-details",
    timestamp: "2026-07-29T12:58:42+02:00",
    title: "Vocabulary cards no longer reserve a blank band before the source sentence",
    summary:
      "The text-details vocabulary card no longer keeps a hidden sticky header in the normal flow, so the blank band before the source sentence is removed and the card reads more compactly.",
    userHighlights: [
      "The blank space above the source sentence is gone.",
      "The card keeps the same vocabulary content and tap behavior.",
    ],
    technicalNotes: [
      "Updated `barkguide-ui/src/components/WordCard/WordCardStickyHeader.tsx` so the mobile sticky header only mounts when it is actually sticky, instead of reserving blank vertical space while hidden.",
      "Kept the `VocabularyItemCard` spacing regression in place so the surrounding card layout stays compact.",
    ],
  },
  {
    id: "phrase-joining-now-retains-one-canonical-merged-entry",
    timestamp: "2026-07-29T12:21:46+02:00",
    title: "Phrase joining now keeps one canonical merged entry",
    summary:
      "When you join adjacent saved words into a phrase and then extend that phrase again, the text details view now keeps only one active merged entry instead of stacking older phrase versions on top of it.",
    userHighlights: [
      "Joining the same words again no longer leaves duplicate phrase cards behind.",
      "Merged phrases now stay represented by one live entry in the current text view.",
    ],
    technicalNotes: [
      "Changed the generated-text details route in `barkguide-ui/src/routes/app/reeprise/$generatedTextId.tsx` to treat optimistic phrase vocabularies as a keyed set and remove superseded IDs when a merge event arrives.",
      "Added a unit regression for the optimistic phrase list helper so repeated merges replace retired entries instead of appending another visible copy.",
    ],
  },
  {
    id: "rabbit-hole-details-now-sync-the-active-language-space-before-opening",
    timestamp: "2026-07-29T12:08:05+02:00",
    title: "Rabbit Hole details now sync the active language space before opening",
    summary:
      "Opening a Rabbit Hole text from the overview now writes the selected language space into app state first, so the generated-text details page shows the inline continue label in the same language space you clicked instead of a stale previous one.",
    userHighlights: [
      "Opening an English Rabbit Hole text now keeps the continue label in English.",
      "The details page no longer reuses an older language space from stale local state.",
    ],
    technicalNotes: [
      "Updated Rabbit Hole overview navigation in `barkguide-ui/src/routes/app/index.tsx` so the clicked language space is written through `useLanguageSpace()` before details navigation starts.",
      "Kept the inline continue-label localization from the earlier change, so the pending state still follows the active space after the route sync.",
    ],
  },
  {
    id: "french-generated-text-quotes-now-stay-attached-to-the-quoted-phrase",
    timestamp: "2026-07-29T12:04:49+02:00",
    title: "French generated-text quotes now stay attached to the quoted phrase",
    summary:
      "French reading text now keeps straight quote marks attached to the quoted phrase instead of letting the opening quote drift onto a new line. The visible text stays calm and readable, especially inside narrow mobile layouts.",
    userHighlights: [
      "Quoted French phrases now stay together when the text wraps.",
      "The opening quote no longer breaks away from the word that follows it.",
    ],
    technicalNotes: [
      "Normalized French quote rendering in `barkguide-ui/src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so straight quotes keep a word-joiner boundary and do not split across lines inside the generated-text details renderer.",
      "Added a unit regression that checks the French quote token keeps the quoted phrase attached in the rendered text span output.",
    ],
  },
  {
    id: "mobile-generated-text-details-now-hold-pending-streams-until-complete",
    timestamp: "2026-07-29T12:01:35+02:00",
    title: "Mobile generated-text details now keep a calm pending state before simplified streaming",
    summary:
      "Generated text details on mobile now stay calm before the first chunk arrives, then use a simplified streaming paragraph while the text is still changing and switch back to the existing interactive reader once complete. Desktop streaming behavior remains unchanged.",
    userHighlights: [
      "Phones now show a calm pending state before streaming starts.",
      "The simplified mobile paragraph stays stable while the text is still changing, and the full interactive article appears once generation finishes.",
    ],
    technicalNotes: [
      "Added a mobile-only streaming gate in `barkguide-ui/src/components/GeneratedTextDetailsTextPane.tsx` so incomplete text uses a calm pending body before the first chunk and a simplified paragraph while streaming, instead of rendering the full interactive span tree too early.",
      "Added a unit regression for the mobile streaming decision and a narrow-phone Playwright regression in `barkguide-ui/e2e/reeprise-generation.spec.ts` that checks the pending state and simplified paragraph behavior on mobile.",
    ],
  },
  {
    id: "thinkera-vocabulary-cards-now-show-explanations-instead-of-literal-translations",
    timestamp: "2026-07-29T12:00:52+02:00",
    title: "THINKERA vocabulary cards now show explanations instead of literal translations",
    summary:
      "THINKERA vocabulary cards now prefer an explanation or meaning-in-context when that is clearer than a literal same-language translation, so learners get a useful learner-facing meaning instead of a repeated word label.",
    userHighlights: [
      "THINKERA cards now lead with an explanation when the word needs one.",
      "The direct dictionary translation stays available as secondary reference.",
    ],
    technicalNotes: [
      "Added a level-aware vocabulary meaning resolver in `barkguide-ui/src/utils/vocabularyTranslationDetails.ts` so THINKERA keeps the concise learner-facing meaning separate from the longer explanation while leaving `translation_word` intact for fallback and storage.",
      "Updated `VocabularyItemCard` and `WordCard` to render the THINKERA short meaning from contextual meaning only and added unit regressions for the resolver and card output.",
    ],
  },
  {
    id: "rabbit-hole-compact-overview-headers-now-reserve-four-lines-on-narrow-desktop",
    timestamp: "2026-07-29T11:50:00+02:00",
    title: "Rabbit Hole compact overview headers now reserve room for longer titles",
    summary:
      "Long Rabbit Hole overview titles now keep their own reserved header space on narrower desktop windows, so the edition covers no longer slide up underneath the title when the browser is minimized.",
    userHighlights: [
      "Long overview titles stay readable instead of being hidden behind the edition cards.",
      "Narrower desktop windows now keep enough room for a four-line compact header.",
    ],
    technicalNotes: [
      "Expanded the shared compact Rabbit Hole header height map in `barkguide-ui/src/components/RabbitHoleEditionCover.tsx` so the overview can reserve space for four title lines on narrow desktop widths.",
      "Added an overview Playwright regression in `barkguide-ui/e2e/app-overview.spec.ts` that renders a long compact title at a minimized desktop width and verifies the edition cover starts below the header block.",
    ],
  },
  {
    id: "inline-continue-reading-now-stays-in-the-active-language-space",
    timestamp: "2026-07-29T11:35:38+02:00",
    title: "Inline continue reading now stays in the active language space",
    summary:
      "The inline Continue reading control in generated-text details now keeps both its idle label and its loading label aligned with the selected language space, so learners no longer see a mixed-language pending state while extending a text.",
    userHighlights: [
      "The inline continuation button now keeps the same language-space wording while it is loading.",
      "Italian, French, German, and English spaces all keep their own calm continue-reading copy.",
    ],
    technicalNotes: [
      "Added a language-space loading copy map in `barkguide-ui/src/constants.ts` and routed `CreateVocabularyTextInlinePreview` through a shared label helper so the idle and loading states resolve from the same active language space.",
      "Added unit coverage for the inline label resolver and a guard in `barkguide-ui/src/constants.test.ts` to ensure every enabled language space has both idle and loading copy.",
    ],
  },
  {
    id: "vocabulary-cards-now-prefer-contextual-meaning-when-it-is-clearer",
    timestamp: "2026-07-29T11:25:41+02:00",
    title: "Vocabulary cards now prefer the contextual meaning when it is clearer",
    summary:
      "Vocabulary cards now show the sentence-specific meaning when it differs from the broader standalone label, so learners do not get a misleading answer line for context-sensitive words like `presto`.",
    userHighlights: [
      "The revealed meaning now follows the sentence context when that meaning is more precise.",
      "The broader dictionary meaning still remains available in the structure details.",
    ],
    technicalNotes: [
      "Added a shared vocabulary-display resolver in `barkguide-ui/src/utils/vocabularyTranslationDetails.ts` that prefers `structure.contextual_meaning` when it differs from the standalone translation label.",
      "Updated `VocabularyItemCard` and `VocabularyPreviewCard` to use the contextual display resolver, and added regressions for the helper and rendered card output.",
    ],
  },
  {
    id: "generate-text-card-now-opens-directly-at-the-top-of-the-language-space",
    timestamp: "2026-07-28T09:57:38+02:00",
    title: "Generate-text now opens directly at the top of each language space",
    summary:
      "The Reeprise language-space page now shows the generate-text card immediately when you open a language space, so learners can start writing without first clicking a plus button or switching into a separate create view.",
    userHighlights: [
      "The generate-text card is now visible as soon as the language space page loads.",
      "You can start creating a text from the same page where the language space feed appears.",
    ],
    technicalNotes: [
      "Moved the `GenerateTextCard` render to the top of `barkguide-ui/src/routes/app/reeprise/index.tsx` so the main language-space view exposes the composer directly.",
      "Added a Playwright regression in `barkguide-ui/e2e/reeprise-generation.spec.ts` that verifies the Italian language space shows the generate-text card immediately on load.",
    ],
  },
  {
    id: "rabbit-hole-overview-removes-broken-recommendation-flow",
    timestamp: "2026-07-26T17:26:00+02:00",
    title: "Rabbit Hole overview now shows the edition preview instead of the broken recommendation flow",
    summary:
      "The Rabbit Hole overview no longer exposes the retryable recommendation branch that was failing on the `Themenvorschlag` card. It now shows the edition preview visual with an empty title and keeps the working custom-topic entry underneath.",
    userHighlights: [
      "The overview card now uses the edition preview visual instead of the broken topic recommendation panel.",
      "The `Eigenes Thema` entry remains directly underneath so learners can still start a manual week from the overview.",
    ],
    technicalNotes: [
      "Removed the overview's Rabbit Hole recommendation fetch and retry branch from `barkguide-ui/src/routes/app/index.tsx`.",
      "Kept the static preview cover and the custom-topic form so the overview still gives learners one usable entry point.",
    ],
  },
  {
    id: "rabbit-hole-structured-validation-and-explicit-failure",
    timestamp: "2026-07-26T17:03:00+02:00",
    title: "Rabbit Hole recommendations now use structured validation and explicit failure",
    summary:
      "Rabbit Hole generation now validates structured topic evidence internally, retries in bounded batches, and returns a recoverable failure instead of silently falling back to curated topics when it cannot produce valid recommendations.",
    userHighlights: [
      "Valid Rabbit Hole topics now come from a narrow six-sub-edition structure instead of the old English-question heuristic.",
      "If generation exhausts its attempt cap, the app returns an explicit recoverable error instead of an empty success payload.",
    ],
    technicalNotes: [
      "Replaced the Rabbit Hole topic validator in `barkguide/reeprise/topic_intelligence.py` with structural checks for concise noun-phrase topics, confident language mismatches, similarity, and a new six-sub-edition evidence schema.",
      "Changed Rabbit Hole generation to request structured candidate payloads, validate them programmatically, retry in two batches plus one repair batch, and raise `RabbitHoleTopicGenerationError` when the cap is exhausted.",
      "Updated `barkguide/reeprise/views.py` so the overview API returns HTTP 503 with a recoverable error message when Rabbit Hole generation fails.",
    ],
  },
  {
    id: "rabbit-hole-empty-recommendation-shows-own-topic-entry",
    timestamp: "2026-07-25T22:20:00+02:00",
    title: "Rabbit Hole no-recommendation state now shows the own-topic entry",
    summary:
      "When the overview has no recommendation to show, it now reveals the existing custom-topic input instead of leaving the learner with only retry text.",
    userHighlights: [
      "The unavailable recommendation state now shows the same own-topic entry used in the normal overview card.",
      "Learners can start a custom Rabbit Hole topic immediately when no suggestion is available.",
    ],
    technicalNotes: [
      "Reused the existing custom-topic form in the unavailable overview branch inside `barkguide-ui/src/routes/app/index.tsx`.",
      "Added an end-to-end regression that forces an empty suggestion response and checks that the own-topic input is visible.",
    ],
  },
  {
    id: "rabbit-hole-abandon-now-shows-confirmation-dialog",
    timestamp: "2026-07-25T22:12:37+02:00",
    title: "\"Thema beenden\" now asks for confirmation before ending",
    summary:
      "Ending a Rabbit Hole topic now shows a confirmation dialog so you don't accidentally lose your current weekly edition with one tap.",
    userHighlights: [
      "The \"Thema beenden und neues starten\" button now opens a confirmation dialog instead of ending the topic immediately.",
      "You can cancel or confirm the action from the dialog.",
      "The confirmation is shown from both the weekly management page and the week-complete summary view.",
    ],
    technicalNotes: [
      "Added confirmation modal overlay to the abandon action in both `ActiveWeekState` and `WeekCompleteState` in `barkguide-ui/src/routes/app/rabbit_hole.tsx`.",
      "Added new translation keys for the confirmation title, body, confirm button, cancel button, and pending state in `src/assets/translations.json`.",
      "Added new translation keys to the guarded keys list in `src/assets/translations.copy.test.ts`.",
    ],
  },
  {
    id: "rabbit-hole-retry-now-refreshes-the-visible-topic-immediately",
    timestamp: "2026-07-25T21:36:11+02:00",
    title: "Rabbit Hole retry now refreshes the visible topic immediately",
    summary:
      "Retrying an unavailable Rabbit Hole recommendation now writes the fresh suggestion back into the overview cache right away, so the German card no longer looks stuck while a replacement is already ready.",
    userHighlights: [
      "The retry button now updates the visible recommendation as soon as the new topic returns.",
      "German overview cards no longer stay on the unavailable state after a successful retry.",
    ],
    technicalNotes: [
      "Extracted the Rabbit Hole batch cache sync into a shared hook helper so a refreshed topic replaces the current language entry immediately.",
      "Kept the loading branch open while the batch refetch is in flight so the overview no longer flashes back to the unavailable state during retry.",
    ],
  },
  {
    id: "rabbit-hole-custom-topic-suggestions-now-stay-narrow-and-six-part",
    timestamp: "2026-07-25T21:30:10+02:00",
    title: "Rabbit Hole custom topics now stay narrow enough for six sub-editions",
    summary:
      "Custom Rabbit Hole topic suggestions now aim for one tightly bounded weekly category that can support exactly six concrete sub-editions instead of a broad article topic, and older overview suggestions are retired when they come from the previous prompt version.",
    userHighlights: [
      "Custom suggestions now favor a narrow weekly umbrella topic with six distinct parts.",
      "Broad themes, single-article prompts, and vague curiosity questions are rejected earlier.",
      "Old overview suggestions now expire when they were generated by the previous topic-idea contract.",
    ],
    technicalNotes: [
      "Updated the shared Rabbit Hole topic-idea structure contract in `barkguide/reeprise/topic_intelligence.py` so custom suggestions must pass a six-sub-edition category test.",
      "Bumped the Rabbit Hole suggestion generator version and expired stored overview suggestions that still used the older prompt contract.",
      "Added backend regression coverage that inspects the suggestion prompt for the new narrow-scope and distinct-sub-edition requirements.",
      "Added a regression that proves the overview path retires an old-version Rabbit Hole suggestion before serving fresh ideas.",
    ],
  },
  {
    id: "rabbit-hole-german-recommendations-accept-complete-warum-headlines",
    timestamp: "2026-07-24T16:41:39+02:00",
    title: "Rabbit Hole now accepts complete German question-style recommendations",
    summary:
      "German recommendation titles no longer fail just because they start with Warum, as long as the headline is complete and still fits the editorial topic rules.",
    userHighlights: [
      "German recommendations can now use a complete Warum headline.",
      "Valid German topics are no longer rejected just for starting with Warum.",
    ],
    technicalNotes: [
      "Removed the blanket German `Warum` rejection from Rabbit Hole topic validation so complete German question-style headlines can be delivered.",
      "Added a regression test that accepts a complete German `Warum ...` recommendation title.",
    ],
  },
  {
    id: "rabbit-hole-refresh-keeps-the-current-topic-visible",
    timestamp: "2026-07-24T16:25:20+02:00",
    title: "Rabbit Hole retry now keeps the current topic visible while it prepares a replacement",
    summary:
      "If a new recommendation cannot be generated right away, the overview keeps showing the existing topic instead of dropping the card into an empty state.",
    userHighlights: [
      "Retry no longer erases the only visible recommendation when replacement generation fails.",
      "The overview stays on the current topic until a valid replacement is ready.",
    ],
    technicalNotes: [
      "Changed the refresh action to generate a replacement before marking the current suggestion as skipped, preserving the visible card when the model fails validation.",
      "Expanded the Rabbit Hole recommendation prompt so the retry path gives the model clearer English and German title structure guidance.",
    ],
  },
  {
    id: "rabbit-hole-expired-editions-now-archive-cleanly",
    timestamp: "2026-07-24T16:22:46+02:00",
    title: "Expired Rabbit Hole editions now archive cleanly",
    summary:
      "When every chapter in a Rabbit Hole week is already in the past, the overview now shows it as an archived edition instead of a dead read-today prompt.",
    userHighlights: [
      "Finished Rabbit Hole weeks now appear as archived editions in overview.",
      "Expired weeks no longer show the current-day read CTA.",
    ],
    technicalNotes: [
      "Added a completed-week archive branch in the overview so expired Rabbit Hole weeks render a quiet archived state instead of the active read action.",
      "Suppressed the direct read CTA on completed Rabbit Hole detail views and added backend, browser, and translation regressions for the archived state.",
    ],
  },
  {
    id: "rabbit-hole-german-recommendation-retry-recovers",
    timestamp: "2026-07-24T16:03:57+02:00",
    title: "Rabbit Hole topic retry now recovers when a title is unsuitable",
    summary:
      "When a suggested topic cannot be used, retry now prepares a new valid recommendation instead of leaving the language card empty.",
    userHighlights: [
      "Retrying an unavailable topic can return a fresh recommendation for that language.",
      "Your card still keeps only one visible topic at a time.",
    ],
    technicalNotes: [
      "Added one corrective model attempt only after the initial single-topic response fails the existing language, headline, or no-repeat checks.",
      "Added a regression test for a rejected German question-style title followed by a valid German replacement.",
    ],
  },
  {
    id: "vocabulary-translation-first-response-speed",
    timestamp: "2026-07-24T15:57:48+02:00",
    title: "New vocabulary now becomes ready faster",
    summary:
      "New words now start with a compact translation response, avoiding long retry delays caused by oversized vocabulary details.",
    userHighlights: [
      "Newly saved words now reach a usable translation more quickly.",
      "Core meaning, form, and sentence context remain available immediately.",
    ],
    technicalNotes: [
      "Replaced the oversized first-pass vocabulary response with a bounded card-ready JSON payload.",
      "The response contract now reserves the token budget for essential fields instead of optional examples and long explanations.",
    ],
  },
  {
    id: "vocabulary-translation-json-contract",
    timestamp: "2026-07-24T15:23:00+02:00",
    title: "Vocabulary translations now use a reliable structured response",
    summary:
      "New words now request a strict structured translation response, preventing valid vocabulary from being left waiting after malformed AI output.",
    userHighlights: [
      "Added words now receive a dependable translation response format.",
      "Malformed formatting no longer causes repeated background retries for otherwise valid words.",
    ],
    technicalNotes: [
      "Configured the vocabulary translation request with OpenAI JSON-object response mode and an explicit system-level JSON-only instruction.",
      "Added a regression test that verifies the translation request always sends the structured-output contract.",
    ],
  },
  {
    id: "retry-translation-for-pending-vocabulary",
    timestamp: "2026-07-24T15:32:00+0200",
    title: "Stuck vocabulary translations can now be retried",
    summary:
      "Words stuck in the pending generating state now offer a retry action, matching the existing retry support for failed translations.",
    userHighlights: [
      "Words that remain stuck in the translating state now show a retry button in the word preview and trainer card.",
      "You no longer need to delete and re-add a word when its translation gets stuck.",
    ],
    technicalNotes: [
      "`SavedWordPreviewFlyout` now shows the retry button for both FAILED and PENDING vocabulary statuses instead of only FAILED.",
      "`VocabularyPreviewCard` now imports and uses `useVocabularyRetryTranslation` and renders a retry button when the vocabulary status is FAILED or PENDING, replacing the guess-reveal UI in those states.",
      "Replaced text-based PENDING detection in `VocabularyPreviewCard`'s `resolvedPrimaryTranslation` memo with a direct `vocabulary.status === \"PENDING\"` check.",
      "Removed the unused `isTranslationPending` helper from `GeneratedTextDetailsTextPane.tsx`.",
    ],
  },
  {
    id: "rabbit-hole-overview-can-explicitly-refresh-one-topic",
    timestamp: "2026-07-24T14:45:03+02:00",
    title: "Rabbit Hole overview can now refresh one topic on request",
    summary:
      "Each inactive language card keeps its recommended topic until you explicitly ask for a new one.",
    userHighlights: [
      "Your suggested topic stays in place when you return to overview.",
      "Generate new topic replaces only that language card’s unchosen suggestion.",
    ],
    technicalNotes: [
      "Added a dedicated refresh endpoint that marks the current unchosen suggestion as skipped and creates one replacement.",
      "The overview refreshes the coordinated recommendation cache after the replacement is stored.",
    ],
  },
  {
    id: "rabbit-hole-overview-now-generates-one-topic-per-language",
    timestamp: "2026-07-24T14:41:14+02:00",
    title: "Rabbit Hole overview now prepares one topic per language",
    summary:
      "The overview now prepares one Rabbit Hole recommendation for each available language space, then reuses that stored topic instead of generating a larger unused pool.",
    userHighlights: [
      "Each language card now receives one focused starting topic.",
      "Returning to overview shows the stored topic instead of generating extra alternatives.",
      "The unused ‘another recommendation’ control has been removed.",
    ],
    technicalNotes: [
      "Changed Rabbit Hole recommendation batch and single-language defaults to one candidate, and the API explicitly requests that budget.",
      "Removed the overview-only cycling state so the frontend cannot consume or display an unused local candidate pool.",
      "Added a backend regression that asserts the coordinated generator is called with one candidate per language.",
    ],
  },
  {
    id: "vocabulary-card-translation-recovery",
    timestamp: "2026-07-24T14:39:14+02:00",
    title: "Vocabulary cards now recover safely from malformed translations",
    summary:
      "Vocabulary cards no longer show fragments of a malformed AI response. Affected cards are returned to translation generation and appear again only with usable vocabulary information.",
    userHighlights: [
      "Malformed translation responses are retried instead of appearing as raw technical text on a card.",
      "When a reliable base form is unavailable, the card simply leaves that optional detail out.",
      "Previously affected cards can be safely regenerated without changing healthy cards.",
    ],
    technicalNotes: [
      "Hardened `generate_translation` parsing in `barkguide/reeprise/tasks.py` to reject malformed JSON-like output and structured payloads without a usable `translation_word`, while keeping legacy line output supported.",
      "Added `regenerate_corrupted_vocabulary_cards` to requeue only cards containing recognizable JSON/code-fence response fragments.",
      "Added defensive normalization in `src/utils/vocabularyTranslationDetails.ts` so raw structured-response fragments never render as card fields.",
    ],
  },
  {
    id: "rabbit-hole-recommendations-now-stay-personal-and-non-repeating",
    timestamp: "2026-07-24T13:25:57+02:00",
    title: "Rabbit Hole recommendations now stay personal and do not repeat",
    summary:
      "Rabbit Hole ideas now learn from the weekly topics you have actually chosen, remain distinct across language spaces, and cannot silently replace an edition already in progress.",
    userHighlights: [
      "A topic you have already chosen will not return as a new Rabbit Hole in another language space.",
      "New editions are chosen from the overview, where each available language receives its own recommendation.",
      "An active edition stays intact until you finish or explicitly end it.",
    ],
    technicalNotes: [
      "Moved repeat-premise checks to the Rabbit Hole backend so suggested and manually entered topics use the same cross-language history guard.",
      "Changed edition creation to return an explicit conflict instead of replacing an active or planning week in place.",
      "Removed client fallback recommendations from the overview path; it now waits for the persisted coordinated recommendation batch and offers a retry state when unavailable.",
    ],
  },
  {
    id: "rabbit-hole-overview-cards-now-load-from-one-coordinated-suggestion-batch",
    timestamp: "2026-07-24T11:55:30+02:00",
    title: "Rabbit Hole overview cards now load from one coordinated suggestion batch",
    summary:
      "The overview now loads inactive Rabbit Hole cards through one coordinated recommendation request, which removes the slow per-card locking path that could leave weekly cards stalled for too long on first load.",
    userHighlights: [
      "Rabbit Hole cards on the overview now appear reliably instead of waiting on several competing recommendation requests.",
      "Visible language spaces still keep distinct weekly Rabbit Hole ideas, but the overview now asks for them in one coordinated batch.",
      "The dedicated planner and reading flow stay unchanged, including stable suggestion IDs and manual-topic handling.",
    ],
    technicalNotes: [
      "Added a coordinated multi-language Rabbit Hole suggestion path in `barkguide/reeprise/topic_intelligence.py` and `views.py`, so the overview can fetch one `languages=` batch instead of triggering several overlapping per-language generators.",
      "Removed the long-lived write transaction from the Rabbit Hole `topic-suggestions` GET path so local SQLite development no longer hits the locked-database failure mode during overview loads.",
      "Updated `src/hooks/useRabbitHole.ts`, `src/routes/app/index.tsx`, and the overview Playwright mocks to consume the new `suggestions_by_language` payload while keeping the existing single-language planner endpoint intact.",
    ],
  },
  {
    id: "rabbit-hole-recommendations-now-learn-from-accepted-weekly-topics",
    timestamp: "2026-07-24T11:27:33+02:00",
    title: "Rabbit Hole recommendations now learn from accepted weekly topics",
    summary:
      "Rabbit Hole suggestions are now persisted with stable IDs, coordinated across your language spaces, and updated from the weekly topics you actually choose instead of relying mostly on generic fallback taste.",
    userHighlights: [
      "Choosing a Rabbit Hole recommendation now teaches future Rabbit Hole suggestions what kind of investigations you actually like.",
      "Manual topics stay manual, while accepted recommendations are tracked separately so the next weekly ideas can respond more precisely.",
      "Accepted Rabbit Hole ideas drop out of future recommendation pools, and visible language spaces keep their suggestion sets more distinct.",
    ],
    technicalNotes: [
      "Extended `barkguide/reeprise/views.py` and `serializer.py` so Rabbit Hole week creation accepts `topic_source` plus `topic_suggestion_id`, validates suggestion ownership/topic-language match transactionally, marks accepted suggestions, and records one `RabbitHoleTopicPreference` per week.",
      "Added `RabbitHoleTopicPreference` in `barkguide/reeprise/models.py` with migration `0102_rabbitholetopicpreference.py`, then rebuilt Rabbit Hole recommendation generation in `barkguide/reeprise/topic_intelligence.py` around persisted `TopicSuggestion` rows, cache-backed preference summaries, accepted-topic exclusions, coordinated language-space batches, and translated-premise dedupe.",
      "Updated `src/hooks/useRabbitHole.ts`, `src/routes/app/rabbit_hole.tsx`, `src/routes/app/index.tsx`, and the Rabbit Hole browser mocks so the planner and overview now pass stable suggestion IDs while reading payloads stay unchanged; added backend coverage for acceptance/rejection paths plus frontend recommendation tests and aligned the Rabbit Hole QA docs.",
    ],
  },
  {
    id: "text-details-unified-vocabulary-toggle-motion",
    timestamp: "2026-07-24T11:18:50+02:00",
    title: "Text details now use one anchored vocabulary toggle on desktop",
    summary:
      "On desktop generated-text pages, the vocabulary panel now opens and closes from one persistent control beside the reading pane, with a restrained motion that keeps the reading title stable.",
    userHighlights: [
      "One desktop control now handles both hide and show states for the vocabulary panel instead of switching between different buttons.",
      "The toggle stays in a consistent spot beside the reading pane, so reopening vocabulary feels like the same action in reverse.",
      "Closing vocabulary still recenters the reading pane, while the title and header stay at the same vertical position during the transition.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/reeprise/$generatedTextId.tsx` so the desktop toggle lives in the reading-shell layer, keeps `showVocabularyColumn` as the route-level source of truth, and exposes one shared `generated-text-vocabulary-toggle` test hook with an explicit open/closed state attribute.",
      "Changed the desktop vocabulary rail from separate control contexts to a width, opacity, and icon-rotation transition so the panel and toggle animate together without changing the mobile flow.",
      "Updated `e2e/classic-tools-nav.spec.ts` plus the generated-text details QA documentation to cover the persistent desktop toggle, centered-reading state, and the same `DTD-AC-05` / `R-003` route-transition mapping.",
    ],
  },
  {
    id: "overview-language-spaces-now-avoid-duplicate-rabbit-hole-recommendations",
    timestamp: "2026-07-24T10:12:53+02:00",
    title: "Language spaces now avoid repeating the same Rabbit Hole recommendation",
    summary:
      "On the overview, inactive Rabbit Hole cards now choose different weekly topics across language spaces instead of repeating the same fallback or first suggestion several times.",
    userHighlights: [
      "If you keep several language spaces visible, the inactive weekly cards now show different Rabbit Hole ideas instead of echoing the same one.",
      "The overview still keeps the strongest personalized suggestion first where possible, but it now skips to another option when a different card already uses that same topic.",
      "Fallback recommendations also stay more distinct across English, German, French, and Italian spaces while suggestions are still loading.",
    ],
    technicalNotes: [
      "Added `resolveDistinctOverviewRabbitHoleRecommendedTopics()` in `src/utils/rabbitHoleRecommendations.ts` so the overview assigns one non-duplicate recommendation per visible language space whenever another candidate is available.",
      "Added language-specific fallback recommendation pools and kept the shared helper tests in `src/utils/rabbitHoleRecommendations.test.ts` up to date.",
      "Extended backend `generate_rabbit_hole_week_topic_suggestions()` in `barkguide/reeprise/topic_intelligence.py` to treat other language spaces' active topics and recent Rabbit Hole suggestions as blocked prior topics for the current space.",
    ],
  },
  {
    id: "rabbit-hole-overview-fallback-topics-now-use-real-rabbit-holes",
    timestamp: "2026-07-24T10:12:53+02:00",
    title: "Inactive Rabbit Hole cards now use stronger fallback topics",
    summary:
      "When personalized Rabbit Hole suggestions are still loading or unavailable, the overview and planner now fall back to more specific curiosity-led topics instead of generic placeholders like coffee or Ancient Rome.",
    userHighlights: [
      "Inactive weekly cards now show more intriguing starting ideas even before personalized suggestions arrive.",
      "The fallback topics now match the Rabbit Hole format more closely, with clearer mysteries and stronger weekly direction.",
      "Once personalized suggestions load, they still replace the fallback cards as before.",
    ],
    technicalNotes: [
      "Replaced the frontend fallback list in `src/utils/rabbitHoleRecommendations.ts` with Rabbit Hole-style weekly topics so overview and planner placeholders stop showing broad generic subjects.",
      "Updated `_WEEK_TOPIC_CURATED_FALLBACKS` in `barkguide/reeprise/topic_intelligence.py` so backend fallback suggestions follow the same more specific editorial direction across English, German, French, and Italian.",
      "Adjusted `src/utils/rabbitHoleRecommendations.test.ts` to pin the new fallback default.",
    ],
  },
  {
    id: "rabbit-hole-load-path-reduction",
    timestamp: "2026-07-24T09:29:52+02:00",
    title: "Rabbit Hole recommendations and weekly pages now open faster",
    summary:
      "The Rabbit Hole planner now shows a real recommendation title immediately, and the weekly page no longer loads unrelated generated-text list data before it can render.",
    userHighlights: [
      "The inactive Rabbit Hole recommendation no longer waits on a blank title while topic suggestions are still loading.",
      "You can open the weekly Rabbit Hole page on mobile with less waiting because it no longer pulls the full generated-text feed just to render the week view.",
      "Pending and completed Rabbit Hole chapters still keep their automatic refresh and read-state behavior.",
    ],
    technicalNotes: [
      "Removed the unconditional `useGeneratedTextPaginatedList()` dependency from `src/routes/app/rabbit_hole.tsx` and switched the page to use the rabbit-hole week payload directly for read-state and completion checks.",
      "Added a shared `rabbitHoleRecommendations` helper so both the overview and the dedicated planner render fallback recommendation titles immediately, then replace them when personalized suggestions arrive.",
      "Slimmed the rabbit-hole week serializer in `barkguide/reeprise/serializer.py` and `barkguide/reeprise/views.py` so current/detail payloads omit `chatgpt_output` while still carrying `view_count`, `learning_progress`, and `has_content` summary state.",
      "Added regression coverage in `src/utils/rabbitHoleRecommendations.test.ts`, `e2e/rabbit-hole-planning.spec.ts`, and focused backend rabbit-hole week serializer tests.",
    ],
  },
  {
    id: "text-details-centered-reading-pane-when-vocabulary-is-closed",
    timestamp: "2026-07-24T09:26:17+02:00",
    title: "Text details now center the reading pane when vocabulary is hidden",
    summary:
      "On desktop, closing the vocabulary column on a generated text now recenters the reading pane instead of leaving an empty reserved sidebar gap.",
    userHighlights: [
      "When you hide the vocabulary column on desktop, the reading pane now sits centered in the available space.",
      "Reopening vocabulary restores the split reading-and-vocabulary view immediately without leaving the page.",
      "Mobile and narrow layouts keep the existing reading flow unchanged.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` so the desktop details route switches between a split grid layout and a centered reading-only layout based on `showVocabularyColumn`.",
      "Moved the desktop reopen affordance into the reading-pane shell and added stable route-level test hooks for split versus centered states.",
      "Added `e2e/classic-tools-nav.spec.ts :: desktop text details centers the reading pane when the vocabulary column is closed` to verify close, centered-state, and reopen behavior on the real desktop route.",
    ],
  },
  {
    id: "generated-title-completeness-validation",
    timestamp: "2026-07-24T09:24:55+02:00",
    title: "Generated titles now fall back when a final character is missing",
    summary:
      "When a generated title arrives with a dangling quote or truncated ending, the app now rejects it before saving and falls back to a safe story-derived headline instead of showing a cut-off title on the reading page.",
    userHighlights: [
      "Generated titles with obvious truncation, such as an unclosed quote, are no longer accepted as the displayed title.",
      "The reading page now keeps the full stored title intact, including balanced quoted phrases.",
      "If a title looks incomplete, the backend replaces it with a safer fallback headline before it reaches the UI.",
    ],
    technicalNotes: [
      "Extended the shared headline-completeness helper in `barkguide/reeprise/language_quality.py` to flag dangling quote marks and truncated endings before title persistence.",
      "Kept the existing generation fallback path intact so incomplete titles fall back to the story-derived short title instead of being saved verbatim.",
      "Added backend regression coverage for dangling quotes and balanced quoted titles, plus a frontend content-resolution regression that preserves the full quoted title string.",
      "Updated the generated-text details QA notes and coverage matrix so the title-completeness guardrail is tracked alongside the existing text-details flow.",
    ],
  },
  {
    id: "overview-rabbit-hole-mobile-topic-headers-use-full-card-width",
    timestamp: "2026-07-24T09:23:11+02:00",
    title: "Rabbit Hole topic headers on overview now stay readable on mobile",
    summary:
      "Long Rabbit Hole topics on the overview now use smaller mobile headline sizing and normal wrapping across the full card header width, so they no longer break awkwardly after nearly every word.",
    userHighlights: [
      "Long weekly topics on mobile now read as normal wrapped headlines instead of one-word stacks.",
      "The topic header uses more of the card width before wrapping.",
      "Desktop Rabbit Hole card typography keeps the existing editorial line treatment.",
    ],
    technicalNotes: [
      "Updated `RabbitHoleCompactEditionHeader` in `src/components/RabbitHoleEditionCover.tsx` to render the unsplit topic text on mobile, keep the balanced multi-line layout on large screens, and reserve right padding around the overflow menu area.",
      "Reduced the compact mobile topic font size and relaxed line height so long topics remain readable without squeezing into one-word lines.",
      "Extended `e2e/app-overview.spec.ts` to assert the mobile Rabbit Hole topic header stays below the desktop-sized font and occupies most of the header width before the existing overflow check runs.",
    ],
  },
  {
    id: "rabbit-hole-title-translations-now-use-title-context",
    timestamp: "2026-07-24T09:14:48+02:00",
    title: "Rabbit Hole title subtitles now use the title-safe translation path",
    summary:
      "Rabbit Hole subtitles and generated-text title translations now use the same title-context request shape, so the overview no longer falls back to the generic translation prompt that could drift into awkward or semantically wrong wording.",
    userHighlights: [
      "The subtitle under a Rabbit Hole title is now translated through the title-specific path instead of the generic translation flow.",
      "Quoted names and established terms in titles are preserved more strictly, which reduces the chance of unnatural literal substitutions.",
      "The overview keeps showing the original generated title as the primary text, while the translated line stays a supporting subtitle.",
    ],
    technicalNotes: [
      "Added a shared `buildGeneratedTextTitleTranslationRequest()` helper in `src/utils/generatedTextTitleTranslationRequest.ts` and reused it from both the overview subtitle effect and the generated-text details page.",
      "Updated `quick_translate()` in `barkguide/reeprise/views.py` to treat `GENERATED_TEXT_TITLE` as a stricter translation mode: it now extracts quoted/named phrases from the source title, includes them in the prompt, and uses `temperature=0` for title-context requests.",
      "Extended the overview Playwright regression so the featured subtitle translation request body is asserted directly, including `translation_context: \"GENERATED_TEXT_TITLE\"`.",
      "Extended the backend quick-translate regression to verify the title-context prompt keeps named phrases stable and preserves the title-specific system prompt.",
    ],
  },
  {
    id: "overview-rabbit-hole-card-ordering",
    timestamp: "2026-07-23T16:35:00+02:00",
    title: "Overview rabbit hole cards now sort by relevance",
    summary:
      "Rabbit hole cards on the overview page now sort by importance instead of a fixed language order: languages with an unread today's discovery appear first, followed by active weeks without today's content yet, then weeks where today's text has already been read. Completed weeks and languages without a topic sit at the bottom. Within the same state, languages with more completed discoveries this week are listed higher.",
    userHighlights: [
      "The language you are actively reading today moves to the top, so your next rabbit hole chapter is always the first thing you see.",
      "Languages where you have already read today's discovery stay visible above completed and empty weeks, but let unread content take priority.",
      "The more discoveries you complete in a language this week, the higher that language appears within its group.",
      "Completed weekly topics and languages without an active topic stay at the bottom, out of the way.",
    ],
    technicalNotes: [
      "Extracted `isRabbitHoleCardActive(week)` — a type guard that mirrors the previous inline `isWeekFullyDone` logic — and added `getCardSortTier(week, todayIso)` that assigns each card a tier: unread-today (0), no-today-discovery (1), read-today (2), completed (3), unset (4).",
      "Applied a stable `.sort()` on `rabbitHoleCards` in `barkguide-ui/src/routes/app/index.tsx` that orders by tier, then by `completed_discoveries` descending within tier, preserving existing language order as a final tiebreaker.",
      "Replaced all three inline `!isWeekFullyDone` conditions with `isRabbitHoleCardActive(week)`, removing the duplicated fully-done check.",
      "Fixed `overviewTopicHeaderLines` to exclude fully-done weeks from the max-header-line computation, since fully-done weeks no longer render edition covers.",
      "TypeScript type guard signature (`week is RabbitHoleWeek`) preserves narrowing at all callsites without extra null checks.",
    ],
  },
  {
    id: "mobile-language-space-page-navigation",
    timestamp: "2026-07-23T16:30:04+02:00",
    title: "Every language space is now one tap away on mobile",
    summary:
      "The mobile top navigation now shows every added language space as a small color-coded edition cover, with a stacked-pages icon for Overview, replacing the swipe carousel.",
    userHighlights: [
      "Every added language space stays visible without swiping, searching, or opening a menu.",
      "Compact language codes and distinct language-space colors make each destination easy to recognize, while full native names remain available to assistive technology.",
      "The stacked Overview icon stays at the far left, followed by the individual language covers.",
      "The complete Overview stack now occupies the same width and height as one individual language cover.",
      "A dark lower rule marks the active cover, using the calm editorial character of the overview edition cards.",
      "The navigation remains compact, touch-friendly, and free of horizontal scrolling on common phone widths.",
    ],
    technicalNotes: [
      "Replaced the pointer-driven circular carousel in `routes/app/route.tsx` with a centered semantic mobile `nav` of compact cover controls and a layered Overview glyph.",
      "Removed carousel gesture state, pointer capture, transform calculations, distance-based visibility, and indicator dots.",
      "Reused the existing native language-name translations and per-language surface, border, and ink tokens without adding a dependency.",
      "Kept Overview and all effective added language spaces mounted at once, with `aria-current=page` marking the active destination.",
      "Updated the mobile navigation Playwright regression to assert simultaneous visibility, direct language switching, distinct space colors, active state, and phone-width overflow safety.",
    ],
  },
  {
    id: "rabbit-hole-recommended-topic-planner",
    timestamp: "2026-07-23T16:17:41+02:00",
    title: "Overview and weekly-topic planner lead with one recommended edition",
    summary:
      "Starting a new 7-day topic now feels like choosing the next edition: one personalized recommendation is shown as the main title, with clear paths to start it, see another idea, or enter a topic of your own.",
    userHighlights: [
      "The recommended topic is now the visual focus instead of one option among many topic chips.",
      "Choose the recommendation directly or ask for another personalized idea without starting anything.",
      "A dedicated custom-topic field remains available and keeps the existing clarification step for ambiguous topics.",
      "The redesigned planner is responsive and uses touch-friendly controls on mobile.",
      "The same recommendation-first design now appears directly on the overview whenever a language has no active weekly topic.",
      "Recommendation covers now appear in restrained grayscale while they are still drafts; starting one reveals the full language- and topic-specific edition colors.",
      "The another-idea action now looks like a clear secondary button, and the custom-topic path is reduced to one input labeled by its placeholder.",
      "The recommendation cover footer now names its language space in the language's native form instead of saying `Recommended for you`.",
      "Inactive recommendation cards no longer show a management menu; the menu remains available only after a 7-day edition has been started.",
      "The choose-topic action now uses the black primary-button treatment, making the recommended starting path unmistakable.",
      "The German recommendation heading is now the more concise `Nächste Ausgabe`.",
      "Draft and active edition covers now begin on the same horizontal baseline and use the same height in the overview.",
    ],
    technicalNotes: [
      "Rebuilt `PlanningState` in `routes/app/rabbit_hole.tsx` around the existing topic-suggestions query, using the first returned suggestion as the primary recommendation and cycling locally through further results.",
      "Replaced the overview's equal-weight suggestion chips in `routes/app/index.tsx` with the same editorial recommendation card, start action, another-idea action, and custom-topic input.",
      "Added a shared `RabbitHoleRecommendationCover` beside `RabbitHoleEditionCover`; both now reuse the same deterministic design tokens, title-balancing algorithm, fitted typography, language signature, spine, texture, and cover-background generator.",
      "Added shared active-color and grayscale-draft palette resolution in `utils/rabbitHoleEditionDesign.ts`, so a confirmed recommendation keeps its cover family while revealing the intended edition colors.",
      "Restyled both recommendation-cycle controls with a bordered surface and refresh icon, removed the duplicate custom-topic heading, and changed the input placeholder to `Eigenes Thema` / `Your own topic`.",
      "Reused the existing native language-label maps for recommendation cover footers and removed the now-unused recommendation-label translation key.",
      "Scoped the overview overflow-menu trigger to active, incomplete Rabbit Hole weeks and added a browser regression for active versus inactive cards.",
      "Aligned the recommendation-start controls on overview and planning with the existing primary edition-action styling, including explicit disabled states.",
      "Updated the shared German `rabbit_hole_next_edition_title` translation and pinned the wording with a copy guardrail.",
      "Extracted the active edition's compact header as a shared component and reused its typography, title balancing, and responsive row-height reservation for inactive recommendation cards.",
      "Moved active and draft cards onto one compact cover-size definition and extended the overview browser regression to compare their top coordinates and heights directly.",
      "Added an explicit recommendation loading state and retained the existing default topic list as a failure fallback.",
      "Kept the existing create-week mutation, Wikipedia-backed topic refinement, offline error handling, generation preview, and overview return path unchanged.",
      "Added German and English translation keys for the new editorial planner copy.",
      "Extended `e2e/rabbit-hole-planning.spec.ts` and `e2e/app-overview.spec.ts` to cover recommendation rendering, cycling, starting a recommendation, custom-topic refinement, and mobile overflow.",
    ],
  },
  {
    id: "title-translation-no-layout-shift",
    timestamp: "2026-07-23T16:10:00+02:00",
    title: "Generated text detail page no longer shifts when title translation appears",
    summary:
      "The generated text detail page now reserves space for the title translation line, so the page layout stays stable when the translation arrives instead of content jumping down.",
    userHighlights: [
      "When reading a generated text, the page no longer shifts when the title translation finishes loading and appears below the original title.",
      "The reserved space is invisible and only becomes visible when the translation text is ready.",
    ],
    technicalNotes: [
      "Removed conditional rendering of the title translation `<p>` element in `GeneratedTextDetailsTextPane.tsx` — the element is now always present with `min-h-[1.4rem]` and a non-breaking space placeholder when `titleTranslation` is empty.",
      "Added `min-h-[1.4rem]` to the title translation paragraph to match the existing layout-stability pattern from `FeaturedOverviewTask` in `app/index.tsx`.",
    ],
  },
  {
    id: "language-specific-rabbit-hole-ctas",
    timestamp: "2026-07-23T14:36:00+02:00",
    title: "Rabbit hole CTAs now appear in the learning language",
    summary:
      "The primary action buttons on rabbit hole edition covers and the inline continuation CTA on generated text pages now display in the target learning language (Italian, French, German, English) instead of the user's UI language.",
    userHighlights: [
      "An Italian rabbit hole cover now shows 'Leggi il capitolo di oggi' instead of 'Heutiges Kapitel lesen' or 'Read today's chapter'.",
      "The 'Weiterlesen' / 'Continue reading' button is now 'Continua a leggere' in Italian, 'Continuer à lire' in French, and stays 'Weiterlesen' in German and 'Continue reading' in English.",
      "The inline text continuation CTA on generated text pages follows the same pattern: it uses the text's language rather than the UI language.",
    ],
    technicalNotes: [
      "Added `READ_TODAY_CTA_BY_LANGUAGE` and `CONTINUE_READING_CTA_BY_LANGUAGE` static maps to `src/constants.ts`, keyed by language space ID.",
      "Updated all 5 call sites across `routes/app/index.tsx`, `routes/app/rabbit_hole.tsx`, and `components/CreateVocabularyTextInlinePreview.tsx` to resolve CTA labels from these maps with a `translate()` fallback.",
      "Added invariant test to `src/constants.test.ts` ensuring every enabled language space has an entry in both CTA maps.",
      "Updated `e2e/inline-vocabulary-continuation.spec.ts` assertion from 'Weiterlesen' to 'Continue reading' to match the English text's native label.",
    ],
  },
  {
    id: "overview-instant-rabbit-hole-cards",
    timestamp: "2026-07-23T15:30:00+02:00",
    title: "Overview page now renders rabbit hole cards instantly with skeleton placeholders",
    summary:
      "The overview page no longer shows the 'Start a rabbit hole' planning template while rabbit hole data is loading. Instead, skeleton placeholder cards appear immediately and transition seamlessly to the edition covers when data arrives, making the page feel instantaneous.",
    userHighlights: [
      "Opening the overview page now shows grey placeholder cards immediately instead of flashing the topic-creation form before edition covers appear.",
      "When navigating back to the overview within 30 seconds, rabbit hole cards appear from cache without waiting for network requests.",
      "Each language card loads independently: cards that finish first show their edition cover right away while others continue loading gracefully.",
    ],
    technicalNotes: [
      "Added per-card `isLoading` field to `rabbitHoleCards` (derived from `useQueries` loading state) to track individual query progress.",
      "Replaced the dead loading condition `isLoading && cards.length === 0` (which was never true since cards always has entries) with `allCardsLoading` check: shows a full skeleton grid when all cards are loading with no data yet.",
      "Added `isWeekLoading` condition in the card rendering ternary: when a card's query is loading and week is undefined, it renders an `animate-pulse` skeleton placeholder instead of falling through to the unset planning template.",
      "Increased `staleTime` of rabbit hole current-week queries from `0` to `30_000` in the overview page's `useQueries` call so that navigating within the app does not trigger unnecessary re-fetches.",
      "Backend `current_week` endpoint: eliminated 2 extra DB queries by loading `discoveries` once into `_prefetched_discoveries` and computing `completed_count`/`total_count` from the preloaded list instead of separate `.count()` calls.",
    ],
  },
  {
    id: "number-to-words-vocabulary",
    timestamp: "2026-07-21T10:50:00+02:00",
    title: "Tapping numbers in text now shows them as words and saves to vocabulary",
    summary:
      "When reading a generated text, tapping a number (digit, Roman numeral, year, fraction, phone number, or range) now opens a preview showing the number written out as words in the text's language and translated to your mother tongue. You can save it as vocabulary with one tap.",
    userHighlights: [
      "Tapping a year like '1990' in a German text shows 'neunzehnhundertneunzig' – year-style pronunciation. In amount context ('1990 Euro') it shows the cardinal form 'eintausendneunhundertneunzig' instead.",
      "Tapping '1. August' shows the ordinal 'erster August'. Tapping '3/4' shows 'drei Viertel'. Tapping '80er' shows 'achtziger'.",
      "English number forms follow: '1990' as a year shows 'nineteen ninety', as cardinal 'one thousand nine hundred ninety'.",
      "Roman numerals work too: tapping 'XIV.' shows 'der vierzehnte' (German) or 'the fourteenth' (English).",
      "Phone digit groups like '0176-1234567' are read digit-by-digit: 'null eins sieben sechs'.",
      "Number ranges like '2-3' show 'zwei bis drei'. The translation in your mother tongue is shown below.",
      "Tap 'Save as vocabulary' to add the spelled-out word form. Once saved, tapping the number again shows the standard vocabulary card.",
    ],
    technicalNotes: [
      "New utility module `src/utils/numberToWords.ts` with `resolveNumberPattern()` for pattern detection (cardinal, year, ordinal, decade, range, fraction, phone, Roman numeral with dot) and `numberToWords()` for conversion to German and English word forms.",
      "Context detection uses surrounding sentence text to decide year/cardinal/ordinal interpretation: year keywords ('geboren', 'Jahr'), currency keywords ('Euro', 'kostet'), and ordinal markers ('am 3. Mai', '2nd').",
      "`collectSentenceTokens()` tokenizes sentence text with awareness of digit groups, connectors (-, /), and Roman numeral characters.",
      "Pattern connection logic (`findConnectedPattern`) expands from the tapped token to find multi-token patterns like ranges and fractions.",
      "In `GeneratedTextDetailsTextPane.tsx`, the `onAddClick` callback in `createTextSpans` now intercepts numeric word taps before the standard `createOrToggleVocabulary` flow. Non-numeric words pass through unchanged.",
      "New `NumberPreviewFlyout` component renders as a portal popover showing the word form, translation, and a save button. Save uses `onCreateVocabularyFromPhrase` to call the `/api/reeprise/vocabulary/agent_add/` endpoint with the spelled-out word.",
      "After save, the number position becomes previewable via `localPreviewVocabularyByStart`, so subsequent taps show the standard vocabulary card.",
      "5 new translation keys added for the number preview UI: `number_preview_label`, `number_preview_context_label`, `number_preview_translation_label`, `number_preview_save`, `number_preview_close`.",
      "83 unit tests in `src/utils/numberToWords.test.ts` covering all pattern types in both German and English, plus French Roman ordinal suffixes.",
    ],
  },
  {
    id: "french-roman-ordinal-detection",
    timestamp: "2026-07-26T16:17:00+02:00",
    title: "French Roman ordinals (XVIIe, Ier) now detected as number patterns",
    summary:
      "Roman numerals with French ordinal suffixes like XVlle (17th) or Ier (1st) in generated texts are now recognized as number patterns. Tapping them opens the word-form preview instead of falling through to a numeric translation.",
    userHighlights: [
      "Tapping 'XVIIe' in a French text now shows the word form ('der siebzehnte' / 'the seventeenth') and translation in the number preview, instead of just the number 17.",
      "Tapping 'Ier' (1st in French) now shows the ordinal word form ('der erste' / 'the first').",
      "Number-word taps on ordinal forms like 'IIe' (2nd), 'Ve' (5th) and others are supported.",
    ],
    technicalNotes: [
      "Extended `isRomanWord()` regex to also match French ordinal suffixes 'e' and 'er' (e.g., `XVIIe`, `Ier`) without case-insensitive flag, requiring the Roman numeral part to be uppercase to avoid false positives on common lowercase French words like `vie` or `Mme`.",
      "Added `frenchOrdinalSuffix` field to the roman `TokenClassification` type so `classifyToken()` preserves the suffix and `resolveNumberPattern()` uses it for ordinal detection alongside existing dot-based and recovery logic.",
      "Updated `getTokenWord()` and `resolveNumberPattern()` to correctly reconstruct display tokens with the French suffix included.",
      "14 new unit tests added: 5 for `isRomanWord` (French suffix detection, rejection of lowercase false positives), 5 for `resolveNumberPattern` (XVIIe, Ier, IIe, Ve, and dot-suffix combined), and 4 for `numberToWords` (German and English word forms for French ordinals).",
    ],
  },
  {
    id: "offline-vocabulary-resilience",
    timestamp: "2026-07-20T16:20:00+02:00",
    title: "Vocabulary translations now handle offline and stuck states",
    summary:
      "When adding vocabulary while offline or when the AI translation service is unreachable, the app now shows clear feedback and offers recovery options instead of leaving words stuck in 'currently being generated' forever.",
    userHighlights: [
      "When you are offline and try to add a word, a clear message tells you to check your connection instead of silently failing.",
      "If a translation generation fails (e.g. because the AI service is temporarily unreachable), the vocabulary now shows a 'Translation failed' message with a Retry button.",
      "When a translation completes while you are still on the page, a toast notification tells you it's ready.",
      "A background check notifies you when all pending translations have finished, even if you navigated away.",
      "Rabbit hole topic weeks now poll for pending titles and content, so edition covers update automatically when generation completes.",
      "Creating a rabbit hole week while offline now shows a clear connection error instead of a stuck empty title.",
    ],
    technicalNotes: [
      "Increased `max_retries` on `generate_translation` and `generate_mnemonic` celery tasks from 3 to 5 with exponential backoff (5s→10s→20s→40s→80s). After all retries are exhausted, vocabulary status is set to FAILED instead of being left in PENDING indefinitely.",
      "Added `retry_translation` POST action on `VocabularyViewSet` at `/api/reeprise/vocabulary/{id}/retry_translation/` to re-dispatch failed translations.",
      "Moved side effects (`invalidateQueries`, `VOCABULARY_ADDED_EVENT`, localStorage flag) from `onSettled` to `onSuccess` in `useVocabularyCreate` to prevent spurious invalidation on POST failures.",
      "Added try/catch around `createOrToggleVocabulary` in `GeneratedTextDetailsTextPane.tsx` with offline-aware error toast messages.",
      "`SavedWordPreviewFlyout` now checks `vocabulary.status === 'FAILED'` and renders a retry button instead of the pending label.",
      "`VocabularyPreviewCard`'s `resolvedPrimaryTranslation` now handles `FAILED` status explicitly.",
      "Added `refetchInterval` (3s) to `useRabbitHoleCurrent` when any discovery has `status === 'PENDING'` or placeholder titles.",
      "Added `handleTopicSelect` error handling in `rabbit_hole.tsx` with offline-aware toast.",
      "Added same-page completion detection in `$generatedTextId.tsx` using a `useRef<Set<number>>` to track previously-PENDING vocabulary IDs.",
      "Added cross-page pending-count polling in `route.tsx` (15s interval) that shows a toast when all pending translations complete.",
      "Replaced silent `except Exception: pass` in `_generate_rabbit_hole_journey_titles` with proper `logger.warning` logging.",
      "Added try/except with logging around `_generate_one_rabbit_hole_title` fallback calls in `prepare_rabbit_hole_discoveries`.",
      "Added 7 new translation keys for offline error, translation failure, retry, and ready notifications.",
    ],
  },
  {
    id: "inline-continuation-now-available-to-all-users",
    timestamp: "2026-07-18T10:41:43+02:00",
    title: "Continue reading is now available to all users",
    summary:
      "The inline continuation (Weiterlesen) feature on the text details page was previously restricted to staff users behind an experimental-features toggle. It is now available to all users when reading a completed generated text.",
    userHighlights: [
      "The Weiterlesen button now appears for anyone reading a completed text, allowing the text to be extended with selected vocabulary.",
      "The original reading conditions still apply: continuation is only shown on the root text (not chapters), for completed texts, and for generated texts (not imported readings).",
    ],
    technicalNotes: [
      "Removed the `show_experimental_features` gating condition from the `showInlineContinuationPreview` derivation in `barkguide-ui/src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Removed the unused `useUserSettings` import and hook call from the same file.",
    ],
  },
  {
    id: "rabbit-hole-week-complete-actions",
    timestamp: "2026-07-17T15:39:00+0200",
    title: "Week-complete view now provides navigation and the ability to start a new rabbit hole topic",
    summary:
      "The week-complete summary view (shown when all 7 discoveries are read) was a dead end with no way to continue. Now it offers a 'Decide later' link back to overview and an abandon action to start a fresh weekly topic without waiting for the current week to expire.",
    userHighlights: [
      "When all chapters in a rabbit hole week are read, you can now return to the overview or abandon the completed week to start a fresh topic immediately.",
      "The overview now detects fully completed weeks and shows the 'start new topic' prompt instead of keeping a stale chapter cover for the last discovery.",
    ],
    technicalNotes: [
      "Added `navigate` and `abandonWeek` props to `WeekCompleteState` in `barkguide-ui/src/routes/app/rabbit_hole.tsx`, so users can go back to `/app` or abandon the week from the all-done view.",
      "Added `isWeekFullyDone` detection in `RabbitHoleOverviewSection` in `barkguide-ui/src/routes/app/index.tsx` — when a week has `completed_discoveries >= total_discoveries > 0`, the overview shows the unset/start-new prompt instead of the edition cover.",
      "The abandoned week mutation already invalidates the current-week query, so the overview and rabbit hole page re-fetch the now-nonexistent week and reset to the planning state.",
    ],
  },
  {
    id: "rabbit-hole-lazy-generation",
    timestamp: "2026-07-17T17:30:00+0200",
    title: "Rabbit hole discovery texts are now generated on demand instead of all at once when a new week is created",
    summary:
      "When creating a new rabbit hole week, only titles are generated eagerly for the 7 daily discoveries. The full text body for each discovery is now generated only when the user opens it, matching the daily nudge lazy-generation pattern. This fixes a misleading progress display where all texts appeared as 'read' before the user had opened any.",
    userHighlights: [
      "Creating a new rabbit hole week no longer immediately generates all 7 texts — they appear as pending and are generated as you open each one.",
      "Topic titles are still visible on the covers so you can preview what each day will cover before committing.",
      "The completed/discoveries progress counter now reflects actual user activity rather than background generation status.",
    ],
    technicalNotes: [
      "Removed the eager enqueue loop (enqueue_generated_text_once for all 7 texts) from prepare_rabbit_hole_discoveries() in barkguide/reeprise/tasks.py.",
      "Text body generation is now triggered lazily via ensure_daily_text_generation_started() when the user opens a discovery, which already supported RABBIT_HOLE origin.",
      "The frontend useEffect in $generatedTextId.tsx already calls start-generation on open for deferred origins including RABBIT_HOLE, so no frontend changes were needed.",
      "create_rabbit_hole_discovery() (used for manually generating a single additional discovery) still enqueues eagerly since the user explicitly requested that generation.",
    ],
  },
  {
    id: "rabbit-hole-folder-nav",
    timestamp: "2026-07-17T12:30:00+0200",
    title: "Rabbit hole weeks are now accessible from the side navigation as expandable folders under each language space",
    summary:
      "Each language space in the sidebar now acts as a collapsible folder that shows the last 5 rabbit hole weeks. Past weeks can be revisited at any time by clicking a week entry.",
    userHighlights: [
      "Click any language space in the sidebar to expand it and see your recent rabbit hole weeks.",
      "Access past rabbit hole weeks from the sidebar without losing context, and return to active reading with the 'Decide later' link on the planning page.",
      "When a language has more than 5 weeks, a 'Show N more' link reveals the rest.",
    ],
    technicalNotes: [
      "Added optional `?language=` query parameter to `RabbitHoleWeekViewSet.get_queryset()` in `barkguide/reeprise/views.py` for efficient per-language week listing.",
      "Added `useRabbitHoleWeeksList(language?)` and `useRabbitHoleWeekDetail(weekId)` hooks in `useRabbitHole.ts`.",
      "Updated `/app/rabbit_hole` route to accept optional `weekId` search param via `validateSearch`. When present, the page fetches and displays that specific week in read-only mode (calendar visible, management sections hidden).",
      "Converted side navigation language space buttons into expandable folders in `route.tsx`: added `expandedFolderLanguage` state, chevron icons, and indented sub-items with topic + date range. Only one folder open at a time. Shows last 5 weeks with a 'Show N more' toggle.",
      "Added `side_nav_show_more_weeks` translation key (DE: Noch {{count}} mehr, EN: Show {{count}} more).",
      "Updated `openPlanner` navigate call in `index.tsx` to include `search: { weekId: undefined }` to match the new route search param type.",
    ],
  },
  {
    id: "rabbit-hole-planning-back-to-overview",
    timestamp: "2026-07-17T16:00:00+0200",
    title: "After abandoning a rabbit hole, users can now return to the overview instead of being forced to pick a new topic immediately",
    summary:
      "The rabbit hole planning page now shows a 'Später entscheiden' / 'Decide later' link so learners can defer creating a new weekly topic after abandoning the current one.",
    userHighlights: [
      "After abandoning an active rabbit hole, you are no longer stuck on the planning screen — you can go back to the overview and plan a new topic later.",
      "The back link is also present during normal weekly topic planning, giving you a quiet exit path.",
    ],
    technicalNotes: [
      "Added `rabbit_hole_back_to_overview` translation key (DE: Später entscheiden, EN: Decide later) in `barkguide-ui/src/assets/translations.json`.",
      "Passed the `navigate` function to `PlanningState` and added a `button` that calls `navigate({ to: '/app' })` at the bottom of the planning form in `barkguide-ui/src/routes/app/rabbit_hole.tsx`.",
    ],
  },
  {
    id: "fix-rabbit-hole-texts-not-loading",
    timestamp: "2026-07-17T14:00:00+0200",
    title: "Rabbit hole discovery texts now generate immediately instead of failing to load",
    summary:
      "New rabbit hole discovery texts are now sent for generation right after creation, eliminating a race condition that caused the error screen 'Dein Text konnte gerade nicht geladen werden' for some users.",
    userHighlights: [
      "Opening a new rabbit hole discovery text now reliably starts generating the content without manual retries.",
      "Texts that were previously stuck in a failed state can now auto-restart when the page is opened.",
    ],
    technicalNotes: [
      "Added enqueue_generated_text_once() calls to create_rabbit_hole_discovery() and prepare_rabbit_hole_discoveries() in barkguide/reeprise/tasks.py so generation starts on creation rather than waiting for the frontend to trigger it.",
      "The mark_stuck_background_generations_as_failed scanner no longer sweeps new rabbit hole texts that are still awaiting their first frontend-triggered generation, because enqueuing happens immediately.",
      "Frontend restartableFailedDeferredText now accepts FAILED rabbit hole and daily nudge texts regardless of chatgpt_output content, so texts that were previously marked FAILED by the stuck scanner can still be restarted.",
    ],
  },
  {
    id: "overview-create-week-navigates-to-rabbit-hole",
    timestamp: "2026-07-17T10:19:40+0200",
    title: "Creating a new rabbit hole week on the overview now navigates to the rabbit hole page",
    summary:
      "When starting a new weekly topic from the overview, the app navigates to the rabbit hole page instead of jumping directly into the first day's text.",
    userHighlights: [
      "After entering a topic on the overview, you now land on the rabbit hole page to see the full weekly plan.",
      "Both topic suggestions and custom topic input lead to the same rabbit hole overview.",
    ],
    technicalNotes: [
      "Replaced navigate to /app/reeprise/$generatedTextId with openPlanner(languageOption.id) in the overview createWeek success handlers, so the flow goes through the rabbit hole page consistently.",
    ],
  },
  {
    id: "fix-overview-referenceerror-index-not-defined",
    timestamp: "2026-07-16T17:04:28+0200",
    title: "Fix overview crash on \"Unerwarteter Fehler\" during onboarding redirect",
    summary:
      "The overview page no longer crashes with a ReferenceError when the RabbitHoleOverviewSection renders, which was most visible during the forced onboarding redirect from /app to /app/start-learning.",
    userHighlights: [
      "The app no longer shows the 'Unerwarteter Fehler' screen when landing on the overview before completing onboarding.",
      "New users can now reach the language-space onboarding flow without hitting a blank error page.",
    ],
    technicalNotes: [
      "Added missing index parameter to the cards.map callback in RabbitHoleOverviewSection (barkguide-ui/src/routes/app/index.tsx:567) so topicSuggestionsQueries[index] resolves correctly.",
    ],
  },
  {
    id: "smarter-weekly-topic-suggestions-and-inline-planning",
    timestamp: "2026-07-16T16:49:29+0200",
    title: "Smarter weekly topic suggestions and inline planning on the overview",
    summary:
      "The overview page now offers topic suggestions personalized to your interests when no weekly topic is set, and lets you start a new week without leaving the overview.",
    userHighlights: [
      "Smart topic suggestions now appear directly on the 'Noch kein Wochenthema' card, based on your past interests and unexplored areas.",
      "Click a suggested topic or type your own to jump straight into the first day's reading — no extra navigation needed.",
      "The dedicated weekly planner page also uses the same smart suggestions instead of static examples.",
    ],
    technicalNotes: [
      "Added GET /api/reeprise/rabbit-hole-weeks/topic-suggestions/ that uses OpenAI to generate personalized topic ideas from past RabbitHoleWeek topics and TopicInterestSignals, with blind-spot horizon expansion via build_interest_map_summary.",
      "New useRabbitHoleTopicSuggestions React Query hook fetches per-language suggestions with 5-minute staleness.",
      "Replaced the static overview unset card with an inline planning form: topic input + suggestion chips + direct navigation to /app/reeprise/{id} on creation.",
      "Replaced EXAMPLE_TOPICS_LIST in the Rabbit Hole planning page with smart suggestions, falling back to curated per-language defaults when AI fails or data is stale.",
      "Added generate_rabbit_hole_week_topic_suggestions() in topic_intelligence.py with per-language curated fallback topics.",
    ],
  },
  {
    id: "title-translations-preserve-essential-original-terms",
    timestamp: "2026-07-15T17:33:41+0200",
    title: "Title translations now preserve essential original terms",
    summary:
      "Mother-tongue title translations now keep names and familiar international terminology when translating them would weaken or distort the title.",
    userHighlights: [
      "Terms such as Bug, SpaceX, GPT, and iPhone can remain in their established original form.",
      "Proper nouns, brands, products, model names, and acronyms are protected from generic substitutions.",
      "The surrounding title is still translated naturally into the learner's mother tongue.",
    ],
    technicalNotes: [
      "Added a scoped `GENERATED_TEXT_TITLE` context to the existing translation request instead of changing vocabulary and quick-translate behavior globally.",
      "Extended the backend prompt with explicit preservation rules and a negative `Bug` to `Fehler` example while allowing grammar-required capitalization.",
      "Versioned the local title-translation cache so previously cached over-translations are regenerated, and added backend plus browser request-contract coverage.",
    ],
  },
  {
    id: "existing-rabbit-hole-title-translations-survive-strict-mode",
    timestamp: "2026-07-15T17:30:24+0200",
    title: "Existing Rabbit Hole titles now receive their translation reliably",
    summary:
      "Opening an older Rabbit Hole text now keeps its mother-tongue title request alive through the development-mode render check.",
    userHighlights: [
      "Existing texts such as the SpaceX Rabbit Hole now receive the same translated subtitle as newly opened texts.",
      "The stored learning-language title remains the source; no text regeneration or database migration is needed.",
      "A slower translation response no longer causes the subtitle to disappear permanently.",
    ],
    technicalNotes: [
      "Removed a redundant request-ref guard that remembered an aborted React Strict Mode effect and suppressed the real follow-up request.",
      "Confirmed Rabbit Hole generation stores only learning-language titles, so details continues to use the shared `/api/reeprise/translate/` endpoint and per-user frontend cache.",
      "Changed the focused browser regression to reproduce text 976's legacy Rabbit Hole payload and delayed translation response.",
    ],
  },
  {
    id: "text-details-title-shows-mother-tongue-translation",
    timestamp: "2026-07-15T17:14:44+0200",
    title: "Reading titles now include a mother-tongue translation",
    summary:
      "Text details now place a readable translation directly below a title written in the learning language.",
    userHighlights: [
      "The original editorial title remains the strongest element on the page.",
      "Its mother-tongue translation appears immediately underneath in a smaller, quieter treatment.",
      "The translation is omitted when both languages match and never blocks the reading view if the request fails.",
    ],
    technicalNotes: [
      "Reused the existing authenticated translation endpoint with the generated text language as source and the configured mother tongue as target.",
      "Caches translations per learner, text, title, source language, and mother tongue so revisiting a text does not repeat the request.",
      "Extended the mobile text-details browser regression to verify the request pair, translated title, 16-pixel size, restrained contrast, and unchanged reading controls.",
    ],
  },
  {
    id: "edition-headers-name-the-language-space",
    timestamp: "2026-07-15T17:08:31+0200",
    title: "Edition cards now lead with their language space",
    summary:
      "Weekly-edition cards now replace the generic This week label with the relevant native language name.",
    userHighlights: [
      "English, Français, Deutsch, or Italiano now appears directly above each edition topic.",
      "The redundant This week and Diese Woche labels no longer appear on edition cards.",
      "The topic, cover, and existing language signature remain unchanged.",
    ],
    technicalNotes: [
      "Reused the shared native `languageLabel` in the edition header and removed the obsolete `thisWeekLabel` component prop and translation key.",
      "Applied the shared component change across overview, active-week, calendar, and pending-preview editions.",
      "Extended the existing overview browser regression with precise header-label and generic-copy absence assertions.",
    ],
  },
  {
    id: "quick-translate-now-lives-with-alpha-features",
    timestamp: "2026-07-15T17:07:46+0200",
    title: "Quick translate now sits with the Alpha-Version features",
    summary:
      "Übersetzen unterwegs now appears directly in the Alpha-Version section of the desktop side navigation.",
    userHighlights: [
      "The feature is grouped beneath the note that these functions are still in development.",
      "It no longer appears among the regular desktop navigation destinations or in the top bar.",
      "The established mobile bottom-navigation entry remains unchanged.",
    ],
    technicalNotes: [
      "Moved the quick-translate link from the general destination array into the existing Alpha-Version navigation group.",
      "Added an explicit Alpha-Version section test target and strengthened browser coverage to verify the link is nested in that group.",
      "Updated quick-translate acceptance, coverage, traceability, and risk records to specify the Alpha-Version placement.",
    ],
  },
  {
    id: "quick-translate-moved-from-topbar-to-side-navigation",
    timestamp: "2026-07-15T17:03:23+0200",
    title: "Quick translate now lives in the side navigation",
    summary:
      "Übersetzen unterwegs is now listed with the other desktop navigation destinations instead of occupying the top bar.",
    userHighlights: [
      "The desktop top bar is now less crowded.",
      "Übersetzen unterwegs remains directly available from the side navigation.",
      "The established mobile bottom-navigation entry remains unchanged.",
    ],
    technicalNotes: [
      "Moved the existing translated quick-translate link and pencil icon into the desktop side-navigation destination list.",
      "Removed the desktop topbar action and added a route-level browser regression covering placement, duplicate removal, and navigation.",
      "Updated the quick-translate QA feature record, coverage and traceability matrices, and risk register.",
    ],
  },
  {
    id: "generated-text-loading-is-editorial",
    timestamp: "2026-07-15T17:02:10+0200",
    title: "Text generation now has an editorial thinking state",
    summary:
      "Text details now show a soft light sweep while the first lines of a generated text take shape.",
    userHighlights: [
      "The old three-dot spinner and outlined loading container are gone.",
      "A muted status line and three fine text rules make the transition feel like a text forming on the page.",
      "Reduced-motion preferences keep the state calm, static, and readable.",
    ],
    technicalNotes: [
      "Replaced the pending-body spinner with translated status copy, an accessible live region, and lightweight CSS-only shimmer primitives.",
      "Clips the moving tonal sweep through the status text and matching hairlines without adding a dependency, image, shadow, or layout card.",
      "Extended copy guardrails and the deferred-generation browser regression to cover the pending design and its transition into streamed content.",
    ],
  },
  {
    id: "structure-mode-is-hidden-for-mvp",
    timestamp: "2026-07-15T16:26:47+0200",
    title: "Text details now stays focused on the MVP reading flow",
    summary:
      "The redundant mode selector is no longer shown on text details, keeping reading and vocabulary work focused.",
    userHighlights: [
      "The Structure option no longer appears in the text-details toolbar.",
      "The Vocabulary icon and label are also removed because vocabulary interaction remains the default behavior.",
      "The remaining reading actions now fill the vacated toolbar space without leaving a blank block on desktop or mobile.",
    ],
    technicalNotes: [
      "Removed the entire generated-text layer selector while keeping the reading overlay internally fixed to its vocabulary-aware default.",
      "Removed the Phrase mode action's former auto-margin so the deleted selector leaves no reserved horizontal space and the audio action can lead the shared toolbar.",
      "Recorded the hidden selector in the MVP feature register and added desktop/mobile browser coverage for absence and toolbar alignment.",
    ],
  },
  {
    id: "text-audio-progress-is-editorial",
    timestamp: "2026-07-15T16:41:35+0200",
    title: "Text audio now matches the editorial reading view",
    summary:
      "The audio entry point and progress section on text details now use one quieter, print-inspired design.",
    userHighlights: [
      "Listen to text now sits beside Phrase mode as part of one compact reading toolbar instead of floating alone below the title.",
      "A fine progress rule and slim position marker replace the generic slider treatment.",
      "Playback time, speed, and ten-second jumps are arranged as calm reading metadata.",
    ],
    technicalNotes: [
      "Moved `AudioTrigger` and its expandable playback layer into the shared reading toolbar, removed the empty metadata spacing when no source cards exist, and kept restrained icons without outlined boxes.",
      "Preserved the existing slider semantics, scrubbing behavior, speed cycle, skip actions, and translated accessible labels.",
      "Added translation coverage and a route-level browser regression with mocked media playback for toolbar alignment, entry point, progress track, and controls.",
    ],
  },
  {
    id: "mobile-edition-aligns-with-chapter-list",
    timestamp: "2026-07-15T16:12:31+0200",
    title: "Mobile edition covers now align with their chapter list",
    summary:
      "The main weekly-edition cover now uses the available mobile width and shares the same edges as More chapters.",
    userHighlights: [
      "The cover, reading action, divider, and More chapters row now form one clean mobile column.",
      "Unnecessary side whitespace around the main mobile cover has been removed.",
      "Desktop edition cards retain their smaller four-column layout.",
    ],
    technicalNotes: [
      "Removed the compact cover's mobile max-width and centering while preserving its desktop `lg` width constraint.",
      "Extended the mobile overview regression to compare the cover and More chapters row edges directly.",
    ],
  },
  {
    id: "desktop-language-spaces-use-native-names",
    timestamp: "2026-07-15T16:08:28+0200",
    title: "Language spaces now use their native names",
    summary:
      "The desktop side navigation now labels each language space in that language itself.",
    userHighlights: [
      "The sidebar now shows English, Français, Deutsch, and Italiano consistently.",
      "Language names no longer switch between translated English or German labels in the sidebar.",
    ],
    technicalNotes: [
      "Shared the existing language-space endonym mapping between the mobile carousel and desktop sidebar without changing other translated selectors or supporting copy.",
      "Added copy and browser regressions for all four enabled language-space labels and the real French-to-Italian sidebar transition.",
    ],
  },
  {
    id: "read-edition-covers-are-clearly-marked",
    timestamp: "2026-07-15T16:06:54+0200",
    title: "Opened edition chapters now have a quieter continue action",
    summary:
      "Edition cover artwork now stays fully visible while an opened chapter uses a quieter Continue reading action.",
    userHighlights: [
      "Opened chapters use “Continue reading” instead of repeating the first-read call to action.",
      "The secondary outlined button is calmer while remaining easy to find.",
      "Edition covers remain fully colored and sharp, including related daily chapter covers.",
    ],
    technicalNotes: [
      "Uses the shared `RabbitHoleEditionCover` read state to switch only the main action label and button treatment, without altering cover artwork.",
      "Derives read state exclusively from positive view count, matching the established feed convention and preferring canonical generated-text data over lightweight weekly payloads.",
      "Added translation, unit, and browser assertions for `Weiterlesen` / `Continue reading`, the secondary action style, and unchanged cover opacity.",
    ],
  },
  {
    id: "overview-week-list-is-more-chapters",
    timestamp: "2026-07-15T15:45:00+0200",
    title: "The overview week list is now called More chapters",
    summary:
      "The expandable weekly-edition list now uses a neutral label because it contains both earlier and upcoming chapters.",
    userHighlights: [
      "German now shows “Weitere Kapitel” instead of “Als Nächstes.”",
      "English now shows “More chapters” instead of “What's next.”",
      "Past and future chapters remain available in the same list.",
    ],
    technicalNotes: [
      "Updated the shared `overview_rabbit_hole_upcoming_title` translations without changing expansion or article-opening behavior.",
      "Renamed the related browser test descriptions and QA traceability references to match the user-facing chapter model.",
      "Extended translation and rendered-overview assertions for the new label.",
    ],
  },
  {
    id: "sidebar-alpha-labels-now-translated",
    timestamp: "2026-07-15T15:45:39+0200",
    title: "Sidebar preview labels now read correctly",
    summary:
      "The sidebar’s early-feature note now appears as normal English or German copy instead of internal translation keys.",
    userHighlights: [
      "English now shows “Alpha version” with a short development note.",
      "German now shows “Alpha-Version” with the matching German note.",
    ],
    technicalNotes: [
      "Added English and German values for `sidenav_alpha_version` and `sidenav_alpha_tools_hint` in the shared translation catalog.",
      "Added translation-copy and browser regressions that prevent the raw keys from returning in the desktop sidebar.",
    ],
  },
  {
    id: "edition-titles-have-safer-line-spacing",
    timestamp: "2026-07-15T15:42:13+0200",
    title: "Edition titles now have clearer line spacing",
    summary:
      "Multi-line edition titles now leave enough vertical space for tall and descending letterforms.",
    userHighlights: [
      "Letters such as T, i, and j no longer visually collide across title lines.",
      "The improved spacing applies to main, overview, and daily edition covers.",
      "Long titles still remain inside their reserved title area.",
    ],
    technicalNotes: [
      "Increased the shared serif cover-title line-height from 0.88 to 1.02.",
      "Updated container-relative height fitting so additional leading is included when scaling long titles.",
      "Extended browser coverage with a computed line-height-to-font-size ratio assertion.",
    ],
  },
  {
    id: "desktop-profile-link-moved-into-sidebar",
    timestamp: "2026-07-15T15:40:29+0200",
    title: "Profile now lives in the side navigation",
    summary:
      "The desktop profile link has moved out of the top bar and into the side navigation beside the other account destinations.",
    userHighlights: [
      "Profile is now available in the desktop sidebar near Settings.",
      "The top bar stays focused on page-level navigation and quick actions.",
      "Mobile profile navigation remains unchanged.",
    ],
    technicalNotes: [
      "Removed the main-page desktop profile icon from the topbar leading-action slot and added a translated profile item to the sidebar navigation list.",
      "Extended desktop browser coverage to verify there is no topbar profile link and that the sidebar profile link opens the profile route.",
    ],
  },
  {
    id: "weekly-editions-lead-the-overview",
    timestamp: "2026-07-15T15:39:59+0200",
    title: "Weekly editions now lead the overview",
    summary:
      "The weekly-topic edition section now appears before the featured daily text on the overview page.",
    userHighlights: [
      "Current weekly editions are the first content shown on the overview.",
      "The featured daily text remains directly underneath with the same reading action and rotating content.",
      "Additional compact reading rows remain below the featured daily text.",
    ],
    technicalNotes: [
      "Reordered the existing overview sections without changing data loading, title translation, navigation, or generation behavior.",
      "Extended overview browser coverage to assert the weekly-edition, featured-daily, and compact-row vertical order.",
    ],
  },
  {
    id: "overview-edition-covers-align-across-topic-lengths",
    timestamp: "2026-07-15T15:28:17+0200",
    title: "Overview edition covers now align evenly",
    summary:
      "Edition covers on the overview now align to the tallest topic that is actually present, without permanently reserving empty heading space.",
    userHighlights: [
      "Short and multi-line weekly topics no longer create staggered cover positions.",
      "The four-column desktop edition row keeps a consistent visual baseline only when alignment space is needed.",
      "Topic text uses the full available card width while the menu remains in the label row above it.",
    ],
    technicalNotes: [
      "Calculates the tallest real compact topic layout in the overview row and passes that shared line count into each active cover.",
      "Moved the management menu higher and removed the compact topic’s narrow character-width cap.",
      "Kept full-size and mini edition compositions unchanged.",
      "Added browser coverage with simultaneous short and long topics and asserts equal rendered cover positions.",
    ],
  },
  {
    id: "sidebar-brand-now-uses-favicon-mark",
    timestamp: "2026-07-15T15:27:17+0200",
    title: "The sidebar brand now uses the Uxhulu favicon",
    summary:
      "The Uxhulu browser favicon now sits beside the product name in the side navigation.",
    userHighlights: [
      "The familiar floral favicon now identifies Uxhulu in the desktop sidebar.",
      "Navigation behavior and the Uxhulu wordmark remain unchanged.",
    ],
    technicalNotes: [
      "Imported the existing root `favicon.ico` through Vite and rendered it in the sidebar brand toggle.",
      "Updated the desktop navigation browser regression to verify the favicon image and wordmark render together without an SVG logo.",
    ],
  },
  {
    id: "daily-edition-dates-stay-on-one-line",
    timestamp: "2026-07-15T15:19:32+0200",
    title: "Daily edition dates now stay intact",
    summary:
      "Daily covers now give the chapter date and weekly date range separate lines so neither date breaks unexpectedly.",
    userHighlights: [
      "Dates such as “Donnerstag, 16. Juli” remain on one line.",
      "The weekly date range moves to its own quiet, right-aligned line within the cover metadata.",
      "The title and bottom divider retain their fixed positions.",
    ],
    technicalNotes: [
      "Changed mini-cover metadata from a competing two-column row to stacked date rows.",
      "Added explicit non-wrapping contracts and test identifiers for the day date and weekly range.",
      "Extended overview browser coverage to verify both rendered date fields remain non-wrapping.",
    ],
  },
  {
    id: "edition-covers-use-one-bottom-rule",
    timestamp: "2026-07-15T15:15:47+0200",
    title: "Edition covers now share one bottom rule",
    summary:
      "Every weekly edition now uses the same single-rule treatment beneath its chapter details.",
    userHighlights: [
      "French editions no longer show an unexpected second line at the bottom.",
      "The bottom details now match the quieter treatment used by the other language editions.",
    ],
    technicalNotes: [
      "Removed the additional rendered double and offset rule variants from the shared edition cover component.",
      "Kept procedural palette, texture, gradient, spine, motif, and daily variation behavior unchanged.",
      "Extended the active-edition browser regression with a single bottom-rule assertion.",
    ],
  },
  {
    id: "long-edition-titles-keep-metadata-fixed",
    timestamp: "2026-07-15T15:05:13+0200",
    title: "Long edition titles no longer move cover details",
    summary:
      "Edition titles now fit within a reserved cover area while chapter progress and dates remain anchored at the bottom.",
    userHighlights: [
      "Chapter, reading progress, and date details stay in the same position for short and long titles.",
      "Long generated titles resize within the editorial title area instead of pushing cover details downward.",
      "Balanced title lines remain intact without arbitrary word breaks.",
    ],
    technicalNotes: [
      "Changed the shared edition interior to fixed header, title, and metadata grid regions across full, compact, and mini covers.",
      "Added container-relative width and height bounds to the existing weighted title sizing system.",
      "Extended browser coverage by replacing the active cover title with an oversized phrase and asserting that the metadata position remains unchanged.",
    ],
  },
  {
    id: "edition-texts-are-chapters-with-compact-progress",
    timestamp: "2026-07-15T14:59:59+0200",
    title: "Weekly edition texts are now called chapters",
    summary:
      "Daily edition texts now use a clear book-like chapter model, and reading progress is shorter and easier to scan on the cover.",
    userHighlights: [
      "Each daily text is labeled “Kapitel” / “Chapter.”",
      "Cover progress now reads “0/7 gelesen” instead of “0 von 7 erkundet.”",
      "Progress remains on one line on main and overview covers.",
      "Calendar, preparation, reading actions, and completion copy now use the same chapter terminology.",
    ],
    technicalNotes: [
      "Updated the weekly-edition translation family from discovery/text terminology to chapter terminology in German and English.",
      "Added a non-wrapping compact progress treatment and browser coverage for the rendered white-space contract.",
      "Added translation coverage that pins the chapter label and compact progress format.",
    ],
  },
  {
    id: "edition-covers-show-language-more-clearly",
    timestamp: "2026-07-15T14:43:13+0200",
    title: "Edition covers now show their language more clearly",
    summary:
      "The readable language name now has stronger hierarchy on every weekly-topic edition, independently of the subtle decorative language signature.",
    userHighlights: [
      "Main and overview covers show a larger, higher-contrast native language name above the edition title.",
      "Smaller daily editions now show the language name above their title as well.",
      "Language remains visible as text instead of relying on color or decorative motifs.",
    ],
    technicalNotes: [
      "Separated the readable horizontal language label from secondary vertical edition metadata and increased its type weight and size on full and compact covers.",
      "Added a compact language label to mini covers and extended overview browser coverage across both cover variants.",
    ],
  },
  {
    id: "end-topic-opens-new-topic-selection",
    timestamp: "2026-07-15T14:29:23+0200",
    title: "Ending a weekly topic now leads directly to a new one",
    summary:
      "The reset section now explains the replacement flow and returns learners directly to topic selection after the current topic ends.",
    userHighlights: [
      "The description makes it clear that a new weekly topic can be started immediately for the same language.",
      "The action now says “Thema beenden und neues starten.”",
      "After the topic ends, the new-topic selection screen appears automatically.",
    ],
    technicalNotes: [
      "The end-topic mutation now awaits the active-week query refresh so the planning state appears reliably before the action settles.",
      "Extended the planning browser regression through ending an active topic and returning to the topic input.",
    ],
  },
  {
    id: "weekly-topic-language-without-internal-feature-name",
    timestamp: "2026-07-15T14:24:47+0200",
    title: "Weekly topics now use clearer language",
    summary:
      "Planning, overview, navigation, calendar, completion, and reset copy now describe weekly topics and 7-day editions directly.",
    userHighlights: [
      "The reset section now says “Thema zurücksetzen” and explains plainly that the topic can be ended for the current language.",
      "Navigation and empty states now use “Wochenthema” instead of the internal feature name.",
      "The action now says “Thema beenden,” which is calmer and clearer than “Thema aufgeben.”",
    ],
    technicalNotes: [
      "Updated the German and English translation keys used across weekly-topic planning, overview, calendar, completion, navigation, and landing-page labels without renaming internal routes or API contracts.",
      "Added translation guardrails that reject the internal feature term in these visible strings and pin the requested German reset wording.",
    ],
  },
  {
    id: "rabbit-hole-calendar-excludes-current-edition",
    timestamp: "2026-07-15T14:16:36+0200",
    title: "Today’s Rabbit Hole edition is no longer duplicated",
    summary:
      "The main Rabbit Hole cover continues to represent today, while the adjacent calendar now shows only the other six days.",
    userHighlights: [
      "Today’s edition appears once as the main cover instead of appearing again in the calendar.",
      "The calendar still provides direct access to every other available day in the week.",
    ],
    technicalNotes: [
      "Derived the calendar slots by excluding the same day index used by the main edition, including the safe day-one fallback.",
      "Extended desktop and mobile browser coverage to assert six calendar editions and the absence of the current-day duplicate.",
    ],
  },
  {
    id: "rabbit-hole-calendar-beside-main-edition",
    timestamp: "2026-07-15T14:04:53+0200",
    title: "The Rabbit Hole calendar now uses a desktop side column",
    summary:
      "The active Rabbit Hole page now places its calendar and related daily editions beside the main edition on desktop while retaining the established mobile grid.",
    userHighlights: [
      "Desktop learners can see the main edition and its seven-day calendar side by side.",
      "Mobile keeps the existing two-column daily-edition arrangement.",
    ],
    technicalNotes: [
      "Expanded the active-week route container and introduced a responsive main-edition/calendar grid at the large breakpoint.",
      "The calendar uses two or three internal columns depending on desktop width without changing discovery actions or state semantics.",
      "Added browser assertions for right-column desktop placement and the retained two-column mobile layout.",
    ],
  },
  {
    id: "edition-titles-preserve-whole-words",
    timestamp: "2026-07-15T13:53:33+0200",
    title: "Edition titles now preserve whole words more carefully",
    summary:
      "Long words on compact Rabbit Hole covers now scale down before wrapping and use language-aware hyphenation only when necessary.",
    userHighlights: [
      "Words such as “Conquistato” no longer split across lines without a hyphen.",
      "Compact covers retain their editorial typography while fitting long multilingual titles more naturally.",
    ],
    technicalNotes: [
      "Added compact- and mini-specific title-size thresholds based on weighted line width.",
      "Added semantic language tags and CSS automatic hyphenation with emergency wrapping reserved as the final overflow fallback.",
      "Extended title-layout unit coverage with the reported Italian cover title.",
    ],
  },
  {
    id: "sidebar-brand-wordmark-without-logo",
    timestamp: "2026-07-15T13:46:23+0200",
    title: "The sidebar brand is now quieter",
    summary:
      "The side navigation now shows the Uxhulu name without a separate logo beside it.",
    userHighlights: [
      "The Uxhulu name remains visible at the top of the expanded side navigation.",
      "The decorative logo beside the name has been removed for a simpler navigation header.",
    ],
    technicalNotes: [
      "Removed the sidebar-only `BrandLogo` render and its unused SVG component import from the app shell.",
      "Extended the desktop navigation browser regression to verify the wordmark remains visible without an adjacent SVG.",
    ],
  },
  {
    id: "rabbit-hole-editions-use-desktop-space-better",
    timestamp: "2026-07-15T13:42:13+0200",
    title: "Rabbit Hole editions now sit more naturally on desktop",
    summary:
      "Rabbit Hole covers are more compact on larger screens and related editions now arrange side by side where space allows.",
    userHighlights: [
      "Up to four language editions now sit in one horizontal row on the desktop overview instead of one long stack.",
      "The seven daily covers form a compact row on the Rabbit Hole page at desktop widths.",
      "Short two-word topics stay together on the overview when they fit, while long topics still wrap safely.",
      "Desktop overview content uses tighter side spacing so the four editions have more room.",
      "The outer edition cards no longer have a visible border, keeping focus on the cover itself.",
    ],
    technicalNotes: [
      "Added responsive desktop width limits to full and compact edition covers while preserving the existing mobile dimensions.",
      "Reclaimed the overview’s unused desktop column and changed the Rabbit Hole edition grid to four columns at large breakpoints without introducing horizontal page overflow.",
      "Reduced desktop overview side padding from 32px to 12px while preserving the existing mobile inset.",
      "Removed the desktop overview width cap so the edition row keeps only its 12px inset from the side navigation and right content edge on wide screens.",
      "Removed the outer overview-card border while retaining the edition cover’s fine structural edge.",
      "Made editorial two-word splitting configurable so generated titles retain the reference treatment while compact topic headings prefer a natural single line.",
    ],
  },
  {
    id: "procedural-rabbit-hole-edition-covers",
    timestamp: "2026-07-15T12:56:37+0200",
    title: "Rabbit Holes now look like their own weekly editions",
    summary:
      "Rabbit Hole topics and daily discoveries now appear as coordinated editorial covers, with a stable visual family for each week and subtle design signatures for each language.",
    userHighlights: [
      "A cover preview appears while a newly selected Rabbit Hole topic is being prepared.",
      "The overview and Rabbit Hole page now show today’s generated title in a book-cover composition.",
      "All seven daily covers belong visibly to the same weekly edition while still differing from one another.",
      "Long generated titles are balanced across lines instead of being truncated.",
    ],
    technicalNotes: [
      "Added a deterministic seeded design generator with eight palettes, six CSS texture recipes, week families, day variations, and abstract language signatures.",
      "Added a reusable `RabbitHoleEditionCover` for planning, overview, active-week, and daily-card states without new image generation, dependencies, API fields, or database changes.",
      "Added weighted title-layout logic, contrast and determinism unit coverage, plus browser regressions for preview, cross-surface family identity, mobile overflow, and discovery navigation.",
      "Primed pending canonical Rabbit Hole details before navigation and added one bounded start retry so redesigned entry points preserve immediate deferred-generation startup through a transient queue failure.",
    ],
  },
  {
    id: "overview-rabbit-hole-intro-copy-removed",
    timestamp: "2026-07-15T12:16:22+0200",
    title: "The overview now gets to Rabbit Hole content faster",
    summary:
      "The Rabbit Hole cards on the overview now appear without a repeated section title and explanatory sentence above them.",
    userHighlights: [
      "The overview is quieter and puts the current Rabbit Hole content directly in view.",
      "Rabbit Hole cards, progress, planning, and reading actions remain unchanged.",
    ],
    technicalNotes: [
      "Removed the Rabbit Hole heading and supporting paragraph from `src/routes/app/index.tsx` and deleted their unused translation keys.",
      "Extended the existing overview browser regression to verify the removed copy does not return.",
    ],
  },
  {
    id: "italian-speaking-card-now-lives-in-the-italian-language-space",
    timestamp: "2026-07-15T12:15:54+0200",
    title: "Italian speaking practice now stays in the Italian language space",
    summary:
      "The Italian speaking card has moved from the general overview into the Italian text feed, keeping the entry point with the language it belongs to.",
    userHighlights: [
      "The general overview no longer shows an Italian-only feature across every language.",
      "Open the Italian language space to start Italian speaking practice.",
      "Other language spaces keep their feeds focused on their own content.",
    ],
    technicalNotes: [
      "Extracted the existing speaking-practice callout into a dedicated component and removed it from the overview route.",
      "Added the callout to the Reeprise feed only when the active language space is `ITALIAN`.",
      "Updated Playwright coverage for overview absence and Italian-only feed visibility across a real language-space transition.",
    ],
  },
  {
    id: "continue-reading-stream-no-longer-flashes",
    timestamp: "2026-07-15T12:01:21+0200",
    title: "Continue reading now grows without a visual flash",
    summary:
      "Starting a continuation no longer makes the existing reading text briefly duplicate or redraw before new words arrive.",
    userHighlights: [
      "The text already on screen stays stable when Continue reading starts.",
      "New words still stream directly into the same reading body.",
    ],
    technicalNotes: [
      "Updated root-stream reconciliation to recognize equal and prefix-related canonical snapshots before treating stream content as a new continuation.",
      "Added unit and browser transition coverage that checks the original body remains present exactly once through loading, completion, and reload.",
    ],
  },
  {
    id: "text-details-title-now-matches-overview-typeface",
    timestamp: "2026-07-15T11:58:36+0200",
    title: "Text titles now use one consistent editorial typeface",
    summary:
      "The title on text details now uses the same serif typeface as the title shown on the overview page.",
    userHighlights: [
      "Text titles keep the same visual character when moving from the overview into the reading page.",
    ],
    technicalNotes: [
      "Applied the shared Tailwind `font-serif` family to the generated-text details title while preserving its existing size and weight.",
      "Added an overview-to-details browser assertion that compares the computed title font family across the route transition.",
    ],
  },
  {
    id: "continue-reading-restored-as-one-continuous-text",
    timestamp: "2026-07-15T09:44:16+0200",
    title: "Continue reading now extends the same text again",
    summary:
      "Reading questions and separate follow-on blocks have been removed from text details. Continue reading now stays on the same page and extends one continuous saved text.",
    userHighlights: [
      "Continue reading keeps you on the current text details page.",
      "New content appears as part of the same reading text instead of a separate chapter block.",
      "The automatic comprehension question no longer interrupts the reading flow.",
      "Previously split continuations are folded back into their original text.",
    ],
    technicalNotes: [
      "Restored root-text continuation streaming and atomic append persistence in `reeprise/views.py`.",
      "Removed the comprehension endpoint, persistence model, frontend prompt, translations, and obsolete tests.",
      "Added a forward migration that merges saved chapter content, rebases vocabulary offsets, removes obsolete comprehension data, and expands generated-text storage for repeated continuation.",
      "Reworked the details route and focused regressions around one same-page text body, reload persistence, failure safety, and Rabbit Hole context.",
      "Reconciled the final continuation response into the details cache before clearing the live stream so the original text never disappears between completion and refetch.",
    ],
  },
  {
    id: "canonical-text-playback-now-appends-deltas-without-full-body-resets",
    timestamp: "2026-07-13T15:01:30+0200",
    title: "Generated text now streams forward without repeatedly flashing the full body",
    summary:
      "Canonical text playback was stable in content but still replaced the full visible text snapshot on every update, which could make the reading surface flash. Playback now sends each final-text segment as a true append-only delta.",
    userHighlights: [
      "Already visible words stay mounted while only the next text segment is added.",
      "The reading surface no longer receives a full-body reset for every streaming tick.",
      "Interrupted playback can still resume without duplicating text or generating a new draft.",
    ],
    technicalNotes: [
      "Replaced cumulative `reset_stream` playback snapshots with indexed delta chunks in `reeprise/tasks.py`.",
      "Extended the internal WebSocket event with `stream_chunk_index`, allowing the frontend to accept only the next unseen chunk and ignore replayed or out-of-order chunks.",
      "Updated `useStreamingTextIntegrator` so indexed canonical playback never uses replacement semantics after the first event.",
      "Added backend and Vitest regressions for append-only chunk transport and idempotent same-job recovery.",
    ],
  },
  {
    id: "rabbit-hole-continue-reading-now-keeps-week-context-out-of-async-database-lookups",
    timestamp: "2026-07-13T14:54:20+0200",
    title: "Continue reading now completes for Rabbit Hole texts",
    summary:
      "Rabbit Hole texts could stay on the three-dot continuation state and then show an error because their weekly topic was loaded too late during generation. Continue reading now carries that topic safely into generation and persists the follow-on text.",
    userHighlights: [
      "Continue reading no longer fails specifically on Rabbit Hole texts.",
      "The loading dots now resolve into a saved follow-on reading block instead of an error message.",
      "The continuation still stays anchored to the Rabbit Hole topic and keeps the original text unchanged.",
    ],
    technicalNotes: [
      "Captured the production traceback for text `2320`, which showed `SynchronousOnlyOperation` when `_build_topic_guidance()` lazily loaded `rabbit_hole_week` inside `_generate_chapter_impl()`.",
      "Resolved the Rabbit Hole anchor synchronously in both `continue_inline` and the background chapter task, then passed the plain topic string into the async prompt builder.",
      "Changed topic-guidance fallback behavior to use only an already-cached relation so async generation cannot accidentally trigger a synchronous ORM query.",
      "Added backend regression coverage that drives the real continuation generator from the endpoint with a Rabbit Hole text and verifies the follow-on chapter is persisted.",
    ],
  },
  {
    id: "generated-text-streams-now-have-one-canonical-append-only-output",
    timestamp: "2026-07-13T13:07:46+0200",
    title: "Generated texts now stay stable from the first visible words to completion",
    summary:
      "A rapid mobile tap could start several generation jobs for the same text, causing competing drafts to replace words a learner had already begun reading. Each text now has one generation job and one accepted output that is streamed once and saved unchanged.",
    userHighlights: [
      "Visible generated text now only grows while it streams; already-read words are not replaced by another draft.",
      "Repeated taps or duplicate generation requests no longer start competing text jobs.",
      "The text saved at completion is exactly the same text that was streamed to the reading page.",
    ],
    technicalNotes: [
      "Added an atomic generation claim, internal job token, explicit generation phases, staged canonical output, and token-checked worker transitions to `GeneratedText` and its Celery tasks.",
      "Model candidates and validation retries now run without WebSocket publication; only the accepted staged result is playback-streamed, then copied byte-for-byte into `chatgpt_output`.",
      "Made the details route the sole deferred-generation entry point, guarded rapid Rabbit Hole navigation, and added `generation_job_id` filtering plus append-only persisted/stream reconciliation in the frontend.",
      "Added backend, Vitest, and mobile Playwright regressions for duplicate claims and deliveries, stale tokens, staged recovery, competing streams, completion refetches, and rapid taps.",
    ],
  },
  {
    id: "failed-empty-rabbit-hole-texts-now-restart-on-reopen",
    timestamp: "2026-07-13T12:09:03+0200",
    title: "Failed empty Rabbit Hole texts now restart when you open them again",
    summary:
      "Rabbit Hole texts that had already fallen into an empty failed state could show the failed screen forever even though the deferred generation flow was still safe to retry. Empty failed deferred texts now restart on reopen instead of stopping at the failed screen.",
    userHighlights: [
      "A Rabbit Hole text that failed before producing any body text can now recover when you open it again.",
      "The details page no longer treats this empty failed Rabbit Hole state as terminal when a deferred retry is still valid.",
      "This is the follow-up fix for production cases like `#2288`, which moved from endless pending into a failed screen but still needed reopen recovery.",
    ],
    technicalNotes: [
      "Expanded the deferred restart gate in `reeprise/tasks.py` so empty failed Rabbit Hole and import-on-open texts can move back to `PENDING` and re-enqueue generation.",
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` so the details route auto-restarts empty failed deferred texts instead of limiting that behavior to daily texts.",
      "Added backend coverage for failed-empty Rabbit Hole restart and a Playwright regression in `e2e/app-overview.spec.ts` for reopening a failed Rabbit Hole into successful content.",
    ],
  },
  {
    id: "rabbit-hole-generation-no-longer-crashes-on-its-weekly-anchor-inside-the-worker",
    timestamp: "2026-07-13T11:07:13+0200",
    title: "Rabbit Hole generation no longer crashes when a text needs its weekly topic anchor in the worker",
    summary:
      "Some pending Rabbit Hole texts could start generation successfully from the app but still never finish, because the worker crashed while rebuilding the Rabbit Hole topic anchor inside an async generation path. Rabbit Hole generation now resolves that weekly anchor safely before entering the async worker flow.",
    userHighlights: [
      "Opening a pending Rabbit Hole text is less likely to stay stuck in generation when the worker needs the weekly topic context.",
      "Rabbit Hole texts that rely on the weekly anchor topic can now complete without the async worker crashing on relation access.",
      "This fixes the production failure mode behind Rabbit Hole texts that kept retrying generation and never produced content.",
    ],
    technicalNotes: [
      "Updated `reeprise/tasks.py` so `complete_generated_text()` loads Rabbit Hole anchor data in the synchronous task context and passes the plain topic string into the async prompt-building path.",
      "Extended the prompt builders to accept an explicit Rabbit Hole anchor topic instead of relying on lazy ORM relation access inside `_generate_text()`.",
      "Added backend regression coverage in `reeprise/tests.py` for a real Rabbit Hole `complete_generated_text()` run that uses the async generator path and still includes the weekly anchor prompt.",
    ],
  },
  {
    id: "rabbit-hole-open-flow-now-has-browser-regression-coverage-for-immediate-failed-starts",
    timestamp: "2026-07-13T09:31:44+0200",
    title: "Rabbit Hole opening is now covered when generation fails immediately after you open a text",
    summary:
      "Opening a pending Rabbit Hole text now has browser-level regression coverage for the case where generation cannot be started and the backend returns a failed state immediately. This protects the route transition where the details screen should leave loading right away instead of waiting on a slower refetch.",
    userHighlights: [
      "Rabbit Hole opening is now checked against the real in-browser navigation flow when a pending text flips straight into a failed state.",
      "The details page is now regression-tested to show the failed state immediately instead of hanging on loading while a slower details request finishes.",
      "This adds stronger protection against the exact class of issue where a Rabbit Hole text appears stuck after opening.",
    ],
    technicalNotes: [
      "Added a Playwright regression in `e2e/app-overview.spec.ts` that opens a pending Rabbit Hole discovery, returns `FAILED` from `start-generation`, delays the details refetch, and asserts the failed screen renders before that delayed refetch completes.",
      "Updated Rabbit Hole QA traceability so the deferred-open cache-sync risk now includes browser-level coverage in addition to the focused React Query cache unit test.",
      "Kept the existing frontend cache-sync fix in place and extended validation to the actual route transition seen by learners.",
    ],
  },
  {
    id: "rabbit-hole-open-now-uses-fresh-generation-state-instead-of-stale-pending-cache",
    timestamp: "2026-07-13T08:53:13+0200",
    title: "Rabbit Hole texts now open with the latest generation state instead of an outdated loading state",
    summary:
      "Opening a Rabbit Hole text could keep the details page on the three-dot generation state even after the backend had already returned a newer status for that text. The app now applies the fresh `start-generation` response to the details cache immediately before the page settles.",
    userHighlights: [
      "Opening a Rabbit Hole text is less likely to get stuck on the three-dot loading state after navigation.",
      "If generation cannot be started, the details page can switch to the newer failure state immediately instead of continuing to show stale pending UI.",
      "Rabbit Hole detail opening now stays in sync with the backend response from the moment generation is requested.",
    ],
    technicalNotes: [
      "Updated `src/hooks/useDailyTextStartGeneration.ts` so the `start-generation` mutation writes its returned generated-text payload directly into the details query cache before the usual invalidation pass.",
      "Added frontend regression coverage in `src/hooks/useDailyTextStartGeneration.test.ts` for replacing stale pending Rabbit Hole details with a fresh failed start-generation response.",
      "Updated Rabbit Hole QA documentation and risk notes to record the deferred-open cache-sync failure mode on the details route.",
    ],
  },
  {
    id: "italian-roleplay-first-recording-now-keeps-a-single-control-and-captures-immediate-speech",
    timestamp: "2026-07-13T08:51:15+0200",
    title: "Italian roleplay first recordings now stay visually clear and capture immediate speech",
    summary:
      "Starting with a spoken topic could briefly show the idle microphone while recording was still stopping, and speaking immediately on the first Italian turn could be mistaken for background noise. Recording now keeps one clear control through shutdown and uses a quieter baseline for first-turn speech detection.",
    userHighlights: [
      "Topic recording now shows one microphone control at a time instead of exposing the blue start state before recording has fully stopped.",
      "The first Italian reply is less likely to produce a false no-speech message when you begin speaking immediately after tapping the microphone.",
      "After stopping, the microphone stays in a processing state until the captured audio has been finalized and transcribed.",
    ],
    technicalNotes: [
      "Kept topic dictation in its listening state until the browser SpeechRecognition or MediaRecorder stop callback completes, and flushes fallback recorder data before stopping.",
      "Changed roleplay recording shutdown to enter the existing transcribing state before requesting the final MediaRecorder chunk, preventing a second recording from starting during blob finalization.",
      "Recalibrated browser-side voice activity detection from the quietest initial samples with a lower capped threshold so immediate speech is not learned as ambient noise.",
      "Added a Playwright regression covering spoken-topic entry, single-control recording states, first-turn audio upload, and successful transcript submission without a no-speech error.",
    ],
  },
  {
    id: "italian-roleplay-mixed-language-voice-now-keeps-the-final-return-to-dialogue-prompt",
    timestamp: "2026-07-12T15:46:40+0200",
    title: "Italian roleplay mixed-language voice now keeps the final return-to-dialogue prompt",
    summary:
      "Mixed German-and-Italian assistant replies in Italian roleplay could lose the final short Italian sentence in speech, especially when the reply ended by steering the learner back into the dialogue. The speech endpoint now keeps the mixed-language text intact, isolates a short trailing sentence into its own TTS chunk, and stitches the result back into one MP3 before playback.",
    userHighlights: [
      "Mixed-language helper replies are less likely to stop before the final Italian question.",
      "German explanations still work, but the return to the live Italian dialogue is now spoken more reliably.",
      "Assistant playback still reaches the app as one audio response instead of a visibly segmented queue.",
    ],
    technicalNotes: [
      "Added conservative sentence-aware chunk preparation in `reeprise/views.py` so a short trailing sentence such as `Torniamo alla borsa: vuoi provarla?` is synthesized separately instead of being left at the tail of one larger mixed-language TTS request.",
      "Added backend MP3 stitching in the same view path using `ffmpeg concat`, with a naive byte-append fallback if concat is unavailable.",
      "Kept the original mixed-language text unchanged for the model and only changed the TTS transport layer.",
      "Added backend regression coverage in `reeprise/tests.py` for a mixed German/Italian reply whose final short Italian sentence must remain audible.",
    ],
  },
  {
    id: "continue-reading-now-streams-as-a-separate-follow-on-block",
    timestamp: "2026-07-12T13:33:26+0200",
    title: "Continue reading now streams as its own follow-on block",
    summary:
      "Continuing a text no longer rewrites the original article while new content streams in. The original text now stays fixed, and the continuation appears below it as a separate reading block that still follows the same topic and context.",
    userHighlights: [
      "The original text now stays unchanged while a continuation is generated.",
      "Only the new continuation block streams, so the reading page no longer mixes old and new text into one unstable body.",
      "If you keep reading, each follow-on block stays tied to the previous text instead of starting a disconnected new article.",
    ],
    technicalNotes: [
      "Reworked `continue_inline` in `barkguide/reeprise/views.py` to persist continuations as real `Chapter` records instead of appending streamed output back into `GeneratedText.chatgpt_output`.",
      "Continuation generation now streams on the chapter websocket key again, so one rendered block maps to one persisted object and one stream.",
      "Updated `GeneratedTextSerializer` and `src/routes/app/reeprise/$generatedTextId.tsx` to return and render stored continuation chapters below the original text on reload.",
      "Added backend regression coverage for separate continuation chapter persistence and a focused Playwright continuation regression that keeps the original article visible only once while the follow-on block renders underneath.",
    ],
  },
  {
    id: "rabbit-hole-planning-now-offers-clarifying-topic-refinements",
    timestamp: "2026-07-12T13:11:22+0200",
    title: "Rabbit Hole planning now helps you narrow ambiguous topics before the week starts",
    summary:
      "If you enter an ambiguous Rabbit Hole topic such as `techno`, the planner now pauses and offers a few sharper directions before it creates the week. You can still keep the broad topic, but the app no longer has to guess immediately from a word that could lead in several directions.",
    userHighlights: [
      "Typing a broad topic like `techno` now opens a quick refinement step instead of starting the week immediately.",
      "You can pick a more specific direction with one tap or keep the broad topic if that is what you want.",
      "The same clarification help also appears when you rename the current Rabbit Hole topic or save next week’s topic.",
    ],
    technicalNotes: [
      "Added a frontend Rabbit Hole topic-refinement helper in `src/utils/rabbitHoleTopicRefinement.ts` with initial ambiguity handling for `techno`.",
      "Updated `src/routes/app/rabbit_hole.tsx` so planning, current-topic edits, and next-topic edits pause for clarification when the entered topic matches a known ambiguous term.",
      "Added new translation keys for the refinement prompt and keep-broad action in `src/assets/translations.json`.",
      "Added unit coverage in `src/utils/rabbitHoleTopicRefinement.test.ts` and Playwright coverage in `e2e/rabbit-hole-planning.spec.ts` for the clarification step before Rabbit Hole creation.",
    ],
  },
  {
    id: "rabbit-hole-topic-refinement-now-checks-wikipedia-disambiguation",
    timestamp: "2026-07-17T12:22:00+0200",
    title: "Rabbit Hole topic refinement now uses Wikipedia to detect ambiguous topics",
    summary:
      "The hardcoded ambiguous-topic list (`techno`) still works but is no longer the only defence. When you type a topic like `Zug`, `Java`, or `Mercury`, the planner now asks Wikipedia whether the word has multiple meanings before creating your week. If Wikipedia knows a disambiguation page for the term, you will see curated refinement suggestions without waiting for generation to drift into the wrong direction.",
    userHighlights: [
      "Entering a topic that could mean several things (e.g. `Zug` — a Swiss city, a train, or a chess move) now pauses and offers Wikipedia-sourced alternatives before week creation.",
      "The same disambiguation check runs when you rename the current Rabbit Hole topic or save next week’s topic.",
      "If Wikipedia is unreachable or the term is unambiguous, the week starts normally with no extra step.",
      "The existing hardcoded list for `techno` still works instantly and takes priority over the Wikipedia check.",
    ],
    technicalNotes: [
      "Added `fetchWikipediaRefinement()` in `src/utils/rabbitHoleTopicRefinement.ts` that first checks for a Wikipedia disambiguation page (`action=query` with `_(disambiguation)` suffix) and then fetches suggestions via `action=opensearch`.",
      "Wired the async check into all three Rabbit Hole topic-entry points in `src/routes/app/rabbit_hole.tsx` (planning, current-topic rename, next-topic save), each with a 5-second `AbortController` timeout and graceful fallthrough to normal creation on any network error.",
      "Added unit coverage in `src/utils/rabbitHoleTopicRefinement.test.ts` for Wikipedia disambiguation detection, empty results, network failures, and the existing hardcoded path.",
    ],
  },
  {
    id: "italian-roleplay-assistant-voice-no-longer-drops-late-playback-tail-due-to-stale-audio-events",
    timestamp: "2026-07-12T13:16:00+0200",
    title: "Italian roleplay assistant voice now resists late audio callbacks cutting playback short",
    summary:
      "Assistant playback in Italian roleplay could get interrupted when an older audio element finished or paused after a newer reply had already started. The dialogue voice path now keeps the latest playback isolated, adds browser-side audio diagnostics, and attempts one guarded resume if a live reply pauses unexpectedly before the end.",
    userHighlights: [
      "Assistant voice replies are less likely to cut off near the end because of overlapping browser audio events from an older reply.",
      "Replay and consecutive assistant turns now keep the newest voice reply active instead of letting stale audio callbacks clear it.",
      "When the browser pauses a reply unexpectedly before the end, the app now makes one safe resume attempt instead of silently giving up immediately.",
    ],
    technicalNotes: [
      "Tagged each assistant playback instance in `src/routes/app/dialogue.tsx` so stale `ended` and `error` callbacks from older audio elements can no longer clear the active playback state for a newer reply.",
      "Added browser-side TTS diagnostics for request start, blob receipt, metadata load, pause, ended, error, cleanup, and explicit stop reasons so local `.logs/ui.log` captures the full assistant-audio lifecycle.",
      "Preserved the original playback handlers when Safari-style autoplay blocking stores a pending audio element, so one-tap retry resumes the same guarded lifecycle instead of replacing it with a simpler cleanup path.",
      "Added a guarded one-time resume attempt when a still-active assistant reply pauses unexpectedly before the final 0.2 seconds.",
      "Added Playwright regression coverage in `e2e/italian-roleplay.spec.ts` for the case where an older assistant audio callback finishes after a newer reply has already started.",
    ],
  },
  {
    id: "generated-text-word-selection-now-keeps-highlighting-to-the-clicked-word",
    timestamp: "2026-07-12T13:15:06+0200",
    title: "Generated-text word selection now keeps the highlight on the word you clicked",
    summary:
      "Clicking a word in a generated Rabbit Hole text could highlight several unrelated words at once because the reader treated loosely similar tokens as matches. Word highlighting now stays exact, so opening a translation or saving vocabulary no longer lights up unrelated parts of the text.",
    userHighlights: [
      "Tapping one word in a reading text now highlights only that word and exact repeats instead of visually scattering across the paragraph.",
      "Opening the vocabulary preview or jumping back from the vocabulary list feels more stable on noisy generated text.",
      "The reading surface is less likely to look broken after a single vocabulary interaction.",
    ],
    technicalNotes: [
      "Reworked `findWordHighlights` in `src/components/GeneratedTextDetailsTextPane.tsx` to use exact normalized token matching instead of the previous prefix-similarity fallback.",
      "Added a frontend regression test in `src/components/GeneratedTextDetailsTextPane.test.tsx` that proves a single focus word no longer fans out into unrelated highlighted tokens.",
      "Updated the generated-text QA knowledge base and traceability docs to record the vocabulary-highlight regression and its new unit coverage.",
    ],
  },
  {
    id: "inline-continuation-stream-no-longer-overwrites-rabbit-hole-body",
    timestamp: "2026-07-12T13:13:00+0200",
    title: "Rabbit Hole continuation streaming no longer corrupts the visible article body",
    summary:
      "Inline continuation streaming could temporarily replace the original Rabbit Hole article body with continuation-only websocket text, which made paragraphs look duplicated or spliced together while the text was still streaming. The reading page now keeps the original article body stable and shows streamed continuation text only in its own continuation section.",
    userHighlights: [
      "Continuing a Rabbit Hole text no longer makes the original article body jump into mixed or duplicated sentences while new text streams in.",
      "The already visible article stays stable during inline continuation instead of being replaced by continuation-only chunks.",
      "New continuation text still appears live, but it stays isolated to the continuation block until the full updated text is saved.",
    ],
    technicalNotes: [
      "Added `resolveRootGeneratedTextRaw(...)` in `src/utils/generatedTextContent.ts` so root-text rendering keeps persisted article content canonical whenever an original root text is available.",
      "Updated `NoChapterText` in `src/components/GeneratedTextDetailsTextPane.tsx` to stop preferring a longer root websocket payload over the persisted article during inline continuation.",
      "Added unit regression coverage in `src/utils/generatedTextContent.test.ts` for the case where continuation-only streamed text becomes longer than the original root article.",
      "Updated generated-text-details and feed QA documentation plus streaming risk notes to capture this inline-continuation body-corruption failure mode.",
    ],
  },
  {
    id: "rabbit-hole-techno-topic-now-stays-anchored-to-the-chosen-domain",
    timestamp: "2026-07-12T13:11:22+0200",
    title: "Rabbit Hole day titles now stay anchored to your chosen topic",
    summary:
      "Rabbit Hole generation could drift when a generated day title was broader than the topic you chose. Weekly tracks such as `techno` now keep that original domain as an explicit anchor during article generation, so later days are less likely to slide into unrelated topics like generic technology.",
    userHighlights: [
      "A Rabbit Hole picked for `techno` is now less likely to turn into a text about `technology` on a later day.",
      "Daily Rabbit Hole articles keep using the weekly topic as the main domain even when the visible title is more editorial or abstract.",
      "Topic continuity across the 7-day Rabbit Hole should feel more trustworthy on ambiguous or multi-meaning terms.",
    ],
    technicalNotes: [
      "Updated Rabbit Hole prompt construction in `barkguide/reeprise/tasks.py` so body generation keeps the weekly Rabbit Hole topic as an explicit domain anchor even after the visible day title has replaced the original draft topic.",
      "Added a homonym-drift guard in the same prompt path so terms like `techno` stay on the learner-selected meaning instead of broadening into adjacent domains.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` for Rabbit Hole prompt generation with an ambiguous day title and a weekly anchor topic.",
      "Updated the Rabbit Hole QA knowledge-base entries and risk mapping to document the relevance-drift failure mode and current test coverage.",
    ],
  },
  {
    id: "italian-roleplay-no-longer-keeps-speaking-while-opening-the-mic",
    timestamp: "2026-07-11T14:13:40+0200",
    title: "Italian roleplay now clears the AI voice before opening your mic",
    summary:
      "Starting your first spoken reply in Italian roleplay could fail with a false `Keine Sprache erkannt` message when assistant playback was still active. The dialogue handoff now stops the assistant voice first, then opens the microphone cleanly.",
    userHighlights: [
      "After the AI asks its first question, starting the mic no longer keeps the previous voice reply running underneath.",
      "The first learner turn is less likely to fail with a false no-speech error right after assistant playback.",
      "Roleplay turn-taking now behaves more like a clean handoff instead of overlapping playback and recording.",
    ],
    technicalNotes: [
      "Added a shared assistant-audio teardown path in `src/routes/app/dialogue.tsx` and reused it before roleplay mic startup, on replay transitions, and when leaving the surface.",
      "Wired the roleplay mic start flow to stop active or pending assistant audio before requesting microphone access.",
      "Changed the roleplay `MediaRecorder` path to flush data before stop and emit chunks continuously during capture, reducing false empty-recording cases on short or timing-sensitive turns.",
      "Raised the browser-side silence-stop confidence by requiring a short span of detected speech before auto-stop can commit a turn, reducing cutoffs from single noise spikes.",
      "Removed the backend short-audio rejection shortcut in `reeprise/views.py` so small but non-empty uploads still reach transcription.",
      "Added a backend retry path for roleplay transcription that falls back to a looser no-hints pass if the first hint-aware attempt returns empty.",
      "Added Playwright regression coverage in `e2e/italian-roleplay.spec.ts` for the assistant-audio-to-mic handoff.",
    ],
  },
  {
    id: "overview-refresh-no-longer-blocked-by-bulk-title-translation",
    timestamp: "2026-07-11T12:18:56+0200",
    title: "Overview refresh opens immediately again instead of waiting on title translation work",
    summary:
      "Refreshing the overview had started fanning out multiple model-backed title translation requests before the page felt ready. The overview now paints immediately again and limits first-load translation work to the featured title only.",
    userHighlights: [
      "Refreshing `/app` no longer waits on several background title translations before the page feels usable.",
      "The featured overview card still keeps its translated subtitle when available, but the rest of the page no longer depends on translation requests to appear.",
      "Rabbit Hole overview cards now open with their source titles directly instead of doing extra translation work during page boot.",
    ],
    technicalNotes: [
      "Removed the overview-wide daily-title translation prefetch effect from `src/routes/app/index.tsx`, which had been calling `/api/reeprise/translate/` once per same-day daily title on mount.",
      "Removed the Rabbit Hole overview subtitle translation fan-out from the same route so `/app` no longer does extra model-backed title work for every visible language on refresh.",
      "Changed the featured hero render path to paint immediately with the source title while keeping the existing single-title subtitle translation fetch and local cache for the featured card only.",
      "Added Playwright regression coverage in `e2e/app-overview.spec.ts` asserting overview first paint triggers only one translation request.",
    ],
  },
  {
    id: "roleplay-dialogue-simplified-to-single-responses-api-call",
    timestamp: "2026-07-10T20:30:05+0200",
    title: "Italian roleplay now feels like talking to a bilingual friend instead of a teacher",
    summary:
      "The Italian conversation practice engine was rebuilt to use a single OpenAI Responses API call per user message, removing the layered correction/repetition/classification pipeline that forced unnatural teacher-like behavior. The AI now behaves like a bilingual friend: Italian is the main language, German help is always available, mistakes are treated casually, and the conversation flows naturally without forced repetition.",
    userHighlights: [
      "The AI no longer forces you to repeat corrected sentences when your meaning was clear.",
      "You can ask questions in German mid-conversation and the AI switches naturally, then continues in Italian.",
      "Mistakes that don't change the meaning are now ignored, while genuinely confusing errors get a friendly native-style correction like 'Ah, du meinst...'",
      "The AI adapts its Italian level to your saved language level instead of guessing.",
      "The conversation remembers facts you share (allergies, preferences, constraints) throughout the session.",
      "Behind the scenes, the model upgraded from gpt-4o-mini to gpt-4.1-mini for more reliable bilingual behavior.",
    ],
    technicalNotes: [
      "Rewrote `roleplay_turn` in `barkguide/reeprise/views.py` from a ~240-line Chat Completions pipeline (transcription correction → intent classification → help-level escalation → JSON extraction → branch tracking) to a single ~50-line Responses API call with clean conversation history.",
      "Added `BASE_ROLEPLAY_PROMPT`, `_build_roleplay_instructions`, and `_build_clean_messages` helper functions with a reusable bilingual prompt and dynamic situation context injection.",
      "Added `conversation_facts` JSON field to `SpeakingSession` model so user-provided facts persist across turns and are injected into the prompt.",
      "Added CEFR level injection from `UserSetting.language_levels` into the roleplay instructions.",
      "Added a staff-only debug panel to the dialogue UI showing model name, full instructions, conversation history, raw user transcript, raw model response, and final rendered response.",
      "Added `OPENAI_ROLEPLAY_MODEL` setting (defaulting to `gpt-4.1-mini`) configurable via environment variable.",
      "Added 9 deterministic backend tests (`test_roleplay_simplified_*`) verifying single API call, dynamic situation context, German input preservation, clean history format, no duplicate messages, direct output usage, empty output fallback, and debug data protection.",
      "Added 6 behavioral regression tests (`test_roleplay_beh_*`) covering German vocabulary questions, alternative comprehension phrasing, unusual requests, short German questions, language switching, and 6 parametrized situations.",
      "Updated existing roleplay tests to mock `responses.create` instead of `chat.completions.create`.",
      "Updated E2E test with a new 'German question mid-conversation' scenario.",
      "Temporarily disabled: transcription correction, intent classification, help-level escalation, exact-sentence comparison, forced repetition, automatic teaching-mode switching, and secondary model calls that rewrite or reevaluate the answer.",
    ],
  },
  {
    id: "desktop-sidebar-scrollbar-now-only-appears-on-hover",
    timestamp: "2026-07-09T11:41:13+0200",
    title: "Desktop sidebar scrollbar now stays hidden until you use it",
    summary:
      "The desktop sidebar remains scrollable, but its scrollbar only appears when you hover or focus inside the sidebar instead of staying visible all the time.",
    userHighlights: [
      "You can still scroll the desktop sidebar normally with wheel or touchpad.",
      "The scrollbar no longer sits visibly in the sidebar when you are not interacting with it.",
      "Hovering the sidebar reveals the scrollbar again when you need orientation or drag control.",
    ],
    technicalNotes: [
      "Added a dedicated `side-nav-hover-scrollbar` utility in `src/index.css` with hidden default scrollbar styling and hover/focus-within reveal behavior.",
      "Applied that utility to the desktop side-navigation panel in `src/routes/app/route.tsx` while keeping `overflow-y-auto` scrolling behavior unchanged.",
      "Re-ran the frontend production build after the sidebar scrollbar interaction change.",
    ],
  },
  {
    id: "desktop-sidebar-now-reads-like-a-plain-space-list",
    timestamp: "2026-07-09T11:31:48+0200",
    title: "Desktop sidebar now stays out of reading mode and switches spaces more directly",
    summary:
      "The desktop app shell now uses a plainer, more utilitarian sidebar: text details open without the desktop sidebar at all, and the remaining desktop nav treats language spaces like a simple list instead of a decorative icon rail.",
    userHighlights: [
      "Opening a text detail on desktop now hides the sidebar completely so the reading page can use the full app shell without a persistent left rail.",
      "On desktop pages outside reading mode, the sidebar now lists your language spaces directly, so switching between them feels closer to moving between folders.",
      "The desktop sidebar styling is now calmer and more functional, with a narrower width, simpler rows, and fewer ornamental treatments.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/route.tsx` so the desktop sidebar and its divider are not rendered on `/app/reeprise/$generatedTextId` routes.",
      "Reworked the desktop sidebar primary section to use direct language-space navigation entries instead of the previous accent-heavy compact rail treatment, while keeping the existing route/state model.",
      "Added Playwright coverage in `e2e/classic-tools-nav.spec.ts` for desktop reading-mode sidebar removal and desktop side-nav language-space switching.",
    ],
  },
  {
    id: "tablet-responsive-text-detail-layout",
    timestamp: "2026-07-09T09:58:00+0200",
    title: "Text detail page now adapts properly on tablet screens",
    summary:
      "The text detail layout previously forced a side-by-side reading panel and vocabulary column starting at 768px, making both far too narrow on tablet devices. Now the page uses the full width for reading on smaller screens and only switches to side-by-side at wider breakpoints with more generous column widths.",
    userHighlights: [
      "On tablets below 1024px, the text now fills the full reading width and vocabulary cards stack below — no more squeezed content.",
      "At 1024px and wider, the reading pane gets two-thirds of the available width and the vocabulary sidebar gets one-third, with padding that adapts to the column width.",
      "On larger desktop screens (1280px+), the layout further refines to three-fifths reading and two-fifths vocabulary.",
    ],
    technicalNotes: [
      "Shifted the grid activation breakpoint in `src/routes/app/reeprise/$generatedTextId.tsx` from `md:` (768px) to `lg:` (1024px) with a 3-column grid (`lg:grid-cols-3`), giving text `col-span-2` and vocab `col-span-1`.",
      "Added `xl:grid-cols-5` with `xl:gap-8` at 1280px+ for a refined three-fifths-to-two-fifths split.",
      "Removed the `md:mr-10` right margin that wasted horizontal space on narrow viewports.",
      "Updated `src/components/VocabularyItemCard.tsx` to use `p-3 sm:p-4 lg:p-3 xl:p-4`, reducing card padding when the sidebar column is narrower at tablet landscape widths.",
    ],
  },
  {
    id: "deferred-start-generation-enqueue-failures-no-longer-stay-pending",
    timestamp: "2026-07-09T11:50:00+0200",
    title: "Deferred text starts now fail cleanly instead of spinning forever",
    summary:
      "If the app cannot queue a pending daily or Rabbit Hole text for background generation, the text no longer stays in a misleading pending state forever.",
    userHighlights: [
      "Pending text details no longer look permanently stuck when background generation cannot be queued.",
      "Rabbit Hole and daily drafts now move into a real failed state instead of silently staying pending.",
      "The visible title can stay in place while the app surfaces a real failure instead of an endless spinner.",
    ],
    technicalNotes: [
      "Added guarded enqueue handling in `barkguide/reeprise/tasks.py` for deferred `start-generation` flows before calling `complete_generated_text.apply_async(...)`.",
      "When the deferred enqueue fails, the backend now marks the generated text as `FAILED` and stores a fallback message instead of leaving the draft in silent `PENDING` limbo.",
      "Added `barkguide/reeprise/tests.py::test_start_generation_marks_deferred_text_failed_when_enqueue_fails` to cover the Rabbit Hole deferred-start broker-failure path.",
    ],
  },
  {
    id: "deferred-text-start-generation-retries-after-transient-failure",
    timestamp: "2026-07-09T09:42:00+0200",
    title: "Pending text details now retry generation after a transient start failure",
    summary:
      "Opening a pending deferred text no longer gets stuck forever if the first background generation-start request fails once.",
    userHighlights: [
      "Pending Rabbit Hole and daily texts can recover from a temporary start-generation failure without needing a full remount.",
      "The visible text title stays in place while the details page retries the background start.",
      "A single failed start attempt no longer leaves a pending text frozen indefinitely.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` so deferred text start-generation tracking now distinguishes in-flight and succeeded request keys instead of permanently latching after the first attempt.",
      "Failed `start-generation` requests now clear the in-flight marker so the next pending-details poll can retry automatically.",
      "Added a Playwright regression in `e2e/app-overview.spec.ts` that forces the first Rabbit Hole `start-generation` request to fail and verifies the details page retries successfully.",
    ],
  },
  {
    id: "overview-whats-next-rabbit-hole-rows-now-open-articles",
    timestamp: "2026-07-09T09:02:36+0200",
    title: "Rabbit Hole `What's next` rows now open the article directly",
    summary:
      "The article rows inside the Rabbit Hole `What's next` section on the overview page are now clickable and follow the same open behavior as the rest of the reading flow.",
    userHighlights: [
      "Tapping an already generated Rabbit Hole article in `What's next` now opens that article immediately.",
      "If a valid Rabbit Hole article is listed there but has not been generated yet, the app now starts generation and moves into the article page instead of doing nothing.",
      "Locked later Rabbit Hole entries still stay non-interactive.",
    ],
    technicalNotes: [
      "Turned the expanded Rabbit Hole overview slot rows in `src/routes/app/index.tsx` into conditional interactive entries with shared discovery-open behavior.",
      "The overview now resolves the clicked Rabbit Hole discovery against the canonical generated-text list before deciding whether to prime details and navigate or call `start-generation` first.",
      "Added Playwright regressions in `e2e/app-overview.spec.ts` for both an existing completed `What's next` article and a pending valid `What's next` article.",
    ],
  },
  {
    id: "overview-existing-text-opens-instantly-on-mobile",
    timestamp: "2026-07-09T11:18:00+0200",
    title: "Overview reading cards now open immediately on mobile",
    summary:
      "Tapping an existing reading card from the overview now moves straight into text details instead of feeling unresponsive while the app waits on background work.",
    userHighlights: [
      "Existing daily and continue-reading entries from overview now open on the first tap.",
      "The text details page can show the known title immediately while background generation or refresh work continues there.",
      "Mobile overview taps now feel like a direct page transition instead of a delayed async action.",
    ],
    technicalNotes: [
      "Changed overview task open controls in `src/routes/app/index.tsx` from async-only buttons to direct TanStack Router `Link` navigation whenever a generated text already exists.",
      "Primed the generated-text details query before overview navigation for existing daily and continue-reading entries so the details route starts with local title/data instead of waiting for the first fetch.",
      "Added a mobile Playwright regression in `e2e/app-overview.spec.ts` that delays the details response and verifies the route still changes immediately after tapping the featured overview entry.",
    ],
  },
  {
    id: "overview-rabbit-hole-mobile-expansion-no-longer-overflows",
    timestamp: "2026-07-08T09:24:00+0200",
    title: "Overview Rabbit Hole cards now stay inside the screen on mobile",
    summary:
      "Opening the inline Rabbit Hole week plan on the overview page no longer pushes the layout wider than the phone screen.",
    userHighlights: [
      "Expanding `Upcoming texts` on a Rabbit Hole card now stays within the mobile viewport.",
      "Longer Rabbit Hole topics and discovery titles wrap or clip safely instead of creating sideways page overflow.",
      "The overview remains readable without horizontal scrolling after opening the card.",
    ],
    technicalNotes: [
      "Tightened width constraints in `src/routes/app/index.tsx` for the overview Rabbit Hole card, its header row, toggle row, and expanded week-plan items by adding `min-w-0`, `overflow-hidden`, and safer text wrapping.",
      "Restored the `overview-rabbit-hole-manage-*` test id on the planner button so the existing overview route test keeps targeting the same control.",
      "Added a mobile Playwright regression in `e2e/app-overview.spec.ts` that expands the Rabbit Hole card at `390x844` and asserts the page does not gain horizontal overflow.",
    ],
  },
  {
    id: "rabbit-hole-weekly-review-now-shows-vocabulary-cards",
    timestamp: "2026-07-08T08:55:18+0200",
    title: "Rabbit Hole weekly completion now shows vocabulary cards inline",
    summary:
      "When you finish a Rabbit Hole week, the completion page now displays the actual vocabulary cards from that week instead of only counts and a link to the general vocabulary page.",
    userHighlights: [
      "Finished Rabbit Hole weeks now show the word list directly on the completion page.",
      "Each vocabulary card works the same as on the text details page: tap to reveal translations, view source sentences, structure details, and edit mnemonics.",
      "The `Review this week's words` button that linked to the full vocabulary list has been removed — the words are now right there.",
    ],
    technicalNotes: [
      "Extracted the shared `VocabularyItemCard` component from `src/routes/app/reeprise/$generatedTextId.tsx` into `src/components/VocabularyItemCard.tsx` so both the text details page and the Rabbit Hole completion page can render the same vocabulary cards.",
      "Added `rabbit_hole_week_id` query parameter to the `GET /api/reeprise/vocabulary/` endpoint and `usePaginatedVocabularyList` hook to filter vocabulary by Rabbit Hole week.",
      "The `WeekCompleteState` in `src/routes/app/rabbit_hole.tsx` now fetches and renders vocabulary cards with pagination support, replacing the removed button to `/app/vocabulary_progress`.",
      "Added new translation keys `rabbit_hole_week_vocabulary_title`, `rabbit_hole_week_vocabulary_empty`, and `rabbit_hole_week_vocabulary_loading`.",
    ],
  },
  {
    id: "rabbit-hole-reopen-now-uses-existing-text-immediately",
    timestamp: "2026-07-07T21:41:00+0200",
    title: "Rabbit Hole texts reopen immediately when they already exist",
    summary:
      "Opening a Rabbit Hole text from the overview or the Rabbit Hole page now shows the existing text right away when it has already been generated, instead of getting stuck on a loading spinner.",
    userHighlights: [
      "If you already opened a Rabbit Hole text through `Jetzt lesen`, reopening the same text from overview now shows it immediately.",
      "Overview and Rabbit Hole management now treat the same text as one shared reading entry instead of acting like generation still needs to start again.",
      "Pending Rabbit Hole texts still open with their visible title while generation continues in the background.",
    ],
    technicalNotes: [
      "Added canonical Rabbit Hole discovery resolution so `src/routes/app/index.tsx` and `src/routes/app/rabbit_hole.tsx` prefer the matching generated-text record before deciding whether to call `start-generation`.",
      "Primed the generated-text details query with the existing complete text before navigation, which lets the details route render body content immediately when the text already exists.",
      "Removed duplicate `useGeneratedTextDetails(...)` subscriptions inside `src/components/GeneratedTextDetailsTextPane.tsx` by passing the already-fetched completion metadata down into `NoChapterText` and `ChapterText`.",
    ],
  },
  {
    id: "landing-page-language-switch-now-uses-de-and-en-labels",
    timestamp: "2026-07-07T14:40:25+0200",
    title: "Landing page language switch now reads DE and EN",
    summary:
      "The public homepage language switch now shows `DE` and `EN` as its visible options, matching the tighter navigation style.",
    userHighlights: [
      "The language switch now uses the shorter `DE` and `EN` labels.",
      "The switch is easier to scan in the compact top navigation row.",
      "Language switching behavior itself stays the same.",
    ],
    technicalNotes: [
      "Updated the native select option labels in `src/routes/index.tsx` from lowercase `en` and `de` to uppercase `EN` and `DE`.",
      "Left the stored values (`ENGLISH` and `GERMAN`) unchanged, so existing local-language behavior continues to work.",
      "Did not change any translation keys or switching logic.",
    ],
  },
  {
    id: "landing-page-outer-desktop-gutters-removed",
    timestamp: "2026-07-07T14:39:42+0200",
    title: "Landing page now sits flush to the desktop edges",
    summary:
      "The extra outer left and right desktop padding around the public homepage has been removed, so the page composition now starts closer to the screen edges again.",
    userHighlights: [
      "The homepage no longer has the added outer white gutters on the left and right.",
      "The internal spacing inside cards and columns stays intact.",
      "The overall composition feels more direct and less boxed in.",
    ],
    technicalNotes: [
      "Removed the `lg:px-5` wrapper padding from `src/routes/index.tsx` while keeping the existing desktop grid, card spacing, and bottom padding.",
      "Left the internal column and card padding unchanged so only the page-level gutters were affected.",
      "Kept the rest of the homepage layout and language-switch header structure intact.",
    ],
  },
  {
    id: "landing-page-language-switch-moved-into-top-nav-row",
    timestamp: "2026-07-07T14:39:00+0200",
    title: "Landing page language switch now sits in the top nav row",
    summary:
      "The public homepage language switch now shares the same top line as the website links, so the left header feels cleaner and more deliberate.",
    userHighlights: [
      "The language switch now sits beside About, Pricing, and the app link instead of floating on a separate row.",
      "The header reads more cleanly from left to right before the brand and intro copy begin.",
      "The rest of the homepage layout stays unchanged.",
    ],
    technicalNotes: [
      "Updated `src/routes/index.tsx` so the top website links and language switch render inside one shared flex row.",
      "Moved the `Uxhulu` brand line below that combined nav row without changing translation behavior or onboarding actions.",
      "Kept the existing language-switch control and styling intact while changing only its placement.",
    ],
  },
  {
    id: "landing-page-desktop-layout-fits-one-screen-again",
    timestamp: "2026-07-07T14:35:40+0200",
    title: "Landing page fits back into one desktop screen",
    summary:
      "The public homepage no longer relies on a scrollable desktop card column. Its content is compressed back into a fixed one-screen composition, so the first page reads as a complete story again.",
    userHighlights: [
      "The homepage fits back into one desktop screen instead of continuing inside a nested scroll area.",
      "The visual story stays intact, but the cards are tighter and quicker to scan.",
      "Mobile behavior remains stacked and readable.",
    ],
    technicalNotes: [
      "Reworked `src/routes/index.tsx` so the right-side landing cards use a fixed two-row desktop grid instead of a scrollable vertical stack.",
      "Reduced desktop spacing, heading scale, copy density, and preview image height to keep the homepage within the viewport.",
      "Trimmed the lexicon card to two visible entries on the website root to preserve the one-screen composition without changing app behavior elsewhere.",
    ],
  },
  {
    id: "landing-page-left-column-cards-removed-again",
    timestamp: "2026-07-07T14:32:47+0200",
    title: "Landing page intro column is simplified again",
    summary:
      "The extra Rabbit Hole-style cards beneath the main homepage button have been removed again, leaving the intro column focused on navigation, product framing, and the main call to action.",
    userHighlights: [
      "The left homepage column is quieter again and no longer continues with stacked discovery cards.",
      "The main website intro now stays focused on the core message and start action.",
      "Language switching still updates the remaining homepage copy immediately.",
    ],
    technicalNotes: [
      "Removed the mapped `leftColumnRabbitHoleCards` block from `src/routes/index.tsx`, including the temporary Rabbit Hole-style card markup under the CTA.",
      "Updated `e2e/root-language-switch.spec.ts` so the homepage regression now checks the supporting intro paragraph again instead of the removed dummy card copy.",
      "Left the temporary translation keys in place for now; they are no longer rendered by the landing page.",
    ],
  },
  {
    id: "landing-page-left-column-now-includes-scrollable-rabbit-hole-style-cards",
    timestamp: "2026-07-07T14:28:21+0200",
    title: "Landing page intro column now continues with scrollable Rabbit Hole-style cards",
    summary:
      "The public homepage intro column no longer ends at the main call to action. It now continues with a short stack of Rabbit Hole-style discovery cards so the left rail feels alive and remains scrollable on desktop.",
    userHighlights: [
      "The intro column now keeps going below the main button instead of stopping abruptly.",
      "Three compact discovery cards now sit under the call to action and match the soft card language used elsewhere on the page.",
      "The left column remains independently scrollable, so longer homepage content does not squeeze the main reading cards.",
    ],
    technicalNotes: [
      "Updated `src/routes/index.tsx` so the left desktop column renders a mapped `leftColumnRabbitHoleCards` list beneath the CTA while keeping `overflow-y-auto` on the aside.",
      "Added new `landingpage_onepager_left_card_*` translation keys in `src/assets/translations.json` for German and English dummy Rabbit Hole card copy.",
      "Kept the homepage root behavior intact: language switching still drives the new left-column cards through the existing translation hook.",
    ],
  },
  {
    id: "homepage-one-pager-now-feels-more-editorial-and-playful",
    timestamp: "2026-07-07T10:23:44+0200",
    title: "Homepage now uses a tighter one-page reading story",
    summary:
      "The public landing page has been rebuilt into a lighter one-pager with fewer words, stronger hierarchy, and a more playful editorial collage that shows the reading flow instead of explaining it at length.",
    userHighlights: [
      "The homepage now feels quicker to scan, with one clear story from headline to call to action.",
      "Landing-page headings now use the same serif title treatment used elsewhere in the product, and the shouty all-caps labels are gone.",
      "A new collage of adaptation, inline vocabulary, topic, word-bank, and app-preview panels makes the page feel closer to the product itself.",
    ],
    technicalNotes: [
      "Replaced the archive-style split layout in `src/routes/index.tsx` with a single flowing landing page made of a top nav, hero, editorial collage, three compact feature panels, and a closing call to action.",
      "Shortened and retuned the homepage translations in `src/assets/translations.json`, added new `landingpage_onepager_*` keys, and changed the visible brand treatment from `UXHULU` to `Uxhulu` on the website root.",
      "Updated `e2e/root-language-switch.spec.ts` so the root-page regression now checks the new one-page copy while continuing to cover language switching and mobile no-overflow behavior.",
    ],
  },
  {
    id: "daily-text-immediate-content-fallback",
    timestamp: "2026-07-07T10:06:16+0200",
    title: "Daily text now shows content immediately during generation instead of staying blank",
    summary:
      "When the backend generates a daily text and saves the content while the status is still PENDING, the frontend now falls back to the persisted API content instead of showing a blank screen. The title area also resolves from the persisted text during PENDING, reducing the wait on first open.",
    userHighlights: [
      "Opening a fresh daily text now shows the text body immediately after the backend finishes generation, even before the streaming playback completes.",
      "The title now resolves from the persisted output during generation, avoiding a placeholder-only state.",
      "First-open wait for daily texts is substantially shorter, especially on slower environments.",
    ],
    technicalNotes: [
      "In `src/components/GeneratedTextDetailsTextPane.tsx`, `NoChapterText` now uses `streamText || persistedRootText` instead of `streamText ?? \"\"`, so the API-delivered `chatgpt_output` is rendered immediately when `streamText` is still empty.",
      "In the same file, `rawTitleText` now falls back to `reepriseDetails.data.chatgpt_output` when `streamTitleText` is empty during PENDING, so the title extraction sees the real generated text instead of waiting for the streaming channel.",
      "The backend continued to persist `chatgpt_output` and `topic` during PENDING (before WebSocket playback); this frontend change ensures that persisted data is used as soon as it is available.",
    ],
  },
  {
    id: "rabbit-hole-placeholder-title-recognition",
    timestamp: "2026-07-06T20:45:00+0200",
    title: "Rabbit Hole discovery detail pages now avoid showing generating-title flash",
    summary:
      "The detail page no longer shows `Generating title…` during the initial data load for rabbit hole discoveries. Rabbit Hole placeholder topics (`rabbit hole – …`) are now recognized as system placeholders so the title area stays clean until the AI-generated title renders.",
    userHighlights: [
      "Rabbit Hole discovery detail pages no longer flash `Generating title…` during the initial loading moment.",
      "Discovery texts that still have a pending placeholder topic now stay visually clean instead of rendering the raw placeholder text.",
    ],
    technicalNotes: [
      "Added `rabbit hole` prefix to `isSystemPlaceholderTopic` in `src/utils/resolvePendingGeneratedTextTitle.ts`.",
      "Guarded `displayTitle` in `GeneratedTextDetailsTextPane` to return empty string while `useQuery` is in initial loading state, preventing the generating copy from appearing before data arrives.",
    ],
  },
  {
    id: "rabbit-hole-overview-today-discovery-date-fix",
    timestamp: "2026-07-06T20:29:00+0200",
    title: "Rabbit Hole overview cards now show the correct day's discovery title",
    summary:
      "The overview Rabbit Hole cards used a weekday-based index to find today's discovery, which could show the wrong day (or the same title for today and tomorrow) when the week started on a day other than Sunday. The card now computes the correct day offset from the cycle's actual start date.",
    userHighlights: [
      "Rabbit Hole cards on the overview now display the discovery title that actually belongs to today.",
      "Tomorrow's upcoming title no longer duplicates today's title.",
    ],
    technicalNotes: [
      "Added `findRabbitHoleDiscoveryForDate` helper in `src/routes/app/index.tsx` that computes the day offset from `week_start` instead of using `new Date().getDay()`.",
      "Replaced both call sites (overview title translation and card rendering) to use the date-based matcher.",
    ],
  },
  {
    id: "rabbit-hole-discovery-generation-fix",
    timestamp: "2026-07-06T20:19:12+0200",
    title: "Rabbit Hole discovery texts now generate reliably via background tasks",
    summary:
      "Rabbit Hole discovery content is now dispatched to Celery for background generation instead of blocking the HTTP request handler. The discovery detail page also includes a safety net that triggers generation when the Rabbit Hole planning page's trigger is missed.",
    userHighlights: [
      "Opening a Rabbit Hole discovery now generates text content reliably in the background instead of silently failing.",
      "The discovery detail page now shows a live streaming preview while the text is being generated.",
      "If a generation trigger is missed on the planning page, the detail page will automatically kick off generation as a fallback.",
    ],
    technicalNotes: [
      "Changed `ensure_daily_text_generation_started` in `barkguide/reeprise/tasks.py` to dispatch `complete_generated_text` via `apply_async` (Celery) instead of calling it synchronously in the Django request handler.",
      "Added `RABBIT_HOLE` to the deferred origin guard in the discovery detail page useEffect (`barkguide-ui/src/routes/app/reeprise/$generatedTextId.tsx`) so the fallback generation trigger covers rabbit hole texts.",
      "Updated `GeneratedText` type in `barkguide-ui/src/hooks/types/GeneratedTexts.tsx` and `vocabularySelectionState.ts` to include the `RABBIT_HOLE` origin.",
    ],
  },
  {
    id: "qa-dashboard-now-separates-generation-time-from-test-result-time",
    timestamp: "2026-07-06T15:46:19+0200",
    title: "The QA dashboard now separates generation time from test result time",
    summary:
      "The test dashboard no longer implies that `Zuletzt generiert` means tests were executed then. It now shows the dashboard generation time separately from the newest detected test results and lists Pytest, Vitest, and Playwright result timing individually.",
    userHighlights: [
      "The dashboard now distinguishes clearly between data generation time and test result time.",
      "You can now see the newest detected test results at the top of the page.",
      "Each framework now shows its own last-result timestamp and matched result count.",
    ],
    technicalNotes: [
      "Extended `scripts/generate_test_dashboard.py` to emit execution-evidence metadata derived from result-file timestamps and matched per-framework results.",
      "Updated `src/routes/app/test_dashboard.tsx` and translations to show `Last generated`, `Latest detected test results`, and a per-framework execution evidence table.",
      "Updated the dashboard guidance so the timestamp semantics are explicit instead of inferred from a single regeneration label.",
    ],
  },
  {
    id: "qa-dashboard-now-shows-generation-state-and-supports-watch-regeneration",
    timestamp: "2026-07-06T15:40:26+0200",
    title: "The QA dashboard now shows its generation state and can regenerate automatically during development",
    summary:
      "The test dashboard now shows when its data was last generated, warns when the dataset may be outdated, and supports a watch mode that regenerates dashboard data automatically while you work.",
    userHighlights: [
      "The test dashboard now shows its latest generation timestamp directly in the page.",
      "You now get a visible stale-data warning when relevant dashboard inputs have changed.",
      "A new watch mode can keep dashboard data regenerating automatically during development.",
    ],
    technicalNotes: [
      "Extended `scripts/generate_qa_index.py` to carry feature-level related source and test file lists into the generated QA index.",
      "Updated `scripts/generate_test_dashboard.py` and `scripts/generate_test_dashboard.sh` to support stale-state metadata, optional QA-index regeneration, and a polling-based `--watch` mode.",
      "Updated `src/routes/app/test_dashboard.tsx`, translations, and dashboard docs so the UI surfaces generation freshness instead of relying on hidden file state.",
    ],
  },
  {
    id: "overview-rabbit-hole-view-plan-copy",
    timestamp: "2026-07-03T17:08:10+0200",
    title: "Rabbit Hole overview now points more clearly to the planning page",
    summary:
      "The active Rabbit Hole card action now says `View plan` / `Plan ansehen` instead of `Open`, so it is clearer that the action leads to the Rabbit Hole planning and calendar page rather than directly into the text.",
    userHighlights: [
      "Active Rabbit Hole cards now use `Plan ansehen` in German.",
      "The button now points more clearly to the planning page instead of sounding like it opens the text itself.",
      "The calmer action hierarchy from the previous update stays intact.",
    ],
    technicalNotes: [
      "Updated the `overview_rabbit_hole_manage` translation in `src/assets/translations.json` to `Plan ansehen` / `View plan`.",
      "Kept the existing overview card layout and navigation behavior unchanged.",
    ],
  },
  {
    id: "overview-rabbit-hole-open-copy-and-softer-manage-action",
    timestamp: "2026-07-03T17:06:36+0200",
    title: "Rabbit Hole overview now uses calmer opening language and softer active actions",
    summary:
      "The overview no longer uses `Manage` / `Verwalten` for active Rabbit Holes, and the active action styling is quieter so the discovery title stays visually primary.",
    userHighlights: [
      "Active Rabbit Hole cards now use `Open` / `Öffnen` instead of `Manage` / `Verwalten`.",
      "The active-card actions are visually lighter, so they no longer dominate the title.",
      "The Rabbit Hole page title is now simpler and less administrative.",
    ],
    technicalNotes: [
      "Changed the active overview action styling in `src/routes/app/index.tsx` to a lighter text-style treatment while keeping the unset start action unchanged.",
      "Updated Rabbit Hole management copy in `src/assets/translations.json` from `Manage` / `Verwalten` to calmer `Open` / `Öffnen` wording.",
      "Adjusted the overview Playwright expectation for the Rabbit Hole page heading.",
    ],
  },
  {
    id: "overview-rabbit-hole-softer-read-action",
    timestamp: "2026-07-03T17:02:15+0200",
    title: "Rabbit Hole overview now gives the title more visual priority",
    summary:
      "The `Read now` action on Rabbit Hole overview cards is now visually quieter, so the discovery title remains the primary focus and the action reads as supporting navigation rather than the main card element.",
    userHighlights: [
      "The Rabbit Hole title now stands out more clearly than the immediate read action.",
      "The `Read now` action is still available in the same place, but with lighter visual emphasis.",
      "The card keeps the same behavior while using a calmer hierarchy.",
    ],
    technicalNotes: [
      "Changed the overview Rabbit Hole `Read now` button styling in `src/routes/app/index.tsx` from a filled primary treatment to a lighter text-style action.",
      "Left the card structure, navigation behavior, and management action unchanged.",
    ],
  },
  {
    id: "overview-rabbit-hole-remove-missing-next-topic-warning",
    timestamp: "2026-07-03T16:54:00+0200",
    title: "Rabbit Hole overview no longer shows an unsupported missing-topic warning",
    summary:
      "The overview Rabbit Hole cards no longer show the `next 7 days still need a topic` warning. That note implied an immediate action without giving one and could misrepresent the state right after topic changes.",
    userHighlights: [
      "Rabbit Hole overview cards now stay neutral when no next topic is saved yet.",
      "The card still shows the planned next topic when one exists.",
      "Missing next-topic planning remains on the dedicated management page instead of appearing as a vague overview warning.",
    ],
    technicalNotes: [
      "Removed the overview-only missing-next-topic paragraph from `src/routes/app/index.tsx`.",
      "Kept the rest of the Rabbit Hole overview card structure unchanged.",
    ],
  },
  {
    id: "overview-rabbit-hole-upcoming-copy",
    timestamp: "2026-07-03T16:46:44+0200",
    title: "Rabbit Hole overview now uses calmer upcoming-text wording",
    summary:
      "The Rabbit Hole overview expansion label now says `Upcoming texts` instead of the more inventory-like remaining-text wording.",
    userHighlights: [
      "The overview Rabbit Hole expansion row now reads `Upcoming texts`.",
      "The German label now reads `Kommende Texte`.",
      "The interaction and expanded list behavior stay the same.",
    ],
    technicalNotes: [
      "Updated the `overview_rabbit_hole_upcoming_title` translation in `src/assets/translations.json`.",
      "Kept the overview expand/collapse structure unchanged and limited the change to user-facing copy.",
    ],
  },
  {
    id: "overview-rabbit-hole-remove-count-copy",
    timestamp: "2026-07-03T16:45:30+0200",
    title: "Rabbit Hole overview cards now keep the remaining-text toggle quieter",
    summary:
      "The Rabbit Hole overview cards no longer show the extra `texts left` count line in the expand toggle. The remaining texts are still available when opened, but the collapsed card stays calmer.",
    userHighlights: [
      "The overview Rabbit Hole cards now show a simpler expand row.",
      "Collapsed cards no longer repeat a numeric remaining-text count.",
      "The day-by-day remaining texts are still available after expanding the card.",
    ],
    technicalNotes: [
      "Removed the secondary count/no-upcoming subtitle from the overview expand toggle in `src/routes/app/index.tsx`.",
      "Kept the existing expanded upcoming-text list and its empty-state behavior unchanged.",
      "Reused the existing Rabbit Hole overview Playwright coverage to verify the interaction still works.",
    ],
  },
  {
    id: "rolling-language-rabbit-holes",
    timestamp: "2026-07-03T16:01:28+0200",
    title: "Rabbit Holes now run as rolling 7-day cycles per language",
    summary:
      "Rabbit Holes no longer snap to shared Monday-to-Sunday weeks. Each language now runs on its own rolling 7-day cycle, so progress and upcoming texts always match the day that language’s Rabbit Hole actually started.",
    userHighlights: [
      "Each language now keeps its own Rabbit Hole timing instead of sharing one calendar week.",
      "Progress like `2 / 7 discoveries explored` now reflects the learner’s real 7-day cycle even when a Rabbit Hole started midweek.",
      "The manage page calendar now follows the actual cycle start date for that language.",
      "If a next topic is already saved, it automatically becomes the next Rabbit Hole when the current cycle ends.",
    ],
    technicalNotes: [
      "Changed the Rabbit Hole backend lifecycle so `week_start` and `week_end` now represent rolling per-language cycle bounds rather than normalized Monday/Sunday dates.",
      "Updated `RabbitHoleWeekViewSet.current_week` to resolve active cycles by stored dates and auto-roll over saved next topics on first access after expiry.",
      "Scoped cycle creation and rollover by language, and kept discovery scheduling relative to `week_start + rabbit_hole_day_index`.",
      "Fixed the manage page day-highlighting logic in `src/routes/app/rabbit_hole.tsx` so `today` is derived from the real slot date instead of Monday-based indexing.",
      "Added backend regression tests for rolling creation, active midcycle progress, saved-topic rollover, and no-topic expiry behavior.",
    ],
  },
  {
    id: "overview-rabbit-hole-inline-week-expansion",
    timestamp: "2026-07-03T15:31:28+0200",
    title: "Rabbit Hole overview cards can now expand to show the week plan",
    summary:
      "Active Rabbit Hole cards on the overview page can now expand inline, so learners can see the remaining texts for the week day by day without leaving the page.",
    userHighlights: [
      "Each active Rabbit Hole card on the overview now has an expand action.",
      "Expanding a card reveals the remaining daily texts still left in the current week.",
      "The week plan stays inline on the overview instead of forcing a navigation into the management page.",
      "Collapsed cards remain compact, while expanded cards give quick planning visibility when needed.",
    ],
    technicalNotes: [
      "Added local expanded-card state inside `src/routes/app/index.tsx` keyed by language id.",
      "Built an overview-local upcoming-slot helper from `rabbit_hole_day_index` and `week_start`.",
      "Added translation keys for expand, collapse, and no-upcoming states in `src/assets/translations.json`.",
      "Extended the Rabbit Hole overview Playwright fixture to cover inline week expansion.",
    ],
  },
  {
    id: "overview-rabbit-hole-per-language-cards",
    timestamp: "2026-07-03T15:31:28+0200",
    title: "Overview now shows one Rabbit Hole card per language",
    summary:
      "The overview now always shows a Rabbit Hole card for each visible language space. If a language does not have a weekly topic yet, the card says so directly instead of hiding that state.",
    userHighlights: [
      "Each visible language on the overview now has its own Rabbit Hole card.",
      "Languages without a Rabbit Hole no longer disappear from the overview section.",
      "Unset cards clearly state that no weekly topic has been chosen yet.",
      "The empty card still gives a direct path into setting up that language’s Rabbit Hole.",
      "Active cards continue to show today’s title, progress, and management actions.",
    ],
    technicalNotes: [
      "Changed the overview Rabbit Hole section in `src/routes/app/index.tsx` to render from all visible language spaces instead of only active weeks.",
      "Added explicit unset-state copy and a missing-next-week reminder in `src/assets/translations.json`.",
      "Kept the existing per-language query pattern and reused the same language-scoped navigation into `/app/rabbit_hole`.",
      "Extended `e2e/app-overview.spec.ts` to verify both the active English card and the unset French card.",
    ],
  },
  {
    id: "rabbit-hole-manage-calendar",
    timestamp: "2026-07-03T15:31:28+0200",
    title: "Rabbit Hole manage page now includes a weekly calendar",
    summary:
      "The Rabbit Hole manage page now acts like a real language-specific planning surface. It shows the week as a calendar and lets learners change the current topic or prepare the next one from the same page.",
    userHighlights: [
      "The `Manage` action now opens a page that behaves like an actual Rabbit Hole management view.",
      "A weekly calendar layout shows which discovery belongs to each day of the current week.",
      "Learners can update the current week’s topic directly from the management page.",
      "The same page now includes a next-week topic field, so the sequence of topics becomes visible and editable.",
      "The current reset action remains available in a dedicated lower section instead of competing with the main planning controls.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/rabbit_hole.tsx` so the active-week state is now a management surface with separate sections for this week, calendar, next week, and reset.",
      "Used the existing `useRabbitHoleCreateWeek` mutation to update the current week’s topic and `useRabbitHoleSetNextTopic` for next-week planning.",
      "Changed the discovery list into a date-based calendar grid with stable test ids for each day card.",
      "Added new management and calendar translation keys in `src/assets/translations.json`.",
      "Extended `e2e/app-overview.spec.ts` so the overview `Manage` path now verifies the management page content.",
    ],
  },
  {
    id: "overview-rabbit-hole-management",
    timestamp: "2026-07-03T14:46:29+0200",
    title: "Overview now includes Rabbit Hole management",
    summary:
      "The overview page now surfaces weekly Rabbit Hole topics directly, so learners can reopen today’s discovery or jump into the planner without leaving the main home screen first.",
    userHighlights: [
      "Active Rabbit Hole weeks now appear on the overview page as dedicated cards.",
      "Each card shows the weekly topic, the current progress, and the next-week topic when one is already planned.",
      "If today’s discovery exists, the overview offers a direct `Read now` action into that text.",
      "The overview also includes a direct `Manage` action into the full Rabbit Hole page.",
      "When no Rabbit Hole is active yet, the overview shows a lightweight start state instead of leaving the feature hidden in navigation.",
    ],
    technicalNotes: [
      "Added overview-local Rabbit Hole queries in `src/routes/app/index.tsx` for each visible language space.",
      "Introduced a new `RabbitHoleOverviewSection` that renders either active weekly cards or a single empty-state starter card.",
      "Wired overview actions to both `/app/rabbit_hole` and the current day’s generated discovery details route.",
      "Added new translation keys for the overview Rabbit Hole section in `src/assets/translations.json`.",
      "Extended `e2e/app-overview.spec.ts` with overview-level Rabbit Hole coverage.",
    ],
  },
  {
    id: "weekly-rabbit-hole",
    timestamp: "2026-07-02T16:00:00+0200",
    title: "Rabbit Hole: wöchentliche Themen-Entdeckungen",
    summary:
      "Ein neuer Seitenbereich unter /app/rabbit_hole bietet ein wöchentliches Leseerlebnis, bei dem Nutzer ein Thema auswählen und täglich eine KI-generierte Entdeckung dazu lesen können — in der Sprache, die sie lernen.",
    userHighlights: [
      "Auf /app/rabbit_hole können Nutzer ein Rabbit-Hole-Thema für die Woche auswählen — per Suche, aus Vorschlägen oder durch freie Eingabe.",
      "Die aktive Woche zeigt den Fortschritt (z. B. 3/7 Entdeckungen erkundet) und tägliche Entdeckungen zum ausgewählten Thema.",
      "Themen für nächste Woche können jederzeit festgelegt werden, ohne die aktuelle Woche zu unterbrechen.",
      "Am Ende der Woche erscheint ein Bericht mit Anzahl der Entdeckungen, neu gelernten Wörtern und Wiederholungsbedarf.",
      "Die generierten Texte nutzen die bestehende Textgenerierungs-Infrastruktur mit einer neuen Origin-Kategorie RABBIT_HOLE.",
      "Der Zugang erfolgt über die Seitenleiste unter „Rabbit Hole“.",
    ],
    technicalNotes: [
      "Neues Django-Modell RabbitHoleWeek mit user, topic, language, week_start, week_end, status, next_week_topic.",
      "Erweiterung von GeneratedText um Origin.RABBIT_HOLE sowie rabbit_hole_week (FK) und rabbit_hole_day_index (SmallInteger).",
      "Neuer RabbitHoleWeekViewSet mit Endpunkten: current, create, generate-discovery, set-next-topic, report, knowledge-map, suggestions.",
      "create_rabbit_hole_discovery() in tasks.py erstellt eine PENDING GeneratedText mit daily/index-basierter Themen-Variation und startet die Generierung via complete_generated_text.",
      "Frontend: Route /app/rabbit_hole mit PlanningState (Themenauswahl), ActiveWeekState (Wochenübersicht), WeekCompleteState (Bericht).",
      "React-Query-Hooks in useRabbitHole.ts: useRabbitHoleCurrent, useRabbitHoleCreateWeek, useRabbitHoleGenerateDiscovery, useRabbitHoleSetNextTopic, useRabbitHoleReport.",
      "29 neue Übersetzungsschlüssel in translations.json für die Rabbit-Hole-UI.",
      "Migration 0096 erweitert die Datenbank um RabbitHoleWeek und die neuen GeneratedText-Felder.",
    ],
  },
  {
    id: "daily-text-streaming-visibility-and-fix",
    timestamp: "2026-07-02T15:20:09+0200",
    title: "Text des Tages: sichtbares Streaming und sofortiger Textkörper",
    summary:
      "Beim Öffnen eines täglichen Textes wird der Textkörper jetzt sichtbar und sichtbar abgespielt, anstatt einen leeren Bildschirm mit nur dem Titel zu zeigen. Der Text erscheint Zeichen für Zeichen wie bei einem Chat-System.",
    userHighlights: [
      "Nach dem Öffnen eines Text-des-Tages erscheint der Titel sofort und der Textkörper wird sichtbar abgespielt.",
      "Der Text erscheint jetzt in lesbarer Geschwindigkeit (~12 Zeichen pro Schritt), sodass das Streaming sichtbar ist.",
    ],
    technicalNotes: [
      "`resolveGeneratedTextContent()` in `src/utils/generatedTextContent.ts` zeigt jetzt den Textkörper an, wenn ein Fallback-Titel verfügbar ist, auch unter `enforceTitleFirst`.",
      "`NoChapterText` in `src/components/GeneratedTextDetailsTextPane.tsx` übergibt `props.displayTitle` jetzt auch während PENDING als `fallbackTitle`, nicht nur bei COMPLETE.",
      "`CHAPTER_STREAM_PLAYBACK_CHUNK_SIZE` von 80 auf 12 reduziert, `CHAPTER_STREAM_PLAYBACK_DELAY_SECONDS` von 0.015s auf 0.06s erhöht, sodass das Streaming sichtbar wird.",
      "Zwei neue Unit-Tests in `generatedTextContent.test.ts` für die Anzeige des Textkörpers mit Fallback-Titel unter `enforceTitleFirst`.",
    ],
  },
  {
    id: "async-daily-title-generation",
    timestamp: "2026-07-01T15:51:34+0200",
    title: "Schnellere Erstellung von Text des Tages",
    summary:
      "Die Erstellung eines täglichen Textes über die Startkarte auf der Übersichtsseite blockiert nicht mehr den HTTP-Request, da die Titelgenerierung jetzt im Hintergrund läuft. Die Klickantwort ist dadurch sofort spürbar.",
    userHighlights: [
      "Beim Klick auf die Text-des-Tages-Karte erscheint die Navigation zum Text sofort, ohne Wartezeit auf die Titelgenerierung.",
      "Der endgültige Titel des Textes wird im Hintergrund nachgeladen, sobald er verfügbar ist.",
    ],
    technicalNotes: [
      "`prepare_daily_text_title()` in `barkguide/reeprise/tasks.py` wird jetzt als Celery-Task (`prepare_daily_text_title_task`) asynchron ausgeführt statt synchron im HTTP-Request.",
      "`create_practice_text_for_user_language()` ruft jetzt `prepare_daily_text_title_task.delay(text.id)` statt des synchronen Aufrufs.",
      "Die synchrone OpenAI-Anfrage zur Titelgenerierung blockierte zuvor den `/daily-practice/` POST-Endpunkt für 10+ Sekunden.",
    ],
  },
  {
    id: "test-automation-dashboard",
    timestamp: "2026-06-30T10:20:56+0200",
    title: "Test Automation Dashboard",
    summary:
      "A new test automation dashboard at /app/test_dashboard helps QA understand test coverage, execution status, risk coverage, and maintainability of the automated test suite.",
    userHighlights: [
      "Summary cards show total test count, passed, failed, skipped, and flaky tests at a glance.",
      "CSS-based bar charts for status breakdown, feature coverage, risk coverage, and test type distribution.",
      "Filterable, sortable table with columns for test ID, name, feature, priority, risk, type, status, duration, and error message.",
      "Needs Review section highlights tests missing metadata annotations (priority, risk, user story link).",
      "Duplicate detection flags tests with very similar names that may be redundant.",
      "Outdated test detection automatically flags tests with ImportErrors from removed or renamed code.",
      "Copy-for-agent button on every issue row copies a structured instruction for the coding agent.",
      "Top-level Copy all issues for agent button aggregates all problems into one message.",
      "QA maintains test metadata in barkguide-ui/test-metadata.json — tests without entries appear in Needs Review.",
    ],
    technicalNotes: [
      "New route at /app/test_dashboard using TanStack Router file-based routing.",
      "Data pipeline: scripts/generate_test_dashboard.py collects test definitions (pytest --collect-only, vitest file parsing, playwright file parsing), merges with run results (pytest JSON report, vitest JSON reporter, playwright JSON reporter), and enriches with QA metadata from test-metadata.json.",
      "pytest-json-report plugin added for structured pytest output.",
      "vitest configured with JSON reporter (outputFile: ./public/vitest-results.json) alongside default terminal reporter.",
      "playwright configured with dual reporters (list + JSON with outputFile).",
      "make test updated to run all three suites (pytest, vitest, playwright) and collect exit codes independently.",
      "make test-dashboard target added to Makefile for generating dashboard data separately from test execution.",
      "Historical data stored in .logs/test-history.jsonl for trend analysis (last 5 runs used for flaky detection).",
      "Feature inference from file paths and test names for tests not yet annotated in metadata.",
      "Duplicate detection uses word-overlap similarity (>80% threshold) on test names.",
      "31 new translation keys added to translations.json for dashboard labels.",
      "BeakerIcon added to side navigation alpha section linking to /app/test_dashboard.",
      "README at barkguide-ui/TEST_DASHBOARD.md documents QA workflow and architecture.",
    ],
  },
  {
    id: "italian-roleplay-speaking-practice-replaces-general-conversation",
    timestamp: "2026-06-29T15:10:00+0200",
    title: "Italienisch üben: situationsbasierte Rollenspiel-Sprachsimulation",
    summary:
      "Die Sprechpraxis ist jetzt eine Italienisch-Rollenspielsimulation. Per Sprache oder Tap wählst du eine Alltagssituation, und die App wird sofort zur anderen Person — auf Italienisch, mit kurzen Korrekturen und natürlicher Gesprächsführung.",
    userHighlights: [
      "Neue Startseite: 'Welche Situation möchtest du üben?' mit Spracheingabe oder Schnellauswahl (Restaurant, Hotel, Café, Bahnhof, Einkaufen, Arzt).",
      "Die App wird sofort zur Kellnerin, zum Rezeptionisten oder zur Ärztin und beginnt das Gespräch auf Italienisch.",
      "Hilfe-Button mit abgestuften Hilfestellungen: Bedeutung erklären, Satzanfang geben, langsam wiederholen, einfache Antwort vorschlagen.",
      "Korrekturschleife: Machst du einen Fehler, zeigt die App eine kurze Korrektur und bittet dich, die verbesserte Version zu wiederholen — ohne den Gesprächsfluss zu unterbrechen.",
      "Szenario-Übersicht zeigt, welche Gesprächszweige du geübt hast (Begrüßung, Bestellung, Bezahlen etc.) und wie weit du die Situation gemeistert hast.",
      "Sitzungszusammenfassung mit nützlichen Sätzen und Empfehlung, was du als Nächstes üben könntest.",
    ],
    technicalNotes: [
      "Neue Django-Modelle: SpeakingSituation, SpeakingSituationBranch, SpeakingSession für zustandsbehaftetes Rollenspiel mit Branch-Tracking und Mastery-Prozentsatz.",
      "10 Situation-Presets mit vordefinierten Branches und Komplikationen in barkguide/reeprise/roleplay_presets.py.",
      "LLM-basierte Klassifikation für benutzerdefinierte Situationen aus deutscher Spracheingabe.",
      "Neue API-Endpunkte: /api/reeprise/roleplay/situation/start/, /turn/, /help/, /continue/, /map/.",
      "ElevenLabs TTS-Integration für native italienische Sprachausgabe (mit OpenAI-Fallback).",
      "Frontend: dialogue.tsx von 2000 Zeilen All-in-One-Chat auf 5 strukturierte Screens umgebaut (Eingabe → Bestätigung → Rollenspiel → Übersicht → Zusammenfassung).",
      "Progressive Hilfe-Level (1-5) mit Eskalation bei wiederholten Hilfeanfragen.",
      "Komplikations-Injektion: zufällige realistische Überraschungen wie 'Kein Tisch frei' oder 'Kartenterminal defekt'.",
      "Alte allgemeine Konversationspraxis, Walking-Mode, Vokabel-Trigger-Queue, Coaching-Toggle und Settings-Panel entfernt.",
      "9 neue Backend-Tests für Rollenspiel-Flow (Situation-Start, Turn, Korrektur, Hilfe, Isolation, Map, Fortsetzung).",
    ],
  },
  {
    id: "daily-text-open-now-keeps-the-resolved-title",
    timestamp: "2026-06-25T22:27:00+0200",
    title: "Opening a daily text now keeps the resolved title",
    summary:
      "Daily texts opened from the overview now keep their already-resolved title instead of briefly falling back to `Titel wird erstellt …` while the details request is still catching up.",
    userHighlights: [
      "Opening the large daily-text preview now shows the real title immediately.",
      "The details screen no longer flashes the temporary generating-title placeholder when the overview already knows the title.",
      "Returning to the overview still rotates the featured daily text as before.",
    ],
    technicalNotes: [
      "Added a shared resolved-title cache in `src/utils/generatedTextResolvedTitleCache.ts` and persist overview-resolved daily titles from `src/routes/app/index.tsx`.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to reuse that cached title during pending details hydration and added a stable details-title test id.",
      "Extended `e2e/app-overview.spec.ts` to mock the stale-details case from the daily overview flow and rebuilt the frontend afterward.",
    ],
  },
  {
    id: "overview-now-drops-the-dummy-familiar-words-line",
    timestamp: "2026-06-25T22:18:56+0200",
    title: "Overview now drops the dummy familiar-words line",
    summary:
      "The featured overview text no longer shows the placeholder familiar-words percentage, so the metadata line stays limited to real title context and word count.",
    userHighlights: [
      "The dummy `99 % vertraute Wörter` line is removed from the featured text.",
      "The hero metadata is now shorter and cleaner.",
      "The rest of the overview remains unchanged.",
    ],
    technicalNotes: [
      "Removed the familiar-words render path from `src/routes/app/index.tsx` and deleted the now-unused translation entry from `src/assets/translations.json`.",
      "Kept the featured hero title, translated subtitle, word count, and language label intact.",
      "Rebuilt the frontend after the metadata cleanup.",
    ],
  },
  {
    id: "overview-now-pushes-active-daily-texts-forward-and-softens-continue",
    timestamp: "2026-06-25T22:17:43+0200",
    title: "Overview now gives the active texts clearer priority",
    summary:
      "The overview now keeps the active daily-text area more visually forward, while the `Weitermachen` section steps back with quieter styling.",
    userHighlights: [
      "The currently active daily-text flow stands out more clearly.",
      "The `Weitermachen` section now reads as secondary instead of competing with the active items.",
      "The page hierarchy is clearer without changing the underlying overview structure.",
    ],
    technicalNotes: [
      "Adjusted `src/routes/app/index.tsx` so the continue-section heading uses lower contrast and the continue rows use softer title, metadata, and chevron styling.",
      "Kept the featured hero and remaining same-day daily rows at their existing stronger contrast levels.",
      "Rebuilt the frontend after the hierarchy refinement.",
    ],
  },
  {
    id: "overview-now-removes-reading-and-listening-progress-metrics",
    timestamp: "2026-06-25T22:16:28+0200",
    title: "Overview now removes reading and listening progress metrics",
    summary:
      "The overview no longer shows the separate reading and listening progress percentages, so the lower metric block stays more focused on broader totals.",
    userHighlights: [
      "The `Lesefortschritt` and `Hörfortschritt` percentages are removed from the overview.",
      "The remaining metrics now focus on overall vocabulary collection and created texts.",
      "The overview feels quieter and less crowded.",
    ],
    technicalNotes: [
      "Removed the reading/listening progress calculations and metric render blocks from `src/routes/app/index.tsx`.",
      "Left the remaining overview metrics and layout structure intact.",
      "Rebuilt the frontend after the metric cleanup.",
    ],
  },
  {
    id: "overview-vocabulary-metric-now-uses-total-collection-wording",
    timestamp: "2026-06-25T22:15:41+0200",
    title: "Overview vocabulary metric now uses total-collection wording",
    summary:
      "The vocabulary total in the overview now uses calmer `Total collection of words` wording instead of the previous `Active vocabulary` label.",
    userHighlights: [
      "The progress metric label now reads more like a total archive than an activity state.",
      "The metric value itself stays unchanged.",
      "This is a wording-only adjustment on the overview page.",
    ],
    technicalNotes: [
      "Updated the `app_overview_active_vocabulary` translation entry in `src/assets/translations.json` for both English and German.",
      "Kept the existing metric layout and data source unchanged in `src/routes/app/index.tsx`.",
      "Rebuilt the frontend after the copy update.",
    ],
  },
  {
    id: "overview-now-drops-the-journey-headings",
    timestamp: "2026-06-25T22:14:22+0200",
    title: "Overview now drops the journey headings",
    summary:
      "The progress area on the overview no longer shows the extra `Deine Reise` and `Fortschritt im Überblick` headings, so the metrics start more directly.",
    userHighlights: [
      "The progress block now begins straight with the metrics.",
      "The extra journey heading text is removed.",
      "The overview stays cleaner and more compact.",
    ],
    technicalNotes: [
      "Removed the journey kicker and title render from `src/routes/app/index.tsx` while keeping the existing metric layout intact.",
      "Left the reading, listening, vocabulary, and texts-created metrics unchanged.",
      "Rebuilt the frontend after the layout cleanup.",
    ],
  },
  {
    id: "overview-now-shows-native-language-names",
    timestamp: "2026-06-25T22:13:12+0200",
    title: "Overview now shows native language names",
    summary:
      "The overview now labels languages with their native names, such as `Français`, `English`, and `Deutsch`, instead of using the previous UI-language wording.",
    userHighlights: [
      "Language labels on overview items now read as native names.",
      "Examples include `Français`, `English`, `Deutsch`, and `Italiano`.",
      "This change is limited to the overview page labels.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/index.tsx` to use a small overview-local native-language label map instead of the translated generic language labels for row metadata.",
      "Kept the rest of the overview copy and routing behavior unchanged.",
      "Rebuilt the frontend after the label update.",
    ],
  },
  {
    id: "overview-now-drops-the-empfohlen-header",
    timestamp: "2026-06-25T22:11:08+0200",
    title: "Overview now drops the extra `Empfohlen` header",
    summary:
      "The overview no longer shows a separate `Empfohlen` label above the remaining daily texts, so the page reads more like one continuous flow from the featured text into the next items.",
    userHighlights: [
      "The extra `Empfohlen` section heading is gone.",
      "The remaining daily texts now sit more naturally below the featured text.",
      "The rest of the overview structure stays the same.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/index.tsx` to suppress the recommended-section heading while keeping the remaining daily rows and continue section behavior unchanged.",
      "Left the shared `OverviewTaskSections` structure intact and changed only the overview-page callsite configuration.",
      "Rebuilt the frontend after the copy/layout simplification.",
    ],
  },
  {
    id: "overview-no-longer-shows-saved-word-counts",
    timestamp: "2026-06-25T22:09:04+0200",
    title: "Overview no longer shows saved-word counts",
    summary:
      "The overview now stays focused on opening texts and tracking reading flow, without the extra saved-word count links under items.",
    userHighlights: [
      "Saved-word counts are removed from both the featured overview text and the smaller list rows.",
      "The overview feels cleaner and less link-heavy.",
      "Opening a text from the overview still works the same way.",
    ],
    technicalNotes: [
      "Removed the overview-only vocabulary count queries, render paths, and jump-to-vocabulary actions from `src/routes/app/index.tsx`.",
      "Deleted the now-unused `app_overview_saved_vocabulary_count` translation entry from `src/assets/translations.json` and cleaned up the overview task model accordingly.",
      "Updated the focused overview Playwright regression to stop asserting the removed saved-word count behavior, then rebuilt the frontend.",
    ],
  },
  {
    id: "overview-featured-hero-spacing-now-reads-as-one-connected-block",
    timestamp: "2026-06-25T22:07:34+0200",
    title: "Overview hero spacing now feels more connected",
    summary:
      "The featured daily-text area on the overview now uses tighter internal spacing so title, subtitle, metadata, and saved-word link read as one connected block.",
    userHighlights: [
      "The large featured text no longer feels too spread apart vertically.",
      "Title, translated subtitle, metadata, and saved-word count now sit closer together.",
      "The overall layout stays minimal, but the hero reads more cohesively.",
    ],
    technicalNotes: [
      "Tightened the vertical spacing in `src/routes/app/index.tsx` for the featured hero subtitle, metadata row, and saved-vocabulary link.",
      "Kept the existing typography, alignment, and interaction behavior unchanged while reducing visual fragmentation.",
      "Rebuilt the frontend after the layout refinement.",
    ],
  },
  {
    id: "overview-row-titles-now-use-the-daily-title-font-instead-of-section-headings",
    timestamp: "2026-06-25T21:19:11+0200",
    title: "Overview row titles now use the daily title font",
    summary:
      "The serif title font now applies to the actual text titles in the overview list, while the section headings return to their quieter smaller style.",
    userHighlights: [
      "The listed text titles under `Recommended` and `Continue` now match the title tone of the featured text more closely.",
      "Section labels themselves are back to the calmer smaller heading style.",
      "This keeps the typography emphasis on the text titles instead of the structural labels.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/index.tsx` so checklist row labels use serif title styling, while `ChecklistSection` and the journey heading kicker revert to their previous quieter heading treatment.",
      "Kept the featured hero typography unchanged and limited this pass to hierarchy correction on the overview page.",
      "Rebuilt the frontend after the typography adjustment.",
    ],
  },
  {
    id: "overview-section-headings-now-match-the-daily-card-title-font",
    timestamp: "2026-06-25T21:17:20+0200",
    title: "Overview section headings now use the same serif title font",
    summary:
      "The remaining overview section headings now use the same serif title styling as the featured daily text, so the page reads more consistently.",
    userHighlights: [
      "Headings like `Recommended`, `Continue`, and the journey area now match the serif title language used elsewhere on the overview.",
      "The page keeps the same structure, but the heading system feels more unified.",
      "This change only affects typography, not the checklist behavior or layout flow.",
    ],
    technicalNotes: [
      "Updated the overview heading styles in `src/routes/app/index.tsx` so checklist section labels and the journey heading use the same serif headline treatment as the featured hero.",
      "Kept the rest of the overview interaction unchanged, including the featured hero behavior and compact checklist rows.",
      "Rebuilt the frontend after the typography refinement.",
    ],
  },
  {
    id: "overview-now-opens-with-ready-daily-titles-and-card-style-hero-type",
    timestamp: "2026-06-25T20:36:31+0200",
    title: "Overview hero now opens with ready daily titles",
    summary:
      "The overview hero now uses the same serif title styling as the daily text cards, drops the extra topic line, and preloads today’s title translations so the first morning open feels more immediate.",
    userHighlights: [
      "The featured overview title now matches the typography used on the daily text cards.",
      "The extra topic line above the title is gone, so the hero starts more directly with the text title itself.",
      "On the first open of a new day, the overview now tries to show ready daily titles and ready title translations instead of falling back to placeholder copy.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/index.tsx` to reuse the serif daily-card headline treatment for the overview hero and removed the overview topic/category line from the hero render.",
      "Changed overview daily-task rendering so same-day daily rows only appear once a real resolved title exists, avoiding the generic fallback headline during morning prefetch.",
      "Added translation prefetching for all same-day daily titles via `/api/reeprise/translate/`, cached those results in local storage, and reran focused frontend validation.",
    ],
  },
  {
    id: "overview-now-features-one-daily-text-at-a-time",
    timestamp: "2026-06-25T16:09:38+0200",
    title: "Overview now leads with one featured daily text",
    summary:
      "The overview now opens directly with a larger featured daily text instead of a separate `Today` header, and the featured slot rotates to the next unread daily text across your active languages after you read one.",
    userHighlights: [
      "The page now starts with the text itself, not an extra `Today` header and date line.",
      "One daily text is featured more prominently with topic, translated title, word count, and a familiar-words hint.",
      "After you open one featured daily text and return, the next unread daily text can take its place.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/index.tsx` so the overview builds a featured hero from same-day `DAILY_NUDGE` texts, ranks unread candidates by existing engagement signals, and keeps the remaining texts as compact rows.",
      "Added `src/utils/overviewDailyTexts.ts` plus focused unit coverage for hero selection, all-read fallback ordering, category inference, and the dummy familiarity formatter.",
      "Reused the existing `/api/reeprise/translate/` title translation flow for the featured subtitle, extended overview Playwright coverage for rotation behavior, and rebuilt the frontend after the redesign.",
    ],
  },
  {
    id: "overview-section-labels-now-use-normal-case",
    timestamp: "2026-06-23T09:25:49+0200",
    title: "Overview section labels now use normal case",
    summary:
      "The overview now uses calmer normal-case section labels instead of small all-caps treatment for headings like `Recommended`, `Continue`, and `Your journey`.",
    userHighlights: [
      "Section labels on the overview no longer appear in all caps.",
      "The page keeps its contrast and hierarchy without the shoutier label style.",
      "This applies to the checklist section headings and the journey kicker.",
    ],
    technicalNotes: [
      "Removed the uppercase tracking treatment from overview section-label classes in `src/routes/app/index.tsx`.",
      "Kept the existing copy and layout structure unchanged while slightly adjusting the label size and tracking for readability.",
      "Rebuilt the frontend and reran the focused overview Playwright regression after the typography refinement.",
    ],
  },
  {
    id: "overview-vocabulary-counts-now-jump-back-into-the-source-text",
    timestamp: "2026-06-23T09:22:40+0200",
    title: "Overview vocabulary counts now reopen the source text",
    summary:
      "Clicking a saved-word count on the overview now takes you back into the source text and jumps straight to its vocabulary section.",
    userHighlights: [
      "The saved-word count no longer opens the separate vocabulary archive page.",
      "Instead, it reopens the original text where those words came from.",
      "The page lands at the vocabulary area so the jump feels tied to that text.",
    ],
    technicalNotes: [
      "Updated the count action in `src/routes/app/index.tsx` to navigate to the text-details route with a small `jumpTo=vocabulary` search flag.",
      "Extended `src/routes/app/reeprise/$generatedTextId.tsx` search validation and added a vocabulary-section ref plus scroll effect that also reopens the desktop vocabulary column when needed.",
      "Kept the existing overview count rendering and reran focused frontend validation after the interaction change.",
    ],
  },
  {
    id: "overview-now-prefetches-todays-daily-title-before-you-open-it",
    timestamp: "2026-06-23T09:20:18+0200",
    title: "Overview now shows today’s real text title earlier",
    summary:
      "The overview now preloads each active daily text draft in the background, so the landing page can show the real title instead of a generic fallback line.",
    userHighlights: [
      "Today’s overview now shows the actual daily text title sooner.",
      "The generic `Your text for today` fallback appears less often on first open.",
      "Opening the row still uses the same text details flow as before.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/index.tsx` to background-create missing same-day daily drafts for visible languages using the existing `/generated_texts/daily-practice/` endpoint.",
      "Stored returned daily drafts per language in local overview state and reused them as a title source until the normal generated-text list catches up.",
      "Added focused overview regression coverage and rebuilt the frontend after the data-path change.",
    ],
  },
  {
    id: "overview-tasks-now-drop-the-auto-checked-note",
    timestamp: "2026-06-23T09:13:52+0200",
    title: "Overview tasks now drop the extra auto-checked note",
    summary:
      "Overview rows now keep the checklist quieter by removing the extra auto-checked note under items that were already detected as done.",
    userHighlights: [
      "Rows still mark themselves as done automatically when the text has been opened.",
      "The extra `Auto checked` note no longer adds visual noise below the task.",
      "The saved-vocabulary count and main row copy stay unchanged.",
    ],
    technicalNotes: [
      "Removed the `app_overview_auto_checked` render block from `src/routes/app/index.tsx` while keeping the existing checked-state logic intact.",
      "Deleted the now-unused `app_overview_auto_checked` translation entry from `src/assets/translations.json`.",
      "Rebuilt the frontend and reran the focused overview Playwright regression after the copy cleanup.",
    ],
  },
  {
    id: "overview-tasks-now-show-saved-vocabulary-counts-per-text",
    timestamp: "2026-06-23T08:57:29+0200",
    title: "Overview tasks now show saved vocabulary counts per text",
    summary:
      "Overview rows can now show how many words from a text have already been saved to vocabulary, and that count opens the matching vocabulary list.",
    userHighlights: [
      "When a text already has saved vocabulary, the overview now shows the count directly on that row.",
      "Rows with no saved words stay visually quiet and do not show an empty `0` state.",
      "Tapping the count opens the vocabulary list already filtered to that source text.",
    ],
    technicalNotes: [
      "Extended `src/routes/app/index.tsx` so each overview task can fetch and render the text-scoped vocabulary count via the existing `generated_text_id` vocabulary query.",
      "Updated `src/routes/app/vocabulary_progress.tsx` and `src/hooks/useVocabularyStageCounts.ts` to accept a `generatedTextId` search scope and keep the list and stage counts aligned to the same source text.",
      "Added translated count copy in `src/assets/translations.json`, plus overview route regression coverage and a frontend rebuild.",
    ],
  },
  {
    id: "comprehension-questions-now-appear-inline-within-the-reading-text",
    timestamp: "2026-06-22T18:39:21+0200",
    title: "Comprehension questions now appear inline within the reading text",
    summary:
      "The reading comprehension section no longer appears as a separate chat panel below the text. Questions now sit inline at the end of the text body, styled like a natural reading prompt. After each continuation round, a new question section appears after the new text.",
    userHighlights: [
      "Comprehension questions now feel like part of the reading flow, not a separate chatbot.",
      "The question appears in italic serif with a minimal underline-style input.",
      "Previous answers stay collapsed by default and can be expanded when needed.",
      "Correction hints still appear inline when the answer needs improvement.",
      "Each continuation round gets its own question section below the new text.",
    ],
    technicalNotes: [
      "Redesigned `src/components/GeneratedTextComprehensionChat.tsx` to use an inline prompt layout instead of a chat panel.",
      "Added `textOverride` and `childrenAfterBody` props to `src/components/GeneratedTextDetailsTextPane.tsx` for injecting content after the text body.",
      "Added round-based continuation segment tracking in `src/routes/app/reeprise/$generatedTextId.tsx` using a frozen original text ref and per-round streaming state.",
      "Fixed WebSocket subscriber key collision in `src/hooks/useStreamingTextIntegrator.ts` by assigning unique keys per `useStreamingTextSession` instance.",
      "Added `reading_comprehension_previous_answers` translation key in `src/assets/translations.json`.",
      "Updated E2E test selectors in `e2e/reeprise-comprehension-chat.spec.ts` to match the new inline layout.",
    ],
  },
  {
    id: "inline-continuation-now-enforces-word-count-limit",
    timestamp: "2026-06-22T20:00:00+0200",
    title: "Inline continuation now enforces the selected word count limit",
    summary:
      "When you continue a text with \"weiterlesen\", the generated continuation now respects the original word count you selected. If the AI produces more words than the chosen limit, the text is trimmed at the nearest sentence boundary so the continuation stays within the expected length.",
    userHighlights: [
      "Continuation text no longer overshoots the word count you originally selected for the text.",
      "Text trimming respects sentence boundaries so you never end mid-sentence.",
    ],
    technicalNotes: [
      "Added `_enforce_max_word_count_with_sentence_boundary` call to `continue_inline` in `reeprise/views.py` to apply post-processing word count enforcement on continuation text.",
      "Added `_enforce_max_word_count_with_sentence_boundary` to the imports from `.tasks` in `reeprise/views.py`.",
      "All existing `continue_inline` backend tests pass unchanged.",
    ],
  },
  {
    id: "daily-text-drafts-now-survive-idle-time-before-you-open-them",
    timestamp: "2026-06-19T16:01:18+0200",
    title: "Daily text drafts now survive idle time before you open them",
    summary:
      "Daily texts that were prepared earlier in the day no longer fall into a broken failed state before you open them, and opening an untouched failed daily draft now retries generation automatically.",
    userHighlights: [
      "Clicking the daily text card later in the day should no longer land on the `text could not be loaded` failure screen just because the draft had been waiting.",
      "If a same-day daily draft was previously marked failed without ever getting a body, opening it now retries generation instead of leaving you stuck.",
      "Normal manual text failures still keep their existing failure handling.",
    ],
    technicalNotes: [
      "Updated `mark_stuck_background_generations_as_failed()` in `reeprise/tasks.py` so unopened deferred drafts such as daily texts and import-on-open reading cards are excluded from the one-minute stuck-job sweeper.",
      "Extended `ensure_daily_text_generation_started()` so an empty `DAILY_NUDGE` draft that was incorrectly marked `FAILED` can be reset to `PENDING` and re-enqueued safely.",
      "Added backend regression coverage for both lifecycle cases and a Playwright route/state regression that opens a failed empty daily draft and verifies the details page retries instead of showing the failure screen.",
    ],
  },
  {
    id: "generated-text-load-failure-card-now-uses-a-cleaner-professional-layout",
    timestamp: "2026-06-19T16:00:23+0200",
    title:
      "Generated-text load failures now use a cleaner, more professional status card",
    summary:
      "The text-details failure screen has been simplified into a calmer status card without decorative artwork or extra links, so the state reads like a clear product status instead of an illustrated empty state.",
    userHighlights: [
      "The failed-load screen now focuses on one clear retry action.",
      "The decorative illustration and secondary navigation/contact links have been removed.",
      "The text id remains visible in a quieter header treatment for support and debugging context.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/reeprise/$generatedTextId.tsx` so `FailedGeneratedTextScreen` now renders a structured neutral panel with a header row, body copy, status note, and a single primary retry button.",
      "Removed the unused `FailureIllustration`, `Link` import, and failure-screen mailto/back-link props from the generated-text details route.",
      "Updated the shared failure-note translation in `src/assets/translations.json` to match the new no-links status-card wording.",
    ],
  },
  {
    id: "daily-text-callout-now-refreshes-its-morning-title-without-a-manual-reload",
    timestamp: "2026-06-14T20:20:24+0200",
    title:
      "Daily text callout now refreshes its morning title without a manual reload",
    summary:
      "The morning daily-text card now updates its title on its own when the first feed response still carries the temporary placeholder, so the real title appears without needing a page refresh.",
    userHighlights: [
      "Opening the app in the morning should now show the daily text title as soon as it becomes available, even if the first feed response was still in the placeholder state.",
      "The daily callout no longer gets stuck on an empty or generic morning heading until you manually reload the page.",
      "The rest of the feed keeps its existing behavior for finished texts and podcast audio readiness.",
    ],
    technicalNotes: [
      "Updated `useGeneratedTextPaginatedList.ts` so the Reeprise feed keeps polling while a pending `DAILY_NUDGE` entry still lacks a display-ready title, instead of polling only for manual pending texts and missing podcast audio.",
      "Added a Vitest regression for the list polling decision and a Playwright morning-load regression that verifies the daily card picks up the committed title without a browser refresh.",
      "Rebuilt the frontend after the polling change to confirm the TypeScript/Vite bundle still compiles cleanly.",
    ],
  },
  {
    id: "text-details-toggle-vocabulary-column",
    timestamp: "2026-06-18T19:59:46+02:00",
    title: "Vocabulary column can be toggled open and closed on the text details page",
    summary:
      "On desktop, the vocabulary column on the text details page now slides open and closed with a smooth animation, giving more room to focus on the reading pane when vocabulary review is not needed.",
    userHighlights: [
      "A chevron button in the vocabulary column header slides the column closed.",
      "A list icon button at the right edge slides it back open.",
      "The transition is animated so the column does not jump.",
      "Mobile layout is unchanged — the toggle is only available on desktop.",
    ],
    technicalNotes: [
      "Added `showVocabularyColumn` state and `ChevronRightIcon`/`RectangleStackIcon` toggle buttons to `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Vocab column uses `translate-x` and `opacity` CSS transitions inside a `relative overflow-hidden` container that stays at `md:col-start-9 md:col-span-4` so the text pane never reflows.",
      "Added translation keys `hide_vocabulary_column` and `show_vocabulary_column` to `src/assets/translations.json`.",
    ],
  },
  {
    id: "article-links-can-now-create-adapted-reading-cards-from-the-main-generator",
    timestamp: "2026-06-14T16:17:00+0200",
    title:
      "Article links can now create adapted reading cards from the main generator",
    summary:
      "You can now paste an article URL directly into the existing reading generator, let Uxhulu fetch and adapt the article to your saved language level, and open the finished reading card on the normal text page.",
    userHighlights: [
      "A new link-import action now sits beside the image upload entry in the main generate-text card.",
      "Article imports reuse the same reading-card flow as image-based imports, including loading stages, archive persistence, and the normal text details screen.",
      "Finished cards now keep calm source metadata such as the original title, source site, language, excerpt, and source link when that information is available.",
    ],
    technicalNotes: [
      "Added a new authenticated backend create action plus deferred Celery task for URL-based reading imports, including SSRF guards, redirect/size/time limits, HTML extraction heuristics, and JSON adaptation output.",
      "Extended the frontend generated-text import flow with a dedicated article-link mutation, inline URL sheet on `GenerateTextCard.tsx`, pending-stage handling, and a source metadata card on the details screen.",
      "Added backend regression coverage for create, localhost rejection, queue start, successful article adaptation, short-article failure, and a Playwright regression for the route/state flow from pasted link to finished reading card.",
    ],
  },
  {
    id: "language-space-feed-now-stays-on-the-user-scoped-selection-after-login",
    timestamp: "2026-06-14T16:08:10+0200",
    title:
      "Language-space feed now stays on the user-scoped selection after login",
    summary:
      "The active language space now stays consistent across login and same-session switching, so desktop and mobile feeds no longer drift away from the selected space and show texts from the wrong language.",
    userHighlights: [
      "Opening the Italian space after login now keeps the feed on Italian instead of falling back to an older anonymous-space value.",
      "Desktop topbar switching and the mobile language carousel now keep the visible feed in sync with the selected space.",
      "Conflicting old local values from before login should no longer make one part of the app show French while another shows Italian.",
    ],
    technicalNotes: [
      "Refactored `useLocalStorage.ts` to re-read the persisted value whenever its storage key changes and to propagate same-session updates from a ref-backed current value instead of relying on React state-updater side effects.",
      "Kept the existing local-storage broadcast unit coverage and extended `e2e/classic-tools-nav.spec.ts` with a mobile regression that seeds conflicting `language-space` and `language-space:<userId>` values.",
      "Added a desktop regression that switches the Reeprise topbar from French to Italian and verifies the visible feed updates to the Italian card in the same session.",
    ],
  },
  {
    id: "image-reading-now-scales-down-oversized-uploads-before-they-hit-ocr",
    timestamp: "2026-06-14T15:20:33+0200",
    title:
      "Image reading now scales down oversized uploads before they enter OCR",
    summary:
      "Large camera photos are now resized on the frontend before image-reading workflows send them to OCR and adaptation, reducing the risk of local worker crashes on oversized uploads.",
    userHighlights: [
      "Full-resolution phone photos now enter the image-reading flow in a smaller, OCR-friendly size instead of always uploading the original camera file.",
      "The same preprocessing now applies consistently across image-based text workflows, including adaptation and direct text import.",
      "Preview behavior stays the same, but the uploaded file behind the workflow is lighter and more stable for local generation.",
    ],
    technicalNotes: [
      "Added `prepareImageUploadFile.ts` to scale raster uploads down to a 2000px long edge while preserving aspect ratio before they are stored in `pendingImageFile` and the form state.",
      "Updated `GenerateTextCard.tsx` so the resized file is reused for preview, standard image-to-story generation, OCR layout detection, adaptation, import-text creation, and vocabulary photo import.",
      "Added a Vitest regression for the image-dimension constraint helper and rebuilt the frontend after the upload preprocessing change.",
    ],
  },
  {
    id: "local-storage-driven-ui-state-now-updates-outside-react-render",
    timestamp: "2026-06-14T11:19:18+0200",
    title:
      "Local-storage driven UI state now updates outside React render",
    summary:
      "Shared UI state that is mirrored through local storage now broadcasts changes after the current render cycle, preventing React warnings about updating one screen while another component is still rendering.",
    userHighlights: [
      "Create-text and vocabulary preview flows now avoid the render-time warning that could appear after local-storage backed state changed.",
      "Shared selections such as language-space and per-language create defaults now propagate more safely across screens.",
      "This reduces unstable UI behavior around image-reading and card updates that depended on the same stored state.",
    ],
    technicalNotes: [
      "Updated `useLocalStorage.ts` so same-tab subscriber notifications are scheduled in a microtask instead of firing synchronously from inside a React state updater.",
      "Wrapped the hook setter in `useCallback` and kept local persistence plus cross-component sync intact while avoiding cross-component `setState` during render.",
      "Added a Vitest regression for deferred subscriber notification and rebuilt the frontend after the hook change.",
    ],
  },
  {
    id: "image-reading-article-selection-now-keeps-the-preview-image-context-only-by-default",
    timestamp: "2026-06-14T11:03:43+0200",
    title:
      "Image reading article selection now keeps the preview image context-only by default",
    summary:
      "The image-reading article picker now uses the detected article cards as the only default selector, while the page preview stays a calm document reference instead of showing selection boxes or numbered markers.",
    userHighlights: [
      "Detected article cards remain the primary way to choose what to read.",
      "The page preview no longer shows article boxes or numbered markers during normal article selection.",
      "Manual region selection still stays available behind `Manuell auswählen` when the detected list is not enough.",
    ],
    technicalNotes: [
      "Updated `GenerateTextCard.tsx` so the preview overlay SVG is rendered only after manual selection is explicitly enabled, removing bbox geometry from the default article-card flow.",
      "Kept the stable `selectedCandidateId` processing path unchanged so Continue still resolves the exact selected candidate by id before submitting `selection_blocks`.",
      "Extended Playwright coverage to assert the default article-selection state renders no manual overlay and that the overlay appears only after the fallback manual-selection action.",
    ],
  },
  {
    id: "same-language-vocabulary-meanings-now-drop-self-referential-bedeutet-lead-ins",
    timestamp: "2026-06-14T10:50:12+0200",
    title:
      "Same-language vocabulary meanings now drop self-referential `bedeutet` lead-ins",
    summary:
      "When a vocabulary meaning is shown in the same language as the word itself, the card now displays only the meaning body instead of repeating the word in a `word means ...` sentence.",
    userHighlights: [
      "Same-language vocabulary cards in thinker-style flows now show the meaning more directly across languages.",
      "A card such as `Kontemplation` now reveals `tiefes Nachdenken oder Besinnung` instead of `Kontemplation bedeutet ...`.",
      "The same cleanup now applies consistently across the shared vocabulary meaning surfaces.",
    ],
    technicalNotes: [
      "Added shared frontend normalization in `vocabularyTranslationDetails.ts` to strip self-referential same-language lead-ins like `means`, `bedeutet`, and `signifie` when they simply restate the clicked word before the meaning.",
      "Mirrored that cleanup in `reeprise/tasks.py` so newly stored translation payloads normalize the same pattern before persistence.",
      "Strengthened translation-prompt guidance and added backend/frontend regression tests for same-language meaning cards across languages.",
    ],
  },
  {
    id: "image-reading-now-keeps-card-selection-and-processing-bound-to-the-same-candidate-id",
    timestamp: "2026-06-13T21:14:05+0200",
    title: "Image reading now keeps card selection and processing bound to the same candidate id",
    summary:
      "The image-reading picker now keeps the article card, selected state, overlay highlight, and processing step tied to the same stable candidate id, preventing card 4 selections from accidentally submitting card 3 content.",
    userHighlights: [
      "Selecting `Gold für Schwarz` now keeps that candidate id through confirmation instead of drifting to the neighboring article.",
      "The preview image no longer shows permanent numbered markers that could suggest the wrong mapping.",
      "Temporary debug logs now trace click, selected id, and processing candidate so candidate-id mismatches are immediately visible during investigation.",
    ],
    technicalNotes: [
      "Refactored `GenerateTextCard.tsx` so card clicks and overlay clicks both route through one `candidate.id`-based selection handler, and the Continue action resolves the chosen candidate by id before submitting its blocks.",
      "Removed the permanent numbered marker circles/text from the article preview overlay while keeping region highlighting intact.",
      "Added a Playwright regression that clicks the fourth article card and asserts the submitted `selection_blocks` belong to the fourth candidate rather than the third.",
    ],
  },
  {
    id: "image-reading-now-rebinds-misplaced-newspaper-articles-to-their-own-page-blocks",
    timestamp: "2026-06-13T20:58:51+0200",
    title: "Image reading now rebinds misplaced newspaper articles to their own page blocks",
    summary:
      "When a detected newspaper title is paired with the wrong page region, the article picker now reassigns that candidate to the correct unmatched layout block so selecting a side article no longer generates text from its neighbor.",
    userHighlights: [
      "A small article such as `Gold für Schwarz` should now stay tied to its own lower-right page region instead of borrowing the neighboring `Knobloch` block.",
      "The article card title and the highlighted page region should now stay in sync more reliably on dense newspaper pages.",
      "Selecting a listed side-column article should now generate the text from that article instead of from the adjacent one.",
    ],
    technicalNotes: [
      "Added `_realign_article_candidates_to_layout_blocks()` in `reeprise/tasks.py` to force candidate-to-layout assignment onto unique visual blocks, repairing cases where two titled candidates point at the same region.",
      "Applied that realignment after layout supplementation so dense newspaper pages can still keep AI titles while correcting misplaced geometry before the picker renders.",
      "Added a backend regression that reproduces the `Gold für Schwarz`/`Knobloch` geometry swap and verifies the small article is rebound to the correct lower-right block.",
    ],
  },
  {
    id: "image-reading-no-longer-repeats-accepted-article-titles-in-diagnostics",
    timestamp: "2026-06-13T20:53:51+0200",
    title: "Image reading no longer repeats accepted article titles in diagnostics",
    summary:
      "The article picker now keeps the debug diagnostics panel focused on rejected detections only, so accepted article titles no longer appear twice on the same screen.",
    userHighlights: [
      "The main article list remains the single place for choosing a detected article.",
      "The diagnostics panel now appears only when something was actually rejected.",
      "This removes the confusing duplicate-title effect in the article selection step.",
    ],
    technicalNotes: [
      "Updated `GenerateTextCard.tsx` so the diagnostics panel renders only `rejected` `candidate_diagnostics` entries instead of echoing every accepted article candidate below the main list.",
      "Kept the main confidence badges on selectable article cards unchanged while narrowing the secondary panel to true rejection/debug information only.",
      "Re-validated the import-text article picker flow with the existing Playwright regression and a fresh frontend build after the UI cleanup.",
    ],
  },
  {
    id: "image-reading-now-shows-confidence-and-rejection-reasons-for-article-detection",
    timestamp: "2026-06-13T17:28:24+0200",
    title: "Image reading now shows confidence and rejection reasons for detected articles",
    summary:
      "The article picker now exposes the confidence score for detected article candidates and explains when a detected candidate was not kept, making article selection easier to debug on dense newspaper pages.",
    userHighlights: [
      "Detected article cards now show their confidence directly in the picker.",
      "If the detector found an article candidate but did not keep it, the picker now explains why instead of hiding that decision.",
      "This makes it easier to understand cases where a side-column article or edge detection was recognized but not offered as a selectable article.",
    ],
    technicalNotes: [
      "Extended `analyze_image_reading_layout()` in `reeprise/tasks.py` to return `candidate_diagnostics` alongside accepted candidates, including rejection reasons such as outside-image geometry and too-low single-candidate confidence.",
      "Updated `GenerateTextCard.tsx` and the image-reading layout types so the article picker renders per-candidate confidence on accepted cards plus a diagnostic list for rejected detections.",
      "Added backend and Playwright regressions for the diagnostics payload and for rendering a rejected candidate reason in the article-selection UI.",
    ],
  },
  {
    id: "homepage-redesigned-as-editorial-reading-archive",
    timestamp: "2026-06-13T17:25:31+0200",
    title: "Homepage redesigned as editorial reading archive",
    summary:
      "The public landing page has been rebuilt as a two-column editorial archive. The old hero card and feature grid are gone, replaced by a fixed identity column on the left and a masonry-style collection of reading-artifact cards on the right.",
    userHighlights: [
      "The left column stays visible while scrolling and acts as a calm identity anchor with a language switcher, headline, product framing, and subtle navigation links.",
      "Eight archive cards describe the product through evidence rather than marketing: adaptation, progression, content diversity, vocabulary support, lexicon building, text generation, the motivation insight, and a closing statement.",
      "Each card uses typography-driven composition — example text blocks, topic tags, vocabulary artifacts, and magazine-excerpt styling — instead of SaaS-style feature icons or screenshots.",
    ],
    technicalNotes: [
      "Replaced the full homepage in `src/routes/index.tsx` with a two-column `grid` layout (420px sticky left, flexible right) and an `ArchiveCard` pattern used across eight card variants.",
      "Added 64 new `landingpage_archive_*` translation keys with full German editorial copy, plus embedded content strings for the adaptation side-by-side, progression text excerpts, topic tag grid, vocabulary inline example, lexicon artifact cards, and creation sample card.",
      "Updated `e2e/root-language-switch.spec.ts` to verify the new archive headline switches between English and German and that the first archive card content is visible on both desktop and mobile viewports.",
    ],
  },
  {
    id: "image-reading-now-keeps-small-side-column-articles-separate",
    timestamp: "2026-06-13T17:12:20+0200",
    title: "Image reading now keeps small side-column newspaper articles separate",
    summary:
      "Small newspaper items with their own section label, headline, and body are now more likely to stay selectable as independent articles instead of being absorbed into a neighboring story.",
    userHighlights: [
      "Short side-column pieces such as `HAPKIDO – Gold für Schwarz` are now treated as valid article choices instead of being dropped just because they are small.",
      "A neighboring article with a wide detected outline no longer silently hides a separate article that sits beside or below it.",
      "The article picker should now reflect newspaper pages more faithfully when they contain micro-articles or narrow side-column items.",
    ],
    technicalNotes: [
      "Updated the article-candidate prompt in `reeprise/tasks.py` to treat section labels plus headline-plus-body structure as a strong article boundary signal and to explicitly preserve narrow, short, and low-area micro-articles.",
      "Changed layout-block supplementation so it checks a candidate’s explicit content blocks before suppressing a neighboring fallback article, avoiding false coverage from oversized candidate bounding boxes.",
      "Added backend regression coverage for the wide-bbox suppression case and for the prompt contract that protects small section-labeled articles.",
    ],
  },
  {
    id: "image-reading-preview-now-lets-the-page-stand-on-its-own",
    timestamp: "2026-06-13T17:04:43+0200",
    title: "Image reading now lets the uploaded page stand on its own",
    summary:
      "The article-selection preview now treats the uploaded page as the document itself instead of nesting it inside multiple preview panels, so the page feels calmer, larger, and more editorial during article choice.",
    userHighlights: [
      "The large beige preview panel and nested framing layers are gone from the article-selection step.",
      "The uploaded page now appears larger and more direct, so it reads more like a newspaper or document placed into the interface.",
      "Article cards remain the main interaction, while the page preview stays as a quieter confirmation surface in the background.",
    ],
    technicalNotes: [
      "Simplified the article-preview markup in `GenerateTextCard.tsx` so the image itself carries the paper treatment instead of sitting inside stacked preview containers.",
      "Increased the preview scale and reduced overlay weight so article regions remain visible without making the image feel like an OCR configuration panel.",
      "Re-validated the image-reading article-selection flow with the existing frontend regression coverage and a fresh frontend build after the preview restyle.",
    ],
  },
  {
    id: "image-reading-now-keeps-visible-article-blocks-selectable-even-when-ai-undercounts",
    timestamp: "2026-06-13T16:55:06+0200",
    title: "Image reading now keeps visible article blocks selectable when AI undercounts the page",
    summary:
      "If the page detector identifies a real article region but the AI candidate pass misses it or places it fully off-page, the article picker now drops unusable candidates and supplements missing visible blocks so the page stays selectable more completely.",
    userHighlights: [
      "Article entries that have no usable on-page geometry are no longer shown as selectable choices.",
      "When the AI article pass misses one visible article block on the page, the picker now keeps that block available as a fallback article choice instead of silently dropping it.",
      "This reduces the broken state where the list and the page overlays disagree about how many selectable articles the page contains.",
    ],
    technicalNotes: [
      "Adjusted `_normalize_article_candidate_block()` in `reeprise/tasks.py` so fully off-page candidate geometry is rejected instead of being clamped into fake edge slivers that still look selectable.",
      "Added `_supplement_article_candidates_with_layout_blocks()` so unmatched layout-detected article blocks are promoted into fallback candidates when the AI candidate pass undercounts the page.",
      "Expanded backend and Playwright coverage for dropping unusable geometry and for rendering a fallback fourth article candidate when one page block is not represented by the AI-titled list.",
    ],
  },
  {
    id: "image-reading-now-clamps-overflowing-article-origins-before-submit",
    timestamp: "2026-06-13T16:41:53+0200",
    title: "Image reading now corrects overflowing article coordinates before submit",
    summary:
      "Detected article regions that start slightly beyond the page edge are now normalized before submit, preventing valid article choices from being rejected with image-bounds errors.",
    userHighlights: [
      "Selecting an article near the right or bottom edge of the page should no longer fail just because the detected region starts a little outside the image bounds.",
      "The article picker now keeps using the same reading workflow instead of bouncing into a repeated validation error for these edge detections.",
      "The existing retry and choose-another actions remain available if a different article-level processing problem still occurs.",
    ],
    technicalNotes: [
      "Adjusted `normalizeImageReadingSelectionBlocks()` in `GenerateTextCard.tsx` so it clamps `x` and `y` as well as `width` and `height`, matching the backend serializer’s image-bound expectations.",
      "Added a Playwright regression that submits an article candidate whose origin starts past `1.0` on the x-axis and verifies the request is normalized before it reaches `/image-reading-text/`.",
      "Re-verified the image-reading selection flow with targeted browser regressions and a fresh frontend build after the normalization change.",
    ],
  },
  {
    id: "image-reading-preview-now-shows-the-page-as-a-reading-artifact",
    timestamp: "2026-06-13T16:22:52+0200",
    title: "Image reading now presents the uploaded page more like printed matter",
    summary:
      "The article picker now shows the uploaded page with a lighter editorial treatment, so it feels more like a newspaper or document resting in the interface and less like a technical OCR preview panel.",
    userHighlights: [
      "The beige preview frame is gone, giving the uploaded page more direct visual presence.",
      "The page now sits in wider whitespace with a softer layered-paper composition that feels calmer and more editorial.",
      "The article list stays the main interaction, while the image works as a trustworthy reading artifact in the background.",
    ],
    technicalNotes: [
      "Restyled the article preview surface in `GenerateTextCard.tsx` to remove the framed panel treatment and replace it with a lighter layered-paper presentation around the image itself.",
      "Kept the article overlays, tap behavior, and selection state intact while reducing the visual weight of the surrounding preview chrome.",
      "Re-verified the image-reading flow with the existing Playwright regression set and a fresh frontend build to ensure the presentation change did not disturb article selection behavior.",
    ],
  },
  {
    id: "image-reading-now-keeps-article-processing-errors-in-the-selection-step",
    timestamp: "2026-06-13T16:17:48+0200",
    title: "Image reading now keeps article-processing failures in the article picker",
    summary:
      "If a selected article cannot be submitted, the image-reading modal now stays in the article picker, shows the real processing error, and retries the selected article instead of incorrectly pretending that article detection failed.",
    userHighlights: [
      "Selecting an article near the page edge no longer fails just because one detected block extends slightly past the image bounds.",
      "When article submission does fail, the modal now explains that the selected article could not be used instead of showing the wrong article-loading error.",
      "You can now retry the same article or choose another one directly from that failure state without restarting the whole image flow.",
    ],
    technicalNotes: [
      "Normalized article selection blocks in `GenerateTextCard.tsx` before submit so candidate boxes are clamped to backend-safe image bounds and rounded to the same precision the serializer expects.",
      "Updated `useGeneratedTextCreateFromImage.ts` to surface backend validation messages from `/image-reading-text/` instead of collapsing every non-auth failure into one generic frontend error.",
      "Expanded Playwright coverage for edge-of-image block clamping and for the corrected processing-error state that stays inside article selection with retry and choose-another actions.",
    ],
  },
  {
    id: "image-reading-article-choice-now-centers-the-detected-article-list",
    timestamp: "2026-06-13T15:49:33+0200",
    title: "Image reading now presents detected articles as the main choice",
    summary:
      "The article-selection step now feels like choosing what to read. Detected article cards lead the interaction, while the uploaded page stays visible as calmer visual context instead of acting like an OCR tool first.",
    userHighlights: [
      "Detected article cards now sit at the center of the screen, so choosing an article starts from the list instead of from page overlays.",
      "The uploaded image now appears as a quieter paper-style reading artifact that confirms where the articles came from.",
      "Direct page tapping is still available, but only after you explicitly open the manual fallback instead of seeing image interaction as the default mode.",
    ],
    technicalNotes: [
      "Reworked the `ImageReadingArticleSelectionStep` layout in `GenerateTextCard.tsx` so candidate cards render as the primary selector and the image preview becomes a secondary context panel with quieter numbered overlays.",
      "Added an explicit manual page-selection toggle that keeps image tapping available without restoring the removed lasso workflow or making the image the default control surface.",
      "Expanded Playwright coverage to assert the new article-count copy, the list-first fallback copy, and the manual page-selection hint behind its dedicated button.",
    ],
  },
  {
    id: "image-reading-modal-now-leaves-the-action-menu-immediately",
    timestamp: "2026-06-13T15:43:00+0200",
    title: "Image reading now leaves the action menu immediately after you choose a text workflow",
    summary:
      "Choosing `Import Text` or `An mein Niveau anpassen` in the image modal now switches straight into loading and article selection states, so the modal no longer looks frozen while the image is being analyzed.",
    userHighlights: [
      "After you tap a reading-text option, the action menu disappears right away instead of staying visible as if nothing happened.",
      "The modal now shows an explicit loading state while article detection runs and an explicit retry state if that detection fails.",
      "The normal OCR reading flow remains unchanged once candidates are found or a single article can be continued automatically.",
    ],
    technicalNotes: [
      "Adjusted `GenerateTextCard.tsx` so OCR-reading choices move the modal into `select_article` before the async layout request finishes, with `detecting_articles` driving the loading branch.",
      "Added an explicit in-modal error branch with retry and back actions so layout-analysis failures no longer render as an unresponsive chooser.",
      "Expanded Playwright coverage for immediate modal transition, loading visibility during pending detection, and retry-state rendering after a failed layout request.",
    ],
  },
  {
    id: "daily-card-now-keeps-the-title-translation-as-its-subtitle",
    timestamp: "2026-06-13T15:30:45+0200",
    title: "Daily card now keeps the title translation as its subtitle",
    summary:
      "The daily card’s second line now stays the mother-tongue translation of the daily title instead of switching to a body preview after the text finishes.",
    userHighlights: [
      "The daily card now keeps one stable two-line meaning: the learning-language title on top and its mother-tongue translation underneath.",
      "Returning to the feed after opening a daily text no longer swaps the subtitle into a sentence from the generated text.",
      "When the same-day card remounts or refetches, the translated subtitle stays attached to that daily title instead of falling back to generic text.",
    ],
    technicalNotes: [
      "Extended the reeprise daily marker in `reeprise/index.tsx` to persist both the committed daily title and its mother-tongue subtitle for the active day and language.",
      "Changed the featured daily-card subtitle resolution to use the translated committed title for both pending and completed daily texts, removing the completed-state body-preview branch.",
      "Expanded Playwright regression coverage so the daily card keeps the translated subtitle after opening the text, completing generation, and returning to the feed.",
    ],
  },
  {
    id: "daily-feed-now-groups-unread-texts-before-read-ones",
    timestamp: "2026-06-13T11:48:56+0200",
    title: "Daily feed now keeps unread texts ahead of read ones",
    summary:
      "The daily text still appears as the featured card, while the remaining feed now keeps unread texts above read ones so recent unfinished reading stays easier to return to.",
    userHighlights: [
      "Unread texts now stay together near the top of the feed instead of being mixed into older read items by timestamp alone.",
      "Older daily texts remain visible in the normal feed without duplicating the featured daily card.",
      "A subtle divider now separates unread texts from read ones when both groups are present.",
    ],
    technicalNotes: [
      "Added `reepriseFeedOrdering.ts` to centralize read-state detection, share-aware timestamps, and unread-before-read sorting for generated texts.",
      "Updated `reeprise/index.tsx` to partition the post-featured feed into unread and read groups while continuing to exclude only the featured same-day `DAILY_NUDGE` item from the normal list.",
      "Added Vitest and Playwright regression coverage for feed ordering, divider rendering, and older daily entries staying in the main feed without duplicate featured rendering.",
    ],
  },
  {
    id: "daily-titles-now-stay-stable-across-card-open-and-completion",
    timestamp: "2026-06-13T11:11:32+0200",
    title: "Daily text titles now stay stable from the card through reading",
    summary:
      "A daily text now keeps the same title once it becomes visible. Opening the text can still start the body generation, but the title no longer changes later or falls back to generic placeholder copy.",
    userHighlights: [
      "The daily card, the reading header, and the finished text now keep one stable title for the same daily text.",
      "A visible daily title no longer jumps back to `Text des Tages` or changes to a different topic after you open it.",
      "Placeholder-style feed titles such as `daily learning text – italian – 2026-06-12` are now suppressed while a real daily title is available.",
    ],
    technicalNotes: [
      "Updated deferred daily generation in `reeprise/tasks.py` so an existing committed daily title is reused instead of being recomputed during `complete_generated_text()`, and the body prompt is anchored to that committed title.",
      "Unified pending-title resolution in `ReepriseTextCard.tsx`, `reeprise/index.tsx`, and `$generatedTextId.tsx` so featured cards, feed cards, and details headers all follow the same placeholder-vs-committed-title rules without retriggering generation from topic changes.",
      "Added backend, Vitest, and Playwright regression coverage for committed daily-title reuse, placeholder suppression, and mobile open-flow stability.",
    ],
  },
  {
    id: "image-reading-no-longer-offers-lasso-selection",
    timestamp: "2026-06-12T20:28:34+0200",
    title: "Image reading no longer offers lasso selection in the article picker",
    summary:
      "The image-reading article picker now stays entirely tap-first. The broken lasso workflow has been removed from this screen so article selection can no longer fall back into point-by-point drawing.",
    userHighlights: [
      "Uploading a reading image now keeps you in article selection or full-image fallback instead of opening a drawing tool.",
      "There are no more point clicks, stroke paths, `Undo Last Stroke`, or `Clear Selection` controls in the OCR article picker.",
      "For unclear layouts, the remaining fallback is to use the full image rather than forcing a lasso interaction that was not working reliably.",
    ],
    technicalNotes: [
      "Removed the `ImageReadingManualSelectionStep` path from `GenerateTextCard.tsx` and deleted all modal buttons that could switch the image-reading flow into lasso mode.",
      "Kept article-candidate selection, grouped candidate submission, and full-image fallback intact so the OCR pipeline still has a reliable non-drawing path.",
      "Re-verified the image-reading frontend flow with the existing targeted Playwright regressions and frontend build.",
    ],
  },
  {
    id: "image-reading-now-detects-tappable-article-candidates-first",
    timestamp: "2026-06-12T20:26:07+0200",
    title: "Image reading now detects tappable article candidates before any manual fallback",
    summary:
      "Full-page newspaper and magazine uploads now try to detect article-level candidates with titles and grouped text regions first, so the default interaction is tapping an article instead of drawing around it.",
    userHighlights: [
      "When the page contains several reading articles, the default selection screen now highlights named article candidates instead of opening the drawing tool.",
      "Selecting an article now carries all of that article’s grouped text blocks forward, which is important for multi-column newspaper layouts.",
      "Manual selection still exists, but only as a fallback when article detection is unclear or you explicitly choose it.",
    ],
    technicalNotes: [
      "Extended `analyze_image_reading_layout()` in `reeprise/tasks.py` to prefer article-level candidates with grouped blocks and titles, using a vision-based candidate pass for dense layouts before falling back to coarse heuristic blocks.",
      "Updated the `GenerateTextCard.tsx` candidate-selection UI and TypeScript contract so one tapped article can highlight and submit multiple grouped selection blocks instead of a single rectangle.",
      "Refreshed backend and Playwright regression coverage to assert article-candidate-first behavior, grouped block submission, and continued fallback support without making the lasso tool the primary path.",
    ],
  },
  {
    id: "image-reading-no-longer-auto-accepts-a-whole-newspaper-page",
    timestamp: "2026-06-12T20:05:40+0200",
    title: "Image reading no longer auto-starts from a full newspaper page",
    summary:
      "When the page detector only sees one coarse full-page block, the app now stops and asks you what to use instead of silently treating the first page-sized result as the selected article.",
    userHighlights: [
      "Dense newspaper photos no longer jump straight into generation just because the detector found one page-wide block.",
      "The app now keeps the reading workflow in article-selection mode when detection is too coarse to trust.",
      "Single-text photos can still continue directly when the detector finds one clear text area instead of the whole page.",
    ],
    technicalNotes: [
      "Adjusted `analyze_image_reading_layout()` in `reeprise/tasks.py` so full-page fallback blocks are marked as low-confidence coarse detections instead of valid single-article candidates.",
      "Added backend regression coverage to prove coarse full-page detections return `detection_status: \"none\"` and do not create a default candidate for auto-continue.",
      "Kept the existing frontend article-selection state machine intact so generation still starts only after an explicit valid selection or a deliberate full-image fallback.",
    ],
  },
  {
    id: "image-reading-selection-now-prefers-tap-to-choose-articles",
    timestamp: "2026-06-12T19:44:23+0200",
    title: "Image reading now lets you tap an article instead of drawing around it",
    summary:
      "Newspaper and magazine uploads now default to detected article regions you can tap directly, while manual drawing remains available only as a fallback when the automatic choice is not right.",
    userHighlights: [
      "If one clear article is detected, the app continues automatically without interrupting you.",
      "If several article blocks are detected, the image now shows subtle tappable regions and a matching list below the image so you can choose the text you want.",
      "Manual selection is still available, but it now sits behind `Select manually` instead of being the primary interaction.",
    ],
    technicalNotes: [
      "Refactored the OCR image-reading selection modal in `GenerateTextCard.tsx` so `Import Text` and `Adapt To My Level` now use tap-to-select candidates first, with the older block-snapped drawing mode kept only as a fallback.",
      "Extended `analyze_image_reading_layout()` in `reeprise/tasks.py` to return article candidates plus detection status, allowing the frontend to auto-continue for a single clear block, show selection for multiple blocks, and present a no-block fallback state when detection is weak.",
      "Updated backend tests, Playwright coverage, translations, and build verification so the revised article-selection flow stays compatible with the existing OCR reading-card pipeline.",
    ],
  },
  {
    id: "image-reading-now-opens-article-selection-only-when-the-page-needs-it",
    timestamp: "2026-06-12T19:09:27+0200",
    title: "Image reading now opens article selection only for multi-article pages",
    summary:
      "Full-page uploads can now be narrowed to one article with a pen-based block selection step, while ordinary single-text photos continue directly into the existing reading-card flow without extra interruption.",
    userHighlights: [
      "Import Text and Adapt To My Level now analyze the uploaded page first and skip selection when the image already looks like one coherent text.",
      "When a page contains multiple article blocks, you can paint roughly over the parts you want and the app snaps that selection to detected text regions before OCR starts.",
      "The resulting reading card still follows the normal text-details flow, but OCR now stays scoped to the selected article instead of the whole newspaper page.",
    ],
    technicalNotes: [
      "Added `POST /api/reeprise/generated_texts/image-reading-layout/` plus normalized `selection_blocks` support on the existing image-reading create endpoint so the frontend sends snapped article regions instead of raw pen strokes.",
      "Updated `GenerateTextCard.tsx` with a conditional pen-selection step for OCR reading workflows only, keeping `Generate Story` and `Extract Vocabulary` unchanged and preserving the direct path for single-flow images.",
      "Extended the OCR task pipeline to defer generation until the details page opens, stitch selected article blocks into layout order before extraction, store selection metadata on `image_import_metadata`, and fail explicitly when the selected area does not resolve into one reliable article.",
    ],
  },
  {
    id: "daily-text-now-stays-title-only-until-opened",
    timestamp: "2026-06-12T10:22:00+0200",
    title: "Daily text now stays title-only until you open it",
    summary:
      "Daily texts no longer generate their full body in the background just because the card appears in the app. The app now creates only the title-first draft automatically and starts the full text only after you open the details page.",
    userHighlights: [
      "Seeing a daily card no longer spends full text generation on its own.",
      "The card still shows a real title so you can decide whether to read it.",
      "The full text begins only after you tap `Öffnen` and land on the text details page.",
    ],
    technicalNotes: [
      "Changed `create_practice_text_for_user_language()` in `reeprise/tasks.py` to prepare only the committed daily title instead of auto-enqueueing `complete_generated_text()`.",
      "Added a dedicated `start-generation` action in `reeprise/views.py` and wired `src/routes/app/reeprise/$generatedTextId.tsx` to trigger it only for pending `DAILY_NUDGE` drafts after navigation.",
      "Updated backend and E2E coverage to prove the daily flow remains title-first until the details page is opened.",
    ],
  },
  {
    id: "image-reading-adaptation-now-keeps-more-specific-source-concepts",
    timestamp: "2026-06-11T21:30:50+0200",
    title: "Image reading adaptation now keeps more of the source’s concrete meaning",
    summary:
      "Adapted reading texts from uploaded images now hold on to more of the source’s actual concepts and wording instead of flattening them into broad summary-style sentences.",
    userHighlights: [
      "Important source concepts such as medicines, organizations, places, and named topics are now more likely to stay visible in the adapted reading text.",
      "Lower-level adaptations should now read more like a faithful rewritten text and less like a short summary of the original.",
      "When a term is difficult, the adaptation is now guided to keep it and explain it through simpler surrounding language instead of removing it entirely.",
    ],
    technicalNotes: [
      "Strengthened the image-reading adaptation prompt in `reeprise/tasks.py` so the single rewrite call explicitly preserves concrete concepts, avoids vague replacements, and rejects summary-like compression.",
      "Added backend regression coverage to lock the new prompt contract around core-concept preservation for OCR-based reading-card adaptation.",
      "Left the direct `Import Text` path and ordinary generated-text workflows unchanged.",
    ],
  },
  {
    id: "image-reading-text-imports-now-handle-longer-and-messier-sources",
    timestamp: "2026-06-11T21:12:47+0200",
    title: "Image reading imports now handle longer and messier source texts more clearly",
    summary:
      "OCR-based reading cards now fail more explicitly on unsuitable uploads, keep the original image style more faithfully, and explain when very long source texts had to be shortened for the final reading card.",
    userHighlights: [
      "Image reading uploads now show a clearer in-progress state directly on the reading page while the card is being prepared.",
      "If the upload is not a continuous reading text or the OCR is too unreliable, the failure screen now explains the specific reason instead of showing only a generic error.",
      "Very long source texts can still be imported, but the final reading card now stops at a controlled length and tells you why it is shorter.",
    ],
    technicalNotes: [
      "Extended the image-import metadata contract with OCR confidence, reading-order confidence, source/final lengths, shortening state, and processing-stage tracking so the details route can render state-specific UI.",
      "Updated `complete_generated_text_from_image` to reject unsupported/non-continuous sources explicitly, preserve style guidance through the single rewrite call, and mark long-source outputs as shortened without reusing the normal Generate Text word-count cap.",
      "Added backend regression coverage for unsupported sources, long-source shortening metadata, and the current JSON-based OCR reading-card generation path.",
    ],
  },
  {
    id: "image-reading-texts-now-keep-their-full-length",
    timestamp: "2026-06-11T20:54:49+0200",
    title: "Image reading texts now keep their full source length",
    summary:
      "Reading texts created from uploaded images are no longer constrained by the Generate Text word-count setting. They now keep their natural rewritten length in the active language space.",
    userHighlights: [
      "An image-based reading text is no longer squeezed into the current `50`, `100`, `200`, or `300` word setting.",
      "Longer source pages can now become longer reading texts when the workflow needs that length.",
      "Language space and level still control the output language and difficulty, but not its artificial length cap.",
    ],
    technicalNotes: [
      "Locked the image-reading workflow contract with backend regression coverage so `complete_generated_text_from_image` preserves long rewritten output even when the initial record starts at the normal short default.",
      "Kept the standard Generate Text length controls unchanged for the ordinary text-generation flow; this adjustment applies only to OCR-based reading-text creation.",
      "Left the active language-space rewrite behavior intact while separating it from Generate Text composer length defaults.",
    ],
  },
  {
    id: "generate-text-image-ocr-reading-workflows",
    timestamp: "2026-06-11T16:16:54+0200",
    title: "Generate Text image uploads can now become reading texts directly",
    summary:
      "After uploading an image in Generate Text, you can now turn detected text into a normal reading card in your current language space, either as a closer import or a more simplified adaptation.",
    userHighlights: [
      "The photo action chooser now includes `Import Text` and `Adapt To My Level` alongside the existing story and vocabulary options.",
      "Import Text now rewrites the detected source into the active space language and level while keeping the text close to the original structure and meaning.",
      "Adapt To My Level also stays in the active space language, but simplifies more aggressively for the saved reading level without turning the text into a summary or bullet list.",
    ],
    technicalNotes: [
      "Added `POST /api/reeprise/generated_texts/image-reading-text/` plus a dedicated Celery task that performs OCR, rewrites the extracted text into the active space language at the resolved level, derives a title, and stores the result in a normal `GeneratedText` record.",
      "Kept the existing image-to-story submit path and the existing photo-vocabulary import pipeline unchanged; the new workflows branch separately from the same modal.",
      "Extended `GenerateTextCard.tsx`, translations, backend tests, and route-level generation coverage so the new image-reading flows navigate into the standard text-details screen.",
    ],
  },
  {
    id: "daily-text-weather-card-weekday-now-matches-the-serif-date-style",
    timestamp: "2026-06-11T16:02:12+0200",
    title: "Daily text weather card weekday now matches the serif date style",
    summary:
      "The weekday on the daily text weather card now uses the same serif font, size, and weight as the date, so both metadata labels share the quieter date styling.",
    userHighlights: [
      "The weekday now matches the serif date treatment.",
      "Both metadata labels keep the same visual weight.",
      "The header line now reads more consistently across daily cards.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` so `badgeText` reuses the existing serif `date` typography token in every language-space theme.",
      "Left the metadata colors unchanged and adjusted only the weekday typography mapping.",
      "Kept the learning-language date formatter and card behavior unchanged.",
    ],
  },
  {
    id: "daily-text-weather-card-metadata-now-uses-one-shared-type-style",
    timestamp: "2026-06-11T15:59:05+0200",
    title: "Daily text weather card metadata now uses one shared type style",
    summary:
      "The weekday and date on the daily text weather card now use the same font style and size, so the header metadata reads as one calm, consistent line.",
    userHighlights: [
      "The weekday and date now match visually on the daily text weather card.",
      "The serif date treatment was removed from this metadata row.",
      "The card header now feels more consistent across language spaces.",
    ],
    technicalNotes: [
      "Replaced the separate daily-card `badge` and `date` typography tokens in `src/routes/app/reeprise/index.tsx` with one shared `metadata` style.",
      "Applied that shared metadata class to both `badgeText` and `dateText` across all language-space theme variants.",
      "Kept the existing learning-language date formatting logic unchanged so this release only adjusts typography consistency.",
    ],
  },
  {
    id: "daily-text-weather-card-now-shows-day-and-date-in-the-learning-language",
    timestamp: "2026-06-11T15:53:23+0200",
    title: "Daily text weather card now shows day and date in the learning language",
    summary:
      "The daily text weather card now formats its weekday and date using the active learning language instead of the app UI language, so English cards show labels like `Thursday` and `11 June` while other language spaces follow their own local convention.",
    userHighlights: [
      "The weekday on the daily text weather card now follows the learning language you are practicing.",
      "The date now uses that language’s normal month formatting instead of the previous abbreviated UI-locale version.",
      "Switching language spaces updates the card header language accordingly.",
    ],
    technicalNotes: [
      "Extracted daily-card date formatting into `src/utils/dailyPracticeDateFormat.ts` with explicit locale mapping by learning-language id and an English fallback.",
      "Updated `src/routes/app/reeprise/index.tsx` so the weather card derives `weekdayLabel` and `dateLabel` from the resolved learning language instead of `useTranslation().language`.",
      "Added `src/utils/dailyPracticeDateFormat.test.ts` to lock English, German, and fallback formatting behavior.",
    ],
  },
  {
    id: "vocabulary-reveal-rolled-back-to-simple-click-blur-steps",
    timestamp: "2026-06-09T19:44:55+0200",
    title: "Vocabulary reveal is back to simple click-based blur steps",
    summary:
      "The recent experimental reveal treatments have been rolled back. The vocabulary answer now keeps the simpler tap-to-advance interaction, but the visual treatment is once again just a staged blur from hidden to readable.",
    userHighlights: [
      "The slider stays removed.",
      "Each tap still reveals the answer in steps.",
      "The reveal styling is back to a plain blurred word instead of the more experimental versions.",
    ],
    technicalNotes: [
      "Simplified `src/components/VocabularyRevealText.tsx` back to one full-word blur layer without edge masks or alternate overlays.",
      "Retuned `src/utils/vocabularyRevealEffect.ts` to a plain four-step blur progression and removed the extra reveal metadata used by the experimental states.",
      "Restored the previous bordered reading-card surface in `src/routes/app/reeprise/$generatedTextId.tsx` and updated `src/utils/vocabularyRevealEffect.test.ts` to match the simpler reveal contract.",
    ],
  },
  {
    id: "vocabulary-reveal-first-step-now-shows-a-clearer-silhouette",
    timestamp: "2026-06-09T19:42:31+0200",
    title: "Vocabulary reveal first step now shows a clearer silhouette",
    summary:
      "The first reveal step has been made noticeably more visible so the learner can sense the word form immediately instead of seeing an almost vanished blur.",
    userHighlights: [
      "The first step now shows a clearer silhouette of the word.",
      "It still stays hidden enough for guessing, but it no longer feels nearly invisible.",
      "The later steps keep the same progression.",
    ],
    technicalNotes: [
      "Raised the first-stage opacity and reduced its blur in `src/utils/vocabularyRevealEffect.ts` after the previous tuning still rendered too faint on the real card.",
      "Kept the stable-width reveal layout unchanged so only first-step visibility was adjusted.",
      "Reused the existing reveal tests because the four-step contract and progression logic did not change.",
    ],
  },
  {
    id: "vocabulary-reveal-first-step-now-hints-at-the-word-shape",
    timestamp: "2026-06-09T19:40:42+0200",
    title: "Vocabulary reveal now hints at the word earlier",
    summary:
      "The first hidden step now gives a faint sense of the word shape instead of hiding it almost completely. This keeps the guessing interaction intact while making the first tap more useful.",
    userHighlights: [
      "The first reveal step now lets you roughly sense what the word might be.",
      "The word is still clearly hidden, but it no longer disappears into an almost unreadable fog.",
      "The later reveal steps stay the same.",
    ],
    technicalNotes: [
      "Retuned the first-stage blur and opacity values in `src/utils/vocabularyRevealEffect.ts` so the initial reveal preserves a clearer silhouette.",
      "Kept the stable-width reveal behavior introduced in the previous pass and changed only the first-stage readability level.",
      "Extended `src/utils/vocabularyRevealEffect.test.ts` to assert that the first stage still stays blurrier than later stages while remaining faintly visible.",
    ],
  },
  {
    id: "vocabulary-reveal-third-step-now-keeps-one-stable-word-width",
    timestamp: "2026-06-09T19:23:32+0200",
    title: "Vocabulary reveal now keeps one stable word width across steps",
    summary:
      "The reveal states now keep the same underlying word layout instead of stretching or splitting the text. The third step still makes the beginning and end clearer, but it does so without widening the word.",
    userHighlights: [
      "The third reveal step no longer changes the word width.",
      "The hidden and slightly readable states now behave more like the same word moving through different blur levels.",
      "The move from stronger blur to lighter blur is softer than before.",
    ],
    technicalNotes: [
      "Reworked `src/components/VocabularyRevealText.tsx` so the third stage uses a sharp masked edge overlay on top of the same blurred full-word base instead of splitting the text into separate spans.",
      "Retuned `src/utils/vocabularyRevealEffect.ts` to remove spacing and horizontal scaling from all reveal stages and keep the word layout stable.",
      "Extended `src/utils/vocabularyRevealEffect.test.ts` to lock in the no-stretch reveal contract and the new edge-overlay stage metadata.",
    ],
  },
  {
    id: "vocabulary-cards-now-use-softer-surfaces-without-heavy-strokes",
    timestamp: "2026-06-09T19:19:46+0200",
    title: "Vocabulary cards now use softer surfaces without heavy strokes",
    summary:
      "The reading-view vocabulary cards no longer use the darker framed card outline. The reveal interaction stays the same, but the card surface is quieter and less boxed in.",
    userHighlights: [
      "Vocabulary cards now feel lighter because the heavy outer stroke has been removed.",
      "The detail section inside each card is also less framed, so the word and reveal states stand out more clearly.",
      "The four-step click reveal stays unchanged.",
    ],
    technicalNotes: [
      "Removed the outer border treatment from the vocabulary item surface in `src/routes/app/reeprise/$generatedTextId.tsx` and shifted it to a softer background-only card.",
      "Dropped the bordered subpanel styling for the lower structure/details section in the same component and replaced it with a lighter inset surface.",
      "Left the current four-stage reveal logic untouched so the change is strictly a card-surface adjustment.",
    ],
  },
  {
    id: "vocabulary-reveal-simplified-to-four-click-readability-steps",
    timestamp: "2026-06-09T19:14:51+0200",
    title: "Vocabulary reveal now uses four click-based readability steps",
    summary:
      "The experimental ribbed-glass treatment has been removed. The reveal interaction now uses four explicit click states: not readable, slightly readable, edge-readable, and fully readable.",
    userHighlights: [
      "Each tap now moves through one simple readability step instead of switching into a poster-style visual effect.",
      "The third step makes the beginning and end of the word clearer while the middle stays obscured.",
      "The final step shows the word plainly, and the next tap resets the cycle.",
    ],
    technicalNotes: [
      "Removed the reusable `src/components/RibbedGlassWord.tsx` renderer and simplified `src/components/VocabularyRevealText.tsx` back to text-focused stage rendering.",
      "Retuned `src/utils/vocabularyRevealEffect.ts` to model four explicit readability modes instead of distortion metadata for the ribbed-glass effect.",
      "Updated `src/utils/vocabularyRevealEffect.test.ts` and kept the existing click-to-advance reveal flow intact across all current vocabulary surfaces.",
    ],
  },
  {
    id: "vocabulary-ribbed-glass-now-uses-light-ribs-on-dark-ground",
    timestamp: "2026-06-09T19:11:03+0200",
    title: "Vocabulary ribbed-glass effect now uses light ribs on dark ground",
    summary:
      "The ribbed-glass reveal now inverts its tonal values so the optical ribs are the illuminated element, while the spaces between them remain dark.",
    userHighlights: [
      "The word now emerges from warm light bands instead of being cut by dark stripes.",
      "The overall look feels more internally lit and closer to white ribbed glass on a dark background.",
      "The distortion behavior stays the same. Only the tonal relationship was inverted.",
    ],
    technicalNotes: [
      "Retuned the rib overlay in `src/components/RibbedGlassWord.tsx` from dark stripes to bright cream/ivory ribs with darker intervening gaps.",
      "Lifted the text gradient and background glow values in `src/components/RibbedGlassWord.tsx` and `src/components/VocabularyRevealText.tsx` to support the brighter rib system.",
      "Kept the existing SVG displacement/filter pipeline unchanged so the visual geometry stays stable while luminance is reversed.",
    ],
  },
  {
    id: "vocabulary-reveal-now-uses-svg-ribbed-glass-refraction",
    timestamp: "2026-06-09T19:05:28+0200",
    title: "Vocabulary reveal now uses SVG ribbed-glass refraction",
    summary:
      "The reveal effect has been rebuilt as a reusable ribbed-glass component using real SVG displacement plus a crisp foreground stripe layer, replacing the previous text-styling approximation.",
    userHighlights: [
      "The hidden word is now refracted through sharp vertical ribs instead of just looking blurred.",
      "The visible state still keeps the optical distortion, so the word looks viewed through lenticular glass rather than becoming plain text.",
      "The treatment feels closer to the reference poster effect: cropped, moody, and optically sliced.",
    ],
    technicalNotes: [
      "Added reusable `src/components/RibbedGlassWord.tsx` with SVG `feTurbulence` + `feDisplacementMap`, oversized serif text, warm dark background, and a crisp rib overlay.",
      "Simplified `src/components/VocabularyRevealText.tsx` into a wrapper around the new reusable ribbed-glass component using stage-based distortion values.",
      "Retuned `src/utils/vocabularyRevealEffect.ts` and updated `src/utils/vocabularyRevealEffect.test.ts` to reflect the new SVG-driven reveal model.",
    ],
  },
  {
    id: "vocabulary-reveal-now-stays-stylized-when-visible-and-turns-darker-when-hidden",
    timestamp: "2026-06-09T18:54:18+0200",
    title: "Vocabulary reveal now stays stylized when visible and turns darker when hidden",
    summary:
      "The reveal treatment now keeps a ribbed scanline look even in the visible state, while the hidden stages are darker and much less legible than before.",
    userHighlights: [
      "A fully revealed word no longer snaps back to plain text. It stays inside the same visual language as the hidden states.",
      "The earliest hidden stages are now much harder to read at a glance.",
      "The result is closer to the reference image: soft contours, vertical ribbing, and a controlled editorial distortion.",
    ],
    technicalNotes: [
      "Retuned `src/utils/vocabularyRevealEffect.ts` so the early stages use stronger blur, wider displacement, and lower readable opacity.",
      "Updated `src/components/VocabularyRevealText.tsx` so the revealed state still renders a scanline overlay and softened contour instead of plain text.",
      "Extended `src/utils/vocabularyRevealEffect.test.ts` to match the new final-stage styling contract.",
    ],
  },
  {
    id: "vocabulary-reveal-effect-now-treats-hidden-words-more-like-typography",
    timestamp: "2026-06-09T18:51:21+0200",
    title: "Vocabulary reveal now treats hidden words more like typography",
    summary:
      "The hidden-word reveal effect has been refined from a simple obscuring filter into a more deliberate typographic treatment with shifting echoes and character-level interference.",
    userHighlights: [
      "Hidden answers now feel more like a visual study of the word than a plain blur mask.",
      "Early reveal stages keep the word present but unstable, then tighten gradually into a readable form.",
      "The same art direction now carries across the reading card, word flyout, and trainer preview.",
    ],
    technicalNotes: [
      "Extended `src/utils/vocabularyRevealEffect.ts` with stronger stage metadata for shift, shear, spacing, and faint readability.",
      "Updated `src/components/VocabularyRevealText.tsx` to add layered ghost text plus a character-level wave/displacement layer on top of the scanline treatment.",
      "Reused the same component in `src/routes/app/reeprise/$generatedTextId.tsx`, `src/components/GeneratedTextDetailsTextPane.tsx`, and `src/components/VocabularyPreviewCard.tsx` so reveal styling stays consistent.",
    ],
  },
  {
    id: "a0-text-generation-prompt-now-prioritizes-effortless-reading-flow",
    timestamp: "2026-06-09T18:45:19+02:00",
    title: "A0 text generation now prioritizes effortless reading flow",
    summary:
      "Absolute-beginner texts now push harder for calm, easy reading flow without dropping the existing short-sentence and simple-vocabulary limits.",
    userHighlights: [
      "A0 texts now stay more steadily on one small topic or scene instead of drifting into extra plot.",
      "Beginner reading should feel calmer and easier to continue line by line.",
      "The generator is now more explicitly guided toward concrete, pleasant, almost dictionary-free reading.",
    ],
    technicalNotes: [
      "Merged new A0 prompt guidance into `reeprise/tasks.py` so the level instructions now emphasize effortless reading flow, gradual information, concrete examples, and low-plot complexity.",
      "Kept the existing A0 hard constraints in place, including very short sentences, simple SVO structure, and the restricted-vocabulary bias.",
      "Added backend regression coverage in `reeprise/tests.py` to lock the merged A0 prompt contract.",
    ],
  },
  {
    id: "vocabulary-preview-card-now-reveals-answers-in-tapped-stages",
    timestamp: "2026-06-09T18:36:15+0200",
    title: "Vocabulary preview answers now reveal in tapped stages",
    summary:
      "The vocabulary preview card no longer uses a blur slider. Instead, the answer now reveals gradually with repeated taps, giving the guess-first interaction a calmer, more tactile progression.",
    userHighlights: [
      "Tap the answer area repeatedly to reveal more of the word step by step.",
      "The old slider is gone, so the card keeps one clear interaction instead of splitting attention between guessing and dragging.",
      "One more tap after full reveal hides the answer again for another recall pass.",
    ],
    technicalNotes: [
      "Replaced the slider-based `revealLevel` control in `src/components/VocabularyPreviewCard.tsx` with a discrete tap-to-advance reveal interaction.",
      "Applied a striped text-mask treatment plus staged blur/opacity steps so partially revealed answers feel closer to the reference effect.",
      "Added `src/utils/vocabularyRevealEffect.ts` with Vitest coverage and introduced the new helper copy key `vocabulary_guess_tap_to_reveal_more_hint` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "trainer-session-complete-card-now-follows-language-space-palette",
    timestamp: "2026-06-09T18:31:44+02:00",
    title: "Trainer continue card now follows each language-space palette",
    summary:
      "The session-complete card in the vocabulary trainer now stays inside the active language space color theme instead of switching back to a neutral gray surface.",
    userHighlights: [
      "After finishing a round, the 'Mit den nächsten Wörtern weitermachen?' card now visually matches the current language space.",
      "The refresh action at the bottom uses the same per-language palette, so the return path feels like one connected surface.",
      "Behavior stays the same. This is a visual consistency fix only.",
    ],
    technicalNotes: [
      "Extracted the vocabulary preview palette mapping into `src/utils/vocabularyPreviewTheme.ts` so the same language-space colors can be reused across trainer states.",
      "Updated the session-complete container and refresh CTA in `src/components/VocabularyPreviewCard.tsx` to use the active theme's `cardBg`, `buttonBg`, and text colors instead of hard-coded slate classes.",
      "Added Vitest coverage in `src/utils/vocabularyPreviewTheme.test.ts` for supported-language and fallback theme resolution.",
    ],
  },
  {
    id: "sidebar-brand-logo-toggle",
    timestamp: "2026-06-07T16:57:07+0200",
    title: "Sidebar brand logo and improved toggle",
    summary:
      "The sidebar now shows the Uxhulu brand mark when collapsed, with a clean expand/collapse interaction on hover. The toggle button moved from the topbar into the sidebar itself, next to the navigation entries, making it feel more integrated.",
    userHighlights: [
      "The collapsed sidebar now shows the Uxhulu brand logo instead of a bare toggle button.",
      "Hovering the brand mark reveals the expand or collapse arrow, keeping the sidebar clean when not in use.",
      "The expand/collapse toggle is now part of the sidebar panel rather than sitting separately in the topbar.",
      "The brand logo uses an organic flowing mark with a small accent, matching the app's editorial character.",
    ],
    technicalNotes: [
      "Created `brand-logo.svg` as a minimal artsy mark (32×32 viewBox, currentColor, brush-stroke style U with floating accent).",
      "Moved the sidebar toggle from the topbar spacer `div` into the sidebar panel's `space-y-4` container as the first child.",
      "Hover behavior implemented via Tailwind `group` + `group-hover:opacity-*` transitions — no additional JS state needed.",
      "Added `ChevronLeftIcon` and `ChevronRightIcon` imports; removed `ChevronDoubleLeftIcon`/`ChevronDoubleRightIcon` usage from topbar toggle.",
    ],
  },
  {
    id: "review-practice-text-from-trainer-session",
    timestamp: "2026-06-07T18:30:00+0200",
    title: "Review practice text from trainer session",
    summary:
      "When you review vocabulary in the trainer and mark at least 5 words as not known, you can now create a short practice text containing exactly those words with one click. The text helps you encounter the unknown words again in a natural reading context.",
    userHighlights: [
      "Mark 5 or more words as not known during a trainer session, and a new one-click practice-text callout appears at session end.",
      "The generated text is built around those exact words, so you can read them again in a natural context.",
      "The callout respects your per-language compose settings (level, tense, word count, text type).",
      "The text is created in the background and a 'Text created' confirmation appears once it is ready.",
    ],
    technicalNotes: [
      "Added `useReviewPracticeTextCreate` hook wrapping `POST /api/reeprise/generated_texts/` with `practice_word_ids` support.",
      "Extended `VocabularyPreviewCard` with session-scoped forgotten-word tracking via a `Set<number>` state and an `onForgotten` callback wired through `VocabularyPreviewItem`.",
      "Rendered a practice-text callout in the session-complete area when at least 5 forgotten words are collected, reusing the same `per-language-create-settings-v1` localStorage key that `GenerateTextCard` uses.",
      "Added translation keys `review_practice_text_callout`, `review_practice_text_created`, and `review_practice_text_generate`.",
    ],
  },
  {
    id: "thinkera-register-now-language-agnostic-with-elevated-vocabulary-core-idea-display",
    timestamp: "2026-06-07T17:57:00+0200",
    title: "Thinkera register now language-agnostic with elevated vocabulary emphasis and core-idea display",
    summary:
      "The Thinkera level is no longer restricted to German. All languages can now use the advanced academic register. The prompt guidance has been rewritten to be language-agnostic, the base quality rules no longer suppress elevated vocabulary for Thinkera texts, and the dialogue/podcast formats now support high-register conversational language. Generated Thinkera texts are also scanned with expanded cross-language marker patterns for learned vocabulary. When reading Thinkera-level texts, vocabulary cards now show the core idea (conceptual definition) instead of a simple translated word.",
    userHighlights: [
      "Thinkera is now available for all learning languages, not just German.",
      "Thinkera texts now push more deliberately for elevated, erudite, academic, and learned vocabulary.",
      "Vocabulary encountered in Thinkera texts shows the conceptual meaning (Kernidee) instead of the simple word translation.",
    ],
    technicalNotes: [
      "Removed German-only language gates in `constants.ts`, `views.py`, `serializer.py`.",
      "Rewrote `difficulty_level_explainations['THINKERA']` (tasks.py:780) to be language-agnostic with international vocabulary examples.",
      "Added thinkera branch in `_build_base_quality_rules` (tasks.py:949) — replaces 'prefer clear, direct phrasing' with encouragement of sophisticated vocabulary.",
      "Added thinkera overrides in dialogue and podcast type clarifications (tasks.py:1201, 1262) for elevated conversational register.",
      "Expanded `THINKERA_REGISTER_MARKER_PATTERNS` from 19 to 47 patterns covering multiple languages.",
      "Updated thinkera knowledge rules and retry instruction to be language-agnostic — removed 'German' references.",
      "Updated `VocabularyItem` in `$generatedTextId.tsx` to display `core_idea` as the primary translation when the text level is Thinkera.",
      "Updated tests: constants test for all-language Thinkera, backend test for Thinkera + English, cross-language marker test, and prompt assertion updates.",
    ],
  },
  {
    id: "loading-screen-redesigned-with-centered-spinner",
    timestamp: "2026-06-07T16:00:00+0200",
    title: "Loading screen redesigned with centered spinner",
    summary:
      "All full-page loading states now use a shared centered spinner component instead of raw unstyled text, making transition moments feel intentional rather than broken.",
    userHighlights: [
      "The loading screen is now centered on the page with a calm spinner and translated label.",
      "The brief loading flash between routes no longer shows unstyled text in the top-left corner.",
    ],
    technicalNotes: [
      "Created a new `LoadingScreen` component in `src/components/LoadingScreen.tsx` with a centered spinner and translated label.",
      "Added `app_loading` translation keys to `src/assets/translations.json`.",
      "Replaced three full-page loading states in `src/routes/app/reeprise/index.tsx`, `src/routes/app/vocabulary_training_mode.tsx`, and `src/routes/app/interest_map.tsx`.",
    ],
  },
  {
    id: "generated-text-title-character-limit-raised-from-80-to-120",
    timestamp: "2026-06-07T15:46:00+0200",
    title: "Generated text title character limit raised from 80 to 120",
    summary:
      "The LLM prompt guidance for generated text titles now allows up to 120 characters instead of 80, giving German titles more breathing room without cropping.",
    userHighlights: [
      "German headlines can now be slightly longer and more natural while still staying concise.",
      "No visible UI change — this only affects how the generator is instructed.",
    ],
    technicalNotes: [
      "Updated `reeprise/tasks.py` — `_build_output_contract` (headline and podcast title lines) and the inline podcast prompt (line ~1196) from 80 to 120 characters.",
      "Updated the matching test assertion in `reeprise/tests.py`.",
    ],
  },
  {
    id: "thinkera-texts-now-force-a-clearly-more-academic-german-register",
    timestamp: "2026-06-07T15:22:12+0200",
    title: "Thinkera texts now force a clearly more academic German register",
    summary:
      "German Thinkera generation now pushes much more visible Bildungs- und Wissenschaftssprache instead of settling for only mildly polished everyday wording.",
    userHighlights: [
      "Thinkera texts should now contain more noticeable advanced vocabulary and Fremdwörter when they fit the topic naturally.",
      "A first draft that stays too plain is now regenerated once with a stricter academic-register instruction.",
      "The result should teach more sophisticated German wording instead of mostly standard descriptive prose.",
      "The generator is now explicitly biased toward high-register, erudite, academic, learned, and more Latinate or Greek-derived vocabulary where that sounds idiomatic.",
    ],
    technicalNotes: [
      "Strengthened Thinkera guidance in `reeprise/tasks.py` to require at least three visibly elevated academic or foreign-derived expressions in the body when natural for the topic.",
      "Added stronger register language in `reeprise/tasks.py` for high-register, erudite, academic, learned, formal-register, and idiomatic Latin- or Greek-derived German vocabulary.",
      "Added a German Thinkera register-marker check plus a retry instruction in `reeprise/tasks.py` so overly plain knowledge-text drafts are regenerated once with stricter Bildungs- und Wissenschaftssprache constraints.",
      "Added backend regression coverage in `reeprise/tests.py` for the stronger prompt wording, register-marker detection, and the retry path for plain Thinkera drafts.",
    ],
  },
  {
    id: "thinkera-now-targets-an-even-more-erudite-and-learned-german-register",
    timestamp: "2026-06-07T15:26:04+0200",
    title: "Thinkera now targets an even more erudite and learned German register",
    summary:
      "Thinkera prompt guidance now explicitly asks for genuinely high-register, learned, and more Latinate or Greek-derived German vocabulary instead of only generally elevated wording.",
    userHighlights: [
      "Thinkera now aims more directly at the register of educated essays, Feuilleton prose, cultural analysis, and scholarly explanation.",
      "The generator is told to prefer more erudite and formal vocabulary when that sounds idiomatic in German.",
      "This should make advanced lexical choices more visible in the final text body.",
    ],
    technicalNotes: [
      "Expanded the Thinkera level description in `reeprise/tasks.py` to explicitly call for high-register, elevated, erudite, academic, learned, and formal-register German.",
      "Extended the Thinkera knowledge-text rules and retry instruction in `reeprise/tasks.py` to bias toward idiomatic Latin- or Greek-derived German wording where it improves precision.",
      "Updated backend regression assertions in `reeprise/tests.py` so these stronger register requirements remain enforced.",
    ],
  },
  {
    id: "generate-text-card-now-uses-the-saved-space-level-from-settings",
    timestamp: "2026-06-07T15:08:53+0200",
    title: "Generate text now starts from the saved space level in Settings",
    summary:
      "The level control in the generate-text card now follows the saved level for the current language space instead of showing an older stale card default.",
    userHighlights: [
      "If you change a language-space level in Settings, the generate-text card now opens with that same level selected.",
      "A stale local create-card level no longer overrides the saved level for the active language space.",
      "The card still keeps its own local defaults for other controls such as tense and word count.",
    ],
    technicalNotes: [
      "Changed `src/components/GenerateTextCard.tsx` so the saved `user_settings.language_levels[activeLanguage]` value takes precedence over `per-language-create-settings-v1` when resolving the active level.",
      "Extended `src/components/MySelect.tsx` with stable test hooks and `aria-pressed` state so button-group selections can be asserted in E2E coverage.",
      "Added an end-to-end regression in `e2e/mobile-profile-space-settings.spec.ts` to verify the generate-text card reflects the saved Settings level even when stale local card defaults exist.",
    ],
  },
  {
    id: "german-knowledge-titles-now-stay-anchored-to-the-requested-topic",
    timestamp: "2026-06-07T15:06:34+0200",
    title: "German knowledge titles now stay anchored to the requested topic",
    summary:
      "German knowledge-title generation now keeps the headline tied to the actual topic instead of drifting into a different concrete scene such as students, projects, or other unrelated actors.",
    userHighlights: [
      "A German title should now stay conceptually connected to the topic you entered, even when that topic is phrased in another language.",
      "The generator is less likely to replace the requested café, table, queue, or social-setting concept with a random vignette.",
      "German knowledge headlines now avoid actor-plus-action scene titles when the topic calls for a calm observational label.",
    ],
    technicalNotes: [
      "Strengthened German knowledge-title guidance in `reeprise/content_philosophy.py` to preserve the core setting and entities of the topic and avoid inventing groups such as students unless explicitly requested.",
      "Extended `reeprise/tasks.py` title-context prompt instructions so the model must keep the headline anchored to the same topic concept instead of swapping in a different example scene.",
      "Added a German-specific title validator guard and backend regression tests in `reeprise/tests.py` for scene-drift headlines such as `Studenten bereiten zusammen ihre Projekte vor und genießen`.",
    ],
  },
  {
    id: "daily-thinkera-texts-now-use-the-saved-german-academic-level",
    timestamp: "2026-06-07T15:03:46+0200",
    title: "Daily Thinkera texts now use the saved German academic level",
    summary:
      "Daily German texts now respect the saved Thinkera language-space level, and the Thinkera prompt itself now asks for substantially more advanced academic and editorial vocabulary.",
    userHighlights: [
      "If your German language space is set to Thinkera, the daily text now actually generates at that level instead of silently falling back to an older easier level.",
      "Thinkera texts are now instructed to use more sophisticated Bildungssprache, Wissenschaftssprache, and terminology-rich editorial German.",
      "Daily German readings should now better teach genuinely elevated vocabulary instead of mostly everyday beginner wording.",
    ],
    technicalNotes: [
      "Changed `create_practice_text_for_user_language()` in `reeprise/tasks.py` so daily texts prefer the saved per-language `language_levels` value before falling back to older text history.",
      "Strengthened the `THINKERA` difficulty guidance and added extra Thinkera-specific knowledge-text rules in `reeprise/tasks.py` for elevated academic-editorial German diction.",
      "Added backend regression tests in `reeprise/tests.py` covering saved Thinkera daily-level reuse and the stronger Thinkera prompt wording.",
    ],
  },
  {
    id: "daily-german-texts-no-longer-force-unrelated-reinforcement-words-into-the-body",
    timestamp: "2026-06-07T15:00:57+0200",
    title: "Daily German texts no longer force unrelated reinforcement words into the body",
    summary:
      "Daily texts now treat reinforcement vocabulary as optional context instead of a hard requirement, so German daily readings no longer become incoherent or end with raw leftover words.",
    userHighlights: [
      "A daily text can now stay focused on its title and topic even when some saved vocabulary would not fit naturally.",
      "The generator no longer appends stray fallback words such as isolated household items to the end of a daily reading.",
      "Reinforcement still happens when it fits, but it no longer overrides coherence.",
    ],
    technicalNotes: [
      "Changed daily reinforcement prompt wording in `reeprise/tasks.py` from mandatory per-word inclusion to optional natural reuse of one or two familiar words or themes.",
      "Removed the struggle-word retry and fallback-append path for `DAILY_NUDGE` generation, so only struggle-practice texts keep hard required-word enforcement.",
      "Added backend regression coverage in `reeprise/tests.py` to verify daily knowledge prompts stay soft and missing reinforcement words are not appended into the final text body.",
    ],
  },
  {
    id: "german-now-has-a-thinkera-register-level-for-technical-vocabulary",
    timestamp: "2026-06-07T14:46:52+0200",
    title: "German now has a Thinkera level for scientific and academic vocabulary",
    summary:
      "German language spaces now include a special Thinkera level that generates texts in a more educated technical register with Fachbegriffe, Termini, and selected Anglicisms or Latinisms.",
    userHighlights: [
      "When your learning language is German, you can now choose Thinkera in onboarding, space settings, and text generation.",
      "New German texts at this level aim for Wissenschaftssprache and Bildungssprache instead of standard CEFR simplification.",
      "The level stays saved per German language space and is reused for later text generation.",
    ],
    technicalNotes: [
      "Added `THINKERA` as a generated-text level in the Django model plus migration `0089_alter_generatedtext_level.py`.",
      "Made level validation and fallback language-aware in `reeprise/serializer.py` and `reeprise/views.py`, so Thinkera is accepted only for German and can also be stored in `language_levels.GERMAN`.",
      "Updated frontend level pickers in `src/components/GenerateTextCard.tsx`, `src/routes/start-learning.tsx`, and `src/routes/app/space_settings.tsx` to expose the new German-only option, and added prompt guidance in `reeprise/tasks.py` for the new register.",
    ],
  },
  {
    id: "space-settings-now-lets-you-change-each-language-space-level",
    timestamp: "2026-06-07T14:39:46+0200",
    title: "Space Settings now lets you change each language space level",
    summary:
      "In Profile > Settings, each active language space now includes its own level selector so you can set the starting difficulty directly for that language.",
    userHighlights: [
      "You can change the saved level for each active language space without leaving the settings flow.",
      "New texts now start from the level you saved for that specific language space.",
      "Levels chosen during onboarding are now saved with the language space instead of being treated as a temporary step only.",
    ],
    technicalNotes: [
      "Added persisted `language_levels` support to user settings in the Django model, serializer validation, and migration `0088_usersetting_language_levels.py`.",
      "Updated `src/routes/app/space_settings.tsx` to render a per-space CEFR selector and send immediate user-settings PATCH updates.",
      "Updated onboarding persistence in `src/routes/start-learning.tsx` and `src/routes/app/start-learning.tsx`, plus generation fallback logic in `reeprise/views.py`, so saved language-space levels are reused by default.",
    ],
  },
  {
    id: "legacy-users-no-longer-inherit-newly-enabled-language-spaces",
    timestamp: "2026-06-07T14:36:53+0200",
    title: "Legacy users no longer inherit newly enabled language spaces",
    summary:
      "Users without an explicit saved space list now keep only their current active space instead of automatically picking up every enabled MVP language.",
    userHighlights: [
      "Re-enabling German no longer causes it to appear automatically for users who never added it.",
      "Legacy users without a saved space list stay scoped to their current space until they explicitly add another one.",
      "The add-space action stays available instead of acting as if every MVP space is already present.",
    ],
    technicalNotes: [
      "Changed `deriveEffectiveLanguageSpaceScope()` in `src/hooks/useAddedLanguageSpaces.ts` so missing persisted added-space state falls back to the current active space rather than the full enabled MVP allowlist.",
      "Updated `src/routes/app/route.tsx` and `src/routes/app/space_settings.tsx` to pass the current resolved language as the legacy fallback scope.",
      "Extended `src/hooks/useAddedLanguageSpaces.test.ts` and `e2e/language-space-add-flow.spec.ts` to verify legacy users do not implicitly gain German or other newly enabled spaces.",
    ],
  },
  {
    id: "text-details-iphone-mini-now-keeps-punctuation-with-tappable-words",
    timestamp: "2026-06-07T14:30:34+02:00",
    title: "Text details now keep punctuation attached on iPhone Mini even for tappable words",
    summary:
      "On very narrow screens such as iPhone Mini, commas and full stops in text details no longer jump onto a new line just because the previous word is tappable.",
    userHighlights: [
      "Sentence punctuation now stays visually attached to the preceding word on narrow mobile widths.",
      "This applies in the interactive reading view, not only in static text rendering.",
      "Reading rhythm should stay stable when tapping words for vocabulary actions.",
    ],
    technicalNotes: [
      "Updated `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so detachable leading punctuation is wrapped together with the preceding rendered word in a `whitespace-nowrap` inline wrapper.",
      "Kept existing punctuation normalization behavior for newline-before-punctuation cleanup and French spacing rules.",
      "Added regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for the clickable-word mobile wrapping case.",
    ],
  },
  {
    id: "german-is-enabled-again-for-mvp-language-spaces",
    timestamp: "2026-06-07T14:31:29+0200",
    title: "German is enabled again for MVP language spaces",
    summary:
      "German is available again anywhere the MVP language-space list drives onboarding and in-app language-space management.",
    userHighlights: [
      "German can be selected again when creating a language space.",
      "German counts as a valid added space in the app’s language-space flow.",
      "The add-language action now hides only after all four MVP spaces are already added.",
    ],
    technicalNotes: [
      "Re-enabled `GERMAN` in `ENABLED_LANGUAGE_SPACE_IDS` in `src/constants.ts`, which is the shared allowlist used by onboarding, language-space persistence, and app-level filtering.",
      "Added regression assertions in `src/constants.test.ts` and `src/hooks/useAddedLanguageSpaces.test.ts` so German stays part of the enabled MVP set and full-set detection reflects the updated list.",
      "Extended onboarding and add-language-space E2E coverage in `e2e/start-learning-onboarding.spec.ts` and `e2e/language-space-add-flow.spec.ts` to verify German remains selectable and counted in the real flow.",
    ],
  },
  {
    id: "daily-text-details-title-now-promotes-immediately-without-duplication",
    timestamp: "2026-06-07T14:23:32+02:00",
    title: "Daily text details now promote the real title immediately without showing it twice",
    summary:
      "When a generated title becomes available on text details, it now moves straight into the main headline instead of first appearing as a duplicate smaller line below.",
    userHighlights: [
      "The large title appears as soon as the committed title is known.",
      "The same title no longer shows twice in the header area.",
      "The smaller metadata line stays for actual prompt context, not repeated headline text.",
    ],
    technicalNotes: [
      "Updated `src/utils/generatedTextContent.ts` so title-first rendering can use an already-known fallback title during pending generation instead of holding the large headline on the loading copy.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to hide the metadata line when it matches the resolved main title.",
      "Extended `src/utils/generatedTextContent.test.ts` to cover title-first pending rendering with and without fallback-title data.",
    ],
  },
  {
    id: "daily-text-details-now-show-committed-pending-title-earlier",
    timestamp: "2026-06-07T14:23:32+02:00",
    title: "Daily text details now show the real title earlier while loading",
    summary:
      "When a daily text is still finishing in the background, the details page now shows the committed reading title as soon as it exists instead of waiting until the full text completes.",
    userHighlights: [
      "The large headline on daily text details appears earlier.",
      "The page no longer sits on a delayed title placeholder when the final title is already known.",
      "Manual text prompts still stay in the smaller prompt line instead of taking over the main headline.",
    ],
    technicalNotes: [
      "Added `resolvePendingGeneratedTextTitle()` in `src/utils/resolvePendingGeneratedTextTitle.ts` so pending-title fallback logic distinguishes committed titles from system placeholders such as daily and tricky-word seed topics.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to use that helper for the pending large-title fallback.",
      "Added regression coverage in `src/utils/resolvePendingGeneratedTextTitle.test.ts` for manual prompts, daily placeholders, and committed daily titles without prompt-cache entries.",
    ],
  },
  {
    id: "featured-daily-weather-text-no-longer-duplicates-in-feed",
    timestamp: "2026-06-06T13:15:00+02:00",
    title: "Featured daily weather text no longer duplicates in the feed",
    summary:
      "When today’s daily weather text is already shown in the featured daily card, it no longer also appears as a second regular text card in the main feed.",
    userHighlights: [
      "Today’s daily weather text now has one clear entry point instead of two matching cards.",
      "The featured daily card still opens the same text details page.",
      "Older feed texts remain visible below without losing their place.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` to exclude only the featured same-day `DAILY_NUDGE` text from the regular feed-card list while keeping it available to the daily card.",
      "Added a shared same-day daily-text resolver so the feed and daily card use the same featured item.",
      "Extended `e2e/reeprise-generation.spec.ts` to verify the featured daily text stays visible only in the daily card and is not rendered as a duplicate regular feed card.",
    ],
  },
  {
    id: "daily-text-topic-ideas-now-seed-fresher-daily-topics",
    timestamp: "2026-06-06T13:12:10+0200",
    title: "Daily texts now use fresher topic ideas instead of repeating one old theme",
    summary:
      "Daily text generation now pulls a fresh AI topic idea from your saved interests before creating the daily title, which reduces the chance of getting the same topic framing again and again.",
    userHighlights: [
      "Daily texts should vary more instead of circling back to one repeated topic.",
      "Your saved interests still guide the daily text, but now through a fresher topic seed.",
      "Repeated older topics are less likely to become the daily title again unchanged.",
    ],
    technicalNotes: [
      "Updated `barkguide/reeprise/tasks.py` so `DAILY_NUDGE` generation requests a fresh topic idea from the backend topic-intelligence system before title precomputation.",
      "Fed recent completed daily titles into the topic-idea exclusion history so daily generation avoids near-duplicate title seeds.",
      "Removed the hardcoded English fallback-title list from the daily title path and simplified the daily generation context setup.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` for daily-guidance topic seeding and committed-title context.",
    ],
  },
  {
    id: "mobile-create-button-now-keeps-a-true-circle",
    timestamp: "2026-06-06T13:08:33+02:00",
    title: "Mobile create button now keeps a true circle",
    summary:
      "On phones, the floating create button in the bottom navigation now keeps a stricter circular shape instead of appearing slightly stretched.",
    userHighlights: [
      "The plus button now reads as a cleaner circle.",
      "Its size stays stable inside the mobile nav layout.",
      "The create action keeps the same position and behavior.",
    ],
    technicalNotes: [
      "Tightened the mobile create button sizing in `src/routes/app/route.tsx` with explicit minimum dimensions, square aspect ratio, and no text-line influence so flex layout cannot distort the circular shape.",
    ],
  },
  {
    id: "mobile-profile-nav-icon-now-stays-inside-the-bottom-bar",
    timestamp: "2026-06-06T13:06:22+02:00",
    title: "Mobile profile icon now stays inside the bottom bar",
    summary:
      "On phones, the selected profile icon in the bottom navigation now stays fully inside the capsule instead of pushing partly outside the bar.",
    userHighlights: [
      "The active profile state no longer overlaps the edge of the mobile nav bar.",
      "Bottom-nav icons now use a cleaner, more stable size.",
      "The mobile nav keeps the same icon-only layout and spacing.",
    ],
    technicalNotes: [
      "Changed the mobile bottom-nav item buttons in `src/routes/app/route.tsx` from a label-era minimum width to fixed square icon-button sizing so the active background cannot overflow the capsule on the last item.",
    ],
  },
  {
    id: "daily-card-now-uses-language-space-colors-instead-of-weather-theme",
    timestamp: "2026-06-05T18:04:46+02:00",
    title:
      "Daily card now uses language-space colors instead of weather theme",
    summary:
      "The `Text des Tages` card no longer renders weather-themed backgrounds, cloud animations, or daylight overlays. Instead, it uses a subtle, restrained color scheme per language space.",
    userHighlights: [
      "Weather cloud animations and colorful gradients are gone from the daily card.",
      "Each learning space now has a soft, understated background with matching text tones.",
      "The card feels calmer and visually consistent across all spaces.",
    ],
    technicalNotes: [
      "Replaced `getDailyCardThemeClasses(theme, daylightPhase, languageSpace)` with a simplified version that only takes `languageSpace`.",
      "Removed `getDaylightMoodOverlay` and `getDailyCardLanguageOverlay` helper functions.",
      "Removed `useDailyWeatherTheme` hook call and related imports (`DailyWeatherTheme`, `DaylightPhase`).",
      "Removed all cloud-layer JSX (`daily-cloud-*` elements) and overlay divs from both the done and create card branches.",
      "Removed unused CSS keyframes (`daily-cloud-drift-left/right`), `.daily-cloud` class, `.daily-cloud-a` through `.daily-cloud-f` classes, and the `prefers-reduced-motion` override for `.daily-cloud` from `src/index.css`.",
    ],
  },
  {
    id: "mobile-reeprise-space-wash-now-flows-through-the-carousel-without-restarting",
    timestamp: "2026-06-05T16:50:09+02:00",
    title:
      "Mobile Reeprise space wash now flows through the carousel without restarting",
    summary:
      "On phones, the active space background on Reeprise now runs continuously through the language carousel and the page below, so the top no longer fades down and then starts the same gradient again.",
    userHighlights: [
      "The carousel area now blends into the page instead of restarting the color wash.",
      "The visible seam between the top switcher and the main page is reduced.",
      "Italian and the other spaces now keep a calmer, more continuous top-to-page transition.",
    ],
    technicalNotes: [
      "Kept the active space `pageWash` on the app shell in `src/routes/app/route.tsx`, removed the duplicate mobile carousel `pageWash` background there, and removed the duplicate content wrapper background for mobile Reeprise.",
      "Changed the mobile Reeprise route container in `src/routes/app/reeprise/index.tsx` to stay transparent over the shell wash instead of painting its own second page gradient.",
    ],
  },
  {
    id: "language-space-backgrounds-now-use-a-softer-wash",
    timestamp: "2026-06-05T16:44:24+02:00",
    title: "Language-space backgrounds now use a softer wash",
    summary:
      "The mobile language-space backgrounds on Reeprise are now noticeably softer, so the active space colors stay present without dominating the page.",
    userHighlights: [
      "Space colors now sit more quietly in the background.",
      "The top area and page body feel lighter and less saturated.",
      "The daily card tint is also softened to match.",
    ],
    technicalNotes: [
      "Reduced contrast and opacity in the mobile language-space wash values in `src/routes/app/route.tsx` and `src/routes/app/reeprise/index.tsx`, including the page wash, content wash, and daily-card overlay layers.",
    ],
  },
  {
    id: "daily-weather-card-now-picks-up-the-active-language-space-tone",
    timestamp: "2026-06-05T16:42:35+02:00",
    title: "Daily weather card now picks up the active language-space tone",
    summary:
      "The `Text des Tages` card on Reeprise now carries the current language-space color more clearly, so it feels visually tied to the selected space instead of only to the weather theme.",
    userHighlights: [
      "The daily card now reflects the active language-space color.",
      "Weather styling stays in place, but the card feels more connected to the selected space.",
      "Switching spaces now changes the daily card tone as well.",
    ],
    technicalNotes: [
      "Added a language-space overlay layer inside `DailyPracticeCallout` in `src/routes/app/reeprise/index.tsx` and passed the active `languageSpace` into the daily-card theme resolver.",
    ],
  },
  {
    id: "mobile-language-carousel-strip-now-blends-with-the-page-wash",
    timestamp: "2026-06-05T16:40:34+02:00",
    title: "Mobile language carousel strip now blends with the page wash",
    summary:
      "On phones, the language carousel strip on Reeprise now uses the same softer wash family as the page underneath, so it no longer creates a harsh dark band above a brighter section.",
    userHighlights: [
      "The dark band under the carousel is gone.",
      "The carousel area now blends more smoothly into the page.",
      "Space colors still stay visible without a hard seam.",
    ],
    technicalNotes: [
      "Changed the mobile carousel strip background in `src/routes/app/route.tsx` from the darker space `wash` to a softened composition based on the active space `pageWash`.",
    ],
  },
  {
    id: "mobile-reeprise-page-background-now-follows-the-active-space",
    timestamp: "2026-06-05T16:38:14+02:00",
    title: "Mobile Reeprise page background now follows the active space",
    summary:
      "On phones, the main Reeprise page itself now carries the active language-space tint, so the full screen changes mood when you switch spaces.",
    userHighlights: [
      "The full mobile Reeprise page now reflects the selected space color.",
      "The effect is visible beyond the carousel area itself.",
      "Switching spaces now changes the page mood more clearly.",
    ],
    technicalNotes: [
      "Applied language-space-specific page washes directly to the Reeprise route container in `src/routes/app/reeprise/index.tsx`, replacing the route’s prior hardcoded neutral background on mobile.",
    ],
  },
  {
    id: "mobile-reeprise-no-longer-keeps-an-empty-top-strip-above-the-carousel",
    timestamp: "2026-06-05T16:37:00+02:00",
    title: "Mobile Reeprise no longer keeps an empty top strip above the carousel",
    summary:
      "On phones, the main Reeprise screen no longer reserves the unused top-bar strip above the language carousel, so the page starts directly with the active-space control.",
    userHighlights: [
      "The empty white strip above the carousel is gone.",
      "The mobile Reeprise screen now starts more directly.",
      "Desktop and other routes keep their existing top bar behavior.",
    ],
    technicalNotes: [
      "Updated the top-shell container in `src/routes/app/route.tsx` to stay hidden on mobile `/app/reeprise` while remaining present on desktop and non-Reeprise routes.",
    ],
  },
  {
    id: "mobile-reeprise-now-carries-the-active-space-color-across-the-page",
    timestamp: "2026-06-05T16:35:38+02:00",
    title: "Mobile Reeprise now carries the active space color across the page",
    summary:
      "On phones, the active language space on Reeprise now softly tints the full page instead of keeping the color treatment only inside the carousel area.",
    userHighlights: [
      "The active space color now reaches across the full mobile Reeprise page.",
      "The effect stays soft and atmospheric rather than turning into a loud full-screen gradient.",
      "Switching spaces now changes the page mood more clearly.",
    ],
    technicalNotes: [
      "Extended the mobile language theme object in `src/routes/app/route.tsx` with a page-level wash and applied it to the mobile Reeprise shell, header, and content background.",
    ],
  },
  {
    id: "mobile-language-carousel-now-switches-the-real-active-space",
    timestamp: "2026-06-05T16:32:02+02:00",
    title: "Mobile language carousel now switches the real active space",
    summary:
      "On phones, swiping the language carousel on Reeprise now changes the actual active language space instead of only updating a visual mock state.",
    userHighlights: [
      "Swiping to another language now changes the real active space.",
      "The Reeprise screen can now follow the selected space instead of only restyling the header.",
      "The carousel still keeps the same compact motion and wrapping behavior.",
    ],
    technicalNotes: [
      "Removed the separate mock active-space state from `src/routes/app/route.tsx` and wired the mobile carousel directly to `useLanguageSpace()` via `setLanguageSpace()`.",
      "Updated `e2e/classic-tools-nav.spec.ts` to verify the real persisted language-space value changes in local storage after carousel interaction.",
    ],
  },
  {
    id: "mobile-language-carousel-now-uses-text-labels-instead-of-pills",
    timestamp: "2026-06-05T16:30:41+02:00",
    title: "Mobile language carousel now uses text labels instead of pills",
    summary:
      "On phones, the language-space carousel on Reeprise no longer presents the spaces as pill buttons. The control now uses lighter floating text labels with a small active rule.",
    userHighlights: [
      "The carousel no longer looks like a row of pill buttons.",
      "Active space styling is lighter and more editorial.",
      "The switcher keeps the same swipe interaction while feeling less UI-heavy.",
    ],
    technicalNotes: [
      "Removed the pill-style background, border, and rounded button treatment from the mobile language carousel labels in `src/routes/app/route.tsx` and replaced it with transparent text targets plus a small active underline.",
    ],
  },
  {
    id: "mobile-language-carousel-now-uses-a-tighter-footprint",
    timestamp: "2026-06-05T16:29:31+02:00",
    title: "Mobile language carousel now uses a tighter footprint",
    summary:
      "On phones, the language-space carousel on Reeprise is now more compact overall, so it behaves more like a light navigation control and less like a large header block.",
    userHighlights: [
      "The carousel row now takes up less vertical space.",
      "The pills and indicators are both smaller.",
      "The switcher feels lighter while keeping the same interaction.",
    ],
    technicalNotes: [
      "Reduced the mobile carousel step width, pill width, pill height, row height, and indicator sizing in `src/routes/app/route.tsx` to compress the control as one system.",
    ],
  },
  {
    id: "mobile-language-carousel-now-uses-a-softer-display-font",
    timestamp: "2026-06-05T16:28:35+02:00",
    title: "Mobile language carousel now uses a softer display font",
    summary:
      "On phones, the language-space labels in the Reeprise carousel now use a softer display font in normal case, so the switcher feels designed without the heavier poster-style tone.",
    userHighlights: [
      "The language names keep normal case.",
      "The pill labels now use a softer display face.",
      "The carousel feels lighter and less rigid.",
    ],
    technicalNotes: [
      "Changed the mobile language carousel pill labels in `src/routes/app/route.tsx` from `Paytone` to the existing `TikTok` font while keeping the current size, casing, and interaction model.",
    ],
  },
  {
    id: "mobile-language-carousel-display-labels-are-back-in-normal-case",
    timestamp: "2026-06-05T16:27:37+02:00",
    title: "Mobile language carousel labels are back in normal case",
    summary:
      "On phones, the language-space labels in the Reeprise carousel now keep normal casing again, so the display styling stays expressive without slipping into all-caps copy.",
    userHighlights: [
      "The carousel keeps the slimmer art-directed pills.",
      "Language names no longer appear in all caps.",
      "The switcher now fits the calmer copy direction used elsewhere in the app.",
    ],
    technicalNotes: [
      "Removed the uppercase text transform from the mobile language carousel pill labels in `src/routes/app/route.tsx` while keeping the `Paytone` display treatment and reduced pill size.",
    ],
  },
  {
    id: "mobile-language-carousel-pills-now-use-lighter-display-typography",
    timestamp: "2026-06-05T16:25:15+02:00",
    title: "Mobile language carousel pills now use lighter display typography",
    summary:
      "On phones, the language-space pills in the Reeprise carousel are now slimmer and use a more display-oriented type treatment, so the switcher feels more art-directed and less heavy.",
    userHighlights: [
      "The carousel pills now take up less space.",
      "The language labels use a more stylized display font.",
      "The switcher reads more like a visual mode control than a standard selector.",
    ],
    technicalNotes: [
      "Reduced the mobile carousel pill width and height in `src/routes/app/route.tsx` and changed the label treatment to `Paytone` with uppercase styling.",
      "Kept the same swipe behavior, wrapping logic, and indicator row so the change stays visual-only.",
    ],
  },
  {
    id: "mobile-language-carousel-now-uses-richer-space-gradients",
    timestamp: "2026-06-05T16:24:03+02:00",
    title: "Mobile language carousel now uses richer space gradients",
    summary:
      "On phones, the language-space carousel now uses moodier gradients and tones, giving each space a clearer visual identity without changing the structure of the screen.",
    userHighlights: [
      "Each space now feels more distinct at a glance.",
      "The mobile Reeprise header wash uses warmer and darker transitions instead of flat pastel tints.",
      "The carousel keeps the same interaction while the visual identity feels more intentional.",
    ],
    technicalNotes: [
      "Updated the per-space mobile carousel theme map in `src/routes/app/route.tsx` with layered gradient washes and adjusted surface, border, and ink colors.",
      "Kept the same carousel layout and gesture model so the change stays purely visual.",
    ],
  },
  {
    id: "mobile-language-carousel-now-shows-space-position-indicators",
    timestamp: "2026-06-05T16:19:21+02:00",
    title: "Mobile language carousel now shows space position indicators",
    summary:
      "On phones, the language-space carousel now includes a small position indicator row, so the control stays understandable when more spaces are added.",
    userHighlights: [
      "The carousel now shows a quiet row of markers beneath the active space.",
      "Your current space is easier to locate at a glance.",
      "The pattern scales more clearly when several spaces exist in parallel.",
    ],
    technicalNotes: [
      "Added a mobile-only indicator row in `src/routes/app/route.tsx` under the Reeprise language-space carousel, with an elongated active marker and neutral inactive dots.",
      "Kept the carousel itself focused on one centered active space plus immediate neighbors so the control remains calm on mobile.",
    ],
  },
  {
    id: "mobile-language-carousel-now-wraps-visually-at-the-edges",
    timestamp: "2026-06-05T16:17:41+02:00",
    title: "Mobile language carousel now wraps visually at the edges",
    summary:
      "On phones, the language-space carousel now keeps the full loop visible, so the first space still peeks in when you are focused on the last one.",
    userHighlights: [
      "The carousel no longer feels like it ends at the outer spaces.",
      "When you reach the last visible space, the next space in the loop still appears at the opposite edge.",
      "Swiping now feels like rotating through spaces instead of hitting a stop.",
    ],
    technicalNotes: [
      "Updated the mobile Reeprise carousel in `src/routes/app/route.tsx` to use circular distance math for rendering and wrap-around index selection for swipe snaps.",
      "Extended the focused Playwright regression in `e2e/classic-tools-nav.spec.ts` to assert that the first space remains visibly adjacent after swiping to the last one.",
    ],
  },
  {
    id: "mobile-reeprise-now-has-language-space-carousel-mock-interaction",
    timestamp: "2026-06-05T16:07:13+02:00",
    title: "Mobile Reeprise now has a swipeable language-space carousel mock",
    summary:
      "On phones, the main Reeprise screen now uses a centered swipe carousel for language spaces, so moving between active learning lanes feels more focused than the previous tab row.",
    userHighlights: [
      "Mobile Reeprise now shows one centered language space at a time with neighboring spaces peeking in from the sides.",
      "You can swipe between spaces or tap a side space to bring it into focus.",
      "Each visible space now carries a light accent theme while the interaction stays mock-only for this first pass.",
    ],
    technicalNotes: [
      "Replaced the mobile language-space tabs in `src/routes/app/route.tsx` with a pointer-driven centered carousel that snaps between local mock states.",
      "Added endonym translation keys in `src/assets/translations.json` for the mobile carousel labels and applied a restrained per-space accent wash to the mobile Reeprise shell.",
      "Updated Playwright coverage in `e2e/classic-tools-nav.spec.ts` to validate the mobile carousel gesture and active-state transition.",
    ],
  },
  {
    id: "mobile-reeprise-now-has-language-space-tabs-mock-interaction",
    timestamp: "2026-06-05T15:52:58+02:00",
    title: "Mobile Reeprise now has language-space tabs mock interaction",
    summary:
      "On phones, the main Reeprise screen now shows a tab-style language-space row under the top bar so switching between active spaces can be evaluated as a faster interaction pattern.",
    userHighlights: [
      "Mobile Reeprise now shows direct language-space tabs under the top bar.",
      "The current space is highlighted while other spaces can show a subtle attention marker.",
      "This first pass is interaction-only and does not yet change the actual feed content.",
    ],
    technicalNotes: [
      "Added mobile-only language-space tabs and a local-state-only overflow sheet in `src/routes/app/route.tsx` for `/app/reeprise`.",
      "Kept the existing top-bar language switch for desktop while hiding it on mobile Reeprise so the mock interaction reads as a single control.",
      "Added translation keys in `src/assets/translations.json` and Playwright coverage in `e2e/classic-tools-nav.spec.ts` for the tab interaction state.",
    ],
  },
  {
    id: "mobile-bottom-nav-now-includes-quick-translate",
    timestamp: "2026-06-05T15:36:00+02:00",
    title: "Mobile bottom nav now includes quick translate",
    summary:
      "On phones, quick translate now lives in the bottom navigation instead of the top bar, so the mobile entry point stays in the same place as the other primary app actions.",
    userHighlights: [
      "Quick translate is now reachable from the mobile bottom nav.",
      "The separate mobile top-bar quick translate icon is removed.",
      "Desktop keeps the existing top-bar quick translate entry.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/route.tsx` so the mobile bottom navigation includes a quick-translate icon, stays visible on `/app/quick_translate`, and hides the top-bar quick-translate button below the desktop breakpoint.",
      "Kept desktop quick translate behavior unchanged by preserving the existing top-bar link on `md` and above.",
      "Added Playwright coverage in `e2e/classic-tools-nav.spec.ts` for the new mobile navigation path.",
    ],
  },
  {
    id: "text-details-page-prompt-line-now-sits-directly-under-title",
    timestamp: "2026-06-04T12:40:16+02:00",
    title: "Text details page prompt line now sits directly under the title",
    summary:
      "The generation prompt line on the text details page now appears immediately below the main title, before the other metadata rows.",
    userHighlights: [
      "The prompt text now sits directly under the headline.",
      "Supporting metadata stays below it in a clearer order.",
      "The full-prompt dialog remains available for longer prompt text.",
    ],
    technicalNotes: [
      "Reordered the metadata block in `src/components/GeneratedTextDetailsTextPane.tsx` so the prompt-topic row renders before `metadataAccessory` and `metadataSecondary`.",
      "Kept the restored prompt visibility behavior unchanged.",
    ],
  },
  {
    id: "text-details-page-generation-prompt-line-kept-visible",
    timestamp: "2026-06-04T12:37:48+02:00",
    title: "Text details page keeps the generation prompt line visible",
    summary:
      "The text details header shows the original generation prompt text whenever it exists, including cases where that text matches the visible title.",
    userHighlights: [
      "The generation prompt text stays visible in the header area under the title.",
      "It no longer disappears when the prompt and title are the same.",
      "The full-prompt dialog still works for longer prompt text.",
    ],
    technicalNotes: [
      "Kept the prompt metadata row, truncation helper, and full-prompt modal in `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Simplified the visibility rule so the prompt row renders whenever `promptTopic` is present.",
    ],
  },
  {
    id: "daily-weather-card-subline-restored",
    timestamp: "2026-06-04T12:32:14+02:00",
    title: "Daily weather card subline restored",
    summary:
      "The descriptive line under `Text des Tages` is back on the daily weather card in both its create and done states.",
    userHighlights: [
      "The daily card again shows the supporting line under the title.",
      "Create and done states both have the subtitle/preview restored.",
      "Recent typography and CTA changes stay in place.",
    ],
    technicalNotes: [
      "Restored the daily-card body-text render blocks in `DailyPracticeCallout` inside `src/routes/app/reeprise/index.tsx`.",
      "Reintroduced the helper logic that extracts a short first-sentence preview for the done-state card, with the existing translation fallback when no preview is available.",
    ],
  },
  {
    id: "daily-weather-card-cta-now-uses-larger-bold-typography",
    timestamp: "2026-06-04T12:26:17+02:00",
    title: "Daily weather card CTA now uses larger bold typography",
    summary:
      "The daily weather card action such as `jetzt lesen` now uses a larger, bolder treatment so the primary action stands out more clearly.",
    userHighlights: [
      "The CTA is now easier to spot at a glance.",
      "The action text is larger and bold again.",
      "The custom doodle icon stays in place beside the stronger label.",
    ],
    technicalNotes: [
      "Updated the shared daily-card `action` class in `src/routes/app/reeprise/index.tsx` from `text-[11px] font-normal` to `text-sm font-semibold` and slightly increased the icon gap.",
      "Kept CTA wording, custom icon, and weather-theme colors unchanged.",
    ],
  },
  {
    id: "daily-weather-card-cta-icon-now-uses-organic-doodle-style",
    timestamp: "2026-06-04T12:24:15+02:00",
    title: "Daily weather card CTA icon now uses an organic doodle style",
    summary:
      "The daily weather card action now uses a more organic hand-drawn icon inspired by the reference shapes instead of a conventional arrow symbol.",
    userHighlights: [
      "The CTA icon now feels closer to the soft abstract reference style.",
      "The mark reads less like a standard UI control.",
      "The daily card keeps the same behavior with a more distinctive visual accent.",
    ],
    technicalNotes: [
      "Redrew `DailyCardArrow` in `src/routes/app/reeprise/index.tsx` as a custom multi-stroke SVG with an organic branching silhouette.",
      "Kept the icon inline so it still inherits theme color and stays sharp at small sizes.",
    ],
  },
  {
    id: "reeprise-feed-section-header-removed",
    timestamp: "2026-06-04T12:23:06+02:00",
    title: "Reeprise feed section header removed",
    summary:
      "The separate `Feed` heading above the Reeprise list is removed, so the daily card and text stream start immediately without an extra section label.",
    userHighlights: [
      "The feed starts directly without a redundant `Feed` title.",
      "The page feels cleaner and less scaffolded.",
      "The daily card now leads straight into the rest of the reading stream.",
    ],
    technicalNotes: [
      "Removed the `reeprise_feed_section_title` heading render block from `src/routes/app/reeprise/index.tsx` while leaving the existing list structure and test ids unchanged.",
      "No feed ordering or card behavior changed.",
    ],
  },
  {
    id: "daily-weather-card-cta-now-uses-regular-font-weight",
    timestamp: "2026-06-04T12:22:22+02:00",
    title: "Daily weather card CTA now uses regular font weight",
    summary:
      "The daily weather card action text such as `jetzt lesen` now uses regular font weight instead of the still-too-strong medium styling.",
    userHighlights: [
      "The CTA is no longer bold-looking.",
      "The action now sits more quietly beside the title and date.",
      "Case and arrow styling stay as previously adjusted.",
    ],
    technicalNotes: [
      "Updated the shared daily-card `action` class in `src/routes/app/reeprise/index.tsx` from `font-medium` to `font-normal`.",
      "Kept CTA wording, custom arrow, and weather-theme color mapping unchanged.",
    ],
  },
  {
    id: "daily-weather-card-cta-now-uses-a-custom-arrow-mark",
    timestamp: "2026-06-04T12:21:40+02:00",
    title: "Daily weather card CTA now uses a custom arrow mark",
    summary:
      "The daily weather card action now uses a small custom arrow mark instead of the previous generic chevron, giving the CTA a more natural visual finish.",
    userHighlights: [
      "The CTA arrow no longer looks like a stock UI chevron.",
      "The new arrow feels lighter and more natural next to `jetzt lesen`.",
      "Only the daily card CTA changed, so the rest of the feed stays consistent.",
    ],
    technicalNotes: [
      "Replaced the imported `ChevronRightIcon` in `src/routes/app/reeprise/index.tsx` with a local `DailyCardArrow` SVG component using a slightly irregular shaft and sharper arrowhead.",
      "Applied the custom arrow in both the create and done CTA states of `DailyPracticeCallout`.",
    ],
  },
  {
    id: "daily-weather-card-cta-now-uses-regular-case-medium-weight",
    timestamp: "2026-06-04T12:20:40+02:00",
    title: "Daily weather card CTA now uses regular case and lighter weight",
    summary:
      "The daily weather card action text such as `jetzt lesen` no longer appears in uppercase or heavy bold, so it sits more quietly within the card header.",
    userHighlights: [
      "The CTA text is no longer all caps.",
      "The CTA now uses a lighter weight instead of bold emphasis.",
      "The action fits the calmer feed-card typography better.",
    ],
    technicalNotes: [
      "Updated the shared daily-card `action` class in `src/routes/app/reeprise/index.tsx` to remove uppercase tracking and reduce the weight from semibold to medium.",
      "Kept the existing weather-theme color mapping and CTA behavior unchanged.",
    ],
  },
  {
    id: "daily-weather-card-subline-removed",
    timestamp: "2026-06-04T12:19:20+02:00",
    title: "Daily weather card subline removed",
    summary:
      "The `Text des Tages` daily weather card no longer shows the extra descriptive subline under the title, leaving a cleaner header with just the day, date, title, and action.",
    userHighlights: [
      "The daily card header is now more compact.",
      "The extra `Dein täglicher Text ...` line is removed.",
      "The card now matches the cleaner feed-card rhythm more closely.",
    ],
    technicalNotes: [
      "Removed the body text render block from both the create and done states of `DailyPracticeCallout` in `src/routes/app/reeprise/index.tsx`.",
      "Kept the existing action, title, and weather-theme styling unchanged.",
    ],
  },
  {
    id: "daily-weather-card-title-now-matches-feed-card-typography",
    timestamp: "2026-06-04T12:18:30+02:00",
    title: "Daily weather card title now matches feed card typography",
    summary:
      "The `Text des Tages` headline on the daily weather card now uses the same serif title size and spacing as the regular feed text cards.",
    userHighlights: [
      "The daily card title now looks consistent with the other text cards.",
      "Header hierarchy feels more unified across the feed.",
      "The daily card keeps its weather styling without using a separate title scale.",
    ],
    technicalNotes: [
      "Updated the shared daily-card `heading` class in `src/routes/app/reeprise/index.tsx` to reuse the same serif typography sizing and tracking used in `ReepriseTextCard` titles.",
      "Kept the existing weather-theme color mapping so only the title typography changed.",
    ],
  },
  {
    id: "daily-weather-card-weekday-now-uses-plain-text-styling",
    timestamp: "2026-06-04T12:12:54+02:00",
    title: "Daily weather card weekday now uses plain text styling",
    summary:
      "The weekday label on the `Text des Tages` card now appears as normal text instead of an all-caps pill, so the card header feels calmer and less badge-like.",
    userHighlights: [
      "The day name is no longer rendered in all caps.",
      "The pill-shaped weekday label is removed.",
      "The card header now reads more like part of the editorial layout.",
    ],
    technicalNotes: [
      "Updated the shared daily-card `badge` class in `src/routes/app/reeprise/index.tsx` to remove the rounded pill container, border, background, and uppercase tracking treatment.",
      "Kept the existing weather-theme color mapping so only the weekday presentation changed.",
    ],
  },
  {
    id: "daily-weather-card-now-renders-inside-the-feed",
    timestamp: "2026-06-04T12:10:27+02:00",
    title: "Daily weather card now renders inside the feed",
    summary:
      "The `Text des Tages` daily weather card now appears as the first card in the main feed instead of sitting in a separate block above it.",
    userHighlights: [
      "The daily card now feels like part of the same reading feed.",
      "The first feed item is consistently `Text des Tages` when it is available.",
      "The screen has one clearer content flow instead of a detached card above the list.",
    ],
    technicalNotes: [
      "Removed the separate `reeprise-daily-section` block from `src/routes/app/reeprise/index.tsx` and rendered `DailyPracticeCallout` as the first `<li>` inside `reeprise-feed-list`.",
      "Added a dedicated `reeprise-daily-feed-item` test id for stable feed-order assertions.",
      "Updated `e2e/reeprise-generation.spec.ts` to verify the daily card is inside the feed and remains the first visible feed item.",
    ],
  },
  {
    id: "mobile-bottom-navigation-now-opens-a-focused-create-view",
    timestamp: "2026-06-04T10:57:31+02:00",
    title: "Mobile bottom navigation now opens a focused create view",
    summary:
      "On phones, the main app now uses a bottom navigation with a dedicated Create action that opens a calmer text-composition view instead of keeping the generator pinned at the top of the feed.",
    userHighlights: [
      "Mobile now has a bottom navigation for the main app surfaces.",
      "Tapping `Create` opens a focused generation view with the existing text-generation card ready to use.",
      "The feed stays easier to scan because the generation card no longer sits above it on small screens.",
    ],
    technicalNotes: [
      "Added a mobile-only bottom navigation rail in `src/routes/app/route.tsx` with direct actions for texts, create, progress, and profile.",
      "Extended `src/routes/app/reeprise/index.tsx` with route search state for the mobile create view and wrapped the existing `GenerateTextCard` in a focused composition layout with prompt seeds.",
      "Added new translation keys in `src/assets/translations.json` and Playwright coverage in `e2e/reeprise-generation.spec.ts` for the mobile bottom-nav creation flow.",
    ],
  },
  {
    id: "trainer-source-link-type-safety-now-matches-the-missing-sentence-guard",
    timestamp: "2026-06-04T10:29:51+02:00",
    title:
      "Trainer source-link type safety now matches the missing-sentence guard",
    summary:
      "Vocabulary source-text links now use the same saved-sentence guard in code and UI, so the trainer screens build cleanly again without changing when the button appears.",
    userHighlights: [
      "The source-text button behavior stays the same for learners.",
      "Cards without a saved source sentence still keep the button hidden.",
      "The trainer screens now compile cleanly again.",
    ],
    technicalNotes: [
      "Added a shared local `sourceLinkTarget` guard in `src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_trainer.tsx`, and `src/routes/app/vocabulary_training_mode.tsx` before reading `generatedTextId` or `chapter`.",
      "Defined the missing `sourceSentence` value in `src/routes/app/vocabulary_training_mode.tsx` so the focused source-text navigation payload is built from trimmed sentence data.",
      "Documented the follow-up fix in the product logbook after the earlier source-link visibility change.",
    ],
  },
  {
    id: "homepage-preview-hero-now-uses-a-calmer-first-screen-scale",
    timestamp: "2026-06-03T22:02:58+02:00",
    title: "Homepage preview hero now uses a calmer first-screen scale",
    summary:
      "The generated-text preview on the homepage is now shorter, smaller, and easier to scan on mobile while keeping the vocabulary demonstration visible.",
    userHighlights: [
      "The first homepage card now stays closer to the earlier compact portrait-card size.",
      "The preview title and reading sample are shorter, so the page feels more composed on mobile.",
      "The vocabulary translations now appear as compact glosses instead of long explanation cards.",
    ],
    technicalNotes: [
      "Reduced the homepage hero title, sample text, card padding, portrait height, and glossary footprint in `src/routes/index.tsx`.",
      "Shortened the landing-page preview copy and glossary intro in `src/assets/translations.json`.",
      "Updated the root landing Playwright regression to assert the calmer preview content and three highlighted words.",
    ],
  },
  {
    id: "archive-headlines-now-use-stronger-title-styling",
    timestamp: "2026-06-03T22:00:36+02:00",
    title: "Archive headlines now use stronger title styling",
    summary:
      "The release log cards keep the new sentence-case copy, but the titles now stand out more clearly with stronger visual emphasis.",
    userHighlights: [
      "Archive titles are easier to spot at a glance.",
      "The sentence-case copy stays readable without losing hierarchy.",
      "Summaries and bullet notes remain visually quieter than the title.",
    ],
    technicalNotes: [
      "Increased the archive card title weight and contrast in `src/routes/app/product_logbook.tsx` and `src/routes/app/technical_change_log.tsx`.",
      "Kept the sentence-case headline normalization in place so historical entries still read naturally.",
      "Added a release-notes entry for the updated title hierarchy.",
    ],
  },
  {
    id: "homepage-hero-now-previews-contextual-vocabulary-reading",
    timestamp: "2026-06-03T21:57:11+02:00",
    title: "Homepage hero now previews contextual vocabulary reading",
    summary:
      "The main website now opens with a magazine-like generated English text preview, highlighted words, and nearby translations that show how Uxhulu works.",
    userHighlights: [
      "The homepage now demonstrates the core reading flow instead of leading with abstract product copy.",
      "Highlighted English words in the hero show how vocabulary can stay connected to the sentence.",
      "The word translations sit beside the text, making the product value visible immediately.",
    ],
    technicalNotes: [
      "Reworked `src/routes/index.tsx` so the portrait hero contains a generated-text preview, inline vocabulary highlights, glossary cards, and quieter editorial support sections.",
      "Added landing-page translation keys for the generated English sample, highlighted words, glossary notes, and revised editorial copy.",
      "Updated the root homepage Playwright regression to assert the generated text preview, glossary, and mobile readability.",
    ],
  },
  {
    id: "main-website-now-follows-the-editorial-reference-layout",
    timestamp: "2026-06-03T21:49:46+02:00",
    title: "The main website now follows the editorial reference layout",
    summary:
      "The homepage now uses the warm portrait-card composition from the reference image, with a peach hero, centered story section, and vivid feature blocks.",
    userHighlights: [
      "The homepage now opens with a large editorial hero card instead of a generic marketing layout.",
      "The center section now reads like a quiet brand story instead of a sales page.",
      "The colored feature blocks are now used as accents, not as the main background.",
    ],
    technicalNotes: [
      "Reworked `src/routes/index.tsx` to use the portrait-led hero composition, centered narrative block, and four color-coded feature cards.",
      "Swapped the homepage hero image to the existing portrait asset and tuned the palette to peach, yellow, mint, pink, and blue accents.",
      "Updated landing-page translations for the new hero statement, story copy, and feature labels.",
      "Adjusted the homepage Playwright regression to assert the new hero copy, story heading, and colored feature blocks on desktop and mobile.",
    ],
  },
  {
    id: "ui-copy-uppercase-wording-now-uses-normal-case",
    timestamp: "2026-06-03T21:47:27+02:00",
    title: "Visible copy now uses normal case instead of shouty uppercase wording",
    summary:
      "The pricing page, legal pages, language selector, vocabulary labels, and Maison Mémoire mockup copy now read in calmer sentence case instead of all-caps labels.",
    userHighlights: [
      "The visible labels now read more naturally and are easier to scan.",
      "Language chips, vocabulary headings, and the free-price callout no longer shout at the user.",
      "The product pages keep the same behavior and layout.",
    ],
    technicalNotes: [
      "Normalized visible copy in `src/assets/translations.json` for vocabulary labels, the Maison Mémoire mockup, weather hints, and read-aloud helper labels.",
      "Rewrote the terms and privacy policy date and section copy in `src/routes/terms-of-service.tsx` and `src/routes/privacy-policy.tsx` to remove uppercase shouty wording.",
      "Updated the compact language selector labels in `src/components/NavbarWebsite.tsx` and `src/components/NavbarSmallWebsite.tsx`, the pricing price label, and the settings model label.",
      "Added translation copy coverage in `src/assets/translations.copy.test.ts` for the normalized labels.",
    ],
  },
  {
    id: "generate-text-card-badge-now-reads-daily-text",
    timestamp: "2026-06-03T21:46:10+02:00",
    title: "Generate text card badge now reads Daily Text",
    summary:
      "The small badge on the generate text card now uses Daily Text instead of the older German label.",
    userHighlights: [
      "The generate text card now shows a clearer English badge label.",
      "The card badge wording is consistent across app languages.",
      "The card layout and behavior are unchanged.",
    ],
    technicalNotes: [
      "Replaced the hardcoded `Übungstext` badge text in `src/components/ReepriseTextCard.tsx` with the `struggle_words_badge` translation key.",
      "Added the new `struggle_words_badge` translations in `src/assets/translations.json`.",
      "Extended `src/assets/translations.copy.test.ts` to guard the new badge copy.",
    ],
  },
  {
    id: "trainer-source-link-now-hides-when-source-sentence-is-not-saved",
    timestamp: "2026-06-03T21:44:34+02:00",
    title: "Trainer source text is now hidden when the source sentence was not saved",
    summary:
      "Vocabulary cards no longer show the `Zum Ursprungstext` button when the card only has a source text reference but no saved source sentence.",
    userHighlights: [
      "Cards with `Ausgangssatz nicht gespeichert.` no longer offer the source-text button.",
      "The source sentence status message still explains what happened.",
      "Cards with a saved source sentence continue to show the source-text button as before.",
    ],
    technicalNotes: [
      "Tightened the `trainer_open_source_text` rendering condition in `src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_trainer.tsx`, and `src/routes/app/vocabulary_training_mode.tsx` so the link only appears when a saved source sentence exists.",
      "Updated the Playwright regression in `e2e/reeprise-generation.spec.ts` to assert that the source-text button is absent for cards with the missing-source-sentence fallback.",
      "Documented the UI change in the release notes page.",
    ],
  },
  {
    id: "struggle-word-practice-now-uses-shown-word-history-and-refreshes-after-manual-adds",
    timestamp: "2026-06-03T21:58:28+02:00",
    title: "Struggle-word practice now uses shown-word history and refreshes after manual adds",
    summary:
      "The practice-word selector now looks at real vocabulary exposure history, so due review words come back before a large fresh backlog while fresh words still enter the batch. Manual vocabulary adds also refresh the struggle-word list immediately.",
    userHighlights: [
      "Words already shown in generated texts are now tracked as exposure history and are not repeated too soon.",
      "Newly added words can surface in struggle practice without waiting for the next daily cache refresh.",
      "Hard words still come back, but the batch keeps a mix of fresh and review items instead of repeating the same set.",
    ],
    technicalNotes: [
      "Changed `select_struggle_practice_vocabularies()` in `barkguide/reeprise/tasks.py` to use `VocabularyUsage` and `VocabularyUserProgress` together when ranking the next batch.",
      "Split selection into due review words, fresh words, and cooling review words so weak due items are not crowded out by a large backlog of never-shown vocabulary.",
      "Ordered due review words by learner weakness before raw age, so recently failed words outrank older but less urgent review items.",
      "Aggregated `VocabularyUsage` exposure counts and last-shown timestamps in the database instead of iterating every usage row in Python.",
      "Kept the existing progress fields and exposure records as the source of truth instead of adding a separate scheduling table.",
      "Invalidated the `struggle-words` query after vocabulary adds from the shared create hook and the direct add flows used by quick translate and the language-helper panel.",
    ],
  },
  {
    id: "main-website-landing-page-now-matches-the-brand-voice",
    timestamp: "2026-06-03T21:39:53+02:00",
    title: "The main website now uses brand-aligned landing copy",
    summary:
      "The homepage now reads like Uxhulu itself: quieter, more editorial, and centered on browser-first language learning instead of a generic trial funnel.",
    userHighlights: [
      "The homepage hero now explains the product in a calmer, more precise tone.",
      "The landing page now highlights texts, words in context, and browser-first use instead of trial messaging.",
      "The shared website navigation now uses more neutral labels for the product and the example flow.",
    ],
    technicalNotes: [
      "Reworked `src/routes/index.tsx` into a branded landing page with a hero, feature cards, a process section, and a closing callout.",
      "Updated shared website navigation styling and labels in `src/components/NavbarWebsite.tsx` to remove the trial-heavy mobile menu copy.",
      "Added new landing-page translations in `src/assets/translations.json` for both German and English copy.",
      "Extended the Playwright homepage regression to cover the new landing copy, language switching, and mobile overflow behavior.",
    ],
  },
  {
    id: "trainer-filter-and-quick-translate-bug-fixes",
    timestamp: "2026-06-03T21:25:20+02:00",
    title: "Trainer filters and quick translate are more reliable",
    summary:
      "The vocabulary trainer now applies saved language selections correctly, and quick translate no longer fails while checking example sentences.",
    userHighlights: [
      "Saved language choices in the vocabulary trainer now scope the word queue as expected.",
      "Trainer sessions now keep separate progress for different language selections.",
      "Quick translate example-sentence cleanup no longer causes a server error.",
    ],
    technicalNotes: [
      "Threaded selected vocabulary trainer languages into the priority-vocabulary query, local filtering, and session persistence key.",
      "Added a Playwright regression assertion that saved trainer language filters reach the API request.",
      "Defined the missing backend word-segment regex used by quick translate example validation.",
      "Cleared lint errors found during the second pass, including the daily callout hook-order regression.",
    ],
  },
  {
    id: "source-sentence-not-saved-copy-now-shorter",
    timestamp: "2026-06-03T21:13:05+02:00",
    title: "Source sentence copy is now shorter",
    summary:
      "The German fallback copy for missing source sentences now uses a shorter, quieter sentence.",
    userHighlights: [
      "The missing-source-sentence message now reads `Ausgangssatz nicht gespeichert.`",
      "The meaning stays the same, but the card copy is less verbose.",
      "The English translation and fallback behavior remain unchanged.",
    ],
    technicalNotes: [
      "Shortened the German `vocabulary_source_sentence_not_saved_on_card` translation in `src/assets/translations.json`.",
      "Kept the existing fallback key and rendering logic unchanged.",
      "Documented the copy update in the release notes page.",
    ],
  },
  {
    id: "mnemonic-card-copy-now-stays-shorter",
    timestamp: "2026-06-03T21:10:13+02:00",
    title: "Mnemonic card copy now stays shorter",
    summary:
      "The guided mnemonic card now removes the extra description line and uses a more direct German generation action.",
    userHighlights: [
      "The explanatory sentence under `Eselsbrücke` is no longer shown.",
      "The German guided action now reads `Mnemonik anfertigen`.",
      "The anchor-word flow remains unchanged.",
    ],
    technicalNotes: [
      "Removed the `mnemonic_card_description` render and translation key.",
      "Updated the `mnemonic_card_generate_with_guidance` translations.",
      "Adjusted the mnemonic Playwright selector for the new German action label.",
    ],
  },
  {
    id: "vocabulary-details-no-longer-repeat-added-on-date",
    timestamp: "2026-06-03T21:09:13+02:00",
    title: "Vocabulary details no longer repeat the added-on date",
    summary:
      "The vocabulary card on the generated text details page no longer repeats the created date, so that timestamp stays visible only in the vocabulary list.",
    userHighlights: [
      "Text details now keep the vocabulary card quieter by hiding the added-on date line.",
      "The vocabulary progress list still shows when each word was added.",
      "The rest of the vocabulary card content stays unchanged.",
    ],
    technicalNotes: [
      "Removed the `vocabulary_label_added_on` render block and its shared date formatter from `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Kept the existing vocabulary progress table row unchanged so the added-on metadata remains available there.",
      "Updated the release log to document the vocabulary-details-only copy change.",
    ],
  },
  {
    id: "quick-translate-example-sentences-now-rewrite-bad-repetitions",
    timestamp: "2026-06-03T21:08:41+02:00",
    title: "Quick translate example sentences now rewrite bad repetitions",
    summary:
      "The quick translation tool now rewrites suspicious short example sentences before showing them, so obvious repeated fragments are less likely to reach the screen.",
    userHighlights: [
      "Short example sentences are checked more strictly before they appear under the translation.",
      "If an example sentence repeats part of the translated phrase awkwardly, the app asks for a cleaner rewrite.",
      "The translation itself still shows immediately while the example sentence is repaired in the background.",
    ],
    technicalNotes: [
      "Strengthened the quick translate example-sentence prompt to require a single natural sentence that uses the translated phrase exactly once.",
      "Added a guarded rewrite pass in `quick_translate()` for suspicious short examples with repeated translated tokens.",
      "Covered the regression in the backend test suite with a repeated-fragment example for French-to-German quick translate.",
    ],
  },
  {
    id: "mnemonic-guidance-now-focuses-only-on-anchor-words",
    timestamp: "2026-06-03T16:26:29+02:00",
    title: "Mnemonic guidance now focuses only on anchor words",
    summary:
      "The guided mnemonic flow now removes the separate `Direction or scene` field, keeping generation focused on the anchor word alone.",
    userHighlights: [
      "The mnemonic form now has one guided input: the anchor word.",
      "The removed scene field no longer appears on mobile or desktop.",
      "Anchor suggestions and exact anchor usage continue to work as before.",
    ],
    technicalNotes: [
      "Removed direction state, request payload wiring, UI field rendering, and translation keys from the mnemonic card.",
      "Simplified mnemonic API guidance serialization and prompt construction to anchor-only guidance.",
      "Updated backend and Playwright regressions to assert the anchor-only guided mnemonic flow.",
    ],
  },
  {
    id: "mnemonic-anchor-input-now-uses-less-helper-copy",
    timestamp: "2026-06-03T16:25:21+02:00",
    title: "Mnemonic anchor input now uses less helper copy",
    summary:
      "The guided mnemonic anchor area now removes extra explanatory helper text so the field and suggestion action stay visually quieter.",
    userHighlights: [
      "The anchor field no longer shows the extra sentence under the input.",
      "The `Suggest anchor words` action no longer has a second explanatory line beside it.",
      "The guided mnemonic flow keeps the same anchor behavior with less copy on screen.",
    ],
    technicalNotes: [
      "Removed the anchor dynamic hint and empty-suggestions hint render blocks from `VocabularyPreviewCard`.",
      "Deleted the now-unused translation keys from `translations.json`.",
      "Kept the existing mnemonic generation payload and suggestion request behavior unchanged.",
    ],
  },
  {
    id: "mnemonic-anchor-field-now-uses-single-line-validation",
    timestamp: "2026-06-03T16:23:53+02:00",
    title: "Mnemonic anchor field now uses single-line validation",
    summary:
      "The guided mnemonic anchor field now behaves like a regular text input, keeping anchor words short, single-line, and easier to edit on mobile.",
    userHighlights: [
      "The anchor word field now matches the `Direction or scene` field style.",
      "Pasted line breaks are normalized into spaces so the anchor stays a clean sound hook.",
      "Anchor words remain exact in the generated draft.",
    ],
    technicalNotes: [
      "Replaced the anchor textarea in `VocabularyPreviewCard` with a validated single-line text input.",
      "Added shared anchor normalization for typed values and selected suggestion chips, with an 80-character native `maxLength`.",
      "Extended the mnemonic Playwright regression to assert the anchor control is an input with validation attributes.",
    ],
  },
  {
    id: "structure-mode-now-shows-paragraph-ordering-markers",
    timestamp: "2026-06-03T16:22:42+02:00",
    title: "Structure mode now shows paragraph ordering markers",
    summary:
      "The structure layer now recognizes clearer paragraph flow markers and comparison patterns in French reading texts, so the mode marks more of the structure a learner expects to see.",
    userHighlights: [
      "Ordering markers such as `Premièrement`, `De plus`, and `Puis` are now highlighted as structure.",
      "Comparison patterns such as `plus chers que` and `plus personnalisé` are now treated as structural spans.",
      "Mobile structure mode keeps more meaningful markers visible while still limiting visual clutter.",
    ],
    technicalNotes: [
      "Expanded French rule-based grammar annotation coverage for sequence connectors and comparison structures.",
      "Adjusted structure-overlay selection to prioritize sentence-leading semantic connectors and raise the mobile visible-structure budget.",
      "Added unit coverage for the new parser and selector behavior plus E2E coverage for semantic connector and comparison markers in the pattern layer.",
    ],
  },
  {
    id: "vocabulary-sentence-translations-now-repair-bad-german-calques",
    timestamp: "2026-06-03T16:22:34+02:00",
    title: "Vocabulary sentence translations now reject a bad German calque",
    summary:
      "Saved vocabulary cards now guard against a literal German sentence translation pattern that produced ungrammatical mother-tongue text.",
    userHighlights: [
      "German sentence translations for saved French vocabulary should no longer show the broken `Vorstellungskraft eintaucht` wording.",
      "When the app detects this bad sentence pattern, it asks for a repaired sentence before storing the shared translation.",
      "If the repair still fails the quality check, the app avoids saving the misleading sentence translation instead of reusing it on more cards.",
    ],
    technicalNotes: [
      "Strengthened `_build_translation_prompt()` so sentence translations must pass a target-language grammar check and explicitly avoid transitive German `eintauchen` calques.",
      "Added `_translation_sentence_quality_issue()` and a repair path in `generate_translation()` before shared `VocabularySentenceTranslation` values are cached.",
      "Added backend regression coverage for the observed French-to-German `trempe votre imagination` failure and for the prompt safeguard.",
    ],
  },
  {
    id: "word-preview-now-resets-hidden-translation-when-switching-words",
    timestamp: "2026-06-03T16:19:47+02:00",
    title: "Word previews now reset before showing another translation",
    summary:
      "When you switch from one saved word to another in a generated text, the preview no longer carries over the previously revealed translation.",
    userHighlights: [
      "Clicking a different highlighted word now starts with its own hidden or pending translation state.",
      "A revealed answer for one word no longer makes the next word's translation visible by accident.",
      "The fix applies to both body text words and clickable title words.",
    ],
    technicalNotes: [
      "Added a vocabulary/start-based React key to `SavedWordPreviewFlyout` render sites in `GeneratedTextDetailsTextPane.tsx` so local reveal state remounts per selected word.",
      "Extended the highlighted-word Playwright regression to reveal one saved translation, switch to another saved word, and assert the second preview returns to the reveal state.",
    ],
  },
  {
    id: "similar-words-now-hide-case-only-same-word-duplicates",
    timestamp: "2026-06-03T16:16:35+02:00",
    title: "Similar words now hide case-only duplicate words",
    summary:
      "The similar-words card now avoids showing the same word again when the app cannot confirm that it represents a distinct meaning.",
    userHighlights: [
      "Case-only duplicates such as lowercase and uppercase versions of the same word no longer appear as similar words just because punctuation differs in the saved meaning.",
      "Same-spelling words are shown only when both entries have a concrete different meaning available.",
      "The similar-words list should feel less misleading when generated vocabulary contains near-duplicate entries.",
    ],
    technicalNotes: [
      "Tightened exact-spelling filtering in `computeSimilarVocabularySuggestions()` so fallback-only or pending meanings no longer qualify as different senses.",
      "Added normalized meaning comparison that ignores quote and punctuation-only differences before accepting same-spelling matches.",
      "Added unit regression coverage for pending meanings and the `ecco`/`Ecco` case-only duplicate.",
    ],
  },
  {
    id: "similar-word-distinctions-now-use-paired-example-sentences",
    timestamp: "2026-06-03T16:16:10+02:00",
    title: "Similar-word distinctions now use paired example sentences",
    summary:
      "The word-comparison panel now keeps examples in a consistent two-line format: one sentence in the learning language and one in the learner's mother tongue.",
    userHighlights: [
      "Example sentences in the usage section should no longer mix languages inside one sentence.",
      "Each comparison now shows the learning-language example and its mother-tongue translation as separate lines.",
      "The distinction panel keeps the same structure across generated responses, making related words easier to compare.",
    ],
    technicalNotes: [
      "Bumped the similar-words explainer cache schema version so older mixed-language cached responses are not reused.",
      "Updated the backend prompt and normalizer to require `example_sentence_learning_language` and `example_sentence_translation_language` for main usage and every similar word.",
      "Updated the frontend payload type, panel rendering, translations, backend regression coverage, and Playwright route test fixture for the paired-example structure.",
    ],
  },
  {
    id: "mnemonic-anchor-words-now-stay-literal-across-languages",
    timestamp: "2026-06-03T16:15:07+02:00",
    title: "Mnemonic anchor words now stay literal across languages",
    summary:
      "Guided mnemonic drafts now preserve the exact anchor word you typed or selected, even when that anchor is in a different language than your app language or target language.",
    userHighlights: [
      "A typed anchor such as `Select` now remains `Select` in the generated mnemonic.",
      "Anchor suggestions are treated as personal sound hooks, not words that should be translated or adapted.",
      "The anchor hint no longer says the word has to be in your own language.",
      "The rest of the mnemonic still follows your interface language where possible.",
    ],
    technicalNotes: [
      "Strengthened `_build_mnemonic_prompt()` in `barkguide/reeprise/tasks.py` so guided anchors are explicitly treated as literal cross-language sound hooks.",
      "Updated `VocabularyPreviewCard` so any provided anchor is sent as an exact anchor instead of depending on a separate checkbox.",
      "Adjusted mnemonic anchor guidance copy in `translations.json` to describe personal sound anchors rather than mother-tongue-only anchors.",
      "Added prompt rules that forbid translating, localizing, inflecting, correcting, or replacing a provided anchor word.",
      "Added backend regression coverage for an Italian learning word with an English anchor and German mnemonic context.",
    ],
  },
  {
    id: "struggle-word-practice-now-rotates-through-unused-vocabulary-first",
    timestamp: "2026-06-03T16:14:30+02:00",
    title: "Struggle-word practice now rotates through unused vocabulary first",
    summary:
      "Practice texts for tricky words now prefer vocabulary that has not already been used in earlier struggle texts, so repeated sessions keep introducing new items until the full set has been covered.",
    userHighlights: [
      "You should see new reinforcement words sooner instead of getting the same set on repeat.",
      "Previously used struggle words are now deferred until the unused pool has been exhausted.",
      "The struggle-words list refreshes again after creating a practice text, so the composer does not keep showing stale items.",
    ],
    technicalNotes: [
      "Updated `select_struggle_practice_vocabularies()` in `barkguide/reeprise/tasks.py` to rank vocabulary that has not appeared in prior `STRUGGLE_PRACTICE` texts ahead of reused items.",
      "Invalidated the `struggle-words` React Query cache after creating daily or struggle-practice texts so the CTA refetches fresh candidates immediately.",
      "Added backend regression coverage for the unused-before-reuse selection order and a Playwright check that the struggle-word lookup refetches after a practice-text create.",
    ],
  },
  {
    id: "german-knowledge-title-style-now-avoids-translated-why-headlines",
    timestamp: "2026-06-02T17:19:22+02:00",
    title: "German knowledge titles now avoid translated Why-style headlines",
    summary:
      "German idea suggestions and generated knowledge-text titles now use calmer knowledge labels instead of copying the English `Why ...` headline rhythm.",
    userHighlights: [
      "German topic ideas should feel more natural to German-speaking learners.",
      "Suggestions now prefer labels such as `Leisere Stimmen im Aufzug` over translated question headlines.",
      "Older queued German suggestions using the translated `Warum ...` style are skipped before they are shown.",
    ],
    technicalNotes: [
      "Added German-specific knowledge-title guidance to the shared editorial title rules.",
      "Rejected German knowledge titles and topic ideas that start with the translated `Warum ...` pattern.",
      "Rewrote German fallback topic ideas from scenario fragments into knowledge-text labels.",
      "Added backend regression coverage for prompts, validators, fallbacks, and queued topic suggestions.",
    ],
  },
  {
    id: "topic-title-completeness-now-covers-every-learning-language",
    timestamp: "2026-06-02T17:12:32+02:00",
    title:
      "Topic and title completeness checks now cover every learning language",
    summary:
      "Topic ideas and generated-text titles now use one shared language-quality guard so incomplete question fragments are handled consistently across all supported learning languages.",
    userHighlights: [
      "Incomplete idea titles are checked before they appear in the composer.",
      "Generated titles use the same completeness rules before they are saved.",
      "Future language additions now include this validation in the rollout checklist.",
    ],
    technicalNotes: [
      "Added `barkguide/reeprise/language_quality.py` with completeness guidance for every backend learning language.",
      "Wired the shared completeness guard into topic-idea validation and generated-title validation.",
      "Updated title-generation and topic-idea prompts to include language-specific completeness guidance.",
      "Extended backend regression coverage and updated the add-new-language rollout documentation.",
    ],
  },
  {
    id: "text-details-header-metadata-now-removes-final-status-pill",
    timestamp: "2026-06-02T17:07:11+02:00",
    title: "Text details now keeps header metadata quieter",
    summary:
      "The text details header now moves directly from the title into publication metadata, without the extra finalized-status pill.",
    userHighlights: [
      "The `Text final` pill no longer appears under the text title.",
      "Publication date, time, level, and prompt metadata now use a cleaner sans-serif style.",
      "The header keeps the same reading content while reducing visual noise.",
    ],
    technicalNotes: [
      "Removed the reading-phase badge from `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Deleted the now-unused reading-phase resolver and translation keys.",
      "Updated Playwright regression coverage to assert the pill is absent and metadata renders with a sans-serif font family.",
    ],
  },
  {
    id: "topic-ideas-now-reject-incomplete-german-questions",
    timestamp: "2026-06-02T17:04:13+02:00",
    title: "Topic ideas now reject incomplete German question titles",
    summary:
      "German topic ideas now avoid incomplete question phrasing before they appear in the composer or become generated-text titles.",
    userHighlights: [
      "Suggested ideas should no longer produce titles like `Warum fühlen sich Montags weniger motiviert?`.",
      "Queued ideas created before the fix are checked again before they are shown.",
      "Generated German titles now fall back when the model returns the same incomplete reflexive question shape.",
    ],
    technicalNotes: [
      "Added German reflexive-question validation to `barkguide/reeprise/topic_intelligence.py` for fresh and queued topic ideas.",
      "Removed the first-pending queue bypass so every pending suggestion is validated before being marked shown.",
      "Added the same German headline guard to `_validate_headline_style(...)` in `barkguide/reeprise/tasks.py`.",
      "Added backend regression coverage for fresh validation, queued suggestions, and generated-title validation.",
    ],
  },
  {
    id: "reeprise-compose-keeps-settings-but-hides-text-type-select-for-mvp",
    timestamp: "2026-05-31T21:47:59+02:00",
    title: "Generate Text keeps settings panel but hides text-type selector",
    summary:
      "The settings panel toggle was restored, while the text-type selector itself is now hidden for MVP and generation uses the fixed MVP text type by default.",
    userHighlights: [
      "The settings toggle is available again for level, tense, and text length.",
      "Text type is no longer shown as a chooser in the composer.",
      "Generation still submits a valid fixed text type in the background.",
    ],
    technicalNotes: [
      "Restored the settings toggle and tabbed settings panel in `src/components/GenerateTextCard.tsx`.",
      "Removed visible `MyOtherSelect` text-type chooser and replaced it with a hidden registered `typ` field using the MVP default text type.",
      "Updated `e2e/reeprise-generation.spec.ts` to assert the settings toggle exists, text-type chooser is hidden, and `text_type=KNOWLEDGE` is still posted.",
    ],
  },
  {
    id: "reeprise-compose-removes-set-values-toggle-for-mvp",
    timestamp: "2026-05-31T21:43:11+02:00",
    title: "Generate Text now removes the set-values toggle for MVP",
    summary:
      "The additional set-values toggle was removed from the Generate Text composer to keep the MVP flow focused, while level, tense, and text length are now applied through stable defaults.",
    userHighlights: [
      "The extra options-toggle icon is no longer shown in the composer row.",
      "Generation remains one-step from the topic field and text-type selector.",
      "Hidden defaults now keep level, tense, and text length valid without extra setup.",
    ],
    technicalNotes: [
      "Removed the options-toggle button and the entire settings panel block from `src/components/GenerateTextCard.tsx`.",
      "Added hidden registered fields for `level`, `tense`, and `word_number`, with normalization defaults (`A2`, `A`, `50`) to prevent invalid `unselected` submissions.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert the toggle icon is absent and POST still includes `level`, `tense`, and `word_number` defaults.",
    ],
  },
  {
    id: "reeprise-compose-hides-language-selector-in-single-language-space",
    timestamp: "2026-05-31T21:39:20+02:00",
    title:
      "Generate Text now hides language selection in single-language spaces",
    summary:
      "The `Sprache` selector was removed from the Generate Text options panel for the current single-language-space MVP model, while generation still uses the active space language automatically.",
    userHighlights: [
      "The `Sprache` tab is no longer shown in `Weitere Optionen` during normal single-space use.",
      "Text generation still runs in the active language space without extra input.",
      "No other compose controls changed in this step.",
    ],
    technicalNotes: [
      "Removed the visible language tab/select block from `src/components/GenerateTextCard.tsx`.",
      "Kept `language` as a hidden registered form field so payload structure remains stable and still submits the active language.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert the language tab is hidden and POST still carries `language=ENGLISH`.",
    ],
  },
  {
    id: "reeprise-compose-hides-main-character-selection-for-mvp",
    timestamp: "2026-05-31T21:36:04+02:00",
    title: "Generate Text now hides main-character selection for MVP",
    summary:
      "The main-character selector was removed from the Generate Text composer, so story generation now runs without character selection as part of the MVP flow.",
    userHighlights: [
      "The `Hauptfigur` field is no longer shown in `Weitere Optionen`.",
      "Quick preset chips like `Ich`, `Anna`, and `Enrico` are removed from the composer.",
      "Text generation still works normally and no main character is sent from the UI.",
    ],
    technicalNotes: [
      "Removed visible main-character controls from `src/components/GenerateTextCard.tsx` and kept a hidden registered field for request compatibility.",
      'Normalized create and struggle-text payloads to always send `main_character: ""` and stopped persisting per-language main-character defaults.',
      "Updated helper copy in `src/routes/app/reeprise/index.tsx` to remove the `Eine Hauptfigur bestimmen` bullet from `Weitere Optionen`.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify the controls are hidden and POST still includes an empty `main_character` field.",
    ],
  },
  {
    id: "topic-idea-queue-now-expires-stale-pending-items-before-selection",
    timestamp: "2026-05-31T21:25:22+02:00",
    title: "Topic idea queue now drops stale pending entries before selection",
    summary:
      "Topic ideas now automatically expire outdated pending queue entries before choosing the next idea, so old premade suggestions no longer block fresh AI-generated ones.",
    userHighlights: [
      "Old pending ideas past their expiry window are no longer shown again.",
      "The next idea request now moves directly to valid current suggestions.",
      "Queue refill now reacts correctly after stale entries are removed.",
    ],
    technicalNotes: [
      "Added `_expire_stale_pending_topic_suggestions(...)` in `barkguide/reeprise/topic_intelligence.py` and call it before queue reads in `get_next_topic_suggestion_for_user(...)`.",
      "Also run the same expiration sweep at the start of `top_up_topic_suggestions_for_user(...)` so refill counts ignore stale pending rows.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` for stale-expiry-before-selection and top-up behavior with expired pending entries.",
    ],
  },
  {
    id: "reeprise-removes-separate-earlier-text-cards-from-daily-strip",
    timestamp: "2026-05-31T21:23:53+02:00",
    title:
      "Reeprise now shows earlier texts only in the feed, not as separate daily cards",
    summary:
      "The daily strip no longer renders a separate `earlier texts` history block. Older texts are now visible only in the main feed list.",
    userHighlights: [
      "The top daily area stays focused on today’s daily text card.",
      "Older texts are still available in the feed below.",
      "The extra show-more/show-less control for earlier daily cards is removed.",
    ],
    technicalNotes: [
      "Simplified `src/routes/app/reeprise/index.tsx` by removing daily-history card rendering and desktop history toggle state from `DailyPracticeCallout` and `Reeprise`.",
      "Updated Playwright regression in `e2e/reeprise-generation.spec.ts` to assert that no `reeprise-daily-history-card` is rendered while older items remain visible in `reeprise-feed-list`.",
    ],
  },
  {
    id: "trainer-cards-restore-canonical-noun-forms-in-reverse-and-forward-recall",
    timestamp: "2026-05-31T20:42:59+02:00",
    title:
      "Trainer cards now restore canonical noun forms for forward and reverse recall",
    summary:
      "Vocabulary trainer cards now correctly show canonical noun source forms (including articles) from structured translation details, instead of always showing only the clicked surface word.",
    userHighlights: [
      "Noun cards in trainer mode now keep article-bearing base forms like `le chat` when available.",
      "Reverse recall cards now use the same corrected source label, so answer checks remain consistent.",
      "Only trainer-card labeling changed; queue logic and review direction behavior stay the same.",
    ],
    technicalNotes: [
      "Updated `resolveTrainerCardLabels` in `src/utils/vocabularyTrainerCardLabels.ts` to prefer `source_dictionary_form` only for noun-like entries, while preserving surface-form behavior for other parts of speech.",
      "Kept existing pending-translation fallback logic unchanged.",
      "Executed frontend regression coverage with `npx vitest run`, including `src/utils/vocabularyTrainerCardLabels.test.ts`.",
    ],
  },
  {
    id: "trainer-now-unlocks-reverse-recall-even-with-strict-forward-streak-filters",
    timestamp: "2026-05-29T18:28:55+02:00",
    title:
      "Vocabulary trainer now keeps reverse recall active under strict streak filters",
    summary:
      "Reverse recall cards (mother tongue to learning language) now remain available once unlocked, even when forward-direction cards are filtered out by a low maximum streak setting.",
    userHighlights: [
      "After a word is established, you can keep training active recall from your mother tongue into the learning language.",
      "Strict forward-streak filters no longer accidentally suppress reverse-direction practice.",
      "Trainer card selection now better matches production-focused vocabulary learning.",
    ],
    technicalNotes: [
      "Updated `/api/reeprise/vocabulary/top_fifteen_priority/` in `barkguide/reeprise/views.py` to compute reverse-direction unlocks from full forward progress history, independent of `maximum_streak` filtering.",
      "Kept `maximum_streak` as a queue-card eligibility filter while preventing filtered forward progress from being misclassified as unseen.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` (`test_top_priority_keeps_reverse_unlocked_when_forward_is_above_max_streak`).",
    ],
  },
  {
    id: "topic-ideas-now-balance-interest-anchors-with-adjacent-novelty",
    timestamp: "2026-05-29T18:24:44+02:00",
    title:
      "Topic ideas now follow your interest anchors while adding adjacent novelty",
    summary:
      "Topic suggestions now track each user’s strongest interest categories as engagement anchors and intentionally mix in adjacent categories for smarter variety, reducing repetitive suggestions while staying personally relevant.",
    userHighlights: [
      "Idea suggestions now stay closer to what you actually engage with, instead of drifting into random topics.",
      "The helper now adds controlled adjacent variety so suggestions feel fresh without losing relevance.",
      "Repeated taps should now produce ideas that feel both familiar-in-direction and new-in-angle.",
    ],
    technicalNotes: [
      "Added engagement-category selection in `reeprise/topic_intelligence.py` (`_select_interest_categories_for_generation`) to derive anchor and exploration categories from user topic signals.",
      "Extended AI prompt construction with explicit sections for engagement anchors, exploration edges, and semantic anchor terms extracted from user history.",
      "Aligned fallback idea generation with the same anchor+adjacent category strategy and added backend regression coverage for category-direction selection and prompt guidance.",
    ],
  },
  {
    id: "generated-reading-flow-now-uses-stream-lifecycle-and-title-first-state-machine",
    timestamp: "2026-05-29T18:21:48+02:00",
    title:
      "Generated reading flow now uses lifecycle events and a title-first state machine",
    summary:
      "The generation-to-reading path now uses explicit stream lifecycle events, smoother frame-synced text application, reconnect-aware refresh behavior, and a visible title-first reading phase indicator on details.",
    userHighlights: [
      "Streaming now transitions through a clearer sequence: title pending, title ready, reading ready, then finalized.",
      "Chunk rendering feels smoother during generation because text updates are applied on animation-frame cadence.",
      "If websocket reconnects during pending generation, details now refreshes automatically to reduce stale state risk.",
      "The details header now shows a calm live phase label while generation is progressing.",
    ],
    technicalNotes: [
      "Extended websocket stream payloads in `barkguide/reeprise/tasks.py` with lifecycle event kinds (`STREAM_CHUNK`, `STREAM_COMPLETE`) and explicit completion emission for both live and playback streaming.",
      "Reworked `src/hooks/useStreamingTextIntegrator.ts` into a session-aware integrator with stream completion state, last-update timestamps, and frame-synced subscriber publishing.",
      "Added websocket reconnect event dispatch in `src/hooks/useWebsocket.ts` (`ws-connection-ready`) and wired pending-details refetch in `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Added reading-phase resolution helper (`resolveGeneratedTextReadingPhase`) in `src/utils/generatedTextContent.ts` and surfaced a metadata phase badge in details pane.",
      "Updated regressions in `src/utils/generatedTextContent.test.ts` and `reeprise/tests.py` for phase progression and stream completion event semantics.",
    ],
  },
  {
    id: "first-time-onboarding-now-uses-server-state-clear-guards-and-dropoff-telemetry",
    timestamp: "2026-05-29T18:19:38+02:00",
    title:
      "First-time onboarding now uses server state, focused navigation, and telemetry",
    summary:
      "New-user onboarding now persists language spaces on the server, uses an explicit completion flag for routing, hides confusing topbar exits during forced setup, and adds submission/error/confirmation states plus drop-off telemetry.",
    userHighlights: [
      "First-time setup is now account-based and no longer depends only on browser local storage.",
      "During forced onboarding, quick translate, language switching, and topbar back are hidden to keep the flow focused.",
      "Onboarding now shows clear save/retry feedback and a short confirmation step before entering the feed.",
      "If onboarding is opened intentionally from settings, normal back/close behavior remains available.",
    ],
    technicalNotes: [
      "Extended backend `UserSetting` with `added_language_spaces` and `onboarding_completed`, including migration `0087_usersetting_onboarding_and_events.py` that marks existing users as already onboarded.",
      "Persisted onboarding completion and selected spaces through `PATCH /api/reeprise/user_settings/` from `src/routes/app/start-learning.tsx`.",
      "Switched app onboarding guard logic in `src/routes/app/route.tsx` to server `onboarding_completed` instead of local-only heuristics.",
      "Added onboarding telemetry endpoint `POST /api/reeprise/onboarding/events/` and frontend tracking (`onboarding_started`, `step_completed`, `onboarding_completed`, `onboarding_abandoned`).",
      "Improved onboarding UX in `src/routes/start-learning.tsx` with submit loading, retry-on-error feedback, search-empty retry, and completion confirmation CTA.",
      "Updated regressions in `e2e/language-space-add-flow.spec.ts` and `e2e/mobile-profile-space-settings.spec.ts`, plus backend coverage in `reeprise/tests.py` for new user-settings and onboarding-event API behavior.",
    ],
  },
  {
    id: "generated-text-streaming-now-flushes-every-chunk-for-lower-latency",
    timestamp: "2026-05-29T18:15:40+02:00",
    title: "Generated text streaming now flushes every chunk for lower latency",
    summary:
      "Streaming delivery now flushes each model chunk immediately to the client instead of waiting for small batch buffering, reducing visible delay before title/story updates appear.",
    userHighlights: [
      "Title and story updates now appear faster during generation.",
      "Streaming feels more immediate while preserving title-first render order on details.",
      "No workflow changes are required; this is a latency improvement.",
    ],
    technicalNotes: [
      "Reduced websocket stream aggregation threshold in `barkguide/reeprise/tasks.py` (`LIVE_STREAM_WS_BATCH_CHUNK_COUNT = 1`) so each chunk is forwarded as it arrives.",
      "Kept existing stream-reset semantics unchanged (`reset_stream` still sent on first payload only).",
      "Title-first gating remains active on details rendering paths from the previous update.",
    ],
  },
  {
    id: "topic-ideas-now-avoid-repeats-with-history-aware-generation-and-variant-blocking",
    timestamp: "2026-05-29T18:13:41+02:00",
    title:
      "Topic ideas now avoid repeats using history-aware generation and variation blocking",
    summary:
      "The idea helper now uses each user’s past topic history when generating new suggestions and blocks close rewrites of already shown topics, so repeated idea loops happen much less often.",
    userHighlights: [
      "Idea suggestions now stay fresher across repeated taps instead of circling back to the same topic wording.",
      "Previously shown or already used topics are now actively treated as blocked context for new suggestion generation.",
      "Small wording variations of the same idea are now filtered out before they appear in the queue.",
    ],
    technicalNotes: [
      "Updated backend topic-idea generation in `reeprise/topic_intelligence.py` to pass user topic history into the AI prompt as explicit blocked topics plus a strict headline-structure contract.",
      "Added stronger near-duplicate detection (token-overlap + normalized string similarity) and reused it in queue refill and candidate validation.",
      "Extended backend tests in `reeprise/tests.py` to cover history-aware prompting, rejection of near-duplicate variations, and skipping pending suggestions that repeat already shown topics.",
    ],
  },
  {
    id: "generated-text-details-now-enforces-title-first-before-body-stream",
    timestamp: "2026-05-29T18:12:21+02:00",
    title:
      "Generated text details now shows the title first before body streaming",
    summary:
      "The details page now enforces a strict title-first reading flow during generation: body text stays hidden until a title is available, then the story streams in.",
    userHighlights: [
      "When generation is pending, you now always see the title phase first.",
      "Story text no longer appears before a title on the generated-text details page.",
      "Once the title is available, streaming continues normally below it.",
    ],
    technicalNotes: [
      "Extended `resolveGeneratedTextContent(...)` in `src/utils/generatedTextContent.ts` with `enforceTitleFirst` for strict pending render order.",
      "Wired `enforceTitleFirst: true` into generated-text details rendering paths in `src/routes/app/reeprise/$generatedTextId.tsx` and `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Added regression coverage in `src/utils/generatedTextContent.test.ts` to ensure pending story text is suppressed until a title exists when title-first mode is enabled.",
    ],
  },
  {
    id: "first-sign-in-onboarding-now-hides-topbar-back-button",
    timestamp: "2026-05-29T18:06:50+02:00",
    title: "First sign-in onboarding now hides the topbar back button",
    summary:
      "When onboarding is mandatory for a new account, the topbar back button is now hidden to avoid sending users toward a non-meaningful previous screen.",
    userHighlights: [
      "New users in forced `/app/start-learning` onboarding no longer see a topbar back button.",
      "Onboarding opened intentionally from settings still keeps the back button available.",
      "First-setup navigation now stays clearer and less ambiguous.",
    ],
    technicalNotes: [
      "Added forced-onboarding detection in `src/routes/app/route.tsx` (`isForcedStartLearningOnboarding`) using route + persisted user-scoped language-space state.",
      "Updated mobile and desktop topbar leading-action rendering to hide `Go back` only for forced onboarding while preserving normal behavior elsewhere.",
      "Extended `e2e/language-space-add-flow.spec.ts` to assert back button visibility for settings-launched onboarding and absence for forced first-sign-in onboarding.",
    ],
  },
  {
    id: "generated-text-details-now-shows-title-earlier-and-streams-body-sooner",
    timestamp: "2026-05-29T18:02:42+02:00",
    title:
      "Generated text details now shows the title earlier and starts body streaming sooner",
    summary:
      "Text generation now starts immediately without waiting on a separate upfront title request for standard generations, so the details page can show a model-generated title early and begin streaming body content sooner.",
    userHighlights: [
      "The details page now receives the generated title earlier during active generation.",
      "Body streaming starts sooner for normal text generation flows.",
      "Live text updates feel more continuous with shorter websocket batching.",
    ],
    technicalNotes: [
      "Updated `complete_generated_text` in `barkguide/reeprise/tasks.py` so only deferred reinforcement flows precompute a committed title before body generation.",
      "For standard flows, generation now requests title+story in one streamed model response and extracts the final story/title from that single response.",
      "Reduced websocket live-stream batching in `barkguide/reeprise/tasks.py` (`LIVE_STREAM_WS_BATCH_CHUNK_COUNT = 2`) so text fragments reach the client with lower visible latency.",
      "Added regression coverage in `src/utils/generatedTextContent.test.ts` for streamed `title`-first JSON followed by partial `story` chunks.",
    ],
  },
  {
    id: "start-learning-onboarding-now-hides-topbar-language-and-quick-translate-controls",
    timestamp: "2026-05-29T18:02:24+02:00",
    title:
      "Start-learning onboarding now hides topbar language and quick-translate controls",
    summary:
      "While selecting language spaces in onboarding, the topbar no longer shows quick translate or language-space switching, so first-time setup stays focused.",
    userHighlights: [
      "On `/app/start-learning`, the quick-translate icon is now hidden.",
      "On `/app/start-learning`, the top-right language-space selector is now hidden.",
      "New users stay focused on onboarding without unrelated navigation controls.",
    ],
    technicalNotes: [
      'Updated topbar route gating in `src/routes/app/route.tsx` to hide `quick-translate-topbar-link` and `topbar-language-space-switch` when `pathname === "/app/start-learning"`.',
      "Extended onboarding redirect regression in `e2e/language-space-add-flow.spec.ts` to assert both controls are absent on the onboarding route.",
    ],
  },
  {
    id: "first-sign-in-now-asks-for-language-space-selection-per-user",
    timestamp: "2026-05-29T17:56:46+02:00",
    title: "First sign-in now opens language-space onboarding per user",
    summary:
      "Language-space selection is now scoped to each account, and users without account-specific language spaces are guided into onboarding before entering the main app.",
    userHighlights: [
      "A newly signed-in account no longer inherits the previous account’s active language space from the same browser.",
      "If your account has no language spaces yet, the app now opens the onboarding flow immediately.",
      "After finishing onboarding, your selected spaces and active language are saved for your account in this browser.",
    ],
    technicalNotes: [
      "Added user-scoped localStorage keys for language-space state (`language-space:{userId}`) and added-space scope (`added-language-spaces-v1:{userId}`).",
      "Updated app-level language-space consumers in `src/routes/app/route.tsx`, `src/routes/app/space_settings.tsx`, and `src/routes/app/start-learning.tsx` to use user-scoped keys.",
      "Added first-sign-in route/state guard in `src/routes/app/route.tsx` that redirects authenticated users without persisted user-scoped language spaces to `/app/start-learning`.",
      "Updated regressions in `e2e/language-space-add-flow.spec.ts` for user-scoped key seeding and added a first-visit redirect test to onboarding.",
      "Added unit coverage for storage-key helpers in `src/hooks/useAddedLanguageSpaces.test.ts` and `src/hooks/useLanguageSpace.test.ts`.",
    ],
  },
  {
    id: "mobile-signout-now-works-from-vpn-and-extra-local-interface-origins",
    timestamp: "2026-05-29T17:45:58+02:00",
    title:
      "Mobile sign out now works from VPN and additional local interface origins",
    summary:
      "Sign out now works again when the app is opened on mobile from non-loopback local dev origins (for example VPN or Tailscale-style interface IPs) that were previously rejected by CSRF origin checks.",
    userHighlights: [
      "The `Sign out` button in settings now completes correctly on mobile when using local network dev URLs outside plain localhost.",
      "You return to the logged-out state immediately after sign out instead of staying stuck in settings.",
      "This also stabilizes other authenticated write actions from the same local origin.",
    ],
    technicalNotes: [
      "Expanded local IPv4 discovery in `barkguide/settings.py` (`_collect_local_ipv4_addresses`) to include interface addresses parsed from `ip -4 addr show` and `ifconfig` output, while skipping loopback and `0.0.0.0`.",
      "Kept existing hostname/getaddrinfo/active-route discovery and merged all sources into CSRF trusted-origin generation.",
      "Added backend regression tests in `barkguide/tests.py` to assert interface-derived addresses (including `100.x.x.x`) are captured and invalid addresses are excluded.",
    ],
  },
  {
    id: "multi-word-selection-now-uses-phrase-mode-labels",
    timestamp: "2026-05-25T16:17:52+02:00",
    title: "Multi-word selection now uses clear Phrase mode labels",
    summary:
      "The context-selection toggle in reading now uses phrase-oriented wording so the feature intent is clearer before interaction.",
    userHighlights: [
      "Entry label is now `Phrase mode` (German: `Phrasenmodus`).",
      "Exit label is now `Exit phrase mode` (German: `Phrasenmodus beenden`).",
      "Only wording changed; the multi-word selection behavior stays the same.",
    ],
    technicalNotes: [
      "Updated `generated_text_context_mode_enter` and `generated_text_context_mode_exit` in `src/assets/translations.json`.",
      "No component logic changes were required because the toggle already uses translation keys.",
    ],
  },
  {
    id: "text-details-removes-fixed-bottom-continue-button",
    timestamp: "2026-05-25T16:01:37+02:00",
    title: "Text details no longer shows a fixed bottom continue button",
    summary:
      "The fixed `Text fortsetzen` action attached to the bottom of text details was removed to keep continuation focused on the in-flow mechanism.",
    userHighlights: [
      "The bottom-attached continue button is no longer shown on text details.",
      "The page now has more vertical space on mobile without a fixed CTA bar.",
      "Continuation remains available through the existing in-flow continuation behavior.",
    ],
    technicalNotes: [
      "Removed mobile fixed-bottom `create-vocabulary-text-classic-button-mobile` rendering from `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Kept existing desktop continuation controls and inline continuation logic unchanged.",
      "Extended `e2e/inline-vocabulary-continuation.spec.ts` to assert the removed mobile button test id is absent.",
    ],
  },
  {
    id: "pattern-help-label-now-uses-struktur-erklart",
    timestamp: "2026-05-25T16:01:49+02:00",
    title: "Pattern help label now uses “Struktur erklärt” in German",
    summary:
      "The inline help label and its panel title were renamed from `Muster erklärt` to `Struktur erklärt` for wording consistency with the selector.",
    userHighlights: [
      "The help link now reads `Struktur erklärt`.",
      "The help panel title now also reads `Struktur erklärt`.",
      "Behavior and placement remain unchanged.",
    ],
    technicalNotes: [
      "Updated `generated_text_patterns_help_link` and `generated_text_patterns_help_title` German values in `src/assets/translations.json`.",
      "No component logic changes were required because the UI already uses translation keys.",
    ],
  },
  {
    id: "reading-toolbar-removes-layer-header-label",
    timestamp: "2026-05-25T16:00:15+02:00",
    title: "Reading toolbar no longer shows the “Layer” header label",
    summary:
      "The small `Layer/Layers` heading above the reading controls was removed so the toolbar starts directly with the mode options and feels cleaner.",
    userHighlights: [
      "The selector now starts immediately with `Lesemodus`, `Vokabel`, and `Struktur`.",
      "Toolbar density is reduced without changing behavior.",
      "Pattern and context controls remain in the same place.",
    ],
    technicalNotes: [
      "Removed visible `generated_text_layer_label` rendering from `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Kept the group accessibility label and existing test IDs unchanged.",
    ],
  },
  {
    id: "pattern-layer-selector-labels-now-use-reading-vocabulary-structure",
    timestamp: "2026-05-25T15:32:48+02:00",
    title: "Pattern layer selector labels now use calmer semantic wording",
    summary:
      "The layer selector labels were renamed to align with the reading-first product language and avoid technical shorthand.",
    userHighlights: [
      "German labels now read `Lesemodus`, `Vokabel`, and `Struktur`.",
      "English labels now read `Reading mode`, `Vocabulary`, and `Structure`.",
      "The selector behavior is unchanged; only wording was refined.",
    ],
    technicalNotes: [
      "Updated translation keys `generated_text_layer_none`, `generated_text_layer_words`, and `generated_text_layer_patterns` in `src/assets/translations.json`.",
      "No component logic changes were required because the selector already reads labels through translation keys.",
    ],
  },
  {
    id: "text-details-metadata-audio-now-keeps-mobile-layout-clean-and-stable",
    timestamp: "2026-05-25T15:26:51+02:00",
    title:
      "Text metadata now keeps audio on a clean dedicated line on small mobiles",
    summary:
      "The text-details metadata header was refined so very narrow screens no longer push the audio trigger into an awkward wrapped position with separators.",
    userHighlights: [
      "Date/time and level remain grouped in the primary metadata line.",
      "The audio trigger now sits on its own compact line, aligned and predictable on iPhone mini widths.",
      "The metadata block no longer shows a dangling separator before wrapped audio.",
      "Pattern help link now appears only when the `Muster` layer is active.",
    ],
    technicalNotes: [
      "Adjusted metadata rendering in `src/components/GeneratedTextDetailsTextPane.tsx` to move `metadataAccessory` below the primary date/level row.",
      "Removed inline separator coupling between metadata text and accessory in the same component to prevent broken wrap rhythm.",
      'Scoped `generated-text-patterns-help-link` visibility to `overlayMode === "PATTERNS"` and auto-closes the panel when leaving that mode.',
      "Updated `e2e/highlighted-word-preview.spec.ts` to assert hidden-by-default help link and visible-on-pattern-layer behavior.",
    ],
  },
  {
    id: "reading-toolbar-now-uses-flat-aligned-layer-controls",
    timestamp: "2026-05-25T15:22:00+02:00",
    title: "Reading toolbar controls are now flat, aligned, and less intrusive",
    summary:
      "The reading toolbar was refined from pill-style controls to a flat semantic strip so the layer and context actions consume less space and stay visually aligned, especially on mobile.",
    userHighlights: [
      "Layer controls (`Keine`, `Wörter`, `Muster`) now use a clean text-tab style instead of large pills.",
      "Control labels now share one height and baseline rhythm, eliminating mixed vertical alignment.",
      "Context selection action is now a lightweight mode toggle instead of a large rounded button.",
      "The toolbar feels calmer and leaves more room for the text.",
    ],
    technicalNotes: [
      "Replaced split mobile/desktop control markup in `src/components/GeneratedTextDetailsTextPane.tsx` with one unified responsive control row.",
      "Removed layer-picker menu state/effects (`layerMenuOpen`, `layerMenuRef`) and related outside-click/Escape logic from the same component.",
      "Shifted active-state signaling to underline/typography hierarchy (`border-b-2`) instead of filled rounded containers.",
      "Removed unused copy key `generated_text_context_mode_compact` from `src/assets/translations.json`.",
      "Preserved existing interaction hooks and test IDs (`generated-text-layer-*`, `generated-text-context-mode-toggle`, `generated-text-patterns-help-link`).",
    ],
  },
  {
    id: "mobile-reading-toolbar-controls-now-use-compact-density",
    timestamp: "2026-05-25T15:15:06+02:00",
    title: "Reading toolbar now uses a compact mobile picker layout",
    summary:
      "On mobile, the large segmented `Layer` switch was replaced with a compact layer picker and a minimal context toggle so reading controls feel lighter and calmer.",
    userHighlights: [
      "The layer control is now a single compact picker instead of a full-width segmented bar.",
      "The context action is now a minimal `Kontext` toggle chip on mobile.",
      "The toolbar takes significantly less vertical space on narrow screens.",
      "Desktop behavior and existing interactions remain unchanged.",
    ],
    technicalNotes: [
      "Added a mobile-only layer picker menu in `src/components/GeneratedTextDetailsTextPane.tsx` with accessible menu semantics and outside-click / Escape dismissal.",
      "Kept desktop segmented controls in place (`sm:` breakpoint) to avoid desktop interaction regressions while modernizing mobile layout.",
      "Introduced compact context label copy key `generated_text_context_mode_compact` in `src/assets/translations.json`.",
      "Preserved existing control behavior and test IDs (`generated-text-layer-*`, `generated-text-context-mode-toggle`, `generated-text-patterns-help-link`).",
    ],
  },
  {
    id: "text-details-metadata-layout-now-uses-cleaner-structure-and-fewer-dividers",
    timestamp: "2026-05-25T14:57:28+02:00",
    title:
      "Text details metadata now uses a cleaner layout with fewer dividers",
    summary:
      "The header metadata on text details was restructured to reduce visual noise: fewer divider lines, tighter spacing, and clearer grouping between date/level/audio and optional prompt context.",
    userHighlights: [
      "Header spacing is tighter and less fragmented.",
      "The metadata section now uses a single clean divider instead of stacked lines.",
      "Prompt context no longer duplicates the main title when both are the same.",
      "Audio action placement stays visible but no longer breaks hierarchy with stray separators.",
    ],
    technicalNotes: [
      "Refactored metadata rendering in `src/components/GeneratedTextDetailsTextPane.tsx` into a compact two-row structure with a single top divider (`border-t`).",
      "Moved audio accessory into the primary metadata row and reduced row spacing/margins to avoid wrapped bullet artifacts.",
      "Added deduplication logic (`shouldShowPromptTopicMeta`) so prompt metadata is hidden when it matches the resolved title.",
      "Kept full prompt dialog behavior intact and verified via targeted E2E regression in `e2e/highlighted-word-preview.spec.ts`.",
    ],
  },
  {
    id: "patterns-layer-rule-quality-expanded-for-value-first-surfacing",
    timestamp: "2026-05-25T14:45:35+02:00",
    title: "Pattern Layer now recognizes more high-value French structures",
    summary:
      "Rule coverage for meaningful structures was expanded so Pattern underlines prioritize reusable chunks, semantic connectors, and modality/time-frame signals while generic spans remain mostly hidden.",
    userHighlights: [
      "More useful chunks like fixed expressions are now recognized and surfaced.",
      "Meaning-shaping connectors are surfaced more reliably.",
      "Modality and time-shift sentence skeletons are easier to notice on desktop.",
      "Underlying detection remains deterministic and reading-first.",
    ],
    technicalNotes: [
      "Expanded deterministic French connector coverage in `src/utils/grammarPatternAnnotations.ts` with additional single/multi-token connector rules.",
      "Added fixed-expression rule coverage for reusable chunks such as `avoir besoin de` and `prendre en compte` in `src/utils/grammarPatternAnnotations.ts`.",
      "Improved verb-form heuristics in `src/utils/grammarPatternAnnotations.ts` for conditional/future-like endings to support better value-driven surfacing.",
      "Refined display-value scoring in `src/utils/grammarPatternAnnotations.ts` with modality and time-shift boosts for high-value verbal structures.",
      "Extended parser/selection tests in `src/utils/grammarPatternAnnotations.test.ts` for new fixed expressions, connector variants, and modality visibility behavior.",
    ],
  },
  {
    id: "text-details-title-topic-now-uses-metadata-hierarchy-with-level",
    timestamp: "2026-05-25T14:43:29+02:00",
    title: "Text details now keeps topic copy in metadata and shows level",
    summary:
      "The text-details header now demotes topic copy into the metadata line instead of rendering it as an all-caps strip, and the text level is now visible in the same metadata context.",
    userHighlights: [
      "The topic line on text details is no longer styled as an all-caps strip.",
      "Topic copy now appears as calm metadata near date/time context.",
      "Each text now shows its level directly in the metadata row.",
    ],
    technicalNotes: [
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` metadata rendering to include level (`form_level`) and inline topic preview with normal casing.",
      "Removed the standalone uppercase topic strip block from the same component and kept full-prompt dialog access in metadata.",
      "Added regression coverage in `e2e/highlighted-word-preview.spec.ts` to verify metadata-level visibility and non-uppercase topic rendering (`text-transform: none`).",
    ],
  },
  {
    id: "text-details-clarify-now-opens-as-topbar-overlay-and-sidebar-card-is-removed",
    timestamp: "2026-05-25T14:33:26+02:00",
    title: "Text details Clarify now opens from topbar as an overlay",
    summary:
      "The Clarify experience on text details now opens directly from the topbar action as an overlay, and the old always-visible sidebar card was removed.",
    userHighlights: [
      "Clarify no longer stays pinned as a card in the right sidebar.",
      "Use the topbar “Was möchtest du klären?” action to open the helper when needed.",
      "The helper now opens and closes as a focused overlay flow.",
    ],
    technicalNotes: [
      "Updated the text-details topbar action in `src/routes/app/route.tsx` to emit an overlay-open event for Clarify.",
      "Removed the sidebar `LanguageHelperPanel` block and rendered the helper as a route-level overlay in `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Extended E2E checks in `e2e/classic-tools-nav.spec.ts` to verify Clarify overlay open/close behavior and input visibility from the topbar entry.",
    ],
  },
  {
    id: "patterns-layer-now-uses-display-value-selection-instead-of-detect-equals-underline",
    timestamp: "2026-05-25T14:32:45+02:00",
    title: "Patterns now underline only structures worth noticing",
    summary:
      "The Pattern layer now separates detection confidence from display value, so only high-value sentence structures are surfaced by default while generic low-value spans stay hidden.",
    userHighlights: [
      "Underlines are now intentionally sparse and focused on meaningful structures.",
      "Reusable chunks and sentence skeletons are prioritized over generic article+noun groups.",
      "Mobile remains calmer than desktop with stricter visibility thresholds.",
      "Recurring purple markers remain rare and are no longer used for generic repeated chunks.",
    ],
    technicalNotes: [
      "Extended grammar annotation model in `src/utils/grammarPatternAnnotations.ts` with `displayValue` and value-aware scoring logic.",
      "Added deterministic high-value detectors for fixed expressions and semantic connectors in `src/utils/grammarPatternAnnotations.ts` (`fixed_expression`, `semantic_connector`).",
      "Reworked `selectGrammarOverlayAnnotations()` in `src/utils/grammarPatternAnnotations.ts` to apply desktop/mobile value thresholds, sentence budgets, and global density caps.",
      "Kept low-value detected spans internal while hiding them from default Pattern rendering unless they meet value/density criteria.",
      "Added/updated tests in `src/utils/grammarPatternAnnotations.test.ts` for low-value hiding, idiom visibility, connector visibility, recurring suppression, and dense paragraph caps.",
      "Added pattern label translations for new value-prioritized categories in `src/assets/translations.json` and wired them in `src/components/GeneratedTextDetailsTextPane.tsx`.",
    ],
  },
  {
    id: "text-details-removes-previous-next-chapter-controls",
    timestamp: "2026-05-25T14:31:32+02:00",
    title: "Text details no longer shows previous/next chapter controls",
    summary:
      "The dedicated `Vorheriges Kapitel` and `Nächstes Kapitel` controls were removed from text details because continuation now happens inline in the same reading flow.",
    userHighlights: [
      "Text details now stays focused on inline continuation without chapter navigation pills.",
      "The previous/next chapter controls no longer occupy space above the main text.",
      "Continuation actions remain available through the existing inline/classic continue flow.",
    ],
    technicalNotes: [
      "Removed `LastChapterNavigationTile` and `NextChapterNavigationTile` rendering from `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Deleted the now-unused chapter navigation helper components and related icon/router imports in the same route file.",
      "Extended E2E regression assertions in `e2e/inline-vocabulary-continuation.spec.ts` to ensure `Vorheriges Kapitel` and `Nächstes Kapitel` are absent on text details.",
    ],
  },
  {
    id: "text-details-topbar-now-surfaces-clarify-and-hides-quick-translate",
    timestamp: "2026-05-25T14:29:39+02:00",
    title:
      "Text Details topbar now opens Clarify and no longer shows Quick Translate",
    summary:
      "On text details pages, the topbar now gives direct access to the Clarify helper (`Was möchtest du klären?`) and removes the quick-translate shortcut from that context.",
    userHighlights: [
      "On text details pages, quick translate is no longer shown in the topbar.",
      "A new topbar Clarify action now appears with the same “Was möchtest du klären?” wording.",
      "Tapping the Clarify action scrolls to the helper and focuses the input so asking is immediate.",
    ],
    technicalNotes: [
      "Updated topbar route gating in `src/routes/app/route.tsx` to render a text-details-only Clarify trigger and keep quick translate on non-text-details routes.",
      "Added `focus-language-helper` event wiring in `src/components/LanguageHelperPanel.tsx` so the topbar trigger scrolls to and focuses the helper textarea.",
      "Added stable selectors (`topbar-text-details-clarify-trigger`, `language-helper-question-input`) and extended E2E assertions in `e2e/classic-tools-nav.spec.ts` for text-details topbar behavior.",
    ],
  },
  {
    id: "patterns-layer-refinement-prioritizes-calm-reading-and-meaningful-structure",
    timestamp: "2026-05-25T14:23:44+02:00",
    title:
      "Pattern Layer now prioritizes calmer reading with stricter semantic selection",
    summary:
      "Pattern overlays now surface fewer, higher-value structures with rarer recurring markers and stronger phrase-level inspection focus, so reading flow stays clean while meaningful structure remains visible.",
    userHighlights: [
      "Recurring dotted markers are now noticeably rarer and no longer dominate dense passages.",
      "Pattern visibility is calmer by default, especially on mobile.",
      "Selecting a pattern now isolates its phrase more clearly while surrounding annotations soften.",
      "Underline distinctions remain semantic by type without adding extra colors.",
    ],
    technicalNotes: [
      "Refined recurring conversion and visibility defaults in `src/utils/grammarPatternAnnotations.ts` with stricter lexical/confidence gates, limited conversions, and secondary-level recurring visibility.",
      "Tuned overlay selection budgets and scoring in `src/utils/grammarPatternAnnotations.ts` to reduce low-value/default density and cap visible recurring overlays per viewport context.",
      "Adjusted underline intensity and interaction-state hierarchy in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to preserve shape differentiation while reducing baseline annotation noise.",
      "Updated active-pattern isolation logic in `src/components/GeneratedTextDetailsTextPane.tsx` so selected inspection narrows visible overlays (stronger on mobile) without removing core mechanics.",
      "Updated legend/help copy in `src/assets/translations.json` to keep wording concise and marker-oriented.",
      "Extended parser hierarchy tests in `src/utils/grammarPatternAnnotations.test.ts` for recurring rarity/visibility caps and re-ran targeted frontend unit tests.",
    ],
  },
  {
    id: "patterns-help-now-uses-visual-legend-without-color-words",
    timestamp: "2026-05-25T14:03:39+02:00",
    title:
      "“Muster erklärt” now uses a visual legend instead of color-name text",
    summary:
      "The Pattern help overlay now explains structure with visual line/color markers plus concise labels, and the guide copy no longer uses the product name in the description.",
    userHighlights: [
      "The legend now shows visual markers directly, instead of writing color names as text.",
      "“Muster erklärt” remains compact and unobtrusive next to the layer control.",
      "The explanation copy stays short and focused on reading support.",
    ],
    technicalNotes: [
      "Updated `PatternLegendPanel` in `src/components/GeneratedTextDetailsTextPane.tsx` to render swatch/line markers for noun, verb, relation, and recurring groups.",
      "Replaced color-name legend strings with neutral structural labels in `src/assets/translations.json`.",
      "Adjusted help-body wording to remove product self-reference while keeping concise guidance tone.",
    ],
  },
  {
    id: "patterns-layer-now-includes-subtle-inline-legend-help",
    timestamp: "2026-05-25T13:52:37+02:00",
    title: "Patterns layer now includes a compact “Muster erklärt” guide",
    summary:
      "A subtle inline help link was added next to the layer selector so you can quickly understand color and line semantics without leaving the reading flow.",
    userHighlights: [
      "A small “Muster erklärt” link now appears next to the layer controls.",
      "Tap/click opens a lightweight guide with color and line meaning.",
      "The guide closes with Escape or outside tap and keeps the reading page clean.",
    ],
    technicalNotes: [
      "Added a lightweight portal panel component (`PatternLegendPanel`) in `src/components/GeneratedTextDetailsTextPane.tsx` with mobile sheet and desktop modal behavior.",
      'Integrated a subtle trigger near the layer toggle (`data-testid="generated-text-patterns-help-link"`) and panel state handling with keyboard close support.',
      "Added dedicated translations for concise German legend/documentation copy in `src/assets/translations.json`.",
      "Extended reading-flow E2E coverage in `e2e/highlighted-word-preview.spec.ts` to verify guide open/close and legend content visibility.",
    ],
  },
  {
    id: "patterns-layer-now-prioritizes-semantic-density-with-mobile-aware-hierarchy",
    timestamp: "2026-05-25T13:49:48+02:00",
    title: "Patterns layer now highlights fewer, higher-value structures",
    summary:
      "Pattern overlays now use a priority hierarchy with mobile-aware density reduction so reading stays calm while important reusable structures remain visible.",
    userHighlights: [
      "Purple recurring marks are now rare and reserved for repeated meaningful chunks.",
      "Most text remains unmarked, with fewer default overlays per sentence.",
      "Mobile now shows a stricter subset of patterns to preserve readability on narrow screens.",
    ],
    technicalNotes: [
      "Added visibility hierarchy levels (`primary`, `secondary`, `advanced`) and mobile-aware annotation selection in `src/utils/grammarPatternAnnotations.ts` via `selectGrammarOverlayAnnotations()`.",
      "Refined recurring-pattern assignment to avoid broad family-wide conversion and instead flag repeated high-value chunks only.",
      "Updated reading-pane integration in `src/components/GeneratedTextDetailsTextPane.tsx` to render filtered annotations rather than all detected annotations.",
      "Adjusted grammar styling in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so visibility levels map to subtle underline intensity differences without adding new colors.",
    ],
  },
  {
    id: "text-details-now-hides-topbar-language-space-switch",
    timestamp: "2026-05-25T13:27:09+02:00",
    title: "Text Details now hides the topbar language selector",
    summary:
      "The topbar language-space selector is now hidden while viewing Text Details, reducing distraction during focused reading and vocabulary work.",
    userHighlights: [
      "On Text Details pages, the language-space selector no longer appears in the topbar.",
      "The selector remains available on the main feed and other app pages.",
      "Topbar back/profile and other existing controls keep their previous behavior.",
    ],
    technicalNotes: [
      "Updated topbar route gating in `src/routes/app/route.tsx` with `showLanguageSpaceSwitch` so `/app/reeprise/:generatedTextId` hides the language-space selector.",
      'Added `data-testid="topbar-language-space-switch"` on the selector trigger button for stable regression assertions.',
      "Added desktop and mobile E2E regression coverage in `e2e/classic-tools-nav.spec.ts` and `e2e/mobile-profile-space-settings.spec.ts` to verify the selector is visible on `/app/reeprise` and hidden on `/app/reeprise/:id`.",
    ],
  },
  {
    id: "knowledge-continuations-now-retry-away-from-story-scene-drift",
    timestamp: "2026-05-25T09:25:32+02:00",
    title:
      "Knowledge continuations now retry when output drifts into scene narration",
    summary:
      "Knowledge-mode continuation now performs an editorial-style retry when a generated continuation drifts into story-scene narration, reducing narrative fallback in continuation flows.",
    userHighlights: [
      "Knowledge continuations now stay factual/editorial more consistently.",
      "Story-scene drift in continuation output is detected and retried automatically.",
      "Inline continuation keeps rendering from the validated final continuation draft.",
    ],
    technicalNotes: [
      "Added narrative-scene drift detection heuristics and retry instruction in `barkguide/reeprise/tasks.py` (`_looks_like_knowledge_story_scene_drift`, `KNOWLEDGE_EDITORIAL_RETRY_INSTRUCTION`).",
      "Updated `_generate_chapter_impl` in `barkguide/reeprise/tasks.py` to rerun knowledge continuations once when drift is detected and to aggregate token usage across attempts.",
      "Adjusted live-stream behavior for knowledge continuations to defer websocket playback until style validation is complete, then stream the validated final draft.",
      "Added backend regression tests in `barkguide/reeprise/tests.py` for drift detection and knowledge-retry behavior.",
    ],
  },
  {
    id: "text-details-continuation-now-uses-inline-flow-only",
    timestamp: "2026-05-25T09:17:53+02:00",
    title: "Text continuation now stays in one inline flow",
    summary:
      "Continuing a text from details now uses the same inline continuation behavior for both actions, so continuation stays on the same screen without chapter-route jumps.",
    userHighlights: [
      "“Text mit Wörtern fortsetzen” now continues directly in the current text view.",
      "The continuation flow no longer jumps to chapter URLs from the classic button.",
      "Both continuation entry points now behave consistently.",
    ],
    technicalNotes: [
      "Rewired classic continue buttons in `src/routes/app/reeprise/$generatedTextId.tsx` to call `continue_inline` instead of `generate_chapter` navigation flow.",
      "Removed unused chapter-create client hook usage from details flow (`src/hooks/useChapterCreate.ts` no longer used by reeprise details).",
      "Updated E2E coverage in `e2e/inline-vocabulary-continuation.spec.ts` with a regression that asserts the classic button posts to `continue_inline` and stays on root route.",
      "Updated podcast continuation E2E expectations in `e2e/reeprise-generation.spec.ts` to validate inline continuation payload/route behavior instead of chapter-route navigation.",
    ],
  },
  {
    id: "language-space-scope-now-stays-strictly-on-added-spaces",
    timestamp: "2026-05-25T09:15:16+02:00",
    title:
      "Language-space scope now stays strict across settings and main toggle",
    summary:
      "Language selection now consistently stays within spaces you have added, so settings and the main toggle use the same scope and no longer leak unadded languages.",
    userHighlights: [
      "The main language toggle now shows only spaces you have added.",
      "Space Settings now lists only your added language spaces (legacy users still keep the MVP fallback).",
      "If all enabled MVP spaces are already added, `Add new language space` stays hidden in both places.",
    ],
    technicalNotes: [
      "Added shared scope derivation in `src/hooks/useAddedLanguageSpaces.ts` (`deriveEffectiveLanguageSpaceScope`) to keep add-space visibility and effective language-space scope consistent across screens.",
      "Updated topbar language menu logic in `src/routes/app/route.tsx` to consume the shared scope and stop unioning non-added active language IDs into visible options.",
      "Added active-language guard in `src/routes/app/route.tsx` so persisted added-space scope cannot stay on a non-added language.",
      "Updated `src/routes/app/space_settings.tsx` to render language rows from effective added-space scope instead of all enabled spaces.",
      "Extended E2E coverage in `e2e/language-space-add-flow.spec.ts` for non-added active-language leak prevention and settings-only-added-space visibility.",
      "Added unit tests in `src/hooks/useAddedLanguageSpaces.test.ts` for persisted scope, legacy fallback, and all-spaces-added detection.",
    ],
  },
  {
    id: "language-space-add-flow-now-stays-in-app-and-italian-is-enabled-for-mvp",
    timestamp: "2026-05-25T08:47:07+02:00",
    title:
      "Adding language spaces now stays in-app, with Italian enabled for MVP",
    summary:
      "Creating a new language space now opens as an in-app onboarding flow for logged-in users, Italian is enabled again for MVP language spaces, and the main language dropdown now lists only spaces the user has already added.",
    userHighlights: [
      "The settings flow to add a language space no longer sends you to a login interruption when you are already signed in.",
      "Italian is available again in the MVP language-space list.",
      "The main page language dropdown now lists only your added spaces and still includes an `Add new language space` action.",
      "When all enabled language spaces are already added, the add-space action is hidden.",
      "Legacy users without the new added-space storage keep seeing the enabled MVP spaces instead of being reduced to one language.",
    ],
    technicalNotes: [
      "Enabled `ITALIAN` in `ENABLED_LANGUAGE_SPACE_IDS` in `src/constants.ts` and added a regression assertion in `src/constants.test.ts`.",
      "Added authenticated route `src/routes/app/start-learning.tsx` that mounts `StartLearningOnboardingFlow` inside the `/app` shell and closes back to `/app/reeprise`.",
      "Updated the space-settings add-space entry in `src/routes/app/space_settings.tsx` and the main topbar language menu option in `src/routes/app/route.tsx` to route to `/app/start-learning`.",
      "Persisted added spaces through local storage (`added-language-spaces-v1`) on onboarding finish and filtered topbar language options to that user-added list (with active-space fallback).",
      "Hid add-space actions in both `src/routes/app/route.tsx` and `src/routes/app/space_settings.tsx` whenever all enabled language spaces are already available in the effective scope.",
      "Added a legacy fallback: when `added-language-spaces-v1` is not yet persisted, language menus fall back to enabled MVP spaces, and add-space actions stay hidden because all enabled spaces are already available.",
      "Added translation key `space_settings_add_new_space` in `src/assets/translations.json` for the new entry labels.",
      "Added end-to-end regression coverage in `e2e/language-space-add-flow.spec.ts` for both entry points, user-added-only language visibility, add-option hiding when all spaces are already added, and legacy fallback visibility.",
    ],
  },
  {
    id: "home-screen-launch-start-url-now-opens-the-app-main-page",
    timestamp: "2026-05-22T19:29:02+02:00",
    title: "Home-screen launch now starts on the app main page",
    summary:
      "When opening Uxhulu from a home-screen install, the launch URL now targets the main app screen instead of the public website root.",
    userHighlights: [
      "Home-screen launches now open the app main page directly.",
      "The extra step through the public website landing page is removed for installed app launches.",
      "Regular browser navigation remains unchanged.",
    ],
    technicalNotes: [
      "Changed PWA manifest `start_url` in `public/manifest.json` from `/` to `/app/reeprise`.",
      "Added regression coverage in `src/utils/pwaManifest.test.ts` to assert the manifest start URL stays pinned to `/app/reeprise`.",
    ],
  },
  {
    id: "text-details-now-keeps-detached-comma-and-full-stop-on-the-same-reading-line",
    timestamp: "2026-05-22T17:00:23+02:00",
    title: "Text details now keeps detached punctuation with the sentence flow",
    summary:
      "When generated text contains a raw line break right before punctuation, text details now keeps the punctuation attached to the surrounding sentence instead of forcing `,` or `.` to start a new line.",
    userHighlights: [
      "Commas and full stops no longer jump onto their own new line in text details.",
      "Reading flow now stays stable even when source content contains accidental newline-before-punctuation artifacts.",
      "Normal paragraph breaks continue to render as before.",
    ],
    technicalNotes: [
      "Updated `createTextSpans` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to suppress `<br>` rendering for newline clusters that are immediately followed by detachable punctuation (`.,:;!?…`) after optional spaces/tabs.",
      "Kept index semantics unchanged for vocabulary interactions by only altering render-time newline output, not the underlying text indexing model.",
      "Added regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to assert no forced `<br>` appears before detached punctuation and to verify punctuation normalization output (`\\u2060,` and `\\u2060.`).",
    ],
  },
  {
    id: "settings-signout-now-works-from-local-network-dev-origins",
    timestamp: "2026-05-22T16:47:34+02:00",
    title: "Settings sign out now works during mobile LAN testing",
    summary:
      "Signing out from settings now works reliably when the app is opened from a local network device, instead of failing due to strict CSRF origin checks.",
    userHighlights: [
      "The Sign out action in settings now completes correctly when you open the app on your phone over local Wi-Fi.",
      "You are returned to the login state immediately after sign out.",
      "Existing localhost sign-out behavior remains unchanged.",
    ],
    technicalNotes: [
      "Expanded backend CSRF trusted-origin setup in `barkguide/settings.py` to include localhost + loopback + detected local IPv4 dev origins (UI and backend ports), with optional `EXTRA_CSRF_TRUSTED_ORIGINS` overrides.",
      "Added Playwright regression coverage in `e2e/mobile-profile-space-settings.spec.ts` for the mobile `/app/space_settings` sign-out route/state transition back to login.",
    ],
  },
  {
    id: "quick-translate-mobile-send-now-uses-explicit-form-submit-guard",
    timestamp: "2026-05-22T16:45:19+02:00",
    title: "Quick Translate send now stays stable on mobile",
    summary:
      "The quick-translate send flow now uses explicit form submit handling so mobile send interactions stay in-app and no longer risk triggering browser-level page reload behavior.",
    userHighlights: [
      "Tapping the send control on mobile now reliably keeps you on the quick-translate screen.",
      "Translation results render in place after send without navigation interruptions.",
      "Desktop behavior remains unchanged.",
    ],
    technicalNotes: [
      'Added explicit `onSubmit` + `preventDefault()` handling in `src/routes/app/quick_translate.tsx` for both minimal editor branches, and switched the legacy send action to `type="submit"` to use one guarded submit path.',
      "Added mobile route/state regression coverage in `e2e/quick-translate-mobile-submit.spec.ts` to assert send-click flow stays on `/app/quick_translate` and returns full translation output.",
    ],
  },
  {
    id: "generator-and-topic-idea-now-show-clear-sign-in-feedback-on-expired-session",
    timestamp: "2026-05-22T16:38:59+02:00",
    title: "Generator and topic ideas now show clear sign-in feedback",
    summary:
      "When a session is no longer authenticated, generate-text and topic-idea actions now show explicit sign-in feedback instead of appearing to do nothing.",
    userHighlights: [
      "If your session expired, pressing send now shows a clear “Please sign in again” message.",
      "The topic-idea button now shows the same sign-in feedback when authentication is missing.",
      "General generation failures now show an explicit retry message instead of silent failure.",
    ],
    technicalNotes: [
      "Updated `useGeneratedTextCreate` and `useTopicIdeaNext` to map `401/403` responses to a dedicated `AUTH_REQUIRED` error signal.",
      "Updated `GenerateTextCard.tsx` to catch mutation failures, show auth-aware snackbar feedback, and invalidate `LOGIN_STATUS_QUERY_KEY` so app auth state refreshes immediately.",
      "Added new translation keys `auth_session_expired` and `generate_text_failed` in `src/assets/translations.json` (DE/EN).",
    ],
  },
  {
    id: "legacy-level-fallback-now-keeps-text-generation-working-without-onboarding-state",
    timestamp: "2026-05-22T16:27:38+02:00",
    title: "Text generation now recovers when legacy users have no saved level",
    summary:
      "Generation now stays available for legacy accounts that still carry missing or stale level state, including old `unselected` values from pre-onboarding flows.",
    userHighlights: [
      "Older accounts can generate texts again even if they never completed the newer onboarding setup.",
      "When no valid level is present, generation now uses the latest known user level for that language, or a safe A2 fallback.",
      "Legacy local defaults that stored `unselected` for level no longer block generation in the composer.",
    ],
    technicalNotes: [
      "Added backend level resolution in `barkguide/reeprise/views.py` (`GeneratedTextViewSet.create`) to normalize invalid/missing level input and fallback to latest user language level or `A2`.",
      "Normalized frontend composer defaults in `src/components/GenerateTextCard.tsx` so persisted legacy level state (`unselected`/invalid) is upgraded to a valid CEFR default (`A2`) before submit.",
      "Added backend regression tests in `barkguide/reeprise/tests.py` for both fallback paths (latest known level and no-history A2 fallback).",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` to assert a legacy `unselected` local level submits as `A2` and navigates to the created text.",
    ],
  },
  {
    id: "inline-continuation-now-shows-on-fresh-texts-without-vocabulary",
    timestamp: "2026-05-22T15:52:33+02:00",
    title: "Inline continuation now appears on newly generated texts",
    summary:
      "The experimental inline continuation control now appears on fresh completed root texts even before any vocabulary entries exist, so continuation can start immediately.",
    userHighlights: [
      "Newly generated texts no longer hide “Text fortsetzen” just because vocabulary extraction is still empty.",
      "You can continue the text in place right away on the text details page.",
      "Classic chapter continuation remains unchanged.",
    ],
    technicalNotes: [
      "Simplified inline preview visibility gating in `src/routes/app/reeprise/$generatedTextId.tsx` to depend on experiment flag + root text context + complete status, without a vocabulary-count precondition.",
      "Extended `e2e/inline-vocabulary-continuation.spec.ts` with a regression case that validates visibility and continuation success when vocabulary results are empty.",
    ],
  },
  {
    id: "inline-continuation-now-streams-live-on-root-text-without-final-swap",
    timestamp: "2026-05-22T14:37:16+02:00",
    title: "Inline continuation now streams directly into the root text",
    summary:
      "Inline continuation on text details now renders incoming continuation chunks live in the current text flow, instead of waiting for final persistence and swapping content at the end.",
    userHighlights: [
      "When you use “Text fortsetzen”, continuation now appears progressively in-place.",
      "The current text no longer jumps from old content to a full final replacement at completion.",
      "Classic chapter continuation remains unchanged.",
    ],
    technicalNotes: [
      "Extended `_generate_chapter_impl` in `barkguide/reeprise/tasks.py` with `stream_to_root_text` so inline continuation can emit websocket chunks on the root text stream key.",
      "Updated `continue_inline` in `barkguide/reeprise/views.py` to call `_generate_chapter_impl(..., stream_live=True, stream_to_root_text=True)` while preserving append/persist semantics and no-chapter creation.",
      "Updated `useStreamingTextIntegrator` in `src/hooks/useStreamingTextIntegrator.ts` to normalize optional `chapter_number` payloads for root-stream events.",
      "Strengthened backend coverage in `barkguide/reeprise/tests.py` to assert inline continuation requests root-text streaming.",
    ],
  },
  {
    id: "patterns-layer-now-bridges-underline-through-phrase-spacing",
    timestamp: "2026-05-22T14:19:24+02:00",
    title: "Pattern underlines now read as one phrase-level structure",
    summary:
      "Pattern rendering now bridges inter-word spacing inside the same structure so multi-word patterns read as one continuous semantic line instead of disconnected word marks.",
    userHighlights: [
      "Multi-word patterns now feel grouped as one structure while keeping reading flow.",
      "The underline can visually continue through internal spaces of the same pattern.",
      "Pattern interaction remains calm and avoids enclosure-heavy token framing.",
    ],
    technicalNotes: [
      "Added spacing-bridge rendering in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` for symbol runs that sit between two words of the same grammar annotation.",
      "Bridge segments reuse grammar-tone underline behavior and active selection state, while remaining non-interactive (`aria-hidden`) to preserve keyboard semantics.",
      "Added regression test in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to assert bridge segment insertion (`data-grammar-pattern-bridge`).",
      "Validated with targeted Pattern-layer E2E flow (`e2e/highlighted-word-preview.spec.ts`, pattern toggle interaction case).",
    ],
  },
  {
    id: "patterns-layer-reading-state-now-avoids-word-boxing",
    timestamp: "2026-05-22T14:09:04+02:00",
    title: "Patterns inspection now avoids boxed tokens in normal reading",
    summary:
      "Pattern overlays were tuned to keep reading calm by removing heavy per-word selection boxes and reserving stronger emphasis for interaction moments.",
    userHighlights: [
      "Selected pattern words no longer appear as boxed markup during normal reading.",
      "Grammar categories still stay distinct through underline behavior and subtle rhythm.",
      "Clicking a pattern now emphasizes the structure semantically without enclosure-heavy framing.",
    ],
    technicalNotes: [
      "Removed active grammar ring boxing and hard boundary-shadow enclosure from `src/components/utils/generatedTextDetailsTextPaneUtils.tsx`.",
      "Kept shape differentiation per grammar tone and shifted active emphasis to underline intensity/opacity instead of token framing.",
      "Extended pattern activation plumbing by annotation id so full multi-word structures can share active inspection state in both prose and dialogue rendering.",
      "Updated grammar-style unit tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to validate no hard boxing and semantic active emphasis.",
    ],
  },
  {
    id: "inline-vocabulary-continuation-experiment-with-fade-preview",
    timestamp: "2026-05-22T12:01:52+02:00",
    title:
      "Experimental inline continuation now extends the same text in place",
    summary:
      "An experimental continuation variant is now available on text details: instead of creating a new chapter, it can continue the current text inline with a subtle editorial fade and calm in-context action.",
    userHighlights: [
      "You can now test an inline continuation flow that keeps you on the same text page.",
      "The continuation action appears as a subtle faded ending with “Text fortsetzen”.",
      "When successful, new content is appended directly to the current text instead of creating a new chapter.",
      "The existing classic chapter workflow remains available and unchanged.",
    ],
    technicalNotes: [
      "Added backend endpoint `POST /api/reeprise/generated_texts/:id/continue_inline/` in `barkguide/reeprise/views.py`, reusing chapter-generation prompt logic via `_generate_chapter_impl` with explicit continuation instruction and without creating `Chapter` records.",
      "Inline continuation now streams continuation chunks over the root text websocket channel before persisting, so appended content appears progressively in-place during generation.",
      "Inline continuation now uses live generation streaming (`stream_live=True`) instead of post-generation playback, so text appears progressively while generation is still running.",
      "Added frontend experiment variant component `src/components/CreateVocabularyTextInlinePreview.tsx` and mutation hook `src/hooks/useGeneratedTextInlineContinuation.ts`.",
      "Wired variant gating in `src/routes/app/reeprise/$generatedTextId.tsx` behind `show_experimental_features` while preserving the existing classic action buttons.",
      "Added regression coverage in `barkguide/reeprise/tests.py` and `barkguide-ui/e2e/inline-vocabulary-continuation.spec.ts` for append-in-place behavior, error handling, and independent variant visibility.",
    ],
  },
  {
    id: "reading-audio-now-embeds-into-metadata-with-contextual-controls",
    timestamp: "2026-05-22T11:26:18+02:00",
    title: "Reading audio now lives in metadata with contextual controls",
    summary:
      "The text-details audio experience is now embedded into the reading metadata row, with a minimal Listen action before playback and lightweight transport controls that appear only when playback is active or being adjusted.",
    userHighlights: [
      "Audio is now a quiet metadata action instead of a standalone player block.",
      "Before playback, only a compact Listen trigger is shown.",
      "During playback, pause, seek, skip back/forward, and speed controls appear inline in a restrained editorial layer.",
    ],
    technicalNotes: [
      "Refactored generated-text details audio UI in `src/routes/app/reeprise/$generatedTextId.tsx` into `AudioTrigger`, `PlaybackControls`, `PlaybackStateLayer`, and `ReadingAudioIntegration`, preserving existing playback capabilities.",
      "Extended `src/components/GeneratedTextDetailsTextPane.tsx` with metadata slot props so audio trigger and contextual controls render directly under the title metadata.",
      "Added compact transport accessibility labels in `src/assets/translations.json` (`audio_player_skip_back`, `audio_player_skip_forward`, `audio_player_speed`).",
    ],
  },
  {
    id: "patterns-layer-reading-flow-refinement-reduces-persistent-boundary-markup",
    timestamp: "2026-05-22T11:22:53+02:00",
    title: "Patterns overlay now keeps boundaries quiet during normal reading",
    summary:
      "The Patterns layer was refined to reduce permanent boundary markup so text stays calmer in default reading, while stronger structure edges appear primarily during active inspection.",
    userHighlights: [
      "Persistent bracket-like boundary signals were removed from normal reading state.",
      "Grammar categories remain distinguishable via underline behavior and subtle spacing rhythm.",
      "Selected structures still become dominant during inspection, with surrounding overlays softened.",
    ],
    technicalNotes: [
      "Adjusted shape rendering in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to remove always-on recurring bracket markers and limit boundary shadows to active inspection state.",
      "Kept semantic underline differentiation for noun, verbal, prepositional, and recurring structures while reducing idle-state visual intensity.",
      "Extended active selection plumbing by annotation id in `createTextSpans` and `GeneratedTextDetailsTextPane.tsx` so full multi-word structures share interaction emphasis.",
      "Updated regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for interaction-only boundaries and calm recurring idle state.",
    ],
  },
  {
    id: "patterns-layer-now-uses-shape-language-for-dense-structure-inspection",
    timestamp: "2026-05-22T11:18:03+02:00",
    title: "Patterns layer now uses semantic shape signals for dense passages",
    summary:
      "Pattern overlays now communicate structure through underline behavior, boundary markers, and focused inspection states, so adjacent structures stay distinguishable without adding more colors.",
    userHighlights: [
      "Noun, verb, phrase, and recurring structures now use different visual line behaviors, not just different colors.",
      "Adjacent structures are separated with explicit edge and spacing signals so dense passages stay readable.",
      "Selecting a structure now isolates it more clearly while surrounding annotations soften.",
    ],
    technicalNotes: [
      "Extended grammar highlight styling in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` with shape-specific rendering (solid, segmented, dashed, and recurring bracket signals).",
      "Added per-word structural boundary logic (pattern start/end plus adjacent left/right seam detection) and exposed those states through data attributes for stable interaction semantics.",
      "Refined inspection focus behavior in `src/components/GeneratedTextDetailsTextPane.tsx` so active structure context remains dominant while non-selected layers are softened.",
      "Added regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for boundary detection, segmented verb styling, and recurring bracket marker rendering.",
    ],
  },
  {
    id: "patterns-layer-inspect-polish-and-recurring-structure-signals",
    timestamp: "2026-05-22T11:09:48+02:00",
    title: "Patterns layer now behaves more like semantic inspect mode",
    summary:
      "The Patterns overlay was refined from a card-like UI into a lighter inspect-style system with sharper typographic hierarchy, stronger semantic underline logic, and focused selection behavior.",
    userHighlights: [
      "Pattern inspection now feels more transient and contextual, with a lighter floating panel and calmer system-like labels.",
      "Selecting a structure now subtly isolates it by reducing surrounding visual noise while keeping reading flow intact.",
      "Pattern marks now use clearer semantic underline variants, so groups, verb structures, phrase structures, and recurring structures read as different structural signals.",
    ],
    technicalNotes: [
      "Refined pattern overlay rendering and focus behavior in `src/components/GeneratedTextDetailsTextPane.tsx` and `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` (selection isolation, inspect cursor states, lighter panel shell, tighter metadata rhythm).",
      "Extended grammar style mapping with differentiated underline systems by structural tone (noun/verb/prepositional/recurring), while preserving vocabulary-highlight compatibility.",
      "Improved deterministic French parser quality in `src/utils/grammarPatternAnnotations.ts`: stricter participle heuristics, agreement-sensitive confidence tuning, and recurring-structure flagging for repeated structural families.",
      "Verified with updated unit coverage and full reading-flow E2E regression (`src/utils/grammarPatternAnnotations.test.ts`, `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx`, `e2e/highlighted-word-preview.spec.ts`).",
    ],
  },
  {
    id: "reading-layer-patterns-overlay-for-french-structure",
    timestamp: "2026-05-22T09:25:04+02:00",
    title:
      "Reading layer now includes Patterns overlay for French text structure",
    summary:
      "The text details page now includes a subtle layer control with None, Words, and Patterns so you can inspect sentence structure without losing readability.",
    userHighlights: [
      "Reading now has an optional Patterns layer that marks key French structures like article+noun groups, subject+verb groups, and preposition phrases.",
      "Patterns are shown as restrained structure marks while existing word highlights remain visible.",
      "Selecting a pattern opens a compact explanation panel with concise pattern notes.",
    ],
    technicalNotes: [
      "Added deterministic French rule parser + normalization pipeline in `src/utils/grammarPatternAnnotations.ts` with span-based annotations and confidence filtering.",
      "Extended `createTextSpans` rendering pipeline to merge vocabulary highlights and grammar pattern overlays with accessible pattern metadata and keyboard-friendly interactions.",
      "Integrated layer toggle and pattern interaction UI in `src/components/GeneratedTextDetailsTextPane.tsx`, including mobile bottom-sheet behavior and desktop popover behavior.",
      "Added regression coverage in `src/utils/grammarPatternAnnotations.test.ts` and extended `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for grammar overlay wiring and style composition.",
    ],
  },
  {
    id: "daily-generation-now-commits-headline-before-reinforcement-body",
    timestamp: "2026-05-22T09:21:49+02:00",
    title: "Daily generation now locks headline before reinforcement body",
    summary:
      "Daily texts now follow a headline-first generation path so body content stays aligned with the final headline while still enforcing reinforcement words.",
    userHighlights: [
      "Daily text body now follows the same headline direction more consistently.",
      "Reinforcement words are still enforced without allowing the body to drift away from the selected headline.",
    ],
    technicalNotes: [
      "Updated `complete_generated_text()` in `barkguide/reeprise/tasks.py` to generate and validate `committed_title` for daily texts before body generation, using daily guidance context.",
      "Body generation now receives the committed title in daily flow just like non-daily flow.",
      "Added backend regression test `test_daily_generated_text_commits_title_before_body_using_daily_guidance_context` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "daily-reinforcement-knowledge-mode-now-stays-non-narrative",
    timestamp: "2026-05-22T09:14:52+02:00",
    title: "Daily reinforcement texts now keep knowledge mode in body content",
    summary:
      "The daily + reinforcement-words generation path now preserves knowledge-style body output instead of drifting into story-like narration.",
    userHighlights: [
      "Daily knowledge texts that include reinforcement words now stay explanatory and editorial.",
      "Retry generations for missing reinforcement words no longer bias the model toward story framing.",
    ],
    technicalNotes: [
      "Added knowledge-specific reinforcement prompt text in `barkguide/reeprise/tasks.py` for `DAILY_NUDGE` generation with reinforcement words.",
      "Updated retry instruction wording from “final story” to “final text body” and reinforced text-type/style alignment.",
      "Added regression coverage in `barkguide/reeprise/tests.py` (`test_daily_knowledge_reinforcement_prompt_avoids_story_scene_framing`).",
    ],
  },
  {
    id: "daily-knowledge-prompt-now-blocks-story-scene-drift",
    timestamp: "2026-05-22T09:15:00+02:00",
    title: "Daily knowledge prompt now blocks story-scene drift",
    summary:
      "Daily knowledge text generation now has stronger anti-story guardrails so body content stays explanatory instead of narrative.",
    userHighlights: [
      "Daily knowledge texts now stay in an editorial/explanatory style more consistently.",
      "Story-like openings and motivational-adventure framing are now explicitly blocked in prompt rules.",
    ],
    technicalNotes: [
      "Updated daily knowledge guidance in `barkguide/reeprise/tasks.py` with explicit anti-narrative constraints for both topic-level guidance and DAILY PRACTICE section.",
      "Added regression test `test_daily_knowledge_prompt_builder_blocks_story_scene_framing` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "struggle-knowledge-body-now-stays-editorial-not-story",
    timestamp: "2026-05-22T08:49:18+02:00",
    title: "Struggle knowledge texts now keep editorial body style",
    summary:
      "When generating texts from tricky-word mode, knowledge-type body content now stays explanatory/editorial instead of drifting into story-scene framing.",
    userHighlights: [
      "Tricky-word generation now reads like normal knowledge content, not a story scene.",
      "Title and body style are now aligned for knowledge-mode outputs.",
    ],
    technicalNotes: [
      "Updated struggle-word prompt branch in `barkguide/reeprise/tasks.py` to add a dedicated knowledge-mode requirement: coherent explanatory text with explicit no-fiction/no-roleplay guardrail.",
      "Added backend regression test `test_prompt_builder_for_knowledge_struggle_text_enforces_editorial_mode` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "quick-translate-save-aligns-with-vocabulary-card-language-direction",
    timestamp: "2026-05-22T08:33:01+02:00",
    title:
      "Übersetzen unterwegs save now aligns with vocabulary card direction",
    summary:
      "Saved quick-translate words now follow the same language direction as other vocabulary cards, so the main card header stays in the learning language.",
    userHighlights: [
      "Saved quick-translate entries now appear with the learning language in the main header.",
      "Newly saved quick-translate words now appear immediately in vocabulary views without manual refresh.",
    ],
    technicalNotes: [
      "Switched quick-translate save actions from `/api/reeprise/translate/` to `/api/reeprise/vocabulary/agent_add/` in `src/routes/app/quick_translate.tsx`.",
      "Added `resolveQuickTranslateVocabularyPayload()` in `src/utils/quickTranslateVocabularyPayload.ts` to map word/translation fields to learning-language-first semantics.",
      "Dispatched shared vocabulary refresh signals (`VOCABULARY_ADDED_EVENT` + `VOCABULARY_ADDED_FLAG`) after save success.",
      "Added regression tests in `src/utils/quickTranslateVocabularyPayload.test.ts` for both input directions.",
    ],
  },
  {
    id: "text-details-flyout-keeps-added-date-out",
    timestamp: "2026-05-22T08:30:02+02:00",
    title: "Text details flyout no longer shows added date",
    summary:
      "The added date was removed from the word flyout and remains only on vocabulary cards in the text details list.",
    userHighlights: [
      "The quick word flyout is now cleaner and focused on word + translation preview.",
      "Added date is still visible on the sidebar vocabulary cards.",
    ],
    technicalNotes: [
      "Removed the `vocabulary_label_added_on` row from `SavedWordPreviewFlyout` in `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Kept added-date rendering in `VocabularyItem` cards in `src/routes/app/reeprise/$generatedTextId.tsx`.",
    ],
  },
  {
    id: "text-details-vocabulary-list-cards-now-show-added-date",
    timestamp: "2026-05-22T08:27:55+02:00",
    title: "Text details vocabulary list cards now show when a word was added",
    summary:
      "The vocabulary cards in the text details sidebar now include the original added date.",
    userHighlights: [
      "Each vocabulary card in the text details list now shows an “Added on” date.",
      "The created-date display is now present in both card list and preview surfaces.",
    ],
    technicalNotes: [
      "Updated `VocabularyItem` in `src/routes/app/reeprise/$generatedTextId.tsx` to render `vocabulary_label_added_on`.",
      "Reused `formatVocabularyCreatedAt()` from `src/utils/vocabularyProgress.ts` with `progress_never` fallback.",
    ],
  },
  {
    id: "text-details-word-flyout-now-shows-added-date",
    timestamp: "2026-05-22T08:24:16+02:00",
    title: "Text details word card now shows when the word was added",
    summary:
      "The vocabulary preview card in text details now includes the added date, matching other vocabulary card views.",
    userHighlights: [
      "When you open a saved word on text details, the card now shows an “Added on” date.",
      "Date display is now consistent across vocabulary progress, trainer, preview, and text-details cards.",
    ],
    technicalNotes: [
      "Updated `SavedWordPreviewFlyout` in `src/components/GeneratedTextDetailsTextPane.tsx` to render `vocabulary_label_added_on` using shared `formatVocabularyCreatedAt()`.",
      "Reused existing translation and fallback behavior (`progress_never`) for missing/invalid timestamps.",
    ],
  },
  {
    id: "vocabulary-cards-now-show-added-date",
    timestamp: "2026-05-21T20:37:03+02:00",
    title: "Vocabulary cards now show when a word was added",
    summary:
      "Vocabulary cards now include the original added date so you can see when each word entered your collection.",
    userHighlights: [
      "Each vocabulary card now shows an “Added on” date.",
      "The added date is visible in vocabulary progress, vocabulary preview, and training mode cards.",
    ],
    technicalNotes: [
      "Extended frontend `VocabularyDto` type in `src/hooks/usePaginatedVocabularyList.ts` to include `created_at` from the API payload.",
      "Added shared date formatter `formatVocabularyCreatedAt()` in `src/utils/vocabularyProgress.ts` and used it across vocabulary card renderers.",
      "Updated card UIs in `src/routes/app/vocabulary_progress.tsx`, `src/components/VocabularyPreviewCard.tsx`, and `src/routes/app/vocabulary_training_mode.tsx` to render the added-date label.",
      "Added translation key `vocabulary_label_added_on` and a utility regression test in `src/utils/vocabularyProgress.test.ts`.",
    ],
  },
  {
    id: "quick-translate-save-now-uses-explicit-source-language-and-shows-errors",
    timestamp: "2026-05-21T20:13:06+02:00",
    title: "Übersetzen unterwegs save now uses explicit source language",
    summary:
      "Quick-translate save actions now send explicit source-language context and show an inline error if saving fails.",
    userHighlights: [
      "Saved items now land more reliably in vocabulary with the expected language direction.",
      "If saving fails, the page now shows a visible error instead of failing silently.",
    ],
    technicalNotes: [
      "Stored resolved source/target language from translation response in `src/routes/app/quick_translate.tsx`.",
      "Updated save requests (`saveCurrent` and `saveSelectedPhrase`) to include explicit `source_lang`.",
      "Added inline error state rendering for failed save responses.",
    ],
  },
  {
    id: "quick-translate-now-renders-first-result-without-waiting-for-fallback-line",
    timestamp: "2026-05-21T20:07:47+02:00",
    title: "Übersetzen unterwegs now shows translation faster",
    summary:
      "Quick translate now renders the primary translation immediately and loads the mother-tongue fallback example line asynchronously.",
    userHighlights: [
      "The first translation appears faster after pressing Enter/send.",
      "The fallback line still appears, but no longer blocks the initial result.",
    ],
    technicalNotes: [
      "Updated `submitTranslation` in `src/routes/app/quick_translate.tsx` to stop blocking on the secondary mother-tongue translate request.",
      "Kept stale-submit guards in place so background fallback updates only apply to the active request.",
    ],
  },
  {
    id: "quick-translate-clear-reset-no-longer-blocks-next-enter-submit",
    timestamp: "2026-05-21T20:04:58+02:00",
    title:
      "Übersetzen unterwegs clear-reset no longer blocks the next Enter submit",
    summary:
      "Reset logic was corrected so old output still clears on edit, while the next Enter translation round continues to work normally.",
    userHighlights: [
      "You can now delete old text, type new text, and press Enter to translate again.",
      "Clearing behavior remains in place without breaking submit.",
    ],
    technicalNotes: [
      "Adjusted input-change reset behavior in `src/routes/app/quick_translate.tsx` so stale-submit invalidation only triggers when input is fully empty.",
    ],
  },
  {
    id: "struggle-text-mode-now-stays-fully-internal",
    timestamp: "2026-05-21T20:04:21+02:00",
    title: "Struggle-text mode now stays fully internal",
    summary:
      "Struggle-word generation now runs as a background mode without exposing struggle marker topics in user-facing card content.",
    userHighlights: [
      "Texts generated from tricky-word mode now appear as normal knowledge readings.",
      "The app no longer relies on visible struggle marker topic phrases.",
      "Daily card visibility checks still correctly detect same-day struggle texts.",
    ],
    technicalNotes: [
      "Added hidden create payload flag `is_struggle_practice` in generated-text create flow (`useGeneratedTextCreate` + backend serializer/view) so origin is set explicitly without topic-prefix parsing.",
      "Switched struggle/daily detection logic in backend and frontend utilities to rely on `origin` instead of topic-string markers.",
      "Updated struggle creation in `GenerateTextCard.tsx` to send neutral topic copy (`struggle_words_topic_generic`) plus hidden struggle flag.",
      "Adjusted regression tests in `barkguide/reeprise/tests.py` and `src/utils/hasTodayStrugglePracticeText.test.ts` for origin-only behavior.",
    ],
  },
  {
    id: "quick-translate-input-change-now-clears-old-result-and-cancels-stale-submit",
    timestamp: "2026-05-21T20:03:17+02:00",
    title: "Übersetzen unterwegs now clears old result on input change",
    summary:
      "Changing or clearing the input now immediately removes prior translation/output state and cancels stale in-flight submit updates.",
    userHighlights: [
      "Old translation no longer remains visible after deleting or replacing input.",
      "After clearing text, entering a new word and pressing Enter starts a clean new translation round.",
    ],
    technicalNotes: [
      "Extended the input-change effect in `src/routes/app/quick_translate.tsx` to clear translation/example state, invalidate previous submit ids, and reset translating state.",
    ],
  },
  {
    id: "quick-translate-legacy-mobile-top-anchor-and-stable-output-offset",
    timestamp: "2026-05-21T20:00:26+02:00",
    title:
      "Übersetzen unterwegs legacy layout now stays top-anchored on mobile",
    summary:
      "In legacy quick-translate layout, mobile now anchors input at the top, avoids center-shift after submit, and offsets output to the right of the input left edge.",
    userHighlights: [
      "Input starts at the top on mobile instead of centered.",
      "Submit no longer causes vertical re-centering/jump of the input area.",
      "Output content now starts slightly to the right of the input’s left boundary.",
    ],
    technicalNotes: [
      "Updated legacy wrapper alignment in `src/routes/app/quick_translate.tsx` from `items-center` to `items-start` with explicit top padding.",
      "Added left offset (`ml-4`) for translating/output blocks and grouped output content in a dedicated offset container.",
    ],
  },
  {
    id: "quick-translate-editorial-shell-rolled-back-to-legacy-layout",
    timestamp: "2026-05-21T19:15:01+02:00",
    title: "Übersetzen unterwegs editorial shell rolled back",
    summary:
      "The quick-translate route has been switched back to the legacy layout path, reverting the editorial shell presentation.",
    userHighlights: [
      "Top command-strip/split-shell editorial presentation is no longer active.",
      "Quick-translate page now renders the prior legacy-style layout again.",
    ],
    technicalNotes: [
      "Set `QUICK_TRANSLATE_LAYOUT_VARIANT` to `legacy` in `src/routes/app/quick_translate.tsx`.",
      "Behavior logic remains unchanged; rollback is layout-only.",
    ],
  },
  {
    id: "quick-translate-editorial-shell-now-visibly-applied-on-route",
    timestamp: "2026-05-21T19:13:20+02:00",
    title: "Übersetzen unterwegs now visibly uses an editorial route shell",
    summary:
      "The quick-translate route now applies a clearly visible editorial shell with top command chips and a split rail/content structure.",
    userHighlights: [
      "Top command-strip chips are now visible on the quick-translate page.",
      "Page now uses a split layout with a compact left rail and main translation column.",
      "Core translate/save behavior remains intact in the redesigned shell.",
    ],
    technicalNotes: [
      "Reworked editorial variant render structure in `src/routes/app/quick_translate.tsx` with a command strip and two-column shell while reusing existing translation/save logic.",
      "Legacy variant remains available via the existing layout switch.",
    ],
  },
  {
    id: "quick-translate-layout-variants-now-are-visibly-distinct",
    timestamp: "2026-05-21T19:11:05+02:00",
    title: "Übersetzen unterwegs layout variants now look clearly different",
    summary:
      "The rollback layout and editorial layout now have clearly distinct visual shells so variant switching is obvious in frontend.",
    userHighlights: [
      "Editorial variant keeps the top-aligned minimal shell with floating send action.",
      "Legacy variant now uses a centered boxed composer with inline send action.",
    ],
    technicalNotes: [
      "Added a dedicated `legacyMainContent` render branch in `src/routes/app/quick_translate.tsx`.",
      "Variant switch now controls both spacing shell and send-button placement for clear visual difference.",
    ],
  },
  {
    id: "quick-translate-layout-now-has-one-switch-rollback-path",
    timestamp: "2026-05-21T19:09:08+02:00",
    title: "Übersetzen unterwegs layout now has a one-switch rollback path",
    summary:
      "The quick-translate route now includes an explicit layout variant switch so the new design can be rolled back instantly without touching translation behavior logic.",
    userHighlights: [
      "Current design remains active.",
      "Rollback to legacy layout is available via a single code switch.",
    ],
    technicalNotes: [
      "Added `QUICK_TRANSLATE_LAYOUT_VARIANT` (`editorial` | `legacy`) in `src/routes/app/quick_translate.tsx`.",
      "Split render shell into variant-specific wrappers while keeping shared translation/save behavior intact.",
    ],
  },
  {
    id: "quick-translate-example-highlights-now-respect-input-token-counts",
    timestamp: "2026-05-21T18:59:42+02:00",
    title: "Übersetzen unterwegs highlights now respect input token counts",
    summary:
      "Example highlighting now preserves user-intended tokens like `le`, while limiting highlights to the number of times those tokens appear in the translated phrase.",
    userHighlights: [
      "If your phrase includes `le`, it will be highlighted in the example.",
      "Repeated function words in the sentence are no longer all highlighted automatically.",
    ],
    technicalNotes: [
      "Replaced set-based highlight matching in `src/routes/app/quick_translate.tsx` with count-based token matching (`Map<string, number>`).",
      "Rendering now decrements remaining token counts while scanning the example sentence, so highlighting is bounded by source phrase frequency.",
    ],
  },
  {
    id: "quick-translate-example-highlights-now-skip-function-words",
    timestamp: "2026-05-21T18:58:12+02:00",
    title: "Übersetzen unterwegs highlights now skip short function words",
    summary:
      "Example-sentence highlighting now ignores short and grammatical function words such as `le`, `de`, `der`, and `the`.",
    userHighlights: [
      "Highlights now focus on meaningful content words instead of articles/connectors.",
      "Examples are easier to scan because low-value tokens are no longer emphasized.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/quick_translate.tsx` highlight-word derivation to filter out short tokens (`<=2`) and a language-agnostic function-word set before applying sentence highlights.",
    ],
  },
  {
    id: "quick-translate-consecutive-submissions-now-use-race-safe-state-updates",
    timestamp: "2026-05-21T18:56:58+02:00",
    title: "Übersetzen unterwegs now handles consecutive submissions reliably",
    summary:
      "Submitting a new translation after clearing/changing text now resets state immediately and ignores stale async responses from earlier requests.",
    userHighlights: [
      "Consecutive translations no longer break after deleting previous input.",
      "Old pending responses can no longer overwrite newer translation results.",
      "Selection/example state resets cleanly on each new submit.",
    ],
    technicalNotes: [
      "Added submit request sequencing in `src/routes/app/quick_translate.tsx` via a latest-submit ref to guard async state updates.",
      "On new submit, translation/example/selection state is reset before network calls, and all response handlers are gated to the active submit id.",
    ],
  },
  {
    id: "quick-translate-example-lines-now-always-visible-with-translation-highlights",
    timestamp: "2026-05-21T18:53:57+02:00",
    title:
      "Übersetzen unterwegs now keeps example lines visible with translation-word highlights",
    summary:
      "Example lines are now shown consistently and learning-language example text highlights translated words in a distinct color.",
    userHighlights: [
      "Example lines no longer disappear for longer inputs.",
      "Words from the translated text are highlighted in the learning-language example line.",
      "Mother-tongue example line remains available as a direct comprehension fallback.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/quick_translate.tsx` to fall back to translated output as the learning-language example when no explicit example sentence is returned.",
      "Added translation-word-based highlighting for example sentence rendering and kept mother-tongue example generation via a follow-up translate call.",
    ],
  },
  {
    id: "quick-translate-example-lines-now-plain-and-direct-click-selection",
    timestamp: "2026-05-21T18:50:12+02:00",
    title:
      "Übersetzen unterwegs example lines are now plain and directly selectable",
    summary:
      "Example sentence lines now render without `Beispiel auf …` style headings, and long-input vocabulary selection happens directly in the translation text.",
    userHighlights: [
      "Example lines now show plain sentence content without language-title prefixes.",
      "Long-input selection no longer uses token pills; you click words directly in the translated sentence.",
      "Input-overlap words in the learning-language example sentence are now highlighted for quicker orientation.",
    ],
    technicalNotes: [
      "Removed sentence-title rendering from `src/routes/app/quick_translate.tsx` and kept the two-sentence flow as plain lines.",
      "Replaced pill-based token selection with inline tokenized sentence rendering and clickable word selection by token index.",
      "Added overlap-highlighting logic for words appearing in both user input and example sentence.",
    ],
  },
  {
    id: "quick-translate-clickable-translation-words-now-keep-natural-spacing",
    timestamp: "2026-05-21T17:28:44+02:00",
    title:
      "Übersetzen unterwegs clickable translation words now keep natural spacing",
    summary:
      "Clickable word tokens in the translation line now preserve natural word spacing without added horizontal padding.",
    userHighlights: [
      "Word spacing in translated lines now looks tighter and more natural.",
      "Direct click-to-select behavior remains unchanged.",
    ],
    technicalNotes: [
      "Removed inline token button horizontal padding in `src/routes/app/quick_translate.tsx` (`px-0.5` -> `p-0`).",
    ],
  },
  {
    id: "quick-translate-direct-word-selection-from-translation-text",
    timestamp: "2026-05-21T17:26:46+02:00",
    title:
      "Übersetzen unterwegs now supports direct word selection in translation text",
    summary:
      "In long-input mode, words can now be selected directly inside the translation line instead of using separate token pills.",
    userHighlights: [
      "Translation words are now directly clickable for selection.",
      "The separate selection-pill row was removed.",
      "Input and translation continue using the same ABeeZee typography style.",
    ],
    technicalNotes: [
      "Replaced pill-based token UI in `src/routes/app/quick_translate.tsx` with inline tokenized translation rendering and clickable token selection.",
      "Selection state now tracks token indexes to preserve phrase order and support repeated words correctly.",
    ],
  },
  {
    id: "quick-translate-input-now-disables-browser-spellcheck-underlines",
    timestamp: "2026-05-21T17:24:07+02:00",
    title:
      "Übersetzen unterwegs input now disables browser spellcheck underlines",
    summary:
      "The quick-translate writing field now disables browser spellcheck and autocorrection so multilingual text is not flagged with red underlines.",
    userHighlights: [
      "German or mixed-language input no longer shows distracting red misspelling marks by default.",
      "Typing behavior stays focused on translation input rather than browser correction.",
    ],
    technicalNotes: [
      'Added `spellCheck={false}`, `autoCorrect="off"`, and `autoCapitalize="off"` to the quick-translate textarea in `src/routes/app/quick_translate.tsx`.',
    ],
  },
  {
    id: "quick-translate-send-button-now-fixed-to-page-bottom-right",
    timestamp: "2026-05-21T17:22:53+02:00",
    title:
      "Übersetzen unterwegs send button now stays fixed at page bottom-right",
    summary:
      "The send action is no longer inside the growing input region and now stays fixed at the page bottom-right.",
    userHighlights: [
      "Send button no longer moves down while text wraps and grows.",
      "Button position stays stable at the lower-right corner of the page.",
    ],
    technicalNotes: [
      "Moved send button out of the textarea container in `src/routes/app/quick_translate.tsx`.",
      "Applied fixed positioning (`right-6 bottom-6`) and removed textarea padding that was previously reserved for inline button overlap.",
    ],
  },
  {
    id: "quick-translate-send-button-now-absolutely-anchored-bottom-right",
    timestamp: "2026-05-21T17:22:09+02:00",
    title:
      "Übersetzen unterwegs send button now uses true bottom-right anchoring",
    summary:
      "The send button is now absolutely anchored to the bottom-right corner of the input area.",
    userHighlights: [
      "Send control no longer appears at the top-right.",
      "Button stays fixed at the lower-right edge while input text wraps.",
    ],
    technicalNotes: [
      "Changed input wrapper to `relative` and send button to `absolute right-0 bottom-0` in `src/routes/app/quick_translate.tsx`.",
      "Added textarea right/bottom padding so text does not overlap the button.",
    ],
  },
  {
    id: "quick-translate-send-button-now-aligns-bottom-right",
    timestamp: "2026-05-21T17:21:21+02:00",
    title: "Übersetzen unterwegs send button now aligns bottom-right",
    summary:
      "In the minimal quick-translate layout, the send action now aligns to the bottom-right edge of the writing area.",
    userHighlights: [
      "Send button now sits at the bottom-right of the input zone.",
      "Alignment stays stable as the textarea grows to multiple lines.",
    ],
    technicalNotes: [
      "Changed the input row alignment to `items-end` in `src/routes/app/quick_translate.tsx` so the send button anchors to the textarea bottom edge.",
    ],
  },
  {
    id: "quick-translate-minimal-typography-now-uses-looser-line-spacing",
    timestamp: "2026-05-21T17:21:04+02:00",
    title: "Übersetzen unterwegs now uses more line spacing in minimal view",
    summary:
      "Line spacing in the minimal quick-translate writing and main output text was increased for easier reading.",
    userHighlights: [
      "Input lines now have more vertical breathing room.",
      "Translation and loading lines are more readable.",
    ],
    technicalNotes: [
      "Updated line-height classes in `src/routes/app/quick_translate.tsx` from `leading-[1.35]` to `leading-[1.6]` on input and primary output/loading text.",
    ],
  },
  {
    id: "quick-translate-minimal-input-now-uses-abeezee-font",
    timestamp: "2026-05-21T17:20:22+02:00",
    title: "Übersetzen unterwegs minimal input now uses ABeeZee",
    summary:
      "The minimal quick-translate writing and result text now use ABeeZee instead of monospace styling.",
    userHighlights: [
      "Input typography now matches the requested ABeeZee style.",
      "Translation output and loading text follow the same font for consistency.",
    ],
    technicalNotes: [
      "Replaced `font-mono` with `font-ABee-zee` in `src/routes/app/quick_translate.tsx` for input, loading text, and translation output.",
    ],
  },
  {
    id: "quick-translate-minimal-input-now-wraps-on-mobile",
    timestamp: "2026-05-21T17:18:56+02:00",
    title:
      "Übersetzen unterwegs input now wraps instead of overflowing on mobile",
    summary:
      "The minimal quick-translate writing field now wraps entered text and auto-grows, preventing horizontal overflow on mobile screens.",
    userHighlights: [
      "Long text now breaks into new lines instead of running outside the screen.",
      "Input area grows with content while staying in the same minimal layout.",
    ],
    technicalNotes: [
      "Replaced the single-line input with a wrapping textarea in `src/routes/app/quick_translate.tsx`.",
      "Added auto-height adjustment logic and capped textarea height with vertical overflow fallback.",
      "Enter still submits translation (`Shift+Enter` allows line breaks).",
    ],
  },
  {
    id: "quick-translate-minimal-layout-now-restores-save-and-example-flows",
    timestamp: "2026-05-21T17:18:40+02:00",
    title:
      "Übersetzen unterwegs minimal layout now restores save and example flows",
    summary:
      "The minimal quick-translate layout now includes the previously available vocabulary-save and example-sentence behavior.",
    userHighlights: [
      "Example sentences are shown again after translation.",
      "Save-to-vocabulary is back for short input.",
      "For longer input, translated tokens can be selected and saved as a focused phrase.",
    ],
    technicalNotes: [
      "Extended `src/routes/app/quick_translate.tsx` to render translation examples, saved feedback, and save actions in the minimal page layout.",
      "Reused `/api/reeprise/translate/` save paths for short input and translated-token phrase saving in long-input mode.",
    ],
  },
  {
    id: "quick-translate-minimal-page-send-icon-restored",
    timestamp: "2026-05-21T17:16:56+02:00",
    title: "Übersetzen unterwegs minimal page now includes send icon again",
    summary:
      "The minimal quick-translate page now shows the previous send action icon next to the input.",
    userHighlights: [
      "You can submit with Enter or by tapping the send icon.",
      "Send icon styling matches the prior quick-translate action control.",
    ],
    technicalNotes: [
      "Added `ArrowUpIcon` send button in `src/routes/app/quick_translate.tsx` and wired it to the same submit handler used by Enter.",
      "Button stays disabled for empty input and while a request is in progress.",
    ],
  },
  {
    id: "quick-translate-minimal-page-now-submits-on-enter",
    timestamp: "2026-05-21T17:16:17+02:00",
    title: "Übersetzen unterwegs now submits translation on Enter",
    summary:
      "On the minimal quick-translate page, pressing Enter now triggers translation and shows the result inline.",
    userHighlights: [
      "Pressing Enter now starts translation immediately.",
      "Translation appears directly below the input line.",
      "No extra button click is required.",
    ],
    technicalNotes: [
      "Wired Enter key handling in `src/routes/app/quick_translate.tsx` to call `/api/reeprise/translate/` with `save: false`.",
      "Added inline minimal loading and output rendering while keeping the stripped page layout.",
    ],
  },
  {
    id: "quick-translate-minimal-input-font-size-reduced",
    timestamp: "2026-05-21T17:15:18+02:00",
    title: "Übersetzen unterwegs minimal input now uses a smaller font size",
    summary:
      "The minimal quick-translate writing field now uses a smaller text size for a lighter visual footprint.",
    userHighlights: [
      "Input text size is now smaller while staying readable.",
      "Mobile-safe minimum font sizing is preserved to avoid focus zoom behavior.",
    ],
    technicalNotes: [
      "Adjusted quick-translate minimal input typography in `src/routes/app/quick_translate.tsx` from `text-xl/sm:text-2xl` to `text-base/sm:text-lg`.",
    ],
  },
  {
    id: "quick-translate-minimal-input-now-monospace-and-top-aligned",
    timestamp: "2026-05-21T17:14:57+02:00",
    title:
      "Übersetzen unterwegs minimal input now uses monospace and top alignment",
    summary:
      "The minimal quick-translate input now uses monospace typography, starts near the top of the page, and keeps caret/text alignment tighter.",
    userHighlights: [
      "Input now uses a monospace style.",
      "The writing field is positioned near the top instead of vertically centered.",
      "Caret and text baseline alignment is visually cleaner.",
    ],
    technicalNotes: [
      "Updated layout alignment and top spacing in `src/routes/app/quick_translate.tsx`.",
      "Applied `font-mono` and explicit line-height tuning on the input for caret/text alignment.",
    ],
  },
  {
    id: "quick-translate-custom-caret-removed-system-caret-only",
    timestamp: "2026-05-21T17:14:05+02:00",
    title: "Übersetzen unterwegs now uses only the system text caret",
    summary:
      "The custom visual caret line was removed from the minimal quick-translate page, leaving only the native system caret.",
    userHighlights: [
      "Only the standard system insertion cursor is shown while typing.",
      "The extra custom blinking line is gone.",
    ],
    technicalNotes: [
      "Removed the custom caret element from `src/routes/app/quick_translate.tsx`.",
      "Removed unused custom caret animation styles from `src/index.css`.",
    ],
  },
  {
    id: "quick-translate-minimal-input-now-autofocuses-on-page-open",
    timestamp: "2026-05-21T17:12:41+02:00",
    title: "Übersetzen unterwegs input now autofocuses on page open",
    summary:
      "The minimal quick-translate page now lets you type immediately on entry without an extra click.",
    userHighlights: [
      "Typing can start as soon as the page opens.",
      "Click-to-focus still works as usual.",
    ],
    technicalNotes: [
      "Replaced static placeholder text with an actual text input in `src/routes/app/quick_translate.tsx`.",
      "Added autofocus behavior using both `autoFocus` and mount-time ref focus.",
    ],
  },
  {
    id: "quick-translate-minimal-cursor-now-blinks-fully-on-off",
    timestamp: "2026-05-21T17:11:59+02:00",
    title: "Übersetzen unterwegs cursor now blinks fully on and off",
    summary:
      "The minimal quick-translate cursor now alternates between fully visible and fully invisible states.",
    userHighlights: [
      "Cursor animation now behaves like a true typing blink.",
      "It no longer uses only brightness pulsing.",
    ],
    technicalNotes: [
      "Replaced pulse animation class with `quick-translate-cursor-blink` in `src/routes/app/quick_translate.tsx`.",
      "Added a dedicated on/off opacity keyframe animation in `src/index.css`.",
    ],
  },
  {
    id: "quick-translate-page-now-uses-minimal-cursor-placeholder-layout",
    timestamp: "2026-05-21T17:10:50+02:00",
    title: "Übersetzen unterwegs page now uses a minimal cursor layout",
    summary:
      "The quick-translate page now renders as an empty surface with only a pulsing cursor line and a short placeholder prompt.",
    userHighlights: [
      "All boxes, cards, and heading blocks were removed from the page.",
      "The page now shows only a vertical pulsing cursor and prompt text.",
    ],
    technicalNotes: [
      "Replaced the route content in `src/routes/app/quick_translate.tsx` with a minimal layout and removed `QuickTranslateCard` usage from that page.",
      "Added `quick_translate_minimal_placeholder` in `src/assets/translations.json` for localized placeholder copy.",
    ],
  },
  {
    id: "quick-translate-topbar-entry-now-uses-pen-icon",
    timestamp: "2026-05-21T17:08:26+02:00",
    title: "Übersetzen unterwegs topbar entry now uses a pen icon",
    summary:
      "The quick-translate top-navbar trigger now uses a pen icon instead of the walking emoji.",
    userHighlights: [
      "Topbar quick-translate access now appears as a pen icon.",
      "Behavior and placement stay the same.",
    ],
    technicalNotes: [
      "Replaced the emoji glyph with `PencilSquareIcon` in `src/routes/app/route.tsx`.",
      "Updated the link title to use the translated quick-translate label.",
    ],
  },
  {
    id: "quick-translate-access-moved-to-top-navbar",
    timestamp: "2026-05-21T16:59:46+02:00",
    title: "Übersetzen unterwegs access moved to the top navbar",
    summary:
      "Quick translate is now accessed from the top navbar via a walking emoji button.",
    userHighlights: [
      "The `🚶` quick-translate trigger is now in the top navbar.",
      "Quick-translate entry points were removed from side navigation and main-page Tools blocks.",
    ],
    technicalNotes: [
      "Added a dedicated topbar `Link` (`quick-translate-topbar-link`) in `src/routes/app/route.tsx`.",
      "Removed quick-translate nav link block from side navigation in `src/routes/app/route.tsx`.",
      "Removed quick-translate Tools links from `src/routes/app/reeprise/index.tsx`.",
    ],
  },
  {
    id: "quick-translate-main-page-entry-now-uses-walking-emoji-only",
    timestamp: "2026-05-21T16:58:52+02:00",
    title: "Übersetzen unterwegs main-page entry now uses walking emoji only",
    summary:
      "The quick-translate entry on the main page Tools blocks now uses a walking emoji without visible text.",
    userHighlights: [
      "Both mobile and desktop main-page Tools entries now show only `🚶`.",
      "No visible text label is shown for quick translate in those cards.",
    ],
    technicalNotes: [
      "Updated both `/app/reeprise` Tools links in `src/routes/app/reeprise/index.tsx` to emoji-only UI with preserved `aria-label` accessibility.",
      "No route or API changes.",
    ],
  },
  {
    id: "quick-translate-nav-entry-now-uses-walking-emoji-only",
    timestamp: "2026-05-21T16:57:45+02:00",
    title: "Übersetzen unterwegs nav entry now uses walking emoji only",
    summary:
      "The quick-translate navigation entry is now shown as a walking emoji without visible text.",
    userHighlights: [
      "Quick translate is now accessed via a `🚶` nav entry.",
      "No visible text label is shown for this entry.",
    ],
    technicalNotes: [
      "Replaced the quick-translate side-nav icon+label block in `src/routes/app/route.tsx` with an emoji-only trigger while keeping `data-testid` and accessibility label.",
      "Removed the unused `WrenchScrewdriverIcon` import.",
    ],
  },
  {
    id: "quick-translate-token-selection-keeps-apostrophe-words-intact",
    timestamp: "2026-05-21T16:56:10+02:00",
    title:
      "Übersetzen unterwegs token selection now keeps apostrophe words intact",
    summary:
      "Selectable translation tokens now keep apostrophe-based words such as `j'attends` together instead of splitting them into separate chips.",
    userHighlights: [
      "French contractions like `j'attends` now appear as one selectable token.",
      "Word selection for saving vocabulary is more accurate for apostrophe-based forms.",
    ],
    technicalNotes: [
      "Updated translated-token extraction in `src/components/QuickTranslateCard.tsx` to use a Unicode-aware pattern that preserves internal apostrophes (`'` and `’`).",
      "No API behavior changes.",
    ],
  },
  {
    id: "quick-translate-header-direction-line-removed",
    timestamp: "2026-05-21T16:54:29+02:00",
    title: "Übersetzen unterwegs header direction line removed",
    summary:
      "The secondary direction text line under the quick-translate title was removed entirely.",
    userHighlights: [
      "The header now shows only the `Übersetzen unterwegs` title.",
      "No source/target direction line appears under the title anymore.",
    ],
    technicalNotes: [
      "Removed the header direction text element and its local computed label from `src/components/QuickTranslateCard.tsx`.",
      "No translation keys or API behavior changed.",
    ],
  },
  {
    id: "quick-translate-auto-direction-label-now-shows-target-only",
    timestamp: "2026-05-21T16:54:13+02:00",
    title: "Übersetzen unterwegs auto direction now shows target only",
    summary:
      "When source is auto-detected, quick-translate direction labels now show only the target language name.",
    userHighlights: [
      "Phrases like `Nach Französisch übersetzen` were removed from visible direction labels.",
      "Auto direction now appears as a simple target label such as `Französisch`.",
    ],
    technicalNotes: [
      'Updated direction label rendering in `src/components/QuickTranslateCard.tsx` for `resolvedSourceLanguage === "AUTO"` to display target language only.',
      "No API or translation key changes.",
    ],
  },
  {
    id: "quick-translate-direction-label-no-longer-shows-auto-source",
    timestamp: "2026-05-21T16:53:25+02:00",
    title:
      "Übersetzen unterwegs direction label no longer shows Auto as source",
    summary:
      "The quick-translate direction label now avoids the `Auto …` source wording and shows a direct target action instead.",
    userHighlights: [
      "You no longer see labels like `Auto nach …` in the quick-translate header or direction badge.",
      "When source is auto-detected, wording now focuses on the target language action.",
    ],
    technicalNotes: [
      "Updated direction-label rendering in `src/components/QuickTranslateCard.tsx` to use `quick_translate_action_to_language` when `resolvedSourceLanguage` is `AUTO`.",
      "No API or translation-key changes were required.",
    ],
  },
  {
    id: "quick-translate-header-now-shows-title-without-leading-icon",
    timestamp: "2026-05-21T16:53:01+02:00",
    title: "Übersetzen unterwegs header now shows title without leading icon",
    summary:
      "The leading icon in the quick-translate card header was removed so the title appears cleaner and more direct.",
    userHighlights: [
      "Übersetzen unterwegs now appears without an icon in front of the title.",
      "Header layout is simpler with no behavior changes.",
    ],
    technicalNotes: [
      "Removed `LanguageIcon` usage and import from `src/components/QuickTranslateCard.tsx`.",
      "No translation keys or API behavior changed.",
    ],
  },
  {
    id: "quick-translate-long-input-now-supports-multi-word-save-selection",
    timestamp: "2026-05-21T16:52:32+02:00",
    title: "Übersetzen unterwegs now supports multi-word save selection",
    summary:
      "For long input, you can now select multiple translated words together and save them as one vocabulary entry.",
    userHighlights: [
      "You can now select multiple words from the translated output instead of a single word only.",
      "The save action updates to the exact selected phrase.",
      "Selection is limited to up to 4 words so saved entries stay focused.",
    ],
    technicalNotes: [
      "Changed long-input selection state in `src/components/QuickTranslateCard.tsx` from single-token to multi-token selection.",
      "Save payload now sends the selected translated phrase assembled in translation order.",
      "Updated `quick_translate_pick_word_hint` in `src/assets/translations.json` and adjusted `e2e/quick-translate-language-space.spec.ts` for two-word selection save coverage.",
    ],
  },
  {
    id: "quick-translate-long-input-now-supports-select-and-save-word",
    timestamp: "2026-05-21T16:49:54+02:00",
    title:
      "Übersetzen unterwegs now lets you save a selected word from long input",
    summary:
      "For longer input, you can now select a single word from the translated output and save it to vocabulary directly inside the quick-translate card.",
    userHighlights: [
      "Long inputs no longer stop at a disabled save action.",
      "You can tap a word from the translation and save that word as vocabulary.",
      "The saved confirmation stays visible in the same card.",
    ],
    technicalNotes: [
      "Extended `src/components/QuickTranslateCard.tsx` with translated-word token selection and a dedicated save path for selected words in long-input mode.",
      "Added translation keys `quick_translate_pick_word_hint` and `quick_translate_save_selected_word_action` in `src/assets/translations.json`.",
      "Updated `e2e/quick-translate-language-space.spec.ts` to verify long-input word selection and successful save behavior.",
    ],
  },
  {
    id: "quick-translate-save-limit-now-shown-in-disabled-action-label",
    timestamp: "2026-05-21T16:46:56+02:00",
    title: "Übersetzen unterwegs now shows save limit in the action label",
    summary:
      "For long inputs, the disabled vocabulary-save action now includes the word limit directly in the button label.",
    userHighlights: [
      "The save action stays visible for long inputs and explains the limit in place.",
      "The previous extra helper line was removed to keep the card cleaner.",
      "Short-input save behavior is unchanged.",
    ],
    technicalNotes: [
      "Updated the long-input disabled save state in `src/components/QuickTranslateCard.tsx` to use a dedicated limited-action label.",
      "Replaced `quick_translate_save_limit_hint` with `quick_translate_save_action_limited` in `src/assets/translations.json`.",
      "Adjusted `e2e/quick-translate-language-space.spec.ts` to assert the new disabled action label.",
    ],
  },
  {
    id: "quick-translate-now-shows-save-limit-for-long-input",
    timestamp: "2026-05-21T16:43:19+02:00",
    title: "Übersetzen unterwegs now shows the vocabulary-save limit clearly",
    summary:
      "When a translation input is too long for vocabulary save, the card now shows a clear disabled save state with a short limit note.",
    userHighlights: [
      "For longer inputs, the save action stays visible but disabled instead of disappearing.",
      "A concise note now explains that saving is available for up to 4 words.",
      "Short-input save behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated `src/components/QuickTranslateCard.tsx` to render a disabled save button and limit hint when input exceeds four words.",
      "Added `quick_translate_save_limit_hint` to `src/assets/translations.json` (German and English).",
      "Extended `e2e/quick-translate-language-space.spec.ts` with coverage for the disabled save state and limit hint text.",
    ],
  },
  {
    id: "quick-translate-subtitle-now-uses-shorter-focus-copy",
    timestamp: "2026-05-21T16:42:10+02:00",
    title: "Übersetzen unterwegs subtitle now uses shorter focus copy",
    summary:
      "The quick-translate subtitle was shortened to keep the page intro tighter and task-focused.",
    userHighlights: [
      "The subtitle now only states the core action: translating short inputs.",
      "Intro copy is now more concise and easier to scan.",
    ],
    technicalNotes: [
      "Updated `quick_translate_page_subtitle` in `src/assets/translations.json` for German and English.",
      "No UI behavior changes.",
    ],
  },
  {
    id: "quick-translate-ux-writing-now-matches-editorial-product-voice",
    timestamp: "2026-05-21T16:39:15+02:00",
    title: "Übersetzen unterwegs wording now matches product voice",
    summary:
      "Quick-translate copy is now clearer and more neutral, with wording aligned to the app's calm editorial style.",
    userHighlights: [
      "Ambiguous-input guidance is now more precise and easier to understand.",
      "Page intro text now focuses directly on the task without extra phrasing.",
      "English/German sentence labels are now phrased more consistently.",
    ],
    technicalNotes: [
      "Updated quick-translate translation keys in `src/assets/translations.json` (`quick_translate_no_match_hint`, `quick_translate_example_source_label`, `quick_translate_page_eyebrow`, `quick_translate_page_subtitle`).",
      "No behavior or API contract changes; copy-only update.",
    ],
  },
  {
    id: "text-details-punctuation-now-stays-with-previous-word",
    timestamp: "2026-05-21T16:34:57+02:00",
    title: "Text punctuation now stays attached at line wraps",
    summary:
      "On text details and shared text rendering paths, punctuation marks are now kept attached to the previous word so periods no longer appear alone at the start of a new line.",
    userHighlights: [
      "A period now stays visually attached to the previous word when a line wraps.",
      "Detached punctuation from AI spacing artifacts is still normalized.",
      "Reading flow looks more natural across text detail content.",
    ],
    technicalNotes: [
      "Updated shared symbol-run normalization in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to prepend a Unicode word-joiner before punctuation at run start.",
      "Kept existing locale-aware spacing normalization (including French narrow non-breaking punctuation spacing).",
      "Added a regression test in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to assert punctuation binding for line-wrap safety.",
    ],
  },
  {
    id: "headline-highlighted-words-now-open-translation-preview",
    timestamp: "2026-05-21T16:32:26+02:00",
    title: "Headline highlighted words now open the translation preview",
    summary:
      "On text details pages, highlighted words in the headline now open the same translation popup as highlighted words in the body text.",
    userHighlights: [
      "Tapping a highlighted headline word now shows its translation popup in place.",
      "Headline and body highlighted words now behave consistently.",
      "Newly added headline words can open their preview immediately after creation.",
    ],
    technicalNotes: [
      "Updated title word click handling in `src/components/GeneratedTextDetailsTextPane.tsx` to resolve preview vocabulary by word start and open `SavedWordPreviewFlyout` for existing highlighted title words.",
      "Added local title preview mapping and lifecycle close handlers (outside click, Escape, scroll, resize) to mirror body-word popup behavior.",
      "Extended title-word span buttons with `data-word-preview-trigger` and anchor-aware click callbacks so flyout positioning and outside-click guards match the body interaction model.",
    ],
  },
  {
    id: "desktop-topbar-leading-action-now-has-extra-left-spacing",
    timestamp: "2026-05-20T20:51:39+02:00",
    title: "Desktop topbar leading action now has extra left spacing",
    summary:
      "The desktop topbar now adds extra spacing between the menu-toggle area and the left leading action button for clearer separation.",
    userHighlights: [
      "Back/Profile button on desktop no longer sits as tightly against the navbar toggle area.",
      "Topbar spacing feels cleaner and easier to scan.",
      "Left action keeps its current behavior and position with improved breathing room.",
    ],
    technicalNotes: [
      "Added desktop-only left padding (`md:pl-2`) to the leading-action container in `src/routes/app/route.tsx`.",
      "No route logic changed; this update is spacing-only.",
    ],
  },
  {
    id: "desktop-topbar-back-profile-action-moved-to-left-slot",
    timestamp: "2026-05-20T20:50:45+02:00",
    title: "Desktop topbar back/profile action moved to the left slot",
    summary:
      "On desktop, the route-aware Back/Profile action now sits in the left topbar area next to the menu toggle instead of the right side.",
    userHighlights: [
      "Back on non-main pages is now positioned at the left side of the desktop topbar.",
      "Main-page Profile action now uses the same left slot for consistent orientation.",
      "Right topbar area stays focused on language selection and related controls.",
    ],
    technicalNotes: [
      "Moved desktop route-aware top action rendering from the right topbar cluster to the left cluster in `src/routes/app/route.tsx`.",
      "Added `desktop-topbar-leading-action` test id to make placement assertions stable in E2E.",
      "Extended `e2e/classic-tools-nav.spec.ts` to verify desktop action visibility and left-of-center placement.",
    ],
  },
  {
    id: "desktop-topbar-now-matches-mobile-back-behavior",
    timestamp: "2026-05-20T20:48:53+02:00",
    title: "Desktop topbar now matches mobile back behavior",
    summary:
      "Desktop now uses the same topbar pattern as mobile: non-main app pages show Back in the top action slot, while the main feed keeps Profile.",
    userHighlights: [
      "On desktop, non-main pages now show Back where Profile was shown before.",
      "The top action pattern now stays consistent across mobile and desktop.",
      "Back follows real browser history first and safely falls back when needed.",
    ],
    technicalNotes: [
      "Updated topbar action logic in `src/routes/app/route.tsx` to use one shared route-aware back/profile decision for both mobile and desktop.",
      "Removed the duplicate desktop-only back control on the left side to keep one clear top action source of truth.",
      "Added desktop E2E regression coverage in `e2e/classic-tools-nav.spec.ts` for non-main-page back-button behavior.",
    ],
  },
  {
    id: "mobile-topbar-now-shows-back-on-all-non-main-pages",
    timestamp: "2026-05-20T20:46:27+02:00",
    title: "Mobile topbar now shows back on all non-main pages",
    summary:
      "On mobile, the topbar now shows a back action on every app page except the main feed so returning is always available.",
    userHighlights: [
      "When you open any non-main app page, the top-left action is now Back instead of Profile.",
      "Back now first follows your real navigation history when available.",
      "If no browser history exists, Back safely returns to the main app flow.",
    ],
    technicalNotes: [
      "Updated the `/app` layout topbar condition in `src/routes/app/route.tsx` to render the mobile back button for all non-main routes.",
      "Back action now prefers `window.history.back()` and falls back to route navigation when history is unavailable.",
      "Added mobile E2E regression coverage in `e2e/mobile-profile-space-settings.spec.ts` for non-main-page back-button behavior.",
    ],
  },
  {
    id: "daily-and-struggle-texts-now-stream-only-final-validated-output",
    timestamp: "2026-05-20T20:41:37+02:00",
    title: "Daily and struggle texts now stream only final validated output",
    summary:
      "Daily weather-card texts and struggle-practice texts now run retries off-screen and stream only one final accepted version.",
    userHighlights: [
      "Daily card generation no longer visibly swaps between intermediate drafts.",
      "Struggle-practice generation keeps required-word retries but only streams the final accepted text.",
      "The visible stream now stays consistent from first chunk to final saved text.",
    ],
    technicalNotes: [
      "Added deferred streaming mode to base text generation in `reeprise/tasks.py` via `stream_live` and `push_to_websocket` wiring.",
      "Updated `complete_generated_text` so `DAILY_NUDGE` and struggle-origin texts generate off-screen, persist pending, playback-stream final content, then mark complete.",
      "Added backend regression coverage in `reeprise/tests.py` to assert daily nudge generation uses deferred streaming and playback of final content.",
    ],
  },
  {
    id: "chapter-stream-reset-now-prevents-truncated-tail-fragments",
    timestamp: "2026-05-20T19:44:08+02:00",
    title: "Chapter stream reset now prevents truncated tail fragments",
    summary:
      "Chapter streaming now resets buffered stream text at the first chunk so stale partial tails from earlier runs cannot append to new text.",
    userHighlights: [
      "Streaming chapters no longer end with leftover clipped tails such as partial repeated starts.",
      "Each new chapter stream now begins from a clean visible buffer.",
      "The final text stays coherent from first streamed chunk to completion.",
    ],
    technicalNotes: [
      "Added `reset_stream` to the first websocket payload of both live and playback streaming in `reeprise/tasks.py`.",
      "Updated `useStreamingTextIntegrator` to replace buffered content when `reset_stream` is true, then append subsequent chunks normally.",
      "Added backend regression coverage in `reeprise/tests.py` to assert first-chunk reset semantics for playback streaming.",
    ],
  },
  {
    id: "chapter-continuation-now-prevalidates-required-words-before-visible-stream",
    timestamp: "2026-05-20T19:38:44+02:00",
    title:
      "Chapter continuation now prevalidates required words before visible stream",
    summary:
      "Continuing a text with vocabulary now streams only the final validated chapter so required words can still be enforced without visible post-stream text replacement.",
    userHighlights: [
      "The visible chapter stream now stays stable from first chunk to final text.",
      "Required continuation words are still enforced through internal retries before the visible stream starts.",
      "Completion now feels smoother because the final chapter no longer changes to a different draft after streaming.",
    ],
    technicalNotes: [
      "Refactored `reeprise/tasks.py` chapter generation to run retries off-screen (`stream_live=False`) and stream only the final accepted content as websocket playback chunks.",
      "Changed chapter state sequencing so content is persisted while pending, streamed to the user, then marked complete with identical content.",
      "Expanded `reeprise/tests.py` coverage to verify retry behavior and that playback streaming sends only the final chapter content once.",
    ],
  },
  {
    id: "chapter-continuation-stream-now-stays-stable-until-finish",
    timestamp: "2026-05-20T19:34:52+02:00",
    title: "Chapter continuation stream now stays stable until finish",
    summary:
      "Continuing a text with vocabulary no longer visibly swaps to a different chapter body right after streaming completes.",
    userHighlights: [
      "The text you see while a continuation is streaming now remains the same after completion.",
      "The chapter no longer re-renders with a second generated version immediately after the stream ends.",
      "Continuation flow now feels predictable during vocabulary-guided chapter generation.",
    ],
    technicalNotes: [
      "Changed chapter generation in `reeprise/tasks.py` to keep a single streamed generation attempt instead of regenerating chapter content after stream output.",
      "Kept post-generation normalization and persistence on the same streamed content so completion state aligns with what the user already saw.",
      "Updated backend regression coverage in `reeprise/tests.py` to assert single-attempt chapter generation behavior.",
    ],
  },
  {
    id: "speaking-practice-now-uses-a-simpler-modern-voice-surface",
    timestamp: "2026-05-18T05:17:37+02:00",
    title: "Speaking practice now uses a simpler modern voice surface",
    summary:
      "Speaking practice now uses a quieter neutral interface with a clearer walking-mode control, compact status, and simpler voice controls.",
    userHighlights: [
      "The conversation surface is cleaner and easier to scan.",
      "Walking mode is now the primary control at the top of the screen.",
      "Voice, writing, and settings controls now use a simpler modern layout.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/dialogue.tsx` with a neutral full-screen surface, compact header, and flatter 8px-radius controls.",
      "Added Heroicons to primary voice, writing, settings, close, and retry controls.",
      "Removed the walking-mode explanatory paragraph from the active screen to keep the voice flow focused.",
    ],
  },
  {
    id: "speaking-walking-mode-now-supports-spoken-mic-language-commands",
    timestamp: "2026-05-13T20:47:53+02:00",
    title: "Speaking walking mode now supports spoken mic language commands",
    summary:
      "Walking mode now makes microphone language recovery possible without looking at the phone by accepting spoken commands such as `mic German`, `mic French`, `mic auto`, and `mic learning`.",
    userHighlights: [
      "You can now change the microphone recognition language by voice while walking.",
      "Language commands are handled locally and do not get sent as conversation turns.",
      "Automatic language detection still runs, but spoken commands give a reliable fallback when browser recognition starts in the wrong locale.",
    ],
    technicalNotes: [
      "Added spoken microphone language command parsing in `src/routes/app/dialogue.tsx` for English and German command phrases.",
      "Added explicit recognition-language state so auto switching and manual commands update the active browser speech-recognition locale consistently.",
      "Added translation keys for microphone command confirmations in `src/assets/translations.json`.",
      "Extended `e2e/speaking-practice.spec.ts` with coverage that a walking-mode mic language command is confirmed locally and does not hit the reply API.",
    ],
  },
  {
    id: "translation-on-the-go-now-uses-a-modern-composer",
    timestamp: "2026-05-13T20:44:54+02:00",
    title: "Translation on the go now uses a modern composer",
    summary:
      "The translation-on-the-go feature now uses the real translation flow on its page and presents input, target, result, and saving in a calmer modern composer.",
    userHighlights: [
      "The quick-translate page now uses the same real translation behavior as the reusable translation tool.",
      "The input area, language direction, and translation result are easier to scan on desktop and mobile.",
      "Saving short translations to vocabulary remains available directly below the result.",
    ],
    technicalNotes: [
      "Replaced the `/app/quick_translate` stub implementation with `QuickTranslateCard` so the page uses the authenticated `/api/reeprise/translate/` flow.",
      "Restyled `src/components/QuickTranslateCard.tsx` around a neutral composer surface, target-aware action control, quieter metadata, and higher-hierarchy translation output.",
      "Added translation keys for the target-aware action label and composer word-count metadata.",
    ],
  },
  {
    id: "speaking-practice-now-adds-walking-mode-for-eyes-free-use",
    timestamp: "2026-05-13T20:43:22+02:00",
    title: "Speaking practice now adds walking mode for eyes-free use",
    summary:
      "Speaking practice now has a walking mode that prepares the conversation for phone-away use with automatic voice, microphone restart, calmer pause timing, and non-visual feedback.",
    userHighlights: [
      "A new walking mode turns on voice replies and hands-free microphone restart together.",
      "Walking mode uses calmer pause timing so short pauses while walking are less likely to send a turn too early.",
      "The screen stays awake where the browser supports it, with small sound and vibration cues for listening, sending, and errors.",
    ],
    technicalNotes: [
      "Added persisted `speaking-walking-mode-v1` state and a dedicated walking-mode control in `src/routes/app/dialogue.tsx`.",
      "Walking mode now enables auto voice, hands-free restart, automatic mic locale, patient pause timing, and screen Wake Lock when available.",
      "Added best-effort Web Audio cues, vibration cues, and browser `speechSynthesis` fallback when backend voice playback fails.",
      "Extended `e2e/speaking-practice.spec.ts` with mocked microphone and Wake Lock coverage for the walking-mode route flow.",
    ],
  },
  {
    id: "maison-memoire-cutouts-now-use-detected-object-regions",
    timestamp: "2026-05-13T20:42:53+02:00",
    title: "Maison Mémoire cutouts now use detected object regions",
    summary:
      "Maison Mémoire now asks vision generation for specific object cutout candidates with image coordinates instead of using arbitrary image crops.",
    userHighlights: [
      "Cutouts are now based on visible objects such as a racket, ball, shoe, cup, bag, or notebook when the model detects them.",
      "The cutout source region now follows the detected object box instead of a random part of the photo.",
      "Object names remain available for accessible drag labels while the visual fragment comes from the uploaded image.",
    ],
    technicalNotes: [
      "Changed the Maison Mémoire prompt to request `cutout_suggestions` as `{ name, bbox, mask_polygon }` objects with percentage image coordinates and object outline points.",
      "Normalized cutout suggestions in `reeprise/views.py` and preserved backward compatibility for string-only suggestions.",
      "Updated `src/routes/app/maison_memoire.tsx` to crop uploaded images by detected bounding boxes and use the model-provided outline for clipping and text flow.",
    ],
  },
  {
    id: "maison-memoire-text-now-anchors-to-visible-image-vocabulary",
    timestamp: "2026-05-13T20:35:42+02:00",
    title: "Maison Mémoire text now anchors to visible image vocabulary",
    summary:
      "Maison Mémoire generation now focuses the French text on visible objects, colors, materials, textures, and scene details from the uploaded image.",
    userHighlights: [
      "Generated text should now teach words connected to what is actually visible in the photo.",
      "The writing remains lyrical and engaging while naming concrete image details.",
      "The model is now explicitly told not to invent objects, colors, places, or materials that are not visible or strongly implied.",
    ],
    technicalNotes: [
      "Expanded the Maison Mémoire vision prompt in `reeprise/views.py` to require object, color, material, texture, light, spatial, and scene vocabulary.",
      "Updated the fallback payload to include concrete color/material vocabulary derived from the uploaded image tone.",
      "Added backend regression assertions for prompt vocabulary grounding and fallback vocabulary output.",
    ],
  },
  {
    id: "speaking-practice-now-hides-debug-context-and-sends-clean-turn-history",
    timestamp: "2026-05-13T20:37:34+02:00",
    title:
      "Speaking practice now hides debug context and sends cleaner turn history",
    summary:
      "Speaking practice now keeps internal language details out of the learner view, sends cleaner conversation context, and exposes turn-level coaching as an intentional setting.",
    userHighlights: [
      "The conversation screen no longer shows debug language-context details.",
      "Turn-level coaching can now be enabled from speaking settings and appears inline when available.",
      "Speaking settings are grouped more clearly across voice, microphone, and cue controls.",
    ],
    technicalNotes: [
      "Removed the visible debug language-context panel, related runtime state, and obsolete debug translation keys.",
      "Changed speaking reply requests so `recent_messages` contains only prior conversation turns, while the current message is sent through `message`.",
      "Wired persisted `speaking-coaching-enabled-v1` state into `useSpeakingPracticeReply` and rendered structured coaching hints on assistant replies.",
      "Updated `e2e/speaking-practice.spec.ts` to cover write-mode entry, coaching, debug-panel absence, and clean request history.",
    ],
  },
  {
    id: "maison-memoire-now-uses-small-justified-editorial-text",
    timestamp: "2026-05-13T20:33:02+02:00",
    title: "Maison Mémoire now uses small justified editorial text",
    summary:
      "Maison Mémoire now renders generated memories as compact justified body text, closer to a magazine article layout.",
    userHighlights: [
      "Generated text now appears in small multi-column body copy instead of oversized display typography.",
      "Paragraphs use justified alignment for a blocksatz-style editorial feel.",
      "Image fragments remain embedded inside the text so dragging them still changes the live text flow.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/maison_memoire.tsx` composition typography to use CSS columns, small body sizing, and `text-justify`.",
      "Removed the highlighted-word renderer from the composition so the output reads as clean editorial body copy.",
      "Reduced cutout scale and shape margins to fit inside compact blocksatz text.",
    ],
  },
  {
    id: "maison-memoire-now-starts-as-upload-only",
    timestamp: "2026-05-13T20:30:00+02:00",
    title: "Maison Mémoire now starts as upload-only",
    summary:
      "Maison Mémoire now removes the remaining controls and starts from a single upload action before revealing the generated editorial text composition.",
    userHighlights: [
      "Before upload, the page shows only the image upload action.",
      "After upload, the visible result is only generated French text with image fragments embedded in the typography.",
      "Level controls, reset controls, status labels, metadata, and side panels were removed from the experience.",
    ],
    technicalNotes: [
      "Simplified `src/routes/app/maison_memoire.tsx` to render either upload-only entry or the generated full-screen text canvas.",
      "Removed level state, reset action, status/error display, poster metadata, and secondary quote/sidebar layout from the route.",
      "Updated the Maison Mémoire Playwright regression to mock the upload generation endpoint and verify the upload-to-composition flow.",
    ],
  },
  {
    id: "maison-memoire-now-uses-full-screen-living-typography-canvas",
    timestamp: "2026-05-12T22:55:50+02:00",
    title: "Maison Mémoire now uses a full-screen living typography canvas",
    summary:
      "Maison Mémoire now focuses on the core demo: uploaded image fragments become movable collage cutouts inside dramatic editorial French text.",
    userHighlights: [
      "The screen is now one full editorial canvas rather than a dashboard or framed poster section.",
      "Uploaded image fragments appear as irregular cutouts placed directly in paragraph text.",
      "Dragging a cutout keeps live text reflow around irregular object-shaped polygons.",
    ],
    technicalNotes: [
      "Removed framed poster image areas, rounded containers, and card-like layout from `src/routes/app/maison_memoire.tsx`.",
      "Changed cutouts to use shared CSS `clip-path` and `shape-outside` polygons so text wraps around irregular silhouettes.",
      "Kept requestAnimationFrame-throttled pointer dragging and level controls within the full-screen editorial surface.",
    ],
  },
  {
    id: "maison-memoire-upload-generation-no-longer-dead-ends-on-model-failure",
    timestamp: "2026-05-12T22:44:45+02:00",
    title:
      "Maison Mémoire upload generation no longer dead-ends on model failure",
    summary:
      "Maison Mémoire now keeps valid image uploads usable even when the remote image-text generation call is slow or unavailable.",
    userHighlights: [
      "Uploading a photo should no longer end with only `Text konnte nicht erstellt werden` when the image itself is valid.",
      "The generated poster can still receive photo-derived French memory text if the model call fails.",
      "Image uploads are compressed more aggressively so generation starts from a lighter request.",
    ],
    technicalNotes: [
      "Reduced Maison Mémoire image payload size and switched the vision request to low detail in `reeprise/views.py`.",
      "Added a local photo-derived fallback payload for Maison Mémoire generation exceptions.",
      "Added backend regression coverage for the fast vision request options and model-failure fallback behavior.",
    ],
  },
  {
    id: "maison-memoire-now-matches-editorial-memory-poster-direction",
    timestamp: "2026-05-12T22:40:29+02:00",
    title: "Maison Mémoire now matches the editorial memory poster direction",
    summary:
      "Maison Mémoire now renders uploaded moments as a bold magazine-style French memory page with cutout imagery, highlights, notes, and poster metadata.",
    userHighlights: [
      "The main composition now feels like a finished editorial page instead of a plain text canvas.",
      "Uploaded photos appear as a large paper-cut image inside the memory poster.",
      "French text, marker highlights, handwritten-style notes, and draggable cutouts now share the same composition.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/maison_memoire.tsx` around a single poster canvas with large headline hierarchy, metadata, image cutout treatment, notes, quote block, and footer controls.",
      "Added reusable highlighted text rendering and kept requestAnimationFrame-based cutout drag updates inside the editorial text flow.",
      "Added poster-facing translation keys in `src/assets/translations.json`.",
    ],
  },
  {
    id: "maison-memoire-now-uses-release-facing-creation-surface",
    timestamp: "2026-05-12T22:32:32+02:00",
    title: "Maison Mémoire now uses a release-facing creation surface",
    summary:
      "Maison Mémoire now presents the photo-to-memory experience as a product screen instead of an internal prototype panel.",
    userHighlights: [
      "The page now focuses on choosing a photo, reading the generated French memory, and arranging cutouts in the text.",
      "Internal model, pipeline, and prototype explanation cards were removed from the visible experience.",
      "The draggable cutout interaction remains available directly inside the editorial text canvas.",
    ],
    technicalNotes: [
      "Removed prototype status cards, demo controls, and implementation-facing copy from `src/routes/app/maison_memoire.tsx`.",
      "Updated Maison Mémoire translation copy in `src/assets/translations.json` to use product-facing labels.",
      "Updated the Maison Mémoire Playwright regression to assert the released surface and absence of prototype panels.",
    ],
  },
  {
    id: "maison-memoire-upload-now-shows-clear-generation-state",
    timestamp: "2026-05-12T22:27:23+02:00",
    title: "Maison Mémoire upload now shows a clear generation state",
    summary:
      "Uploading a photo in Maison Mémoire now visibly shows that AI text generation is running and when it has completed.",
    userHighlights: [
      "After upload, the text canvas now shows a clear generating message instead of looking unchanged.",
      "The page no longer marks the composition ready until the image-based text generation returns.",
      "Successful generation and failed generation now both show explicit status messages in the main canvas.",
    ],
    technicalNotes: [
      "Added `isGeneratingText` state in `src/routes/app/maison_memoire.tsx` to separate demo pipeline animation from real upload generation.",
      "Added visible generation success/failure panels and a generation status row.",
      "Added new translation keys in `src/assets/translations.json` for upload-generation progress and error states.",
    ],
  },
  {
    id: "maison-memoire-cutout-click-no-longer-moves-image",
    timestamp: "2026-05-12T22:23:15+02:00",
    title: "Maison Mémoire cutout click no longer moves the image",
    summary:
      "Clicking a Maison Mémoire cutout no longer changes its position; movement starts only after a real drag gesture.",
    userHighlights: [
      "Tapping or clicking a cutout keeps it in place.",
      "Text flow updates only when the cutout is dragged.",
      "A small drag threshold prevents accidental layout shifts from normal clicks.",
    ],
    technicalNotes: [
      "Tracked drag origin and movement state in `src/routes/app/maison_memoire.tsx`.",
      "Removed immediate position commits on pointer down and guarded final position commits behind the movement threshold.",
      "Kept smooth requestAnimationFrame updates for actual drag gestures.",
    ],
  },
  {
    id: "maison-memoire-cutout-dragging-now-feels-smoother",
    timestamp: "2026-05-12T22:19:24+02:00",
    title: "Maison Mémoire cutout dragging now feels smoother",
    summary:
      "Dragging cutout images through Maison Mémoire text now updates the text flow more continuously.",
    userHighlights: [
      "Cutouts now track pointer movement against the editorial text area instead of the whole stage.",
      "The text reflows immediately while dragging, with smoother frame-paced updates.",
      "Dragging can place cutouts deeper into the text column, not only on the hard left or right edge.",
    ],
    technicalNotes: [
      "Added a text-flow coordinate ref and requestAnimationFrame-throttled drag updates in `src/routes/app/maison_memoire.tsx`.",
      "Adjusted cutout float margins, shape margin, cursor states, pointer cancellation, and selection suppression during drag.",
      "Kept cutouts as real floated elements so browser text wrapping updates live around the moved objects.",
    ],
  },
  {
    id: "maison-memoire-upload-now-generates-text-from-image",
    timestamp: "2026-05-12T22:16:59+02:00",
    title: "Maison Mémoire upload now generates text from the image",
    summary:
      "Maison Mémoire now calls a backend vision endpoint when a user uploads a photo, replacing the demo copy with generated French editorial text.",
    userHighlights: [
      "Uploading a photo now starts real text generation instead of only showing static prototype text.",
      "The generated headline, memory line, and body paragraphs are placed directly into the frontend text-flow canvas.",
      "If generation fails, the page keeps the demo text visible and shows a compact failure note.",
    ],
    technicalNotes: [
      "Added authenticated endpoint `POST /api/reeprise/maison-memoire/generate/` that accepts an image and CEFR level, sends the image to the existing OpenAI vision client, and returns structured JSON text.",
      "Updated `src/routes/app/maison_memoire.tsx` to post uploaded files with CSRF protection and render generated headline, memory, and body segments.",
      "Added `MaisonMemoireGenerateRequestSerializer` for request validation and kept the cutout/text-flow interaction frontend-local.",
    ],
  },
  {
    id: "maison-memoire-cutouts-now-drive-real-text-flow",
    timestamp: "2026-05-12T22:08:35+02:00",
    title: "Maison Mémoire cutouts now drive real text flow",
    summary:
      "The Maison Mémoire prototype now shows cutout image pieces embedded in frontend text so dragging them changes how the words wrap.",
    userHighlights: [
      "The main composition is now a text-first editorial page instead of a photo background with a text card.",
      "Freigestellte cutout objects sit inside the text body and can be dragged between sides and text sections.",
      "The prototype now makes the intended text-flow behavior visible: words rewrap around the moved image fragments.",
    ],
    technicalNotes: [
      "Reworked `src/routes/app/maison_memoire.tsx` to render cutouts as floated interactive elements inside paragraph flow rather than absolute overlays.",
      "Added text-flow-specific UI copy in `src/assets/translations.json` and clarified that generated text is real frontend text.",
      "Kept the uploaded source photo visible separately so the cutout concept remains connected to the user's original image.",
    ],
  },
  {
    id: "maison-memoire-standalone-editorial-ai-product-mockup",
    timestamp: "2026-05-12T21:48:50+02:00",
    title: "Maison Mémoire standalone editorial AI product mockup",
    summary:
      "A separate premium product mockup now explores transforming real-life photos into interactive editorial French memory compositions.",
    userHighlights: [
      "A new Maison Mémoire page is available from the side navigation.",
      "The mockup supports photo upload, staged multimodal processing, adaptive French text levels, and draggable editorial object cutouts.",
      "The experience is intentionally framed as a separate product concept rather than part of the existing Uxhulu learning flow.",
    ],
    technicalNotes: [
      "Added `src/routes/app/maison_memoire.tsx` as a local-state TanStack route with upload preview, pipeline state, level switching, and pointer-drag object positioning.",
      "Added sidebar navigation and translation keys for the new route in `src/routes/app/route.tsx` and `src/assets/translations.json`.",
      "Kept the prototype frontend-only so no backend data model or generation pipeline is coupled to the current app.",
    ],
  },
  {
    id: "chapter-continue-german-cta-now-uses-words-wording",
    timestamp: "2026-05-11T22:16:37+02:00",
    title: "Chapter continuation German CTA now uses requested wording",
    summary:
      "The German chapter continuation action label was adjusted to the exact requested phrasing.",
    userHighlights: [
      "The action now reads `Text mit Wörtern fortsetzen`.",
      "The wording keeps the continuation intent explicit with `Text` and `fortsetzen`.",
      "Other chapter actions remain unchanged.",
    ],
    technicalNotes: [
      "Updated `btn_continue_with_words` German translation in `src/assets/translations.json`.",
      "Kept key names and button wiring unchanged so behavior and route flow remain stable.",
    ],
  },
  {
    id: "struktur-panel-now-opens-by-default-and-vocabulary-cards-use-fewer-dividers",
    timestamp: "2026-05-11T22:10:23+02:00",
    title:
      "Struktur panel now opens by default and vocabulary cards use fewer dividers",
    summary:
      "Vocabulary cards now open with Struktur details visible by default, and the card sections were simplified to reduce visual divider noise.",
    userHighlights: [
      "You now see Struktur content immediately without an extra `Mehr Struktur` click.",
      "Text-details vocabulary cards now use a cleaner section layout with fewer separator lines.",
      "The Struktur toggle remains available so you can still collapse details when needed.",
    ],
    technicalNotes: [
      "Changed default Struktur expansion state in `src/routes/app/reeprise/$generatedTextId.tsx` and switched persisted grammar toggle defaults to open in `src/components/VocabularyPreviewCard.tsx` and `src/routes/app/vocabulary_progress.tsx` via a new local-storage key version.",
      "Refined text-details card layout in `src/routes/app/reeprise/$generatedTextId.tsx` by removing extra border-y separators and consolidating structure metadata into a single calmer container.",
      "Kept vocabulary interaction behavior unchanged (selection, reveal/hide, jump, and structure collapse).",
    ],
  },
  {
    id: "reeprise-vocabulary-translation-reveal-now-uses-tap-on-text-without-large-button",
    timestamp: "2026-05-11T22:08:00+02:00",
    title:
      "Reeprise vocabulary translation reveal now uses tap-on-text without large button",
    summary:
      "On text details vocabulary cards, the large reveal button was removed and translation reveal now works by tapping the translation line itself.",
    userHighlights: [
      "The blurred translation is now directly tappable to reveal or hide.",
      "A compact hint line explains the interaction without adding a large control.",
      "Card layout stays cleaner while keeping reveal discoverable.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` to replace the large reveal button with tap-to-toggle behavior on the translation text.",
      "Added tiny helper hints with new translation keys in `src/assets/translations.json` (`vocabulary_guess_tap_to_reveal_hint`, `vocabulary_guess_tap_to_hide_hint`).",
      "Kept jump/navigation behavior intact by marking the translation toggle control with `data-vocabulary-control`.",
    ],
  },
  {
    id: "struktur-beispiele-now-follow-language-space-level",
    timestamp: "2026-05-11T22:07:07+02:00",
    title: "Struktur examples now follow your language-space level",
    summary:
      "The `BEISPIELE` section in Struktur now explicitly targets the active CEFR level of the relevant language space instead of using potentially too-advanced phrasing.",
    userHighlights: [
      "Related examples are now constrained to your current language-space level.",
      "Advanced or rare constructions above your level should no longer appear in `BEISPIELE`.",
      "Level guidance applies while preserving pattern relevance to the current vocabulary card.",
    ],
    technicalNotes: [
      "Added language-space level resolution in `barkguide/reeprise/tasks.py` for translation generation using the current/linked text context with fallback.",
      "Extended `_build_translation_prompt(...)` in `barkguide/reeprise/tasks.py` to include the resolved level and enforce level-matched `structure.related_examples` guidance.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert level-aware Struktur prompt constraints.",
    ],
  },
  {
    id: "reeprise-text-details-vocabulary-items-now-blur-translation-by-default",
    timestamp: "2026-05-11T22:05:14+02:00",
    title:
      "Reeprise text details vocabulary items now blur translation by default",
    summary:
      "Vocabulary items shown on the text details page now use guess-first translation blur with an explicit reveal/hide action.",
    userHighlights: [
      "Translation lines like `sanft` now start blurred in the text-details vocabulary list.",
      "You can reveal and hide each translation directly on its card.",
      "Word, sentence, and structure areas remain unchanged.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` `VocabularyItem` to add per-card translation reveal state.",
      "Applied blur styling to the translation line and added reveal/hide button wiring using existing translation keys.",
      "Marked reveal control with `data-vocabulary-control` so card click-to-jump behavior remains intact.",
    ],
  },
  {
    id: "pending-generated-text-preview-now-stays-within-final-word-limit",
    timestamp: "2026-05-11T22:03:40+02:00",
    title: "Pending generated-text preview now stays within final word limit",
    summary:
      "Text previews during generation no longer show overlong drafts that later collapse after completion.",
    userHighlights: [
      "While a text is still generating, the visible preview now stays within the selected word length.",
      "The jump from long pending text to shorter final text is reduced on text details.",
      "Pending previews now use sentence-safe shortening behavior aligned with final output rules.",
    ],
    technicalNotes: [
      "Extended `resolveGeneratedTextContent(...)` in `src/utils/generatedTextContent.ts` with optional `maxWords` and frontend sentence-safe max-word enforcement for pending story parsing.",
      "Wired `maxWords` through generated-text details render paths in `src/routes/app/reeprise/$generatedTextId.tsx` and `src/components/GeneratedTextDetailsTextPane.tsx`.",
      "Added Vitest regression coverage in `src/utils/generatedTextContent.test.ts` for pending plain-story and JSON-story clamp behavior.",
    ],
  },
  {
    id: "text-details-word-preview-flyout-now-uses-blurred-translation-reveal",
    timestamp: "2026-05-11T22:01:04+02:00",
    title:
      "Text details word preview flyout now uses blurred translation reveal",
    summary:
      "The vocabulary preview flyout on text details now blurs translations by default and reveals them on demand.",
    userHighlights: [
      "Translation on word preview starts hidden with blur.",
      "You can reveal and hide translation directly in the flyout.",
      "Guess-first behavior now also applies to hover/tap preview cards in text details.",
    ],
    technicalNotes: [
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` (`SavedWordPreviewFlyout`) to add local reveal state for translation text.",
      "Applied blur styling and reveal/hide action using existing translation keys.",
      "Kept pending-translation handling unchanged while aligning display behavior with guess-first flow.",
    ],
  },
  {
    id: "struktur-fields-now-enforce-mother-tongue-output-and-remove-english-fallback-copy",
    timestamp: "2026-05-11T21:58:13+02:00",
    title:
      "Struktur fields now enforce mother-tongue output and remove English fallback copy",
    summary:
      "The Struktur section now avoids mixed-language explanations by enforcing target-language output in generation prompts and removing English-only fallback phrasing.",
    userHighlights: [
      "New Struktur explanations are now explicitly instructed to stay in your translation language.",
      "Fallback Struktur content no longer shows fixed English wording.",
      "Pattern fallback lines stay compact and readable without language mixing.",
    ],
    technicalNotes: [
      "Updated translation prompt rules in `barkguide/reeprise/tasks.py` to require all learner-facing Struktur fields in the configured target language only.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` for the new target-language-only Struktur prompt constraint.",
      "Reworked frontend participle fallback strings in `src/utils/vocabularyTranslationDetails.ts` to avoid hardcoded English prose and updated `src/utils/vocabularyTranslationDetails.test.ts` expectations.",
    ],
  },
  {
    id: "chapter-continue-german-cta-now-uses-clearer-vocabulary-wording",
    timestamp: "2026-05-11T21:57:40+02:00",
    title:
      "Chapter continuation German CTA now uses clearer vocabulary wording",
    summary:
      "The German action label for chapter continuation was renamed to reduce ambiguity and match the vocabulary-focused intent.",
    userHighlights: [
      "The action now reads `Text mit Vokabeln fortsetzen` in German.",
      "The wording is more explicit about continuing an existing text with selected vocabulary.",
      "The separate `Neuen Text aus Vokabeln generieren` action remains clearly distinct.",
    ],
    technicalNotes: [
      "Updated the German translation for `btn_continue_with_words` in `src/assets/translations.json`.",
      "Kept translation key usage unchanged so existing CTA wiring and tests remain stable.",
    ],
  },
  {
    id: "vocabulary-card-reveal-now-requires-explicit-tap-even-when-translation-toggle-is-on",
    timestamp: "2026-05-11T21:39:48+02:00",
    title:
      "Vocabulary card reveal now requires explicit tap even when translation toggle is on",
    summary:
      "Vocabulary answers now stay blurred until you reveal them manually, even if translation visibility was enabled globally before.",
    userHighlights: [
      "Cards no longer auto-show the answer due to old translation visibility state.",
      "Reveal and hide are now consistently controlled by the card’s own button.",
      "Guess-first behavior remains stable across card opens.",
    ],
    technicalNotes: [
      "Updated visibility helper in `src/utils/isVocabularyTranslationVisible.ts` to depend only on per-card reveal state.",
      "Removed the global show-translation override path in `src/routes/app/vocabulary_trainer.tsx` reveal logic.",
      "Adjusted regression coverage in `src/utils/isVocabularyTranslationVisible.test.ts` to enforce explicit reveal behavior.",
    ],
  },
  {
    id: "text-details-vocabulary-cards-now-use-guess-first-blur-reveal",
    timestamp: "2026-05-11T21:34:13+02:00",
    title: "Text details vocabulary cards now use guess-first blur and reveal",
    summary:
      "Vocabulary cards on the text details page now keep the answer blurred until you reveal it, matching the intended guess-first flow.",
    userHighlights: [
      "After opening a vocabulary card on text details, the answer is now blurred first.",
      "You can reveal and hide the answer with one tap.",
      "Each new card starts hidden again so recall practice stays consistent.",
    ],
    technicalNotes: [
      "Updated answer rendering in `src/components/VocabularyPreviewCard.tsx` to apply blurred hidden state by default when a card is expanded.",
      "Added reveal/hide button behavior using existing translation keys (`vocabulary_guess_reveal_answer`, `vocabulary_guess_hide_answer`).",
      "Reset per-card reveal state on vocabulary change so navigating to the next card starts hidden.",
    ],
  },
  {
    id: "vocabulary-progress-table-now-shows-plain-sentence-without-inline-word-highlight",
    timestamp: "2026-05-11T21:34:06+02:00",
    title:
      "Vocabulary progress table now shows plain sentence without inline highlight",
    summary:
      "The vocabulary progress table now renders source sentences as plain text and no longer highlights the vocabulary word inline in that table view.",
    userHighlights: [
      "Sentence lines in the desktop vocabulary progress table now stay visually neutral.",
      "Inline emphasis used in text-reading views is no longer reused in the table sentence line.",
      "The rest of the vocabulary progress actions and metadata remain unchanged.",
    ],
    technicalNotes: [
      "Updated desktop table sentence rendering in `src/routes/app/vocabulary_progress.tsx` to output `primarySentence` directly instead of `highlightVocabularyWordInSentence(...)`.",
      "Kept existing mobile-card sentence rendering unchanged for now.",
    ],
  },
  {
    id: "struktur-cards-now-prioritize-meaning-over-grammar-metadata",
    timestamp: "2026-05-10T20:26:40+02:00",
    title: "Struktur cards now prioritize meaning over grammar metadata",
    summary:
      "Vocabulary Struktur output now focuses on understanding and reusable patterns, while noisy grammar-only fields are filtered out.",
    userHighlights: [
      "Struktur now stays compact and meaning-first for simple words like `oublier`.",
      "Empty or trivial values such as `Keine` are no longer shown as fake content.",
      "Pattern-focused cards like `sirotant` still show reusable form patterns and breakdowns.",
    ],
    technicalNotes: [
      "Updated translation detail normalization in `src/utils/vocabularyTranslationDetails.ts` to filter non-informative optional fields and keep structure fallback behavior for legacy cards.",
      "Reduced grammar-noise rendering in vocabulary detail expansions in `src/routes/app/vocabulary_trainer.tsx` and `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Refined Struktur generation instructions and optional-value cleanup in `barkguide/reeprise/tasks.py` so prompts avoid overproducing school-grammar metadata.",
      "Verified with targeted frontend vitest suites, backend pytest translation tests, and frontend production build.",
    ],
  },
  {
    id: "wordcard-progressive-disclosure-redesign",
    timestamp: "2026-07-21T12:17:00+02:00",
    title: "Vocabulary card redesigned with progressive disclosure",
    summary:
      "The vocabulary card in the reading sidebar now shows only essential information by default (translation, core idea, examples) and reveals additional sections through expandable accordion panels when you choose Mehr entdecken.",
    userHighlights: [
      "The card now shows only the essentials: word, translation, core idea, and examples. No empty rows or placeholder dashes.",
      "When useful data exists for a category (Typische Verwendung, Wortherkunft, Wortaufbau, Grammatik, Ähnliche Wörter, Weitere Beispiele), a Mehr entdecken button reveals it as inline accordion sections.",
      "Empty sections are hidden entirely rather than shown as unavailable.",
      "On mobile, a compact sticky header keeps the word and audio control visible while you scroll through expanded content.",
      "The existing vocabulary actions, audio controls, selection checkbox, and pattern-unlock analytics are preserved.",
    ],
    technicalNotes: [
      "Created `src/components/WordCard/` directory with `WordCard.tsx`, `WordCardHeader.tsx`, `WordCardEssentials.tsx`, `WordCardSections.tsx`, `WordCardSection.tsx`, `WordCardStickyHeader.tsx`, `useWordCardSections.ts`, `wordCardSectionData.ts`, and `wordCardAnalytics.ts`.",
      "Replaced `VocabularyItemCard` with `WordCard` in `src/routes/app/reeprise/$generatedTextId.tsx`. Existing `VocabularyItemCard` component preserved for future migration of other card implementations.",
      "Section availability computed from existing `translation_details` data via `deriveWordCardData` presentation adapter. No backend changes needed.",
      "Pattern filter in `isRealPattern` ensures Typische Verwendung section only shows actual linguistic patterns, not generic sentences.",
      "Added new translation keys for section titles, subtitles, and unavailable-state messaging in `src/assets/translations.json`.",
      "Multi-open accordion sections persist expand/collapse state per card and track section-open analytics via `CustomEvent` dispatch.",
      "Built and verified with frontend production build. No TypeScript errors in new files.",
    ],
  },
  {
    id: "struktur-section-consolidated-redundancy-reduced-examples-fixed",
    timestamp: "2026-07-21T12:00:00+02:00",
    title: "Struktur section expanded: Merkhilfe, Wortherkunft, reduced redundancy, and example relevance",
    summary:
      "The Struktur section gained two new fields (Merkhilfe for cross-language memory anchors, Wortherkunft for etymology), shows alternative meanings instead of duplicate word labels, removes the redundant contextual-meaning field, and ensures related examples always contain the vocabulary word via both prompt changes and a client-side filter.",
    userHighlights: [
      "New `Merkhilfe` field shows similar-looking words across languages you know (mother tongue, English, etc.) as a memory anchor.",
      "New `Wortherkunft` field shows word etymology when it helps understanding (Latin roots, compound breakdowns).",
      "The old `Wort` field now shows `Alternative Bedeutungen` with synonyms and other translations instead of repeating the main card translation.",
      "Redundant `Im Kontext` has been removed since the translated sentence already appears above the card.",
      "Related examples (`Beispiele`) now always contain the actual vocabulary word via client-side filtering.",
    ],
    technicalNotes: [
      "Added `memory_hint` and `etymology` fields to vocabulary translation details type, API normalizers, and AI prompt in `barkguide/reeprise/tasks.py`, `src/utils/vocabularyTranslationDetails.ts`, and `src/hooks/usePaginatedVocabularyList.ts`.",
      "Threaded `mother_tongue` through `_build_translation_prompt` and `generate_translation` so the AI can generate language-aware memory hints.",
      "Added client-side `filterRelatedExamples` in `getVocabularyStructureDetails` that drops example entries missing the source word or its translation, catching both existing and new data.",
      "Added `memory_hint` and `etymology` rendering blocks to `VocabularyItemCard.tsx`, `VocabularyPreviewCard.tsx`, and `vocabulary_progress.tsx`.",
      "Replaced `word_meaning` rendering with `alternative_meanings` and removed `contextual_meaning` rendering from all three Struktur rendering surfaces.",
      "Updated `related_examples` AI prompt to require the vocabulary word and added backend `_filter_unrelated_examples` post-generation filter.",
      "Added backend test coverage and new translation keys (`vocabulary_label_memory_hint`, `vocabulary_label_etymology`).",
    ],
  },
  {
    id: "vocabulary-trainer-cards-now-use-guess-first-blur-reveal",
    timestamp: "2026-05-10T20:21:12+02:00",
    title: "Vocabulary trainer cards now use guess-first blur and reveal",
    summary:
      "The classic vocabulary trainer card now matches the guess-first interaction by blurring translations until you reveal them.",
    userHighlights: [
      "Trainer cards now show a guess prompt before translation reveal.",
      "Translation stays blurred until you tap reveal, and can be hidden again.",
      "Card interaction now aligns better with the vocabulary list reveal pattern.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/vocabulary_trainer.tsx` to add per-card reveal state and guess-first translation rendering.",
      "Added accessibility labels and reveal/hide button wiring for trainer card translations.",
      "Added `src/utils/isVocabularyTranslationVisible.ts` with regression test coverage in `src/utils/isVocabularyTranslationVisible.test.ts`.",
    ],
  },
  {
    id: "vocabulary-list-now-supports-multi-select-delete-and-select-all-visible",
    timestamp: "2026-05-10T20:19:59+02:00",
    title:
      "Vocabulary list now supports multi-select delete and select-all visible",
    summary:
      "The vocabulary list now lets users select multiple entries, use a single select-all-visible control, and delete selected items in one confirmed action.",
    userHighlights: [
      "Each vocabulary row now includes a selection checkbox on desktop and mobile.",
      "A new `Select all visible` control toggles the full currently filtered list.",
      "You can now delete multiple selected vocabulary items at once with one confirmation dialog.",
      "Single-item delete remains available per vocabulary entry.",
    ],
    technicalNotes: [
      "Extended selection and bulk-delete state handling in `src/routes/app/vocabulary_progress.tsx`, including checkbox rendering for table and mobile card rows.",
      "Added select-all-visible behavior and multi-delete confirmation handling using repeated existing delete mutations.",
      "Added i18n keys in `src/assets/translations.json` for selection column labels, bulk-delete CTA, and multi-delete confirmation/success copy.",
      "Added regression tests in `src/utils/vocabularyBulkSelection.test.ts` and shared selection helpers in `src/utils/vocabularyBulkSelection.ts`.",
    ],
  },
  {
    id: "chapter-continue-now-respects-selected-word-limit-without-cut-sentences",
    timestamp: "2026-05-10T20:14:14+02:00",
    title:
      "Mit Wörtern fortsetzen now respects selected word limit without cut-off sentences",
    summary:
      "Chapter continuation now enforces the selected word length as a hard maximum and avoids ending in the middle of a sentence.",
    userHighlights: [
      "Continuation text no longer exceeds the selected word length.",
      "If a generated draft is too long, it now trims safely instead of cutting a sentence mid-way.",
      "The resulting continuation keeps complete sentence endings.",
    ],
    technicalNotes: [
      "Updated backend generation guidance in `barkguide/reeprise/tasks.py` to enforce a hard maximum word cap for generated text and chapter continuation.",
      "Added `_enforce_max_word_count_with_sentence_boundary(...)` in `barkguide/reeprise/tasks.py` and applied it to both `complete_generated_text` and `generate_chapter` flows.",
      "Added regression tests in `barkguide/reeprise/tests.py` for sentence-safe trimming behavior and strict word-limit prompt guidance.",
    ],
  },
  {
    id: "classic-tools-folder-now-opens-from-collapsed-nav-and-shows-page-titles",
    timestamp: "2026-05-10T20:02:10+02:00",
    title:
      "Classic Tools folder now opens from collapsed nav and shows page titles",
    summary:
      "The Classic Tools section in side navigation now opens reliably from collapsed mode and each entry now shows a small page-title line for easier scanning.",
    userHighlights: [
      "Clicking the Classic Tools folder from collapsed navigation now expands the menu and reveals its links.",
      "Sidebar items now include a quieter second line so page purpose is clearer at a glance.",
      "Classic Barkguide, Shopping List, and Vocabulary List remain grouped in one expandable section.",
    ],
    technicalNotes: [
      "Updated sidebar folder toggle behavior in `src/routes/app/route.tsx` to auto-open the full nav when Classic Tools is clicked in collapsed mode.",
      "Added `pageTitle` rendering in sidebar entries and subtitle lines for grouped Classic Tools child links.",
      "Added new translation keys for sidebar title lines in `src/assets/translations.json`.",
    ],
  },
  {
    id: "vertiefen-helper-now-asks-for-a-better-question-instead-of-answering-off-topic",
    timestamp: "2026-05-10T19:57:47+02:00",
    title:
      "Vertiefen now asks for a better question instead of answering off-topic",
    summary:
      "When Vertiefen cannot answer reliably, it now gives a short rephrase instruction so users can ask in a way the feature can answer, instead of returning unrelated output.",
    userHighlights: [
      "Unclear requests now return a concrete rephrase suggestion.",
      "Fallback guidance includes better prompts like meaning-in-sentence or short-example requests.",
      "The helper remains anchored to the selected word or sentence when available.",
    ],
    technicalNotes: [
      "Updated helper prompt contract in `barkguide/reeprise/views.py` to require rephrase guidance for unclear intent.",
      "Added deterministic fallback builder `_build_agent_rephrase_guidance(...)` and used it when model output is unparsable or low-confidence and too short.",
      "Added backend regressions in `barkguide/reeprise/tests.py` for non-JSON output fallback and short low-confidence fallback behavior.",
    ],
  },
  {
    id: "vertiefen-helper-now-stays-grounded-on-the-latest-user-question",
    timestamp: "2026-05-10T19:56:04+02:00",
    title: "Vertiefen helper now stays grounded on the latest user question",
    summary:
      "Vertiefen follow-up replies are now constrained more strictly to the user’s most recent question and provided text context, reducing off-topic answers.",
    userHighlights: [
      "The helper now prioritizes your newest question instead of drifting to older turns.",
      "If context is unclear, it should ask one short clarification instead of guessing.",
      "When a focus word is present, replies stay anchored to that word and sentence.",
    ],
    technicalNotes: [
      "Updated helper system prompt contract in `barkguide/reeprise/views.py` with explicit grounding rules for latest-turn focus, context-text anchoring, and ambiguity handling.",
      "Lowered helper completion temperature from `0.3` to `0.2` in `agent_answer` for more deterministic, less drifting output.",
      "Added backend regression test `test_agent_answer_passes_recent_messages_and_context_payload` in `barkguide/reeprise/tests.py`.",
      "Extended prompt regression assertions in `test_agent_answer_prompt_enforces_short_default_replies`.",
    ],
  },
  {
    id: "text-details-vertiefen-now-keeps-chat-visible-and-uses-an-expanding-input",
    timestamp: "2026-05-10T19:52:22+02:00",
    title:
      "Text details Vertiefen now keeps chat visible and uses an expanding input",
    summary:
      "The Vertiefen helper on text details now behaves more like a professional chat: the latest turns stay in view while sending, and the message field grows with longer questions.",
    userHighlights: [
      "After sending a message, the newest user and helper turns remain visible automatically.",
      "The input starts compact but expands for multi-line questions instead of staying one line.",
      "Follow-up context sent to the helper now includes a longer recent history window for better question continuity.",
    ],
    technicalNotes: [
      "Updated `src/components/LanguageHelperPanel.tsx` to auto-scroll the conversation container to the newest message on updates.",
      "Added textarea auto-resize behavior with a capped max height and adjusted input container alignment for multi-line typing.",
      "Increased helper payload continuity by sending the last six turns via `buildRecentMessagesForAgent` and expanded max context text length.",
      "Added frontend regression test `src/components/LanguageHelperPanel.test.ts` for recent-message payload slicing order and size.",
    ],
  },
  {
    id: "classic-tools-sidebar-group-now-bundles-classic-barkguide-shopping-list-and-vocabulary-list",
    timestamp: "2026-05-10T19:50:35+02:00",
    title:
      "Classic Tools sidebar group now bundles Classic Barkguide, Shopping List, and Vocabulary List",
    summary:
      "The app sidebar now groups Classic Barkguide, Shopping List, and Vocabulary List under one expandable Classic Tools folder so related utility pages stay together.",
    userHighlights: [
      "Classic utility pages now appear in one expandable section instead of separate top-level links.",
      "When you open one of these pages directly, the section opens automatically so the current location is easier to understand.",
      "Main navigation stays cleaner while keeping one-tap access to the same pages.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/route.tsx` to replace separate nav entries with a route-aware `Classic Tools` expandable section.",
      "Added the new translation key `sidenav_classic_tools` in `src/assets/translations.json`.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify the expandable nav section and navigation to `/app/vocabulary_trainer`.",
    ],
  },
  {
    id: "clarify-helper-now-keeps-replies-short-and-follows-german-input-reliably",
    timestamp: "2026-05-10T19:49:48+02:00",
    title:
      "Clarify helper now keeps replies shorter and follows German input reliably",
    summary:
      "The in-app Clarify helper now defaults to short answers and responds in German when your question is written in German, even if your default response language differs.",
    userHighlights: [
      "Helper answers now stay compact by default for faster scanning.",
      "When you switch to German mid-chat, the helper now answers in German more reliably.",
      "Existing add-to-vocabulary behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated helper system prompt in `barkguide/reeprise/views.py` to enforce short default responses (1-3 sentences).",
      "Added question-language detection for German in `agent_answer` so response language can follow the user turn dynamically.",
      "Hardened helper request shaping in `src/components/LanguageHelperPanel.tsx` so language-switch-only inputs no longer get sent as fake focus words from stale turns.",
      "Reduced helper completion budget (`max_tokens`) for tighter outputs.",
      "Added regression tests in `barkguide/reeprise/tests.py` and `src/components/LanguageHelperPanel.test.ts` covering German response-language switching, short-reply prompt constraints, and UI word-payload resolution.",
    ],
  },
  {
    id: "vocabulary-trainer-now-unlocks-pattern-rewards-after-structure-engagement-and-follow-up-success",
    timestamp: "2026-05-10T19:46:55+02:00",
    title:
      "Vocabulary trainer now unlocks pattern rewards after structure engagement and follow-up success",
    summary:
      "Vocabulary training now includes compact pattern unlock rewards that appear once when learners engage with structure and then successfully apply the pattern on a different card.",
    userHighlights: [
      "Pattern rewards now focus on reusable French structure insight rather than one-off word memorization.",
      "Unlocks only appear after meaningful structure engagement and a distinct follow-up success, preventing one-click reward spam.",
      "Rewards are compact and non-blocking, with calm copy and short examples.",
      "Cards without pattern metadata still behave exactly as before.",
    ],
    technicalNotes: [
      "Added central French pattern registry with `PatternUnlock` model and lookup helper in `src/features/patternUnlocks/patternUnlocks.ts`.",
      "Added local persistence/service APIs `getUnlockedPatternIds`, `isPatternUnlocked`, and `unlockPattern` in `src/features/patternUnlocks/patternUnlockStorage.ts`.",
      "Implemented unlock trigger orchestration in `src/features/patternUnlocks/patternUnlockTrigger.ts`, including distinct-card follow-up rules and once-only persistence.",
      "Added pattern ID detection/fallback mapping for existing vocabulary cards in `src/features/patternUnlocks/patternUnlockDetection.ts`.",
      "Integrated reward triggering and queue display into `src/components/VocabularyPreviewCard.tsx` using existing review completion flow and structure-engagement gating.",
      "Added compact reward UI component `src/components/PatternUnlockReward.tsx` and i18n copy in `src/assets/translations.json`.",
      "Added regression tests in `src/features/patternUnlocks/patternUnlockTrigger.test.ts` and `src/features/patternUnlocks/patternUnlockDetection.test.ts`.",
    ],
  },
  {
    id: "vocabulary-cards-now-add-compact-structure-explanations-for-pattern-learning",
    timestamp: "2026-05-10T19:34:32+02:00",
    title:
      "Vocabulary cards now add compact Struktur explanations for pattern learning",
    summary:
      "Vocabulary cards in both Trainer and Vocabulary List now keep quick translation as-is while adding an optional expandable Struktur section that explains base form, pattern, and contextual meaning when available, and new AI translations now populate these fields.",
    userHighlights: [
      "Quick translation remains the first visible answer for fast recall.",
      "A new expandable Struktur section adds short pattern-oriented context instead of long grammar text.",
      "Newly generated vocabulary cards can now include pattern-level breakdown fields such as form, pattern, and related examples.",
      "Cards without structure data still render exactly as before.",
    ],
    technicalNotes: [
      "Extended typed `translation_details` handling with optional `structure` fields in `src/utils/vocabularyTranslationDetails.ts` and `src/hooks/usePaginatedVocabularyList.ts`.",
      "Updated Trainer vocabulary card UI in `src/components/VocabularyPreviewCard.tsx` to render an expandable Struktur section with conditional fields only.",
      "Updated Vocabulary List desktop/mobile rows in `src/routes/app/vocabulary_progress.tsx` with the same Struktur expansion pattern and labels.",
      "Added new i18n keys for Struktur labels and toggles in `src/assets/translations.json`.",
      "Extended backend translation prompt and JSON parser in `barkguide/reeprise/tasks.py` to request, parse, normalize, and persist optional `structure` fields.",
      "Added backend regressions in `barkguide/reeprise/tests.py` covering prompt contract, structured payload parsing, and `generate_translation` persistence for structure metadata.",
      "Added central pattern unlock registry, detection, trigger, and persistent unlocked state in `src/features/patternUnlocks/` with one-time unlock behavior.",
      "Connected vocabulary trainer and vocabulary progress cards to trigger unlock rewards on Struktur expansion or card completion.",
      "Added non-blocking reward UI (`PatternUnlockReward`) and a new `/app/language_insights` screen to revisit unlocked patterns.",
      "Added regression coverage with a `sirotant` structure example fixture in `src/utils/vocabularyTranslationDetails.test.ts`.",
    ],
  },
  {
    id: "vocabulary-similar-word-differences-now-auto-reopen-after-first-generation",
    timestamp: "2026-05-10T19:29:01+02:00",
    title:
      "Vocabulary difference panel now reopens automatically after first generation",
    summary:
      "After you generate word-difference guidance once on a vocabulary card, the same card now opens that guidance automatically on future visits without an extra click.",
    userHighlights: [
      "First-time behavior stays unchanged: generation still starts only after tapping `Unterschiede lernen`.",
      "Once generated, the same word-difference content appears automatically when you revisit that vocabulary card.",
      "If no cached result exists yet, nothing auto-generates in the background.",
    ],
    technicalNotes: [
      "Extended `VocabularySimilarWordsExplainerRequestSerializer` in `barkguide/reeprise/serializer.py` with `only_cached`.",
      "Updated `similar_words_explainer` action in `barkguide/reeprise/views.py` to return `204` on cache miss when `only_cached=true`, preventing unintended generation.",
      "Added backend regression test `test_similar_words_explainer_only_cached_does_not_generate_on_cache_miss` in `barkguide/reeprise/tests.py`.",
      "Updated `src/hooks/useVocabularySimilarWordsExplainer.ts` to support cache-only fetches and `204` handling.",
      "Added auto-open cache lookup flow in `src/components/VocabularyPreviewCard.tsx` using cache-only mode so persisted explainers reopen without user click.",
    ],
  },
  {
    id: "quick-translate-moved-into-tools-navigation-with-a-smaller-homepage-entry-point",
    timestamp: "2026-05-09T20:25:01+02:00",
    title:
      "Übersetzen unterwegs moved into Tools navigation with a smaller homepage entry point",
    summary:
      "Übersetzen unterwegs no longer occupies a large card on the main page and now lives in a dedicated Tools section, while remaining quickly accessible from the homepage via a compact link.",
    userHighlights: [
      "The main page now stays focused on daily reading and vocabulary flow.",
      "Übersetzen unterwegs is still one tap away through the new Tools section in side navigation.",
      "A compact homepage Tools link keeps the feature discoverable without taking major visual space.",
    ],
    technicalNotes: [
      "Updated side navigation in `src/routes/app/route.tsx` to add a Tools section and a direct `/app/quick_translate` entry.",
      "Replaced the large quick-translate utility card on `src/routes/app/reeprise/index.tsx` with compact Tools links for desktop and mobile homepage layouts.",
      "Updated UI regression coverage in `e2e/quick-translate-language-space.spec.ts` to validate navigation access and language-space-aware target labeling on `/app/quick_translate`.",
    ],
  },
  {
    id: "generated-texts-now-reliably-process-in-local-dev-after-celery-startup-hardening",
    timestamp: "2026-05-09T20:24:15+02:00",
    title:
      "Generated texts now reliably process in local development after Celery startup hardening",
    summary:
      "Text generation no longer gets stuck in pending state during local development due to a fragile Celery CLI startup path.",
    userHighlights: [
      "Newly generated texts now complete again in local development instead of waiting indefinitely.",
      "Background generation services recover more reliably from environment-specific CLI path differences.",
      "Generated-text testing in development is now stable across sessions.",
    ],
    technicalNotes: [
      "Updated `scripts/develop.sh` to start Celery worker and beat via `uv run python -m celery ...` instead of `uv run celery ...`.",
      "This removes dependency on external script shim resolution and keeps execution bound to the active project interpreter.",
      "Validated pipeline behavior by checking local generation flow and background processing logs after startup.",
    ],
  },
  {
    id: "daily-texts-now-prioritize-words-you-engaged-with-via-mnemonics-and-grammar",
    timestamp: "2026-05-09T20:18:00+02:00",
    title:
      "Daily texts now prioritize words you engaged with via mnemonics and grammar",
    summary:
      "Words you actively revisited through mnemonic generation or grammar review now surface sooner in daily reinforcement text generation.",
    userHighlights: [
      "Daily texts now more quickly reuse words you just spent focused attention on.",
      "Generating a mnemonic for a word increases its near-term chance of appearing in reinforcement text.",
      "Reading the grammar section of a text boosts that text’s vocabulary for upcoming daily practice.",
    ],
    technicalNotes: [
      "Added backend `VocabularyEngagementSignal` tracking model in `barkguide/reeprise/models.py` with migration `0086_vocabularyengagementsignal.py`.",
      "Recorded engagement signals on `generate_mnemonic` and `grammar_lesson` actions in `barkguide/reeprise/views.py`.",
      "Updated `select_struggle_practice_vocabularies()` ranking in `barkguide/reeprise/tasks.py` to prioritize recent engagement signals and added regression coverage in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "vocabulary-now-supports-guess-first-reveal-cards-in-list-and-trainer",
    timestamp: "2026-05-09T20:18:52+02:00",
    title:
      "Vocabulary now supports guess-first reveal cards in list and trainer",
    summary:
      "Vocabulary cards now keep translations blurred by default so learners can guess from context first, then reveal or hide the answer on demand.",
    userHighlights: [
      "New words in vocabulary list now show a clear guess prompt before translation reveal.",
      "Translation reveal uses one tap and can be hidden again for a second recall pass.",
      "The same reveal pattern is available in vocabulary trainer cards for consistent practice flow.",
    ],
    technicalNotes: [
      "Added per-vocabulary reveal state and reveal/hide controls in `src/routes/app/vocabulary_progress.tsx` for both desktop table and mobile cards.",
      "Updated `src/routes/app/vocabulary_training_mode.tsx` with guess-first translation reveal controls and trainer hint copy.",
      "Added new translation keys in `src/assets/translations.json` for reveal actions and accessibility labels.",
    ],
  },
  {
    id: "speaking-long-sentence-turn-detection-now-adds-extra-pause-patience-to-avoid-mid-thought-cutoffs",
    timestamp: "2026-05-10T02:42:00+02:00",
    title:
      "Speaking long-sentence turn detection now adds extra pause patience to avoid mid-thought cutoffs",
    summary:
      "Turn-end timing now scales with utterance length, so longer answers get more pause tolerance before auto-send and are less likely to be interrupted mid-sentence.",
    userHighlights: [
      "Longer spoken answers can include natural clause pauses without immediate cutoff.",
      "Fast mode remains quick for short turns but is calmer on long turns.",
      "Balanced and patient modes now protect long-form speech even more.",
    ],
    technicalNotes: [
      "Added word-count-based long-sentence timing buffers in `src/routes/app/dialogue.tsx` for both silence timer and onend grace timer.",
      "Timing now adds extra delay tiers at 10+, 14+, and 20+ words to reduce premature auto-send on long utterances.",
    ],
  },
  {
    id: "speaking-desktop-audio-now-targets-default-browser-output-to-avoid-unwanted-iphone-routing",
    timestamp: "2026-05-10T02:28:00+02:00",
    title:
      "Speaking desktop audio now targets default browser output to avoid unwanted iPhone routing",
    summary:
      "On desktop, speaking playback now explicitly targets the browser’s default output device where supported, reducing unwanted route attempts to external devices like iPhone.",
    userHighlights: [
      "Desktop voice playback now prefers local default output device.",
      "Fewer unexpected output-route switches during assistant playback.",
      "More predictable behavior when multiple Apple devices are nearby.",
    ],
    technicalNotes: [
      "Added desktop detection in `src/routes/app/dialogue.tsx`.",
      'Applied `audio.setSinkId("default")` before playback on supported browsers in `src/routes/app/dialogue.tsx`.',
      "Added telemetry events for sink routing success/failure (`tts_sink_default_applied`, `tts_sink_default_failed`).",
    ],
  },
  {
    id: "speaking-iphone-default-now-disables-auto-voice-to-avoid-annoying-output-route-reconnects",
    timestamp: "2026-05-10T02:18:00+02:00",
    title:
      "Speaking on iPhone now defaults to manual voice playback to avoid output-route reconnect annoyances",
    summary:
      "To reduce disruptive iPhone audio-route reconnect behavior, speaking mode now defaults auto-voice to off on first iOS use and adds an explicit auto-voice toggle in settings.",
    userHighlights: [
      "iPhone no longer auto-plays every assistant voice turn by default on first use.",
      "You can still enable automatic voice anytime via settings.",
      "A dedicated iOS hint explains why manual mode may feel better.",
    ],
    technicalNotes: [
      "Added persisted `speaking-auto-voice-v1` toggle in `src/routes/app/dialogue.tsx`.",
      "Added first-use iOS detection and default opt-out for auto voice in `src/routes/app/dialogue.tsx`.",
      "Gated automatic reply/welcome/error playback behind auto-voice setting and added new translation keys in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-hands-free-mic-now-restarts-immediately-after-assistant-audio-ends",
    timestamp: "2026-05-10T01:16:00+02:00",
    title:
      "Speaking hands-free mic now restarts immediately after assistant audio ends",
    summary:
      "Hands-free speaking now removes the extra dead-air delay after assistant voice playback, so users can answer immediately when the question finishes.",
    userHighlights: [
      "Microphone reopens right after voice playback ends in hands-free mode.",
      "Less silent waiting between assistant question and user response.",
      "Conversation pacing feels closer to a natural live call rhythm.",
    ],
    technicalNotes: [
      "Removed delayed post-audio mic restart timeout in `src/routes/app/dialogue.tsx` (`audio.onended`).",
      "Reduced backup hands-free restart effect delay from `300ms` to `60ms` in `src/routes/app/dialogue.tsx`.",
    ],
  },
  {
    id: "speaking-mother-tongue-rescue-now-detects-more-help-phrases-and-onend-keeps-interim-transcripts",
    timestamp: "2026-05-10T00:52:00+02:00",
    title:
      "Speaking rescue mode now detects more mother-tongue help phrases and keeps interim transcript turns",
    summary:
      "Mother-tongue rescue detection now reacts to a broader set of real help phrases, and speech turns are no longer dropped when a browser ends recognition without finalized chunks.",
    userHighlights: [
      "Help phrases like `auf Deutsch`, `in English`, `je ne comprends pas`, and similar now trigger rescue behavior more reliably.",
      "Recognition-end events now keep valid interim transcript turns instead of silently losing them.",
      "Switching briefly to mother tongue for clarification should feel more immediate and less ignored.",
    ],
    technicalNotes: [
      "Expanded rescue phrase detection in `barkguide/reeprise/views.py` control-intent extraction.",
      "Expanded frontend rescue utterance recognition patterns in `src/routes/app/dialogue.tsx` for better auto-mode transcript candidate scoring.",
      "Updated `onend` turn-send logic in `src/routes/app/dialogue.tsx` to allow interim transcript fallback when final chunks are absent.",
    ],
  },
  {
    id: "speaking-safari-autoplay-block-now-shows-explicit-one-tap-voice-recovery-instead-of-silent-welcome-fail",
    timestamp: "2026-05-10T00:42:00+02:00",
    title:
      "Speaking autoplay blocks now show explicit one-tap voice recovery instead of silent welcome failure",
    summary:
      "When browser autoplay rules block audio playback (common on Safari), speaking mode now always surfaces a clear recovery message and keeps a one-tap replay action ready.",
    userHighlights: [
      "No silent welcome-audio failure when autoplay is blocked.",
      "A clear message explains what happened and how to continue.",
      "One tap on retry starts the pending voice immediately.",
    ],
    technicalNotes: [
      "Updated `speakText(...)` in `src/routes/app/dialogue.tsx` to detect autoplay block errors (`NotAllowedError` / `AbortError`) and always show recovery UI, even when welcome uses suppress-error mode.",
      "Added pending replay buffer state in `src/routes/app/dialogue.tsx` so retry replays the exact blocked text.",
      "Added translation key `speaking_practice_tts_autoplay_blocked` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-turn-end-now-requires-brief-final-transcript-stability-before-auto-send",
    timestamp: "2026-05-10T00:34:00+02:00",
    title:
      "Speaking turn-end now requires brief final-transcript stability before auto-send",
    summary:
      "To improve interruption/speed balance, speaking turn-end now waits for a short final-transcript stability window before auto-send, with stronger protection for short beginner utterances.",
    userHighlights: [
      "Fast mode remains responsive but is less likely to cut off a still-forming short answer.",
      "Balanced/patient modes now better protect natural thinking pauses.",
      "Auto-send timing now uses both silence and transcript stability, not silence alone.",
    ],
    technicalNotes: [
      "Added `finalTranscriptLastChangedAtRef` tracking in `src/routes/app/dialogue.tsx`.",
      "Added adaptive `computeFinalStabilityMs(...)` and enforced it in both silence-timer and `onend` auto-send paths.",
      "Stability threshold is stricter for short utterances and calmer modes to reduce premature cutoff risk.",
    ],
  },
  {
    id: "speaking-browser-hardening-adds-firefox-type-first-fallback-and-voice-debug-telemetry",
    timestamp: "2026-05-10T00:24:00+02:00",
    title:
      "Speaking browser hardening adds Firefox type-first fallback and voice debug telemetry",
    summary:
      "Speaking mode now handles browser capability differences more explicitly: unsupported mic flows are gated cleanly, Firefox gets clear type-first guidance, and voice/mic failure telemetry is logged for faster debugging.",
    userHighlights: [
      "If live mic dictation is unavailable, the mic start control is disabled instead of failing silently.",
      "Firefox now shows explicit guidance that typing is the stable path there.",
      "Voice/mic issues are easier to diagnose through structured telemetry logs.",
    ],
    technicalNotes: [
      "Added browser capability fallback behavior in `src/routes/app/dialogue.tsx` with mic-action disablement when STT API is unavailable.",
      "Added Firefox-specific support hint copy via `speaking_practice_mic_browser_hint_firefox` in `src/assets/translations.json`.",
      "Added client telemetry events in `src/routes/app/dialogue.tsx` for mic permission state, STT constructor/API failures, STT errors/end, and TTS playback failures.",
    ],
  },
  {
    id: "speaking-entry-now-plays-welcome-voice-immediately-on-open-even-before-mic-start",
    timestamp: "2026-05-10T00:14:00+02:00",
    title:
      "Speaking entry now plays welcome voice immediately on open, even before mic start",
    summary:
      "Opening speaking mode now starts the welcome voice directly during entry initialization, so users hear the first turn immediately without needing to start the microphone first.",
    userHighlights: [
      "Welcome voice starts right when the speaking overlay opens.",
      "Microphone state no longer gates first-turn voice playback.",
      "Entry feels more like an immediate real conversation start.",
    ],
    technicalNotes: [
      "Moved first-turn speech trigger into the same initialization path that creates the first assistant message in `src/routes/app/dialogue.tsx`.",
      "Removed the delayed follow-up welcome speech effect to reduce startup latency and avoid sequencing lag.",
    ],
  },
  {
    id: "speaking-latency-tuning-now-starts-faster-and-responds-faster-after-user-turns",
    timestamp: "2026-05-10T00:06:00+02:00",
    title:
      "Speaking latency tuning now starts faster and responds faster after user turns",
    summary:
      "Speaking mode now reduces wait time both at session start and after user turns by tuning turn-end timers and shrinking reply token budget.",
    userHighlights: [
      "Faster handoff from your speech to assistant reply in balanced and fast pause modes.",
      "Quicker startup in hands-free flow when microphone permission is already granted.",
      "Replies stay concise, which reduces average server response latency.",
    ],
    technicalNotes: [
      "Adjusted pause and end-grace timing profiles in `src/routes/app/dialogue.tsx` for faster balanced/fast response behavior.",
      "Added microphone permission-state detection via `navigator.permissions` in `src/routes/app/dialogue.tsx` to enable quicker auto-start behavior when permission is pre-granted.",
      "Reduced speaking reply completion budget in `barkguide/reeprise/views.py` from 350 to 220 max tokens (including retry path).",
    ],
  },
  {
    id: "speaking-auto-turn-end-now-waits-for-stable-final-transcript-to-avoid-user-interruption",
    timestamp: "2026-05-09T23:58:00+02:00",
    title:
      "Speaking auto turn-end now waits for stable final transcript to avoid interrupting the user",
    summary:
      "Hands-free turn detection now waits longer and prefers stable final speech recognition results, reducing premature cutoffs while the learner is still thinking or speaking.",
    userHighlights: [
      "Short pauses are now treated more patiently before ending your turn.",
      "Auto-send avoids firing on unstable interim transcript fragments.",
      "Unexpected recognition-end flow now sends only finalized speech content.",
    ],
    technicalNotes: [
      "Raised pause and end-grace thresholds in `src/routes/app/dialogue.tsx` across fast/balanced/patient profiles.",
      "Updated silence timeout logic to require stable final transcript for short/interim-only input before auto-send.",
      "Updated `onend` handling to auto-send only when final transcript is present, avoiding interruption from partial interim text.",
    ],
  },
  {
    id: "speaking-repair-mode-now-forces-correction-repeat-before-any-topic-follow-up",
    timestamp: "2026-05-09T23:52:00+02:00",
    title:
      "Speaking repair mode now forces correction repeat before any topic follow-up",
    summary:
      "When learner input needs repair, speaking mode now blocks premature topic continuation and always asks for one corrected repeat first.",
    userHighlights: [
      "No immediate jump to new questions after a correction suggestion.",
      "Repair turns now stay focused on what you just tried to say.",
      "If correction is available, the assistant asks for one repeat before continuing.",
    ],
    technicalNotes: [
      "Added server-side repair-focus enforcement in `barkguide/reeprise/views.py` so reply shape is normalized to `correction + repeat request` even when model output drifts.",
      "Added regression test `test_speaking_practice_reply_repair_focus_enforces_repeat_before_topic_shift` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-stt-now-avoids-duplicate-transcript-chunks-and-repair-mode-stays-question-anchored",
    timestamp: "2026-05-09T23:44:00+02:00",
    title:
      "Speaking transcript now avoids duplicate chunks, and repair mode stays anchored to the active question",
    summary:
      "Voice transcript assembly is now more stable during continuous speech, and correction behavior in repair mode now stays tied to the immediate question context instead of drifting to unrelated words.",
    userHighlights: [
      "Repeated transcript fragments from live dictation are reduced significantly.",
      "If the assistant asked for weekday preference, correction proposals now stay in weekday space.",
      "When uncertain, the assistant is instructed to clarify briefly instead of inventing unrelated nouns.",
    ],
    technicalNotes: [
      "Refactored STT result handling in `src/routes/app/dialogue.tsx` to build final transcript deterministically by result index instead of re-appending finalized chunks.",
      "Added light duplicate-token compaction before sending user turns in `src/routes/app/dialogue.tsx`.",
      "Extended speaking backend in `barkguide/reeprise/views.py` with `expected_answer_hint` inferred from the last assistant turn.",
      "Updated repair-focus prompt policy to constrain corrections to contextual answer sets (e.g., weekdays) and prefer clarification over unrelated substitutions.",
      "Added regression test `test_speaking_practice_reply_weekday_hint_restricts_repair_corrections` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-cue-now-respects-focus-flow-and-idle-time-with-user-controlled-intensity",
    timestamp: "2026-05-09T23:18:00+02:00",
    title:
      "Speaking cue now respects focus flow and idle time with user-controlled intensity",
    summary:
      "The speaking entry cue now avoids interrupting active learning flows, appears only after idle time, and can be tuned or snoozed from speaking settings.",
    userHighlights: [
      "Cue pulse is suppressed while you are actively in reading/trainer/speaking flows.",
      "Pulse only appears after short idle time, reducing interruptions during concentrated work.",
      "In speaking settings, you can set cue intensity (`Off`, `Subtle`, `Prominent`) and snooze cue pulses for the current session.",
    ],
    technicalNotes: [
      "Added idle-based and route-based cue gating in `src/routes/app/route.tsx` with session snooze awareness.",
      "Added prominent pulse variant style `nav-speaking-entry-pulse--prominent` in `src/index.css`.",
      "Added cue controls to speaking settings in `src/routes/app/dialogue.tsx` backed by local storage key `speaking-cue-mode-v1` and session key `speaking-cue-snooze-session-v1`.",
      "Added new translation keys for cue mode and session snooze labels in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-trigger-open-now-starts-with-progress-aware-word-intro-instead-of-generic-opening",
    timestamp: "2026-05-09T22:40:00+02:00",
    title:
      "Speaking trigger open now starts with progress-aware word intro instead of generic opening",
    summary:
      "When speaking is opened from a trigger cue, the first assistant turn now explicitly reflects the learner event (forgot, knew, mnemonic, new word) and starts immediate word-focused practice.",
    userHighlights: [
      "Triggered opens now clearly reference the exact word and why it was suggested.",
      "If you knew a word, the assistant acknowledges progress and offers a challenge.",
      "If you forgot a word, the assistant starts with usage help before topic drift.",
    ],
    technicalNotes: [
      "Extended trigger reason set with `known_word` in `src/hooks/useSpeakingTriggers.ts`.",
      "Wired trainer `known` result into trigger queue in `src/components/VocabularyPreviewCard.tsx`.",
      "Updated speaking startup in `src/routes/app/dialogue.tsx` to consume trigger context robustly and generate reason-aware first-turn copy.",
    ],
  },
  {
    id: "speaking-repair-mode-now-asks-for-one-correct-repeat-before-continuing-topic",
    timestamp: "2026-05-09T22:31:00+02:00",
    title:
      "Speaking repair mode now asks for one correct repeat before continuing topic",
    summary:
      "When the learner sentence needs correction, speaking repair mode now asks for one corrected repeat first and avoids immediate topic switching.",
    userHighlights: [
      "After a mistake, the assistant now keeps focus on the exact sentence.",
      "Learners are prompted to repeat the corrected version once.",
      "New topic questions are delayed until the repair step is completed.",
    ],
    technicalNotes: [
      "Updated repair-focus prompt policy in `barkguide/reeprise/views.py` to require one corrected repeat before continuation.",
      "Added regression assertions in `barkguide/reeprise/tests.py` to lock the new repair-mode prompt contract.",
    ],
  },
  {
    id: "speaking-trigger-queue-now-opens-word-focused-conversations-from-real-learning-events",
    timestamp: "2026-05-09T22:08:00+02:00",
    title:
      "Speaking trigger queue now opens word-focused conversations from real learning events",
    summary:
      "Speaking cues are now tied to real vocabulary learning events and open the conversation with concrete word context instead of generic small talk.",
    userHighlights: [
      "Cue can be triggered by `Didn't know`, saved mnemonic, and newly added words.",
      "Opening speaking from the navbar now carries focused word context into the session.",
      "First assistant turn starts on that word and mnemonic context when available.",
    ],
    technicalNotes: [
      "Added shared speaking-trigger queue/state module in `src/hooks/useSpeakingTriggers.ts` with scoring, dedup, and timed cue consumption.",
      "Wired trigger production in `src/components/VocabularyPreviewCard.tsx` and `src/hooks/useVocabularyCreate.ts`.",
      "Updated navbar entry in `src/routes/app/route.tsx` to consume trigger cue and start speaking with active trigger context.",
      "Extended speaking request payload in frontend and backend (`focus_word`, `focus_mnemonic`, `trigger_reason`) and integrated into `barkguide/reeprise/views.py` prompt/payload.",
    ],
  },
  {
    id: "speaking-navbar-entry-now-pulses-periodically-to-signal-conversation-start",
    timestamp: "2026-05-09T21:52:00+02:00",
    title:
      "Speaking navbar entry now pulses periodically to signal conversation start",
    summary:
      "The speaking entry icon in the top navbar now uses a subtle periodic pulse to improve discoverability and invite quick conversation starts.",
    userHighlights: [
      "The speaking entry is easier to notice without being constantly distracting.",
      "Pulse timing feels like a lightweight call-to-talk cue.",
      "Users can react and jump into conversation from anywhere in the app.",
    ],
    technicalNotes: [
      "Added `nav-speaking-entry-pulse` class on the speaking trigger in `src/routes/app/route.tsx`.",
      "Implemented periodic halo pulse animation in `src/index.css` with long idle interval and short active burst.",
    ],
  },
  {
    id: "speaking-text-input-is-now-collapsible-and-opens-on-demand-from-write-trigger",
    timestamp: "2026-05-09T21:44:00+02:00",
    title:
      "Speaking text input is now collapsible and opens on demand from write trigger",
    summary:
      "The speaking text field is no longer permanently expanded. Users can open writing only when needed via a compact write trigger.",
    userHighlights: [
      "Voice-first layout stays cleaner during normal conversation.",
      "Writing is still available instantly via a dedicated button.",
      "Input can be closed again to keep focus on speaking turns.",
    ],
    technicalNotes: [
      "Added `isTextInputOpen` state in `src/routes/app/dialogue.tsx` to toggle textarea visibility.",
      "Added open/close writing controls and guarded submit enablement when input is collapsed.",
      "Added translation keys `speaking_practice_write_open` and `speaking_practice_close_write` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-transcript-now-auto-scrolls-to-latest-turn-and-stays-visible-above-input",
    timestamp: "2026-05-09T21:36:00+02:00",
    title:
      "Speaking transcript now auto-scrolls to latest turn and stays visible above input",
    summary:
      "The speaking transcript viewport now keeps the latest conversation turn visible, preventing new messages from appearing hidden near the input section.",
    userHighlights: [
      "Latest assistant and user turns remain visible while chatting.",
      "Transcript no longer feels clipped behind the input area.",
      "Long conversations stay readable without manual scroll correction each turn.",
    ],
    technicalNotes: [
      "Added transcript viewport ref and auto-scroll effect in `src/routes/app/dialogue.tsx` on message updates.",
      "Added extra bottom spacing in transcript viewport to keep last bubble visually clear above controls.",
    ],
  },
  {
    id: "speaking-now-uses-repair-first-tutoring-and-auto-mother-tongue-rescue-when-user-struggles",
    timestamp: "2026-05-09T21:29:00+02:00",
    title:
      "Speaking now uses repair-first tutoring and auto mother-tongue rescue when user struggles",
    summary:
      "Speaking practice now keeps focus on correcting and clarifying the learner’s intended sentence before switching topics, and can proactively provide one short mother-tongue rescue when the learner is clearly stuck.",
    userHighlights: [
      "The assistant no longer jumps to a new topic immediately after a mistake.",
      "When input is very short/struggling, it stays on the same idea and asks for a retry.",
      "If the learner is clearly lost, it can switch briefly to mother tongue automatically to help.",
    ],
    technicalNotes: [
      "Added support-mode inference in `barkguide/reeprise/views.py` (`normal` / `repair_focus` / `rescue_mt`).",
      "Extended speaking prompt policy to enforce repair-first behavior before topic transitions.",
      "Added regression coverage in `barkguide/reeprise/tests.py` for repair-focus mode activation.",
    ],
  },
  {
    id: "speaking-input-placeholder-now-uses-clear-speak-or-type-guidance",
    timestamp: "2026-05-09T21:22:00+02:00",
    title: "Speaking input placeholder now uses clear speak-or-type guidance",
    summary:
      "The speaking input placeholder copy now clearly communicates the intended behavior: speak naturally, and type only when speaking is not possible.",
    userHighlights: [
      "Placeholder no longer sounds like a pure writing task.",
      "Copy now reflects voice-first conversation behavior.",
      "Users get a clear fallback path when they cannot speak.",
    ],
    technicalNotes: [
      "Updated `speaking_practice_input_placeholder` translations in `src/assets/translations.json`.",
      "German and English copy now match the agreed product wording.",
    ],
  },
  {
    id: "added-missing-close-button-translation-key-for-speaking-overlay",
    timestamp: "2026-05-09T21:18:00+02:00",
    title: "Added missing close button translation key for speaking overlay",
    summary:
      "The speaking overlay close action now uses a valid translation key instead of falling back to raw key text.",
    userHighlights: [
      "Close action now shows proper localized text.",
      "No raw `close_button` key appears in the UI anymore.",
      "Overlay header copy is now consistent with the rest of the app translations.",
    ],
    technicalNotes: [
      "Added `close_button` entry to `src/assets/translations.json` with German and English values.",
      'Existing `translate("close_button")` usage in `src/routes/app/dialogue.tsx` now resolves correctly.',
    ],
  },
  {
    id: "speaking-debug-panel-now-shows-learning-language-mother-tongue-and-mic-locale",
    timestamp: "2026-05-09T21:12:00+02:00",
    title:
      "Speaking debug panel now shows learning language, mother tongue, and mic locale",
    summary:
      "A dedicated debug area in speaking mode now shows the effective language context so language-routing issues can be diagnosed directly in the UI.",
    userHighlights: [
      "You can now see which learning language is active for the reply.",
      "You can now see which mother tongue context is being used.",
      "You can now see the active microphone recognition locale.",
    ],
    technicalNotes: [
      "Extended speaking reply API response in `barkguide/reeprise/views.py` with `mother_tongue` metadata.",
      "Updated `src/hooks/useSpeakingPracticeReply.ts` response typing to include `mother_tongue`.",
      "Added debug language-context block in `src/routes/app/dialogue.tsx` plus translation keys in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-now-interprets-french-shorthand-like-sava-and-avoids-greeting-loop-restarts",
    timestamp: "2026-05-09T21:05:00+02:00",
    title:
      "Speaking now interprets French shorthand like `sava` and avoids greeting-loop restarts",
    summary:
      "Speaking replies now handle short learner shorthand such as `sava` as natural French continuation and prevent repetitive re-greeting patterns after the first turn.",
    userHighlights: [
      "Short input like `sava` is interpreted as `ça va` instead of derailing the turn.",
      "Conversation no longer restarts with another greeting after you already answered.",
      "Follow-up turns now move forward naturally with concise continuation prompts.",
    ],
    technicalNotes: [
      "Added learner-message normalization for French shorthand in `barkguide/reeprise/views.py`.",
      "Updated speaking system prompt to explicitly forbid greeting restarts after turn one.",
      "Added regression test in `barkguide/reeprise/tests.py` to verify shorthand normalization and prompt contract.",
    ],
  },
  {
    id: "speaking-entry-now-uses-professional-navbar-trigger-and-opens-as-overlay-layer",
    timestamp: "2026-05-09T19:24:00+02:00",
    title:
      "Speaking entry now uses professional navbar trigger and opens as overlay layer",
    summary:
      "Speaking practice now opens as an overlay-style layer on mobile and desktop, and the navbar trigger uses a cleaner professional visual marker.",
    userHighlights: [
      "Speaking starts from the top navigation without a full-page feel.",
      "Conversation opens in a focused overlay layer with soft aura background.",
      "Navbar entry is now a professional voice marker instead of smiley-style icon.",
    ],
    technicalNotes: [
      "Updated top-navbar speaking trigger markup in `src/routes/app/route.tsx` to a professional waveform-style marker.",
      "Refactored `src/routes/app/dialogue.tsx` container into fixed full-screen overlay layout with backdrop and close control.",
      "Added history-aware close behavior to return users smoothly to their previous app context.",
    ],
  },
  {
    id: "speaking-now-supports-continuous-hands-free-conversation-after-initial-mic-permission",
    timestamp: "2026-05-09T19:15:00+02:00",
    title:
      "Speaking now supports continuous hands-free conversation after initial mic permission",
    summary:
      "Speaking mode can now run as a continuous hands-free conversation loop after the user grants microphone permission once.",
    userHighlights: [
      "After initial mic permission, the app can continue listening without repeated taps.",
      "When voice playback ends, listening resumes automatically in hands-free mode.",
      "Hands-free mode can be toggled on or off in settings.",
    ],
    technicalNotes: [
      "Added `handsFreeMode` state and automatic listen restart flow in `src/routes/app/dialogue.tsx`.",
      "Stored microphone permission readiness in runtime state and used it to gate auto-restart behavior.",
      "Added translation keys for hands-free on/off labels in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-voice-error-panel-no-longer-shows-permanently-on-auto-welcome",
    timestamp: "2026-05-09T19:07:00+02:00",
    title:
      "Speaking voice error panel no longer shows permanently on auto-welcome",
    summary:
      "The voice error panel now avoids false-positive display on automatic welcome playback, so users do not see a persistent failure state when opening speaking mode.",
    userHighlights: [
      "No constant `Sprachausgabe konnte nicht starten` message on every open.",
      "Welcome can fail silently without blocking the conversation UI.",
      "Voice error panel still appears when voice playback actually fails during normal conversation use.",
    ],
    technicalNotes: [
      "Removed aggressive TTS timeout race in `src/routes/app/dialogue.tsx` to prevent unnecessary timeout errors.",
      "Added `suppressErrorUi` option to `speakText` and used it for auto-welcome playback.",
      "Kept regular voice-failure error handling for user-triggered/reply playback flows.",
    ],
  },
  {
    id: "speaking-voice-failure-now-shows-clear-status-panel-instead-of-browser-fallback-voice",
    timestamp: "2026-05-09T19:02:00+02:00",
    title:
      "Speaking voice failure now shows clear status panel instead of browser fallback voice",
    summary:
      "When voice playback cannot start, speaking mode now shows a clear status panel with next steps instead of switching to browser fallback voices.",
    userHighlights: [
      "No fallback to low-quality browser voices.",
      "If voice fails, users see what happened and what to do next.",
      "One-tap retry is available directly in the status panel.",
    ],
    technicalNotes: [
      "Removed browser `speechSynthesis` fallback path from `src/routes/app/dialogue.tsx`.",
      "Added timeout-specific voice error handling and a dedicated in-screen recovery panel.",
      "Added new translation keys for voice status title, guidance steps, and retry action in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-french-first-welcome-now-uses-short-natural-conversation-tone",
    timestamp: "2026-05-09T18:56:00+02:00",
    title:
      "Speaking French first welcome now uses short natural conversation tone",
    summary:
      "The first French welcome line in speaking mode is now shorter and more natural, matching how people typically open a casual conversation.",
    userHighlights: [
      "French welcome now sounds like a real person, not a teacher prompt.",
      "Opening line is shorter, so conversation starts faster and feels lighter.",
      "First-turn tone better matches the product’s natural speaking vibe.",
    ],
    technicalNotes: [
      "Updated French first-turn welcome in `src/routes/app/dialogue.tsx` to `Salut, ça va ?`.",
      "Aligned route fallback welcome string to the same shorter French phrasing.",
    ],
  },
  {
    id: "speaking-entry-voice-now-starts-faster-with-openai-timeout-fallback",
    timestamp: "2026-05-09T18:53:00+02:00",
    title:
      "Speaking entry voice now starts faster with OpenAI timeout fallback",
    summary:
      "Voice start on entering speaking mode is now faster by adding a short OpenAI TTS timeout and immediate browser-voice fallback when network TTS is slow.",
    userHighlights: [
      "Initial voice response starts faster when opening speaking mode.",
      "Slow network TTS no longer causes long silent waits before playback.",
      "Conversation can begin smoothly even when remote TTS is delayed.",
    ],
    technicalNotes: [
      "Updated `speakText` in `src/routes/app/dialogue.tsx` to race OpenAI TTS fetch against a short timeout (`900ms`).",
      "Added immediate browser `speechSynthesis` fallback when timeout/fetch fails, instead of waiting for delayed remote audio.",
      "Kept OpenAI TTS as primary path when it responds quickly.",
    ],
  },
  {
    id: "speaking-auto-mic-now-prioritizes-german-mother-tongue-rescue-phrases-before-learning-language-misrecognition",
    timestamp: "2026-05-09T18:49:00+02:00",
    title:
      "Speaking auto-mic now prioritizes German mother-tongue rescue phrases before learning-language misrecognition",
    summary:
      "Auto microphone recognition now better preserves German rescue/help phrases instead of turning them into made-up learning-language text when users briefly switch to mother tongue.",
    userHighlights: [
      "In auto mode, German rescue requests are now recognized more reliably.",
      "Short help turns like `was heißt ...` and `ich verstehe nicht` are less likely to be misheard as learning-language text.",
      "Mother-tongue interruptions now route more cleanly into the intended rescue behavior.",
    ],
    technicalNotes: [
      "Extended speech-recognition typing in `src/routes/app/dialogue.tsx` to evaluate multiple STT alternatives.",
      "Added auto-mode candidate scoring to prefer likely German rescue transcripts and better language-fit alternatives.",
      "Set `recognition.maxAlternatives = 5` and aligned auto-mode locale default with German app language for faster mother-tongue capture.",
    ],
  },
  {
    id: "speaking-ship-hardening-adds-backend-rate-limits-and-updated-e2e-flow-coverage",
    timestamp: "2026-05-07T21:36:02+02:00",
    title:
      "Speaking ship hardening adds backend rate limits and updated E2E flow coverage",
    summary:
      "Final hardening for speaking practice adds endpoint-level rate limits and aligns E2E coverage with the simplified primary flow plus settings-zone behavior.",
    userHighlights: [
      "Speaking API usage is better protected against burst misuse.",
      "Conversation entry path remains stable with updated regression coverage.",
      "Feature planning/spec documentation now reflects shipped behavior and guardrails.",
    ],
    technicalNotes: [
      "Added speaking throttle classes in `barkguide/reeprise/views.py` and configured scope rates in `barkguide/barkguide/settings.py`.",
      "Updated `barkguide-ui/e2e/speaking-practice.spec.ts` assertions to match current route and settings-panel interaction model.",
      "Rewrote `planning-speaking-practice-conversation-adaptive.md` as final ship-plan documentation with implementation/status checklist.",
    ],
  },
  {
    id: "speaking-audio-and-auto-welcome-now-run-only-on-active-dialogue-route",
    timestamp: "2026-05-07T21:27:26+02:00",
    title:
      "Speaking audio and auto-welcome now run only on active dialogue route",
    summary:
      "Speaking voice playback and welcome-start behavior are now route-scoped, preventing accidental conversation audio while users are on other app pages.",
    userHighlights: [
      "No speaking welcome or voice output is triggered on the main page anymore.",
      "Auto-conversation behavior only activates while `/app/dialogue` is actually open.",
      "Mic start actions are ignored outside the speaking route for safety.",
    ],
    technicalNotes: [
      "Added route-activity guard using router state in `src/routes/app/dialogue.tsx`.",
      "Gated welcome initialization, auto-welcome playback, speech playback, and mic toggle path behind active-route checks.",
      "Updated speaking effect dependencies to respect route activation lifecycle.",
    ],
  },
  {
    id: "speaking-primary-ui-now-keeps-mic-start-prominent-and-moves-advanced-controls-into-settings-zone",
    timestamp: "2026-05-07T20:45:34+02:00",
    title:
      "Speaking primary UI now keeps mic start prominent and moves advanced controls into settings zone",
    summary:
      "Speaking practice now keeps the conversation entry controls focused around microphone start, while secondary voice and recognition controls are grouped in an explicit settings zone.",
    userHighlights: [
      "Main conversation control row stays simpler with mic start and one settings entry point.",
      "Advanced controls are still available but hidden by default behind Settings.",
      "This reduces visual noise while keeping full tuning options reachable.",
    ],
    technicalNotes: [
      "Added `showVoiceSettings` state in `src/routes/app/dialogue.tsx` and moved secondary controls to a conditional settings panel.",
      "Kept all existing voice/mic tuning logic intact; changed only control placement/visibility.",
      "Added new translation keys `speaking_practice_show_settings` and `speaking_practice_hide_settings` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-mic-now-supports-explicit-german-locale-mode-for-reliable-interrupt-commands",
    timestamp: "2026-05-06T22:21:30+02:00",
    title:
      "Speaking mic now supports explicit German locale mode for reliable interrupt commands",
    summary:
      "Speaking practice now includes an explicit microphone language mode so users can force German recognition when they need to interrupt with German control phrases.",
    userHighlights: [
      "New mic language mode cycles between Auto, German, and learning-language recognition.",
      "In German mode, dictation uses `de-DE` directly instead of relying on auto-detection.",
      "This improves reliability for phrases like `was heißt ...`, `nochmal`, and `wiederhole`.",
    ],
    technicalNotes: [
      "Added `micLanguageMode` state and locale resolver in `src/routes/app/dialogue.tsx`.",
      "Wired recognition initialization and updates to use explicit locale mode.",
      "Added translation keys for mic language mode labels in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-now-detects-direct-german-control-phrases-for-repeat-and-meaning-requests",
    timestamp: "2026-05-06T22:18:12+02:00",
    title:
      "Speaking now detects direct German control phrases for repeat and meaning requests",
    summary:
      "Speaking-practice backend now recognizes key German control phrases (for example repeat and “was heißt …”) and routes them into explicit conversation intents.",
    userHighlights: [
      "Phrases like `wiederhole`, `nochmal`, and `was heißt …` are now interpreted reliably as control intents.",
      "The system can apply repeat/meaning behavior even when the user switches into German mid-conversation.",
      "Default learning-language reply behavior remains intact.",
    ],
    technicalNotes: [
      "Added `_extract_speaking_control_intent` in `barkguide/reeprise/views.py` with repeat/meaning/lost intent parsing.",
      "Included `control_intent` in speaking payload and added prompt-level behavior instructions for each intent.",
      "Added regression test `test_speaking_practice_reply_extracts_german_control_intent` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-live-stt-now-attempts-faster-in-turn-language-detection-and-locale-switching",
    timestamp: "2026-05-06T22:14:32+02:00",
    title:
      "Speaking live STT now attempts faster in-turn language detection and locale switching",
    summary:
      "Speaking dictation now uses a lightweight live language guess on partial transcript text and can switch recognition locale mid-turn for better cross-language input handling.",
    userHighlights: [
      "Mixed-language turns can recover faster when recognition starts in the wrong locale.",
      "Locale switching happens automatically in tap mode without manual language changes.",
      "Retry and stop handling were hardened to avoid duplicate sends during locale transitions.",
    ],
    technicalNotes: [
      "Added transcript-based language guess heuristic and `maybeSwitchRecognitionLocale` flow in `src/routes/app/dialogue.tsx`.",
      "Added locale-switch cooldown/in-flight guards plus manual-stop tracking to prevent restart loops and accidental sends.",
      "Integrated locale switching with existing retry, pause, and onend grace timing lifecycle.",
    ],
  },
  {
    id: "speaking-now-explicitly-supports-mixed-mother-tongue-input-while-keeping-learning-language-replies",
    timestamp: "2026-05-06T22:11:59+02:00",
    title:
      "Speaking now explicitly supports mixed mother-tongue input while keeping learning-language replies",
    summary:
      "Speaking-practice prompt policy now explicitly allows mixed-language user input while keeping the default reply language on the active learning language.",
    userHighlights: [
      "Users can switch into mother tongue mid-turn without breaking the conversation flow.",
      "Mixed-language input is accepted as normal speaking behavior.",
      "Replies still default to the learning language unless a short rescue turn is needed.",
    ],
    technicalNotes: [
      "Updated speaking system prompt in `barkguide/reeprise/views.py` with explicit any-language input acceptance rule.",
      "Kept default learning-language reply rule and existing short mother-tongue rescue fallback.",
      "Added regression test `test_speaking_practice_prompt_explicitly_allows_codeswitch_input` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-language-space-now-controls-welcome-stt-and-requested-learning-language",
    timestamp: "2026-05-06T22:06:00+02:00",
    title:
      "Speaking language space now controls welcome, STT, and requested learning language",
    summary:
      "Speaking practice now keeps language behavior consistent with the selected language space across welcome message, microphone recognition locale, and backend language selection.",
    userHighlights: [
      "Welcome message now opens in the selected learning language instead of UI language fallback.",
      "Speech recognition now starts with the active language-space locale immediately.",
      "Requested language space is no longer overridden by vocabulary-language statistics in backend routing.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/dialogue.tsx` to initialize recognition locale from language space and added language-space-specific welcome copy mapping.",
      "Fixed precedence bug in `barkguide/reeprise/views.py` so explicit `language_space` remains dominant during learning-language resolution.",
      "Added backend regression test `test_speaking_practice_reply_requested_language_space_not_overridden_by_vocab` in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-mic-reliability-updated-with-hold-mode-pause-control-retry-and-resend",
    timestamp: "2026-05-06T22:01:55+02:00",
    title:
      "Speaking mic reliability updated with hold mode, pause control, retry, and resend",
    summary:
      "Speaking input now includes multiple reliability controls so dictation feels smoother and less fragile in real conversation use.",
    userHighlights: [
      "New hold-to-talk mode reduces accidental cutoff during pauses.",
      "Pause sensitivity can be switched between fast, balanced, and patient.",
      "Recognition now auto-retries in tap mode when it ends unexpectedly.",
      "Users can resend the last transcript with one tap if recognition fails mid-flow.",
    ],
    technicalNotes: [
      "Added `micMode` (`toggle`/`hold`), `pauseSensitivity`, retry timer lifecycle, and keep-listening state in `src/routes/app/dialogue.tsx`.",
      "Added adaptive pause/onend timing calculators and recognition auto-restart path for toggle mode.",
      "Added UI controls and translation keys for mic mode, pause sensitivity, hold-to-talk, and resend-last-transcript actions.",
    ],
  },
  {
    id: "speaking-turn-detection-now-waits-longer-on-short-thinking-pauses",
    timestamp: "2026-05-06T22:00:28+02:00",
    title: "Speaking turn detection now waits longer on short thinking pauses",
    summary:
      "Speaking auto-send now uses adaptive pause handling so users are less likely to be cut off while thinking between short phrases.",
    userHighlights: [
      "Very short utterances now get a longer pause window before auto-send.",
      "Recognition end no longer triggers an immediate hard send.",
      "Conversation stays responsive but less interruptive.",
    ],
    technicalNotes: [
      "Added adaptive silence timeout in `src/routes/app/dialogue.tsx` (`~1.4s` for short input, `~0.85s` otherwise).",
      "Replaced immediate `onend` send with a grace-timer send path (`~1.7s`/`~1.1s`) to tolerate thinking pauses.",
      "Added dedicated `onEndTimerRef` lifecycle cleanup alongside silence timer cleanup.",
    ],
  },
  {
    id: "speaking-turn-detection-now-responds-faster-after-user-pause",
    timestamp: "2026-05-06T21:59:33+02:00",
    title: "Speaking turn detection now responds faster after user pause",
    summary:
      "Speaking practice now starts reply generation faster after users stop talking by shortening pause detection and sending immediately on recognition end.",
    userHighlights: [
      "Short pause wait before auto-send is noticeably shorter.",
      "If recognition stops, the current spoken input is sent right away.",
      "Conversation rhythm now feels more real-time.",
    ],
    technicalNotes: [
      "Reduced silence timeout in `src/routes/app/dialogue.tsx` from ~1.1s to ~0.55s.",
      "Added immediate send path in `recognition.onend` when transcript text is available.",
      "Added `inputRef` synchronization to avoid stale input reads during recognition callbacks.",
    ],
  },
  {
    id: "speaking-correction-hints-now-attach-to-the-users-own-turn",
    timestamp: "2026-05-06T21:58:47+02:00",
    title: "Speaking correction hints now attach to the user’s own turn",
    summary:
      "Correction hints in speaking practice are now shown on the user message they refer to, not on the following assistant message.",
    userHighlights: [
      "Correction feedback now appears where users expect it: directly under their own sentence.",
      "Hints remain optional and can still be expanded or collapsed per turn.",
      "Assistant replies stay cleaner because correction metadata no longer sits under assistant text.",
    ],
    technicalNotes: [
      "Updated speaking message assembly in `src/routes/app/dialogue.tsx` to attach backend `correction` to the latest user turn before appending assistant reply.",
      "Switched correction-render condition from assistant messages to user messages in the same route.",
      "No backend contract changes; frontend mapping logic only.",
    ],
  },
  {
    id: "speaking-welcome-message-now-autoplays-reliably-on-screen-open",
    timestamp: "2026-05-06T21:54:09+02:00",
    title: "Speaking welcome message now autoplays reliably on screen open",
    summary:
      "The speaking screen now reliably plays the initial welcome turn after route open, so users hear a direct conversation start without manual interaction.",
    userHighlights: [
      "Welcome message audio now starts automatically after opening speaking practice.",
      "The first assistant turn is spoken only once to avoid repeated playback.",
      "Conversation startup now matches the expected open-and-talk behavior.",
    ],
    technicalNotes: [
      "Fixed first-turn playback effect in `src/routes/app/dialogue.tsx` by reacting to message state updates instead of mount-only timing.",
      "Added `hasWelcomedRef` guard to ensure the first assistant message is auto-played exactly once.",
      "Kept existing reply playback logic unchanged for subsequent turns.",
    ],
  },
  {
    id: "speaking-voice-playback-now-includes-a-normal-slow-speed-toggle",
    timestamp: "2026-05-06T21:51:35+02:00",
    title: "Speaking voice playback now includes a normal/slow speed toggle",
    summary:
      "Speaking practice now offers a direct speed toggle for reply playback so users can slow down voice output when needed.",
    userHighlights: [
      "Voice speed can now be switched between normal and slow during conversation.",
      "The selected speed affects reply playback immediately.",
      "Slow playback helps comprehension without changing generated text content.",
    ],
    technicalNotes: [
      "Added speech speed control state and toggle UI in `src/routes/app/dialogue.tsx`.",
      "Extended speaking speech fetch contract in `src/hooks/useSpeakingPracticeSpeech.ts` with `speed` (`normal` | `slow`).",
      "Added backend `speed` support in `SpeakingPracticeSpeechRequestSerializer` and OpenAI TTS instruction shaping in `barkguide/reeprise/views.py`.",
    ],
  },
  {
    id: "speaking-correction-hints-now-appear-as-optional-per-reply-details",
    timestamp: "2026-05-06T21:50:02+02:00",
    title: "Speaking correction hints now appear as optional per-reply details",
    summary:
      "Speaking replies now expose optional correction hints directly in the conversation flow, hidden by default and expandable per assistant turn.",
    userHighlights: [
      "If a correction hint is available, users can reveal it with one tap.",
      "Correction content stays optional and does not clutter normal conversation reading.",
      "Hints are attached to the specific assistant turn they belong to.",
    ],
    technicalNotes: [
      "Extended speaking message rendering in `src/routes/app/dialogue.tsx` to store backend `correction` text on assistant turns.",
      "Added per-message expand/collapse state (`expandedCorrections`) and toggle actions in the conversation UI.",
      "Added translation keys `speaking_practice_show_correction` and `speaking_practice_hide_correction` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-now-auto-sends-after-short-silence-without-audio-upload-input",
    timestamp: "2026-05-06T21:49:00+02:00",
    title:
      "Speaking now auto-sends after short silence without audio-upload input",
    summary:
      "The speaking flow now detects a short pause after dictation and sends the recognized text automatically, so users can talk naturally without pressing Send.",
    userHighlights: [
      "When mic is active, a short speaking pause is treated as end of turn and the message is sent automatically.",
      "Input speech is still handled as local browser transcription, not as uploaded voice files.",
      "Manual text typing and Send remain available as fallback.",
    ],
    technicalNotes: [
      "Added silence-timer based end-of-turn detection in `src/routes/app/dialogue.tsx` and auto-submit after ~1.1s pause.",
      "Refactored message submission into a shared `sendUserTurn` path used by both typed submit and speech end detection.",
      "Added transcript/focus refs (`finalTranscriptRef`, `messagesRef`, pending/listening refs) to avoid stale state during recognition callbacks.",
    ],
  },
  {
    id: "speaking-voice-playback-now-prefers-openai-tts-with-stable-ash-profile",
    timestamp: "2026-05-06T21:47:19+02:00",
    title:
      "Speaking voice playback now prefers OpenAI TTS with stable ash profile",
    summary:
      "Speaking replies now use server-side OpenAI speech synthesis as the primary playback path, resulting in more consistent and natural voice output across browsers.",
    userHighlights: [
      "Reply playback now uses a consistent voice profile instead of random system voices.",
      "Firefox and other browsers now receive the same generated audio quality where playback is supported.",
      "If server-side speech is temporarily unavailable, local fallback playback can still prevent silent replies.",
    ],
    technicalNotes: [
      "Added authenticated endpoint `/api/reeprise/speaking-practice/speech/` in `barkguide/reeprise/views.py` using OpenAI `gpt-4o-mini-tts` with `ash` voice.",
      "Added request schema `SpeakingPracticeSpeechRequestSerializer` and route wiring in `barkguide/reeprise/serializer.py` and `barkguide/reeprise/urls.py`.",
      "Refactored `src/routes/app/dialogue.tsx` to fetch and play backend MP3 blobs (`useSpeakingPracticeSpeech`) and keep browser `speechSynthesis` as fallback.",
    ],
  },
  {
    id: "speaking-mic-button-now-requests-browser-mic-permission-before-stt-check",
    timestamp: "2026-05-06T21:43:27+02:00",
    title:
      "Speaking mic button now requests browser microphone permission before STT check",
    summary:
      "The speaking screen now asks for microphone access immediately when tapping Start mic, even before speech-to-text capability checks are finalized.",
    userHighlights: [
      "Start mic now triggers the browser microphone permission flow first when available.",
      "Users can grant microphone access even in browsers where speech-to-text is later unavailable.",
      "Unsupported speech-to-text messaging remains explicit after permission preflight.",
    ],
    technicalNotes: [
      "Reordered `toggleListening` flow in `src/routes/app/dialogue.tsx` to run `getUserMedia` preflight before `SpeechRecognition` availability gating.",
      "Added explicit `getUserMedia` availability guard and error path handling without breaking existing mic error feedback.",
      "Kept stop-listening behavior safe when recognition is unavailable by null-guarding `stop()` call.",
    ],
  },
  {
    id: "speaking-mic-support-check-is-now-more-robust-with-clear-browser-guidance",
    timestamp: "2026-05-06T21:41:52+02:00",
    title:
      "Speaking mic support check is now more robust with clear browser guidance",
    summary:
      "The speaking screen now detects microphone speech APIs more defensively and shows clearer guidance when the browser does not provide speech-to-text support.",
    userHighlights: [
      "Mic availability detection now handles more browser runtime variants before showing unsupported state.",
      "If speech-to-text is unavailable, users see a direct browser hint instead of a generic dead end.",
      "Error text now clearly separates browser capability limits from microphone permission issues.",
    ],
    technicalNotes: [
      "Updated speech-recognition constructor resolution in `src/routes/app/dialogue.tsx` to check multiple runtime candidates and guard constructor failures.",
      "Added `micApiSupported` UI state and a dedicated inline capability hint on the speaking form.",
      "Added translation key `speaking_practice_mic_browser_hint` and refined `speaking_practice_mic_unsupported` wording in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-mic-activation-now-requests-permission-and-shows-clear-errors",
    timestamp: "2026-05-06T21:37:33+02:00",
    title:
      "Speaking mic activation now requests permission and shows clear errors",
    summary:
      "Microphone activation on speaking practice now performs a permission preflight and surfaces explicit error text when speech input cannot start.",
    userHighlights: [
      "Tapping mic now actively requests microphone permission before recognition starts.",
      "If mic start fails, the screen shows a clear error message instead of silently doing nothing.",
      "Recognition language now follows active language space (for example French uses `fr-FR`).",
    ],
    technicalNotes: [
      "Updated `src/routes/app/dialogue.tsx` to run `getUserMedia({ audio: true })` preflight before `SpeechRecognition.start()` and handle start errors.",
      "Added `micError` UI state and rendered inline error feedback below voice controls.",
      "Added translation key `speaking_practice_mic_error` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "speaking-now-follows-active-language-space-from-app-context",
    timestamp: "2026-05-06T21:33:56+02:00",
    title: "Speaking now follows active language space from app context",
    summary:
      "Speaking practice now reads the user’s active language space and sends it with each turn, so conversation scope follows the same app-level language context.",
    userHighlights: [
      "Speaking context now matches the selected language space.",
      "Switching language space in the app now affects speaking turns immediately.",
      "When language space is `ALL`, fallback language inference remains in place.",
    ],
    technicalNotes: [
      "Extended speaking request schema with `language_space` in `barkguide/reeprise/serializer.py`.",
      "Updated `barkguide/reeprise/views.py` to prioritize requested language space (except `ALL`) before historical inference.",
      "Wired `useLanguageSpace` into `src/routes/app/dialogue.tsx` and forwarded value via `useSpeakingPracticeReply`; added backend regression coverage in `barkguide/reeprise/tests.py`.",
    ],
  },
  {
    id: "speaking-screen-simplified-to-open-and-talk-with-auto-welcome",
    timestamp: "2026-05-06T21:31:59+02:00",
    title: "Speaking screen simplified to open-and-talk flow with auto welcome",
    summary:
      "Speaking practice was reduced to a minimal conversation surface: users open the feature, receive an immediate welcome message, and can start talking without extra panels or metadata blocks.",
    userHighlights: [
      "The screen now opens directly into conversation with a first welcome turn.",
      "Extra UI blocks were removed so focus stays on talking.",
      "Mic input and voice playback controls remain available.",
    ],
    technicalNotes: [
      "Refactored `src/routes/app/dialogue.tsx` to remove secondary coaching/metadata panels and keep a minimal chat + voice control layout.",
      "Added `speaking_practice_welcome` translation key and used it for first-turn auto-greeting.",
      "Updated `e2e/speaking-practice.spec.ts` assertions to match the simplified UX flow.",
    ],
  },
  {
    id: "speaking-entrypoints-consolidated-to-top-navbar-only",
    timestamp: "2026-05-06T21:29:08+02:00",
    title: "Speaking entry points consolidated to top navbar only",
    summary:
      "Speaking practice entry points were reduced to a single location: the top navbar. Duplicate access points in side navigation and above daily cards were removed.",
    userHighlights: [
      "Only one speaking entry point remains, reducing UI duplication.",
      "Top-navbar trigger remains the single source of access.",
      "Main feed and side navigation now feel cleaner.",
    ],
    technicalNotes: [
      "Removed `MainAiEntryCard` usage and implementation from `src/routes/app/reeprise/index.tsx`.",
      "Removed side-navigation `/app/dialogue` item from `src/routes/app/route.tsx` while keeping top-navbar trigger.",
      "No route/backend behavior changes; only entry-point placement cleanup.",
    ],
  },
  {
    id: "navbar-speaking-wave-gesture-now-uses-an-explicit-hand-for-visibility",
    timestamp: "2026-05-06T21:28:31+02:00",
    title:
      "Navbar speaking wave gesture now uses an explicit hand for visibility",
    summary:
      "The wave animation on the top-navbar speaking marker was upgraded from a tiny pseudo-cue to an explicit animated hand element so the wave is clearly visible.",
    userHighlights: [
      "Wave gesture is now visibly readable in normal navbar usage.",
      "Wave cadence is slightly faster, so it appears more often.",
      "Overall icon footprint and navigation behavior stay unchanged.",
    ],
    technicalNotes: [
      "Added a dedicated `nav-speaking-mouth-hand` element to the top-navbar icon markup in `src/routes/app/route.tsx`.",
      "Replaced prior pseudo-element wave styling with explicit hand animation in `src/index.css`.",
      "Increased rotation amplitude and adjusted timing (`6.5s` cycle) for clearer wave perception.",
    ],
  },
  {
    id: "top-navbar-speaking-trigger-size-increased-for-better-visibility",
    timestamp: "2026-05-06T21:27:23+02:00",
    title: "Top-navbar speaking trigger size increased for better visibility",
    summary:
      "The speaking trigger in the top navbar was enlarged to improve discoverability and tap clarity while keeping alignment with nearby controls.",
    userHighlights: [
      "Top-navbar speaking trigger is now visibly larger.",
      "Tap target is bigger for easier interaction on touch devices.",
      "Animated mouth proportions remain balanced at the larger size.",
    ],
    technicalNotes: [
      "Increased top-bar speaking link control from 36px to 44px equivalent dimensions in `src/routes/app/route.tsx`.",
      "Added a large variant class (`nav-speaking-mouth--large`) and tuned eye/mouth geometry in `src/index.css`.",
      "No behavior/API changes.",
    ],
  },
  {
    id: "speaking-trigger-now-appears-in-the-top-navbar-controls",
    timestamp: "2026-05-06T21:26:24+02:00",
    title: "Speaking trigger now appears in the top navbar controls",
    summary:
      "The speaking entry is now available in the top navbar control row (not only in side navigation), so access matches the requested top-bar placement.",
    userHighlights: [
      "Speaking can now be opened directly from the top bar.",
      "The same animated mouth marker is used for consistent recognition.",
      "Side-nav access remains available as a secondary path.",
    ],
    technicalNotes: [
      "Added `/app/dialogue` link button in the top-navbar auxiliary control group inside `src/routes/app/route.tsx`.",
      "Reused existing animated mouth icon classes for visual consistency with other speaking entry points.",
      "No backend or route contract changes.",
    ],
  },
  {
    id: "navbar-speaking-mouth-now-includes-an-occasional-wave-motion",
    timestamp: "2026-05-06T21:25:39+02:00",
    title: "Navbar speaking mouth now includes an occasional wave motion",
    summary:
      "The animated speaking marker in the navbar now performs a subtle periodic wave gesture so it feels more welcoming without becoming visually noisy.",
    userHighlights: [
      "The icon still stays small and calm in the navbar.",
      "Wave motion appears only occasionally, not continuously.",
      "Navigation behavior is unchanged.",
    ],
    technicalNotes: [
      "Added a small pseudo-element “hand” on `.nav-speaking-mouth` in `src/index.css`.",
      "Introduced `mouth-wave` keyframes with sparse timing windows for occasional motion.",
      "No route/copy/backend changes.",
    ],
  },
  {
    id: "speaking-entry-is-now-pinned-in-navbar-with-animated-mouth-icon",
    timestamp: "2026-05-06T21:24:32+02:00",
    title: "Speaking entry is now pinned in navbar with animated mouth icon",
    summary:
      "Speaking practice can now be opened directly from the main navbar and uses a compact animated mouth icon for faster access without relying on the in-flow card area.",
    userHighlights: [
      "Speaking practice is now always visible in navigation.",
      "You can open the conversation flow from anywhere in the app with one tap.",
      "The navbar icon keeps a small playful speaking cue without changing overall nav density.",
    ],
    technicalNotes: [
      "Moved `/app/dialogue` navigation item in `src/routes/app/route.tsx` out of the staff/experimental gate and made it part of the default app nav list.",
      "Replaced the previous academic-cap nav icon with a compact animated mouth icon markup in the same route file.",
      "Added dedicated navbar mouth icon styles in `src/index.css` (`nav-speaking-mouth*`) reusing existing subtle motion keyframes.",
    ],
  },
  {
    id: "speaking-entry-now-uses-a-small-animated-mouth-trigger",
    timestamp: "2026-05-06T21:23:52+02:00",
    title: "Speaking entry now uses a small animated mouth trigger",
    summary:
      "The main-page speaking entrance now uses a tiny animated mouth-style floating trigger instead of a chip button, keeping the flow lightweight while preserving one-tap access.",
    userHighlights: [
      "Speaking entry feels playful and compact without taking over the main feed.",
      "A subtle hover label still clarifies the entry purpose.",
      "Tap behavior and speaking-route access remain unchanged.",
    ],
    technicalNotes: [
      "Replaced speaking-entry chip markup in `src/routes/app/reeprise/index.tsx` with a circular animated mouth trigger.",
      "Added dedicated mouth animation styles/keyframes in `src/index.css` (`mouth-fab-float`, `mouth-fab-halo`, `mouth-smile`).",
      "No backend/API contract changes.",
    ],
  },
  {
    id: "speaking-chip-now-uses-a-subtle-animated-orb-marker",
    timestamp: "2026-05-06T21:22:33+02:00",
    title: "Speaking chip now uses a subtle animated orb marker",
    summary:
      "The compact speaking entry on the main page now includes a soft animated orb marker inspired by lightweight assistant-presence cues, while keeping the existing in-flow layout and calm visual tone.",
    userHighlights: [
      "The speaking entry now feels more alive without becoming visually dominant.",
      "Animation remains subtle and keeps the main feed readable.",
      "Entry interaction and routing stay unchanged.",
    ],
    technicalNotes: [
      "Updated speaking-entry orb markup in `src/routes/app/reeprise/index.tsx` with dedicated orb classes.",
      "Added lightweight `orb-float`, `orb-halo`, and `orb-core` keyframes in `src/index.css` for subtle motion.",
      "No copy/API changes; visual behavior only.",
    ],
  },
  {
    id: "speaking-entry-on-main-page-is-now-a-compact-in-flow-floating-chip",
    timestamp: "2026-05-06T21:21:15+02:00",
    title: "Speaking entry on main page is now a compact in-flow floating chip",
    summary:
      "The speaking-practice entry on the main page was reduced from a large callout to a small floating-style chip in the existing content flow.",
    userHighlights: [
      "The entry now feels lighter and less dominant on the main screen.",
      "Speaking practice remains one tap away without breaking reading/feed focus.",
      "Visual style now sits closer to existing card and border rhythm in Reeprise.",
    ],
    technicalNotes: [
      "Replaced the large speaking callout block in `src/routes/app/reeprise/index.tsx` with a compact rounded chip link element.",
      "Kept subtle water-like visual cues through soft gradient accents while matching existing neutral border/layout language.",
      "No behavior/API changes; entry still routes to `/app/dialogue`.",
    ],
  },
  {
    id: "speaking-flow-copy-now-uses-neutral-product-voice-across-status-and-voice-hints",
    timestamp: "2026-05-06T21:20:16+02:00",
    title:
      "Speaking flow copy now uses neutral product voice across status and voice hints",
    summary:
      "Additional speaking-practice copy was aligned to product voice by removing remaining AI-persona wording in runtime status/hint text and by broadening guard coverage.",
    userHighlights: [
      "Runtime status text now uses neutral phrasing (for example `Reply in progress`) instead of AI-actor language.",
      "Voice hint text now focuses on user action and response behavior without AI persona framing.",
      "Entry title now references the user’s learning language instead of a fixed language example.",
    ],
    technicalNotes: [
      "Updated translation values in `src/assets/translations.json` for speaking status/hint keys and the main speaking-entry title.",
      "Updated fallback literals in `src/routes/app/dialogue.tsx` to match the same neutral tone.",
      "Extended `src/assets/translations.copy.test.ts` to guard additional speaking-practice keys against AI/persona token regressions.",
    ],
  },
  {
    id: "speaking-entry-copy-now-follows-product-voice-without-ai-persona-wording",
    timestamp: "2026-05-06T21:18:43+02:00",
    title:
      "Speaking entry copy now follows product voice without AI-persona wording",
    summary:
      "Speaking-practice entry wording was revised to remove AI/persona framing and align with the product voice: user action first, calm wording, and no assistant-style phrasing.",
    userHighlights: [
      "The main speaking entry now uses neutral, user-focused wording instead of “AI ready” style copy.",
      "Speaking page title was simplified from AI-centric wording to plain `Sprechpraxis` / `Speaking practice`.",
      "Entry copy now emphasizes starting a short speaking round and level-adaptive replies without persona language.",
    ],
    technicalNotes: [
      "Updated translation values in `src/assets/translations.json` for `speaking_practice_title`, `reeprise_ai_ready_badge`, `reeprise_ai_ready_title`, and `reeprise_ai_ready_body`.",
      "Added copy guard regression coverage in `src/assets/translations.copy.test.ts` to block AI-persona tokens in speaking-entry keys.",
      "No behavioral/API changes were required.",
    ],
  },
  {
    id: "speaking-practice-now-allows-brief-mother-tongue-rescue-when-user-is-lost",
    timestamp: "2026-05-06T21:14:46+02:00",
    title:
      "Speaking practice now allows a brief mother-tongue rescue turn when users are lost",
    summary:
      "Speaking practice now defaults to the learning language but can briefly switch to mother tongue for one rescue turn when the learner is clearly stuck, then returns to the learning language.",
    userHighlights: [
      "Conversation still stays primarily in the learning language.",
      "If you are totally lost, the assistant can now give one short rescue response in your mother tongue.",
      "After rescue help, the assistant guides the dialogue back to the target language.",
    ],
    technicalNotes: [
      "Adjusted speaking-practice prompt policy in `barkguide/reeprise/views.py` from strict learning-language-only output to default learning-language output with an explicit one-turn mother-tongue rescue fallback.",
      "Updated backend regression assertion in `barkguide/reeprise/tests.py` to verify the new fallback guardrail text.",
      "No frontend API contract change was required.",
    ],
  },
  {
    id: "speaking-practice-now-keeps-replies-in-learning-language-even-if-input-is-mother-tongue",
    timestamp: "2026-05-06T21:14:03+02:00",
    title:
      "Speaking practice now keeps replies in the learning language even when input is mother tongue",
    summary:
      "When users write in their mother tongue because they cannot yet form a sentence in the learning language, speaking practice now explicitly keeps assistant replies in the learning language.",
    userHighlights: [
      "You can write in your mother tongue when blocked, and conversation still continues in your target learning language.",
      "The assistant now treats mother-tongue input as intent/context support, not as a language switch.",
      "This keeps practice momentum without forcing perfect first-turn production.",
    ],
    technicalNotes: [
      "Strengthened speaking-practice system prompt in `barkguide/reeprise/views.py` with explicit instruction to always answer in the learning language, including when learner input is mother tongue.",
      "Added regression coverage in `barkguide/reeprise/tests.py` to ensure prompt-level guardrails include the learning-language-only response rule.",
      "No API contract change was required for frontend integration.",
    ],
  },
  {
    id: "speaking-practice-turn-level-coaching-can-now-be-toggled",
    timestamp: "2026-05-06T21:12:00+02:00",
    title: "Speaking practice turn-level coaching can now be toggled on or off",
    summary:
      "Users can now control turn-level coaching in speaking practice. When enabled, each reply can include optional mini hints for phrasing, pronunciation focus, and one new word.",
    userHighlights: [
      "A new on/off coaching control is available directly in speaking practice.",
      "When enabled, replies can show compact coaching hints: better phrasing, pronunciation focus, and one new word.",
      "When disabled, conversation stays cleaner without coaching hints.",
    ],
    technicalNotes: [
      "Extended speaking-practice request schema with `coaching_enabled` and response schema with a structured `coaching` object plus `used_struggle_words`.",
      "Updated backend prompt payload in `barkguide/reeprise/views.py` to include coaching toggle and normalize coaching fields in API responses.",
      "Updated `src/routes/app/dialogue.tsx` and `src/hooks/useSpeakingPracticeReply.ts` with persisted UI toggle state and conditional hint rendering, plus E2E coverage updates.",
    ],
  },
  {
    id: "speaking-practice-now-periodically-reuses-struggle-words",
    timestamp: "2026-05-06T21:10:00+02:00",
    title: "Speaking practice now periodically reuses your struggle words",
    summary:
      "Speaking conversations now include actual struggle words from your learning history, and every third assistant turn can require one of those words in natural context.",
    userHighlights: [
      "Conversation replies now train against words you still struggle with, not just your general vocabulary.",
      "Struggle-word reuse is periodically enforced so difficult words come back in realistic dialogue turns.",
      "Replies keep the conversational flow while still pulling targeted difficult vocabulary back into active use.",
    ],
    technicalNotes: [
      "Extended `/api/reeprise/speaking-practice/reply/` context payload with `struggle_words` and `required_struggle_word` derived from struggle-practice selection logic.",
      "Added periodic enforcement rule (every third assistant turn) and a controlled retry instruction if the required struggle word is missing.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert struggle-word payload wiring and required-word signaling.",
    ],
  },
  {
    id: "speaking-practice-now-supports-mic-input-and-voice-replies",
    timestamp: "2026-05-06T21:08:33+02:00",
    title: "Speaking practice now supports mic input and voice replies",
    summary:
      "Speaking practice now feels more like real conversation: you can dictate with microphone input and AI replies can be played aloud automatically.",
    userHighlights: [
      "A new mic control lets you dictate your message instead of typing.",
      "AI replies are now spoken aloud automatically after each response.",
      "Voice controls include explicit stop actions and clear browser-support fallback messages.",
    ],
    technicalNotes: [
      "Added browser speech-recognition integration (`SpeechRecognition` / `webkitSpeechRecognition`) in `src/routes/app/dialogue.tsx` for live dictation into the message field.",
      "Added `speechSynthesis` playback for assistant replies with explicit `Stop voice` control and lifecycle cleanup.",
      "Extended speaking-practice E2E coverage in `e2e/speaking-practice.spec.ts` to verify voice-control visibility in the main-page entry flow.",
    ],
  },
  {
    id: "main-page-speaking-practice-entry-and-dreamlike-ai-aura",
    timestamp: "2026-05-06T21:03:54+02:00",
    title:
      "Main page now opens speaking practice with a soft AI aura while replying",
    summary:
      "The main screen now includes a direct speaking-practice entry, and the conversation page shows a soft animated aura while AI is actively responding.",
    userHighlights: [
      "You can open speaking practice directly from the main `/app/reeprise` page.",
      "The conversation view now uses a calmer dreamlike visual style with soft gradients and shapes.",
      "While AI is responding, an animated aura appears so the interaction state feels clearer and more alive.",
    ],
    technicalNotes: [
      "Added a new entry card linking to `/app/dialogue` in `src/routes/app/reeprise/index.tsx`.",
      "Updated `src/routes/app/dialogue.tsx` with a dreamlike layout/background and a loading-state aura section.",
      "Introduced reusable aura animation styles in `src/index.css`, added translation keys, and updated Playwright coverage to verify opening from main page before send/reply flow.",
    ],
  },
  {
    id: "speaking-practice-now-uses-your-vocabulary-topics-and-level",
    timestamp: "2026-05-06T20:55:39+02:00",
    title: "Speaking practice now adapts to your vocabulary, topics, and level",
    summary:
      "A new speaking-practice conversation page now replies with context from your known vocabulary, recently used topics, and your latest generation level for that language.",
    userHighlights: [
      "You can now practice a continuous AI conversation directly in the app.",
      "Replies are tuned to your active learning language and recent level instead of generic difficulty.",
      "The conversation context uses your saved vocabulary and recent generation topics.",
    ],
    technicalNotes: [
      "Added backend endpoint `/api/reeprise/speaking-practice/reply/` that builds prompt context from recent vocabulary, recent generated-text topics, and detected dominant learning language/level.",
      "Replaced placeholder `/app/dialogue` route with a working conversation UI and new mutation hook `useSpeakingPracticeReply`.",
      "Added automated regression coverage in Django tests (`reeprise/tests.py`) and Playwright (`e2e/speaking-practice.spec.ts`) for message flow and adaptive metadata rendering.",
    ],
  },
  {
    id: "source-vocabulary-list-no-longer-shows-duplicate-entries",
    timestamp: "2026-05-04T20:59:02+02:00",
    title: "Source vocabulary list no longer shows duplicate entries",
    summary:
      "Words used to generate tricky-word texts now appear only once in the vocabulary list instead of rendering both fallback and persisted copies.",
    userHighlights: [
      "Used generation words now keep the expected selected/highlighted state without duplicate list rows.",
      "When the same word already exists in persisted vocabulary, the fallback copy is suppressed.",
      "Vocabulary list scanning is cleaner and avoids repeated checkbox rows for the same term.",
    ],
    technicalNotes: [
      "Switched struggle-source card mapping in `reeprise/$generatedTextId.tsx` to use real backend `struggle_words[].id` values when available instead of always synthetic negative IDs.",
      "Added `resolveUnresolvedStruggleVocabularyCards()` to filter fallback entries against persisted vocabulary by both ID and normalized word.",
      "Added unit regression coverage in `resolveUnresolvedStruggleVocabularyCards.test.ts` for ID-based and word-based deduplication.",
    ],
  },
  {
    id: "generation-source-vocabulary-now-shows-selected-by-default",
    timestamp: "2026-05-04T20:53:15+02:00",
    title: "Generation source vocabulary now shows selected by default",
    summary:
      "Vocabulary that was used as source input for tricky-word generation now appears selected in the text details list by default.",
    userHighlights: [
      "Used source words no longer look unchecked when the text opens.",
      "The selection state now better matches what was actually used to generate the text.",
      "Jump-to-word behavior remains unchanged for unresolved source words.",
    ],
    technicalNotes: [
      "Updated unresolved struggle-word list rows in `reeprise/$generatedTextId.tsx` to render as selected by default.",
      "This aligns visual selection state with source vocabulary that was already part of the generation input, even before those entries become persisted selectable IDs.",
      "Added regression coverage in `vocabularySelectionState.test.ts` to keep default preselection behavior stable when existing selections are already present.",
    ],
  },
  {
    id: "struggle-word-highlights-now-load-without-manual-page-refresh",
    timestamp: "2026-05-04T20:50:40+02:00",
    title: "Struggle-word highlights now appear without a manual page refresh",
    summary:
      "Practice texts for tricky words now load their highlighted vocabulary list on first open, even when the first vocabulary fetch arrives before generation finalization.",
    userHighlights: [
      "The highlighted tricky-word list now appears as soon as the text is complete.",
      "You no longer need to reload the page to see expected highlight coverage.",
      "Reading flow stays continuous after opening a newly generated struggle-practice text.",
    ],
    technicalNotes: [
      "Added a completion-gated vocabulary refetch in `reeprise/$generatedTextId.tsx` so the details screen refreshes vocabulary once when status reaches `COMPLETE`.",
      "This closes the stale-empty-list window where an early fetch returned no entries and polling stopped before struggle-word usages were visible.",
      "Added `shouldRefetchVocabularyAfterCompletion` with unit coverage to guard one-time refetch behavior per text/chapter route key.",
    ],
  },
  {
    id: "chapter-highlights-now-expand-multi-word-generation-entries",
    timestamp: "2026-05-04T20:39:50+02:00",
    title:
      "Chapter highlights now include all words from multi-word generation entries",
    summary:
      "When multiple words are selected via context selection and used for chapter generation, highlights now cover all words instead of only the first word of a combined entry.",
    userHighlights: [
      "Multi-word selections carried into chapter generation now highlight each component word.",
      "Next-chapter reading view no longer appears to drop highlight coverage for later words in the same selected phrase.",
      "Existing selected-word and title-word highlight behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated `resolveActiveTextHighlightWords()` to expand multi-word entries into both full phrase and component-word highlight tokens.",
      "This ensures chapter-generated arrays that contain phrase-level entries still drive per-word highlighting in text rendering.",
      "Added regression coverage in `resolveActiveTextHighlightWords.test.ts` for multi-word chapter entry expansion.",
    ],
  },
  {
    id: "chapter-highlights-now-cover-selected-and-close-inflected-forms",
    timestamp: "2026-05-04T20:34:24+02:00",
    title:
      "Chapter highlights now keep selected words visible across form variants",
    summary:
      "Highlighting in continued chapters now keeps selected vocabulary active and can highlight close inflected forms when the exact surface form changes but meaning stays aligned.",
    userHighlights: [
      "Selected words remain in the active highlight set during chapter mode.",
      "If a continued chapter uses a close inflected variant, the word can still be highlighted.",
      "Title-based highlights remain active together with chapter and selected vocabulary highlights.",
    ],
    technicalNotes: [
      "Updated `resolveActiveTextHighlightWords()` to merge chapter-generated words with currently selected words instead of replacing them.",
      "Extended highlight matching in `resolveGeneratedTextHighlightWords.ts` and `GeneratedTextDetailsTextPane.tsx` with a constrained fallback for close inflected variants when exact surface matching is absent.",
      "Added regression tests in `resolveActiveTextHighlightWords.test.ts` and `resolveGeneratedTextHighlightWords.test.ts` for chapter-selected merge behavior and inflected-form fallback matching.",
    ],
  },
  {
    id: "newly-added-chapter-words-now-stay-selected-after-refresh",
    timestamp: "2026-05-04T20:29:35+02:00",
    title:
      "Newly added words now stay selected while chapter vocabulary refreshes",
    summary:
      "When you add another word in later chapters, it now remains selected instead of appearing unchecked after the vocabulary list refresh.",
    userHighlights: [
      "Words you add in chapter details now stay selected consistently.",
      "The selection no longer flips off during the short gap before the refreshed vocabulary payload includes the new entry.",
      "Continue-with-words behavior now keeps newly added words active across chapter flow.",
    ],
    technicalNotes: [
      "Updated `pruneVocabularySelectionToExistingIds()` to preserve `selectedIds` instead of dropping IDs not yet present in the current fetched vocabulary page.",
      "Kept deselection pruning scoped to existing vocabulary IDs so explicit deselection remains stable.",
      "Adjusted regression expectations in `src/utils/vocabularySelectionState.test.ts` for selected-ID persistence during refresh lag.",
    ],
  },
  {
    id: "chapter-generation-now-includes-selected-and-struggle-list-words",
    timestamp: "2026-05-04T20:27:02+02:00",
    title:
      "Chapter generation now includes selected words and struggle-list words",
    summary:
      "Continuing a text now always sends user-selected vocabulary words, and struggle-practice texts also include the existing struggle word list in chapter generation.",
    userHighlights: [
      "Words you selected are now always part of the continuation payload.",
      "For struggle-practice texts, list words are included even when they are not yet persisted selectable vocabulary IDs.",
      "Continuation can now reliably use both your added words and struggle-list words together.",
    ],
    technicalNotes: [
      "Extended `resolveSelectedVocabularyWordsForChapterGeneration()` with `fallbackWords` to merge explicit required words into generation payload resolution.",
      "Updated `reeprise/$generatedTextId.tsx` to pass `struggle_words` as fallback words when `origin === STRUGGLE_PRACTICE`.",
      "Added regression coverage in `resolveSelectedVocabularyWordsForChapterGeneration.test.ts` for fallback-word inclusion and deduplication behavior.",
    ],
  },
  {
    id: "chapter-continue-with-words-now-enforces-all-selected-words",
    timestamp: "2026-05-04T20:21:18+02:00",
    title: "Continue with words now keeps every selected word in the chapter",
    summary:
      "When you continue a text with selected words, the generated chapter now enforces all selected words so none are silently dropped.",
    userHighlights: [
      "Selected words in `Mit Wörtern fortsetzen` are now reliably carried into the generated chapter.",
      "If a draft misses one of your selected words, chapter generation now retries automatically.",
      "Continuation stays context-aware while keeping the complete selected vocabulary set.",
    ],
    technicalNotes: [
      "Added required-word verification in `barkguide/reeprise/tasks.py` for chapter generation using the same boundary-aware surface matching used by reinforcement flows.",
      "Added controlled regeneration attempts with targeted retry guidance that explicitly lists still-missing words.",
      "Added Django regression coverage in `barkguide/reeprise/tests.py` to verify chapter regeneration retries when a selected word is initially missing.",
    ],
  },
  {
    id: "text-details-vocabulary-no-longer-reuses-stale-previous-list",
    timestamp: "2026-05-04T20:12:56+02:00",
    title: "Text details now uses fresh vocabulary data on route changes",
    summary:
      "Opening a generated text now immediately uses its own vocabulary list instead of temporarily reusing the previous text’s list, so default selection and highlights appear without a manual refresh.",
    userHighlights: [
      "Used vocabulary highlights now appear on first open instead of only after reload.",
      "Used words are preselected immediately when entering a new text details page.",
      "Cross-text navigation no longer inherits stale vocabulary rows from the previous text.",
    ],
    technicalNotes: [
      "Removed `placeholderData` carry-over from `usePaginatedVocabularyList` so query-key changes do not render stale prior-page vocabulary results.",
      "This ensures selection/highlight derivation runs against the active text’s fetched vocabulary as soon as data arrives.",
      "Retained existing polling behavior for pending vocabulary/pronunciation states.",
    ],
  },
  {
    id: "single-vocabulary-pronunciation-now-skips-unsaved-preview-ids",
    timestamp: "2026-05-04T20:11:29+02:00",
    title:
      "Single-word pronunciation no longer fails on unsaved preview entries",
    summary:
      "Pronunciation controls for single-word preview cards now avoid failing requests when the word is only a temporary preview and not yet saved as a real vocabulary item.",
    userHighlights: [
      "Single-word pronunciation no longer triggers a failing request for temporary preview words.",
      "Pronunciation still works for saved vocabulary words with real IDs.",
      "Preview cards now remain stable instead of surfacing a generation error for unsaved entries.",
    ],
    technicalNotes: [
      "Added a persisted-ID guard in `src/components/VocabularyPronunciationButton.tsx` so pronunciation generation only runs for positive persisted vocabulary IDs.",
      "Updated waiting-state gating to treat non-persisted preview entries as non-generatable until saved.",
      "Added regression coverage in `src/components/VocabularyPronunciationButton.test.ts` for temporary/missing IDs versus persisted IDs.",
    ],
  },
  {
    id: "reinforcement-texts-now-default-select-all-linked-vocabulary",
    timestamp: "2026-05-04T20:10:15+02:00",
    title: "Reinforcement texts now default-select all linked vocabulary",
    summary:
      "Daily and tricky-word generated texts now default-select all currently linked vocabulary entries, ensuring continuation and word highlights are immediately active.",
    userHighlights: [
      "Used vocabulary in daily/tricky generated texts now opens checked by default.",
      "`Text fortsetzen` can be used immediately without manually selecting each word first.",
      "Word highlights tied to selected vocabulary remain visible right away.",
    ],
    technicalNotes: [
      "Updated `resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText()` to preselect all available linked vocabulary IDs for `DAILY_NUDGE` and `STRUGGLE_PRACTICE` texts.",
      "Kept explicit deselection handling so manually removed words are not auto-readded.",
      "Adjusted regression expectations in `src/utils/vocabularySelectionState.test.ts` and revalidated the selection-state suite.",
    ],
  },
  {
    id: "text-details-now-default-select-vocabulary-for-all-text-origins",
    timestamp: "2026-05-04T20:05:57+02:00",
    title: "Text details now default-select vocabulary for all text origins",
    summary:
      "Vocabulary checkboxes in text details now default-select across all text origins, so chapter continuation works immediately without manual re-selection.",
    userHighlights: [
      "Newly opened text details now start with vocabulary selected by default for manual, daily, and struggle-generated texts.",
      "Daily and struggle reinforcement words still merge in automatically and remain prechecked.",
      "If you manually deselect words, your explicit choice is preserved.",
    ],
    technicalNotes: [
      "Extended `resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText()` in `src/utils/vocabularySelectionState.ts` to default-select available vocabulary for non-reinforcement origins on first load.",
      "Kept existing daily/struggle reinforcement merge behavior and deselection safeguards.",
      "Added regression coverage in `src/utils/vocabularySelectionState.test.ts` for manual-origin default preselection and explicit-deselection persistence.",
    ],
  },
  {
    id: "reinforcement-preselection-now-updates-even-when-list-size-matches",
    timestamp: "2026-05-04T20:05:06+02:00",
    title: "Reinforcement preselection now updates even when list size matches",
    summary:
      "Vocabulary preselection for daily and struggle-generated texts now correctly replaces stale selections even when the new and old selection counts are the same.",
    userHighlights: [
      "Used generation words are now reliably prechecked on text details.",
      "You can continue immediately with `Text fortsetzen` without reselecting words.",
      "Selection no longer gets stuck unchecked because of same-size stale state.",
    ],
    technicalNotes: [
      "Removed the length-only shortcut in `reeprise/$generatedTextId.tsx` that reused previous `selectedVocabularyIds` when `preselectedIds.length` matched, even if IDs differed.",
      "Selection pruning now always runs against the computed preselected ID set for the active text.",
      "This fixes state carryover where a same-sized stale selection could block expected reinforcement preselection.",
    ],
  },
  {
    id: "struggle-text-details-now-preselect-reinforcement-vocabulary",
    timestamp: "2026-05-04T19:59:24+02:00",
    title: "Struggle-text details now preselect reinforcement vocabulary",
    summary:
      "Texts created from the struggle-words CTA now open with reinforcement vocabulary selected in the real checkbox list, matching daily-weather behavior.",
    userHighlights: [
      "After `Text mit deinen Wörtern Text erstellen`, reinforcement words are prechecked in vocabulary details.",
      "If a reinforcement word already exists in the selectable list, you now see only one entry instead of a duplicate unchecked preview row.",
      "Manual deselection still persists and is not auto-undone.",
    ],
    technicalNotes: [
      "Generalized default preselection in `src/utils/vocabularySelectionState.ts` to include both `DAILY_NUDGE` and `STRUGGLE_PRACTICE` origins.",
      "Updated `reeprise/$generatedTextId.tsx` to render synthetic struggle preview cards only for unresolved entries not already present in fetched selectable vocabulary.",
      "Added regression coverage in `src/utils/vocabularySelectionState.test.ts` and `e2e/reeprise-generation.spec.ts` for struggle-text prechecked selection behavior.",
    ],
  },
  {
    id: "daily-weather-vocabulary-now-shows-single-selectable-checked-card",
    timestamp: "2026-05-04T19:56:40+02:00",
    title: "Daily weather vocabulary now opens as one selectable checked list",
    summary:
      "Daily weather text details no longer show a duplicate unchecked reinforcement preview card, so reinforcement words appear as the real checked vocabulary entries.",
    userHighlights: [
      "Daily weather reinforcement words now appear once in the vocabulary list.",
      "Those words are shown in the selectable checkbox list and open preselected.",
      "The confusing unchecked duplicate card is removed for daily weather texts.",
    ],
    technicalNotes: [
      "Scoped `showStruggleWords` in `src/routes/app/reeprise/$generatedTextId.tsx` to `origin === STRUGGLE_PRACTICE` so daily nudge details stop rendering the synthetic unchecked struggle preview list.",
      "Kept daily default checkbox preselection logic for linked vocabulary IDs in place.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert daily reinforcement words render as a single checked selectable entry.",
    ],
  },
  {
    id: "daily-weather-text-details-now-default-select-linked-vocabulary",
    timestamp: "2026-05-04T19:51:13+02:00",
    title: "Daily weather text details now default-select linked vocabulary",
    summary:
      "When opening a daily weather-generated text, the vocabulary list now reliably starts with the generation-linked words selected.",
    userHighlights: [
      "Daily weather text details now show linked vocabulary checkboxes checked by default.",
      "Chapter continuation can start immediately without reselecting those words.",
      "Manual deselection still persists and prevents automatic re-adding.",
    ],
    technicalNotes: [
      "Updated `resolveDefaultSelectedVocabularyIdsForDailyNudge()` in `src/utils/vocabularySelectionState.ts` to fall back to all available linked vocabulary IDs when `struggle_words` IDs are empty.",
      "This keeps daily default-selection behavior stable even if the `struggle_words` payload is missing or incomplete.",
      "Added regression coverage in `src/utils/vocabularySelectionState.test.ts` for the new daily fallback path.",
    ],
  },
  {
    id: "word-added-toast-now-handles-multi-word-phrases",
    timestamp: "2026-05-04T19:48:31+02:00",
    title: "Vocabulary add toast now handles multi-word phrases correctly",
    summary:
      "When adding a phrase with multiple words, the success toast now uses plural wording instead of always saying a single word was added.",
    userHighlights: [
      "Single-word additions still show `Wort hinzugefügt`.",
      "Phrase additions now show `Wörter hinzugefügt`.",
      "The same behavior applies no matter whether you add from title or body text.",
    ],
    technicalNotes: [
      "Added `resolveWordAddedToastKey()` in `src/utils/resolveWordAddedToastKey.ts` to select singular vs plural toast keys from the added vocabulary text.",
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` to use the helper in the shared `onVocabularyCreate(...)` path.",
      "Added translation key `words_added_toast` in `src/assets/translations.json` and regression tests in `src/utils/resolveWordAddedToastKey.test.ts`.",
    ],
  },
  {
    id: "topic-placeholder-now-uses-medium-neutral-wording",
    timestamp: "2026-05-04T19:46:36+02:00",
    title: "Topic placeholder now works across text and audio formats",
    summary:
      "The topic input prompt now uses medium-neutral wording so it fits text, audio, podcasts, and future media types.",
    userHighlights: [
      "The topic field no longer implies read-only content.",
      "The prompt now fits multiple content formats consistently.",
      "The wording stays short and clear in German and English.",
    ],
    technicalNotes: [
      "Updated translation key `form_topic_placeholder` in `src/assets/translations.json`.",
      "German copy changed to `Welches Thema interessiert dich?`.",
      "English copy changed to `What topic are you interested in?`.",
    ],
  },
  {
    id: "generated-text-details-no-longer-crashes-on-resolved-display-text-reference",
    timestamp: "2026-05-04T19:45:15+02:00",
    title:
      "Generated text details no longer crashes while preparing chapter vocabulary",
    summary:
      "Opening generated text details now avoids a runtime crash that could happen before chapter generation vocabulary was resolved.",
    userHighlights: [
      "Generated text detail pages now load normally without a `resolvedDisplayText` runtime error.",
      "Chapter generation vocabulary extraction remains available after opening a text or chapter.",
      "No workflow changes were introduced for selecting vocabulary or continuing chapters.",
    ],
    technicalNotes: [
      "Added a memoized `resolvedDisplayText` in `src/routes/app/reeprise/$generatedTextId.tsx` so chapter-generation vocabulary resolution always receives a defined display-text source.",
      "Aligned source selection with active context: chapter content when a chapter is open, otherwise resolved base-story text.",
      "This removes the undefined identifier path that caused `ReferenceError: resolvedDisplayText is not defined` at runtime.",
    ],
  },
  {
    id: "generate-text-now-shows-a-clear-error-when-text-type-is-missing",
    timestamp: "2026-05-04T19:43:19+02:00",
    title: "Generate text now shows a clear error when text type is missing",
    summary:
      "If text creation is submitted without selecting a text type, the form now shows an explicit error message instead of failing silently.",
    userHighlights: [
      "Submitting without a selected text type now shows a clear prompt to choose one.",
      "Validation feedback is now consistent with other missing generation settings.",
      "The message appears immediately when submission fails for this reason.",
    ],
    technicalNotes: [
      "Updated `src/components/GenerateTextCard.tsx` submit-error handling to show a snackbar when `errors.typ` is present.",
      "Added translation key `form_error_text_type_required` in `src/assets/translations.json` for German and English copy.",
      "No backend or API changes were required.",
    ],
  },
  {
    id: "daily-weather-text-reinforcement-words-now-preselected-for-next-chapter",
    timestamp: "2026-05-04T19:38:32+02:00",
    title:
      "Daily weather text reinforcement words now preselect for next chapter",
    summary:
      "On daily weather texts, the reinforcement words used for the card are now preselected in the vocabulary list when generating the next chapter.",
    userHighlights: [
      "Daily weather text cards now open with their reinforcement words already selected in the vocabulary panel.",
      "You can continue to the next chapter immediately without reselecting those words manually.",
      "If you explicitly deselect one of these words, it stays deselected and is not auto-added again.",
    ],
    technicalNotes: [
      "Added `resolveDefaultSelectedVocabularyIdsForDailyNudge()` in `src/utils/vocabularySelectionState.ts` to preselect available `struggle_words` ids only for `DAILY_NUDGE` texts.",
      "Wired the helper into `src/routes/app/reeprise/$generatedTextId.tsx` before selection pruning so daily reinforcement words become default chapter-generation selections.",
      "Extended `src/utils/vocabularySelectionState.test.ts` with regression coverage for daily preselection, non-daily behavior, and deselection persistence.",
    ],
  },
  {
    id: "struggle-word-generation-now-uses-a-new-topic-seed-each-time",
    timestamp: "2026-05-04T19:38:45+02:00",
    title: "Struggle-word generation now uses a fresh topic seed each time",
    summary:
      "Generating a struggle-word text now creates a unique backend topic seed per request, so repeated generations no longer share the same static topic input.",
    userHighlights: [
      "Each struggle-word generation now starts with a fresh topic seed.",
      "Repeated clicks no longer reuse the same fixed topic input.",
      "Daily creation behavior remains unchanged while struggle generation gets unique request context.",
    ],
    technicalNotes: [
      "Added `buildStruggleTopicSeed()` in `src/utils/buildStruggleTopicSeed.ts` to produce a stable struggle prefix plus language and ISO timestamp.",
      "Updated `src/components/GenerateTextCard.tsx` to use `buildStruggleTopicSeed(formLanguage)` instead of `struggle_words_topic_generic` for struggle-text creation.",
      "Added regression tests in `src/utils/buildStruggleTopicSeed.test.ts` to verify normalization and per-generation uniqueness.",
    ],
  },
  {
    id: "chapter-generation-now-preserves-multi-word-vocabulary-order",
    timestamp: "2026-05-04T19:37:29+02:00",
    title: "Chapter generation now preserves multi-word vocabulary order",
    summary:
      "When you generate a new chapter from highlighted vocabulary, adjacent selected words are now passed as one ordered phrase so the generated text keeps them together more reliably.",
    userHighlights: [
      "Multi-word selections from the text can now be carried into chapter generation as one contiguous phrase.",
      "Generated chapters are now more likely to keep phrase words together instead of scattering them across sentences.",
      "Highlighted generation vocabulary still remains available for normal per-word highlighting behavior in the new chapter.",
    ],
    technicalNotes: [
      "Extended `src/utils/resolveSelectedVocabularyWordsForChapterGeneration.ts` to reconstruct ordered phrase runs from selected vocabulary usage spans in the active text/chapter.",
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` to provide current display text and chapter/text context to the generation-word resolver.",
      "Added resolver regression tests in `src/utils/resolveSelectedVocabularyWordsForChapterGeneration.test.ts` and strengthened chapter-generation prompt guidance in `barkguide/reeprise/tasks.py` for contiguous multi-word entries.",
    ],
  },
  {
    id: "continued-text-keeps-selected-word-highlights-during-chapter-load",
    timestamp: "2026-05-04T19:37:23+02:00",
    title: "Continued text now keeps selected-word highlights while loading",
    summary:
      "When continuing a text with selected words, the new chapter now keeps those words highlighted immediately instead of briefly dropping highlights during chapter load.",
    userHighlights: [
      "Selected words used for continue now stay visibly highlighted right after navigation.",
      "You no longer get a blank highlight state while the new chapter data settles.",
      "Highlight behavior remains consistent once the chapter payload finishes loading.",
    ],
    technicalNotes: [
      "Added `pendingChapterGeneratedWords` fallback handling in `src/routes/app/reeprise/$generatedTextId.tsx` so selected words remain available for highlight resolution across the chapter transition.",
      "Extended `resolveActiveTextHighlightWords()` in `src/utils/resolveActiveTextHighlightWords.ts` with `pendingChapterGeneratedWords` support.",
      "Added regression coverage in `src/utils/resolveActiveTextHighlightWords.test.ts` for pending-chapter highlight fallback behavior.",
    ],
  },
  {
    id: "newly-generated-highlighted-text-details-now-open-at-the-top",
    timestamp: "2026-05-04T19:37:20+02:00",
    title: "Newly generated highlighted-text details now open at the top",
    summary:
      "After generating a new text with highlighted vocabulary, the details page now reliably opens at the top instead of keeping an older scroll position.",
    userHighlights: [
      "Generating a new text with highlighted vocabulary now lands at the top of the details page.",
      "The first section of the new text is immediately visible without manual scrolling.",
      "Route transitions to new text details feel consistent across screen sizes.",
    ],
    technicalNotes: [
      "Updated `src/utils/resetAppScrollPosition.ts` to always reset both app-content container scroll and window scroll.",
      "This fixes breakpoint/layout cases where the active scroll container is not the same element across views.",
      "Adjusted utility regression coverage in `src/utils/resetAppScrollPosition.test.ts` to assert the combined scroll-reset behavior.",
    ],
  },
  {
    id: "selected-vocabulary-highlights-now-handle-unicode-and-smart-apostrophes",
    timestamp: "2026-05-03T20:14:12+02:00",
    title: "Selected vocabulary highlights now survive smart punctuation",
    summary:
      "Words used to generate follow-up text now stay highlighted even when the generated text uses typographic apostrophes or non-ASCII letter forms.",
    userHighlights: [
      "Selected words remain highlighted after generating text with selected vocabulary.",
      "Highlighting now works reliably for words containing apostrophes in both straight and typographic forms.",
      "Highlight behavior is more stable across multilingual text with Unicode characters.",
    ],
    technicalNotes: [
      "Updated highlight-word boundary matching in `src/utils/resolveGeneratedTextHighlightWords.ts` to use Unicode-aware word character detection.",
      "Added apostrophe-variant matching (`'` and `’`) so selected-vocabulary highlights still resolve when model output normalizes punctuation differently.",
      "Applied the same matching behavior in `src/components/GeneratedTextDetailsTextPane.tsx` and added regression coverage in `src/utils/resolveGeneratedTextHighlightWords.test.ts`.",
    ],
  },
  {
    id: "scroll-position-now-resets-after-new-text-navigation-and-trainer-actions",
    timestamp: "2026-05-03T20:00:23+02:00",
    title: "Scroll position now resets for new text and trainer transitions",
    summary:
      "Generating a new text now opens the text details page at the top, and finishing trainer-card actions now returns you to the top of the trainer card so the next word is immediately readable.",
    userHighlights: [
      "After generating a new text, the details page now starts at the top instead of keeping an old scroll offset.",
      "After trainer actions like known/almost/forgotten/delete, the page scrolls back to the trainer top for the next word.",
      "The next review step stays readable without manual scrolling after each action.",
    ],
    technicalNotes: [
      "Added shared `resetAppScrollPosition()` in `src/utils/resetAppScrollPosition.ts` to centralize app-scroll reset behavior with window fallback.",
      "Updated `src/routes/app/reeprise/$generatedTextId.tsx` to use the shared reset helper on generated-text/chapter transitions.",
      "Updated `src/components/VocabularyPreviewCard.tsx` to trigger `scrollToTop()` after review and delete actions, and added utility regression tests in `src/utils/resetAppScrollPosition.test.ts`.",
    ],
  },
  {
    id: "context-selection-vocabulary-save-no-longer-highlights-unrelated-words",
    timestamp: "2026-05-03T19:58:32+02:00",
    title: "Context-selection save now avoids unrelated word highlights",
    summary:
      "Saving a context-selected phrase to vocabulary now keeps highlights limited to explicit selection state instead of lighting up unrelated saved words in the same text.",
    userHighlights: [
      "After saving a selected phrase, unrelated words no longer become highlighted automatically.",
      "Highlighting remains tied to your explicit word selection choices.",
      "Context-mode save still provides immediate visual feedback for the saved selection.",
    ],
    technicalNotes: [
      "Replaced persisted-usage-driven highlight starts in `src/components/GeneratedTextDetailsTextPane.tsx` with starts derived from `highlightWords` (explicit selection source of truth).",
      "Re-enabled selected-highlight vocabulary mapping by wiring `buildSelectedHighlightPreviewVocabularyByStartMap` so preview flyouts still resolve correctly for selected highlights.",
      "Kept local immediate highlight state for newly saved context selections while preventing broad persisted vocabulary highlight fan-out.",
    ],
  },
  {
    id: "context-selection-save-now-highlights-all-selected-words",
    timestamp: "2026-05-03T19:58:18+02:00",
    title: "Context-selection save now highlights all selected words",
    summary:
      "Saving a translated context selection to vocabulary now highlights every selected word in the text instead of highlighting only the first token.",
    userHighlights: [
      "After saving a multi-word context selection, all selected words stay visibly highlighted in the text.",
      "The selection-to-highlight behavior is now consistent for phrase saves in context mode.",
      "Preview/flyout access now works from each selected word in the saved phrase selection.",
    ],
    technicalNotes: [
      "Updated context-selection save handling in `src/components/GeneratedTextDetailsTextPane.tsx` to add all selected token starts to local highlight state.",
      "Mapped the saved vocabulary preview data to each selected token start (instead of only the first token) so each selected word remains interactive.",
      "Extended E2E regression coverage in `e2e/highlighted-word-preview.spec.ts` to assert both selected words receive highlight styling after a context-save action.",
    ],
  },
  {
    id: "daily-text-creation-now-prevents-same-day-duplicate-starts",
    timestamp: "2026-05-03T19:57:47+02:00",
    title: "Daily text creation now blocks duplicate same-day starts",
    summary:
      "Creating the daily text now reliably returns the existing text for the day instead of starting a second one with the same placeholder headline.",
    userHighlights: [
      "Tapping the daily card twice in quick succession no longer starts two daily texts.",
      "You now get one daily text per language and day, even under fast repeated taps.",
      "This prevents repeated same-day headline/topic placeholders from appearing in parallel.",
    ],
    technicalNotes: [
      "Wrapped `create_practice_text_for_user_language()` in a DB transaction and added a per-user `select_for_update()` lock in `barkguide/reeprise/tasks.py`.",
      "Kept the existing same-day lookup inside the lock so concurrent requests reuse the already-created `DAILY_NUDGE` record.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to verify second same-day calls return HTTP 200 with the same generated text id and keep only one daily record.",
    ],
  },
  {
    id: "vocabulary-highlights-now-stay-limited-to-explicit-word-selection",
    timestamp: "2026-05-03T19:56:13+02:00",
    title: "Vocabulary highlights now stay limited to explicit word selection",
    summary:
      "Adding a word no longer auto-selects unrelated saved vocabulary in the same text, so highlights now remain tied to what you explicitly selected.",
    userHighlights: [
      "Adding a new word highlights that word, without activating unrelated entries automatically.",
      "Unselected words stay visually quiet unless you explicitly toggle them on.",
      "Selection state stays stable when the vocabulary list refreshes.",
    ],
    technicalNotes: [
      "Removed the reeprise details behavior that repopulated `selectedVocabularyIds` from the full fetched vocabulary list after refresh.",
      "Kept selection strictly explicit by updating list-toggle handlers and by pruning stale ids only when vocabulary entries disappear.",
      "Added regression coverage in `src/utils/vocabularySelectionState.test.ts` for the selection state transition that previously reintroduced automatic highlights.",
    ],
  },
  {
    id: "landing-route-links-now-pass-explicit-onboarding-search-state",
    timestamp: "2026-05-03T08:52:33+02:00",
    title:
      "Landing-page navigation now preserves typed root-route search state",
    summary:
      "Website navbar and onboarding close actions now pass an explicit root search object so home-route navigation remains type-safe and build errors are resolved.",
    userHighlights: [
      "Homepage brand links continue to work as before.",
      "Closing the onboarding overlay returns to the landing page normally.",
      "No visible layout or flow changes were introduced.",
    ],
    technicalNotes: [
      "Updated root-route `Link` usages in `src/components/NavbarSmallWebsite.tsx` and `src/components/NavbarWebsite.tsx` to include `search={{ onboarding: undefined }}`.",
      "Updated onboarding close navigation in `src/routes/index.tsx` to pass typed root search state.",
      "This resolves TanStack Router TS2741/TS2322 errors caused by missing required `search` on route `/` navigation targets.",
    ],
  },
  {
    id: "onboarding-final-step-now-focuses-on-account-needed-to-save-progress",
    timestamp: "2026-05-02T14:21:58+02:00",
    title: "Onboarding final step now clearly explains account requirement",
    summary:
      "The final onboarding step was reframed to account-creation intent and now explicitly explains that an account is needed to store learning progress and settings.",
    userHighlights: [
      "Headline now asks users to create their language space directly.",
      "Supporting text clearly states that progress, vocabulary, and settings are saved with an account.",
      "The temporary dummy preview interaction was removed from this step.",
    ],
    technicalNotes: [
      "Updated `onboarding_preview_title` and `onboarding_preview_subtitle` copy in `src/assets/translations.json` (DE/EN).",
      "Removed onboarding dummy preview interaction code from `src/routes/start-learning.tsx`.",
      "Updated `e2e/start-learning-onboarding.spec.ts` heading assertion to the new final-step wording.",
    ],
  },
  {
    id: "onboarding-preview-now-shows-interactive-dummy-highlighted-text",
    timestamp: "2026-05-02T14:17:20+02:00",
    title: "Onboarding preview now includes interactive highlighted demo text",
    summary:
      "The final onboarding screen now demonstrates core reading interaction with a dummy sample text that includes tappable highlighted words and inline meaning preview.",
    userHighlights: [
      "Preview step now shows a realistic sample text block before finishing onboarding.",
      "Highlighted words can be tapped to reveal a short meaning, mirroring the real reading flow.",
      "This makes app behavior easier to understand without requiring a generated text first.",
    ],
    technicalNotes: [
      "Extended preview UI in `src/routes/start-learning.tsx` with local interactive highlighted-text rendering and selected-word meaning panel.",
      "Added onboarding preview demo translation keys in `src/assets/translations.json` for DE/EN sample text, highlighted words, and meaning labels.",
      "No backend/API calls were introduced; behavior is local-state only for onboarding demonstration.",
    ],
  },
  {
    id: "onboarding-interest-step-title-now-uses-clearer-german-wording",
    timestamp: "2026-05-02T14:13:17+02:00",
    title: "Onboarding interest step title now uses clearer German wording",
    summary:
      "The German headline on the interest step was rewritten to a clearer, direct question so users immediately understand what input is expected.",
    userHighlights: [
      "The step now asks `Welche Themen interessieren dich gerade?`.",
      "The intent of the screen is easier to understand at a glance.",
      "Input behavior and onboarding flow remain unchanged.",
    ],
    technicalNotes: [
      "Updated `onboarding_interest_title` in `src/assets/translations.json` (German value).",
      "Adjusted heading assertion in `e2e/start-learning-onboarding.spec.ts`.",
      "No component logic, validation, or backend behavior changed.",
    ],
  },
  {
    id: "onboarding-text-format-step-now-enforces-single-selection",
    timestamp: "2026-05-02T14:12:29+02:00",
    title: "Onboarding format step now allows only one selection",
    summary:
      "The text-format step was changed from multi-select to single-select so users choose exactly one primary format before continuing.",
    userHighlights: [
      "Only one format can be active at a time in `Welches Format passt zu dir?`.",
      "Choosing a new format now replaces the previous one directly.",
      "Step order and navigation remain unchanged.",
    ],
    technicalNotes: [
      "Replaced `selectedTextTypes` array state with single `selectedTextType` state in `src/routes/start-learning.tsx`.",
      "Updated continue-button validation and preview summary mapping to single-format behavior.",
      "Adjusted onboarding E2E flow in `e2e/start-learning-onboarding.spec.ts` to the single-select interaction.",
    ],
  },
  {
    id: "onboarding-main-character-step-now-shows-explicit-own-character-separator",
    timestamp: "2026-05-02T14:10:29+02:00",
    title:
      "Onboarding main-character step now shows a clear `own character` separator",
    summary:
      "A visual separator line was added between preset character options and the custom-name input to make the alternative input path immediately clear.",
    userHighlights: [
      "Character presets and custom-name entry are now visually separated.",
      "The custom path is explicitly labeled as `oder eigene Hauptfigur`.",
      "This reduces confusion about whether presets and custom input are combined or alternative choices.",
    ],
    technicalNotes: [
      "Added a divider row with new translation key `onboarding_custom_character_separator` in `src/routes/start-learning.tsx`.",
      "Added `onboarding_custom_character_separator` translations in `src/assets/translations.json` (DE/EN).",
      "No state logic, validation behavior, or backend integration changed.",
    ],
  },
  {
    id: "onboarding-main-character-presets-now-include-names-and-short-bios",
    timestamp: "2026-05-02T14:08:52+02:00",
    title: "Onboarding character presets now include names with short bios",
    summary:
      "Main-character presets in onboarding were rewritten to include a concrete name plus a short descriptor, making options easier to imagine and compare.",
    userHighlights: [
      "Character options now read as named personas instead of abstract role labels.",
      "Each preset includes a very short bio for quicker orientation.",
      "Selection behavior and custom-name input remain unchanged.",
    ],
    technicalNotes: [
      "Updated character preset translation values in `src/assets/translations.json` for German and English.",
      "No onboarding step flow, component state, or validation logic changed.",
      "No backend or API changes were required.",
    ],
  },
  {
    id: "onboarding-shell-label-now-uses-singular-sprachwelt-wording",
    timestamp: "2026-05-02T13:59:07+02:00",
    title: "Onboarding shell label now uses singular `Sprachwelt` wording",
    summary:
      "The onboarding top-shell label was updated from plural to singular phrasing to better match the intended concept framing.",
    userHighlights: [
      "The onboarding header now reads `In Sprachwelt eintauchen`.",
      "Wording now uses singular concept language as requested.",
      "No onboarding flow behavior or controls were changed.",
    ],
    technicalNotes: [
      "Updated static onboarding shell label text in `src/routes/start-learning.tsx`.",
      "No translation-key mapping, route logic, or validation behavior changed.",
      "No backend/API changes were required.",
    ],
  },
  {
    id: "onboarding-main-character-step-now-explicitly-explains-option-or-custom-name",
    timestamp: "2026-05-02T13:58:18+02:00",
    title:
      "Onboarding main-character step now clearly explains option vs custom name",
    summary:
      "The main-character helper text was rewritten to explicitly state that users can either pick a preset option or enter a custom name, removing ambiguity in the setup step.",
    userHighlights: [
      "The step now clearly communicates the `either option or own name` input model.",
      "Users can understand the expected action without guessing from the UI alone.",
      "Main-character presets and custom input behavior stay unchanged.",
    ],
    technicalNotes: [
      "Updated `onboarding_main_character_subtitle` in `src/assets/translations.json` (German and English).",
      "No component state, validation, or onboarding step order logic changed.",
      "No backend or API modifications were required.",
    ],
  },
  {
    id: "onboarding-change-anytime-hint-now-uses-selection-specific-wording",
    timestamp: "2026-05-02T13:55:41+02:00",
    title:
      "Onboarding `change anytime` hint now uses selection-specific wording",
    summary:
      "The onboarding reassurance hint was rewritten from abstract phrasing to explicit selection wording so users immediately understand what can be changed later.",
    userHighlights: [
      "German onboarding copy now reads `Du kannst deine Auswahl jederzeit ändern.`.",
      "The hint now feels more concrete and less generic across setup steps.",
      "The onboarding flow and available options remain unchanged.",
    ],
    technicalNotes: [
      "Updated `onboarding_text_type_change_anytime` in `src/assets/translations.json` (DE/EN).",
      "Updated shared `onboarding_change_anytime_hint` in `src/assets/translations.json` (DE/EN).",
      "No route logic, validation, or API behavior changed.",
    ],
  },
  {
    id: "mobile-onboarding-modal-now-uses-full-screen-layout",
    timestamp: "2026-05-02T13:55:04+02:00",
    title: "Mobile onboarding now uses a true full-screen flow",
    summary:
      "The website onboarding modal now fills the full mobile viewport so setup feels like a dedicated flow instead of a padded card overlay.",
    userHighlights: [
      "On phones, onboarding now opens edge-to-edge with full-height coverage.",
      "Mobile no longer shows inset gaps around onboarding content.",
      "Desktop keeps the existing centered modal-card presentation.",
    ],
    technicalNotes: [
      "Updated modal wrapper sizing in `src/routes/index.tsx` to remove mobile padding/insets and stretch to viewport.",
      "Updated embedded onboarding shell in `src/routes/start-learning.tsx` to use `100dvh` on mobile with desktop card styling preserved.",
      "Added mobile viewport regression coverage in `e2e/start-learning-modal-entry.spec.ts` and a stable `data-testid` on the onboarding shell.",
    ],
  },
  {
    id: "onboarding-selected-language-display-now-uses-polished-chip-layout",
    timestamp: "2026-05-02T12:10:06+02:00",
    title:
      "Onboarding selected-language display now uses polished spacing and chip styling",
    summary:
      "The `Ausgewählt` row in onboarding language selection was redesigned from plain inline text to a structured label + chip group with improved spacing and typography.",
    userHighlights: [
      "Selected languages now appear as clean chips instead of a single comma-separated line.",
      "Label hierarchy and spacing now better match the rest of the onboarding card style.",
      "The selected-state area is now easier to scan on mobile and desktop.",
    ],
    technicalNotes: [
      "Updated selected-language block in `src/routes/start-learning.tsx` to a bordered container with uppercase label and chip list.",
      "Adjusted margins and font sizes to align with existing onboarding form rhythm.",
      "No selection logic, filtering behavior, or validation flow changed.",
    ],
  },
  {
    id: "onboarding-now-shows-change-anytime-hint-on-all-direction-setting-steps",
    timestamp: "2026-05-02T12:07:45+02:00",
    title:
      "Onboarding now shows `change anytime` hint across all direction-setting steps",
    summary:
      "The reversibility hint is now consistently shown on every onboarding step where users set learning direction, not only on the format step.",
    userHighlights: [
      "Language, level, main character, format, and interest steps now all show that choices can be changed later.",
      "The message is consistent and visible across the full setup flow.",
      "This reduces perceived commitment pressure during onboarding.",
    ],
    technicalNotes: [
      "Updated `src/routes/start-learning.tsx` to pass a shared hint key to all direction-setting `StepShell` instances.",
      "Added shared translation key `onboarding_change_anytime_hint` in `src/assets/translations.json`.",
      "No step ordering, validation, or option-data behavior changed.",
    ],
  },
  {
    id: "onboarding-format-step-now-highlights-change-anytime-as-separate-hint",
    timestamp: "2026-05-02T12:06:06+02:00",
    title:
      "Onboarding format step now highlights `change anytime` as a distinct hint",
    summary:
      "The reversibility message on the format step is now shown as a separate highlighted hint line instead of being embedded in the subtitle.",
    userHighlights: [
      "The `you can change this anytime` message is now clearly visible at a glance.",
      "Subtitle stays concise while the reassurance note gets its own emphasis treatment.",
      "No step order or interaction behavior changed.",
    ],
    technicalNotes: [
      "Extended `StepShell` in `src/routes/start-learning.tsx` with an optional `hint` prop and added a distinct hint style.",
      "Wired `onboarding_text_type_change_anytime` to the text-type step and shortened `onboarding_text_type_subtitle`.",
      "Added the new translation key in `src/assets/translations.json`; flow tests remain green.",
    ],
  },
  {
    id: "onboarding-text-format-step-now-explicitly-says-setting-can-be-changed-anytime",
    timestamp: "2026-05-02T12:01:05+02:00",
    title:
      "Onboarding text-format step now explicitly states the choice is reversible",
    summary:
      "The text-format helper line now tells users directly that the selected format can be changed anytime.",
    userHighlights: [
      "Users now get immediate reassurance that format choice is not permanent.",
      "The text-format step feels lower risk and easier to proceed through.",
      "No interaction flow or selection behavior changed.",
    ],
    technicalNotes: [
      "Updated `onboarding_text_type_subtitle` in `src/assets/translations.json` (German and English).",
      "No component logic, route behavior, or validation changes were required.",
      "No backend/API changes were introduced.",
    ],
  },
  {
    id: "onboarding-now-separates-format-choice-from-interest-prompt-screen",
    timestamp: "2026-05-02T11:58:09+02:00",
    title: "Onboarding now separates format choice from interest prompting",
    summary:
      "The text-type step was reframed around content format, and a new separate optional interest screen now captures free-text user interests with smarter prompt examples instead of generic starter categories.",
    userHighlights: [
      "Text type now has its own dedicated format-focused step.",
      "Interests now live on a separate optional screen with free-text input.",
      "Suggestion examples are now more specific reading hooks, not broad generic categories.",
    ],
    technicalNotes: [
      "Extended onboarding step flow in `src/routes/start-learning.tsx` with a new `interest` step and moved free-text interest input out of the text-type step.",
      "Updated text-type and interest copy plus new interest-example translation keys in `src/assets/translations.json`.",
      "Updated e2e flow assertions in `e2e/start-learning-onboarding.spec.ts` to include the new optional interest step.",
    ],
  },
  {
    id: "onboarding-main-character-step-now-includes-first-person-and-clear-name-label",
    timestamp: "2026-05-02T11:54:24+02:00",
    title:
      "Onboarding main-character step now includes Ich-Perspektive and clearer name label",
    summary:
      "The main-character step now offers an explicit `Ich-Perspektive` option and the custom-name field label was clarified to avoid confusion with the user's own name.",
    userHighlights: [
      "`Ich-Perspektive` is now available as a direct selectable character perspective.",
      "Custom name field now reads `Name der Hauptfigur` for clearer intent.",
      "Step interaction flow and validation remain unchanged.",
    ],
    technicalNotes: [
      "Added translation key `onboarding_main_character_first_person` in `src/assets/translations.json` and included it in the main-character preset list.",
      "Updated `onboarding_custom_character_label` translation to `Name der Hauptfigur` / `Main character name`.",
      "No route logic, API, or state-model changes were introduced.",
    ],
  },
  {
    id: "onboarding-main-character-step-now-uses-content-centered-wording",
    timestamp: "2026-05-02T11:52:37+02:00",
    title: "Onboarding main-character step now uses content-centered wording",
    summary:
      "The main-character step copy was revised to explicitly frame the choice as the lead character of the user's content, with updated perspective guidance text.",
    userHighlights: [
      "Question now asks who should be the main character of your content.",
      "Supporting line now explains perspective in a clearer content-focused way.",
      "Step behavior and selectable options remain unchanged.",
    ],
    technicalNotes: [
      "Updated `onboarding_main_character_title` and `onboarding_main_character_subtitle` in `src/assets/translations.json`.",
      "No route, component logic, or validation flow changes were needed.",
      "No API or data model updates were introduced.",
    ],
  },
  {
    id: "landing-page-now-opens-onboarding-as-a-closable-modal-from-navbar",
    timestamp: "2026-05-02T11:51:02+02:00",
    title:
      "Landing page now opens onboarding as a closable modal from the navbar",
    summary:
      "Users can now start the onboarding flow directly from the root-page navigation via a `Start exploring` action, shown as a closable modal for low-friction entry.",
    userHighlights: [
      "A new navbar action opens onboarding instantly from the landing page.",
      "The onboarding opens in a modal that can be closed anytime.",
      "Direct `/start-learning` access still works, so both entry paths remain available.",
    ],
    technicalNotes: [
      "Refactored onboarding UI into reusable `StartLearningOnboardingFlow` in `src/routes/start-learning.tsx` with optional `embedded` mode and close callback.",
      "Added root-route search handling (`/?onboarding=1`) in `src/routes/index.tsx` and mounted modal overlay with close navigation.",
      "Added nav entry wiring in `src/components/NavbarWebsite.tsx`, new i18n keys in `src/assets/translations.json`, and e2e coverage in `e2e/start-learning-modal-entry.spec.ts`.",
    ],
  },
  {
    id: "root-website-language-switch-now-updates-copy-without-affecting-app-settings",
    timestamp: "2026-05-02T11:43:57+02:00",
    title: "Website root language switch now updates DE/EN copy reliably",
    summary:
      "The language selector on the public website now correctly switches between German and English text without changing how app language works inside `/app`.",
    userHighlights: [
      "Switching EN/DE on the website root now immediately updates landing-page copy.",
      "Language selection remains persistent via local storage for website pages.",
      "In-app language behavior under `/app/**` remains unchanged.",
    ],
    technicalNotes: [
      "Updated `src/hooks/useTranslation.ts` to resolve language by route scope: `/app/**` keeps using `userSettings.app_language`, while non-app routes use `local-language`.",
      "Prevented non-app routes from being overwritten by app-language sync in the translation hook effect.",
      "Adjusted website navbar language select sizing (`text-base`) and added e2e regression coverage in `e2e/root-language-switch.spec.ts`.",
    ],
  },
  {
    id: "onboarding-language-search-placeholder-now-stays-standard-while-typing",
    timestamp: "2026-05-02T11:30:27+02:00",
    title: "Onboarding language search now keeps standard placeholder behavior",
    summary:
      "The language selector placeholder now stays a fixed prompt and no longer doubles as selected-value text, matching standard searchable select behavior.",
    userHighlights: [
      "Placeholder now disappears naturally when typing and remains a stable prompt when empty.",
      "Selected languages are now shown separately below the field.",
      "Search interaction now feels closer to expected modern select controls.",
    ],
    technicalNotes: [
      "Updated language search input in `src/routes/start-learning.tsx` to always use `onboarding_language_dropdown_placeholder` as placeholder.",
      "Added separate selected-language display line with new translation key `onboarding_language_selected_label`.",
      "Kept filtering, selection, and dropdown behavior unchanged.",
    ],
  },
  {
    id: "onboarding-language-selector-now-supports-live-search-while-typing",
    timestamp: "2026-05-02T11:27:20+02:00",
    title: "Onboarding language selector now supports live search while typing",
    summary:
      "Typing in the language selector now filters available languages in real time, so users can quickly find entries in long lists.",
    userHighlights: [
      "Language selection now works like searchable dropdowns in modern apps.",
      "Typing narrows the language option list immediately.",
      "A clear empty-state message appears when no language matches the search.",
    ],
    technicalNotes: [
      "Replaced static button-only selector behavior in `src/routes/start-learning.tsx` with a text input + filtered option list.",
      "Added memoized filtering by translated language name, short code, and language id.",
      "Added new i18n keys (`onboarding_language_menu_toggle`, `onboarding_language_search_empty`) and updated e2e coverage in `e2e/start-learning-onboarding.spec.ts`.",
    ],
  },
  {
    id: "onboarding-language-badges-now-align-to-the-right-in-language-rows",
    timestamp: "2026-05-02T11:24:17+02:00",
    title: "Onboarding language short-code badges now align right",
    summary:
      "Language code badges like `EN` and `DE` are now right-aligned in language rows for cleaner scanning and consistent row structure.",
    userHighlights: [
      "Language names stay left while short-code badges align to the right edge.",
      "The same alignment pattern is used in both language selection and per-language level rows.",
      "Visual rhythm is more consistent when scanning long language lists.",
    ],
    technicalNotes: [
      "Updated row layout in `src/routes/start-learning.tsx` to place language badges with right alignment (`ml-auto` / `justify-between`).",
      "Adjusted badge sizing to support 2+ character codes consistently.",
      "No selection, validation, or data flow logic changed.",
    ],
  },
  {
    id: "onboarding-language-dropdown-now-scrolls-inside-options-list-only",
    timestamp: "2026-05-02T11:22:19+02:00",
    title:
      "Onboarding language dropdown now scrolls inside the options list only",
    summary:
      "Scrolling in the language selection step now follows standard dropdown behavior: only the opened options list scrolls, while surrounding form content stays fixed.",
    userHighlights: [
      "Language option scrolling is now confined to the dropdown list.",
      "Input fields and step layout no longer scroll when browsing long language options.",
      "The interaction now feels closer to expected modern UI dropdown behavior.",
    ],
    technicalNotes: [
      "Set the language options menu in `src/routes/start-learning.tsx` to `max-h-60 overflow-y-auto`.",
      "Removed generic inner scroll from `StepShell` content region so regular form sections remain stable.",
      "Kept onboarding structure and validation logic unchanged; this is an interaction behavior fix.",
    ],
  },
  {
    id: "onboarding-shell-label-now-uses-language-world-entry-copy",
    timestamp: "2026-05-02T11:19:50+02:00",
    title: "Onboarding shell label now uses concept-first entry wording",
    summary:
      "The onboarding top shell label was changed from a technical setup phrase to the concept-led wording `In Sprachwelten eintauchen`.",
    userHighlights: [
      "Header tone now feels less technical and more aligned with the product concept.",
      "The brand name was removed from the shell label to keep the line cleaner.",
      "Step content and flow remain unchanged.",
    ],
    technicalNotes: [
      "Updated the static onboarding shell label in `src/routes/start-learning.tsx`.",
      "No translation key or state logic changes were required.",
      "No API, routing, or validation behavior changed.",
    ],
  },
  {
    id: "onboarding-step-headers-now-stay-fixed-while-options-scroll",
    timestamp: "2026-05-02T11:18:13+02:00",
    title: "Onboarding step headers now stay fixed while option lists scroll",
    summary:
      "Scrolling behavior in onboarding was refined so only the option/content area scrolls; step title and subtitle now stay in place.",
    userHighlights: [
      "Long option lists now scroll independently without moving the step header.",
      "Question and helper text remain visible while browsing options.",
      "This behavior now applies consistently across all onboarding steps.",
    ],
    technicalNotes: [
      "Refactored `StepShell` in `src/routes/start-learning.tsx` into a fixed header + inner scroll-body layout (`flex` + `min-h-0` + `overflow-y-auto`).",
      "Moved scrolling responsibility from the whole step container to the step content region.",
      "No step logic, validation, or copy content was changed.",
    ],
  },
  {
    id: "onboarding-content-interests-now-support-free-text-entry",
    timestamp: "2026-05-02T10:18:28+02:00",
    title: "Onboarding content-interest step now supports free-text input",
    summary:
      "The `Welche Inhalte interessieren dich?` step now allows users to add their own interest keywords in addition to selecting predefined content types.",
    userHighlights: [
      "You can now enter custom interests directly in the onboarding content step.",
      "Continue validation now accepts either selected content types or custom free-text interests.",
      "The final onboarding summary now includes custom interest text next to selected presets.",
    ],
    technicalNotes: [
      "Added `customContentInterest` state and validation wiring in `src/routes/start-learning.tsx`.",
      "Added i18n keys for custom content label and placeholder in `src/assets/translations.json`.",
      "Extended e2e coverage in `e2e/start-learning-onboarding.spec.ts` to fill and verify custom interest text.",
    ],
  },
  {
    id: "onboarding-language-badges-now-use-neutral-codes-instead-of-flags",
    timestamp: "2026-05-02T10:12:26+02:00",
    title:
      "Onboarding language markers now use neutral short codes instead of flags",
    summary:
      "Language markers in onboarding were changed from flag icons to neutral short-code badges to avoid geopolitical signaling.",
    userHighlights: [
      "Language rows now show neutral badges like `EN`, `DE`, `FR` instead of country flags.",
      "Language and per-language level sections keep their visual anchors without political associations.",
      "All selection behavior remains unchanged.",
    ],
    technicalNotes: [
      "Replaced `resolveLanguageIcon` with `resolveLanguageBadge` in `src/routes/start-learning.tsx`.",
      "Language badge values now derive from `TEXT_LANGUAGES.short` with normalization for transcription variants.",
      "No flow, validation, or API changes were made.",
    ],
  },
  {
    id: "onboarding-language-lists-now-sort-alphabetically",
    timestamp: "2026-05-02T10:10:25+02:00",
    title: "Onboarding language lists now sort A–Z",
    summary:
      "Language options in onboarding are now sorted alphabetically by their visible label for faster scanning and predictable ordering.",
    userHighlights: [
      "The language dropdown now shows entries in A–Z order.",
      "Per-language level rows follow the same sorted order.",
      "Preview summary now keeps the same alphabetical language sequence.",
    ],
    technicalNotes: [
      "Added a `sortedLanguages` memo in `src/routes/start-learning.tsx` based on translated labels and locale-aware comparison.",
      "Updated language dropdown rendering and selected-language dependent sections to use the sorted list.",
      "No onboarding step logic or validation behavior was changed.",
    ],
  },
  {
    id: "onboarding-language-and-level-rows-now-include-icons",
    timestamp: "2026-05-02T10:09:32+02:00",
    title:
      "Onboarding language and level rows now include icons for faster scanning",
    summary:
      "Language selection and per-language level rows now show compact icons, so visual scanning stays consistent with the icon-supported character and content-type steps.",
    userHighlights: [
      "Language dropdown options now include icons next to each language name.",
      "Per-language level labels now show matching icons as visual anchors.",
      "The onboarding flow now uses icons consistently across all selection-heavy steps.",
    ],
    technicalNotes: [
      "Updated `src/routes/start-learning.tsx` to render language icons in checkbox rows and level labels.",
      "Added a small `resolveLanguageIcon` helper mapping language ids to icons.",
      "Kept existing selection logic and step order unchanged; this is a presentation-only update.",
    ],
  },
  {
    id: "main-character-presets-now-use-the-same-card-style-as-text-types",
    timestamp: "2026-05-02T10:07:45+02:00",
    title: "Main-character options now match text-type card styling",
    summary:
      "Character preset options in onboarding were restyled from pills to the same compact card pattern used in the text-type step.",
    userHighlights: [
      "Character options now look visually consistent with text-type choices.",
      "Selected and unselected states follow the same card language across both steps.",
      "Mobile alignment for icon and label is clearer and easier to scan.",
    ],
    technicalNotes: [
      "Updated main-character preset rendering in `src/routes/start-learning.tsx` from rounded pills to bordered cards with icon + label row.",
      "Reused the same visual classes as text-type cards to keep component style parity.",
      "Added a small local icon resolver for character cards; selection logic remains unchanged.",
    ],
  },
  {
    id: "onboarding-level-wording-now-uses-human-friendly-german-copy",
    timestamp: "2026-05-02T10:05:51+02:00",
    title: "Onboarding level wording now uses friendlier German phrasing",
    summary:
      "The onboarding step previously using `Sprachstand` was rewritten to more natural human wording in German, with aligned English updates.",
    userHighlights: [
      "The level question now sounds conversational and less technical.",
      "Dropdown and summary labels now use friendlier wording.",
      "Preview sentence wording was aligned to the same tone.",
    ],
    technicalNotes: [
      "Updated onboarding translation values in `src/assets/translations.json` for `onboarding_level_title`, `onboarding_level_dropdown_placeholder`, `onboarding_summary_levels`, and `onboarding_preview_text_middle`.",
      "Updated the level-step heading assertion in `e2e/start-learning-onboarding.spec.ts`.",
      "No behavioral logic or layout changes were made.",
    ],
  },
  {
    id: "start-learning-card-now-keeps-a-stable-frame-across-steps",
    timestamp: "2026-05-02T10:04:26+02:00",
    title: "Start-learning onboarding card now keeps fixed width and height",
    summary:
      "The onboarding container no longer grows or shrinks between steps. It now uses a stable frame size with internal scroll when a step has more content.",
    userHighlights: [
      "Card size now stays consistent while moving between onboarding steps.",
      "Longer steps now scroll inside the card instead of changing the page layout.",
      "Desktop centering and mobile usability remain intact.",
    ],
    technicalNotes: [
      "Updated `src/routes/start-learning.tsx` to use a fixed-height flex container for the onboarding card.",
      "Moved step content area into an internal `overflow-y-auto` region with `min-h-0` to support stable frame scrolling.",
      "No onboarding copy, step order, or option logic changed.",
    ],
  },
  {
    id: "onboarding-language-level-labels-now-use-clean-language-first-wording",
    timestamp: "2026-05-02T10:02:36+02:00",
    title:
      "Onboarding level labels now use language-first wording and calmer terminology",
    summary:
      "Per-language level labels in onboarding now show only the language name, and `Niveau` wording was replaced with `Sprachstand`/`stage` phrasing for a more natural tone.",
    userHighlights: [
      "Level rows now display just the selected language name instead of `Sprachniveau für …`.",
      "Step title and dropdown wording now use `Sprachstand` for a calmer, less classroom-like feel.",
      "Summary and preview wording were aligned to the same terminology.",
    ],
    technicalNotes: [
      "Updated onboarding translation values in `src/assets/translations.json` (`onboarding_level_title`, `onboarding_level_for_language`, `onboarding_level_dropdown_placeholder`, `onboarding_summary_levels`, and preview middle text).",
      "Updated e2e heading assertion in `e2e/start-learning-onboarding.spec.ts` for the new title wording.",
      "No route logic or API behavior changes were made.",
    ],
  },
  {
    id: "onboarding-level-subtitle-now-references-existing-knowledge-and-vocabulary",
    timestamp: "2026-05-02T10:01:42+02:00",
    title: "Onboarding level subtitle now uses clearer knowledge-based wording",
    summary:
      "The onboarding level helper text now explains adaptation based on existing knowledge and vocabulary instead of the broader `Niveau` phrasing.",
    userHighlights: [
      "German onboarding copy now reads more concrete and less abstract.",
      "Level adaptation is now described in terms users can map to their actual language state.",
      "No onboarding step flow or control behavior was changed.",
    ],
    technicalNotes: [
      "Updated `onboarding_level_subtitle` in `src/assets/translations.json` (German and English values).",
      "No component logic changes were required in `src/routes/start-learning.tsx`.",
      "No API or state model changes were introduced.",
    ],
  },
  {
    id: "onboarding-copy-now-covers-reading-listening-and-clearer-level-context",
    timestamp: "2026-05-02T10:00:35+02:00",
    title: "Onboarding wording now covers full language use, not only reading",
    summary:
      "The start-learning onboarding copy was revised to avoid reading-only framing, clarify level meaning per language, and improve mobile card clarity for content-type selection.",
    userHighlights: [
      "Language and level prompts now reflect understanding and active use, not only reading.",
      "Each CEFR level option (`A0–C2`) now includes a short explanation to make selection easier.",
      "Main-character step now asks directly for the main character and custom input now expects a name.",
    ],
    technicalNotes: [
      "Updated onboarding translation keys in `src/assets/translations.json` for modality-neutral wording and revised German/English labels.",
      "Updated level dropdown rendering in `src/routes/start-learning.tsx` to show `A0–C2` plus descriptive text.",
      "Adjusted text-type card layout in `src/routes/start-learning.tsx` so icon and label alignment remains stable on mobile; updated e2e heading assertions in `e2e/start-learning-onboarding.spec.ts`.",
    ],
  },
  {
    id: "start-learning-onboarding-rebuilt-with-dropdown-driven-multi-language-setup",
    timestamp: "2026-05-02T09:55:44+02:00",
    title:
      "Start-learning onboarding now uses a clean multi-language setup flow",
    summary:
      "The onboarding at `/start-learning` was fully redesigned into a clean mobile-first flow with language multi-select dropdown, per-language level dropdowns, custom character input, and compact text-type selection cards.",
    userHighlights: [
      "Language selection now supports selecting multiple learning languages from one dropdown.",
      "Reading level is now configured per selected language using focused dropdown inputs.",
      "Story setup now supports either preset characters or a custom character you type yourself.",
    ],
    technicalNotes: [
      "Rebuilt `src/routes/start-learning.tsx` into a new step flow (`language`, `level`, `mainCharacter`, `textType`, `preview`) with modern border-first app styling and responsive spacing.",
      "Removed previous weather-themed onboarding visuals and switched to a cleaner Notion-like presentation with compact controls and no oversized option tiles.",
      "Added/updated translation keys in `src/assets/translations.json` and updated E2E coverage in `e2e/start-learning-onboarding.spec.ts` for the dropdown-driven flow.",
    ],
  },
  {
    id: "start-learning-onboarding-now-uses-live-daily-weather-theme-and-cloud-animation",
    timestamp: "2026-05-02T09:26:30+02:00",
    title:
      "Start-learning onboarding now follows the live daily weather mood with animated clouds",
    summary:
      "The `/start-learning` onboarding now uses the same daily weather theme logic as the main reading area, including animated cloud motion and daylight overlays.",
    userHighlights: [
      "Onboarding background now adapts to current weather mood (clear, cloudy, rain, snow, night, neutral).",
      "Cloud animations now match the live daily-weather card movement style.",
      "Daylight phase tinting (dawn/day/dusk/night) now affects the onboarding atmosphere.",
    ],
    technicalNotes: [
      "Integrated `useDailyWeatherTheme` into `src/routes/start-learning.tsx` and mapped returned theme/daylight values to page-level background classes.",
      "Reused existing daily-weather animation classes (`daily-cloud-*`) from shared CSS instead of adding a separate animation system.",
      "Added weather and daylight overlay layers in onboarding while keeping the existing step content and selection flow unchanged.",
    ],
  },
  {
    id: "ux-copy-pass-now-aligns-product-updates-and-prompting-tone",
    timestamp: "2026-05-02T09:25:29+02:00",
    title:
      "UX copy now uses a more cohesive exploration-first tone across core labels",
    summary:
      "Key translation strings were refined to match Uxhulu's calm, structured exploration voice, with clearer prompts and more consistent wording in Product Updates and technical archive surfaces.",
    userHighlights: [
      "Topic-start prompts now read more naturally and invite exploration instead of form-like input.",
      "Product Updates labels and descriptions now use cleaner archive wording with clearer hierarchy.",
      "Technical change-log copy is now more consistent in tone and capitalization across English and German.",
    ],
    technicalNotes: [
      "Updated user-facing text keys in `src/assets/translations.json` for topic prompts, Product Updates metadata, and Technical Change Log descriptions.",
      "Kept existing translation keys unchanged to avoid behavioral impact and route/component refactors.",
      "No API, data model, or interaction-state logic changed; this release is copy-only.",
    ],
  },
  {
    id: "start-learning-onboarding-redesign-now-matches-details-and-weather-style",
    timestamp: "2026-05-02T09:21:08+02:00",
    title:
      "Start-learning onboarding now uses a compact details-style layout with weather-card accents",
    summary:
      "The onboarding flow at `/start-learning` keeps the same step content but now uses a cleaner generated-text-details visual structure mixed with daily-weather card atmosphere, including compact selection chips and app-consistent continue controls.",
    userHighlights: [
      "Large option blocks were replaced with smaller chip-style selectors that fit better on mobile screens.",
      "Step layout now follows a clearer card hierarchy with calmer typography and tighter spacing.",
      "Continue actions now use the same compact button style pattern already used in app flows.",
    ],
    technicalNotes: [
      "Refactored `src/routes/start-learning.tsx` styling only: preserved onboarding step logic and data mapping, replaced oversized tile controls with reusable compact chip controls.",
      "Applied a generated-text-details-inspired container (serif headline hierarchy, border-led sections) plus weather-style cloud/sky overlays without introducing shadow-heavy UI.",
      "Kept text inputs at `text-base` for mobile focus behavior and verified route transitions via `e2e/start-learning-onboarding.spec.ts`.",
    ],
  },
  {
    id: "website-onboarding-flow-now-matches-new-visual-step-sequence",
    timestamp: "2026-05-02T09:09:00+02:00",
    title:
      "Website onboarding now uses a full visual step flow with real generation options",
    summary:
      "The `/start-learning` experience now follows a full-screen onboarding sequence styled like the new drafts, while using real app option sets for language, reading level, text style, and generated text length mapping.",
    userHighlights: [
      "Onboarding now moves through clear full-screen steps: language, level, story perspective, text type, preview, and account save.",
      "Language options now come directly from the live supported language list, not a hardcoded subset.",
      "Text style choices include real product options such as `Dialogue`, and level selection now maps to realistic text-length defaults.",
    ],
    technicalNotes: [
      "Replaced `src/routes/start-learning.tsx` with a state-driven multi-step onboarding UI that mirrors the provided blue glass visual direction and mobile-first layout.",
      "Reused existing constants (`TEXT_LANGUAGES`) and existing translation keys for text types (`text_type_*`) so onboarding options stay aligned with generation behavior.",
      "Added new onboarding-specific i18n keys in `src/assets/translations.json` and added an end-to-end regression spec for route and step transitions.",
    ],
  },
  {
    id: "vocabulary-progress-counter-now-shows-stored-total-for-selected-languages",
    timestamp: "2026-05-01T22:07:18+02:00",
    title:
      "Vocabulary Progress counter now shows stored total for selected languages",
    summary:
      "The top list counter now reflects the full stored vocabulary total for the selected languages, instead of only currently loaded client rows.",
    userHighlights: [
      "`Angezeigt` now stays aligned with account-side totals for the active language selection.",
      "The number no longer depends on how many pages were loaded in the current session.",
      "List rendering and filtering behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated the list summary count source in `src/routes/app/vocabulary_progress.tsx` to use `totalVocabularyCount` from paginated API metadata.",
      "Kept empty-state behavior based on the actual visible filtered rows.",
      "No backend API changes were required.",
    ],
  },
  {
    id: "vocabulary-progress-language-selection-now-resets-focus-to-all-selected",
    timestamp: "2026-05-01T22:04:55+02:00",
    title:
      "Vocabulary Progress now resets view focus after language selection changes",
    summary:
      "When selected languages are changed, the list view now automatically resets to `all selected languages` so the shown count and rows update as expected.",
    userHighlights: [
      "Adding another language now immediately updates the visible list scope instead of staying silently pinned to the previous single-language focus.",
      "`Angezeigt` now changes consistently when language selection changes.",
      "Users can still manually focus a single language afterwards if needed.",
    ],
    technicalNotes: [
      "Added an effect in `src/routes/app/vocabulary_progress.tsx` that sets `activeWorkspaceLanguage` to `ALL_SELECTED` whenever `effectiveLanguageFilters` changes.",
      "Kept existing filtering, sorting, and pagination logic unchanged.",
      "Preserved explicit manual focus selection behavior after auto-reset.",
    ],
  },
  {
    id: "vocabulary-progress-showing-counter-now-matches-rendered-list",
    timestamp: "2026-05-01T22:02:52+02:00",
    title: "Vocabulary Progress `Angezeigt` counter now matches rendered rows",
    summary:
      "The shown-items counter now uses the same derived list that is rendered in the table/cards, preventing mismatches in the header summary.",
    userHighlights: [
      "`Angezeigt` now reflects exactly what is currently visible in the vocabulary list.",
      "Empty-state behavior now follows the same visible-list source as the rendered rows.",
      "List sorting and filtering behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added a `visibleVocabularyCount` derived from `sortedVocabulary.length` in `src/routes/app/vocabulary_progress.tsx`.",
      "Updated the summary counter and empty-state condition to use `visibleVocabularyCount`.",
      "Kept pagination, language scope, and filter logic unchanged.",
    ],
  },
  {
    id: "vocabulary-progress-language-selector-no-longer-shows-misleading-counts",
    timestamp: "2026-05-01T22:00:57+02:00",
    title: "Vocabulary Progress language selector now avoids misleading counts",
    summary:
      "Language selection controls no longer show per-language numbers that could be inconsistent with the current filtered dataset.",
    userHighlights: [
      "Language selection now focuses on clear scope choice instead of potentially inaccurate per-language count labels.",
      "Language focus dropdown now shows clean language names only.",
      "Reliable totals remain visible in the dedicated list summary area.",
    ],
    technicalNotes: [
      "Removed per-language count rendering from workspace language checklist and focus dropdown in `src/routes/app/vocabulary_progress.tsx`.",
      "Adjusted active-language summary copy for the `all languages` case to avoid count coupling to partial datasets.",
      "Kept existing filtering and sorting behavior unchanged.",
    ],
  },
  {
    id: "vocabulary-progress-redesign-for-clear-configuration-and-better-responsiveness",
    timestamp: "2026-05-01T21:59:43+02:00",
    title: "Vocabulary Progress now has a clearer, more configurable layout",
    summary:
      "The Vocabulary Progress page was redesigned to keep existing behavior while improving visual structure, language configuration clarity, and mobile/desktop usability.",
    userHighlights: [
      "Page sections are now ordered with clearer hierarchy from overview to configuration and then vocabulary list.",
      "Language setup is easier to manage with a dedicated selection panel and a separate view-focus control.",
      "List context counters are grouped in a focused summary block for faster interpretation.",
    ],
    technicalNotes: [
      "Refactored `src/routes/app/vocabulary_progress.tsx` layout and responsive structure without changing vocabulary filtering, sorting, or action logic.",
      "Split language configuration from active view focus controls to avoid duplicate state-changing chips.",
      "Added E2E coverage in `e2e/vocabulary-progress-statistics.spec.ts` for desktop/mobile layout usability checks.",
    ],
  },
  {
    id: "reeprise-hook-order-fixed-for-loading-to-loaded-transition",
    timestamp: "2026-05-01T21:34:11+02:00",
    title: "Reeprise loading transition no longer triggers hook-order errors",
    summary:
      "A hook-order issue in Reeprise was fixed so the page no longer crashes with `Rendered more hooks than during the previous render` when data transitions from loading to loaded.",
    userHighlights: [
      "Reeprise now loads reliably without hook-order runtime errors.",
      "The loading-to-content transition is stable again.",
      "No user-facing flow or layout behavior was intentionally changed.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` to compute feed data from a safe fallback (`data?.pages ?? []`) before loading checks.",
      "Moved the loading early-return below hook declarations so hook execution order remains stable across renders.",
      "Kept existing feed/filter behavior unchanged.",
    ],
  },
  {
    id: "small-screen-layout-now-uses-page-scroll-instead-of-nested-app-scroller",
    timestamp: "2026-05-01T21:32:55+02:00",
    title: "Small-screen app layout now uses normal page scrolling",
    summary:
      "On small-screen layouts (including narrow desktop windows), the app now uses regular document scrolling instead of a nested internal scroll container to prevent trackpad scroll lockups.",
    userHighlights: [
      "Scrolling in narrow-window/mobile-style layouts now follows normal browser page scroll behavior.",
      "Trackpad scroll no longer depends on hitting a specific inner container.",
      "Desktop layout keeps its existing independent in-app scroll regions.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/route.tsx` so `app-content-scroll` uses `overflow-y-visible`/`min-h-full` below `md` breakpoints.",
      "Kept existing desktop behavior via `md:overflow-y-auto` and `md:overscroll-contain`.",
      "No route-level navigation or data-loading behavior was changed.",
    ],
  },
  {
    id: "copy-quality-guardrails-added-for-app-context-wording",
    timestamp: "2026-05-01T21:11:12+02:00",
    title: "Copy-quality guardrails now enforce app-context wording checks",
    summary:
      "The repo now includes explicit process and test guardrails so user-facing wording is validated against app context and recurring German terminology issues are caught earlier.",
    userHighlights: [
      "PRs now include a checklist item to verify that copy matches real feature behavior and screen intent.",
      "A shared terminology guideline now documents preferred German UI wording and disallowed terms.",
      "A regression test now blocks `Sprachraum` from reappearing in critical vocabulary empty-state messages.",
    ],
    technicalNotes: [
      "Added `.github/pull_request_template.md` with copy-quality and review checklist items.",
      "Added `docs/ui-terminology-guidelines.md` with German terminology guidance and required app-context checks.",
      "Added `src/assets/translations.copy.test.ts` to enforce critical German empty-state wording constraints.",
    ],
  },
  {
    id: "reeprise-feed-empty-state-german-copy-now-uses-sprachauswahl",
    timestamp: "2026-05-01T21:05:56+02:00",
    title: "Reeprise feed empty-state German copy now uses natural wording",
    summary:
      "The German empty-state copy in Reeprise feed now uses `Sprachauswahl` instead of `Sprachraum` and a more natural title phrasing.",
    userHighlights: [
      "German empty-state title now reads `Hier beginnt dein Feed`.",
      "German body text now references `aktuelle Sprachauswahl` instead of `Sprachraum`.",
      "The guidance remains the same: create a text and it will appear in the feed.",
    ],
    technicalNotes: [
      "Updated German values for `reeprise_feed_empty_title` and `reeprise_feed_empty_body` in `src/assets/translations.json`.",
      "No behavioral or layout logic was changed.",
      "English copy remains unchanged.",
    ],
  },
  {
    id: "reeprise-feed-empty-language-space-now-shows-illustrated-empty-state",
    timestamp: "2026-05-01T21:04:10+02:00",
    title:
      "Reeprise feed now shows an illustrated empty state for empty language spaces",
    summary:
      "When a language space has no visible feed stories, Reeprise now shows a friendly empty state instead of a confusing `Load more` button.",
    userHighlights: [
      "Empty language-space feeds now show a clear and friendly placeholder card.",
      "The feed no longer offers `Load more` when there are no visible stories yet.",
      "Guidance now points to creating a new story so the next step is obvious.",
    ],
    technicalNotes: [
      "Updated feed rendering in `src/routes/app/reeprise/index.tsx` to gate pagination CTA by visible feed items.",
      "Added a new illustrated `FeedEmptyState` component and related i18n keys in `src/assets/translations.json`.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for empty language-space feed behavior.",
    ],
  },
  {
    id: "german-empty-state-wording-now-uses-sprachauswahl",
    timestamp: "2026-05-01T21:02:51+02:00",
    title: "German empty-state wording now uses more natural terminology",
    summary:
      "German vocabulary empty-state messaging now uses `Sprachauswahl` instead of `Sprachraum` to better match common UI wording.",
    userHighlights: [
      "German empty state now reads more naturally in both vocabulary review surfaces.",
      "The message still keeps the same action guidance: create new content in this language.",
      "Copy stays consistent across spaced preview and vocabulary preview.",
    ],
    technicalNotes: [
      "Updated German text for `spaced_preview_language_empty` in `src/assets/translations.json`.",
      "Updated German text for `vocabulary_preview_empty_state` in `src/assets/translations.json`.",
      "Kept existing English wording unchanged.",
    ],
  },
  {
    id: "language-space-empty-state-copy-now-matches-separated-language-spaces",
    timestamp: "2026-05-01T21:00:44+02:00",
    title: "Language-space empty-state copy now matches separated spaces",
    summary:
      "Empty-state messaging in vocabulary review now reflects separated language spaces and no longer tells you to switch languages for more items.",
    userHighlights: [
      "Empty-state text now clearly explains that this language space currently has no vocabulary.",
      "Guidance now points to creating new content in the same language instead of switching languages.",
      "Copy is now consistent between spaced preview and vocabulary preview empty states.",
    ],
    technicalNotes: [
      "Updated `spaced_preview_language_empty` German/English text in `src/assets/translations.json`.",
      "Updated `vocabulary_preview_empty_state` German/English text in `src/assets/translations.json`.",
      "Aligned both keys to the same separated-language-space wording for consistent UX behavior.",
    ],
  },
  {
    id: "esperanto-added-as-a-supported-learning-language",
    timestamp: "2026-05-01T17:50:04+02:00",
    title: "Esperanto is now available as a learning language",
    summary:
      "Esperanto can now be selected across text generation and language-space flows so it behaves like the other supported learning languages.",
    userHighlights: [
      "Esperanto now appears in language pickers used for learning-language selection.",
      "Language-space controls can now scope reading and vocabulary flows to Esperanto.",
      "Quick-translate language labels now include Esperanto where language names are shown.",
    ],
    technicalNotes: [
      "Extended backend language enums and language-code normalization in `barkguide/reeprise/models.py` and `barkguide/reeprise/views.py` with `ESPERANTO` support.",
      "Added Esperanto TTS/pronunciation language mappings in `barkguide/reeprise/tasks.py`.",
      "Added `TEXT_LANGUAGES` Esperanto metadata and translation key `language_esperanto` in `src/constants.ts` and `src/assets/translations.json`.",
    ],
  },
  {
    id: "quick-translate-ui-now-matches-shared-card-controls",
    timestamp: "2026-05-01T17:43:03+02:00",
    title: "Übersetzen unterwegs now matches shared app control styling",
    summary:
      "Quick translate card controls have been aligned to the same sizing, color, and spacing patterns used in the rest of the app utility cards.",
    userHighlights: [
      "Buttons and chips now use consistent sizing and typography with neighboring cards.",
      "Input area now feels sharper and less prototype-like while still supporting longer text.",
      "Ambiguous source state now uses the same neutral surface and border language as other controls.",
    ],
    technicalNotes: [
      "Adjusted `src/components/QuickTranslateCard.tsx` control classes to align with shared border-first utility-card controls.",
      "Reduced custom visual treatments and removed amber-heavy styling from ambiguity controls.",
      "Kept auto-grow behavior while tightening input line-height and spacing to match existing card rhythm.",
    ],
  },
  {
    id: "quick-translate-composer-ui-now-uses-a-polished-auto-grow-layout",
    timestamp: "2026-05-01T17:39:38+02:00",
    title: "Übersetzen unterwegs now uses a polished composer layout",
    summary:
      "The quick-translate card now uses an auto-growing input composer and a cleaner action hierarchy, replacing the prototype-like fixed input/button layout.",
    userHighlights: [
      "The input now grows with longer text instead of clipping in a fixed-height field.",
      "Primary actions are vertically centered and consistently aligned across states.",
      "Source-confirmation guidance and typo suggestion states now sit in a clearer secondary panel.",
    ],
    technicalNotes: [
      "Refactored `src/components/QuickTranslateCard.tsx` layout structure to a composer + action-bar pattern with consistent spacing and typography.",
      "Implemented textarea auto-resize with a bounded min/max height and overflow handoff for long content.",
      "Standardized button heights (`h-10`) and inline-flex centering for translate/save actions to remove baseline drift.",
    ],
  },
  {
    id: "quick-translate-now-suggests-typo-corrections-on-unclear-input",
    timestamp: "2026-05-01T17:38:32+02:00",
    title: "Übersetzen unterwegs now suggests likely typo corrections",
    summary:
      "When quick translate cannot confidently detect the source language, it can now suggest a likely corrected input such as `chien` for `chin` instead of silently failing direction routing.",
    userHighlights: [
      "Unclear input now offers a `Did you mean …?` correction chip directly in the quick translate card.",
      "You can apply the suggestion in one tap and retry translation immediately.",
      "If no reliable correction exists, the card now clearly asks for manual source-language selection.",
    ],
    technicalNotes: [
      "Extended `/api/reeprise/translate/` ambiguity response in `barkguide/reeprise/views.py` with typo-suggestion fields (`did_you_mean`, `did_you_mean_language`).",
      "Updated `src/components/QuickTranslateCard.tsx` to render a one-tap suggestion action and a clearer no-match fallback hint.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` (`test_quick_translate_returns_spelling_suggestion_when_detection_is_uncertain`).",
    ],
  },
  {
    id: "vocabulary-progress-language-controls-no-longer-duplicate-between-cards",
    timestamp: "2026-05-01T17:34:13+02:00",
    title: "Vocabulary Progress now uses one language control area",
    summary:
      "Language scope controls are now shown only in the workspace section, removing duplicated language selectors that previously competed with each other.",
    userHighlights: [
      "Language selection and language focus now live in one clear place at the top of the page.",
      "The lower filter card now focuses only on stage, search, and sorting.",
      "This removes repeated language UI that could suggest conflicting filter states.",
    ],
    technicalNotes: [
      "Removed the duplicated language dropdown block from the lower filter card in `src/routes/app/vocabulary_progress.tsx`.",
      "Deleted the now-unused `LanguageFilterDropdown` component from the same file.",
      "Kept workspace chip logic as the single source of truth for language scoping and focus.",
    ],
  },
  {
    id: "vocabulary-progress-workspace-counts-now-use-consistent-scope",
    timestamp: "2026-05-01T17:30:12+02:00",
    title: "Vocabulary Progress workspace counts now use one consistent scope",
    summary:
      "Workspace chip counts now use the same visible-data basis, and the account-wide total is shown separately to avoid contradictory numbers in the same control group.",
    userHighlights: [
      "The `All languages` chip now matches the same loaded/visible scope as the per-language chips.",
      "A separate line now shows the account-wide total (`Total in account`) for full-context reference.",
      "Workspace number reading is now consistent and easier to trust at a glance.",
    ],
    technicalNotes: [
      "Updated workspace chip count source in `src/routes/app/vocabulary_progress.tsx` to use summed visible language counts instead of mixed global totals.",
      "Added separate global-total hint text below workspace chips with existing paginated total count.",
      "Added new translation key `vocabulary_progress_workspace_total_hint` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "quick-translate-direction-ui-now-reflects-real-detected-routing",
    timestamp: "2026-05-01T17:28:29+02:00",
    title: "Übersetzen unterwegs now shows the real translation direction",
    summary:
      "The quick-translate UI no longer shows a fixed target that can be wrong before detection. Direction labels now reflect the actual resolved source and target after routing.",
    userHighlights: [
      "The card no longer implies a fixed target language before source detection is resolved.",
      "After translation, direction is shown as `Source -> Target`, matching the real translation route.",
      "When source confirmation is required, the primary action now clearly asks for source confirmation before translating.",
    ],
    technicalNotes: [
      "Updated `src/components/QuickTranslateCard.tsx` to render direction metadata from backend `resolved_source_lang` and `resolved_target_lang` instead of prefilled target assumptions.",
      "Adjusted primary CTA states to use neutral and source-confirmation copy before routing is finalized.",
      "Added new translation keys in `src/assets/translations.json` for neutral and source-confirmation translate actions.",
    ],
  },
  {
    id: "vocabulary-progress-now-has-a-parallel-language-workspace-layout",
    timestamp: "2026-05-01T17:27:11+02:00",
    title: "Vocabulary Progress now has a parallel language workspace",
    summary:
      "Vocabulary Progress has been restructured so you can configure active languages quickly and switch focus instantly between language-specific views.",
    userHighlights: [
      "A new language workspace section makes active language selection visible and fast.",
      "You can now switch between `All selected languages` and one-language focus views in one tap.",
      "Per-language counts are shown directly on workspace chips to make scope decisions clearer.",
    ],
    technicalNotes: [
      "Added a new workspace UI section in `src/routes/app/vocabulary_progress.tsx` with active-language chips and view-mode chips.",
      "Introduced page-local focus state (`ALL_SELECTED` or concrete language id) and routed list/stage counts through that focus logic.",
      "Added new translation keys in `src/assets/translations.json` for workspace labels and guidance copy.",
    ],
  },
  {
    id: "vocabulary-progress-now-supports-multi-language-scope-without-space-lock",
    timestamp: "2026-05-01T17:11:45+02:00",
    title: "Vocabulary Progress now supports parallel language scopes",
    summary:
      "Vocabulary Progress no longer inherits a locked single language space. You can now select multiple languages (for example French and Spanish) and the page uses that exact scope consistently.",
    userHighlights: [
      "Language scope on Vocabulary Progress is now a true multi-select.",
      "You can learn in parallel tracks like `French + Spanish` in one view.",
      "The page no longer auto-overrides your selection with a global language-space lock.",
    ],
    technicalNotes: [
      "Removed `useLanguageSpace` coupling from `src/routes/app/vocabulary_progress.tsx` so language filtering is page-local and explicit.",
      "Persisted selected language filters with local storage key `vocabulary-progress-language-filters-v1`.",
      "Added `resolveVocabularyProgressLanguageFilters` utility and regression tests to enforce empty=all, multi-select retention, and invalid-value cleanup.",
    ],
  },
  {
    id: "quick-translate-now-targets-current-language-space-only",
    timestamp: "2026-05-01T17:06:30+02:00",
    title: "Übersetzen unterwegs now follows your current language space",
    summary:
      "Übersetzen unterwegs now provides one clear translation path: it translates into the language of your currently active language space instead of showing multiple target options.",
    userHighlights: [
      "The target language is now fixed to your active language space for a cleaner, faster translation flow.",
      "When language space is set to `ALL`, the feature safely falls back to your mother tongue as target language.",
      "The card now prioritizes one primary action state (`Translate`) with clearer target/source context chips.",
    ],
    technicalNotes: [
      "Reworked `src/components/QuickTranslateCard.tsx` to remove multi-target buttons and route all translation/save requests through a single resolved target language.",
      "Added `src/utils/quickTranslateTargetLanguage.ts` to centralize language-space target resolution and keep behavior consistent.",
      "Added regression coverage in `src/utils/quickTranslateTargetLanguage.test.ts` for both fixed-space and `ALL` fallback behavior.",
    ],
  },
  {
    id: "generated-texts-loading-no-longer-breaks-on-pagination-count",
    timestamp: "2026-05-01T17:05:52+02:00",
    title: "Loading no longer gets stuck from generated-text list pagination",
    summary:
      "A pagination regression that could return a server error while loading generated texts has been fixed, so app loading no longer hangs on that endpoint.",
    userHighlights: [
      "Main app loading now proceeds normally when the generated-text list is requested.",
      "The generated-text API response now consistently includes a stable `count` value.",
      "This removes the backend crash that could leave the app stuck on loading.",
    ],
    technicalNotes: [
      "Updated `MyCursorPagination` in `barkguide/reeprise/utils/pagination.py` to capture total count during `paginate_queryset()` instead of reading `self.page.paginator` from list-backed cursor pages.",
      "Added safe fallback logic in `get_paginated_response()` so count remains available even when queryset count is unavailable.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` (`test_generated_texts_list_includes_count_without_crashing`).",
    ],
  },
  {
    id: "vocabulary-progress-now-defaults-to-all-languages-for-list-visibility",
    timestamp: "2026-05-01T17:02:30+02:00",
    title: "Vocabulary Progress now defaults to all languages",
    summary:
      "Vocabulary Progress now starts with all languages visible by default, preventing empty lists caused by an implicit English-only language-space default.",
    userHighlights: [
      "Opening Vocabulary Progress now shows your full vocabulary list by default.",
      "You can still narrow to specific languages using the language filter.",
      "The empty-state issue from an implicit language lock is removed.",
    ],
    technicalNotes: [
      "Changed Vocabulary Progress language-space fallback from `ENGLISH` to `ALL` in `src/routes/app/vocabulary_progress.tsx`.",
      "Kept existing language-space behavior intact when a user explicitly sets a fixed language space.",
      "No backend API contract changes were required; this is a frontend default-filter fix.",
    ],
  },
  {
    id: "vocabulary-progress-loads-faster-with-stable-total-count",
    timestamp: "2026-05-01T16:56:53+02:00",
    title: "Vocabulary Progress now loads faster with a stable total count",
    summary:
      "Vocabulary Progress no longer auto-loads the entire vocabulary list on page open, and the `Angezeigt` value now shows a stable total immediately.",
    userHighlights: [
      "Opening `Vocabulary Progress` now feels faster on larger vocabulary sets.",
      "Changing `Sortierung` remains responsive because the page no longer keeps appending all pages in the background.",
      "`Angezeigt` now reflects the full total from the start instead of increasing while pages stream in.",
    ],
    technicalNotes: [
      "Extended cursor pagination response in `barkguide/reeprise/utils/pagination.py` to include a `count` field alongside `next`, `previous`, and `results`.",
      "Updated vocabulary list query typing in `src/hooks/usePaginatedVocabularyList.ts` to accept paginated `count` metadata.",
      "Removed eager `fetchNextPage()` loop in `src/routes/app/vocabulary_progress.tsx`, switched to explicit `Load more`, and bound `Angezeigt` to backend `count` for stable rendering.",
    ],
  },
  {
    id: "vocabulary-progress-statistics-card-labels-now-render-consistently",
    timestamp: "2026-05-01T07:40:20+02:00",
    title: "Vocabulary Progress statistics labels now render consistently",
    summary:
      "Statistics card labels on Vocabulary Progress no longer disappear intermittently while app language settings are still loading.",
    userHighlights: [
      "Vocabulary statistics cards now always show their label text.",
      "No more blank label cards during initial page load or refresh.",
      "Number values and existing translations remain unchanged.",
    ],
    technicalNotes: [
      "Updated statistics card list rendering in `src/routes/app/vocabulary_progress.tsx` to use stable internal React keys instead of translated label text.",
      "This prevents duplicate empty-string keys when `translate()` temporarily returns empty while user settings are loading.",
      "Added Playwright regression coverage in `e2e/vocabulary-progress-statistics.spec.ts` to assert non-empty labels in the vocabulary statistics section.",
    ],
  },
  {
    id: "vocabulary-word-column-now-consistently-shows-seen-surface-form",
    timestamp: "2026-05-01T07:35:33+02:00",
    title: "Vocabulary word column now consistently shows the seen form",
    summary:
      "Vocabulary entries now use one consistent display rule: the `WORT` column shows the seen/clicked surface form, while base form details remain available separately.",
    userHighlights: [
      "Entries like `te pose-t-il tant` and `de l'aide` now follow the same primary-word display logic.",
      "The main word label no longer switches between dictionary form and surface form depending on grammar metadata.",
      "Base form information is still available through `Grundform` when present.",
    ],
    technicalNotes: [
      "Simplified `getVocabularyPrimaryWord` in `src/utils/vocabularyTranslationDetails.ts` to always resolve from `source_surface_form` (fallback: stored `word`).",
      "Removed noun-specific canonical/plural promotion logic that previously replaced surface forms in some cards.",
      "Updated regression expectations in `src/utils/vocabularyTranslationDetails.test.ts` for noun and apostrophe-form scenarios.",
    ],
  },
  {
    id: "vocabulary-progress-search-now-finds-phrases-in-sentence-context",
    timestamp: "2026-05-01T07:31:06+02:00",
    title: "Vocabulary Progress search now finds phrase entries more reliably",
    summary:
      "Vocabulary Progress search now checks sentence context and normalizes punctuation variants, so phrase entries are easier to find even when apostrophes or dashes differ.",
    userHighlights: [
      "Phrase searches like `te pose-t-il tant` are now matched more reliably.",
      "Search now includes sentence text in addition to word and translation.",
      "Search placeholder text now reflects the broader search scope.",
    ],
    technicalNotes: [
      "Added normalized search matching in `src/routes/app/vocabulary_progress.tsx` that aligns apostrophe and dash variants before comparison.",
      "Extended client-side search matching to include `vocabulary.sentence` and `translation_details.source_surface_form`.",
      "Updated `vocabulary_progress_filters_search_placeholder` copy in `src/assets/translations.json` (German and English).",
    ],
  },
  {
    id: "context-save-now-creates-one-phrase-vocabulary-entry",
    timestamp: "2026-05-01T07:24:53+02:00",
    title: "Context save now creates one phrase vocabulary entry",
    summary:
      "Saving a context selection now creates one combined vocabulary entry for the full selected phrase instead of separate entries per clicked token.",
    userHighlights: [
      "A selection like `te pose-t-il tant` is now stored as one vocabulary item.",
      "Searching the phrase in Vocabulary Progress can now find that entry directly.",
      "Context translation and selection flow remains the same; only save behavior changed.",
    ],
    technicalNotes: [
      "Reworked context-save logic in `src/components/GeneratedTextDetailsTextPane.tsx` to use a single `agent_add` call with combined phrase + sentence context.",
      "Kept title-word save on the same backend path by introducing a shared phrase-save helper in the same component.",
      "Updated Playwright coverage in `e2e/highlighted-word-preview.spec.ts` to assert one `agent_add` request with the expected combined phrase payload.",
    ],
  },
  {
    id: "vocabulary-progress-list-now-has-user-friendly-sort-options",
    timestamp: "2026-05-01T07:18:50+02:00",
    title: "Vocabulary list now includes user-friendly sorting options",
    summary:
      "Vocabulary Progress now includes a dedicated sort control so you can quickly switch between learning priority, alphabetic order, added date, and practice-based ordering.",
    userHighlights: [
      "Added a new `Sort` dropdown directly in Vocabulary Progress filters.",
      "You can now sort by `Learn next`, `Word A to Z`, `Word Z to A`, `Recently added`, `Added first`, and `Most practiced`.",
      "The default still prioritizes vocabulary that is due for review first.",
    ],
    technicalNotes: [
      "Added `sortMode` UI state and a select control in `src/routes/app/vocabulary_progress.tsx`.",
      "Extracted sorting logic to `src/utils/sortVocabularyProgress.ts` for consistent behavior and easier testing.",
      "Added regression tests in `src/utils/sortVocabularyProgress.test.ts` and translation keys in `src/assets/translations.json` for both German and English labels.",
    ],
  },
  {
    id: "vocabulary-progress-statistics-now-use-language-aware-thousand-separators",
    timestamp: "2026-05-01T07:14:45+02:00",
    title:
      "Vocabulary Progress statistics now follow your language number format",
    summary:
      "Statistic values on Vocabulary Progress now use locale-aware thousand separators based on app language, so German and English users see expected formatting.",
    userHighlights: [
      "German app language now shows separators like `29.300`.",
      "English app language continues to show separators like `29,300`.",
      "Formatting updates automatically with your app language setting.",
    ],
    technicalNotes: [
      "Updated statistics card number formatting in `src/routes/app/vocabulary_progress.tsx` to use `useTranslation().language` and locale mapping (`GERMAN` -> `de-DE`, `ENGLISH` -> `en-US`).",
      "Replaced default `Intl.NumberFormat()` calls with a language-aware formatter helper for all statistics values rendered on Vocabulary Progress.",
      "Kept existing translation keys and stats data sources unchanged to isolate this to display formatting behavior.",
    ],
  },
  {
    id: "text-click-now-keeps-highlight-on-the-clicked-word-only",
    timestamp: "2026-05-01T07:13:00+02:00",
    title: "Text click now keeps highlight on the clicked word only",
    summary:
      "Clicking a word in generated text no longer triggers additional word highlights from broad active-word matching. Highlighting now stays on saved token positions, including the clicked word you just added.",
    userHighlights: [
      "Clicking a word to translate no longer lights up unrelated words in the same text.",
      "The clicked word still gets saved and highlighted at its exact position.",
      "Saved-word preview behavior on highlighted tokens remains unchanged.",
    ],
    technicalNotes: [
      "Removed selected-word overlay highlight matching from `src/components/GeneratedTextDetailsTextPane.tsx` so body highlights no longer come from full-text word-string matching.",
      "Kept token-position highlighting based on persisted/local vocabulary usage starts in the same component.",
      "Validated frontend utilities with `vitest` runs for `resolveGeneratedTextHighlightWords.test.ts` and `resolveActiveTextHighlightWords.test.ts`.",
    ],
  },
  {
    id: "similar-words-now-keeps-lemma-matches-across-word-forms",
    timestamp: "2026-05-01T07:12:08+02:00",
    title: "Similar words now keeps lemma matches across nearby word forms",
    summary:
      "The similar-words section now keeps close variants when they share the same lemma, even when grammar tagging differs between entries.",
    userHighlights: [
      "Word-form variants like `détendu` and `détendre` can now stay visible in `Ähnliche Wörter` when they are true lemma-related neighbors.",
      "Confusing strict filtering between adjective/verb variants is reduced for close spelling relatives.",
      "Noisy unrelated lookalikes are still filtered by the existing spelling and structure thresholds.",
    ],
    technicalNotes: [
      "Updated POS conflict gating in `src/utils/vocabularySimilarWords.ts` so near-spelling candidates are no longer rejected when either lemma or meaning signal aligns.",
      "Kept existing distance, score, and boundary-structure checks unchanged to preserve noise suppression.",
      "Added regression test in `src/utils/vocabularySimilarWords.test.ts` covering `détendu` vs `détendre` with shared lemma and differing part-of-speech metadata.",
    ],
  },
  {
    id: "feature-mockups-page-now-combines-generate-text-and-read-aloud-mockups",
    timestamp: "2026-05-01T07:09:15+02:00",
    title: "Feature Mockups now combines Generate Text and Read Aloud mockups",
    summary:
      "The two separate mockup pages are now grouped into one `Feature Mockups` page in the side navigation, so concept review happens in one place.",
    userHighlights: [
      "Side navigation now shows a single `Feature Mockups` entry instead of separate mockup links.",
      "The unified page includes both the Generate Text reading-first mockup and the Read Aloud mockup in one screen.",
      "Existing mockup interactions remain clickable and unchanged inside the consolidated page.",
    ],
    technicalNotes: [
      "Added a new `/app/feature_mockups` route in `src/routes/app/feature_mockups.tsx` that renders both existing mockup sections together.",
      "Updated side navigation wiring in `src/routes/app/route.tsx` to use a single `sidenav_feature_mockups` translation key and link target.",
      "Adjusted Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to validate the new unified route and flow entry point.",
    ],
  },
  {
    id: "statistics-page-merged-into-vocabulary-progress",
    timestamp: "2026-05-01T07:07:14+02:00",
    title: "Statistics are now part of Vocabulary Progress",
    summary:
      "The separate Statistics page has been removed from navigation, and all statistics are now shown directly on the Vocabulary Progress page.",
    userHighlights: [
      "Vocabulary and text statistics now live in one place: `Vocabulary Progress`.",
      "The `Statistics` side navigation entry is removed.",
      "Existing `/app/statistics` links now forward to `Vocabulary Progress` automatically.",
    ],
    technicalNotes: [
      "Moved statistics card rendering into `src/routes/app/vocabulary_progress.tsx` using existing text and vocabulary statistics hooks.",
      "Removed the `Statistics` nav item from `src/routes/app/route.tsx` to avoid duplicate destinations.",
      "Replaced standalone statistics page content with a redirect route in `src/routes/app/statistics.tsx` for backward-compatible URL handling.",
    ],
  },
  {
    id: "context-selection-translate-now-can-save-selected-words",
    timestamp: "2026-05-01T07:01:41+02:00",
    title:
      "Context selection translation can now save selected words as vocabulary",
    summary:
      "The multi-word context translation rail now includes a direct action to save all selected words into vocabulary, using the same creation flow as single-word clicks in the text.",
    userHighlights: [
      "After selecting multiple words in context mode, you can now save the whole selection as vocabulary entries in one step.",
      "Saved entries keep the same downstream enrichment behavior as normal word clicks, including full translation metadata generation.",
      "The context rail keeps translation and save actions separate, so you can translate only, save only, or do both.",
    ],
    technicalNotes: [
      "Added a new context-rail save action in `src/components/GeneratedTextDetailsTextPane.tsx` that iterates selected tokens through existing `onCreateVocabulary` handling instead of introducing a new API path.",
      "Reused the existing `add_vocabulary` backend pipeline for each selected token, preserving sentence-scoped extraction and full translation-details generation behavior.",
      "Extended Playwright regression coverage in `e2e/highlighted-word-preview.spec.ts` to verify context-mode selection creates vocabulary entries through the real user flow.",
    ],
  },
  {
    id: "highlighted-word-preview-now-closes-when-clicking-the-same-word-again",
    timestamp: "2026-05-01T07:01:00+02:00",
    title:
      "Highlighted-word preview now closes when clicking the same word again",
    summary:
      "The translation preview flyout for highlighted words now toggles closed when you click the same highlighted word again, so closing no longer depends on using the close icon.",
    userHighlights: [
      "Clicking a highlighted word still opens its translation preview immediately.",
      "Clicking that same highlighted word again now closes the preview in place.",
      "Outside-click and explicit close-button behavior remain available as before.",
    ],
    technicalNotes: [
      "Adjusted preview-open interaction handling in `src/components/GeneratedTextDetailsTextPane.tsx` to support same-word toggle-close while preserving immediate pointerdown-open behavior.",
      "Added press/click interaction guard state so first click after pointerdown does not immediately close the newly opened flyout.",
      "Extended Playwright regression in `e2e/highlighted-word-preview.spec.ts` to assert same-word click closes the preview.",
    ],
  },
  {
    id: "daily-weather-text-no-longer-hides-struggle-words-card",
    timestamp: "2026-05-01T06:59:51+02:00",
    title:
      "Daily weather text creation no longer hides the struggle-words card",
    summary:
      "Creating the morning daily weather text now keeps the struggle-words card visible instead of incorrectly treating the daily text as a struggle-practice text.",
    userHighlights: [
      "After generating the daily weather text, the struggle-words card remains available on the same morning session.",
      "Daily reinforcement words no longer trigger the hide condition for the struggle-words card.",
      "The daily card flow and open-to-details behavior stay unchanged.",
    ],
    technicalNotes: [
      "Adjusted `isStrugglePracticeText()` in `src/utils/hasTodayStrugglePracticeText.ts` to ignore `struggle_words` payload when `origin === DAILY_NUDGE`.",
      "Kept existing fallback detection for genuine struggle-practice texts (origin/topic and non-daily payload-based detection).",
      "Added regression test `does not count a DAILY_NUDGE text with reinforcement words as struggle-practice text` in `src/utils/hasTodayStrugglePracticeText.test.ts`.",
    ],
  },
  {
    id: "title-highlighted-words-now-also-highlight-matching-body-words",
    timestamp: "2026-05-01T06:58:34+02:00",
    title:
      "Title-highlighted words now also highlight matching words in the text body",
    summary:
      "Words saved from a generated text title now also highlight matching occurrences inside the story body, so title and body highlighting behave consistently.",
    userHighlights: [
      "Clicking a title word to save it now highlights that same word in the story text as well.",
      "Body highlights stay consistent even when a word was originally added from the title.",
      "Existing chapter-generated highlights continue to work while title-word highlighting is included.",
    ],
    technicalNotes: [
      "Adjusted text-details highlight word resolution in `src/routes/app/reeprise/$generatedTextId.tsx` to merge title-origin vocabulary words into active text highlight inputs.",
      "Kept previous positional-protection behavior intact by extending only word-based highlight inputs, not usage-index mapping.",
      "Added regression unit coverage in `src/utils/resolveActiveTextHighlightWords.test.ts` and introduced shared merge utility `src/utils/resolveActiveTextHighlightWords.ts`.",
    ],
  },
  {
    id: "trainer-refresh-no-longer-repeats-just-answered-word",
    timestamp: "2026-04-27T21:55:44+02:00",
    title: "Trainer reload no longer repeats a just-answered word",
    summary:
      "After marking a trainer word as known, refreshing the page now keeps the queue aligned with server SRS state instead of resurfacing that same word.",
    userHighlights: [
      "A word marked with `Knew it` no longer reappears immediately after refresh.",
      "Trainer queue now reflects current due words from server state after reload.",
      "Review actions are now more resilient during tab reload/unload timing.",
    ],
    technicalNotes: [
      "Removed early snapshot-only session restore in `src/components/VocabularyPreviewCard.tsx` so restored batches are reconciled against fresh server vocabulary IDs.",
      "Hardened review mutations in `src/hooks/useMarkVocabularyAsLearned.ts` with `keepalive: true` and trainer-query invalidation on settle.",
      "Updated trainer query freshness in `src/hooks/useVocabularyTrainerVocabularies.ts` (`staleTime: 0`, mount/focus refetch enabled) and added regressions in `barkguide/reeprise/tests.py` plus `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-answer-actions-on-mobile-no-longer-force-scroll-to-top",
    timestamp: "2026-04-27T21:46:23+02:00",
    title: "Trainer answer actions no longer force-scroll to top on mobile",
    summary:
      "When answering a trainer card with `Knew it`, `Almost`, or `Didn't know`, the page now keeps your current scroll position instead of jumping back to the trainer top.",
    userHighlights: [
      "Answering from lower down inside an expanded trainer card now feels stable and does not jump upward.",
      "Manual expand/collapse and answer actions now both keep viewport position calm on mobile.",
      "The next card still loads normally after each answer.",
    ],
    technicalNotes: [
      "Removed the explicit `scrollToTop()` call from trainer answer handling in `src/components/VocabularyPreviewCard.tsx`.",
      "Removed the same forced scroll call from trainer delete success flow to keep behavior consistent.",
      "Updated Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts` to assert answer actions preserve scroll position from lower content.",
    ],
  },
  {
    id: "similar-words-now-filters-weak-lookalikes-more-strictly",
    timestamp: "2026-04-27T21:42:05+02:00",
    title: "Similar words now suppress weak letter-only lookalikes",
    summary:
      "The similar-words section now applies stricter matching so words with superficial letter overlap are less likely to appear as confusing suggestions.",
    userHighlights: [
      "The `Ähnliche Wörter` list now focuses more on genuinely useful spelling neighbors instead of loose letter overlap.",
      "Lookalikes with conflicting grammar metadata are now filtered out.",
      "Very short or weakly structured matches are now rejected more aggressively to reduce noise.",
    ],
    technicalNotes: [
      "Strengthened `computeSimilarVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` with a length-aware near-spelling gate and additional structural similarity checks (prefix/suffix and bigram dice overlap).",
      "Added semantic gating for near-spelling candidates using normalized part-of-speech, lemma, and primary-meaning signals.",
      "Added regression unit tests in `src/utils/vocabularySimilarWords.test.ts` for accepted close variants and rejected noisy lookalikes (different starts without semantic signal, POS conflicts).",
    ],
  },
  {
    id: "trainer-same-translation-debug-trace-for-candidate-filtering",
    timestamp: "2026-04-27T21:39:22+02:00",
    title: "Trainer same-translation filtering now has optional debug tracing",
    summary:
      "The trainer can now emit structured debug output showing exactly why same-translation candidates are accepted or rejected, making it easier to investigate missing `Ähnliches Wort` notes.",
    userHighlights: [
      "When debug mode is enabled locally, the trainer logs the full candidate decision trace for the same-translation section.",
      "The trace includes the current word, expected normalized translation, and per-candidate rejection reasons.",
      "Debug logging stays off by default, so normal trainer behavior and UI remain unchanged.",
    ],
    technicalNotes: [
      "Extended `computeSameTranslationVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` with structured debug events for exclusion, same-word filtering, pending/missing translations, translation mismatch, and duplicate-word dedupe.",
      "Added local debug gating through `window.localStorage` key `trainer_same_translation_debug` (value `1` enables logs).",
      "Logs are emitted as `[trainer_same_translation_debug]` payloads so local diagnosis can map missing same-translation notes back to exact filter conditions.",
    ],
  },
  {
    id: "manual-trainer-collapse-on-mobile-no-longer-jumps-to-card-top",
    timestamp: "2026-04-27T21:36:52+02:00",
    title: "Manual trainer collapse on mobile no longer jumps back to card top",
    summary:
      "When you manually close an opened trainer card on mobile, the page now keeps your current reading position instead of force-scrolling to the top of the trainer.",
    userHighlights: [
      "Tapping an expanded trainer card to close it no longer triggers a sudden jump to the trainer top.",
      "The trainer stays calmer during manual open/close checks on mobile.",
      "Review actions such as `Knew it` still advance the flow as before.",
    ],
    technicalNotes: [
      "Removed the `tapped` transition side effect in `src/components/VocabularyPreviewCard.tsx` that previously called `scrollToTop()` on manual collapse.",
      "Kept explicit scroll-to-top behavior for progress actions (`known`/`almost`/`forgotten`) and delete flow, so next-card navigation behavior stays intact.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert manual collapse does not force-scroll back to the trainer root on mobile.",
    ],
  },
  {
    id: "similar-words-v2-structured-distinction-explainer",
    timestamp: "2026-04-27T21:33:01+02:00",
    title: "Similar words now include a structured distinction explainer",
    summary:
      "The similar-words card now offers an on-demand learning panel that explains usage differences between related words in a structured format.",
    userHighlights: [
      "Inside `Ähnliche Wörter`, a new `Unterschiede lernen` action opens a structured explanation for the current word and related alternatives.",
      "The panel now shows main-word meaning, practical usage, per-word differences, and a short key-distinction summary to reduce confusion.",
      "The explainer loads only on demand and then reuses cached results for the same word combination.",
    ],
    technicalNotes: [
      "Added a new backend endpoint `POST /api/reeprise/vocabulary/:id/similar_words_explainer/` in `barkguide/reeprise/views.py` with strict candidate-id validation and structured JSON normalization.",
      "Introduced persistent per-vocabulary explainer caching via `similar_words_explainer_cache` (new migration `0084_vocabulary_similar_words_explainer_cache.py`).",
      "Added request serializer `VocabularySimilarWordsExplainerRequestSerializer` in `barkguide/reeprise/serializer.py`.",
      "Implemented frontend mutation hook `src/hooks/useVocabularySimilarWordsExplainer.ts` and integrated a new expandable explainer panel in `src/components/VocabularyPreviewCard.tsx`.",
      "Added translation keys for explainer controls and section labels in `src/assets/translations.json`.",
      "Added backend regression tests in `barkguide/reeprise/tests.py` for explainer generation, cache reuse, and invalid-candidate handling, plus Playwright coverage in `e2e/similar-words-explainer.spec.ts`.",
    ],
  },
  {
    id: "source-text-focus-highlight-now-has-strong-distinct-visual-treatment",
    timestamp: "2026-04-27T21:31:26+02:00",
    title:
      "Source-text jump focus is now visually distinct from saved word highlights",
    summary:
      "When opening source context from trainer cards, the temporary focus highlight now uses a stronger dedicated visual style so it is clearly distinguishable from regular saved-vocabulary highlights.",
    userHighlights: [
      "Jumped source context now appears with a clearly different focus style from normal blue saved-word highlights.",
      "The focused sentence remains visible longer after navigation, making it easier to spot on arrival.",
      "Persistent saved-vocabulary highlight styling remains unchanged.",
    ],
    technicalNotes: [
      "Updated sentence-focus highlight classes in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to use a dedicated amber gradient + underline + pulse ring treatment.",
      "Extended sentence-focus flash lifetime in `src/components/GeneratedTextDetailsTextPane.tsx` so the navigation focus remains visible longer.",
      "Adjusted route-transition selector coverage in `e2e/reeprise-generation.spec.ts` and updated unit expectations in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx`.",
    ],
  },
  {
    id: "generated-text-now-supports-contextual-multi-word-translation-selection",
    timestamp: "2026-04-26T19:36:37+02:00",
    title:
      "Generated text now supports contextual multi-word translation selection",
    summary:
      "Text details now include a dedicated context-selection mode where multiple words can be translated together, including non-adjacent selections in the same sentence.",
    userHighlights: [
      "A new `Select context words` mode lets you click multiple words and request one combined translation.",
      "You can combine adjacent or non-adjacent words, as long as they belong to the same sentence.",
      "If you click into another sentence by mistake, the UI now shows a clear warning and a one-tap `Start new selection here` action.",
    ],
    technicalNotes: [
      "Extended `src/components/GeneratedTextDetailsTextPane.tsx` with context-selection mode state, multi-token selection rail, sentence-bound guard handling, and phrase translation requests via `/api/reeprise/translate/`.",
      "Extended shared span rendering in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to support context-selection click routing and selected-token styling without breaking existing add/preview behavior.",
      "Added translation keys for context-selection copy in `src/assets/translations.json`.",
      "Added Vitest coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for adjacent/non-adjacent selection flow and cross-sentence guard behavior.",
    ],
  },
  {
    id: "vocabulary-training-mode-now-includes-the-clarify-helper-panel",
    timestamp: "2026-04-26T19:20:40+02:00",
    title: "Vocabulary training mode now includes the Clarify helper",
    summary:
      "The same Clarify panel from text details is now available in vocabulary training mode, so questions can be asked without leaving trainer flow.",
    userHighlights: [
      "You can now ask clarification questions directly on the vocabulary training mode page.",
      "The helper keeps the same chat-style interaction used on text details.",
      "The helper is now bound to the active trainer card, so even generic questions stay anchored to that card’s word and sentence.",
    ],
    technicalNotes: [
      "Mounted shared `LanguageHelperPanel` in `src/routes/app/vocabulary_training_mode.tsx`.",
      "Wired helper inputs with training context (`generatedTextId`, active `word_language`, recent trainer vocabulary, sentence-based `contextText`) and bound the helper payload to the currently active trainer card (`word` + `sentence`).",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify helper rendering, send flow, and active-card-bound request payload.",
    ],
  },
  {
    id: "daily-callout-copy-is-now-fully-neutral-without-type-labeling",
    timestamp: "2026-04-26T19:10:25+02:00",
    title: "Daily callout copy is now fully neutral again",
    summary:
      "The daily card no longer shows text-type labeling and now keeps neutral wording in both pre-create and done states.",
    userHighlights: [
      "Daily card wording stays generic even when the generated text is a podcast or another specific type.",
      "No `Type:` meta line is shown on the done card anymore.",
      "The card now avoids extra inference logic and remains simpler to understand.",
    ],
    technicalNotes: [
      "Removed daily text-type label rendering from `src/routes/app/reeprise/index.tsx`.",
      "Removed now-unneeded type helper and tests (`src/utils/dailyTextType.ts`, `src/utils/dailyTextType.test.ts`).",
      "Removed unused translation key `dailyTextTypeMeta` from `src/assets/translations.json`.",
      "Updated Playwright regression in `e2e/reeprise-generation.spec.ts` to assert the daily done card remains neutral with no type label.",
    ],
  },
  {
    id: "vocabulary-training-mode-now-includes-shared-pronunciation-control",
    timestamp: "2026-04-26T19:04:34+02:00",
    title: "Vocabulary training mode now includes direct word pronunciation",
    summary:
      "Trainer cards in vocabulary training mode now include the same compact pronunciation button used across other vocabulary surfaces.",
    userHighlights: [
      "You can now trigger word audio directly inside each vocabulary training-mode card.",
      "The pronunciation action uses a compact placement in the card header to avoid adding visual noise.",
      "Audio behavior stays consistent with other vocabulary views because the same shared pronunciation control is reused.",
    ],
    technicalNotes: [
      "Reused `VocabularyPronunciationButton` in `src/routes/app/vocabulary_training_mode.tsx` instead of adding a second trainer-specific audio implementation.",
      "Kept layout compact by placing the shared control inline with the card question row and using smaller button sizing.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify the training-mode card renders the shared pronunciation control and sends pronunciation generation requests.",
    ],
  },
  {
    id: "daily-text-callout-now-shows-text-type-for-story-dialogue-podcast-and-more",
    timestamp: "2026-04-26T19:02:45+02:00",
    title: "Daily text callout now reflects the actual text type",
    summary:
      "The daily done-callout now surfaces the real generated text type (for example podcast) and no longer uses story-specific fallback wording in English.",
    userHighlights: [
      "When today’s daily text already exists, the done card now shows a type meta line such as `Type: Podcast`.",
      "Pre-creation daily card copy stays neutral and does not claim a type before the text is actually generated.",
      "English daily fallback text is now generic (`text`) instead of story-only wording.",
    ],
    technicalNotes: [
      "Added `src/utils/dailyTextType.ts` with text-type normalization for safe daily-card type labeling.",
      "Integrated type-aware callout metadata in `src/routes/app/reeprise/index.tsx` for the done daily-card state only (where a real generated text exists).",
      "Updated daily translation copy in `src/assets/translations.json` (`dailyTextHeading`, `dailyTextDoneDescription`) and added `dailyTextTypeMeta`.",
      "Added regression unit tests in `src/utils/dailyTextType.test.ts` for known-type normalization and unknown-type fallback behavior.",
    ],
  },
  {
    id: "similar-words-now-separates-spelling-neighbors-from-same-translation-words",
    timestamp: "2026-04-26T18:58:41+02:00",
    title:
      "Similar words now separate spelling neighbors from same-translation words",
    summary:
      "The similar-words card now keeps same-translation matches in a separate subsection so users can distinguish spelling similarity from translation overlap.",
    userHighlights: [
      "The `Ähnliche Wörter` card now has a dedicated `Gleiche Übersetzung` subsection under the existing spelling-based suggestions.",
      "Words with the same translation (for example `mieux` and `meilleur` -> `besser`) are shown separately instead of being mixed into one list.",
      "The new subsection includes a short note clarifying that same translation does not always mean same meaning.",
    ],
    technicalNotes: [
      "Added `computeSameTranslationVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` to resolve same-translation candidates in the same source language while excluding same-spelling duplicates and already-listed similar-word ids.",
      "Updated `src/components/VocabularyPreviewCard.tsx` to render two distinct blocks inside `Ähnliche Wörter`: existing spelling-based suggestions and a new same-translation block with explanatory copy.",
      "Added translation keys for the new subsection and note in `src/assets/translations.json`.",
      "Extended unit coverage in `src/utils/vocabularySimilarWords.test.ts` for same-translation matching, exclusion of same-spelling duplicates, and cross-section de-duplication.",
    ],
  },
  {
    id: "mobile-trainer-now-scrolls-back-to-next-word-after-close",
    timestamp: "2026-04-26T18:49:38+02:00",
    title:
      "Mobile trainer now returns to the next word after closing an open card",
    summary:
      "On mobile, closing an expanded trainer card from lower down the page now scrolls back to the trainer top so the next word is immediately visible.",
    userHighlights: [
      "After closing or continuing from an expanded trainer card on mobile, the view returns to the trainer top.",
      "The next trainer word stays in view instead of leaving users at lower mnemonic/details sections.",
      "Desktop trainer behavior remains unchanged.",
    ],
    technicalNotes: [
      "Implemented `scrollPreviewToTop` in `src/components/VocabularyPreviewCard.tsx` to perform a mobile-only window scroll to the trainer card root.",
      "Kept desktop behavior unchanged by guarding the scroll logic with a mobile-width check.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for the flow: expand trainer card on mobile, scroll down, continue, and verify the viewport returns to trainer top.",
    ],
  },
  {
    id: "text-details-links-now-use-complete-route-search-shape",
    timestamp: "2026-04-26T10:13:30+02:00",
    title: "Text-details links now consistently open with complete route state",
    summary:
      "Navigation to text details now uses a consistent search payload everywhere, preventing broken builds and keeping transitions stable when opening texts.",
    userHighlights: [
      "Opening generated texts from feed cards, notifications, and quick actions behaves the same as before.",
      "Daily-text and generation flows still navigate directly to the created text details page.",
      "Chapter navigation in text details continues to work without carrying stale word-focus state.",
    ],
    technicalNotes: [
      "Updated all `/app/reeprise/$generatedTextId` navigations that previously passed only `chapter` to now provide the full search object (`chapter`, `focusWord`, `focusSentence`, `focusToken`).",
      "Patched route callers in `src/routes/app/route.tsx`, `src/routes/app/reeprise/index.tsx`, `src/components/ReepriseTextCard.tsx`, `src/components/GenerateTextCard.tsx`, `src/routes/app/vocabulary_training_mode.tsx`, and `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Verified with `npm run build` (includes `tsc -b`) to confirm TypeScript route-search type safety passes end to end.",
    ],
  },
  {
    id: "trainer-source-link-skips-word-focus-when-source-sentence-missing",
    timestamp: "2026-04-26T10:06:24+02:00",
    title:
      "Trainer source-text jump no longer fakes focus when source sentence is missing",
    summary:
      "When a trainer card has no saved source sentence, opening the source text now avoids fallback word-only focus highlights.",
    userHighlights: [
      "Cards without `Ausgangssatz` still open `Zum Ursprungstext` normally.",
      "No misleading word-only flash highlight appears when sentence context is unavailable.",
      "Sentence-level focus remains unchanged for cards that do have a saved source sentence.",
    ],
    technicalNotes: [
      "Updated source-text links in `src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_trainer.tsx`, and `src/routes/app/vocabulary_training_mode.tsx` to only pass focus params when a source sentence exists.",
      "Extended Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts` to assert no focus highlight is rendered for missing-source-sentence navigation.",
      "No backend changes were required.",
    ],
  },
  {
    id: "trainer-source-link-now-uses-clear-copy-when-source-sentence-missing",
    timestamp: "2026-04-26T10:01:01+02:00",
    title: "Trainer now uses clearer copy when source sentence is missing",
    summary:
      "When a card can still open the source text but the sentence field itself is missing, trainer cards now show explicit copy that the sentence was not saved on this card.",
    userHighlights: [
      "You no longer see the contradictory `Kein Ausgangssatz verfügbar.` state next to an available `Zum Ausgangstext` button.",
      "Cards with source-text navigation but no saved sentence now explain that the sentence is not stored on the card.",
      "Source-text navigation remains available and unchanged.",
    ],
    technicalNotes: [
      "Added `resolveSourceSentenceFallbackKey` in `src/utils/vocabularySourceSentenceCopy.ts` and wired it into trainer card sentence rendering.",
      "Applied the same fallback-key behavior in both `src/components/VocabularyPreviewCard.tsx` and `src/routes/app/vocabulary_trainer.tsx`.",
      "Added translation key `vocabulary_source_sentence_not_saved_on_card` in `src/assets/translations.json`.",
      "Added unit coverage in `src/utils/vocabularySourceSentenceCopy.test.ts` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for source-link cards without saved source sentence.",
    ],
  },
  {
    id: "source-sentence-focus-now-has-distinct-visual-highlight",
    timestamp: "2026-04-26T09:56:16+02:00",
    title: "Source sentence focus now stands out from normal word highlights",
    summary:
      "When jumping from trainer to text details, focused source sentences now use a distinct visual highlight style so the sentence is easy to spot even among many highlighted words.",
    userHighlights: [
      "Focused sentence words now use a clearly different color treatment from regular vocabulary highlights.",
      "Sentence focus is easier to scan in dense texts with multiple highlighted words.",
      "Regular word highlights and preview focus behavior remain intact.",
    ],
    technicalNotes: [
      "Extended `createTextSpans` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` with dedicated `sentenceFlashStarts` handling and sentence-focus highlight/ring classes.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to track sentence-focus flash starts separately from regular word flash starts.",
      "Added regression tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to assert sentence-focus styling differs from standard word highlight styling.",
    ],
  },
  {
    id: "vocabulary-canonical-noun-form-guardrails-for-bad-article-shapes",
    timestamp: "2026-04-26T09:48:33+02:00",
    title: "Vocabulary cards now guard against broken noun article forms",
    summary:
      "When noun metadata looks inconsistent, cards now avoid showing a likely broken canonical article form and fall back to the seen word form.",
    userHighlights: [
      "Vocabulary and trainer cards now avoid displaying malformed noun dictionary labels when article metadata is inconsistent.",
      "Valid canonical nouns with apostrophe articles (for example `l'ami`) still render correctly.",
      "This only affects suspicious noun-canonical labels and keeps normal noun article behavior unchanged.",
    ],
    technicalNotes: [
      "Added canonical noun-form guard helpers in `src/utils/vocabularyTranslationDetails.ts` to validate dictionary-form/article alignment before using canonical noun labels.",
      "When the noun dictionary form fails the guard, `getVocabularyPrimaryWord` now falls back to source surface form instead of rendering a potentially bad canonical value.",
      "Added regression tests in `src/utils/vocabularyTranslationDetails.test.ts` for article mismatch fallback and apostrophe-article acceptance.",
    ],
  },
  {
    id: "trainer-source-text-jump-now-opens-context-and-highlights-source-sentence",
    timestamp: "2026-04-26T09:40:00+02:00",
    title: "Trainer words can now jump back to source text with sentence focus",
    summary:
      "From trainer cards, you can now open the original text directly and the matching source sentence is highlighted so context is immediately visible.",
    userHighlights: [
      "Expanded trainer cards now include an `Open source text` action when the word has a saved source usage.",
      "The jump opens the correct generated text (including chapter context when available).",
      "On arrival, the matching source sentence is highlighted so you can quickly re-read the exact context.",
    ],
    technicalNotes: [
      "Extended vocabulary usage payloads with `chapter_counter` in `barkguide/reeprise/serializer.py` and ensured chapter-aware prefetching in `barkguide/reeprise/views.py`.",
      "Added source-text navigation links in trainer surfaces (`src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_training_mode.tsx`, `src/routes/app/vocabulary_trainer.tsx`).",
      "Extended text-details route search handling (`focusWord`, `focusSentence`, `focusToken`) in `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Updated sentence focus behavior in `src/components/GeneratedTextDetailsTextPane.tsx` to flash-highlight all words of the matched source sentence and scroll it into view.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` and Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-cards-now-reuse-canonical-vocabulary-word-pairs",
    timestamp: "2026-04-26T09:37:13+02:00",
    title: "Trainer cards now reuse canonical vocabulary word pairs",
    summary:
      "Trainer cards now display the same canonical source/translation pair as vocabulary cards, including noun article forms from structured translation details.",
    userHighlights: [
      "Trainer prompts and answers now match the exact word pair shown on the vocabulary card for the same entry.",
      "Noun cards with canonical article forms (for example `le ...`) now keep those forms in trainer mode too.",
      "Direction toggles still work, but they now flip the same normalized pair instead of raw stored strings.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/vocabulary_training_mode.tsx` to resolve trainer labels from shared translation-details helpers instead of raw `word`/`translation` fields.",
      "Added `src/utils/vocabularyTrainerCardLabels.ts` to centralize trainer-card word-pair resolution, including pending-translation placeholder normalization.",
      "Added regression coverage in `src/utils/vocabularyTrainerCardLabels.test.ts` for canonical article forms, reversed direction mapping, and pending translation fallback.",
    ],
  },
  {
    id: "trainer-delete-now-handles-already-removed-words-without-false-error",
    timestamp: "2026-04-26T09:35:52+02:00",
    title:
      "Trainer delete now handles already-removed words without a false error",
    summary:
      "Deleting a word from the trainer no longer shows a failure message when that word was already removed in the meantime.",
    userHighlights: [
      "Delete in trainer now stays smooth even if the word was already removed elsewhere.",
      "You no longer get a misleading delete-failed snackbar for that stale-card case.",
      "Trainer continues forward instead of appearing blocked by a stale delete state.",
    ],
    technicalNotes: [
      "Updated `src/hooks/useVocabularyDelete.ts` to treat `404` delete responses as an idempotent success for stale UI state.",
      "Kept existing query invalidation and `vocabulary-deleted` event dispatch so trainer/list state stays in sync.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for deleting a trainer card after the same vocabulary was already deleted via API.",
    ],
  },
  {
    id: "podcast-continue-flow-and-details-player-alignment",
    timestamp: "2026-04-26T09:29:56+02:00",
    title: "Podcast continuation now keeps episode behavior on text details",
    summary:
      "Continuing a podcast text with selected words now keeps podcast episode behavior, and the text-details page now includes podcast-specific controls that match the podcast card experience.",
    userHighlights: [
      "Using `Mit Wörtern fortsetzen` on a podcast now creates a real podcast continuation chapter flow instead of dropping podcast behavior.",
      "Podcast text details now show podcast meta info and a transcript toggle directly in the audio section.",
      "Transcript view in details works for both the base podcast text and generated continuation chapters.",
    ],
    technicalNotes: [
      "Backend: `generate_chapter` now schedules `generate_speech` after commit for podcast chapters in `barkguide/reeprise/tasks.py`, so new podcast chapters enqueue chapter-level audio generation automatically.",
      "Frontend: added podcast-specific details UI in `src/routes/app/reeprise/$generatedTextId.tsx` (meta line, podcast idle copy, transcript toggle/panel) and new translation keys in `src/assets/translations.json`.",
      "Regression coverage: added Django test for podcast chapter speech enqueueing in `barkguide/reeprise/tests.py` and Playwright flow coverage in `e2e/reeprise-generation.spec.ts` for podcast continue-with-words navigation and transcript behavior.",
    ],
  },
  {
    id: "quick-translate-now-shows-example-sentence-in-both-target-and-source-languages",
    timestamp: "2026-04-26T09:28:30+02:00",
    title:
      "Quick Translate now shows the same example sentence in both languages",
    summary:
      "Übersetzung unterwegs now returns and displays a paired example: one sentence in the selected target language and the same sentence in the detected source language.",
    userHighlights: [
      "After translating a short word or phrase, you now see the target-language example sentence plus the same sentence in the detected source language.",
      "The source-language example is shown directly below the target example for immediate side-by-side comparison.",
      "Saving to vocabulary keeps the existing target-language sentence behavior unchanged.",
    ],
    technicalNotes: [
      "Extended `/api/reeprise/translate/` request/response handling to support `source_example_sentence` and to generate it from the target example when source language is known.",
      "Updated `src/components/QuickTranslateCard.tsx` state and payload wiring to render and persist both example sentences in the quick-translate save flow.",
      "Added translation keys for the new target/source example labels in `src/assets/translations.json`.",
      "Added backend regression tests in `reeprise/tests.py` covering dual-sentence response generation and save-path reuse of provided source examples.",
    ],
  },
  {
    id: "mnemonic-anchor-input-now-uses-dynamic-word-specific-guidance",
    timestamp: "2026-04-26T09:15:51+02:00",
    title: "Anchor-word input now uses dynamic per-word guidance",
    summary:
      "The mnemonic anchor input no longer shows a fixed example and now guides users with dynamic wording based on the currently selected vocabulary word.",
    userHighlights: [
      "Static example placeholder was replaced with neutral anchor-word guidance.",
      "A new helper line now references the current word directly (sound-similar hint).",
      "Anchor input guidance now remains relevant across different vocabulary items.",
    ],
    technicalNotes: [
      "Updated anchor placeholder copy and added a dynamic hint key with `{{word}}` interpolation in `src/assets/translations.json`.",
      "Rendered the new dynamic anchor hint in `src/components/VocabularyPreviewCard.tsx` using the current target word.",
      "No API, model, or generation pipeline changes.",
    ],
  },
  {
    id: "reeprise-top-area-spacing-rhythm-polish-for-desktop",
    timestamp: "2026-04-26T09:14:27+02:00",
    title: "Top-area spacing on Reeprise desktop was polished",
    summary:
      "The top layout rhythm was tuned so daily cards, section spacing, and utility-column stack spacing feel more intentional and balanced across desktop widths.",
    userHighlights: [
      "Daily card row spacing is slightly more open and readable.",
      "Main column section rhythm is tighter and more consistent.",
      "Utility column stack now uses matching vertical cadence.",
    ],
    technicalNotes: [
      "Adjusted top layout spacing classes in `src/routes/app/reeprise/index.tsx` (`space-y`, `gap`) for the main column, daily row, and desktop utility column stack.",
      "No component behavior, API, or data-model changes.",
      "Re-ran desktop utility-column Playwright regression to confirm layout behavior remained intact.",
    ],
  },
  {
    id: "guided-mnemonic-anchor-suggestions-now-explicitly-manual",
    timestamp: "2026-04-26T06:48:32+02:00",
    title:
      "Guided mnemonic anchor suggestions now run only on explicit request",
    summary:
      "Anchor suggestions in guided mnemonic mode are no longer auto-fetched in the background and now run only when you click `Suggest anchors`.",
    userHighlights: [
      "No background anchor-suggestion generation is triggered when opening mnemonic editing.",
      "Suggestions load only after an explicit click on `Suggest anchors`.",
      "Guided mnemonic generation with selected anchor and direction remains unchanged.",
    ],
    technicalNotes: [
      "Removed the auto-suggestion `useEffect` fallback in `src/components/VocabularyPreviewCard.tsx`.",
      "Kept the existing manual suggestion button flow and mutation wiring unchanged.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert zero anchor-suggestion calls before click and one call after click.",
    ],
  },
  {
    id: "reeprise-desktop-compose-card-centered-outside-split-grid",
    timestamp: "2026-04-25T18:45:47+02:00",
    title:
      "Desktop Generate Text card now centers independently of split columns",
    summary:
      "The fixed desktop Generate Text card is now centered in the usable content area while the existing 2:1 split structure remains in place.",
    userHighlights: [
      "The Generate Text card now appears visually centered on desktop.",
      "Side navigation width is still respected for positioning.",
      "Main-content split behavior for the page remains unchanged.",
    ],
    technicalNotes: [
      "Kept the desktop `2fr / 1fr` split container in `src/routes/app/reeprise/index.tsx`.",
      "Moved the `GenerateTextCard` render to a centered wrapper outside the split grid within `reeprise-desktop-compose`.",
      "Updated the Playwright desktop layout regression in `e2e/reeprise-generation.spec.ts` to measure the compose form bounds/center directly instead of the outer compose shell.",
    ],
  },
  {
    id: "reeprise-desktop-compose-now-uses-sidenav-aware-width",
    timestamp: "2026-04-25T18:40:48+02:00",
    title: "Desktop Generate Text composer now centers in usable content width",
    summary:
      "The fixed desktop Generate Text composer now uses the available content width after side navigation is subtracted, so it stays centered correctly in both compact and expanded sidenav states.",
    userHighlights: [
      "Desktop composer alignment now tracks the real reading area, not full viewport width.",
      "Composer no longer drifts toward the side navigation when the nav is expanded.",
      "Collapsed and expanded side navigation both keep stable composer centering.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` so desktop composer width is `calc(100vw - var(--app-desktop-nav-width))` while keeping center alignment via `left` + `-translate-x-1/2`.",
      "Reused dynamic `--app-desktop-nav-width` from the app shell (`72px` compact, `440px` expanded).",
      "Extended `e2e/reeprise-generation.spec.ts` desktop layout regression to assert composer bounds and center alignment for both collapsed and expanded side navigation states.",
    ],
  },
  {
    id: "reeprise-utility-column-top-offset-removed-for-top-line-alignment",
    timestamp: "2026-04-25T18:36:53+02:00",
    title: "Trainer column now starts on the same top line as daily cards",
    summary:
      "The desktop utility column sticky offset was reduced so the trainer card aligns with the daily card row instead of starting visibly lower.",
    userHighlights: [
      "Trainer column now starts at the same top line as daily weather cards.",
      "Utility column remains independently scrollable.",
      "No card-height forcing was reintroduced.",
    ],
    technicalNotes: [
      "Changed Reeprise desktop utility sticky offset from `xl:top-20` to `xl:top-0` in `src/routes/app/reeprise/index.tsx`.",
      "Preserved existing utility overflow behavior (`overflow-y-auto`) and hidden-scrollbar styling.",
      "Re-ran Playwright desktop utility scroll regression.",
    ],
  },
  {
    id: "reverted-forced-trainer-daily-height-parity-on-desktop",
    timestamp: "2026-04-25T18:35:05+02:00",
    title: "Reverted forced equal height between trainer and daily cards",
    summary:
      "The temporary desktop rule forcing trainer and daily weather cards to identical fixed height was reverted.",
    userHighlights: [
      "Trainer card no longer uses forced fixed-height parity with daily weather cards.",
      "Desktop utility column keeps independent scrolling behavior.",
      "Top alignment between columns remains unchanged.",
    ],
    technicalNotes: [
      "Removed shared fixed-height token and trainer-slot wrapper from `src/routes/app/reeprise/index.tsx`.",
      "Dropped temporary `className` extension on `VocabularyPreviewCard` used only for forced fixed-height behavior.",
      "Removed the fixed-height parity Playwright regression and kept utility-column scroll regression coverage.",
    ],
  },
  {
    id: "reeprise-desktop-columns-now-share-daily-section-start-line",
    timestamp: "2026-04-25T12:53:55+02:00",
    title: "Trainer column now starts at the same top line as daily cards",
    summary:
      "On Reeprise desktop, the trainer utility column now starts at the same vertical position as the daily weather card section for consistent alignment.",
    userHighlights: [
      "Trainer card and daily weather cards now begin on the same top line.",
      "Notification messages no longer push only the left column down.",
      "Desktop utility scroll and fixed-height trainer behavior remain unchanged.",
    ],
    technicalNotes: [
      "Moved `NotificationsCenter` render placement in `src/routes/app/reeprise/index.tsx` above the two-column desktop grid so both columns share the same start anchor.",
      "Removed duplicate notification rendering from inside the main (left) column.",
      "Re-ran existing desktop layout Playwright regressions for utility scrolling and trainer/daily card height parity.",
    ],
  },
  {
    id: "trainer-card-height-aligned-to-daily-weather-cards-on-desktop",
    timestamp: "2026-04-25T12:52:42+02:00",
    title: "Desktop trainer card height now matches daily weather cards",
    summary:
      "The Reeprise desktop trainer card now uses the same fixed card height as the daily weather cards, with internal trainer scrolling inside that frame.",
    userHighlights: [
      "Trainer card and daily weather cards now share the same desktop height.",
      "Long trainer content scrolls inside the trainer card instead of stretching the card height.",
      "The right utility column still keeps independent scrolling behavior.",
    ],
    technicalNotes: [
      "Introduced a shared desktop card-height class token in `src/routes/app/reeprise/index.tsx` and applied it to desktop-strip daily cards plus the desktop trainer slot.",
      "Extended `VocabularyPreviewCard` with an optional `className` prop so desktop layout can apply internal overflow handling without changing other surfaces.",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` asserting desktop trainer-slot height matches the primary daily weather card height.",
    ],
  },
  {
    id: "reeprise-mobile-generate-card-centers-with-sidenav-aware-width",
    timestamp: "2026-04-25T12:51:30+02:00",
    title: "Generate Text card now stays centered in compact layouts",
    summary:
      "In compact Reeprise layouts, the Generate Text card now centers against the available content area after side navigation width is accounted for.",
    userHighlights: [
      "The Generate Text card stays visually centered when the compact side navigation rail is visible.",
      "Card width now follows the real usable content width instead of the raw viewport.",
      "Desktop fixed composer behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added dynamic `--app-desktop-nav-width` on `src/routes/app/route.tsx` based on side-nav state (`72px` collapsed, `440px` expanded).",
      "Updated the non-`xl` Generate Text wrapper in `src/routes/app/reeprise/index.tsx` to center and use a sidenav-aware max-width.",
      "Extended `e2e/reeprise-generation.spec.ts` with a compact-layout regression that asserts the mobile compose shell is centered within viewport width minus sidenav width.",
    ],
  },
  {
    id: "guided-mnemonic-ux-now-uses-anchor-words-and-clear-submit-flow",
    timestamp: "2026-04-25T12:50:21+02:00",
    title: "Guided mnemonic flow now has explicit submit and clearer output",
    summary:
      "Guided mnemonic input now has a clear submit action, anchor suggestions are constrained to short sound-similar anchor words, and the generated draft section is explicitly labeled.",
    userHighlights: [
      "You can now explicitly submit the anchor word and scene direction with `Generate from these inputs`.",
      "Anchor suggestions are now short sound-based anchor words instead of random sentence fragments.",
      "The generated result area is now labeled `Generated draft` so it is clear where the final mnemonic appears.",
    ],
    technicalNotes: [
      "Updated mnemonic prompt guidance rules in `barkguide/reeprise/tasks.py` to enforce user guidance usage and exact-anchor behavior when requested.",
      "Refined anchor-suggestion prompt and cleaner in `barkguide/reeprise/tasks.py` to prefer short 1–2 word, sound-similar anchors and filter long sentence-like outputs.",
      "Updated `src/components/VocabularyPreviewCard.tsx` with explicit guidance-submit button, clearer result labeling, and automatic scroll-to-result after generation.",
      "Adjusted mnemonic UI translation keys in `src/assets/translations.json` and updated related backend/E2E regression tests.",
    ],
  },
  {
    id: "reeprise-scrollbar-hide-keeps-utility-wheel-scroll-active",
    timestamp: "2026-04-25T12:41:52+02:00",
    title: "Hidden utility scrollbar now preserves scroll interaction",
    summary:
      "The utility column scrollbar hide style was adjusted so the scrollbar stays invisible without breaking normal wheel/touchpad scrolling.",
    userHighlights: [
      "The right utility column remains scrollable.",
      "Scrollbar visuals stay hidden.",
      "No layout or spacing changes were introduced.",
    ],
    technicalNotes: [
      "Updated `.hide-scrollbar` in `src/index.css` to use WebKit `display: none` for scrollbar pseudo-element instead of zero-size scrollbar dimensions.",
      "Kept cross-browser scrollbar suppression via `scrollbar-width: none` and `-ms-overflow-style: none`.",
      "Retained desktop utility column overflow behavior and re-ran the existing Playwright utility-column scroll regression.",
    ],
  },
  {
    id: "reeprise-desktop-utility-scrollbar-hidden-while-keeping-scroll",
    timestamp: "2026-04-25T12:35:03+02:00",
    title: "Desktop utility column now scrolls without visible scrollbar",
    summary:
      "The Reeprise desktop utility column keeps independent scrolling but now hides the scrollbar UI so the panel looks cleaner.",
    userHighlights: [
      "You can still scroll the trainer column with wheel or touchpad.",
      "The visible scrollbar in the right utility column is now hidden.",
      "Trainer expansion no longer forces full-page growth and no longer shows a side scrollbar.",
    ],
    technicalNotes: [
      "Added a reusable `.hide-scrollbar` helper in `src/index.css` with `scrollbar-width: none`, `-ms-overflow-style: none`, and WebKit scrollbar suppression.",
      "Applied `hide-scrollbar` to the desktop utility aside in `src/routes/app/reeprise/index.tsx` while keeping `overflow-y-auto` behavior.",
      "Verified existing desktop utility scroll regression in `e2e/reeprise-generation.spec.ts` still passes.",
    ],
  },
  {
    id: "reeprise-desktop-trainer-column-now-scrolls-independently",
    timestamp: "2026-04-25T12:31:55+02:00",
    title: "Desktop trainer column now scrolls independently",
    summary:
      "On Reeprise desktop layout, the utility column now has its own scroll area so an expanded trainer card no longer forces the full page height to grow.",
    userHighlights: [
      "The right-side trainer/utility column can now scroll independently on desktop.",
      "Opening a larger trainer card no longer pushes the whole page height unexpectedly.",
      "Compose card spacing at the bottom remains preserved while scrolling the utility column.",
    ],
    technicalNotes: [
      "Updated the desktop utility aside in `src/routes/app/reeprise/index.tsx` with `max-height`, `overflow-y-auto`, and bottom padding for fixed compose-area clearance.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert the desktop utility column keeps `overflow-y-auto` behavior.",
      "No API or data-model changes.",
    ],
  },
  {
    id: "trainer-similar-words-semantic-fallback-and-deduping-refinement",
    timestamp: "2026-04-25T09:44:21+02:00",
    title: "Trainer similar words now distinguishes senses more reliably",
    summary:
      "Similar-word suggestions now keep same-gloss homographs separated by grammar/lemma/context and can still suggest exact-spelling alternatives when translation is pending.",
    userHighlights: [
      "Exact same spelling can still appear when one entry has pending translation but context indicates a different sense.",
      "Same-gloss entries are no longer merged when they represent different grammatical senses.",
      "True duplicates with same spelling and same sense still collapse into one suggestion.",
    ],
    technicalNotes: [
      "Refined semantic signature construction in `src/utils/vocabularySimilarWords.ts` to use pending-aware meaning detection plus context/lemma/grammar fallbacks.",
      "Changed dedupe keying to combine meaning with grammar/lemma/context (instead of meaning-only), while preserving duplicate collapse for same-sense entries.",
      "Extended regression coverage in `src/utils/vocabularySimilarWords.test.ts` with pending-meaning fallback and same-gloss/different-grammar cases.",
    ],
  },
  {
    id: "guided-mnemonic-now-supports-ai-anchor-suggestions",
    timestamp: "2026-04-25T09:43:46+02:00",
    title: "Guided mnemonic now supports AI anchor suggestions",
    summary:
      "Mnemonic drafting now supports guided anchors and scene direction, including one-click AI anchor suggestions when you do not have an idea yet.",
    userHighlights: [
      "You can add your own mnemonic anchor before generating a draft.",
      "A new `Suggest anchors` action proposes short anchor ideas for the selected word.",
      "You can add a scene direction and optionally ask the generator to keep your anchor wording as close as possible.",
    ],
    technicalNotes: [
      "Extended `generate_mnemonic` request handling in `barkguide/reeprise/views.py` to accept optional `anchor`, `direction`, and `use_anchor_exactly` guidance.",
      "Added `/api/reeprise/vocabulary/:id/mnemonic_anchor_suggestions/` action that calls AI and returns cleaned, deduplicated suggestion chips.",
      "Updated mnemonic prompt builders and cleaning helpers in `barkguide/reeprise/tasks.py`, including guided-prompt injection and structured anchor-suggestion parsing.",
      "Updated `src/components/VocabularyPreviewCard.tsx` with guided mnemonic fields, suggestion chips, auto-suggest fallback on empty state, and payload wiring through `useVocabularyGenerateMnemonic`.",
      "Added new translation keys in `src/assets/translations.json` and regression coverage in both `reeprise/tests.py` and `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-similar-words-now-shows-homographs-and-hides-true-duplicates",
    timestamp: "2026-04-25T09:37:37+02:00",
    title: "Trainer similar words now handles homographs correctly",
    summary:
      "The “Ähnliche Wörter” list now includes exact same-spelling words when they carry a different meaning, while collapsing real duplicates that only differ by source sentence.",
    userHighlights: [
      "Exact same spelling with different meaning now appears in the trainer suggestions.",
      "Repeated duplicates with the same spelling and same meaning are reduced to a single suggestion.",
      "A new label clarifies when a match is the same spelling but another meaning.",
    ],
    technicalNotes: [
      "Extracted similar-word ranking into `src/utils/vocabularySimilarWords.ts` for deterministic filtering and deduplication.",
      "Updated `src/components/VocabularyPreviewCard.tsx` to use the new selector and show a dedicated reason label for exact-spelling/different-meaning matches.",
      "Added regression tests in `src/utils/vocabularySimilarWords.test.ts` covering homograph inclusion and duplicate-meaning collapse.",
    ],
  },
  {
    id: "struggle-word-text-now-uses-at-most-five-words",
    timestamp: "2026-04-25T09:36:27+02:00",
    title: "Text with your words now uses at most five words",
    summary:
      "The struggle-word text flow now limits selected words to five, so generated texts stay focused and easier to follow.",
    userHighlights: [
      "“Text mit deinen Wörtern” now uses a maximum of 5 words.",
      "If fewer than 5 eligible words exist, it still uses the available set.",
      "The card count and generated text selection stay aligned to the same cap.",
    ],
    technicalNotes: [
      "Added `STRUGGLE_PRACTICE_VOCABULARY_LIMIT = 5` in `barkguide/reeprise/tasks.py` and reused it as the default selector limit.",
      "Updated struggle-practice generation and `/api/reeprise/vocabulary/struggle_words/` endpoint to use the shared limit constant.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert the struggle-words endpoint returns at most five entries.",
    ],
  },
  {
    id: "dialogue-text-typesetting-now-normalizes-punctuation-and-line-breaking",
    timestamp: "2026-04-22T21:18:14+02:00",
    title: "Dialogue text now follows cleaner typesetting rules",
    summary:
      "Text details now normalize detached punctuation and apply better line-breaking behavior, so dialogue lines read more like properly typeset text on mobile and desktop.",
    userHighlights: [
      "Detached punctuation like `word . Next` now renders as `word. Next`.",
      "French dialogue spacing now keeps punctuation attached using proper narrow non-breaking spacing (for example before `?`).",
      "Long words and line wraps now break more gracefully in dialogue and standard text views.",
    ],
    technicalNotes: [
      "Added locale-aware punctuation-spacing normalization in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` during symbol-run rendering.",
      "Passed text language into span rendering so French punctuation spacing can follow language-specific behavior.",
      "Applied `lang`, `hyphens-auto`, and improved wrap rules in `src/components/GeneratedTextDetailsTextPane.tsx` for cleaner typographic line breaks.",
      "Extended regression tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for detached-period and French-question-mark spacing.",
    ],
  },
  {
    id: "struggle-word-label-now-uses-less-technical-german-wording",
    timestamp: "2026-04-22T21:13:15+02:00",
    title: "Struggle-word labels now use less technical wording",
    summary:
      "The newly introduced focus-word phrasing was softened to more natural wording so the UI stays calm and human without sounding technical.",
    userHighlights: [
      "German label now reads `Text mit deinen Wörtern`.",
      "English label now reads `Text with your words`.",
      "Matching loading and error lines were aligned to the same tone.",
    ],
    technicalNotes: [
      "Updated `struggle_words_title`, `struggle_words_started`, and `struggle_words_failed` in `src/assets/translations.json`.",
      "Replaced `Fokuswörtern` / `Focus-word` wording with less technical alternatives.",
      "No behavior or API changes.",
    ],
  },
  {
    id: "focus-word-text-label-replaces-practice-text-wording",
    timestamp: "2026-04-22T21:11:07+02:00",
    title: "Struggle-word flow now avoids classroom wording",
    summary:
      "The struggle-word text labels were changed from practice-style wording to neutral product language that matches the editorial voice guidelines.",
    userHighlights: [
      "The label was moved away from classroom wording to neutral product language.",
      "Status and error messages in the same flow now use matching neutral wording.",
      "Behavior is unchanged; only copy was adjusted.",
    ],
    technicalNotes: [
      "Updated `struggle_words_title`, `struggle_words_started`, `struggle_words_failed`, and `struggle_words_topic_generic` in `src/assets/translations.json`.",
      "Removed `Übungstext` and `Practice text` phrasing from the struggle-word generation flow.",
      "No route, state, API, or data-model changes.",
    ],
  },
  {
    id: "struggle-words-description-copy-now-uses-neutral-editorial-wording",
    timestamp: "2026-04-22T21:09:40+02:00",
    title: "Struggle-word text description now uses neutral wording",
    summary:
      "The practice-text description was tightened to neutral editorial phrasing and no longer uses progress-coaching language.",
    userHighlights: [
      "The struggle-word helper text now reads more direct and factual.",
      "Meaning stays the same: it still describes a short text built from selected words.",
      "No behavior or flow changes were made.",
    ],
    technicalNotes: [
      "Updated `struggle_words_description` in `src/assets/translations.json` for both German and English.",
      "Replaced the phrase `for your next step` with neutral wording aligned to the product voice guidelines.",
      "No route, state, or API changes were required.",
    ],
  },
  {
    id: "translations-copy-tone-aligned-to-editorial-style-guidelines",
    timestamp: "2026-04-22T21:05:42+02:00",
    title: "Application copy now follows the editorial voice guidelines",
    summary:
      "User-facing translation copy was revised to remove chatty, motivational, and promotional phrasing so interface text stays calm, neutral, and concise.",
    userHighlights: [
      "Messages now avoid exclamation-heavy and hype-style wording.",
      "Emoji-based UI copy was removed from key flows.",
      "Pricing and onboarding labels now use more neutral phrasing.",
    ],
    technicalNotes: [
      "Updated multiple entries in `src/assets/translations.json` including naming, signup, login subtitle, subscription success, photo-import success, vocabulary empty state, and pricing copy keys.",
      "Replaced motivational/promotional wording (for example `Get started`, `Perfect for`, and `Happy learning`) with neutral alternatives.",
      "Removed decorative emojis from interface copy to keep tone consistent across languages.",
    ],
  },
  {
    id: "dialogue-auto-detection-now-ignores-structured-step-and-chapter-lines",
    timestamp: "2026-04-22T20:57:35+02:00",
    title: "Dialogue auto-detection now ignores structured labels",
    summary:
      "Text details no longer switch into dialogue layout for structured prose lines like step/chapter labels that use a colon.",
    userHighlights: [
      "Structured lines such as “Step 1: ...” no longer trigger speaker-style dialogue blocks.",
      "Dialogue layout still works for normal multi-speaker text.",
      "Reading layout is now more stable for instructional or outline-like content.",
    ],
    technicalNotes: [
      "Added a stricter speaker-label heuristic in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` for dialogue parsing.",
      "Dialogue detection now rejects labels with digits and common structured prefixes such as `step`, `chapter`, and `part`.",
      "Extended unit tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` with step/chapter false-positive regression cases.",
    ],
  },
  {
    id: "grammar-lesson-now-uses-configured-model-with-cache-and-token-accounting",
    timestamp: "2026-04-22T20:55:59+02:00",
    title:
      "Grammar lesson now follows model settings and avoids duplicate calls",
    summary:
      "Grammar lesson generation now uses the configured AI model, tracks token usage, and reuses a short-lived cached result for repeated requests on the same text.",
    userHighlights: [
      "Grammar lesson generation is now more stable under repeated clicks.",
      "Repeated requests for the same text can return faster during a short cache window.",
      "Response content format remains unchanged.",
    ],
    technicalNotes: [
      "Updated `grammar_lesson` in `barkguide/reeprise/views.py` to resolve model selection from `UserSetting.ai_model` with a safe fallback.",
      "Added usage-token accounting for grammar lesson responses via existing `track_tokens_in_account` flow.",
      "Added a short TTL cache key scoped by user, text, response language, learning language, model, and text-content hash.",
    ],
  },
  {
    id: "highlight-preview-word-matching-now-respects-line-boundaries",
    timestamp: "2026-04-22T20:53:26+02:00",
    title: "Highlight preview matching now works across line breaks",
    summary:
      "Word preview mapping now correctly stops at line boundaries, so highlighted words at the end of a line still resolve to the expected saved vocabulary.",
    userHighlights: [
      "Highlighted words followed by a line break now open the correct preview.",
      "No behavior changed for normal inline punctuation and spaces.",
      "Preview reliability is improved in multiline text details.",
    ],
    technicalNotes: [
      "Added `WORD_BOUNDARY_SYMBOLS` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to include `\\n`, `\\r`, and `\\t` for preview-word extraction.",
      "Updated `readWordAtStart` in the same file to use the boundary-aware symbol set.",
      "Added regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for `harbor\\nmarket` highlight mapping.",
    ],
  },
  {
    id: "desktop-recent-history-copy-now-matches-mixed-history-content",
    timestamp: "2026-04-22T20:51:39+02:00",
    title: "Desktop history copy now reflects mixed recent texts",
    summary:
      "The desktop strip language was updated from daily-only wording to recent-text wording, so labels match the current mixed-history behavior.",
    userHighlights: [
      "History cards now use wording that fits mixed recent content.",
      "The show-more control now describes recent texts instead of older daily cards.",
      "Behavior stays the same; only wording and test naming were clarified.",
    ],
    technicalNotes: [
      "Updated translation copy in `src/assets/translations.json` for `dailyTextPreviousBadge`, `reeprise_daily_show_more`, and `reeprise_daily_show_less` to use recent-history wording.",
      "Renamed the matching Playwright test title in `e2e/reeprise-generation.spec.ts` to reflect recent-history semantics.",
      "No API or data model changes were required.",
    ],
  },
  {
    id: "dialogue-layout-now-emphasizes-the-first-speaker-as-primary",
    timestamp: "2026-04-22T20:27:32+02:00",
    title: "Dialogue layout now highlights the first speaker as primary",
    summary:
      "Dialogue text details now emphasize the first speaker with a stronger accent treatment, while other speakers are intentionally quieter for clearer visual hierarchy.",
    userHighlights: [
      "The first speaker now stands out as the primary highlighted voice.",
      "Subsequent speakers are still distinct but visually calmer.",
      "Reading flow and vocabulary interactions stay unchanged.",
    ],
    technicalNotes: [
      "Updated speaker-tone mapping in `src/components/GeneratedTextDetailsTextPane.tsx` so index `0` uses a stronger primary sky palette.",
      "Moved non-primary speakers to neutral secondary palettes (`slate`, `zinc`, `stone`) to reduce competing emphasis.",
      "No backend/API behavior changes.",
    ],
  },
  {
    id: "dialogue-layout-no-longer-flashes-from-regular-into-speaker-mode",
    timestamp: "2026-04-22T20:26:31+02:00",
    title: "Dialogue details no longer flash from regular to speaker layout",
    summary:
      "Text details now avoid the initial regular-text flash before dialogue styling appears, so dialogue pages open in a visually stable reading layout.",
    userHighlights: [
      "Dialogue texts now open directly in speaker-grouped mode in typical cases.",
      "The brief regular-layout flash before speaker styling is removed.",
      "Non-dialogue texts keep the same existing rendering behavior.",
    ],
    technicalNotes: [
      "Updated dialogue layout selection in `src/components/GeneratedTextDetailsTextPane.tsx` to prefer dialogue rendering when parsed dialogue segments are already detectable, even if `text_type` has not resolved yet.",
      'Kept explicit `text_type === "DIALOGUE"` behavior as the primary condition once API data is available.',
      "No backend/API changes were required.",
    ],
  },
  {
    id: "dialogue-text-details-readability-tuned-with-stronger-speaker-contrast",
    timestamp: "2026-04-22T20:24:43+02:00",
    title: "Dialogue text details readability is now tighter and clearer",
    summary:
      "Dialogue blocks on text details now use a more compact text size and stronger speaker contrast, so conversations are easier to read without changing the non-dialogue layout.",
    userHighlights: [
      "Dialogue lines are now less oversized and easier to scan in sequence.",
      "Speaker separation is clearer through stronger color contrast and border emphasis.",
      "Regular non-dialogue text presentation remains unchanged.",
    ],
    technicalNotes: [
      "Adjusted dialogue speaker tone classes in `src/components/GeneratedTextDetailsTextPane.tsx` to increase border and label contrast while keeping the existing editorial style.",
      "Reduced dialogue body typography from large display sizing to a tighter reading scale (`text-base` / `md:text-lg`) with calmer line-height.",
      "Tightened dialogue block spacing and increased left accent stroke weight for faster speaker switching during reading.",
    ],
  },
  {
    id: "reeprise-feed-card-hover-borders-softened",
    timestamp: "2026-04-22T20:14:52+02:00",
    title: "Feed card hover borders are now softer",
    summary:
      "The hover stroke on Reeprise feed cards was toned down to reduce aggressive contrast and keep the interface calmer.",
    userHighlights: [
      "Hover state now feels subtler and less harsh.",
      "Card hover affordance remains visible.",
      "No interaction behavior changed.",
    ],
    technicalNotes: [
      "Adjusted hover/border token classes in `src/components/ReepriseTextCard.tsx` for both regular and podcast card variants.",
      "Reduced unread-state border intensity from blue-200/300 to blue-100/200.",
      "Reduced neutral-state hover contrast from slate-100/200 + lg 300/400 to softer slate-200/300 levels.",
    ],
  },
  {
    id: "dialogue-text-details-now-use-speaker-grouped-reading-layout",
    timestamp: "2026-04-22T20:11:59+02:00",
    title: "Dialogue text details now group lines by speaker",
    summary:
      "Generated text details now render dialogue texts in speaker-grouped blocks, making multi-person conversations easier to scan while keeping the existing reading and vocabulary interactions.",
    userHighlights: [
      "Dialogue texts now show each speaker in distinct visual blocks instead of one long paragraph.",
      "A compact speaker row appears above the text so multi-person conversations are easier to follow.",
      "Non-dialogue text types keep the existing text-details layout unchanged.",
    ],
    technicalNotes: [
      "Added dialogue-segment parsing in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` and enabled offset-aware span generation so vocabulary interactions keep correct global indexes in per-speaker blocks.",
      'Updated `GeneratedTextDetailsTextPane` to switch to a speaker-grouped renderer only when `text_type === "DIALOGUE"`, while retaining the existing body rendering for all other types.',
      "Extended Vitest coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for dialogue parsing and offset-index behavior.",
    ],
  },
  {
    id: "daily-strip-desktop-cards-now-use-equal-height",
    timestamp: "2026-04-22T19:57:28+02:00",
    title: "Daily strip cards now keep equal height on desktop",
    summary:
      "Daily cards in the desktop strip now align to equal heights, so card rows no longer look uneven when one card has less content.",
    userHighlights: [
      "Daily strip cards on desktop now align with a consistent height in each row.",
      "The primary daily card and older/history cards now follow the same vertical rhythm.",
      "Card click/open behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated daily section grid in `src/routes/app/reeprise/index.tsx` from `items-start` to `items-stretch` for consistent row height behavior.",
      "Removed desktop strip `self-start` overrides and applied `h-full` to primary and history daily cards so they fill their grid tracks.",
      "Extended desktop layout Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert equal heights across the first daily strip cards.",
    ],
  },
  {
    id: "mobile-topbar-toggle-now-has-left-padding",
    timestamp: "2026-04-22T19:55:47+02:00",
    title: "Mobile topbar toggle now has left spacing",
    summary:
      "The top-left side-nav toggle now has a small left inset on mobile so it no longer sits flush against the screen edge.",
    userHighlights: [
      "The side-nav toggle has clearer left breathing room on mobile.",
      "Desktop alignment remains unchanged.",
      "Toggle behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the topbar toggle slot in `src/routes/app/route.tsx` with mobile-only left padding (`pl-2`) and reset on desktop (`md:pl-0`).",
      "Kept the fixed desktop toggle axis and existing sidenav expand/collapse logic unchanged.",
      "No API, state, or navigation flow changes.",
    ],
  },
  {
    id: "desktop-sidenav-top-border-removed-from-icon-rail",
    timestamp: "2026-04-22T19:54:24+02:00",
    title: "Desktop side-nav rail no longer shows a top border line",
    summary:
      "Removed the extra top horizontal border from the desktop side-nav rail so the icon column starts cleaner under the top bar.",
    userHighlights: [
      "The horizontal line above the first side-nav icon is gone.",
      "The left vertical divider and bottom rail border remain in place.",
      "Navigation behavior is unchanged.",
    ],
    technicalNotes: [
      "Changed side-nav panel desktop border classes in `src/routes/app/route.tsx` from `md:border-y md:border-l` to `md:border-b md:border-l`.",
      "Kept the existing desktop rail width logic and expand/collapse behavior untouched.",
      "Extended Playwright desktop sidenav regression in `e2e/reeprise-generation.spec.ts` to assert `borderTopWidth` is `0px`.",
    ],
  },
  {
    id: "daily-strip-show-more-now-reveals-history-when-done-card-is-active",
    timestamp: "2026-04-22T19:53:48+02:00",
    title: "Daily strip now reveals older cards even after today is done",
    summary:
      "The “Alle älteren Karten anzeigen” toggle in the daily strip now correctly reveals older cards when today’s daily card is already marked as done.",
    userHighlights: [
      "Clicking “Alle älteren Karten anzeigen” now shows the expected older cards in the daily strip.",
      "The fix applies even when the primary daily card is in the completed (“done”) state.",
      "Daily card navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Fixed `DailyPracticeCallout` in `src/routes/app/reeprise/index.tsx` to render history cards in both branches (done and create states) instead of returning early without history in the done branch.",
      'Added `data-testid="reeprise-daily-history-card"` to daily history cards for stable regression assertions.',
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` to verify show-more expansion with an active done card state.",
    ],
  },
  {
    id: "desktop-sidenav-divider-now-renders-as-single-full-height-line",
    timestamp: "2026-04-22T19:51:41+02:00",
    title: "Desktop side-nav divider now renders as one full-height line",
    summary:
      "The desktop side-nav divider now renders as one continuous line from top to bottom, without a short extra segment beside the toggle icon.",
    userHighlights: [
      "The side-nav split line now appears continuous from the very top to the bottom.",
      "The extra short divider segment next to the top-left toggle no longer appears as a separate line.",
      "Collapsed and expanded desktop side-nav states keep the same divider behavior.",
    ],
    technicalNotes: [
      "Moved `topbar-sidenav-divider` in `src/routes/app/route.tsx` from a header-only absolute element to a full-height fixed desktop overlay.",
      "Updated desktop side-nav panel borders to avoid a duplicate right-edge divider while preserving panel framing.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert the divider spans the full viewport height in compact desktop mode.",
    ],
  },
  {
    id: "topbar-sidenav-toggle-now-keeps-fixed-x-position-on-open",
    timestamp: "2026-04-22T19:46:11+02:00",
    title: "Side-nav toggle now keeps a fixed position when opening",
    summary:
      "The topbar side-nav toggle no longer shifts horizontally when the side navigation opens on desktop.",
    userHighlights: [
      "The toggle stays in the same place before and after opening the side navigation.",
      "The icon no longer jumps during expand/collapse transitions.",
      "Side navigation behavior and content remain unchanged.",
    ],
    technicalNotes: [
      "Updated topbar layout in `src/routes/app/route.tsx` to keep the toggle in a fixed desktop slot (`md:w-[72px]`) instead of binding it to dynamic nav width.",
      "Removed the temporary root CSS variable wiring used for the moving toggle slot.",
      "Extended desktop Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert stable toggle X-position across open/close.",
    ],
  },
  {
    id: "topbar-toggle-now-aligns-to-sidenav-icon-axis",
    timestamp: "2026-04-22T19:41:59+02:00",
    title: "Topbar side-nav toggle now aligns with sidenav icon axis",
    summary:
      "The topbar side-nav toggle is now centered on the same vertical axis as the compact sidenav icon column, so it no longer appears offset.",
    userHighlights: [
      "The top-left toggle now lines up with the icon rail below.",
      "Collapsed and expanded sidenav states keep consistent alignment.",
      "Sidenav behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added shared `--app-desktop-nav-width` on the app shell in `src/routes/app/route.tsx` and reused it for both topbar and sidenav layout.",
      "Wrapped the topbar toggle in a dedicated desktop nav-width slot (`md:w-[var(--app-desktop-nav-width)]`) centered with `justify-center`.",
      "Kept existing toggle handler, accessibility label, and side navigation interaction logic unchanged.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-directional-chevrons-and-aligned-spacing",
    timestamp: "2026-04-22T19:36:38+02:00",
    title: "Side-nav toggle now uses directional chevrons and aligned sizing",
    summary:
      "The side-nav toggle icon now uses standard directional chevrons for expand/collapse and is aligned with neighboring topbar icons using consistent button sizing and spacing.",
    userHighlights: [
      "The toggle icon now better communicates open/close sidebar direction.",
      "Topbar icon alignment is cleaner with matching button dimensions.",
      "Sidebar behavior remains unchanged.",
    ],
    technicalNotes: [
      "Replaced `ViewColumnsIcon` usage in `src/routes/app/route.tsx` with `ChevronDoubleRightIcon` (collapsed) and `ChevronDoubleLeftIcon` (expanded).",
      "Normalized header icon-button sizing to `h-9 w-9` and removed offset margins that made the toggle look misaligned.",
      "Kept existing sidenav state handling, accessibility label, and desktop/mobile behavior unchanged.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-viewcolumns-panel-icon",
    timestamp: "2026-04-22T19:31:22+02:00",
    title: "Side-nav toggle now uses a panel-style icon",
    summary:
      "The top-left side-nav toggle now uses a standard panel-outline icon that visually matches sidebar controls better than the previous chevron variant.",
    userHighlights: [
      "The toggle now looks closer to common sidebar panel icons.",
      "The icon style is now from a standard free icon set instead of a custom look.",
      "Toggle behavior stays exactly the same.",
    ],
    technicalNotes: [
      "Replaced toggle icon usage in `src/routes/app/route.tsx` with Heroicons `ViewColumnsIcon`.",
      "Removed prior double-chevron toggle icon imports for this control.",
      "Kept existing click handler, accessibility label, and sidenav state logic unchanged.",
    ],
  },
  {
    id: "reeprise-web-declutter-pass-rebalanced-spacing-and-control-emphasis",
    timestamp: "2026-04-22T19:30:54+02:00",
    title: "Reeprise web declutter pass rebalanced spacing and emphasis",
    summary:
      "The Reeprise web view now uses calmer spacing rhythm and lower-contrast secondary controls to reduce visual clutter while preserving existing flows.",
    userHighlights: [
      "Main content blocks now have clearer breathing room and separation.",
      "Daily secondary toggle and feed load-more control are visually quieter.",
      "Right utility column now stays visible while scrolling on desktop for cleaner scanning.",
    ],
    technicalNotes: [
      "In `src/routes/app/reeprise/index.tsx`, increased major layout spacing (`gap`/`space-y`) and slightly reduced feed heading scale.",
      "Changed daily show-more toggle and feed load-more button styles to neutral, lower-contrast treatments.",
      "Made desktop utility sidebar sticky (`xl:sticky xl:top-20`) and tightened internal sidebar spacing.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-standard-free-heroicon",
    timestamp: "2026-04-22T19:28:34+02:00",
    title: "Side-nav toggle now uses a standard free icon",
    summary:
      "The app side-nav toggle now uses a standard Heroicons chevron icon set instead of a custom-drawn glyph, so the control looks cleaner and more native.",
    userHighlights: [
      "The toggle icon now matches a common app-style icon look.",
      "Collapsed state shows a double-right chevron; expanded state shows a double-left chevron.",
      "Sidebar expand/collapse behavior is unchanged.",
    ],
    technicalNotes: [
      "Replaced the custom toggle glyph markup in `src/routes/app/route.tsx` with Heroicons `ChevronDoubleRightIcon` and `ChevronDoubleLeftIcon`.",
      "Removed the now-unused custom line/dot icon structure from the toggle button.",
      "Kept existing accessibility label and toggle handler behavior unchanged.",
    ],
  },
  {
    id: "reeprise-page-top-padding-increased-under-top-bar",
    timestamp: "2026-04-22T19:27:48+02:00",
    title: "Reeprise cards now sit lower under the top bar",
    summary:
      "Increased top padding on the Reeprise page so the first card row is no longer too close to the top bar.",
    userHighlights: [
      "The top cards now have more breathing room below the header bar.",
      "Visual separation between the top bar and first content block is improved.",
      "No changes to card logic or interactions.",
    ],
    technicalNotes: [
      "Updated Reeprise page wrapper classes in `src/routes/app/reeprise/index.tsx` to increase top padding (`pt-4` and `sm:pt-5`).",
      "Kept existing layout grid, sidebar split, and fixed composer behavior unchanged.",
      "No API/data/state logic changes.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-sidebar-chevron-icon",
    timestamp: "2026-04-22T19:26:33+02:00",
    title: "Side-nav toggle now uses a sidebar chevron icon",
    summary:
      "The top-left app toggle now uses a sidebar-style expand/collapse icon (panel divider with chevron) instead of a burger icon, matching common desktop sidenav behavior.",
    userHighlights: [
      "The toggle now visually communicates sidebar expand/collapse instead of menu open.",
      "Collapsed state shows a right chevron; expanded state shows a left chevron.",
      "Navigation behavior remains the same.",
    ],
    technicalNotes: [
      "Updated the toggle glyph in `src/routes/app/route.tsx` from a custom burger/X animation to a panel-divider + directional chevron icon.",
      "Added `ChevronLeftIcon` usage for expanded-state direction while reusing `ChevronRightIcon` for collapsed-state direction.",
      "Kept existing toggle handler, accessibility label, and desktop/mobile navigation logic unchanged.",
    ],
  },
  {
    id: "desktop-sidenav-divider-now-overlays-topbar-crossing",
    timestamp: "2026-04-22T19:23:52+02:00",
    title: "Desktop side-nav divider now overlays the topbar crossing",
    summary:
      "The vertical desktop divider for the side navigation now draws above the topbar line at their crossing point, so the split looks continuous and cleaner.",
    userHighlights: [
      "The side-nav divider no longer looks interrupted where it meets the top bar.",
      "The vertical divider now visually sits above the topbar baseline at the crossing.",
      "Divider positioning still follows compact and expanded side-nav states.",
    ],
    technicalNotes: [
      "Added a desktop-only `topbar-sidenav-divider` overlay element in `src/routes/app/route.tsx` with absolute positioning and animated left offset for collapsed (`72px`) and expanded (`440px`) nav widths.",
      "Kept existing side navigation structure and panel border behavior unchanged.",
      "Extended desktop Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert divider position in compact state and after expansion toggle.",
    ],
  },
  {
    id: "reeprise-daily-header-removed-and-history-toggle-moved-below-cards",
    timestamp: "2026-04-22T19:22:10+02:00",
    title: "Daily header removed and older-cards toggle moved below strip",
    summary:
      "The “Deine täglichen Texte” heading above the daily strip was removed, and the “Alle älteren Karten anzeigen” control now appears below the daily cards.",
    userHighlights: [
      "Daily cards now start immediately without a section title above them.",
      "The older-cards toggle sits below the card row.",
      "Daily card order and behavior remain unchanged.",
    ],
    technicalNotes: [
      "Removed daily section header block from `src/routes/app/reeprise/index.tsx`.",
      "Moved daily history toggle render block to below the daily card grid in the same file.",
      "Updated Playwright layout test in `e2e/reeprise-generation.spec.ts` by removing assertions for the removed daily heading.",
    ],
  },
  {
    id: "reeprise-utility-cards-now-mount-once-per-viewport",
    timestamp: "2026-04-21T22:23:49+02:00",
    title: "Reeprise now mounts one trainer utility stack per viewport",
    summary:
      "The Reeprise screen now mounts a single Vocabulary Trainer/Quick Translate utility stack at a time, preventing duplicate hidden instances that could trigger heavy rerenders.",
    userHighlights: [
      "The trainer utility area on Reeprise no longer duplicates in the DOM across mobile/desktop layouts.",
      "Page responsiveness is improved on Reeprise, especially during state-heavy trainer updates.",
      "This reduces the chance of browser slowdown warnings caused by repeated render/update churn.",
    ],
    technicalNotes: [
      "Added viewport-gated utility rendering in `src/routes/app/reeprise/index.tsx` so mobile and desktop utility stacks are mounted mutually exclusively instead of via CSS-only hiding.",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` to verify exactly one `vocabulary-preview-card` is mounted on Reeprise and no React render-loop console errors are emitted after load.",
      'Validated the new regression locally with `npx playwright test e2e/reeprise-generation.spec.ts --grep "reeprise mounts a single vocabulary preview card and avoids render-loop errors"`.',
    ],
  },
  {
    id: "trainer-now-localizes-pending-translation-placeholder",
    timestamp: "2026-04-21T22:15:11+02:00",
    title: "Trainer now shows localized pending-translation text",
    summary:
      "Words added from text details no longer show the raw backend placeholder `currently beeing generated` in trainer cards.",
    userHighlights: [
      "Pending translations in trainer cards now show a proper localized UI message.",
      "The same fix applies to similar-word suggestions inside the trainer card.",
      "You no longer see backend placeholder wording in normal training flow.",
    ],
    technicalNotes: [
      "Added pending-translation normalization in `src/components/VocabularyPreviewCard.tsx` for both main answer/question text and similar-word translation rows.",
      "Mapped backend placeholders (`currently beeing generated` and `currently being generated`) to existing translation key `generated_text_word_preview_translation_pending`.",
      "Validated frontend compilation with `npm run build`.",
    ],
  },
  {
    id: "daily-weather-texts-now-include-reinforcement-vocabulary-links",
    timestamp: "2026-04-21T22:10:22+02:00",
    title: "Daily weather texts now include reinforcement vocabulary links",
    summary:
      "Daily generated texts now carry reinforcement vocabulary into saved text usages, so those words appear in the vocabulary list and can be highlighted in the text view.",
    userHighlights: [
      "Daily weather-card texts now surface reinforcement words in the right-side vocabulary list.",
      "Those reinforcement words are now highlightable from persisted text positions.",
      "Daily generation still keeps a natural reading flow while weaving the words in.",
    ],
    technicalNotes: [
      "Extended daily generation prompts in `barkguide/reeprise/tasks.py` to include reinforcement vocabulary entries for `DAILY_NUDGE` texts.",
      "Enabled missing-word retry handling for daily generation and persisted linked `VocabularyUsage` spans after completion.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert daily texts include reinforcement words in both `struggle_words` and `/api/reeprise/vocabulary/?generated_text_id=...` results.",
    ],
  },
  {
    id: "highlighted-generation-words-now-open-quick-preview-with-hover-or-click",
    timestamp: "2026-04-21T22:09:24+02:00",
    title: "Generation-highlighted words now open quick preview on hover/click",
    summary:
      "Highlighted words that came from generation targets now open the same quick translation preview as words that were added directly from this text.",
    userHighlights: [
      "Hover/click quick preview now works for both kinds of highlighted words in text details.",
      "Generation-target highlights no longer look interactive without opening a translation preview.",
      "If the word already has a saved translation, the preview shows it immediately.",
    ],
    technicalNotes: [
      "Added selected-highlight fallback mapping in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so preview vocabulary can be resolved by highlighted word match when no usage-position mapping exists.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to merge those fallback mappings into `previewableStarts` while keeping usage-based mappings authoritative.",
      "Added Vitest regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for selected-highlight mapping and non-override behavior.",
    ],
  },
  {
    id: "trainer-delete-no-longer-sticks-on-loading-copy",
    timestamp: "2026-04-21T22:07:38+02:00",
    title: "Trainer delete no longer gets stuck on loading copy",
    summary:
      "Deleting a vocabulary word from the trainer card no longer leaves the card stuck on the “Vokabeln werden geladen...” state.",
    userHighlights: [
      "After deleting a word in the trainer card, the trainer now continues instead of freezing on the loading message.",
      "Queue updates now keep existing trainer state visible during deletion refresh.",
      "The delete action still shows the same success and error feedback messages.",
    ],
    technicalNotes: [
      "Removed hard reset of trainer query data in `src/hooks/useVocabularyDelete.ts` and kept invalidate-based refresh behavior.",
      "Removed the extra awaited trainer refetch inside delete handling in `src/components/VocabularyPreviewCard.tsx` to avoid blocking the state transition.",
      "Narrowed initial-loading placeholder conditions in `src/components/VocabularyPreviewCard.tsx` so delete-driven transitions no longer render as first-load spinner state.",
    ],
  },
  {
    id: "text-details-trainer-now-reuses-main-trainer-card-with-text-scoped-queue",
    timestamp: "2026-04-21T22:02:23+02:00",
    title: "Text Details trainer now uses the same main trainer card",
    summary:
      "Starting trainer from Text Details now opens the same trainer card experience used on the main Reeprise page, while limiting the queue to words from the current text.",
    userHighlights: [
      "Text Details no longer sends you to a separate trainer-style page.",
      "The exact same trainer card UI and actions from the main page are now used in reading mode.",
      "Word selection stays scoped to the current generated text, so unrelated words do not appear.",
    ],
    technicalNotes: [
      "Reused `VocabularyPreviewCard` directly in `src/routes/app/reeprise/$generatedTextId.tsx` and replaced route navigation with an in-page trainer toggle.",
      "Extended `src/components/VocabularyPreviewCard.tsx` to accept optional `generatedTextId` and pass it to the existing trainer query hook.",
      "Added scoped queue regression in `e2e/reeprise-generation.spec.ts` to verify `top_fifteen_priority` requests include `generated_text_id` when launched from Text Details.",
    ],
  },
  {
    id: "on-demand-grammar-lesson-for-generated-text-details",
    timestamp: "2026-04-21T22:00:06+02:00",
    title: "Generated text details can now create an on-demand grammar lesson",
    summary:
      "Each generated text now offers a dedicated grammar-lesson action that analyzes the current text only when you request it.",
    userHighlights: [
      "A new “Generate grammar lesson” action is available in generated text details.",
      "Grammar analysis is generated only after you click the action, not automatically in the background.",
      "The lesson appears directly on the text details screen with a short summary and concrete grammar points from the text.",
    ],
    technicalNotes: [
      "Added `grammar_lesson` action to `GeneratedTextViewSet` in `barkguide/reeprise/views.py` (`POST /api/reeprise/generated_texts/:id/grammar_lesson/`) with model-based analysis prompt and structured JSON response parsing.",
      "Extended generated-text details UI in `src/routes/app/reeprise/$generatedTextId.tsx` with on-demand request state, error handling, and lesson rendering.",
      "Added translation keys in `src/assets/translations.json`, backend API coverage in `barkguide/reeprise/tests.py`, and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify true on-demand behavior.",
    ],
  },
  {
    id: "saved-highlight-word-preview-now-shows-existing-translation-on-fast-repeat-click",
    timestamp: "2026-04-21T21:49:53+02:00",
    title:
      "Saved highlighted words now show existing translations on fast repeat click",
    summary:
      "When you quickly tap or click a highlighted word that was already added before, the preview now shows the saved translation immediately instead of a temporary “translation is being prepared” state.",
    userHighlights: [
      "Fast repeat taps/clicks on already-saved highlighted words now open with the stored translation right away.",
      "Saved-word preview behavior is now consistent across normal and quick interactions.",
      "No extra action is required to reveal translations that are already available on the vocabulary card.",
    ],
    technicalNotes: [
      "Changed preview-vocabulary merge precedence in `src/components/GeneratedTextDetailsTextPane.tsx` so persisted vocabulary data from the API overrides stale local preview cache when both exist for the same highlight position.",
      "Added `buildPreviewVocabularyByStartMap` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to centralize and enforce the merge behavior.",
      "Added a Vitest regression in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` verifying that a persisted translated entry wins over a local pending placeholder.",
    ],
  },
  {
    id: "text-details-now-starts-a-trainer-scoped-to-the-current-text",
    timestamp: "2026-04-21T21:48:46+02:00",
    title: "Text Details now starts a trainer scoped to the current text",
    summary:
      "You can now start vocabulary training directly from a text’s details page, and the trainer session is limited to vocabulary from that specific generated text.",
    userHighlights: [
      "Text Details now includes a direct “Start trainer for this text” action.",
      "The trainer session focuses only on words connected to the current text, avoiding unrelated vocabulary.",
      "You can jump back to the same text details page from inside training mode.",
    ],
    technicalNotes: [
      "Extended trainer query parameters with `generated_text_id` in `barkguide/reeprise/serializer.py` and applied scoped filtering in `barkguide/reeprise/views.py` for `/vocabulary/top_fifteen_priority/`.",
      "Added text-scoped trainer navigation from `src/routes/app/reeprise/$generatedTextId.tsx` and support for scoped mode/back link in `src/routes/app/vocabulary_training_mode.tsx`.",
      "Updated query plumbing in `src/hooks/useVocabularyTrainerVocabularies.ts` and added regression coverage in `barkguide/reeprise/tests.py` plus `e2e/reeprise-generation.spec.ts` for the new route-transition flow.",
    ],
  },
  {
    id: "struggle-word-texts-now-link-target-words-to-text-vocabulary-list",
    timestamp: "2026-04-21T21:47:59+02:00",
    title:
      "Struggle-word texts now link target words into the text vocabulary list",
    summary:
      "Generated struggle-word texts now persist usage links for matched target words, so those words appear in the text’s vocabulary list and can be highlighted from saved usage positions.",
    userHighlights: [
      "Words selected for tricky-word generation now show up in the vocabulary list for that generated text.",
      "Those target words can now be highlighted from persisted text positions, not only from ad-hoc matching.",
      "The fix applies automatically for newly generated struggle-word texts.",
    ],
    technicalNotes: [
      "Added `_find_word_surface_matches` and `_link_struggle_vocabulary_usages` in `barkguide/reeprise/tasks.py`.",
      "Hooked struggle-usage linking into `complete_generated_text` after final content is saved.",
      "Extended regression coverage in `barkguide/reeprise/tests.py` to assert created `VocabularyUsage` rows and inclusion in `/api/reeprise/vocabulary/?generated_text_id=...` results.",
    ],
  },
  {
    id: "vocabulary-trainer-card-now-supports-direct-word-deletion",
    timestamp: "2026-04-21T21:46:46+02:00",
    title: "Vocabulary trainer card now supports direct word deletion",
    summary:
      "You can now delete a vocabulary word directly from the trainer card while practicing, without leaving the current training flow.",
    userHighlights: [
      "A delete action is now available directly inside the expanded trainer card.",
      "Deleting a word removes it from the current trainer flow immediately.",
      "You no longer need to switch to the vocabulary progress page just to remove a word.",
    ],
    technicalNotes: [
      "Added an inline delete action in `src/components/VocabularyPreviewCard.tsx` using the existing `useVocabularyDelete` mutation and existing delete success/error translations.",
      "Updated trainer card state flow so deletion advances the session and refreshes queue data in-place.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to seed a vocabulary item and verify direct deletion from the trainer card.",
    ],
  },
  {
    id: "daily-weather-text-topic-history-now-avoids-single-niche-overfit",
    timestamp: "2026-04-21T21:36:54+02:00",
    title:
      "Daily weather text now avoids overfitting to one repeated niche topic",
    summary:
      "Daily text generation now diversifies topic-history signals so one repeatedly entered niche topic no longer dominates weather-card texts.",
    userHighlights: [
      "Daily weather texts now draw from a broader mix of your past interests.",
      "Repeated niche entries are treated as one preference signal instead of being echoed too often.",
      "You should see more practical and varied daily reading topics over time.",
    ],
    technicalNotes: [
      "Added diversified interest-topic selection in `barkguide/reeprise/tasks.py` before building daily-guidance prompts.",
      "Deduplicated repeated raw-topic entries and capped dominance from heavily repeated topic/category history.",
      "Added regression coverage in `barkguide/reeprise/tests.py` to ensure repeated niche topics appear at most once in daily-guidance context.",
    ],
  },
  {
    id: "reeprise-non-feed-card-surfaces-now-match-feed-background-tone",
    timestamp: "2026-04-17T21:59:47+02:00",
    title: "Reeprise non-feed card surfaces now match feed background tone",
    summary:
      "Utility and generate cards now use the same neutral background tone as the feed area to create a more consistent surface system.",
    userHighlights: [
      "Trainer, Quick Translate, and Generate card backgrounds now match the feed backdrop tone.",
      "Cards look less contrast-heavy and more visually unified.",
      "Card content and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated card root backgrounds to `bg-neutral-50` in `src/components/VocabularyPreviewCard.tsx`, `src/components/QuickTranslateCard.tsx`, and `src/components/GenerateTextCard.tsx`.",
      "Kept existing borders, sizing, and layout behavior unchanged.",
      "No API/state logic changes were introduced.",
    ],
  },
  {
    id: "app-top-bar-now-uses-sidebar-matching-stroke-border",
    timestamp: "2026-04-17T21:58:18+02:00",
    title: "Top bar now uses a sidebar-matching border stroke",
    summary:
      "Added a thin slate border to the top bar so it uses the same stroke treatment as the sidebar panel.",
    userHighlights: [
      "Top bar now has a clear outline stroke.",
      "Header and sidebar now share a consistent border language.",
      "No navigation or interaction behavior changed.",
    ],
    technicalNotes: [
      "Updated top-bar container classes in `src/routes/app/route.tsx` to include `border border-slate-200`.",
      "Kept the previously removed top-bar shadow unchanged (still no shadow).",
      "No layout, routing, or state logic changed.",
    ],
  },
  {
    id: "reeprise-daily-section-title-now-has-more-top-spacing",
    timestamp: "2026-04-17T21:57:24+02:00",
    title: "Reeprise daily section title now has more top spacing",
    summary:
      "Added additional vertical space above the “Deine täglichen Texte” title to improve separation from the content above.",
    userHighlights: [
      "The daily section title now has more breathing room.",
      "Section hierarchy is easier to scan.",
      "No interaction or content behavior changed.",
    ],
    technicalNotes: [
      "Added top padding to the daily section container in `src/routes/app/reeprise/index.tsx`.",
      "No changes were made to daily card logic, feed rendering, or navigation.",
      "Desktop/mobile structure remains unchanged.",
    ],
  },
  {
    id: "app-top-bar-shadow-removed-for-flatter-header",
    timestamp: "2026-04-17T21:56:39+02:00",
    title: "Top bar shadow was removed",
    summary:
      "The app header/top bar no longer uses a drop shadow, resulting in a flatter and cleaner surface transition.",
    userHighlights: [
      "Top bar now appears without elevation shadow.",
      "Header spacing and controls remain unchanged.",
      "Navigation behavior is unchanged.",
    ],
    technicalNotes: [
      "Removed `shadow-lg` from the top bar container class in `src/routes/app/route.tsx`.",
      "No structural, interaction, or routing changes were made.",
      "Other shadow styles in unrelated components were not touched.",
    ],
  },
  {
    id: "reeprise-web-side-gutters-reduced-for-wider-content-fit",
    timestamp: "2026-04-17T21:54:47+02:00",
    title: "Reeprise web side spacing was reduced",
    summary:
      "Desktop Reeprise now uses smaller horizontal side gutters so the content area sits closer to the edges and feels less constrained.",
    userHighlights: [
      "Main Reeprise content now has less left/right outer spacing on web.",
      "The fixed generate composer keeps alignment with the reduced page gutters.",
      "No changes to content order, interactions, or card behavior.",
    ],
    technicalNotes: [
      "Reduced Reeprise route horizontal paddings in `src/routes/app/reeprise/index.tsx` (`lg:px-8` → `lg:px-4`, `xl:px-12` → `xl:px-6`).",
      "Adjusted desktop fixed composer wrapper paddings in the same file to match tighter side gutters.",
      "Kept existing 2-column desktop structure and fixed composer column alignment unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-composer-now-aligns-to-first-content-column",
    timestamp: "2026-04-17T21:53:46+02:00",
    title: "Reeprise desktop composer now aligns to the first content column",
    summary:
      "The fixed desktop generate-text composer is now aligned to column 1 of the Reeprise two-column layout instead of appearing centered across the full page width.",
    userHighlights: [
      "The composer now sits exactly in the main content column.",
      "Sidebar spacing is preserved while composing.",
      "Composer behavior and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated fixed composer wrapper in `src/routes/app/reeprise/index.tsx` to use `max-w-[1100px]` matching page width.",
      "Changed composer inner layout to the same desktop grid split (`2fr / 1fr`) and rendered `GenerateTextCard` in the first column.",
      "Kept existing fixed-bottom placement and pointer-events behavior unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-sidebar-width-adjusted-to-one-third",
    timestamp: "2026-04-17T21:52:31+02:00",
    title: "Reeprise desktop sidebar now uses one-third width",
    summary:
      "The desktop Reeprise layout now uses a 2:1 split so the right utility column takes about one-third of the page width instead of a narrower rail.",
    userHighlights: [
      "The trainer and Quick Translate column is now wider and easier to use.",
      "Trainer remains first, with Quick Translate directly below in the same right column.",
      "Main feed/daily content remains in the left column.",
    ],
    technicalNotes: [
      "Changed desktop Reeprise grid split in `src/routes/app/reeprise/index.tsx` from `4fr/1fr` to `2fr/1fr`.",
      "Kept utility-card stacking order in the right column unchanged (VocabularyPreviewCard first, QuickTranslateCard second).",
      "Updated Playwright layout assertion in `e2e/reeprise-generation.spec.ts` to validate right-column share around one-third and vertical card stacking.",
    ],
  },
  {
    id: "reeprise-daily-and-feed-headlines-now-use-smaller-scale",
    timestamp: "2026-04-17T21:48:48+02:00",
    title: "Reeprise daily and feed headlines now use a smaller size",
    summary:
      "The “Deine täglichen Texte” and “Feed” section headlines were reduced significantly in size to keep the page hierarchy calmer.",
    userHighlights: [
      "Section headlines now appear much smaller and less dominant.",
      "Typography remains consistent with the existing serif heading style.",
      "Layout, spacing, and interaction behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated both Reeprise section `h2` classes in `src/routes/app/reeprise/index.tsx` from larger custom rem sizes to smaller values (`1.3rem` / `1.4rem` on `sm+`).",
      "Kept `font-serif`, `font-semibold`, and `leading-tight` unchanged to preserve style continuity.",
      "No data, card logic, or navigation code changed.",
    ],
  },
  {
    id: "generate-text-card-topic-input-now-auto-grows-with-content",
    timestamp: "2026-04-17T21:46:49+02:00",
    title: "Generate text card input now grows with longer topic text",
    summary:
      "The topic input in the generate-text card now expands vertically as text gets longer, so content is readable without being constrained to a fixed one-line field.",
    userHighlights: [
      "Long topic text now wraps and increases input height automatically.",
      "The generate card itself grows with the input instead of clipping.",
      "Typing, idea insertion, and submit behavior remain unchanged.",
    ],
    technicalNotes: [
      "Replaced the single-line topic input with an auto-resizing textarea in `src/components/GenerateTextCard.tsx`.",
      "Added a resize effect that recalculates textarea height from `scrollHeight` on topic-value updates.",
      "Extended desktop Reeprise Playwright regression in `e2e/reeprise-generation.spec.ts` to assert composer form height increases after entering a long topic.",
    ],
  },
  {
    id: "reeprise-utility-cards-now-use-matched-desktop-heights",
    timestamp: "2026-04-17T21:44:51+02:00",
    title: "Reeprise utility cards now use matched desktop heights",
    summary:
      "The top utility row cards now keep the same height on desktop so the vocabulary and quick-translate surfaces align cleanly.",
    userHighlights: [
      "The two top cards now end at the same baseline on desktop.",
      "Spacing between utility row and following sections is more consistent.",
      "No behavior changes in vocabulary review or quick translate.",
    ],
    technicalNotes: [
      "Set utility-row grid to explicit stretch in `src/routes/app/reeprise/index.tsx`.",
      "Updated root containers in `src/components/VocabularyPreviewCard.tsx` and `src/components/QuickTranslateCard.tsx` to `h-full` for consistent equal-height behavior.",
      "Added Playwright assertion in `e2e/reeprise-generation.spec.ts` to enforce height parity of the first two utility cards on desktop.",
    ],
  },
  {
    id: "reeprise-card-mini-preview-now-crops-at-bottom-edge",
    timestamp: "2026-04-17T21:42:50+02:00",
    title: "Reeprise mini preview now crops at the card bottom edge",
    summary:
      "The small tilted streamed-text preview now sits lower so it is intentionally clipped by the feed card bottom edge, matching the intended visual reference.",
    userHighlights: [
      "The mini preview now appears partially cut at the bottom of the card.",
      "The visual treatment now feels more intentional and less like a full floating card.",
      "Card interaction and content remain unchanged.",
    ],
    technicalNotes: [
      "Updated both mini-preview link overlays in `src/components/ReepriseTextCard.tsx` from top-based to negative bottom offsets.",
      "Changed transform origin for mini preview overlays from `top right` to `bottom right` to keep hover scaling anchored to the cropped edge.",
      "Kept existing dimensions, borders, and hover behavior while adjusting placement only.",
    ],
  },
  {
    id: "reeprise-section-headlines-now-use-serif-type-to-match-reading-surfaces",
    timestamp: "2026-04-17T21:41:46+02:00",
    title: "Reeprise section headlines now use serif typography",
    summary:
      "The “Deine täglichen Texte” and “Feed” section headlines now use a serif style and tuned sizing so they fit the overall Reeprise reading typography better.",
    userHighlights: [
      "Both major section headlines now feel more consistent with the card text style.",
      "Headline scale remains prominent but less disconnected from nearby typography.",
      "Layout and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated both section headline classes in `src/routes/app/reeprise/index.tsx` to `font-serif` with explicit desktop/mobile size tuning.",
      "Adjusted line-height with `leading-tight` to maintain compact headline rhythm above card grids.",
      "No data-fetching, navigation, or card-behavior logic was changed.",
    ],
  },
  {
    id: "reeprise-desktop-compose-now-centers-to-main-content-column",
    timestamp: "2026-04-17T21:40:40+02:00",
    title: "Reeprise desktop composer now centers to the main content column",
    summary:
      "The fixed desktop “Thema für deinen Text eingeben” composer now aligns to the Reeprise main content column center instead of generic viewport center.",
    userHighlights: [
      "The desktop composer now sits visually centered with the main Reeprise column.",
      "Alignment remains stable when the side navigation switches between compact and expanded widths.",
      "Composer size and interaction behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated desktop composer positioning in `src/routes/app/reeprise/index.tsx` to use `left: calc((100vw + var(--app-desktop-nav-width, 72px)) / 2)` with `-translate-x-1/2`.",
      "Set `--app-desktop-nav-width` dynamically in `src/routes/app/route.tsx` based on side-nav state (`72px` compact, `440px` expanded).",
      "Kept the existing `max-w-[860px]` composer width and fixed-bottom behavior unchanged.",
    ],
  },
  {
    id: "reeprise-daily-primary-card-now-shows-pointer-cursor-on-hover",
    timestamp: "2026-04-17T21:38:12+02:00",
    title: "Reeprise daily primary card now shows pointer cursor on hover",
    summary:
      "The primary “Text des Tages” card now exposes a clear pointer cursor on desktop hover to match the interaction signal of other clickable cards.",
    userHighlights: [
      "The first daily card now clearly indicates clickability on hover.",
      "Pointer behavior now matches neighboring daily/history cards.",
      "Card click and keyboard interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated primary daily-card container classes in `src/routes/app/reeprise/index.tsx` to explicitly apply `hover:cursor-pointer` in interactive states.",
      'Added `data-testid="reeprise-daily-primary-card"` in both create and done render branches for stable test targeting.',
      "Extended `e2e/reeprise-generation.spec.ts` desktop-layout test with a CSS cursor assertion on the primary daily card.",
    ],
  },
  {
    id: "reeprise-main-column-width-widened-with-matched-composer-adaptation",
    timestamp: "2026-04-17T21:34:56+02:00",
    title: "Reeprise main column was widened and composer width was adapted",
    summary:
      "The Reeprise content column was widened again for a roomier desktop reading surface, and the fixed composer container was resized to match.",
    userHighlights: [
      "Reeprise now feels less narrow on desktop.",
      "Cards gain more horizontal room without changing their interaction pattern.",
      "The fixed create/composer area remains aligned with the main content width.",
    ],
    technicalNotes: [
      "Updated Reeprise wrapper max width in `src/routes/app/reeprise/index.tsx` from `max-w-[700px]` to `max-w-[860px]`.",
      "Updated the fixed desktop composer wrapper from `max-w-[700px]` to `max-w-[860px]` for consistent alignment.",
      "No changes to feed/daily data logic or card click/navigation behavior.",
    ],
  },
  {
    id: "reeprise-primary-daily-card-now-uses-compact-strip-layout",
    timestamp: "2026-04-17T21:33:48+02:00",
    title: "Primary daily card now uses a compact strip layout",
    summary:
      "The first daily card in the desktop strip now uses a compact layout so it no longer grows much taller than neighboring cards.",
    userHighlights: [
      "The first daily card now aligns better with the height rhythm of adjacent cards.",
      "Long descriptive text no longer creates excessive vertical stretch in strip mode.",
      "Primary action remains visible and accessible.",
    ],
    technicalNotes: [
      "Changed strip-mode layout in `DailyPracticeCallout` (`src/routes/app/reeprise/index.tsx`) from two-column content/action to stacked compact flow.",
      "Hidden long body paragraphs for strip mode on both the primary daily card and done-state daily card to avoid oversized card height.",
      "Kept non-strip/mobile daily card layouts and behavior unchanged.",
    ],
  },
  {
    id: "reeprise-daily-cards-now-use-content-height-to-remove-empty-space",
    timestamp: "2026-04-17T21:30:40+02:00",
    title: "Reeprise daily cards now use content height to remove empty space",
    summary:
      "The daily card row no longer forces equal tall card heights, so cards now size to their content and avoid large empty white areas.",
    userHighlights: [
      "Daily cards now visually fit their text content more tightly.",
      "Large blank areas under short card text are removed.",
      "The daily row keeps the same order and interaction behavior.",
    ],
    technicalNotes: [
      "Updated the daily-strip grid in `src/routes/app/reeprise/index.tsx` to `items-start` so cards don’t stretch to the tallest sibling.",
      "Removed forced desktop `h-full/min-h` sizing from `DailyPracticeCallout` card container classes and switched to intrinsic `self-start` sizing.",
      "Applied `self-start` to desktop history cards to keep each item at natural content height.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-uses-three-column-grid-to-fill-row",
    timestamp: "2026-04-17T21:28:44+02:00",
    title: "Reeprise daily strip now uses a 3-column grid to fill the row",
    summary:
      "The daily section now uses three desktop columns so the three visible daily cards fill the available row width without an empty fourth slot.",
    userHighlights: [
      "Daily cards now span the full row width when three are shown.",
      "The previous unused empty column is removed.",
      "Show-more behavior remains unchanged.",
    ],
    technicalNotes: [
      "Changed daily-strip layout in `src/routes/app/reeprise/index.tsx` from `lg:grid-cols-4` to `lg:grid-cols-3`.",
      "Adjusted layout regression expectation in `e2e/reeprise-generation.spec.ts` to match three-column desktop behavior.",
      "No data loading, card content, or interaction logic changed.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-shows-three-cards-by-default",
    timestamp: "2026-04-17T21:27:40+02:00",
    title: "Reeprise daily strip now shows three cards by default",
    summary:
      "The daily-text strip now shows fewer cards at first load so the section stays tighter and easier to scan.",
    userHighlights: [
      "Only three daily cards are shown by default in the strip view.",
      "You can still expand to see more older cards.",
      "Main daily-card behavior remains unchanged.",
    ],
    technicalNotes: [
      "Reduced `desktopHistoryPreviewLimit` in `src/routes/app/reeprise/index.tsx` from `3` to `2` for older cards, resulting in three visible cards total with the primary daily card.",
      "Kept existing show-more/show-less toggle behavior and history metadata logic intact.",
      "No API/data contract changes were introduced.",
    ],
  },
  {
    id: "reeprise-main-column-width-now-significantly-narrower",
    timestamp: "2026-04-17T21:26:26+02:00",
    title: "Reeprise main column width is now significantly narrower",
    summary:
      "The Reeprise main content column was reduced to a much narrower maximum width so the desktop reading column feels more focused.",
    userHighlights: [
      "Desktop Reeprise now uses a tighter central reading column.",
      "Content lines are shorter and easier to scan.",
      "The fixed composer width was aligned to the same narrower column.",
    ],
    technicalNotes: [
      "Reduced the Reeprise page wrapper max width in `src/routes/app/reeprise/index.tsx` from `max-w-[1260px]` to `max-w-[700px]`.",
      "Aligned the desktop fixed composer container max width from `max-w-[920px]` to `max-w-[700px]`.",
      "No changes to feed/daily logic, card behavior, or data loading.",
    ],
  },
  {
    id: "reeprise-feed-now-uses-single-column-editorial-layout-with-desktop-mini-preview",
    timestamp: "2026-04-17T21:24:55+02:00",
    title:
      "Reeprise feed now uses a single-column editorial layout with desktop mini preview",
    summary:
      "The Reeprise feed now stacks cards vertically for stronger reading rhythm and improved typography, while keeping the mini streamed preview visible on desktop.",
    userHighlights: [
      "Feed cards are now placed one under another for easier long-form scanning.",
      "Typography and spacing were rebalanced for clearer hierarchy and reading rhythm.",
      "The mini preview card remains visible on desktop, not only on mobile.",
    ],
    technicalNotes: [
      "Changed feed container in `src/routes/app/reeprise/index.tsx` to a single-column grid and adjusted the load-more row span accordingly.",
      "Refined `src/components/ReepriseTextCard.tsx` proportions (title scale, body rhythm, spacing) and retained mini preview visibility at desktop breakpoints.",
      "Updated Playwright layout expectation in `e2e/reeprise-generation.spec.ts` so desktop feed grid asserts single-column behavior.",
    ],
  },
  {
    id: "reeprise-feed-cards-now-use-utility-editorial-neutral-tone",
    timestamp: "2026-04-17T21:17:48+02:00",
    title: "Reeprise feed cards now use a utility/editorial-neutral tone",
    summary:
      "Feed cards were shifted away from expressive gradients and decorative styling to a cleaner neutral editorial tone on web view.",
    userHighlights: [
      "Feed cards now use plain neutral surfaces instead of stylized gradient treatments.",
      "Podcast cards now match the same neutral card language as regular feed cards.",
      "Card readability and scan consistency on desktop are improved.",
    ],
    technicalNotes: [
      "Updated desktop feed card surfaces in `src/components/ReepriseTextCard.tsx` to neutral `bg-white` styles and removed decorative overlay accents.",
      "Normalized podcast-card supporting UI elements (audio block, transcript, muted labels) to slate-based neutral colors.",
      "Kept daily-strip cards and other non-feed sections unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-daily-header-toggle-no-longer-triggers-hook-order-crash",
    timestamp: "2026-04-17T21:13:02+02:00",
    title: "Reeprise daily-header toggle no longer triggers a hook-order crash",
    summary:
      "A hook-order regression in the Reeprise overview was fixed so the page no longer falls into a runtime error while loading desktop daily-header controls.",
    userHighlights: [
      "The Reeprise page now loads reliably while daily-header controls initialize.",
      "The daily history toggle behavior remains unchanged after the fix.",
      "No visual workflow changes were introduced beyond stability.",
    ],
    technicalNotes: [
      "Moved the `showAllDailyHistoryDesktop` reset `useEffect` in `src/routes/app/reeprise/index.tsx` before the early loading return to keep hook order stable across renders.",
      "Kept existing daily-strip header-toggle layout and metadata wiring unchanged.",
      "Validated the same Reeprise layout regression flow in Playwright after the fix.",
    ],
  },
  {
    id: "reeprise-daily-history-toggle-now-sits-in-header-with-text-hover-state",
    timestamp: "2026-04-17T21:11:38+02:00",
    title:
      "Reeprise daily-history toggle now sits in the section header with text hover state",
    summary:
      "The daily-history toggle now aligns to the right side of the “Deine täglichen Texte” heading row and uses a text-link style with clear hover feedback.",
    userHighlights: [
      "The toggle now appears on the same height line as the daily section title.",
      "The control is right-aligned in the header area.",
      "Hover now provides a cleaner text-state cue with pointer behavior.",
    ],
    technicalNotes: [
      "Lifted desktop history-toggle state from `DailyPracticeCallout` into the parent `Reeprise` route to place the control in the section header row.",
      "Added metadata callback wiring from `DailyPracticeCallout` to parent (`hasMore`, `hiddenCount`) so the header control can render accurately.",
      "Removed the previous in-grid toggle row from the callout and applied text-link interaction classes in `src/routes/app/reeprise/index.tsx`.",
    ],
  },
  {
    id: "reeprise-feed-now-uses-four-column-desktop-grid-with-redesigned-web-cards",
    timestamp: "2026-04-17T21:09:31+02:00",
    title:
      "Reeprise feed now uses a 4-column desktop grid with redesigned web cards",
    summary:
      "On desktop, the Reeprise feed now renders in a four-column grid and cards were redesigned for a cleaner modern web reading surface while mobile card behavior stays intact.",
    userHighlights: [
      "Desktop feed cards now display in a denser 4-column layout for faster scanning.",
      "Desktop card visuals now use a cleaner editorial style with tighter typography and fewer decorative blocks.",
      "The mobile presentation keeps the previous card experience.",
    ],
    technicalNotes: [
      "Switched the feed container in `src/routes/app/reeprise/index.tsx` from flex-wrap to responsive grid (`md:2`, `lg:3`, `xl:4` columns).",
      "Refined `src/components/ReepriseTextCard.tsx` with desktop-only compact card spacing, hidden floating preview layers on `lg+`, and updated gradient/border styling for webview readability.",
      "Extended Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert desktop/mobile column behavior for both daily strip and feed grid.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-cute-hamburger-button",
    timestamp: "2026-04-17T21:06:45+02:00",
    title: "Side navigation toggle now uses a playful hamburger button",
    summary:
      "The app now uses a clearer, friendlier side-navigation toggle icon that matches common web patterns and animates between closed and open states.",
    userHighlights: [
      "The top-left side-nav toggle now looks like a familiar hamburger button.",
      "When the side navigation opens, the icon smoothly morphs into a close shape.",
      "A small accent dot adds a playful touch while the menu is closed.",
    ],
    technicalNotes: [
      "Replaced the old top-bar command-line icon trigger in `src/routes/app/route.tsx` with an accessible `button` using a custom animated three-line glyph.",
      "Added `aria-label` binding via existing translation key `website_nav_menu_toggle` to improve accessibility and keep i18n consistency.",
      "Kept existing toggle behavior, compact desktop rail behavior, and mobile drawer interaction unchanged.",
    ],
  },
  {
    id: "reeprise-daily-strip-toggle-now-uses-text-only-style",
    timestamp: "2026-04-17T21:06:18+02:00",
    title: "Reeprise daily-strip toggle now uses a text-only style",
    summary:
      "The older-cards toggle in the Reeprise daily strip now appears as plain text without button chrome.",
    userHighlights: [
      "The toggle no longer has background fill.",
      "The toggle no longer has an outline/border.",
      "Behavior and wording stay unchanged.",
    ],
    technicalNotes: [
      "Updated the desktop daily-strip toggle class in `src/routes/app/reeprise/index.tsx` to a text-only style (`text-xs`, no border, no background).",
      "Kept the same click handler and expansion logic for older cards.",
      "No changes were made to data loading or navigation.",
    ],
  },
  {
    id: "reeprise-daily-strip-toggle-button-now-uses-compact-chip-style",
    timestamp: "2026-04-17T21:04:49+02:00",
    title: "Reeprise daily-strip toggle now uses a compact button style",
    summary:
      "The desktop daily-strip toggle for older cards now uses a smaller chip-style button so it feels less visually heavy.",
    userHighlights: [
      "The older-cards button is no longer full width.",
      "The control now appears as a compact rounded chip.",
      "Toggle behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the desktop strip toggle container in `src/routes/app/reeprise/index.tsx` to a non-block flex row.",
      "Replaced the previous full-width `text-sm` button style with a compact `inline-flex`, `rounded-full`, `text-xs` style.",
      "No data, navigation, or history-toggle logic changed.",
    ],
  },
  {
    id: "desktop-sidenav-now-has-thin-outline-stroke",
    timestamp: "2026-04-17T21:03:00+02:00",
    title: "Desktop side navigation now has a thin outline stroke",
    summary:
      "The desktop side navigation now uses a subtle thin border so it is visually separated from the page background.",
    userHighlights: [
      "The side nav is easier to distinguish from the main background.",
      "The new outline stays minimal and does not change navigation behavior.",
      "Mobile navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the side navigation panel container in `src/routes/app/route.tsx` from right-edge-only border styling to a thin desktop border (`md:border md:border-slate-200`).",
      "Kept existing compact/expanded desktop widths and mobile drawer interaction unchanged.",
      "Extended the desktop sidenav Playwright regression in `e2e/reeprise-generation.spec.ts` to assert the panel border width is `1px`.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-reaches-four-columns-earlier-and-expands-from-full-history",
    timestamp: "2026-04-17T21:02:26+02:00",
    title:
      "Reeprise daily strip now reaches four columns earlier and expands from full history",
    summary:
      "The daily strip now switches to four columns at a broader desktop breakpoint, and the older-cards toggle now expands from all generated history cards instead of only daily-nudge history.",
    userHighlights: [
      "Four compact cards now appear at more desktop widths.",
      "The older-cards toggle can reveal a broader set of generated cards.",
      "Daily strip card density is tighter with smaller card footprint and text sizing.",
    ],
    technicalNotes: [
      "Changed the daily-strip breakpoint in `src/routes/app/reeprise/index.tsx` from `xl:grid-cols-4` to `lg:grid-cols-4`.",
      "Updated desktop strip history source in `DailyPracticeCallout` to use `sortGeneratedTexts(generatedTexts)` while still excluding the featured card.",
      "Reduced strip card min-height/padding and adjusted desktop toggle row span to `lg:col-span-4`.",
    ],
  },
  {
    id: "desktop-sidenav-now-defaults-to-compact-icon-rail",
    timestamp: "2026-04-17T21:01:04+02:00",
    title: "Desktop side navigation now starts as a compact icon rail",
    summary:
      "On desktop, the app side navigation now stays visible in a compact icon-only mode by default and expands to the full panel when you toggle it.",
    userHighlights: [
      "Desktop side navigation now stays visible in a minimal icon layout even when collapsed.",
      "The top-left menu toggle now expands that icon rail into the full command and label panel.",
      "Mobile side navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated `/app` shell layout in `src/routes/app/route.tsx` so desktop collapsed width uses a persistent compact rail (`md:w-[72px]`) instead of `md:w-0`.",
      "Kept desktop collapsed rail interactive by preserving pointer events and showing icon-only navigation labels with `aria-label`/`title` metadata.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify compact desktop default width, icon-rail navigation, and expansion on toggle.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-shows-four-cards-with-expandable-older-history",
    timestamp: "2026-04-17T20:59:11+02:00",
    title:
      "Reeprise daily strip now shows four cards with expandable older history",
    summary:
      "The daily-text strip on desktop is now more compact so four cards fit across, and older daily cards can be expanded on demand.",
    userHighlights: [
      "Desktop daily cards are smaller, so the first row fits up to four cards.",
      "Older daily cards are now collapsed by default to keep the page cleaner.",
      "A dedicated button lets you show all older daily cards and collapse them again.",
    ],
    technicalNotes: [
      "Changed the desktop daily-strip grid in `src/routes/app/reeprise/index.tsx` to `xl:grid-cols-4` and reduced strip card footprint (`min-h` and padding).",
      "Added desktop-only history expansion state in `DailyPracticeCallout` with a preview limit and `show more/show less` toggle for older daily cards.",
      "Added translation keys in `src/assets/translations.json` and tightened Playwright desktop layout coverage in `e2e/reeprise-generation.spec.ts` to assert a four-column strip.",
    ],
  },
  {
    id: "reeprise-desktop-side-gutters-now-use-larger-horizontal-padding",
    timestamp: "2026-04-17T20:56:02+02:00",
    title: "Reeprise desktop side gutters now use more horizontal space",
    summary:
      "The Reeprise page now keeps a larger left and right gutter on bigger screens so the content breathes more and feels less edge-to-edge.",
    userHighlights: [
      "Desktop Reeprise now has visibly more side spacing.",
      "Mobile spacing stays compact.",
      "Layout structure and interaction flow stay unchanged.",
    ],
    technicalNotes: [
      "Updated the root Reeprise container padding in `src/routes/app/reeprise/index.tsx` with responsive horizontal classes (`sm:px-3 lg:px-8 xl:px-12`).",
      "Kept existing `max-w-[1260px]` and section composition unchanged.",
      "No data, navigation, or generation behavior was modified.",
    ],
  },
  {
    id: "reeprise-desktop-section-headlines-now-use-smaller-title-size",
    timestamp: "2026-04-17T20:55:01+02:00",
    title: "Reeprise desktop section headlines now use a smaller title size",
    summary:
      "The desktop headlines for the daily-text section and feed section on Reeprise were reduced to a more balanced size for better visual hierarchy.",
    userHighlights: [
      "“Deine täglichen Texte” now appears smaller and less dominant.",
      "“Feed” now matches the same reduced section-title scale.",
      "The page structure and interactions stay unchanged.",
    ],
    technicalNotes: [
      "Updated section heading classes in `src/routes/app/reeprise/index.tsx` from `text-[30px]` to `text-2xl` for both desktop section titles.",
      "No behavior, navigation, or data-loading logic changed.",
      "Kept existing desktop layout composition and test hooks intact.",
    ],
  },
  {
    id: "reeprise-desktop-layout-now-uses-top-utility-row-and-daily-strip",
    timestamp: "2026-04-15T17:50:34+02:00",
    title:
      "Reeprise desktop now follows a top-utility, daily-strip, and feed layout",
    summary:
      "The Reeprise overview on wide screens now uses a clearer desktop composition with utility cards on top, a dedicated daily-texts strip, and a separated feed section while mobile keeps the current flow.",
    userHighlights: [
      "On desktop, vocabulary preview and quick translate now sit side by side at the top for faster access.",
      "Daily texts are grouped into their own section before the main feed so you can scan today and previous days more quickly.",
      "The text creation card now stays docked at the bottom on desktop for faster repeated creation.",
    ],
    technicalNotes: [
      "Restructured `src/routes/app/reeprise/index.tsx` into desktop sections with explicit utility, daily, and feed containers plus a desktop-only docked generate card.",
      "Extended `DailyPracticeCallout` with a `layout` mode to support compact strip rendering without changing its existing generation and navigation logic.",
      "Added new translation keys in `src/assets/translations.json` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for desktop/mobile Reeprise composition behavior.",
    ],
  },
  {
    id: "feed-headlines-now-use-situation-first-style-rules-by-text-type",
    timestamp: "2026-04-13T22:17:45+02:00",
    title:
      "Feed headlines now follow situation-first style rules across text types",
    summary:
      "Generated text headlines are now shaped as short real-life moments with social tension and curiosity, with tailored guidance per text type.",
    userHighlights: [
      "Headlines are less likely to read like generic topics or scenic descriptions.",
      "Story, dialogue, crime, knowledge, and podcast texts now each get type-specific headline guidance.",
      "English headlines are now constrained to concise, click-worthy moment titles with clearer interaction signals.",
    ],
    technicalNotes: [
      "Expanded backend title prompt builders in `reeprise/tasks.py` with shared situation-first headline rules plus per-text-type guidance blocks.",
      "Added backend headline-style validation for English titles (word-count bounds, topic-echo guard, social-tension checks) before persisting `GeneratedText.topic`.",
      "Added rewrite/fallback handling and regression tests in `reeprise/tests.py` to enforce style-safe title outcomes across generation paths.",
    ],
  },
  {
    id: "generated-text-streaming-restored-for-localhost-and-loopback-dev-hosts",
    timestamp: "2026-04-13T22:09:05+02:00",
    title:
      "Generated-text streaming is restored for localhost and loopback in local development",
    summary:
      "Generated-text live streaming now works again in local development when the app and backend are reached via either `localhost` or `127.0.0.1`.",
    userHighlights: [
      "Generated text details can receive live text chunks again while a text is being created.",
      "Local setups that mix `localhost` and `127.0.0.1` no longer lose streaming because of websocket origin mismatch.",
      "Generation completion behavior remains unchanged; this restores live-progress delivery.",
    ],
    technicalNotes: [
      "Replaced `AllowedHostsOriginValidator` usage for `/ws/streams/` with an explicit `OriginValidator` host list builder in `reeprise/routing.py`.",
      "Added `_allowed_ws_hosts()` to include loopback hosts (`localhost`, `127.0.0.1`, `[::1]`) automatically in `DEBUG` while still honoring configured `ALLOWED_HOSTS`.",
      "Added backend regression tests in `reeprise/tests.py` covering debug loopback allowlisting and non-debug configured-host behavior.",
    ],
  },
  {
    id: "tricky-word-card-now-uses-local-created-date-matching",
    timestamp: "2026-04-11T23:00:38+02:00",
    title: "Tricky-word card now matches generated texts by local created date",
    summary:
      "The tricky-word card now compares generated-text dates in local time, so it hides correctly after creating today’s text and reappears correctly when the local day changes.",
    userHighlights: [
      "After generating a tricky-word text, the card now hides more reliably on the same local day.",
      "Day rollover behavior now follows your local calendar date instead of UTC date slicing.",
      "Daily-callout date checks now follow the same local-date logic for consistent behavior.",
    ],
    technicalNotes: [
      "Added `getLocalIsoDateFromTimestamp()` in `src/utils/getLocalIsoDate.ts` to convert backend timestamps into local `YYYY-MM-DD` values.",
      "Updated tricky-word card visibility logic in `hasTodayStrugglePracticeText.ts` to use local timestamp conversion instead of `created_at.slice(0, 10)`.",
      "Updated daily-practice date checks in `reeprise/index.tsx` and added regression tests in `getLocalIsoDate.test.ts`.",
    ],
  },
  {
    id: "tricky-word-card-hide-detection-now-follows-struggle-origin-and-payload",
    timestamp: "2026-04-11T22:59:16+02:00",
    title:
      "Tricky-word card hide detection now follows struggle origin and payload",
    summary:
      "The tricky-word card now hides reliably after generating a tricky-word text, including cases where titles are normalized and would otherwise mask origin by topic text.",
    userHighlights: [
      "Creating a tricky-word text now hides the card the same day more reliably.",
      "Texts with custom/generated titles still count as tricky-word texts when they carry struggle-word payload.",
      "German generic tricky-word topic input is now recognized as tricky-word origin on create.",
    ],
    technicalNotes: [
      "Extended backend `_infer_generated_text_origin()` in `views.py` to classify German generic tricky-word topic input as `STRUGGLE_PRACTICE`.",
      "Updated frontend `isStrugglePracticeText()` to also treat texts with non-empty `struggle_words` payload as tricky-word texts.",
      "Added backend and frontend regression tests for German topic-origin inference and payload-based same-day hide detection.",
    ],
  },
  {
    id: "tricky-word-card-day-rollover-now-uses-local-date",
    timestamp: "2026-04-11T22:56:09+02:00",
    title: "Tricky-word card day rollover now uses local date",
    summary:
      "The tricky-word card now switches day-state based on local calendar date instead of UTC date, so it can reappear immediately at local midnight on the next day.",
    userHighlights: [
      "The tricky-word card can now reappear as soon as your local date changes.",
      "No delayed rollover because of UTC day boundaries.",
      "Daily card behavior keeps using the same visible day logic across the Reeprise page.",
    ],
    technicalNotes: [
      "Added `getLocalIsoDate()` utility in `src/utils` for consistent local `YYYY-MM-DD` date resolution.",
      "Replaced UTC-based `toISOString().slice(0, 10)` day checks in `reeprise/index.tsx` with local-date helper usage.",
      "Added unit tests for local date formatting and zero-padding behavior.",
    ],
  },
  {
    id: "tricky-word-card-now-hides-after-todays-practice-text-is-created",
    timestamp: "2026-04-11T22:51:24+02:00",
    title: "Tricky-word card now hides after today’s practice text is created",
    summary:
      "After creating today’s tricky-word practice text, the generate card now disappears and the new text remains available in the generated-text list. The card appears again on the next day.",
    userHighlights: [
      "The tricky-word generate card no longer stays visible after you already created a practice text today.",
      "Your generated practice text remains accessible in the regular generated-text list.",
      "The tricky-word card still reappears automatically on the next day.",
    ],
    technicalNotes: [
      "Added `hasTodayStrugglePracticeText()` utility to detect same-day `STRUGGLE_PRACTICE` text presence with language-space filtering.",
      "Updated `reeprise/index.tsx` to pass `showStruggleBanner={!hasTodayStruggleText}` into `GenerateTextCard`.",
      "Added Vitest coverage for date and language-space visibility cases in `hasTodayStrugglePracticeText.test.ts`.",
    ],
  },
  {
    id: "tricky-word-generation-no-longer-fails-hard-when-ai-misses-words",
    timestamp: "2026-04-11T22:07:04+02:00",
    title: "Tricky-word generation no longer fails hard when AI misses words",
    summary:
      "When tricky-word generation still misses required words after retries, the text now stays available by appending the missing vocabulary context instead of moving to a hard failed state.",
    userHighlights: [
      "The `text could not be loaded` failure screen is avoided for this tricky-word mismatch case.",
      "Required tricky words are still guaranteed to appear in the final practice text.",
      "Generation remains retry-first, but now has a reliable final fallback instead of aborting.",
    ],
    technicalNotes: [
      "Replaced the strict final `FAILED` branch in `complete_generated_text` with a fallback that appends missing struggle-word context lines after max retry attempts.",
      "Added `_append_missing_struggle_context()` in `tasks.py` to guarantee required-word presence while preserving already-generated content.",
      "Updated backend regression coverage to assert completion with required-word inclusion after max retries.",
    ],
  },
  {
    id: "generated-text-failed-screen-no-longer-triggers-hook-order-crash",
    timestamp: "2026-04-11T21:45:18+02:00",
    title: "Generated-text failed screen no longer triggers a hook-order crash",
    summary:
      "Opening a generated text that transitions into failed state no longer crashes with a React hook-order error.",
    userHighlights: [
      "The generated-text details page now stays stable when a text is marked failed.",
      "The failed-state screen now opens without the `Rendered fewer hooks than expected` crash.",
      "Refreshing from the failed-state screen remains available as before.",
    ],
    technicalNotes: [
      "Moved the `FAILED` early-return branch in `reeprise/$generatedTextId.tsx` to after all hooks in `GeneratedTextDetailsPage`.",
      "Kept the failed-screen UI and behavior unchanged while ensuring consistent hook invocation order across renders.",
      "Verified the runtime source via `.logs/ui.log` stack traces pointing to `GeneratedTextDetailsPage`.",
    ],
  },
  {
    id: "tricky-word-practice-now-enforces-required-vocabulary-coverage",
    timestamp: "2026-04-11T21:40:07+02:00",
    title: "Tricky-word practice now enforces required vocabulary coverage",
    summary:
      "Tricky-word practice texts now validate that all selected tricky words are present in the generated text. If words are missing, generation retries with stricter instructions instead of silently finishing with incomplete coverage.",
    userHighlights: [
      "Practice texts now reliably include the tricky words they were created for.",
      "If the first AI draft misses required words, the app now retries automatically.",
      "If required words still cannot be included after retries, generation fails clearly instead of showing a misleading practice text.",
    ],
    technicalNotes: [
      "Extended text-generation prompt building in `tasks.py` with retry-specific missing-word instructions for struggle practice.",
      "Added backend validation that checks generated body content for each required tricky word and triggers up to 3 generation attempts.",
      "Added regression tests for retry success and fail-after-max-attempt behavior, and updated existing fallback-vocabulary struggle test coverage.",
    ],
  },
  {
    id: "tricky-word-generated-vocabulary-now-highlights-like-clicked-words",
    timestamp: "2026-04-11T21:29:04+02:00",
    title: "Tricky-word generated vocabulary now highlights like clicked words",
    summary:
      "Words used to generate a tricky-word practice text are now highlighted with the same inline style as words you click to translate and save.",
    userHighlights: [
      "Generated tricky-word vocabulary now appears with the same blue inline emphasis as manually added word highlights.",
      "Highlighting is now more reliable for full words and avoids partial substring matches inside other words.",
      "Language filtering still ensures only vocabulary from the generated text language is highlighted.",
    ],
    technicalNotes: [
      "Added a shared `resolveGeneratedTextHighlightWords` utility to unify selected-word and tricky-word highlight resolution.",
      "Switched `GeneratedTextDetailsTextPane` to use the shared boundary-aware resolver instead of separate matching logic paths.",
      "Added Vitest coverage for combined highlight sources, language filtering, deduplication, and deterministic index ordering.",
    ],
  },
  {
    id: "tricky-word-practice-card-now-stays-available-with-broader-word-picks",
    timestamp: "2026-04-11T17:04:03+02:00",
    title: "Tricky-word practice stays available more reliably",
    summary:
      "The tricky-word practice card now stays available more consistently because practice-word selection no longer depends only on a very narrow low-streak review state.",
    userHighlights: [
      "The tricky-word practice card no longer disappears so easily after you have reviewed words a bit differently.",
      "Practice texts can now draw from a broader set of weaker or still-relevant words instead of only the strictest recent failure cases.",
      "After creating one practice text, the card remains available instead of being hidden for the rest of the day.",
    ],
    technicalNotes: [
      "Added a shared backend selector that ranks practice-word candidates by recent weak progress first, then falls back to lower-streak, older-learning, and unreviewed vocabulary in the same language.",
      "Switched both `/api/reeprise/vocabulary/struggle_words/` and struggle-practice text generation to the same selector so the banner and generated text stay aligned.",
      "Removed the frontend's 24-hour local hide gate for the tricky-word practice banner and added backend regression coverage for the new fallback behavior.",
    ],
  },
  {
    id: "desktop-side-navigation-now-keeps-its-scroll-container-when-open",
    timestamp: "2026-04-11T16:48:45+02:00",
    title: "Desktop side navigation now keeps scrolling reliably when open",
    summary:
      "The app sidebar now preserves its own scroll area more consistently on desktop, so long navigation lists remain usable instead of sometimes getting stuck.",
    userHighlights: [
      "Opening the side navigation on desktop now keeps the full menu scrollable more reliably.",
      "Long navigation lists should no longer depend on a fragile height calculation to reach lower entries.",
      "The sidebar still behaves the same visually, but its desktop interaction is more stable.",
    ],
    technicalNotes: [
      "Tightened the desktop `/app` shell layout in `route.tsx` so the side-navigation wrapper and panel both participate in an explicit `min-h-0` flex height chain.",
      "Added desktop overflow containment to the side-navigation shell to keep the sidebar's own `overflow-y-auto` region authoritative when the panel is opened.",
      "Extended Playwright coverage with a desktop regression that checks the side-navigation panel can still scroll after being opened.",
    ],
  },
  {
    id: "mobile-side-navigation-now-opens-with-correct-layering-and-scroll",
    timestamp: "2026-04-11T16:42:56+02:00",
    title: "Mobile side navigation now opens with correct layering and scroll",
    summary:
      "The app side navigation now opens more reliably on phones, with the menu panel staying above its backdrop and the mobile navigation list scrolling normally again.",
    userHighlights: [
      "The side menu no longer feels blocked by its dark backdrop on mobile.",
      "You can scroll through the full navigation list again on smaller screens.",
      "The app shell keeps the same overall navigation structure while behaving more predictably on phones.",
    ],
    technicalNotes: [
      "Reworked the `/app` shell in `route.tsx` so the mobile navigation uses a fixed layer with a valid z-index instead of the previous invalid `z-60` utility.",
      "Simplified the shell scroll behavior by removing the old nested `overflow-scroll` layout and giving both the side navigation and main content their own explicit vertical scrollers.",
      "Added Playwright coverage to verify the side navigation opens on a mobile viewport, can scroll, and still allows link navigation.",
    ],
  },
  {
    id: "mnemonic-prompt-now-prefers-strong-sound-hooks-over-overly-tidy-sentences",
    timestamp: "2026-04-11T16:24:00+02:00",
    title:
      "Mnemonic drafts now prefer stronger sound hooks and punchier scenes",
    summary:
      "Mnemonic generation now leans more toward memorable sound anchors and vivid scenes instead of overly tidy but forgettable sentences. The goal is to make hard words stick faster on the first encounter.",
    userHighlights: [
      "Generated mnemonic drafts can now be a bit more dramatic or unusual when that creates a stronger memory hook.",
      "The prompt now explicitly prefers a strong sound anchor over perfectly polished everyday phrasing.",
      "Weak examples that sounded neat but did not really help recall are now discouraged.",
    ],
    technicalNotes: [
      "Adjusted the mnemonic prompt in `reeprise/tasks.py` so stronger phonetic anchors and immediately sticky imagery outrank stylistic naturalness when both are in tension.",
      "Added a positive example for French `apprend` with an `abbrennt`-style hook and a negative example that rejects the weaker `Affe am Apfelbaum` pattern.",
      "Extended backend prompt tests to lock in the new emphasis on punchier recall-first mnemonic generation.",
    ],
  },
  {
    id: "mnemonic-drafts-now-use-stronger-context-and-clearer-memory-hooks",
    timestamp: "2026-04-11T16:15:28+02:00",
    title: "Mnemonic drafts now use stronger context and clearer memory hooks",
    summary:
      "Mnemonic generation now uses the saved vocabulary context more precisely and aims for clearer first-pass memory hooks instead of loose sound tricks. The editor also frames the AI output as a draft that learners should personalize.",
    userHighlights: [
      "Generated mnemonic drafts now stay closer to the actual meaning of the clicked word in its sentence.",
      "Weak formatting noise such as labels, markdown, or extra explanation text is stripped before a mnemonic is saved.",
      "The mnemonic card now makes it clearer that the best result comes from rewriting the draft in your own words.",
    ],
    technicalNotes: [
      "Rebuilt the mnemonic prompt in `reeprise/tasks.py` to include structured translation context such as dictionary form, translated sentence, context hints, and alternative meanings when available.",
      "Added server-side mnemonic cleanup so prefixed labels, markdown wrappers, and trailing extra lines are normalized before persistence.",
      "Updated backend tests for prompt construction and normalization, and refreshed the mnemonic card copy in `translations.json` to present generation as a draft rather than a final answer.",
    ],
  },
  {
    id: "product-updates-now-hide-technical-notes-from-non-staff-users",
    timestamp: "2026-04-11T16:15:16+02:00",
    title: "Product updates now focus on user-facing changes",
    summary:
      "The updates page now keeps technical implementation detail out of the default user view, so it is easier to understand what changed without reading internal engineering notes.",
    userHighlights: [
      "The main updates page now focuses on visible product changes and user impact.",
      "Technical implementation notes are only shown to staff users.",
      "The page language now reads more like an update archive and less like an internal logbook.",
    ],
    technicalNotes: [
      "Split the old mixed `product_logbook` view into a public `Product Updates` page plus a separate staff-only technical route backed by the same entry data.",
      "Removed technical notes from the public card layout, added a dedicated `/app/technical_change_log` route, and limited its navigation plus direct access to staff users.",
      "Extended Playwright coverage so the public route verifies that technical notes stay hidden from the normal user view while entry numbering still works.",
    ],
  },
  {
    id: "saved-word-preview-typography-now-has-a-clearer-hierarchy",
    timestamp: "2026-04-02T13:05:11+02:00",
    title: "Saved-word preview typography now has a clearer hierarchy",
    summary:
      "The saved-word flyout now makes the clicked word clearly primary, the translation clearly secondary, and the supporting labels visibly quieter. The typography reads more intentionally instead of giving similar weight to unlike information.",
    userHighlights: [
      "The clicked word now stands out first in the flyout instead of competing with the translation block.",
      "The translation still reads easily, but it no longer feels like it has the same priority as the saved word itself.",
      "Labels like `Translation` and `Base form` are quieter metadata now, not pseudo-headings.",
    ],
    technicalNotes: [
      "Adjusted the saved-word preview typography in `GeneratedTextDetailsTextPane.tsx` so the main word uses the strongest size and tone, while the translation is slightly reduced and softened.",
      "Lowered the visual weight of the section label and base-form label through smaller sizing and lighter color values.",
      "Kept the existing layout and interaction behavior unchanged; this pass focuses purely on text hierarchy and semantic emphasis.",
    ],
  },
  {
    id: "saved-word-preview-spacing-rhythm-now-uses-consistent-proportions",
    timestamp: "2026-04-02T13:01:14+02:00",
    title: "Saved-word preview spacing rhythm now uses consistent proportions",
    summary:
      "The saved-word flyout now uses a steadier spacing system, so the gaps between word, metadata, controls, and translation read as one coherent layout instead of a stack of mismatched offsets.",
    userHighlights: [
      "The flyout header now feels less jumpy because word, base-form line, and controls share a more even vertical rhythm.",
      "The divider and translation block sit closer to the content in a calmer proportion instead of leaving awkward empty pockets.",
      "Controls remain easy to reach, but the spacing now follows the reading content instead of arbitrary margins.",
    ],
    technicalNotes: [
      "Normalized the saved-word preview container padding and reduced mixed one-off offsets in `GeneratedTextDetailsTextPane.tsx`.",
      "Changed the header content block to a simple internal spacing rhythm and aligned the control cluster to the same vertical baseline.",
      "Tightened the divider and translation section spacing so the flyout uses one consistent gap system instead of separate `pt`/`mt` corrections.",
    ],
  },
  {
    id: "saved-word-preview-now-labels-the-base-form-explicitly",
    timestamp: "2026-04-02T12:58:04+02:00",
    title: "Saved-word preview now labels the base form explicitly",
    summary:
      "The secondary line in the saved-word flyout now clearly says when it is showing the base form, so inflected words are easier to understand at a glance.",
    userHighlights: [
      "The flyout no longer shows the smaller secondary form without context.",
      "If the seen word differs from its base form, the preview now labels it explicitly as `Base form`.",
      "The label stays visually quiet, so it clarifies the metadata without competing with the main word or translation.",
    ],
    technicalNotes: [
      "Added a dedicated base-form label in the generated-text saved-word flyout header in `GeneratedTextDetailsTextPane.tsx`.",
      "Added translation key `generated_text_word_preview_base_form_label` in English and German.",
      "Kept the base-form row conditional so it only appears when the saved surface form differs from the canonical form.",
    ],
  },
  {
    id: "saved-word-preview-typography-now-stays-compact-again",
    timestamp: "2026-04-02T12:55:34+02:00",
    title: "Saved-word preview typography now stays compact again",
    summary:
      "The saved-word flyout no longer uses oversized word and translation text. The preview keeps the same hierarchy, but the typography is back to a calmer, more proportional reading size.",
    userHighlights: [
      "Saved word and translation in the flyout no longer feel oversized compared with the surrounding reading view.",
      "The base-form line is quieter again and no longer competes with the main saved word.",
      "The flyout still reads clearly, but with less visual weight and better proportion.",
    ],
    technicalNotes: [
      "Reduced the saved-word headline and translation font sizes in `GeneratedTextDetailsTextPane.tsx` after the previous spacing pass pushed them too far.",
      "Scaled the secondary base-form line back down to restore a clearer primary-secondary hierarchy inside the flyout.",
      "Kept the revised spacing and control layout from the previous flyout cleanup while only correcting typography proportion.",
    ],
  },
  {
    id: "saved-word-preview-spacing-now-follows-content-priority-better",
    timestamp: "2026-04-02T12:48:26+02:00",
    title: "Saved-word preview spacing now follows content priority better",
    summary:
      "The saved-word flyout now uses cleaner proportions and whitespace so the word and translation lead the layout more naturally, while the controls stay compact and secondary.",
    userHighlights: [
      "The flyout header now gives the saved word more breathing room instead of crowding it with controls.",
      "The base form and translation sit in a calmer, more readable rhythm with the divider and outer padding.",
      "Close and audio controls still stay easy to reach, but they no longer dominate the preview.",
    ],
    technicalNotes: [
      "Adjusted saved-word preview outer padding, header gap, and divider spacing in `GeneratedTextDetailsTextPane.tsx` to create a more proportional content-first layout.",
      "Reduced the visual weight of the close control and tightened control grouping so the word block remains the primary anchor.",
      "Scaled the main word and translation typography with separate spacing so the flyout hierarchy follows reading function rather than equal visual treatment for every element.",
    ],
  },
  {
    id: "saved-word-preview-now-opens-on-press-instead-of-after-click-release",
    timestamp: "2026-04-02T12:45:12+02:00",
    title:
      "Saved-word preview now opens on press instead of after click release",
    summary:
      "The saved-word flyout now appears as soon as you press a highlighted word, instead of waiting for the full click to finish. The preview feels much more immediate while reading.",
    userHighlights: [
      "The translation flyout now shows up right on press for already saved highlighted words.",
      "Desktop hover preview still works, but click and tap feedback now feels noticeably faster too.",
      "The reading interaction no longer feels like it hesitates before opening the saved-word preview.",
    ],
    technicalNotes: [
      "Moved saved highlighted-word preview opening from the completed `click` path to previewable span pointer-down handling in the shared generated-text span renderer.",
      "Kept add-vocabulary behavior unchanged for non-saved words so first-time word taps still require the normal explicit add action.",
      "Updated Playwright coverage to assert the saved-word flyout appears on press and aligned the spec with the current simplified flyout content.",
    ],
  },
  {
    id: "generate-text-mockup-now-has-a-separate-reading-first-concept-page",
    timestamp: "2026-04-02T12:39:37+02:00",
    title: "Generate Text now has a separate reading-first concept page",
    summary:
      "A new in-app mockup page now demonstrates a reading-first future for Generate Text with a dominant `Surprise Me` path, lightweight topic steering, short story blocks, and a built-in continue loop.",
    userHighlights: [
      "There is now a dedicated `Generate Text Mockup` page in the side navigation.",
      "The mockup shows how the feature could start from a teaser and quickly move into short, curiosity-driven reading blocks.",
      "You can click through surprise, concretize, regenerate, continue, and tapped-word vocabulary states without touching the live generator flow.",
    ],
    technicalNotes: [
      "Added a new `/app/generate_text_mockup` route with local state for teaser rotation, on-demand concretization, mock generation, regeneration, continuation, and vocabulary inspection.",
      "Wired the page into the app navigation, added new translation keys for the mockup copy, and kept the implementation separate from the real `GenerateTextCard` flow.",
      "Added Playwright coverage to verify the mockup page is reachable from the side navigation and that the local reading flow can start and continue.",
    ],
  },
  {
    id: "saved-word-preview-flyout-now-keeps-the-word-primary",
    timestamp: "2026-04-02T12:39:45+02:00",
    title: "Saved-word preview flyout now keeps the word primary",
    summary:
      "The inline word-preview flyout now gives more space to the actual saved word and translation. The oversized close button and redundant saved-word label are gone, so the preview reads more cleanly.",
    userHighlights: [
      "The flyout no longer spends a full line on a `Gespeichertes Wort` heading before the actual word.",
      "The close action is now a compact icon instead of a large pill button that competed with the content.",
      "Saved word and translation stay easier to scan because the controls take up less space.",
    ],
    technicalNotes: [
      "Removed the redundant saved-word eyebrow from the generated-text preview flyout so the saved word itself remains the first visual anchor.",
      "Replaced the full-width close text button with a compact icon button while preserving the same translated close label via `aria-label` and `title`.",
      "Kept the existing pronunciation control and flyout behavior unchanged; this pass only tightens hierarchy and spacing inside the preview header.",
    ],
  },
  {
    id: "saved-highlighted-word-preview-now-also-opens-on-desktop-hover",
    timestamp: "2026-04-02T12:31:31+02:00",
    title: "Saved highlighted word preview now also opens on desktop hover",
    summary:
      "On desktop, saved highlighted words now reveal the same translation-and-audio flyout on hover, so quick meaning checks feel even lighter while reading. Tap behavior stays unchanged for mobile.",
    userHighlights: [
      "Desktop readers can now hover a saved highlighted word to see the translation preview without clicking first.",
      "The flyout stays reachable while moving the pointer from the word into the preview, so it does not disappear mid-hover.",
      "Phone and tablet tap behavior stays the same: the first tap still saves silently, and later taps open the preview on demand.",
    ],
    technicalNotes: [
      "Extended generated-text highlight span rendering with preview hover-start and hover-end hooks for previewable saved words only.",
      "Added fine-pointer hover detection plus a short delayed close in `GeneratedTextDetailsTextPane.tsx` so the flyout can be entered without flicker while preserving the existing click-based preview path.",
      "Updated Playwright coverage to verify the saved-word flyout opens on desktop hover in addition to the existing second-click behavior.",
    ],
  },
  {
    id: "saved-highlighted-words-now-open-an-inline-translation-preview",
    timestamp: "2026-04-02T12:20:53+02:00",
    title: "Saved highlighted words now open an inline translation preview",
    summary:
      "Reading stays interruption-free on the first click, but already saved highlighted words now open a compact inline flyout with the translation and pronunciation audio right where you are reading.",
    userHighlights: [
      "Clicking a new word still just saves it, so the translation does not interrupt the first reading pass.",
      "Clicking an already highlighted saved word now opens a small translation preview directly next to that word.",
      "The inline preview includes a pronunciation action, so you can hear the word without jumping to the side vocabulary list.",
    ],
    technicalNotes: [
      "Reworked generated-text body span rendering so saved highlighted words can open a preview flyout while unsaved words still go through the existing add-vocabulary action.",
      "Added local preview/highlight state in `GeneratedTextDetailsTextPane.tsx` so the second click works immediately without waiting for a vocabulary refetch, and reused the shared `VocabularyPronunciationButton` plus translation helpers.",
      "Added frontend regression coverage for previewable highlighted-word routing and a Playwright flow that checks add-first, preview-second behavior on a real generated text.",
    ],
  },
  {
    id: "read-aloud-mockup-now-reuses-the-existing-generator-and-text-details-ui",
    timestamp: "2026-04-02T11:56:45+02:00",
    title:
      "Read-aloud mockup now reuses the existing generator and text-details UI",
    summary:
      "The speaking mockup no longer presents read-aloud practice as a separate product surface. It now demonstrates how the feature can grow directly out of the current generator card and generated-text details page.",
    userHighlights: [
      "The mockup now shows the real `Generate Text` card as the entry surface instead of inventing a new start screen.",
      "The read-aloud flow is now shown inside a text-details-style layout rather than as a separate full redesign.",
      "The page makes it easier to judge whether speaking can feel native to the existing reading experience.",
    ],
    technicalNotes: [
      "Reworked the `/app/read_aloud_mockup` route to embed the real `GenerateTextCard` and a generated-text-details-style shell instead of a standalone concept layout.",
      "Added translation keys describing the integration direction so the mockup copy explains how read-aloud extends current product surfaces.",
      "Kept the side-navigation route and regression coverage while changing the page structure to align with existing UI patterns.",
    ],
  },
  {
    id: "read-aloud-speaking-flow-now-has-a-clickable-in-app-mockup-page",
    timestamp: "2026-04-02T11:46:06+02:00",
    title: "Read-aloud speaking flow now has a clickable in-app mockup page",
    summary:
      "A new mockup screen now lets the team preview a dedicated speaking-mode flow directly inside the app. The page can be opened from side navigation and walks through intro, recording, feedback, and completion states without needing backend speaking support yet.",
    userHighlights: [
      "There is now a dedicated `Read Aloud Mockup` page in the app navigation.",
      "The mockup demonstrates a focused one-sentence-at-a-time speaking mode instead of layering microphone controls onto the normal reading screen.",
      "You can click through the intended states for starting, recording, processing, feedback, and completion.",
    ],
    technicalNotes: [
      "Added a new `/app/read_aloud_mockup` route with local UI state to simulate the planned read-aloud interaction flow end to end.",
      "Wired the page into the side navigation and added translation keys for the new mockup labels and explanatory copy.",
      "Added Playwright coverage to verify the mockup page is reachable from the app navigation.",
    ],
  },
  {
    id: "product-logbook-numbering-now-stays-secondary-to-the-release-content",
    timestamp: "2026-04-02T11:24:26+02:00",
    title:
      "Product logbook numbering now stays secondary to the release content",
    summary:
      "The archive number now remains visible without competing with the actual release title, summary, and user impact. The page keeps the chronological reference system, but the content regains visual priority.",
    userHighlights: [
      "Release titles and summaries now lead the page more clearly than the archive number.",
      "The current release number is still easy to find, but it no longer dominates the header.",
      "Each note keeps a compact reference number for orientation without distracting from what changed.",
    ],
    technicalNotes: [
      "Reduced the visual weight of the current-release marker in the header so it behaves like secondary archive metadata rather than a hero KPI.",
      "Collapsed the card number rail into a compact inline metadata label with the timestamp, keeping numeric reference available while restoring content-first hierarchy.",
      "Added a frontend design rule to `AGENTS.md` requiring UI hierarchy to follow user task importance instead of the mere existence of a field.",
    ],
  },
  {
    id: "product-logbook-numbering-now-uses-an-archival-record-layout",
    timestamp: "2026-04-02T11:13:27+02:00",
    title: "Product logbook numbering now uses an archival record layout",
    summary:
      "The release-note number now acts as the organizing spine of the page instead of a loose badge, so the newest number is easier to read and every entry feels like part of the same archive.",
    userHighlights: [
      "The newest release number is now presented as a proper archive marker in the page header.",
      "Each note now has a dedicated number column that makes scanning the release sequence much easier.",
      "The layout now reads more like a chronological record and less like a stack of generic cards.",
    ],
    technicalNotes: [
      "Rebuilt the logbook header around a single current-release display with supporting archive context instead of secondary metadata chips.",
      "Refactored each release card into a responsive two-column record layout with a persistent number rail and timestamp metadata.",
      "Kept the numeric sequencing logic and Playwright regression coverage while updating the UI structure around it.",
    ],
  },
  {
    id: "product-logbook-numbering-ui-now-reads-like-a-real-logbook-system",
    timestamp: "2026-04-02T11:08:18+02:00",
    title:
      "Product logbook numbers now read like part of a real release archive",
    summary:
      "The logbook sequence is now presented as a named release-note system instead of a loose `Entry #...` label, so the newest number is easier to spot and each card feels like part of one coherent archive.",
    userHighlights: [
      "The page header now calls out the current release-note number more clearly.",
      "Each card now shows its number as a dedicated release-note badge instead of a floating label.",
      "The newest entry number should now feel intentional and easy to scan.",
    ],
    technicalNotes: [
      "Refined the product-logbook header to introduce the numbering as a named `Current Release Note` marker rather than a generic latest-entry text snippet.",
      "Reworked release-card metadata so each number appears in a dedicated badge with consistent wording and hierarchy next to the timestamp.",
      "Updated the Playwright regression to assert the new release-note copy instead of the earlier `Entry #...` phrasing.",
    ],
  },
  {
    id: "product-logbook-entry-numbers",
    timestamp: "2026-04-02T11:05:22+02:00",
    title: "Product logbook entries now show clear running numbers",
    summary:
      "The release log now shows a visible entry number for every card, so you can immediately tell which number the newest product update has.",
    userHighlights: [
      "Each product logbook card now shows a visible `Entry #...` label.",
      "The top of the page now states the latest release-note number.",
      "The existing human-readable title and internal slug ID still stay aligned with the same release item.",
    ],
    technicalNotes: [
      "Derived stable display numbers from the ordered `releaseEntries` list so newly prepended items increment the latest entry number without renumbering historical cards incorrectly.",
      "Updated the release-card header to show the numbered label separately from the timestamp while keeping the slug ID available as the React key.",
      "Added a Playwright regression that verifies the newest rendered product-logbook entry number matches the total number of entries on the page.",
    ],
  },
  {
    id: "russian-now-has-a-separate-cyrillic-learning-language-option",
    timestamp: "2026-03-22T21:53:20+01:00",
    title:
      "Russian now appears as its own learning language next to a separate Latin-transcription option",
    summary:
      "The language picker now distinguishes real Russian from Russian in Latin transcription. Existing transliterated Russian data keeps its previous behavior, while new Russian selections can use the normal Cyrillic script.",
    userHighlights: [
      "You can now choose real Russian as a learning language instead of only the transliterated variant.",
      "The existing transliterated Russian mode remains available as its own separate option.",
      "Older saved transliterated Russian content keeps the same meaning after the rollout.",
    ],
    technicalNotes: [
      "Added a dedicated `RUSSIAN_LATIN_TRANSCRIPTION` language code in frontend and backend registries while keeping `RUSSIAN` for actual Russian script.",
      "Moved Russian-specific transliteration and pronunciation hints onto the transcript variant so Cyrillic Russian is no longer normalized into Latin text by default.",
      "Added a data migration plus backend and Playwright regression coverage so historical transliterated Russian rows are relabeled safely and both Russian options remain selectable.",
    ],
  },
  {
    id: "russian-now-stays-in-latin-transliteration-across-titles-and-vocabulary",
    timestamp: "2026-03-22T21:36:06+01:00",
    title:
      "Russian now stays in Latin transliteration across titles, topics, and saved vocabulary",
    summary:
      "Russian generation and vocabulary storage now enforce Latin transliteration as a final backend normalization step, so Cyrillic no longer leaks into generated titles, topics, text bodies, or saved Russian word entries.",
    userHighlights: [
      "Russian text titles and topics now stay in Latin transliteration instead of sometimes showing Cyrillic.",
      "Generated Russian text bodies are normalized before saving, so selected vocabulary words come from transliterated text.",
      "Manually added Russian vocabulary entries now save in Latin transliteration as well.",
    ],
    technicalNotes: [
      "Added a centralized Russian Cyrillic-to-Latin normalization helper in `reeprise/tasks.py` and applied it to generated text bodies, committed titles, and translation-detail payloads.",
      "Updated Russian title-generation prompts to explicitly forbid Cyrillic in addition to the main text-generation prompt rules.",
      "Added backend regression coverage for Russian title/body normalization, Russian translation-detail normalization, and manual Russian vocabulary insertion.",
    ],
  },
  {
    id: "ai-quota-incidents-now-alert-ops-and-show-a-friendly-detail-screen",
    timestamp: "2026-03-22T08:51:39+01:00",
    title:
      "AI quota incidents now alert ops immediately and show a friendly text-failure screen",
    summary:
      "When AI credits run out, generated texts no longer fall back to fake offline content. The app now fails explicitly, sends an operator alert, and shows readers a calm recovery screen with a direct contact option.",
    userHighlights: [
      "A broken text no longer opens with confusing `Offline-Fallback` content.",
      "Readers now see a clear, friendly incident screen that explains we are already working on the issue.",
      "The detail page includes a direct contact option and an easy way back to the text list.",
    ],
    technicalNotes: [
      "Added quota-aware failure handling in `reeprise/tasks.py` so `insufficient_quota` errors mark generated texts as `FAILED`, stop retry loops for affected generation tasks, and trigger a cooldown-protected alert email.",
      "Exposed the configured support email through the account API and used it in the generated-text details route for a `mailto:` incident CTA.",
      "Updated frontend generated-text typing and polling so `FAILED` states stop refetching, then added a dedicated detail-page failure surface instead of rendering the normal reading layout.",
    ],
  },
  {
    id: "funny-smileys-added-to-the-website-footer",
    timestamp: "2026-03-21T21:38:38+01:00",
    title: "The website footer now has a row of funny smileys",
    summary:
      "The public website footer now includes a playful smiley strip so the landing page has a bit more character without changing navigation or core app behavior.",
    userHighlights: [
      "The landing page footer now shows a decorative row of funny smileys.",
      "The change is visible without logging in or opening any app screens.",
      "It adds personality without affecting navigation or feature flows.",
    ],
    technicalNotes: [
      "Added a decorative smiley line near the top of `Footer.tsx` so it appears across website pages that use the shared public footer.",
      "Kept the smileys as non-interactive footer content so the existing layout and links remain unchanged.",
      "Added a Playwright regression that checks the landing page footer renders the smiley strip.",
    ],
  },
  {
    id: "login-pricing-nav-and-quick-translate-now-use-translation-keys",
    timestamp: "2026-03-21T20:58:27+01:00",
    title:
      "Login, pricing, website navigation, and quick translate now follow the shared translation system",
    summary:
      "Several high-visibility screens now read their copy from the shared translation file instead of shipping hardcoded text, so English and German interface switching covers more of the product consistently.",
    userHighlights: [
      "The login flow now switches labels, actions, and reset-password messaging with the selected app language.",
      "Website navigation and the pricing page now follow the shared English and German translation setup.",
      "The quick translate page now uses translated labels for headings, prompts, and target-language actions.",
    ],
    technicalNotes: [
      "Wired `login.tsx`, `NavbarWebsite.tsx`, `pricing.tsx`, and `/app/quick_translate` to `useTranslation()`.",
      "Added variable substitution for the password-reset alert and a dedicated translation key for the quick-translate `Auto` label.",
      "Kept the legal and static-content pages out of this pass so the migration can continue incrementally without bundling a much larger copy rewrite.",
    ],
  },
  {
    id: "translation-keys-added-for-untranslated-ui-copy",
    timestamp: "2026-03-21T20:53:54+01:00",
    title:
      "Translation keys are now prepared for more untranslated app and website copy",
    summary:
      "The shared translation file now includes a new batch of English and German keys for login, pricing, website navigation, quick translate, and generic error messaging, so those surfaces can be moved off hardcoded copy safely.",
    userHighlights: [
      "Core screens now have a larger translation-key foundation behind them, including login and pricing copy.",
      "Common fallback messaging such as a generic error state now has a shared translation entry.",
      "This is groundwork for switching more hardcoded interface text to the app’s translation hook.",
    ],
    technicalNotes: [
      "Added a new `general_error` key that matches existing frontend usage in helper and quick-translate components.",
      "Added grouped translation entries for login, website navigation, pricing, and the `/app/quick_translate` route in both `GERMAN` and `ENGLISH`.",
      "Kept this pass focused on the shared translation dictionary so follow-up component wiring can reuse stable key names.",
    ],
  },
  {
    id: "daily-text-settings-no-longer-inherit-from-older-daily-texts",
    timestamp: "2026-03-21T20:49:54+01:00",
    title:
      "Daily texts now keep using your last regular generator settings instead of drifting from older daily texts",
    summary:
      "Creating a daily text no longer reuses a previous daily text as the settings seed for the next one. This prevents daily texts from recursively inheriting earlier daily text settings, which could make a language space drift away from your last regular generator setup.",
    userHighlights: [
      "Daily texts now follow your latest regular text settings more reliably.",
      "A previous daily text no longer becomes the hidden template for the next daily text.",
      "This specifically prevents language-specific drift in things like length, level, or text type.",
    ],
    technicalNotes: [
      "Changed daily-practice seed selection in `create_practice_text_for_user_language()` to exclude `DAILY_NUDGE` texts, not just struggle-practice texts.",
      "Kept the existing same-language-first fallback order, but now only across non-daily, non-struggle texts.",
      "Added a backend regression test proving that a new daily text inherits settings from the latest manual text instead of the latest earlier daily text.",
    ],
  },
  {
    id: "app-language-is-now-separate-from-explanation-language",
    timestamp: "2026-03-21T18:52:35+01:00",
    title: "App language is now separate from your explanation language",
    summary:
      "Changing the app interface language no longer changes the language used for word translations, grammar notes, and explanations. You can now control those two preferences separately in settings.",
    userHighlights: [
      "A new `App language` setting now controls menus, buttons, and interface copy only.",
      "A separate `Explanations and translations` setting now controls vocabulary translations, grammar notes, and helper explanations.",
      "Switching the interface to English no longer silently changes your learning support language.",
    ],
    technicalNotes: [
      "Added a dedicated `app_language` user setting and migrated existing users so their current interface language is preserved.",
      "Updated the translation hook, settings page, and command bar language shortcut to read and write `app_language` instead of `mother_tongue`.",
      "Added backend API regression coverage and a Playwright settings-flow test to ensure app-language changes no longer mutate mother-tongue translation behavior.",
    ],
  },
  {
    id: "word-highlights-now-use-the-weather-card-blue",
    timestamp: "2026-03-21T18:42:30+01:00",
    title: "Word highlights now use the same blue as the weather card",
    summary:
      "Highlighted words in texts and vocabulary cards now use the weather card’s sky-blue highlight color instead of the older green treatment.",
    userHighlights: [
      "Highlighted words now feel visually consistent with the blue weather-card style.",
      "The same blue treatment is used in generated text, titles, and vocabulary-card sentence previews.",
      "Highlight behavior stays the same; only the color system changed.",
    ],
    technicalNotes: [
      "Replaced the green highlight fill in generated-text body spans with a weather-card-inspired sky-blue gradient.",
      "Updated title-word highlight rendering in `GeneratedTextDetailsTextPane.tsx` to use the same blue treatment.",
      "Updated `highlightVocabularyWordInSentence.tsx` so card sentence previews share the same weather-card blue highlight style.",
    ],
  },
  {
    id: "continue-text-button-visible-without-saved-vocabulary",
    timestamp: "2026-03-21T18:39:43+01:00",
    title:
      "Continue text now stays visible even before you save any vocabulary",
    summary:
      "The continue action on generated-text details no longer disappears when a text has no saved vocabulary yet. You can now continue directly from the text context even on a fresh text where no words have been added.",
    userHighlights: [
      "Fresh texts without saved vocabulary now still show the `Continue text` action.",
      "You no longer need to save a word first just to keep reading forward.",
      "The selected-word continuation flow still works the same once vocabulary exists.",
    ],
    technicalNotes: [
      "Removed the generated-text details CTA visibility guard that depended on `vocabularies.length > 0`.",
      "Kept the adaptive label logic so the same button still switches between selected-word and context-only continuation states.",
      "Added a Playwright regression covering the no-saved-vocabulary state on generated-text details.",
    ],
  },
  {
    id: "topic-ideas-now-open-faster-from-the-generator",
    timestamp: "2026-03-21T18:31:32+01:00",
    title: "Topic ideas now appear faster after tapping the idea button",
    summary:
      "The topic-idea helper now returns queued suggestions before doing refill work, so tapping the idea button should feel much faster when ideas are already available.",
    userHighlights: [
      "The idea button should now respond faster in normal use.",
      "Ready suggestions are returned immediately instead of waiting on new background generation.",
      "The topic queue still refills, but no longer blocks the click.",
    ],
    technicalNotes: [
      "Changed backend topic-idea lookup to read existing pending suggestions before any top-up work.",
      "Moved suggestion-queue refill onto a Celery task so queue maintenance no longer sits on the request path.",
      "Switched queue top-up classification to bootstrap classification and added regression coverage for the fast existing-suggestion path.",
    ],
  },
  {
    id: "topic-ideas-now-stay-shorter-in-the-input",
    timestamp: "2026-03-21T18:13:33+01:00",
    title: "Topic ideas now stay shorter and fit the topic field more cleanly",
    summary:
      "The idea helper now fills the topic input with much shorter phrase-style suggestions instead of longer sentence-like prompts, so the generated topic is easier to scan and edit inside the field.",
    userHighlights: [
      "Suggested topics are now noticeably shorter.",
      "Ideas should fit the topic field more comfortably at a glance.",
      "The helper still keeps ideas specific, but now in a compact phrase style.",
    ],
    technicalNotes: [
      "Tightened backend topic-idea prompting to require compact 2-5 word ideas instead of sentence-length suggestions.",
      "Added backend validation so overly long AI ideas are rejected and replaced by shorter fallback topics.",
      "Updated fallback topic banks and added backend plus Playwright regression coverage for concise idea lengths.",
    ],
  },
  {
    id: "context-only-text-continuation-without-selected-vocabulary",
    timestamp: "2026-03-21T18:02:49+01:00",
    title: "You can now continue a text even when no vocabulary is selected",
    summary:
      "The continue action on generated-text details no longer depends on selected vocabulary. If words are selected, the next chapter uses them. If none are selected, the app now continues the text from its existing context instead of leaving the continuation path framed around vocabulary only.",
    userHighlights: [
      "The chapter button now adapts its label between `Continue with words` and `Continue text`.",
      "Clearing all selected vocabulary no longer leaves you stuck with a vocabulary-only continuation concept.",
      "Continuing without selected words now intentionally follows the current text context and story flow.",
    ],
    technicalNotes: [
      "Added a frontend label-state resolver for the generated-text continuation CTA and wired it into both desktop and mobile button surfaces.",
      "Updated chapter-generation prompt construction so empty selected-word lists explicitly trigger context-only continuation instructions instead of emitting an empty required-words prompt.",
      "Added regression coverage in Playwright for the checkbox-to-zero-selection CTA label switch and backend tests for empty generated-word chapter creation.",
    ],
  },
  {
    id: "daily-text-topics-now-respect-level-maturity",
    timestamp: "2026-03-21T18:00:17+01:00",
    title:
      "Daily texts now avoid beginner-style topic framing for higher levels",
    summary:
      "Daily generated texts now use stronger level-aware topic guardrails, so higher-level learners are less likely to receive childish or beginner-style titles such as a 'first English adventure'.",
    userHighlights: [
      "Daily texts now aim for adult, real-world situations instead of generic learner-journey framing.",
      "Higher-level users are less likely to see beginner-style daily titles.",
      "Your own accepted topics now guide daily inspiration more than earlier automatic daily titles.",
    ],
    technicalNotes: [
      "Updated daily-text guidance in `tasks.py` so topic framing must match both CEFR difficulty and scenario maturity.",
      "Changed daily inspiration lookup to prefer durable `TopicInterestSignal` topics and to exclude previous automatic daily titles from fallback history.",
      "Stopped pre-committing daily titles from the placeholder `daily learning text ...` topic and added backend regression tests for the new title flow.",
    ],
  },
  {
    id: "plural-vocabulary-cards-keep-their-number",
    timestamp: "2026-03-21T17:23:20+01:00",
    title:
      "Plural vocabulary cards now stay plural instead of being rewritten to singular",
    summary:
      "Vocabulary cards now keep plural nouns in their plural card form when the clicked word in the text is plural. The card still adds the correct article, but it no longer rewrites entries like `chiots` into `le chiot` while leaving the translation plural.",
    userHighlights: [
      "Plural noun cards now keep the plural source label, such as `les chiots`.",
      "Plural translations now stay plural too, such as `die Welpen`.",
      "Singular noun cards still use the matching singular article and singular translation.",
    ],
    technicalNotes: [
      "Updated the vocabulary translation prompt so plural noun clicks must preserve plural number on both the source card label and `translation_word`.",
      "Changed frontend vocabulary-word selection to prefer `source_plural_form` for noun entries whose grammar metadata says plural.",
      "Added backend and frontend regression tests for plural noun card reconstruction and singular noun fallback behavior.",
    ],
  },
  {
    id: "multi-word-vocabulary-selections-now-share-one-sentence-translation",
    timestamp: "2026-03-21T17:08:56+01:00",
    title:
      "Vocabulary cards from the same sentence now keep one shared sentence translation",
    summary:
      "When you save multiple words from the same sentence in one text, the resulting vocabulary cards now show the same translated sentence context instead of letting each card drift into its own sentence variant.",
    userHighlights: [
      "Saving several words from one sentence now keeps the translated sentence consistent across those cards.",
      "The shared translation is scoped to that sentence occurrence in that text, so the same raw sentence in another text can still have a different translation.",
      "Word-level meanings and grammar notes still stay specific to each selected vocabulary item.",
    ],
    technicalNotes: [
      "Added a dedicated shared sentence-translation model linked from vocabulary rows created out of generated-text selections.",
      "Reused the shared sentence translation by generated text, chapter, sentence bounds, source language, and target language instead of deduplicating globally by raw sentence text.",
      "Updated translation completion and serializer output so sibling vocabulary cards converge on the same translated sentence while preserving per-word `translation_details`.",
    ],
  },
  {
    id: "text-creation-no-longer-waits-on-topic-classification-or-daily-list-refresh",
    timestamp: "2026-03-20T06:22:07+01:00",
    title:
      "Text creation now returns immediately instead of waiting on extra classification and refresh work",
    summary:
      "Creating a text no longer waits for semantic topic classification inside the request itself, and the daily-text flow no longer waits for a list refresh before navigating. This reduces avoidable delay before the new text opens, especially on slower environments.",
    userHighlights: [
      "Manual text creation now prioritizes creating the text first and enriches topic intelligence afterwards.",
      "Daily text creation no longer pauses on a list refresh before opening the new text.",
      "This reduces extra waiting around the moment a new text starts.",
    ],
    technicalNotes: [
      "Changed topic-interest signal creation to use a fast bootstrap classification in-request and moved semantic upgrade to an async Celery task.",
      "Enqueued the topic-signal classification upgrade from `GeneratedTextViewSet.perform_create` after the text is already created.",
      "Removed the awaited list invalidation from the daily-card create flow so navigation can happen immediately after the create response.",
    ],
  },
  {
    id: "generate-flow-no-longer-blocked-by-auto-topic-prefetch",
    timestamp: "2026-03-19T22:47:54+01:00",
    title:
      "Opening the generate page no longer starts background topic-idea work that can delay text creation",
    summary:
      "The compact idea helper no longer auto-prefetches a topic as soon as the generate page opens. This avoids background helper work competing with the real create-text request on slower or more constrained environments, especially on mobile.",
    userHighlights: [
      "Generating a text is now prioritized over loading helper ideas in the background.",
      "The idea button still works, but only requests a topic once you actually tap it.",
      "This specifically protects the main text-creation flow from feeling delayed on mobile.",
    ],
    technicalNotes: [
      "Removed automatic topic-idea prefetching from `GenerateTextCard.tsx` and kept the helper request user-triggered.",
      "Added a Playwright regression that asserts the generate page does not call the topic-ideas endpoint until the idea button is clicked.",
      "This change is frontend-only and isolates the helper feature from the critical create-text request path.",
    ],
  },
  {
    id: "idea-button-return-flow-regression-test",
    timestamp: "2026-03-19T22:38:14+01:00",
    title:
      "Added a regression test for returning to the generator after opening a text",
    summary:
      "The generate-text flow now includes an end-to-end regression test that covers creating a text, opening its details page, returning to the main screen, and immediately using the compact idea helper again.",
    userHighlights: [
      "The return-to-generator flow is now explicitly protected by an automated browser test.",
      "This specifically guards against the idea button getting stuck in a loading state after navigation.",
      "The test exercises the real UI path instead of only backend suggestion logic.",
    ],
    technicalNotes: [
      "Added a Playwright regression in `reeprise-generation.spec.ts` for the details-page return flow and idea-button reusability.",
      "Updated repository guidance in `AGENTS.md` to require route-transition/state-transition regression coverage for user-facing changes when relevant.",
      "Executed the targeted Playwright scenario locally alongside the usual frontend checks.",
    ],
  },
  {
    id: "idea-button-no-longer-blocked-by-background-prefetch",
    timestamp: "2026-03-19T22:34:54+01:00",
    title:
      "The idea button no longer gets stuck in loading state after returning from a generated text",
    summary:
      "Returning from a generated text to the main page no longer makes the compact idea helper feel frozen while a background topic prefetch is still running. The card now keeps background idea loading separate from the button’s active click state.",
    userHighlights: [
      "The main generate-text card should feel responsive again after navigating back from a story.",
      "The `Idee` button no longer shows `Lädt` just because a silent background refill is happening.",
      "Background idea preloading still happens, but it no longer blocks the visible control.",
    ],
    technicalNotes: [
      "Split topic-idea background prefetching from the foreground button request state in `GenerateTextCard.tsx`.",
      "Kept the cached next-idea behavior while preventing the prefetch mutation from driving the button’s disabled/loading UI.",
      "No backend changes were needed for this fix.",
    ],
  },
  {
    id: "smart-topic-ideas-and-interest-map",
    timestamp: "2026-03-19T21:34:14+01:00",
    title:
      "Smart topic ideas now learn from your interests, and a new Interest Map shows your strong and weak areas",
    summary:
      "The small `Idee` helper on the generate-text card no longer depends on a fixed frontend list. It now pulls backend-generated topic ideas based on your accepted interests, and the new Interest Map page turns that same interest history into a visual landscape of strong areas, vocabulary depth, and blind spots.",
    userHighlights: [
      "The `Idee` button now serves personalized topic suggestions instead of reusing a tiny built-in pool.",
      "Accepted idea topics and your own manually chosen topics now shape future topic ideas together.",
      "The new Interest Map page shows which topic worlds you read a lot, where your vocabulary is deeper, and which adjacent areas are still underexplored.",
    ],
    technicalNotes: [
      "Added backend persistence for topic-interest signals and queued topic suggestions, plus a new `/generated_texts/topic-ideas/next/` endpoint.",
      "Updated generated-text creation so accepted idea topics are tracked separately from manual topics and can improve future recommendations.",
      "Added `/api/reeprise/interest-map/`, a new frontend route, and a first visualization layer that aggregates topic signals, text exposure, vocabulary usage, and recommendation areas.",
    ],
  },
  {
    id: "generate-text-topic-suggestions-for-blank-moments",
    timestamp: "2026-03-19T20:52:35+01:00",
    title: "Generate text now has a compact idea button for blank moments",
    summary:
      "The generate-text card now keeps topic help compact: a small inline idea button can instantly drop a random prompt into the field when you want something to read but do not want to think of a topic first.",
    userHighlights: [
      "You now get a tiny idea helper directly in the topic row instead of a large suggestion block.",
      "One tap fills the topic field with a random prompt immediately.",
      "The card stays visually light while still helping when your mind is blank.",
    ],
    technicalNotes: [
      "Added a small frontend topic-suggestion source and random-picker helper used by `GenerateTextCard.tsx`.",
      "Wired the inline helper to populate the existing topic field through the current form flow, without changing the backend API.",
      "Added translation keys for all new user-facing suggestion labels and a Vitest regression test for the random-picker helper.",
    ],
  },
  {
    id: "vocabulary-base-form-visible-without-more-grammar",
    timestamp: "2026-03-18T22:29:19+01:00",
    title: "Base forms are now visible without opening More grammar",
    summary:
      "Vocabulary cards now show a separate `Base form` field directly in the main card content instead of hiding it behind `More grammar`, so verb infinitives and other base forms stay visible without mixing them into the grammar toggle.",
    userHighlights: [
      "You can now see `Base form` immediately when it exists, without opening `More grammar`.",
      "The `More grammar` toggle is back to controlling grammar details only.",
      "This is especially useful for verb forms where the seen form and the infinitive are both important.",
    ],
    technicalNotes: [
      "Moved `Base form` rendering out of the expanded grammar sections in preview, trainer, progress, and generated-text vocabulary cards.",
      "Removed `Base form` from the generated-text card logic that decides whether the grammar toggle should appear.",
      "No backend changes were needed; this is a frontend rendering fix.",
    ],
  },
  {
    id: "vocabulary-verbs-keep-seen-form-as-main-word",
    timestamp: "2026-03-18T22:25:53+01:00",
    title:
      "Inflected verbs now stay as the main word while the base form gets its own field",
    summary:
      "Vocabulary cards no longer replace inflected non-nouns like `brille` with their dictionary form as the main label. Instead, the seen form stays primary and the base form is shown separately when available.",
    userHighlights: [
      "Verb forms you actually clicked in the text now stay visible as the main word on the card.",
      "Base forms like infinitives are still available, but in their own section instead of replacing the seen form.",
      "Noun cards keep their canonical article form behavior, so this change is specific to non-nouns.",
    ],
    technicalNotes: [
      "Changed frontend vocabulary-word selection so only noun-like entries promote the canonical dictionary form to the main label.",
      "Added a shared `base form` helper and surfaced it in preview, trainer, progress, and generated-text vocabulary cards.",
      "Added a regression test that keeps `brille` as the primary word while exposing `briller` as the separate base form.",
    ],
  },
  {
    id: "vocabulary-target-nouns-now-request-canonical-articles",
    timestamp: "2026-03-18T22:16:28+01:00",
    title:
      "New noun translations now request canonical articles on the translated side too",
    summary:
      "Freshly generated vocabulary entries now explicitly ask for canonical definite articles on translated noun labels as well, so cards should more consistently teach pairs like `le chat` and `die Katze` instead of dropping the translated article.",
    userHighlights: [
      "New noun cards should now more often include the article on both the source and translated side.",
      "Translated noun labels are now meant to stay in dictionary form instead of sentence-shaped forms.",
      "English targets still stay article-free, and non-nouns are unaffected.",
    ],
    technicalNotes: [
      "Extended the vocabulary translation prompt so `translation_word` must include the canonical definite article for nouns in article languages.",
      "Added prompt regression coverage for the new target-side article rule and for avoiding context-shaped translated articles.",
      "No migration or UI contract changes were required because the existing structured translation payload already supports article-bearing target labels.",
    ],
  },
  {
    id: "vocabulary-canonical-source-forms-with-articles",
    timestamp: "2026-03-18T21:53:30+01:00",
    title:
      "New vocabulary cards can separate canonical word forms from the exact text you clicked",
    summary:
      "Freshly generated vocabulary entries now store both the exact source form seen in the text and a reusable canonical source form, so noun cards can teach entries like `le chat` or `die Katze` while still preserving context-specific forms such as `chat` or `den Katzen` from the sentence.",
    userHighlights: [
      "New cards can now show the learnable base form with article for languages that teach nouns that way.",
      "When the clicked text is inflected or context-shaped, the card can still show the exact seen form separately.",
      "Plural and context notes can appear on the card without replacing the canonical base form.",
    ],
    technicalNotes: [
      "Added structured `translation_details` storage on `Vocabulary` for new entries, including source surface form, canonical dictionary form, lemma, plural form, context hint, target translation, and grammar fields.",
      "Changed translation generation to request strict JSON so source-language vocabulary labeling and target-language glossing are stored separately.",
      "Updated vocabulary preview, trainer, generated-text details, and progress views to prefer canonical source forms while keeping legacy entries on their old fallback path.",
    ],
  },
  {
    id: "word-translation-lexical-vs-context-separation",
    timestamp: "2026-03-18T21:43:18+01:00",
    title:
      "Word translations now ask the AI to keep dictionary meaning separate from sentence grammar",
    summary:
      "Vocabulary translation generation now explicitly asks for a reusable standalone word meaning on the first line and reserves context-shaped grammar for the translated sentence line, reducing cases where cards showed phrase fragments or expanded sentence-only renderings as if they were the word’s base meaning.",
    userHighlights: [
      "Word cards should more often show the actual learnable word meaning instead of a sentence-shaped phrase.",
      "Context-specific renderings such as case-marked or expanded phrases are now meant to stay in the translated sentence line.",
      "This should reduce confusing outputs like teaching a full contextual phrase when the saved item is only a single word.",
    ],
    technicalNotes: [
      "Rewrote the translation prompt contract to distinguish lexical word translation from sentence-level rendering explicitly.",
      "Added a concrete `matin` / `jeden Morgen` example inside the prompt so the model sees the expected separation directly.",
      "Added a backend regression test that asserts the prompt now forbids absorbing neighboring context words into the primary translation line.",
    ],
  },
  {
    id: "vocabulary-pronunciation-autoplay-resets-playhead",
    timestamp: "2026-03-18T20:54:13+01:00",
    title: "Vocabulary autoplay now restarts clips from the beginning reliably",
    summary:
      "When a newly generated pronunciation clip starts automatically, playback now resets to the beginning of the word first, which avoids the brief tail-end blip that could happen before immediate stop.",
    userHighlights: [
      "Automatically started pronunciation clips should now begin from the start of the word more reliably.",
      "The first auto-play after generation is less likely to produce only a tiny end-of-word sound.",
      "Manual replay still works as before and now also resets cleanly after an ended clip.",
    ],
    technicalNotes: [
      "Updated the shared pronunciation button to pause, reset `currentTime`, and reload the audio element before auto-play on a freshly available clip.",
      "Also reset the playhead before manual replay when the previous playback had already ended.",
      "This change is limited to frontend playback-state handling; no backend audio generation changes were required.",
    ],
  },
  {
    id: "vocabulary-pronunciation-pending-word-toast-removed",
    timestamp: "2026-03-18T20:51:14+01:00",
    title: "Pending pronunciation buttons no longer show a toast on tap",
    summary:
      "Pronunciation buttons for still-loading vocabulary no longer pop a toast when tapped; they now stay non-interactive until the word is ready while keeping their explanatory label.",
    userHighlights: [
      "Pending pronunciation buttons no longer interrupt the flow with a toast.",
      "The control still stays unavailable until the vocabulary item has finished loading.",
      "Ready vocabulary items keep the normal pronunciation generation and playback behavior.",
    ],
    technicalNotes: [
      "Removed the snackbar feedback from the shared pronunciation button’s pending-word branch.",
      "Restored the non-interactive pending state for vocabulary items whose own generation has not yet completed.",
      "Kept the existing accessibility label/title text that explains pronunciation becomes available once the word is ready.",
    ],
  },
  {
    id: "vocabulary-pronunciation-pending-word-toast-feedback",
    timestamp: "2026-03-18T20:48:58+01:00",
    title:
      "Pending vocabulary pronunciation buttons now explain why they cannot play yet",
    summary:
      "When a vocabulary item is still loading, tapping its pronunciation button now shows an immediate explanatory message instead of acting like a dead control.",
    userHighlights: [
      "Tapping pronunciation on a still-loading word now gives feedback instead of feeling broken.",
      "The actual pronunciation generation still waits until the vocabulary item is ready.",
      "Ready vocabulary items keep the normal spinner/play/pause behavior.",
    ],
    technicalNotes: [
      "Changed the shared pronunciation button so the pending-word state is clickable again.",
      "Instead of silently doing nothing, that state now shows a snackbar using the existing waiting-for-word translation key.",
      "The button remains truly disabled only while pronunciation generation itself is actively in progress.",
    ],
  },
  {
    id: "vocabulary-pronunciation-disabled-until-word-ready",
    timestamp: "2026-03-18T20:46:35+01:00",
    title:
      "Pronunciation buttons now wait for vocabulary generation to finish first",
    summary:
      "If a vocabulary item is still loading its translation/details, the pronunciation control now stays disabled instead of starting a pronunciation request too early and getting stuck in a spinner state.",
    userHighlights: [
      "You can only request pronunciation once the vocabulary item itself has finished loading.",
      "The button no longer enters a stuck loading state just because the word card was still pending.",
      "A tooltip/label now explains that pronunciation becomes available after the word is ready.",
    ],
    technicalNotes: [
      "Extended the shared pronunciation button to read the vocabulary item’s own `status` field in addition to pronunciation state.",
      "Blocked pronunciation requests while vocabulary status is not yet `COMPLETE`, unless an audio clip already exists.",
      "Added a dedicated translation key for the waiting-for-word state.",
    ],
  },
  {
    id: "vocabulary-pronunciation-button-now-shows-play-when-ready",
    timestamp: "2026-03-18T20:32:40+01:00",
    title:
      "Vocabulary pronunciation buttons now switch to a clear play icon when audio is ready",
    summary:
      "The pronunciation control now changes from its generation icon and loading spinner into a real play symbol as soon as the clip is available, making it clearer that the next tap starts playback.",
    userHighlights: [
      "Before audio exists, the button still looks like a pronunciation/generation control.",
      "While audio is being prepared, the button still shows the loading spinner.",
      "Once the clip is ready, the icon now becomes play, and while playing it becomes pause.",
    ],
    technicalNotes: [
      "Updated the shared `VocabularyPronunciationButton` state rendering without changing button layout or styling.",
      "Ready-but-not-playing state now renders a play icon instead of falling back to the generic pronunciation icon.",
      "Playing state now renders pause, while generation and loading behavior remain unchanged.",
    ],
  },
  {
    id: "vocabulary-pronunciation-now-uses-ash-voice",
    timestamp: "2026-03-18T20:27:46+01:00",
    title:
      "Vocabulary pronunciation now uses the same base voice as regular text audio",
    summary:
      "Vocabulary pronunciation clips now use the `ash` voice, matching the base voice used for normal single-voice text audio while still keeping language-specific pronunciation instructions.",
    userHighlights: [
      "Word pronunciation and regular text audio now share the same underlying voice choice.",
      "Non-English vocabulary still keeps native-language pronunciation guidance in the prompt.",
      "Podcast audio remains unchanged and still uses its separate multi-voice setup.",
    ],
    technicalNotes: [
      "Changed the vocabulary pronunciation voice profiles in `reeprise/tasks.py` so all supported languages now resolve to `ash`.",
      "Kept the per-language native-speaker instruction text intact so accent/pronunciation steering still happens through the TTS instructions.",
      "Updated the backend regression test to assert the new `ash` voice expectation for vocabulary pronunciation profiles.",
    ],
  },
  {
    id: "topic-previews-now-stay-expanded-up-to-twelve-words",
    timestamp: "2026-03-18T20:21:13+01:00",
    title: "Topic previews now stay fully visible up to twelve words",
    summary:
      "Topic and prompt previews on generated text cards and text details now remain untrimmed for substantially longer inputs, so medium-length prompts are easier to scan without opening the full prompt dialog.",
    userHighlights: [
      "Topic previews now stay fully visible until they exceed 12 words.",
      "Medium-length prompts should need the full-prompt action less often on both the card and the text details page.",
      "Very long prompts still collapse with an ellipsis and keep the full-prompt option.",
    ],
    technicalNotes: [
      "Raised the shared word-based topic-preview truncation threshold from 4 words to 12 words in `ReepriseTextCard.tsx`.",
      "Applied the same 12-word truncation limit in `GeneratedTextDetailsTextPane.tsx` so card and details behavior stay aligned.",
      "The full-prompt action remains conditional on actual truncation, so it only appears for prompts longer than the new threshold.",
    ],
  },
  {
    id: "first-person-main-character-inputs-now-treated-as-perspective",
    timestamp: "2026-03-18T20:14:02+01:00",
    title:
      "First-person main-character inputs are now treated as perspective, not as names",
    summary:
      "When users choose or type first-person inputs such as `Ich`, `I`, `moi`, or `yo`, generation now interprets that as a perspective signal instead of a literal character name, so texts and titles should stay in first person without inventing a character called `Ich` or `Moi`.",
    userHighlights: [
      "Choosing a first-person option should now lead to `I`/`je`/`ich`-style narration instead of a literally named protagonist.",
      "Typed first-person inputs are now handled the same way as select-based ones.",
      "Regular names like `Mia` or `Paul` still stay literal character names.",
    ],
    technicalNotes: [
      "Added backend normalization in `reeprise/tasks.py` that distinguishes between first-person perspective inputs and actual named characters before prompt construction.",
      "Applied the normalized main-character guidance consistently to title generation, body generation, topic guidance, and chapter continuation prompts.",
      "Added regression tests covering first-person pronoun handling and named-character preservation in the prompt layer.",
    ],
  },
  {
    id: "titles-now-commit-before-body-streaming",
    timestamp: "2026-03-18T19:53:59+01:00",
    title: "Generated text titles now appear earlier and stay fixed",
    summary:
      "Text generation now commits the final title before the body starts streaming, so users should see a stable title much earlier instead of waiting for enough streamed body content to infer one.",
    userHighlights: [
      "Generated texts should now reveal their title earlier during creation.",
      "Once the title appears, it should stay the same instead of changing later.",
      "The story body still streams in progressively, but title display no longer depends on parsing enough of the streamed text payload.",
    ],
    technicalNotes: [
      "Backend generation now calls a dedicated title-from-prompt step before the body stream begins and persists that committed title on the `GeneratedText` record while status is still pending.",
      "Body generation now runs in a body-only mode that is told to fit the already chosen title, instead of returning a fresh `title/story` payload for the main creation flow.",
      "Frontend pending-title display now switches from `Generating title…` to the committed title as soon as the polled `topic` value diverges from the cached raw prompt, and backend regression tests cover the committed-title flow.",
    ],
  },
  {
    id: "vocabulary-pronunciation-louder-normalized-audio",
    timestamp: "2026-03-18T19:42:19+01:00",
    title:
      "Vocabulary pronunciation clips are now saved at a louder listening level",
    summary:
      "Single-word pronunciation audio is now normalized to a noticeably louder output level before it is stored, so vocabulary clips should be easier to hear on phones and laptop speakers.",
    userHighlights: [
      "Vocabulary pronunciation clips should now sound louder without needing as much device-volume compensation.",
      "The change targets the saved audio itself, so it applies across the different vocabulary surfaces that reuse the same clip.",
      "Story and chapter audio behavior stay unchanged in this adjustment.",
    ],
    technicalNotes: [
      "Added a dedicated post-processing normalization step for vocabulary pronunciation audio in `reeprise/tasks.py`.",
      "The pronunciation pipeline now runs the generated clip through `ffmpeg` loudness normalization with a stronger target suited to short single-word audio.",
      "If normalization fails locally, the pipeline still falls back to saving the original generated clip.",
    ],
  },
  {
    id: "inline-generated-text-titles-arrive-with-first-ai-response",
    timestamp: "2026-03-17T21:38:13+01:00",
    title: "New text titles now arrive with the first AI response more often",
    summary:
      "Regular generated texts now ask the AI for both the title and the story in one structured response, which should make titles appear sooner instead of waiting for a separate fallback title-generation step after the full body finishes.",
    userHighlights: [
      "Story, dialogue, and knowledge texts should now show their generated title sooner in many cases.",
      "The app still keeps a fallback path if the AI returns only the story or malformed structured output.",
      "Podcast generation keeps the same structured title-and-story behavior it already used.",
    ],
    technicalNotes: [
      "Changed the shared generated-text output contract in `reeprise/tasks.py` so non-podcast texts now request JSON with `title` and `story`, matching the existing parser path.",
      "Kept `_extract_story_and_title_from_model_output(...)` and `_generate_title_from_story(...)` fallback logic so malformed or story-only responses still recover.",
      "Added backend regression tests covering the new inline-title fast path and the structured-output prompt contract for standard story generation.",
    ],
  },
  {
    id: "dev-startup-waits-for-redis-before-launching-services",
    timestamp: "2026-03-17T21:29:03+01:00",
    title:
      "Local startup now waits for Redis before launching the rest of the stack",
    summary:
      "The development launcher now starts Redis first and waits for a successful ping before bringing up Django, Celery, and the UI, which avoids local generated-text failures caused by backend services booting before Redis is ready.",
    userHighlights: [
      "After starting local development, text generation should no longer depend on restarting the stack a second time just because Redis was still booting.",
      "Generated-text, websocket, and background-task flows now have a more reliable local startup sequence.",
      "The Redis pane still starts first, but the rest of the stack now waits for a confirmed `PONG` before continuing.",
    ],
    technicalNotes: [
      "Changed `scripts/develop.sh` to launch the Redis Docker container before the other panes.",
      "Added a blocking readiness loop that polls `docker exec redis redis-cli ping` until Redis returns `PONG`.",
      "Only after Redis is confirmed ready does the script start Django, the Vite UI, Celery Beat, and the Celery Worker.",
    ],
  },
  {
    id: "struggle-word-text-create-removes-redundant-toast",
    timestamp: "2026-03-17T21:16:12+01:00",
    title:
      "Tricky-word text creation no longer shows a redundant start toast before redirect",
    summary:
      "When you generate a text from tricky words, the app now relies on the immediate redirect to the new text details page instead of also showing a separate start toast first.",
    userHighlights: [
      "Generating a tricky-word text now opens the new text directly without the extra `started` toast.",
      "The details page remains the place to follow generation progress after the redirect.",
      "Validation and failure feedback for the tricky-word flow still stay in place.",
    ],
    technicalNotes: [
      "Removed the success toast from the `handleGenerateStruggleText` path in `GenerateTextCard.tsx`.",
      "Kept the existing mutation, local-storage persistence, banner-hide behavior, and direct navigation to `/app/reeprise/$generatedTextId` unchanged.",
      "Scoped the change to the tricky-word creation flow only.",
    ],
  },
  {
    id: "generated-text-create-redirect-removes-redundant-toast",
    timestamp: "2026-03-17T20:42:30+01:00",
    title:
      "Text creation no longer shows a redundant `Generating Story` toast before redirect",
    summary:
      "Because the main generate-text flow now opens the new text details page immediately, the temporary `Generating Story` toast has been removed from that flow to avoid duplicate feedback during navigation.",
    userHighlights: [
      "Tapping generate text now redirects straight to the new text without showing the extra `Generating Story` toast first.",
      "The details page itself remains the main place to follow generation progress.",
      "Other creation feedback and error handling stay unchanged.",
    ],
    technicalNotes: [
      "Removed the immediate success-path toast from the active submit handler in `GenerateTextCard.tsx`.",
      "Kept the redirect-to-details behavior added to the main create flow, so the route transition now serves as the primary acknowledgement.",
      "Scoped the cleanup to the live Reeprise create card rather than unused legacy components.",
    ],
  },
  {
    id: "generated-text-create-redirect-to-details",
    timestamp: "2026-03-17T20:36:38+01:00",
    title: "Newly created texts now open their own details page immediately",
    summary:
      "Creating a text from the main Reeprise form now takes you straight to that text's details page, so you can follow generation progress and interact with the new text without staying on the overview screen.",
    userHighlights: [
      "After tapping generate text, the app now opens the new text directly instead of leaving you on the main list.",
      "The redirect also works while the text is still generating, so you can watch it progress from the details page.",
      "Daily practice and struggle-word creation keep their existing direct-to-details behavior.",
    ],
    technicalNotes: [
      "Updated the primary success path in `GenerateTextCard.tsx` to reuse the existing TanStack Router navigation pattern already used by other text-creation flows.",
      "Kept existing create-mutation cache priming and invalidation behavior so the details route can open immediately on the returned text ID.",
      "Preserved form-default persistence and draft cleanup before navigating to `/app/reeprise/$generatedTextId`.",
    ],
  },
  {
    id: "chapter-highlights-now-read-from-persisted-generation-words",
    timestamp: "2026-03-17T20:29:30+01:00",
    title:
      "Generated chapter highlights now come from chapter-owned selection data",
    summary:
      "Continued texts now keep their selected-word highlights by storing the generation word list on the chapter itself, so highlight behavior no longer depends on temporary page state surviving the navigation into the new chapter.",
    userHighlights: [
      "Words used to generate a continued text now stay highlighted even after reloading or reopening that chapter.",
      "The chapter no longer depends on leftover checkbox state from the previous page view to know which words were used.",
      "This makes the continuation flow more stable and predictable.",
    ],
    technicalNotes: [
      "Added persisted `generated_words` metadata to `Chapter` and returned it through the chapter serializer.",
      "Changed chapter creation and background generation to read the selected words from the saved chapter record instead of passing them only through transient client/task state.",
      "Updated generated-text details to highlight from `chapter.generated_words` and added a backend regression test covering persistence, enqueueing, and retrieval.",
    ],
  },
  {
    id: "generated-chapter-keeps-selected-word-highlights-after-navigation",
    timestamp: "2026-03-17T20:21:59+01:00",
    title: "Generated chapters now keep selected-word highlights after opening",
    summary:
      "When you generate a continued text from selected vocabulary, the new chapter now keeps those chosen words highlighted after the app navigates into the chapter instead of dropping the highlight state on arrival.",
    userHighlights: [
      "The words used to generate the new chapter stay visually highlighted when the chapter opens.",
      "The highlight survives the chapter navigation instead of disappearing immediately.",
      "This makes it easier to confirm which selected vocabulary was woven into the continuation.",
    ],
    technicalNotes: [
      "Stopped relying only on the live checkbox selection state for chapter highlights in `reeprise/$generatedTextId.tsx`.",
      "Added a one-shot pending chapter highlight payload that is attached to the freshly created chapter before navigation and consumed by the matching chapter view.",
      "Added a focused frontend unit test for the chapter highlight resolution rule.",
    ],
  },
  {
    id: "descriptive-topics-now-converted-to-target-language-concepts",
    timestamp: "2026-03-17T20:12:31+01:00",
    title:
      "Descriptive topic input should now be understood and rewritten in the learning language",
    summary:
      "Generated texts are now instructed to treat the topic field as semantic input first, so normal descriptive words like `Hundewiese` should be understood as a concept and expressed with the natural term in the target language instead of being echoed verbatim inside the generated text.",
    userHighlights: [
      "If you type a descriptive topic in another language, the generated text should now use the natural wording of that idea in the learning language.",
      "Raw topic words are less likely to leak directly into the text when they are not real names or brands.",
      "Names, brands, and other exact identifiers should still keep their original wording when that is the correct interpretation.",
    ],
    technicalNotes: [
      "Strengthened topic guidance in `reeprise/tasks.py` so the model must interpret topic input as meaning first rather than as text to preserve.",
      "Added an explicit rule that descriptive terms like `Hundewiese` should not reappear verbatim in the target-language text and should instead be replaced with the natural target-language concept.",
      "Extended backend prompt tests to cover this descriptive-topic translation behavior.",
    ],
  },
  {
    id: "title-selected-vocabulary-now-generates-chapters",
    timestamp: "2026-03-17T20:10:04+01:00",
    title: "Title-selected vocabulary is now included in chapter generation",
    summary:
      "Words added from a generated text title now count immediately when you create a new text from vocabulary, so chapter generation uses those title selections instead of silently ignoring them until the vocabulary list refresh catches up.",
    userHighlights: [
      "Words you add from the title now influence the next generated chapter right away.",
      "You no longer need to wait for the vocabulary sidebar to refresh before those title words are used.",
      "Body-word selection behavior stays the same, but chapter generation now includes both body and title selections reliably.",
    ],
    technicalNotes: [
      "Stopped deriving chapter-generation words only from the fetched vocabulary query in `reeprise/$generatedTextId.tsx`.",
      "Added an optimistic selected-word map so freshly created title vocabulary can be sent to `generate_chapter` before the list query refetch completes.",
      "Added a focused frontend unit test covering optimistic inclusion and deduplication of selected generation words.",
    ],
  },
  {
    id: "topic-input-normalized-instead-of-copied-literally",
    timestamp: "2026-03-17T20:07:45+01:00",
    title:
      "Topic input is now treated more as meaning guidance than literal wording",
    summary:
      "Generated texts are now instructed to follow the meaning of the topic field without blindly reusing unusual raw wording, so descriptive inputs like `HundeWiese` should be normalized into natural target-language phrasing while real names and brands keep their exact wording.",
    userHighlights: [
      "Oddly formatted topic input should no longer be copied into titles and text just because it was typed that way.",
      "Descriptive topic phrases are now meant to be rewritten into natural wording in the learning language.",
      "Proper names, brands, and similar exact identifiers are still supposed to stay unchanged when that is the correct reading.",
    ],
    technicalNotes: [
      "Extended generated-text topic guidance in `reeprise/tasks.py` to block literal reuse of camel case, fused compounds, and awkward raw topic wording unless the topic is clearly a proper noun, brand, or name.",
      "Changed the request wording inside the generation prompt from a direct topic request to raw meaning guidance, reducing pressure on the model to echo user input verbatim.",
      "Applied the same normalization rule to chapter continuation prompts and added backend regression tests for the `HundeWiese`-style case.",
    ],
  },
  {
    id: "vocabulary-translation-fragment-fallback",
    timestamp: "2026-03-16T21:27:41+01:00",
    title:
      "Vocabulary cards now avoid showing broken translation fragments as standalone meanings",
    summary:
      "When a vocabulary translation only yields a context-bound fragment like a trailing compound stem, the app now falls back to the sentence-level translation instead of presenting that fragment as if it were a complete standalone meaning.",
    userHighlights: [
      "Vocabulary cards no longer show obvious broken fragments like a trailing-hyphen translation as the main meaning.",
      "In those edge cases, the card now uses the fuller translated sentence so the visible translation stays understandable.",
      "The same fallback applies consistently across text details, trainer, and vocabulary progress views.",
    ],
    technicalNotes: [
      "Added a shared primary-translation resolver on top of normalized vocabulary translation details.",
      "Introduced a narrow fragment heuristic for obvious non-standalone translations such as leading or trailing hyphen forms.",
      "Updated vocabulary UIs to avoid duplicating the translated sentence when it is already used as the main translation label, and added a frontend regression test for the fragment fallback.",
    ],
  },
  {
    id: "pronunciation-button-self-polling-reliability",
    timestamp: "2026-03-16T21:18:34+01:00",
    title:
      "Pronunciation buttons now track audio readiness more reliably on their own",
    summary:
      "The pronunciation control now polls its own vocabulary entry after generation starts, which avoids stuck loading states and makes the ready-to-play transition more dependable across pages and mobile browsers.",
    userHighlights: [
      "Pronunciation requests are less likely to stay stuck in loading when the surrounding page cache is slow to refresh.",
      "The button can now detect when the clip becomes ready even if the parent page does not re-render immediately.",
      "Mobile pronunciation flow is more stable because the audio button no longer depends only on page-level query timing.",
    ],
    technicalNotes: [
      "Moved readiness tracking into the shared pronunciation button via local pronunciation status/audio state.",
      "Added lightweight polling of `/api/reeprise/vocabulary/<id>/` while pronunciation is pending and no audio URL is present.",
      "Kept parent-query invalidation in place, but no longer rely on it as the only source of readiness updates.",
    ],
  },
  {
    id: "pronunciation-autoplay-when-audio-becomes-ready",
    timestamp: "2026-03-16T21:03:21+01:00",
    title:
      "Pronunciation clips now try to start automatically once generation finishes",
    summary:
      "After you request a vocabulary pronunciation, the app now attempts to start playback automatically as soon as the generated audio file becomes available, reducing the need for a second tap when the browser allows it.",
    userHighlights: [
      "Requested pronunciation clips now try to begin playing automatically once loading finishes.",
      "If the browser allows delayed playback, the extra second tap is no longer needed.",
      "Browsers that block async autoplay still fall back to the existing manual play button.",
    ],
    technicalNotes: [
      "Restored best-effort autoplay in the shared pronunciation button by tracking an explicit `shouldAutoplayWhenReady` intent across the generation round-trip.",
      "The button now attempts `audio.play()` once a generated audio URL appears, then clears loading state whether playback succeeds or the browser blocks it.",
      "The fallback remains non-destructive: if autoplay is denied, the clip stays ready for manual replay.",
    ],
  },
  {
    id: "text-details-read-marker-text-scrub",
    timestamp: "2026-03-16T21:00:35+01:00",
    title: "Story audio now includes a draggable read marker in the text",
    summary:
      "The text details page now shows a small read marker above the currently estimated spoken word, so you can drag it through the story and jump playback closer to the section you want to hear.",
    userHighlights: [
      "During story playback, the currently read word now stays visually marked in the text.",
      "A small marker appears above that word and can be dragged through the story to reposition playback.",
      "Word tapping for vocabulary still works; the new marker is the dedicated audio-scrub control.",
    ],
    technicalNotes: [
      "Extended the generated-text details text renderer with a separate read-along state layered on top of existing vocabulary highlights.",
      "Mapped audio time to rendered word positions using the existing text-span DOM so playback can be repositioned from the story body without backend timestamp dependencies.",
      "Added a frontend regression test for word-boundary extraction and active read-along word marking.",
    ],
  },
  {
    id: "iphone-pronunciation-loading-state-stability",
    timestamp: "2026-03-16T20:54:28+01:00",
    title:
      "Pronunciation buttons now stay in loading state until audio is actually ready",
    summary:
      "Pronunciation generation now keeps its loading state visible until the audio file is available, which avoids a misleading quick spinner drop on slower devices and mobile browsers like iPhone Safari.",
    userHighlights: [
      "After requesting pronunciation, the button now keeps showing loading until the clip is really ready.",
      "Vocabulary cards on text details and other vocabulary views now refresh their pronunciation state more reliably.",
      "The button no longer assumes the clip can auto-play after background generation.",
    ],
    technicalNotes: [
      "Added local optimistic waiting state inside the shared pronunciation button so UI does not depend only on the short enqueue request lifetime.",
      "Cleared that waiting state only when pronunciation becomes `COMPLETE`/`FAILED` or an audio URL is present.",
      "Extended pronunciation mutation invalidation to include generated-text details queries in addition to vocabulary list queries.",
    ],
  },
  {
    id: "text-details-vocabulary-cards-pronunciation-button",
    timestamp: "2026-03-16T20:42:55+01:00",
    title: "Text details vocabulary cards now include pronunciation replay",
    summary:
      "The pronunciation button is now also available on vocabulary cards inside the text details page, so saved words can be replayed there without leaving the reading flow.",
    userHighlights: [
      "Vocabulary cards on text details now show the same pronunciation button as the main vocabulary views.",
      "You can generate and replay a word’s pronunciation directly from the text details sidebar/cards.",
      "Using the pronunciation button does not trigger the card’s jump/select behavior.",
    ],
    technicalNotes: [
      "Reused the shared `VocabularyPronunciationButton` inside the generated-text details vocabulary card header.",
      "Wrapped the pronunciation control in the existing `data-vocabulary-control` guard so card-level click handlers ignore audio interactions.",
      "No backend changes were required for this follow-up; it uses the same pronunciation API added previously.",
    ],
  },
  {
    id: "vocabulary-on-demand-pronunciation-audio",
    timestamp: "2026-03-16T20:27:24+01:00",
    title: "Saved vocabulary can now generate pronunciation audio on demand",
    summary:
      "Added words now show a small audio button next to the vocabulary item so you can request a pronunciation clip when needed and replay it once it is ready.",
    userHighlights: [
      "Saved vocabulary entries now include an audio icon directly next to the word.",
      "The first tap generates a pronunciation clip for that word on demand.",
      "Once the clip is ready, the same button plays and pauses the pronunciation.",
    ],
    technicalNotes: [
      "Extended `Vocabulary` with stored pronunciation audio and a dedicated pronunciation generation status.",
      "Added authenticated vocabulary speech and pronunciation-generation endpoints plus a background task that synthesizes standalone word audio.",
      "Updated vocabulary list polling and shared vocabulary UI rows so the button can reflect pending, ready, and playback states.",
    ],
  },
  {
    id: "translation-prompt-close-but-idiomatic-alignment",
    timestamp: "2026-03-16T20:08:25+01:00",
    title:
      "Sentence translations now stay closely mappable without becoming literal-sounding",
    summary:
      "Translations are now instructed to stay closely aligned with the original sentence so learners can map words, grammar, and meaning across both versions more reliably, while still avoiding literal target-language phrasing that would sound wrong or teach the wrong instinct.",
    userHighlights: [
      "Translations should now be easier to compare with the source sentence word by word.",
      "Sentence structure and information order are meant to stay close enough that you can check which part matches which part.",
      "If a literal rendering would sound wrong in the target language, the translator is now told to make only the smallest necessary adjustment instead of teaching an unnatural phrase.",
    ],
    technicalNotes: [
      "Refactored translation prompt construction in `reeprise/tasks.py` into a dedicated helper so prompt policy can be tested directly.",
      "Added an explicit learner-mapping rule: preserve meaning, structure, specificity, and sentence order where the target language allows it, so users can map source words and grammar onto the translation more confidently.",
      "Added a counter-rule that blocks false friends, literal calques, and wrong-sounding target-language phrasing, so closeness does not come at the cost of correctness.",
      "Added backend regression tests covering this close-but-idiomatic translation contract and the deterministic sentence-translation guidance.",
    ],
  },
  {
    id: "generated-text-prompt-quality-rewrite",
    timestamp: "2026-03-16T19:21:06+01:00",
    title:
      "Generated learning texts now aim for more natural and native-sounding phrasing",
    summary:
      "The AI text-generation prompt was rewritten to reduce awkward translated-sounding sentences, relax overly rigid wording constraints, and favor clearer idiomatic learner content across stories, dialogues, podcasts, and daily practice texts.",
    userHighlights: [
      "Generated texts should now read more naturally instead of sounding forced or overly translated.",
      "Topics are still followed, but the AI is less likely to insert topic words in unnatural ways just to satisfy the prompt.",
      "Length guidance is now more flexible, which should improve rhythm and sentence flow.",
    ],
    technicalNotes: [
      "Refactored backend prompt construction in `reeprise/tasks.py` into modular prompt-builder helpers instead of one large mixed instruction block.",
      "Removed contradictory topic instructions, added explicit native-sounding quality rules, and changed rigid exact word-count enforcement into an acceptable target range.",
      "Added backend regression tests to cover the new prompt policy, podcast JSON output contract, and struggle-word prompt handling.",
    ],
  },
  {
    id: "generated-text-page-loop-fixes-and-e2e-smoke-pass",
    timestamp: "2026-03-15T09:15:13+01:00",
    title:
      "Fixed reeprise page render loops and verified generated-text creation end to end",
    summary:
      "The reeprise home page and generated-text details page no longer fall into React update-depth loops during text creation, and the create-text flow is now verified with a passing Playwright smoke test.",
    userHighlights: [
      "The main text page no longer gets stuck in a render loop before submission.",
      "Opening a generated text detail page no longer retriggers the old vocabulary-selection loop.",
      "The critical flow from creating a text to opening its completed details page now passes as an automated smoke check.",
    ],
    technicalNotes: [
      "Made `GenerateTextCard` form default hydration idempotent by hydrating once per defaults snapshot instead of reapplying values on every render.",
      "Guarded the empty-state reset logic in `VocabularyPreviewCard` so it does not keep resetting already-empty state on every render when no vocabulary is available.",
      "Guarded `selectedVocabularyIds` synchronization in `$generatedTextId.tsx` and tightened the new Playwright smoke test to seed valid create-form defaults and submit the correct form.",
    ],
  },
  {
    id: "generated-text-smoke-test-and-critical-flow-policy",
    timestamp: "2026-03-15T08:51:36+01:00",
    title:
      "Generated text creation now has a dedicated smoke test and repo policy",
    summary:
      "The repo now treats generated-text creation as a critical flow that must be verified with an executed regression test, helping catch broken background generation earlier during development.",
    userHighlights: [
      "The most important text-generation flow now has automated smoke coverage.",
      "Development checks are now expected to verify real generation behavior, not just builds.",
      "Broken worker or Redis setups should be caught earlier before UI changes are considered done.",
    ],
    technicalNotes: [
      "Added a Playwright smoke test that logs in, creates a generated text, polls the generated-text API until completion, and opens the details page.",
      "Updated `AGENTS.md` with a critical-flow testing policy for generated-text creation and a required first-pass check of Django, Celery, Beat, Redis, and text status when generation is stuck.",
      "This change is intended to catch failures like `PENDING` texts caused by unavailable background workers.",
    ],
  },
  {
    id: "toast-duration-standardized-two-seconds",
    timestamp: "2026-03-15T08:06:41+01:00",
    title: "Standardized toast timing to a shorter transient duration",
    summary:
      "The app toast now uses a standard short success duration again, so brief confirmations disappear faster and feel closer to common UX patterns.",
    userHighlights: [
      "Short confirmations like `Word added` now fade out faster.",
      "The toast still remains visible long enough to read at a glance.",
      "This change only affects timing, not toast styling or placement.",
    ],
    technicalNotes: [
      "Reduced the `SnackBar.tsx` auto-close timer from 2600ms to 2000ms.",
      "This keeps the toast aligned with common transient success-toast timing.",
    ],
  },
  {
    id: "agents-german-umlaut-copy-rule",
    timestamp: "2026-03-15T08:03:53+01:00",
    title: "Added a repo rule to always use real German umlauts in copy",
    summary:
      "The repository instructions now explicitly require real German characters in user-facing text, so copy should use `ü`, `ö`, `ä`, and `ß` whenever the correct spelling needs them.",
    userHighlights: [
      "German UI copy should now consistently use proper spellings like `hinzugefügt`.",
      "ASCII fallback forms like `ue`, `oe`, `ae`, or `ss` are now explicitly discouraged when the proper character is required.",
      "This is an instruction change for future edits rather than a product behavior change.",
    ],
    technicalNotes: [
      "Updated `AGENTS.md` with a dedicated German copy rule for umlauts and `ß`.",
      "The rule applies to future user-facing text changes across the repo.",
    ],
  },
  {
    id: "word-added-toast-german-umlaut-fix",
    timestamp: "2026-03-15T08:02:42+01:00",
    title: "Corrected the German `Word added` toast wording",
    summary:
      "The German vocabulary-success toast now uses the proper umlaut spelling in `Wort hinzugefügt` instead of the ASCII fallback.",
    userHighlights: [
      "German toast copy now reads `Wort hinzugefügt`.",
      "The English toast stays `Word added`.",
      "This only changes wording, not toast behavior.",
    ],
    technicalNotes: [
      "Updated the `word_added_toast` German translation value in `translations.json`.",
      "No component logic or toast timing changed in this adjustment.",
    ],
  },
  {
    id: "generated-text-title-highlight-no-outline-ring",
    timestamp: "2026-03-15T08:00:03+01:00",
    title:
      "Title-word highlights now use the same fill-only style as body text",
    summary:
      "Highlighted vocabulary in generated text titles now matches the main reading text exactly, with no extra outline ring added on headline clicks.",
    userHighlights: [
      "Headline words now highlight with the same simple green fill as words in the story body.",
      "Clicking a title word no longer adds an extra outline stroke around that word.",
      "The title and body now feel visually identical when showing saved vocabulary.",
    ],
    technicalNotes: [
      "Removed the title-only flash ring state from `GeneratedTextDetailsTextPane`.",
      "Title words now keep only the persistent fill highlight, matching the saved-vocabulary treatment used in the main text.",
      "No backend changes.",
    ],
  },
  {
    id: "generated-text-title-click-no-first-body-word-highlight",
    timestamp: "2026-03-15T07:48:55+01:00",
    title: "Title clicks no longer highlight the first word in the body text",
    summary:
      "Adding vocabulary from a generated text headline no longer leaks into positional body highlights, so clicking any headline word keeps the effect scoped to the headline.",
    userHighlights: [
      "Clicking the first word in a title no longer highlights the first word of the story.",
      "Headline vocabulary still gets added correctly and keeps its headline highlight.",
      "Body highlights now only come from words that were actually added from the body text.",
    ],
    technicalNotes: [
      "Filtered title-origin vocabulary entries out of `usages`-based body highlight positions in `GeneratedTextDetailsTextPane`.",
      "Used the resolved title sentence as the boundary for deciding whether a saved vocabulary belongs to the headline or the reading body.",
      "Applied the same exclusion in both chapter and non-chapter text rendering paths.",
    ],
  },
  {
    id: "generated-text-title-highlights-persist-after-refresh",
    timestamp: "2026-03-15T07:47:47+01:00",
    title: "Title-word highlights now persist after page refresh",
    summary:
      "Words added from a generated text title now stay highlighted after reloading the page, matching the persistence users already expect from saved vocabulary inside the body text.",
    userHighlights: [
      "Words you added from the title stay visibly highlighted after a refresh.",
      "The title keeps the same saved-vocabulary visual state as the text body.",
      "Refreshing no longer makes title-added words look as if they were never added.",
    ],
    technicalNotes: [
      "Rebuilt title highlights in `GeneratedTextDetailsTextPane` from saved vocabulary records instead of relying only on local click state.",
      "Matched title-origin vocabulary by comparing saved `sentence` values against the resolved generated title.",
      "Also restored the persisted `title-only` classification in the details route so refreshes do not reintroduce body-highlight side effects.",
    ],
  },
  {
    id: "generated-text-title-highlight-style-matched",
    timestamp: "2026-03-14T08:24:26+01:00",
    title: "Title-word highlights now match text-word highlights",
    summary:
      "Words added from the generated text title now use the same green highlight treatment as added vocabulary inside the main text, so both interaction zones feel consistent.",
    userHighlights: [
      "Added vocabulary highlights now look the same in the title and the text body.",
      "Title clicks still behave the same and remain title-local.",
      "The green highlight fill no longer looks visually different just because the word was clicked in the title.",
    ],
    technicalNotes: [
      "Adjusted title-word button classes in `GeneratedTextDetailsTextPane.tsx` to use the same conditional background logic as body highlight spans.",
      "Stopped forcing `bg-transparent` in highlighted title states so the green fill can render identically.",
      "No backend changes.",
    ],
  },
  {
    id: "word-added-toast-shared-parent-handler",
    timestamp: "2026-03-14T08:18:56+01:00",
    title:
      "`Word added` toast now fires from the shared add-vocabulary handler",
    summary:
      "The vocabulary success toast is now triggered from the common parent callback instead of the individual title/text click handlers, so `Word added` appears consistently no matter where the user taps.",
    userHighlights: [
      "Tapping a word in the title still shows `Word added`.",
      "Tapping a word in the body text now shows the same `Word added` toast.",
      "The toast behavior is now consistent across both add-word entry points.",
    ],
    technicalNotes: [
      "Removed direct snackbar calls from the title/body add-word code paths inside `GeneratedTextDetailsTextPane.tsx`.",
      "Moved the toast trigger into the shared `onVocabularyCreate(...)` handler in `reeprise/$generatedTextId.tsx`.",
      "This centralizes add-word feedback behind one callback so title and text interactions cannot drift apart.",
    ],
  },
  {
    id: "generated-text-title-click-no-body-mis-highlight",
    timestamp: "2026-03-14T08:17:58+01:00",
    title: "Title clicks now stop causing wrong body-word highlights",
    summary:
      "Adding vocabulary from the generated text title now keeps the title interaction local, so the reading area no longer highlights an unrelated body word after a title click.",
    userHighlights: [
      "Clicking a title word still adds that word to your vocabulary list.",
      "The clicked title word keeps its title highlight behavior.",
      "The main text no longer highlights a mismatched body word after a title-based vocabulary add.",
    ],
    technicalNotes: [
      "Split generated-text vocabulary creation events by source: `title` versus `text`.",
      "Updated the details page to exclude title-origin vocabulary IDs from body-word highlight derivation.",
      "Body highlights still respond normally for vocabulary created from direct text-span clicks.",
    ],
  },
  {
    id: "word-added-toast-mobile-visibility-tune",
    timestamp: "2026-03-14T08:15:30+01:00",
    title:
      "Compact `Word added` toast now sits higher and stays visible longer",
    summary:
      "The compact vocabulary-success toast is now positioned near the top on mobile and remains visible a bit longer, so the short `Word added` confirmation is easier to notice.",
    userHighlights: [
      "On mobile, `Word added` now appears near the top-center instead of low in the screen.",
      "The toast still stays compact with no extra helper text.",
      "The message remains visible slightly longer before fading out.",
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to anchor the snackbar near the top on mobile while keeping the lower-right placement on larger screens.",
      "Removed the width-constraining wrapper behavior and switched the message pill to a natural-content layout.",
      "Extended the auto-close timer from 2000ms to 2600ms to improve visibility for very short success messages.",
    ],
  },
  {
    id: "compact-word-added-toast",
    timestamp: "2026-03-14T08:11:24+01:00",
    title: "Vocabulary add feedback is now a compact `Word added` toast",
    summary:
      "Adding a vocabulary word from generated text now shows a much smaller success toast with only the final confirmation message, instead of a larger styled card with extra labels.",
    userHighlights: [
      "The toast is now compact and only says `Word added`.",
      "It appears after the word was actually added, not before the request finishes.",
      "Mobile still keeps the toast above the sticky bottom action area.",
    ],
    technicalNotes: [
      "Simplified `SnackBar.tsx` into a small rounded weather-tinted pill while preserving the mobile-safe placement and z-index.",
      "Replaced the previous pre-request `Generating Vocabulary.` notifications in `GeneratedTextDetailsTextPane.tsx` with a post-success `word_added_toast` notification.",
      "Removed the now-unused secondary snackbar translation keys and added `word_added_toast` to `translations.json`.",
    ],
  },
  {
    id: "snackbar-matched-daily-card-layout",
    timestamp: "2026-03-14T08:08:01+01:00",
    title: "Success toast now matches the daily card layout more closely",
    summary:
      "The success toast now follows the same wide sky-card composition as the daily text card, with capsule status copy, serif message styling, and integrated cloud shapes.",
    userHighlights: [
      "The toast now looks like a compact version of the daily card instead of a separate widget style.",
      "Status feedback is reduced to a simple `All worked` capsule and the actual action message.",
      "Mobile still keeps the toast above the sticky bottom action area.",
    ],
    technicalNotes: [
      "Reworked `SnackBar.tsx` from a centered badge layout into a wide weather-card panel with left status pill, serif content, and right-side timestamp accent.",
      "Adjusted cloud placement and overlay treatment to mirror the daily card structure more directly.",
      "Added translation key `snackbar_now` for the small time accent label.",
    ],
  },
  {
    id: "weather-card-inspired-success-toast",
    timestamp: "2026-03-14T08:02:41+01:00",
    title: "Success toasts now borrow the daily weather-card look",
    summary:
      "Feedback toasts now use the same bright-sky and cloud language as the daily text card, with a simpler success message and mobile-safe placement above sticky actions.",
    userHighlights: [
      "Toasts now feel visually connected to the daily text card instead of using a separate brand-style bubble.",
      "The toast no longer shows `Uxhulu`; it just confirms the action in a cleaner success layout.",
      "On mobile, the toast still stays above the fixed bottom action area so the message remains readable.",
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to use a weather-card-inspired sky gradient, cloud clusters, and a compact success badge with check icon.",
      "Removed the extra brand label and replaced it with a minimal `All worked` confirmation line under the main message.",
      "Kept the snackbar at `z-40` with safe-area-aware bottom spacing so it clears the mobile chapter-generation CTA.",
    ],
  },
  {
    id: "generated-text-details-story-stream-restored",
    timestamp: "2026-03-14T08:01:23+01:00",
    title:
      "Generated text details now stream story text again without JSON flashes",
    summary:
      "The generated text details page now keeps the safe header/body phases and restores the live reading effect by streaming partial story text directly from the structured `story` field.",
    userHighlights: [
      "Story text now grows live again while generation is running.",
      "The main title still stays on `Titel wird erstellt …` until a real title is ready.",
      "The reading area still never shows raw JSON syntax or `title` payload text.",
    ],
    technicalNotes: [
      "Adjusted the generated-text resolver to allow partial extraction of the structured `story` string while a payload is still incomplete.",
      "Kept title parsing strict so incomplete title transport data does not leak into the header.",
      "Safe streaming still hides raw JSON framing and only renders decoded story characters.",
    ],
  },
  {
    id: "cloud-toast-snackbar-refresh",
    timestamp: "2026-03-14T07:55:33+01:00",
    title: "Toasts now float in a cloud-style bubble above mobile actions",
    summary:
      "Feedback toasts now use a softer cloud-like design and sit above sticky mobile controls, so status messages stay visible without clashing with the reading layout.",
    userHighlights: [
      "Toast messages now appear in a rounded cloud-style bubble that matches the app's lighter editorial look.",
      "On mobile, the toast now clears the sticky bottom action area instead of getting hidden behind it.",
      "The updated toast stays centered on small screens and moves back to the lower-right corner on larger screens.",
    ],
    technicalNotes: [
      "Redesigned `SnackBar.tsx` with layered circular shapes and a light sky-tinted gradient instead of the previous plain rectangular slab.",
      "Raised the snackbar stacking order to `z-40` and added mobile-safe bottom padding so it clears the fixed chapter-generation CTA.",
      "Removed shadow-based styling from the toast refresh to stay aligned with repository frontend guidance.",
    ],
  },
  {
    id: "generated-text-details-explicit-stream-phases",
    timestamp: "2026-03-14T07:49:22+01:00",
    title: "Generated text details now use explicit safe streaming phases",
    summary:
      "The generated text details page now renders generation through explicit title/story phases so the header no longer falls back to the topic and the body never shows raw JSON transport data.",
    userHighlights: [
      "While a title is not ready yet, the page now keeps the neutral `Titel wird erstellt …` header instead of showing the typed topic as the main title.",
      "The body stays empty until a clean story is available, then streams only story text.",
      "JSON `title` and `story` payload text no longer flashes inside the reading area during generation.",
    ],
    technicalNotes: [
      "Added a shared `resolveGeneratedTextContent(...)` helper with explicit phases: `pending_no_title`, `pending_title_only`, `pending_story`, and `complete`.",
      "Updated `GeneratedTextDetailsTextPane` to resolve one header state and one body state instead of independently mixing topic, websocket chunks, and final saved output.",
      "Pending-state story rendering now reads only a closed `story` string field from structured payloads; otherwise it renders nothing until the content is safe.",
    ],
  },
  {
    id: "generated-text-details-stable-generation-copy",
    timestamp: "2026-03-14T07:18:36+01:00",
    title:
      "Generated text details now hide raw JSON and keep a stable title while loading",
    summary:
      "The generated text details page now uses the same loading copy as the text cards, never exposes raw JSON during streaming, and transitions smoothly into the finished text.",
    userHighlights: [
      "The details header now shows `Titel wird erstellt …` until a real generated title is available.",
      "Users no longer see raw JSON or code fences flash in the main text while generation is still in progress.",
      "The loading-to-finished transition now keeps the same title/body rendering path for a smoother read.",
    ],
    technicalNotes: [
      "Extracted shared generated-text parsing helpers so cards and the details page resolve AI title and story content the same way.",
      "Updated the details page to derive its title from parsed AI output instead of the generic prompt topic during pending state.",
      "Suppressed incomplete JSON/code-fence payloads in streamed text parsing so only valid story content is rendered.",
    ],
  },
  {
    id: "daily-and-tricky-text-origins-separated",
    timestamp: "2026-03-14T06:37:56+01:00",
    title: "Daily texts and tricky-word texts now stay separate",
    summary:
      "The daily learning card now creates its own daily text instead of reusing the tricky-word practice flow, so both cards can support different learning goals.",
    userHighlights: [
      "The daily card now opens a separate daily learning text just for that nudge.",
      "The tricky-word card keeps its own practice texts focused on hard words.",
      "Reading one card no longer marks the other card as already generated.",
    ],
    technicalNotes: [
      "Added explicit `GeneratedText.origin` values so the app can distinguish manual, daily, and tricky-word texts without guessing from content.",
      "Changed `/generated_texts/daily-practice/` to create `DAILY_NUDGE` texts that use recent interests and vocabulary context instead of the struggle-word generator.",
      "Updated `DailyPracticeCallout` to filter daily-card history by origin and added backend regression tests for same-day separation.",
    ],
  },
  {
    id: "daily-callout-keep-all-generated-daily-cards-neutral-history",
    timestamp: "2026-03-03T21:09:56+01:00",
    title:
      "Daily callout now keeps all generated daily cards as neutral history",
    summary:
      "All generated daily cards now remain visible in the top daily section, while only one main card keeps the weather-driven style and the rest are neutral compact history cards.",
    userHighlights: [
      "Older generated daily cards no longer disappear from the daily section.",
      "Only one main card uses the full weather/daylight visual treatment.",
      "All other daily cards are shown in compact neutral style and stay clickable.",
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` to build a sorted list of all daily-practice texts and render non-featured items in a neutral compact list.",
      "Introduced `featuredDailyTextId` to ensure exactly one weather-styled main card at a time.",
      "Updated `dailyTextPreviousBadge` copy for multi-item history context.",
    ],
  },
  {
    id: "daily-callout-keep-yesterday-minimal-card",
    timestamp: "2026-03-03T21:04:02+01:00",
    title:
      "Restructured daily callout to keep yesterday's card in minimal mode",
    summary:
      "When no new daily text is done yet today, yesterday's daily text now remains visible as a compact neutral card below the active daily card.",
    userHighlights: [
      "Yesterday's daily text no longer disappears from the top area.",
      "The previous card is intentionally compact and neutral.",
      "You can still open yesterday's text directly from this minimal card.",
    ],
    technicalNotes: [
      "Added previous-day daily-text detection in `DailyPracticeCallout` using dated daily-practice heuristics.",
      "Rendered a new compact neutral card branch for the latest pre-today daily text when today is not done.",
      "Added translation key `dailyTextPreviousBadge` for the compact card label.",
    ],
  },
  {
    id: "directional-srs-vocabulary-trainer",
    timestamp: "2026-03-02T20:49:20+01:00",
    title: "Vocabulary trainer now tracks SRS in both directions",
    summary:
      "The trainer now stores separate spaced-repetition progress for Target→Native and Native→Target, and introduces reverse cards only after forward progress is stable.",
    userHighlights: [
      "Your answers now count for the exact card direction you practiced.",
      "Reverse direction (Native→Target) appears gradually after you build a solid forward streak.",
      "Trainer cards now show a clear direction label so each prompt is unambiguous.",
    ],
    technicalNotes: [
      "Backend: `VocabularyUserProgress` now stores `direction`; review actions persist streak/last_tested per direction; trainer state now stores active `review_direction`.",
      "Trainer queue logic now selects due cards direction-aware, unlocks reverse cards after a forward streak threshold, and returns per-card `review_direction`.",
      "Frontend trainer sends `review_direction` in review mutations and renders headers/subheaders from per-card direction data.",
    ],
  },
  {
    id: "weather-location-smart-search-dropdown",
    timestamp: "2026-03-02T20:38:15+01:00",
    title: "Added smart weather-location search with dropdown suggestions",
    summary:
      "Settings now provides city autocomplete via backend geocoding suggestions, so users can pick valid locations faster and auto-fill country code.",
    userHighlights: [
      "Typing at least 2 characters in the city field now opens a location dropdown.",
      "Selecting a suggestion fills both city and country code automatically.",
      "Supports common variants like Vienna/Wien through geocoding-backed search.",
    ],
    technicalNotes: [
      "Backend: added authenticated `GET /api/reeprise/weather/location-suggestions/` endpoint with Open-Meteo geocoding, optional country filter, fallback, and caching.",
      "Frontend: added `useWeatherLocationSuggestions` hook and integrated dropdown UI in `settings.tsx`.",
      "Added i18n keys for suggestion loading and empty states in `translations.json`.",
    ],
  },
  {
    id: "settings-weather-location-controls-ui",
    timestamp: "2026-03-02T20:29:29+01:00",
    title: "Added weather location controls in Settings",
    summary:
      "Users can now set city and country code in Settings so the daily card weather theme uses their chosen location.",
    userHighlights: [
      "New settings section for daily-card weather location.",
      "Save city and 2-letter country code directly from the app.",
      "Inline saving, success, and error feedback for location updates.",
    ],
    technicalNotes: [
      "Updated `settings.tsx` with controlled `weather_city` and `weather_country_code` inputs wired to `useUserSettingsUpdate`.",
      "Added new translation keys in `translations.json` for weather-location labels, hints, and status messages.",
      "Country code input is normalized to uppercase and constrained to 2 characters before PATCH.",
    ],
  },
  {
    id: "daily-done-style-today-vs-past-scope-fix",
    timestamp: "2026-03-02T19:24:50+01:00",
    title:
      "Scoped done-card styling by day (today keeps weather, past goes neutral)",
    summary:
      "The done-state daily card now keeps the weather/daylight look on the same day, and only falls back to neutral styling once it is no longer today.",
    userHighlights: [
      "Heute-erledigt still matches today’s daily weather design.",
      "Only non-current daily cards lose weather styling and appear neutral.",
      "The active new daily card remains the only weather-driven daily prompt each day.",
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, added `isDoneCardForToday` based on `doneCardText.created_at` and local done marker date.",
      "Done-card theme now conditionally uses `activeThemeClasses` for today and neutral classes for non-today states.",
    ],
  },
  {
    id: "daily-done-card-no-live-weather-theme",
    timestamp: "2026-03-02T19:22:50+01:00",
    title: "Done-state daily card no longer uses live weather styling",
    summary:
      "The completed daily card now keeps a fixed neutral style, while only the active daily card reflects current weather/daylight mood.",
    userHighlights: [
      "Heute-erledigt card now looks consistent and no longer changes with hourly weather.",
      "Only the active daily prompt card uses live weather visualization.",
      "Avoids the impression that past daily cards all carry changing weather moods.",
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, split theme mapping into `activeThemeClasses` (weather/daylight-aware) and `doneThemeClasses` (fixed neutral/day).",
      "Applied neutral theme classes to the done-card render branch in `reeprise/index.tsx`.",
    ],
  },
  {
    id: "daily-done-card-title-preview-by-marker-id-fix",
    timestamp: "2026-03-02T19:19:30+01:00",
    title: "Fixed done-card fallback copy when daily text already exists",
    summary:
      "The done-state daily card now resolves title and preview text via the stored daily text id, preventing fallback copy from showing after the text was generated.",
    userHighlights: [
      "Done-card now more reliably shows the real generated title.",
      "Done-card preview now more reliably shows the beginning of the generated text with `...` when needed.",
      "Fallback `Morgen wartet ...` text is now only used when the actual text record is not available yet.",
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` in `reeprise/index.tsx` to prioritize `openTextId` lookup in `generatedTexts` before topic-based daily-text heuristics.",
      "This avoids false negatives when the backend replaces the generic daily topic with the final generated title.",
    ],
  },
  {
    id: "daily-card-daylight-phase-mood-visualization",
    timestamp: "2026-03-02T19:11:03+01:00",
    title: "Daily card now reflects daylight mood (dawn/day/dusk/night)",
    summary:
      "The weather-adaptive daily card now also adjusts atmosphere by local daylight phase, so visuals match sunrise, daytime, sunset, and night mood.",
    userHighlights: [
      "The same weather theme now looks different in dawn, day, dusk, and night.",
      "Morning card mood is warmer at sunrise, calmer at dusk, and darker at night.",
      "Done-state and active daily card both use the same daylight-aware visual behavior.",
    ],
    technicalNotes: [
      "Backend weather-theme endpoint now includes `daylight_phase` derived from local current time and sunrise/sunset from Open-Meteo daily data.",
      "Frontend `useDailyWeatherTheme` DTO extended with `daylight_phase` typing and consumed in `DailyPracticeCallout`.",
      "Updated daily-card class mapper in `reeprise/index.tsx` to combine weather theme layers with daylight-phase mood overlays.",
    ],
  },
  {
    id: "weather-adaptive-daily-card-v1-implementation",
    timestamp: "2026-03-02T18:59:27+01:00",
    title:
      "Implemented weather-adaptive daily card v1 (city-based, hourly refresh)",
    summary:
      "The daily card now adapts its visual mood to local weather using a backend weather-theme endpoint, with Vienna fallback and hourly client refresh.",
    userHighlights: [
      "Daily card now switches among clear/cloudy/rain/snow/night/neutral visual variants while keeping the same core card design language.",
      "Weather theme refreshes every hour and when the page regains focus.",
      "Daily card is now visible from morning hours (from 06:00 local time) instead of only later in the day.",
    ],
    technicalNotes: [
      "Backend: added `GET /api/reeprise/generated_texts/weather-theme/` using Open-Meteo via server-side proxy with caching and graceful neutral fallback.",
      "Backend: added `UserSetting` fields `weather_city` and `weather_country_code` (migration `0073_usersetting_weather_location`) and exposed them via `UserSettingSerializer`.",
      "Frontend: added `useDailyWeatherTheme` hook with hourly/focus refetch and integrated theme class mapping into `DailyPracticeCallout` while preserving existing layout/animation language.",
    ],
  },
  {
    id: "weather-planning-morning-visibility-requirement",
    timestamp: "2026-03-02T18:43:46+01:00",
    title: "Weather card planning now requires morning visibility",
    summary:
      "The planning now explicitly states that the daily card must be visible in the morning in the user’s local timezone.",
    userHighlights: [
      "Daily card availability is now clearly defined for morning usage.",
      "Timezone handling is explicitly required for visibility timing.",
      "Weather theming must not block or delay morning card display.",
    ],
    technicalNotes: [
      "Updated `planning_daily_text_card.md` scope and added `Visibility Timing` section.",
      "No runtime code logic changed in this update.",
    ],
  },
  {
    id: "weather-planning-animation-style-guardrails-added",
    timestamp: "2026-03-02T18:42:01+01:00",
    title: "Weather planning updated with animation consistency guardrails",
    summary:
      "The weather-adaptive card plan now explicitly requires animation style consistency, using the current cloud drift motion as baseline across weather themes.",
    userHighlights: [
      "Ensures weather variants feel like one coherent design system in motion.",
      "Prevents flashy or mismatched animation styles between weather states.",
      "Keeps reduced-motion accessibility behavior consistent across variants.",
    ],
    technicalNotes: [
      "Added `Animation Consistency Guardrails` section to `planning_daily_text_card.md`.",
      "Defined baseline cloud animation approach and weather-specific motion guidance (clear/cloudy/rain/snow/night).",
    ],
  },
  {
    id: "weather-planning-visual-style-guardrails-added",
    timestamp: "2026-03-02T18:41:16+01:00",
    title:
      "Weather card planning now enforces existing visual style consistency",
    summary:
      "The weather-adaptive daily-card plan now explicitly requires all weather variants to keep the current card design language and only vary atmospheric mood layers.",
    userHighlights: [
      "Prevents each weather state from feeling like a different component.",
      "Keeps typography, spacing, structure, and interaction consistent across weather themes.",
      "Ensures weather visuals stay in the same recognizable product style.",
    ],
    technicalNotes: [
      "Added `Visual Consistency Guardrails` section to `planning_daily_text_card.md`.",
      "Guardrails define stable card shell/layout and limit weather changes to gradients/overlays/subtle motion.",
    ],
  },
  {
    id: "weather-planning-initial-location-vienna",
    timestamp: "2026-03-01T21:34:02+01:00",
    title: "Weather planning updated with Vienna as first location",
    summary:
      "The weather-adaptive daily-card planning now explicitly sets Vienna as the initial/default rollout location for V1.",
    userHighlights: [
      "Initial city for rollout and QA is now clearly defined as Vienna.",
      "Helps align implementation, testing, and demo behavior.",
      "User-specific location support remains planned for later phases.",
    ],
    technicalNotes: [
      "Updated `planning_daily_text_card.md` with an `Initial Location (V1)` section specifying Vienna (`AT`).",
      "No runtime code behavior changed yet.",
    ],
  },
  {
    id: "weather-adaptive-daily-card-planning-doc",
    timestamp: "2026-03-01T21:33:04+01:00",
    title: "Added planning document for weather-adaptive daily card",
    summary:
      "A new technical planning document defines architecture and rollout for weather-driven daily card theming with hourly refresh and safe fallbacks.",
    userHighlights: [
      "Plan covers location strategy, weather source, UI theme mapping, refresh cadence, and fallback behavior.",
      "Production-safe V1 focuses on city-based weather via backend proxy (no live GPS required).",
      "Includes clear phased rollout and QA checklist.",
    ],
    technicalNotes: [
      "Added `planning_daily_text_card.md` at repository root.",
      "Document includes proposed backend API contract and frontend update strategy for hourly weather theming.",
    ],
  },
  {
    id: "daily-card-read-cta-inline-style-aligned",
    timestamp: "2026-03-01T21:28:47+01:00",
    title: "Daily `Jetzt lesen` CTA aligned to inline cue style",
    summary:
      "The active daily card CTA now uses the same subtle inline style as the done-card open cue, replacing the large filled button look.",
    userHighlights: [
      "Daily and done card actions now feel visually consistent.",
      "No oversized filled button on the active daily card.",
      "Action still stays clear through inline text plus arrow cue.",
    ],
    technicalNotes: [
      "Restyled active daily-card action button classes in `reeprise/index.tsx` to inline text treatment.",
      "Added chevron icon to non-loading CTA state while preserving existing click behavior.",
    ],
  },
  {
    id: "daily-done-open-cue-non-button-inline",
    timestamp: "2026-03-01T21:26:33+01:00",
    title: "Done-card open indicator changed to non-button inline cue",
    summary:
      "The done-state card no longer uses a button-like open indicator; it now uses a lightweight inline cue while keeping the full card tappable.",
    userHighlights: [
      "Cleaner look without a secondary button feel.",
      "Whole card remains the main interaction target.",
      "Open affordance is still visible via subtle inline label + arrow.",
    ],
    technicalNotes: [
      "Replaced bordered/pill `dailyTextDoneOpen` hint with plain inline text cue in `reeprise/index.tsx`.",
      "No behavior changes to click/keyboard navigation.",
    ],
  },
  {
    id: "daily-done-text-cleanup-for-natural-preview",
    timestamp: "2026-03-01T21:26:08+01:00",
    title: "Cleaned up done-card title and sentence preview",
    summary:
      "Done-card text extraction was refined to avoid odd-looking raw fragments and produce cleaner, natural-looking title/preview text.",
    userHighlights: [
      "Heading now trims noisy characters and overly long titles.",
      "Sentence preview now removes raw prefixes and clips at word boundaries.",
      "Raw JSON-like payload artifacts are no longer shown as preview text.",
    ],
    technicalNotes: [
      "Enhanced daily-card parsing/sanitization helpers in `reeprise/index.tsx` for title and first-sentence extraction.",
      "Added safe clipping rules and JSON-like suppression fallback for done-card teaser content.",
    ],
  },
  {
    id: "daily-done-card-tappable-surface-open-cue",
    timestamp: "2026-03-01T21:24:39+01:00",
    title: "Done-state daily card switched from large button to tappable card",
    summary:
      "The large `Öffnen` button was replaced by a cleaner interaction: the whole done-state card is now tappable, with a subtle open indicator.",
    userHighlights: [
      "No oversized button in the done-state card anymore.",
      "A tap/click anywhere on the card opens the text.",
      "A small visual `Open` cue still communicates the action clearly.",
    ],
    technicalNotes: [
      "Made done-state daily card container keyboard/tap interactive (`role`, `tabIndex`, Enter/Space handling) in `reeprise/index.tsx`.",
      "Replaced CTA button with a compact open hint chip using `dailyTextDoneOpen` + chevron icon.",
    ],
  },
  {
    id: "daily-done-description-from-generated-text-first-sentence",
    timestamp: "2026-03-01T21:23:55+01:00",
    title: "Done-card description now uses first sentence from generated text",
    summary:
      "Instead of static copy, the done-state daily card now displays the first sentence of the generated text (trimmed with `...` when long).",
    userHighlights: [
      "Done-card description now reflects the actual generated text.",
      "Long opening sentences are shortened cleanly with `...`.",
      "Static fallback text still appears when no text sentence is available.",
    ],
    technicalNotes: [
      "Added daily-card helpers to parse story body and extract first-sentence preview in `reeprise/index.tsx`.",
      "Done-state description now prefers parsed text preview over `dailyTextDoneDescription` translation.",
    ],
  },
  {
    id: "daily-done-title-from-generated-text-and-copy-tightening",
    timestamp: "2026-03-01T21:22:31+01:00",
    title: "Heute-erledigt heading now uses generated text title",
    summary:
      "The done-state daily card now shows the actual generated text title as its heading, and the German done description was shortened.",
    userHighlights: [
      "Replaced generic `Heute erledigt` heading with the real generated title when available.",
      "German done description now reads only `Morgen wartet ein neuer auf dich.`",
      "Fallback remains stable if a parsed title is not available.",
    ],
    technicalNotes: [
      "Added local title parsing helper for daily done card and derived heading from today’s resolved daily text (`chatgpt_output` title, then topic fallback).",
      "Updated `dailyTextDoneDescription.GERMAN` translation value in `translations.json`.",
    ],
  },
  {
    id: "daily-card-white-line-removed",
    timestamp: "2026-03-01T21:20:40+01:00",
    title: "Removed white line from daily card background",
    summary:
      "The white bottom line/glow accent was removed from the daily card to keep the visual surface cleaner.",
    userHighlights: [
      "No more white line at the bottom of the daily card.",
      "Card looks calmer and more uniform.",
      "Applied consistently to both active and done card states.",
    ],
    technicalNotes: [
      "Removed the absolute bottom border/gradient layer from both daily-card render branches in `reeprise/index.tsx`.",
      "No behavior changes.",
    ],
  },
  {
    id: "daily-done-open-button-reliability-fallback",
    timestamp: "2026-03-01T21:20:17+01:00",
    title: "Fixed missing open path from Heute-erledigt card",
    summary:
      "The done-state daily card now reliably keeps an open button by using broader same-day matching and marker backfill for older stored states.",
    userHighlights: [
      "You can open today’s generated text from the Heute-erledigt card more reliably.",
      "Older done markers without a stored text id are now auto-repaired when today’s text is found.",
      "Done-state remains visually unchanged while interaction is more robust.",
    ],
    technicalNotes: [
      "Added fallback same-day practice-text detection (without strict topic-date dependency) in `DailyPracticeCallout`.",
      "When a target text id is resolved, the local daily marker is backfilled with `{ date, textId }` to stabilize future opens.",
    ],
  },
  {
    id: "daily-done-card-openability-and-style-alignment",
    timestamp: "2026-03-01T21:18:38+01:00",
    title:
      "Heute-erledigt card now keeps open access and matches daily card style",
    summary:
      "The completed daily card now follows the same sky/cloud design as the active daily card and still lets users open the generated text reliably.",
    userHighlights: [
      "Done-state card now visually matches the regular daily card.",
      "You can open today’s generated text directly from the done-state card.",
      "Open access remains stable even during list refresh timing delays.",
    ],
    technicalNotes: [
      "Extended daily done marker storage to support `{ date, textId }` (while remaining compatible with previous string values).",
      "Done-state card now resolves open target from either list match or stored marker `textId`, and reuses the same sky/cloud layout as the active card in `reeprise/index.tsx`.",
    ],
  },
  {
    id: "daily-cloud-animation-visibility-boost",
    timestamp: "2026-03-01T21:15:29+01:00",
    title: "Increased visibility of daily card cloud animation",
    summary:
      "Cloud movement on the daily card was intensified so the animation is clearly visible instead of nearly static.",
    userHighlights: [
      "Cloud drift is now easier to perceive at a glance.",
      "Motion remains smooth and calm, but no longer feels frozen.",
      "Reduced-motion users still get a static version.",
    ],
    technicalNotes: [
      "Adjusted `daily-cloud-drift-left/right` keyframes in `src/index.css` to use larger translate offsets and subtle opacity/scale variation.",
      "Shortened animation durations per cloud layer to improve perceivable movement.",
    ],
  },
  {
    id: "daily-text-card-cloud-animation",
    timestamp: "2026-03-01T21:13:35+01:00",
    title: "Added subtle cloud animation to the daily text card",
    summary:
      "The daily text card now has gentle cloud movement to make the morning surface feel more alive without being distracting.",
    userHighlights: [
      "Clouds now drift slowly across the daily card background.",
      "Animation is subtle and readability-focused.",
      "Motion is automatically disabled for users with reduced-motion preference.",
    ],
    technicalNotes: [
      "Added `daily-cloud-*` classes to daily-card cloud layers in `reeprise/index.tsx`.",
      "Implemented `daily-cloud-drift-left/right` keyframes and reduced-motion media handling in `src/index.css`.",
    ],
  },
  {
    id: "daily-and-uebungstext-button-style-aligned",
    timestamp: "2026-03-01T21:12:06+01:00",
    title: "Aligned Übungstext and daily card button style",
    summary:
      "The main CTA buttons on the Übungstext and daily cards now use the same shape and blue style for a consistent visual language.",
    userHighlights: [
      "Both cards now use the same rounded-full button shape.",
      "Color, border, hover, and disabled states are now consistent.",
      "Button typography now follows the same uppercase CTA style.",
    ],
    technicalNotes: [
      "Updated the Übungstext banner CTA classes in `GenerateTextCard.tsx` to match the daily card CTA style from `reeprise/index.tsx`.",
      "No behavior logic changes.",
    ],
  },
  {
    id: "uebungstext-placement-switched-back-to-generator-card",
    timestamp: "2026-03-01T21:11:21+01:00",
    title: "Übungstext card moved back to the generator panel",
    summary:
      "The Übungstext card placement was switched back: the generator card version is visible again and the separate top-area callout was removed.",
    userHighlights: [
      "Übungstext is now back in its original generator area.",
      "The extra card near the daily section is removed.",
      "Only one Übungstext card remains visible.",
    ],
    technicalNotes: [
      "Removed `StruggleWordsCallout` rendering and implementation from `reeprise/index.tsx`.",
      "Restored default `GenerateTextCard` usage without `showStruggleBanner={false}` override.",
    ],
  },
  {
    id: "uebungstext-duplicate-card-removed",
    timestamp: "2026-03-01T21:10:13+01:00",
    title: "Removed duplicate Übungstext card on Reeprise page",
    summary:
      "The duplicate Übungstext card was removed by keeping only the top callout near the daily card.",
    userHighlights: [
      "Only one Übungstext card is now visible on the page.",
      "Top placement next to the daily flow is preserved.",
      "Generator form stays cleaner without repeated callout content.",
    ],
    technicalNotes: [
      "Added `showStruggleBanner` prop to `GenerateTextCard` (default `true`).",
      "Set `showStruggleBanner={false}` in `reeprise/index.tsx` to avoid rendering the in-card duplicate.",
    ],
  },
  {
    id: "uebungstext-card-no-cloud-shapes",
    timestamp: "2026-03-01T21:09:25+01:00",
    title: "Removed cloud shapes from Übungstext card design",
    summary:
      "The Übungstext cards now keep the blue style but no longer include cloud-form decorations.",
    userHighlights: [
      "Cleaner Übungstext card look with less visual ornament.",
      "Blue color direction stays consistent with the current theme.",
      "Both Übungstext surfaces now follow the same no-cloud style.",
    ],
    technicalNotes: [
      "Removed cloud shape overlay markup from the Reeprise `StruggleWordsCallout`.",
      "Removed cloud shape overlay markup from the `GenerateTextCard` struggle banner block.",
    ],
  },
  {
    id: "uebungstext-callout-added-near-daily-card",
    timestamp: "2026-03-01T21:08:33+01:00",
    title: "Übungstext card also added to the daily-card area",
    summary:
      "The Übungstext callout now appears in the same top Reeprise section as the daily card, so users can access it immediately without opening the generator panel.",
    userHighlights: [
      "Übungstext is now visible next to the morning daily flow.",
      "One tap still creates and opens the generated practice text.",
      "Visual style remains aligned with the blue sky/cloud design.",
    ],
    technicalNotes: [
      "Added `StruggleWordsCallout` to `reeprise/index.tsx` and rendered it below `DailyPracticeCallout`.",
      "Hooked it to `useStruggleWords` + `useGeneratedTextCreate` and reused the per-language 24h hide gate from localStorage.",
    ],
  },
  {
    id: "uebungstext-card-sky-cloud-style",
    timestamp: "2026-03-01T21:07:23+01:00",
    title: "Übungstext card redesigned to match the sky/cloud visual style",
    summary:
      "The practice-text card in the generator now follows the same calm blue-sky direction as the daily card, with a compact cloud-themed surface.",
    userHighlights: [
      "Übungstext card now feels visually consistent with the morning daily card.",
      "Color tone is calmer and less noisy.",
      "Action button style now matches the blue visual direction.",
    ],
    technicalNotes: [
      "Restyled the struggle-word callout block in `GenerateTextCard.tsx` with sky gradient and layered cloud shapes.",
      "Kept existing behavior and logic unchanged; this was a styling-only update without drop shadows.",
    ],
  },
  {
    id: "daily-text-title-duplicate-removed",
    timestamp: "2026-03-01T21:06:33+01:00",
    title: "Removed duplicate `Text des Tages` labeling",
    summary:
      "Only the morning daily card now uses the `Text des Tages` title; the separate struggle-word card has distinct wording again.",
    userHighlights: [
      "No more second card with the same `Text des Tages` headline.",
      "Exercise card is now clearly labeled as `Übungstext`.",
      "Card descriptions are now distinct and less confusing.",
    ],
    technicalNotes: [
      "Updated translation keys `struggle_words_title` and `struggle_words_description` in `translations.json`.",
      "No logic or routing changes.",
    ],
  },
  {
    id: "daily-text-card-blue-sky-cloud-redesign",
    timestamp: "2026-03-01T21:05:41+01:00",
    title:
      "Daily morning card redesigned to realistic blue-sky cloud direction",
    summary:
      "The first daily card now uses a calmer sky-blue visual system with layered cloud forms instead of a multi-color palette.",
    userHighlights: [
      "Card now feels cleaner and more motivating in the morning.",
      "Background follows a realistic blue-sky gradient with soft cloud structure.",
      "Primary action now matches the blue direction for a consistent look.",
    ],
    technicalNotes: [
      "Replaced warm gradient and accent classes in `DailyPracticeCallout` with sky-blue palette classes.",
      "Added absolute layered cloud shape elements (radial gradients + rounded forms) without drop shadows.",
    ],
  },
  {
    id: "daily-text-copy-positive-german-tone",
    timestamp: "2026-03-01T21:04:28+01:00",
    title: "Daily text German description changed to positive wording",
    summary:
      "The daily card description in German now avoids negative framing and uses a more encouraging tone.",
    userHighlights: [
      "Removed wording that could feel corrective or discouraging.",
      "Daily card now reads as a positive next-step invitation.",
      "Tone remains clear that this is your daily text.",
    ],
    technicalNotes: [
      "Updated German values for `dailyTextDescription` and aligned `struggle_words_description` in `translations.json`.",
      "No behavior or layout logic changed.",
    ],
  },
  {
    id: "daily-text-card-warm-morning-palette",
    timestamp: "2026-03-01T21:03:53+01:00",
    title: "Daily text morning card shifted to a warmer, less dark style",
    summary:
      "The first card users see in the morning now avoids the previous black-heavy look and uses a softer, more inviting color direction.",
    userHighlights: [
      "The daily card now uses a warm sunrise-like background instead of cool gray.",
      "Date/weekday accents now feel lighter and more editorial.",
      "The main action button is no longer black and feels more inviting to start reading.",
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` container, text, and badge color classes in `reeprise/index.tsx`.",
      "Changed CTA from slate/black styling to a warm rose accent while keeping behavior unchanged.",
    ],
  },
  {
    id: "daily-text-copy-text-des-tages",
    timestamp: "2026-03-01T21:03:13+01:00",
    title: "Daily card copy updated to `Text des Tages`",
    summary:
      "The morning daily-text card now uses clearer German wording so it is immediately understood as the daily text.",
    userHighlights: [
      "Headline changed from `Dein Text für heute` to `Text des Tages`.",
      "Description now explicitly says this is your daily text.",
      "Wording stays warm but now communicates the daily rhythm more clearly.",
    ],
    technicalNotes: [
      "Updated `dailyTextHeading` and `dailyTextDescription` German translations in `translations.json`.",
      "Aligned legacy `struggle_words_title` and `struggle_words_description` German strings to the same wording.",
    ],
  },
  {
    id: "daily-text-done-state-latched-per-day",
    timestamp: "2026-03-01T21:02:27+01:00",
    title: "Daily text done-state now stays latched for the day",
    summary:
      "The compact `done for today` card no longer flips back to the full create card during short data refresh transitions.",
    userHighlights: [
      "Once today is marked done, the compact done card stays stable.",
      "Temporary list/query refresh timing no longer causes card state bouncing.",
      "The card still resets normally for the next day.",
    ],
    technicalNotes: [
      "Added a day+language-scoped `doneLatched` UI state in `DailyPracticeCallout`.",
      "Latched state is reset only when the day or language bucket changes, and is promoted to `true` whenever done conditions become true.",
    ],
  },
  {
    id: "daily-text-done-state-flicker-fix",
    timestamp: "2026-03-01T20:59:59+01:00",
    title: "Fixed daily text done-state flicker after creation",
    summary:
      "The collapsed `done for today` daily-text card now remains stable instead of briefly switching back to the full creation card.",
    userHighlights: [
      "After creating today’s text, the compact done-state stays visible consistently.",
      "State now survives route changes and short list refresh delays.",
      "A fresh full card still appears on the next day.",
    ],
    technicalNotes: [
      "Moved daily-done marker from volatile component state to localStorage-backed state.",
      "Marker matching now covers both current language-space and resolved generation language to avoid transient mismatches.",
    ],
  },
  {
    id: "daily-text-card-collapses-to-done-state",
    timestamp: "2026-03-01T20:48:06+01:00",
    title: "Daily text card now collapses into a done-for-today state",
    summary:
      "After creating today’s daily text, the large card now collapses into a compact completion state instead of disappearing entirely.",
    userHighlights: [
      "You now get a clear `done for today` confirmation once your daily text is created.",
      "A compact card remains visible and can open today’s daily text directly.",
      "On the next day, the full creation card appears again automatically.",
    ],
    technicalNotes: [
      "Replaced the previous hide-on-create behavior in `DailyPracticeCallout` with a conditional compact completion surface.",
      "Added new translation keys for done-state heading, description, and open action in German and English.",
    ],
  },
  {
    id: "daily-text-heading-explicitly-today",
    timestamp: "2026-03-01T20:45:22+01:00",
    title: "Daily text heading now explicitly says it is for today",
    summary:
      "The callout headline was refined so users can immediately recognize the card as today’s daily text.",
    userHighlights: [
      "German headline now reads `Dein Text für heute`.",
      "English headline now reads `Your story for today`.",
      "Warm tone remains while daily intent is now explicit.",
    ],
    technicalNotes: [
      "Updated only `dailyTextHeading` translation values in `translations.json`.",
      "No component logic or layout changes.",
    ],
  },
  {
    id: "daily-text-callout-warm-non-school-copy",
    timestamp: "2026-03-01T20:44:48+01:00",
    title: "Daily text card copy rewritten to feel warmer and less school-like",
    summary:
      "The daily text callout wording was rewritten to sound more personal and inviting, with less technical and classroom-like phrasing.",
    userHighlights: [
      "Headline now reads as a personal invitation instead of a utility label.",
      "Description now feels softer and more human while keeping the same meaning.",
      "CTA now uses a more natural reading-focused wording.",
    ],
    technicalNotes: [
      "Updated existing `dailyTextHeading`, `dailyTextDescription`, and `dailyTextCta` entries in translations without changing keys or component logic.",
      "Applied in both German and English translation values.",
    ],
  },
  {
    id: "daily-text-callout-compact-tone-down",
    timestamp: "2026-03-01T20:43:59+01:00",
    title: "Daily text callout scaled down to a calmer compact style",
    summary:
      "The newly redesigned morning callout was intentionally reduced in visual intensity and size for a cleaner first-view balance.",
    userHighlights: [
      "Card now uses a more compact footprint with smaller headline and tighter spacing.",
      "Date/weekday row remains, but with lighter visual emphasis.",
      "CTA remains clear while feeling less dominant.",
    ],
    technicalNotes: [
      "Reduced typography scale, spacing, and border weight in `DailyPracticeCallout`.",
      "Removed oversized decorative ticket-cut accents and simplified surface treatment.",
    ],
  },
  {
    id: "daily-text-callout-morning-first-impression-redesign",
    timestamp: "2026-03-01T20:42:28+01:00",
    title:
      "Daily text morning callout redesigned as a standout first-impression card",
    summary:
      "The morning `Ein Text für dich …` callout was redesigned to feel more collectible and editorial, with a stronger reading invitation and built-in weekday/date context.",
    userHighlights: [
      "The card now presents weekday and date at the top for stronger daily rhythm.",
      "Headline and CTA hierarchy were rebuilt so the card feels like a featured daily drop.",
      "Visual treatment now uses a unique border + ticket-cut motif without drop shadows.",
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` layout in `reeprise/index.tsx` with new grid structure, typographic hierarchy, and responsive CTA behavior.",
      "Added locale-aware weekday/date formatting derived from UI language (`de-DE`/`en-US`) and applied a shadow-free gradient + border visual system.",
    ],
  },
  {
    id: "daily-text-callout-editorial-openai-color-restyle",
    timestamp: "2026-03-01T20:39:06+01:00",
    title:
      "Daily text callout redesigned with editorial layout and fresh color style",
    summary:
      "The `Ein Text für dich …` daily text callout now uses an editorial text-details-inspired structure with a modern color-forward treatment.",
    userHighlights: [
      "Heading and description now follow a cleaner magazine-style hierarchy.",
      "The card now uses soft atmospheric color gradients and subtle depth.",
      "The CTA button is now a stronger rounded accent action while keeping the same behavior.",
    ],
    technicalNotes: [
      "Restyled `DailyPracticeCallout` container with layered radial gradients, soft shadow, and decorative blur shapes.",
      "Updated callout typography, divider rhythm, and CTA styling in `reeprise/index.tsx` without changing translations or business logic.",
    ],
  },
  {
    id: "speech-timestamps-migration-compat-safety",
    timestamp: "2026-03-01T20:36:29+01:00",
    title: "Added migration safety guard for speech timestamp columns",
    summary:
      "Database startup is now more resilient when local migration history and schema drift differ for speech timestamp fields.",
    userHighlights: [
      "Reduced risk of local crashes caused by mixed migration states.",
      "Story generation setup is more stable after branch switching.",
      "No visible UI behavior changes.",
    ],
    technicalNotes: [
      "Added an idempotent compatibility migration that ensures `speech_timestamps` exists on `reeprise_generatedtext` and `reeprise_chapter` in SQLite environments.",
      "Migration checks actual table columns first and only applies `ALTER TABLE` when a column is missing.",
    ],
  },
  {
    id: "text-details-audio-readalong-highlight-rollback",
    timestamp: "2026-03-01T20:31:36+01:00",
    title: "Audio read-along highlighting was rolled back",
    summary:
      "The recently added audio-synced text highlighting on text details was removed again.",
    userHighlights: [
      "Text no longer auto-highlights during audio playback.",
      "Audio playback and chapter-specific audio selection continue to work as before.",
      "Rollback was applied to avoid shipping low-quality highlighting behavior.",
    ],
    technicalNotes: [
      "Removed frontend audio-time-driven text highlight wiring and related highlight computation.",
      "Removed backend timestamp-generation/storage plumbing introduced for audio word highlighting.",
    ],
  },
  {
    id: "chapter-specific-audio-playback-in-text-details",
    timestamp: "2026-03-01T20:18:24+01:00",
    title: "Audio playback now follows the active chapter",
    summary:
      "When you generate and open a new chapter from selected vocabulary, the audio player now loads that chapter’s own speech instead of replaying the first/base text audio.",
    userHighlights: [
      "Chapter audio now matches the chapter you are currently viewing.",
      "Switching chapter or opening another text no longer reuses stale audio playback state.",
      "Pressing play requests speech generation for the current chapter context.",
    ],
    technicalNotes: [
      "Added chapter-aware speech support in backend: `Chapter` now stores `speech`, text-to-speech accepts `chapter_number`, and streaming endpoint reads `?chapter=<counter>`.",
      "Updated text-details player to build chapter-specific audio URLs, request speech with chapter context, and reset player state when text/chapter changes.",
    ],
  },
  {
    id: "text-details-chapter-buttons-moved-into-text-column-and-scaled-down",
    timestamp: "2026-03-01T20:09:25+01:00",
    title: "Chapter buttons moved into text column and resized smaller",
    summary:
      "Previous/next chapter controls now sit directly in the main text column and use a smaller visual footprint for a cleaner article layout.",
    userHighlights: [
      "Vorheriges/Nächstes Kapitel now appear at the top of the main text column.",
      "Buttons are smaller and less dominant while still easy to tap/click.",
      "Right sidebar no longer carries its own separate chapter button.",
    ],
    technicalNotes: [
      "Removed standalone mobile top-row and sidebar chapter-button placements; added shared button row above the text/audio area.",
      "Reduced chapter button typography, spacing, and icon gaps in `LastChapterNavigationTile` and `NextChapterNavigationTile`.",
    ],
  },
  {
    id: "text-details-chapter-buttons-top-padding",
    timestamp: "2026-03-01T20:08:32+01:00",
    title: "Added top padding to previous/next chapter buttons",
    summary:
      "Both chapter navigation controls now include additional top padding for more balanced vertical spacing.",
    userHighlights: [
      "Vorheriges Kapitel button now has extra top breathing room.",
      "Nächstes Kapitel button now matches the same top spacing.",
      "Applied consistently to active and disabled states.",
    ],
    technicalNotes: [
      "Updated shared chapter button class sets in `LastChapterNavigationTile` and `NextChapterNavigationTile` to include top padding.",
      "No behavior, routing, or translation changes.",
    ],
  },
  {
    id: "text-details-next-chapter-to-vertiefen-spacing",
    timestamp: "2026-03-01T20:08:00+01:00",
    title: "Added spacing between Next Chapter and Vertiefen on desktop",
    summary:
      "The desktop layout now includes clear vertical spacing between the `Nächstes Kapitel` control and the `Vertiefen` section.",
    userHighlights: [
      "The two sections no longer touch visually.",
      "Desktop right-column rhythm is cleaner and easier to scan.",
      "No behavior changes to chapter navigation or helper panel.",
    ],
    technicalNotes: [
      "Added desktop bottom margin on the `NextChapterNavigationTile` wrapper before `LanguageHelperPanel` in generated text details route.",
      "No API, logic, or translation changes.",
    ],
  },
  {
    id: "text-details-vertiefen-collapse-empty-chat-gap",
    timestamp: "2026-03-01T19:54:31+01:00",
    title: "Vertiefen no longer leaves a large empty gap before first message",
    summary:
      "When the helper chat is empty, the intermediate conversation area now collapses so the input sits directly under the Vertiefen title.",
    userHighlights: [
      "Reduced vertical whitespace between `Vertiefen` and the input field when no messages exist.",
      "Conversation area appears only once there are messages or loading state.",
      "No change to ask/send behavior.",
    ],
    technicalNotes: [
      "Added conditional rendering for the message rail in `LanguageHelperPanel` based on message/loading presence.",
      "Retained existing message, action, and loading UI once conversation content exists.",
    ],
  },
  {
    id: "text-details-vertiefen-send-button-alignment-fix",
    timestamp: "2026-03-01T19:53:32+01:00",
    title: "Vertiefen send button alignment refined",
    summary:
      "The `Senden` control in Vertiefen was adjusted for cleaner pill geometry and centered label alignment.",
    userHighlights: [
      "Send button shape now appears visually balanced within the input row.",
      "Label is centered more consistently inside the rounded control.",
      "No behavior changes to sending messages.",
    ],
    technicalNotes: [
      "Adjusted helper send-button dimensions from fixed width to min-width plus horizontal padding and explicit line-height normalization.",
      "Kept existing interaction and disabled-state behavior.",
    ],
  },
  {
    id: "text-details-vertiefen-remove-all-divider-lines",
    timestamp: "2026-03-01T19:52:51+01:00",
    title: "Vertiefen section divider lines removed",
    summary:
      "All horizontal divider lines were removed from the Vertiefen helper section for a cleaner, lighter layout.",
    userHighlights: [
      "No horizontal separator lines remain in the Vertiefen panel.",
      "The section now appears visually cleaner and less cluttered.",
      "Helper behavior and messaging remain unchanged.",
    ],
    technicalNotes: [
      "Removed remaining border styling from the helper message rail wrapper in `LanguageHelperPanel`.",
      "No logic, API, or translation changes.",
    ],
  },
  {
    id: "text-details-vertiefen-single-divider-line",
    timestamp: "2026-03-01T19:52:25+01:00",
    title: "Vertiefen section now uses a single divider line",
    summary:
      "The helper section divider was simplified from two horizontal lines to one for a cleaner editorial layout.",
    userHighlights: [
      "Removed one of the two horizontal lines in `Vertiefen`.",
      "Section now feels less busy while preserving structure.",
      "All helper interactions remain unchanged.",
    ],
    technicalNotes: [
      "Changed message rail container from `border-y` to `border-t` in `LanguageHelperPanel`.",
      "No behavioral or API changes.",
    ],
  },
  {
    id: "text-details-vertiefen-editorial-restyle",
    timestamp: "2026-03-01T19:50:09+01:00",
    title: "Vertiefen section now follows the editorial text-details style",
    summary:
      "The `Vertiefen` helper panel on text details now uses the same newspaper-like visual language as the surrounding layout.",
    userHighlights: [
      "Panel heading now uses compact editorial metadata styling.",
      "Message area now sits inside subtle divider rails for cleaner article rhythm.",
      "Input and send controls are now visually aligned with the outlined pill/button style used across text details.",
    ],
    technicalNotes: [
      "Restyled `LanguageHelperPanel` container, message bubbles, loading state, confirmation card typography, and input action controls.",
      "No helper behavior, API contract, or translation key changes.",
    ],
  },
  {
    id: "text-details-vocabulary-card-editorial-restyle",
    timestamp: "2026-03-01T19:49:04+01:00",
    title: "Text-details vocabulary cards now match editorial page styling",
    summary:
      "Vocabulary cards on text details now use the same newspaper-like visual language as the rest of the page, with quieter surfaces, serif hierarchy, and subtle controls.",
    userHighlights: [
      "Vocabulary cards now appear as clean white editorial panels instead of heavy gray blocks.",
      "Sentence and grammar areas use thin divider sections for better article-like rhythm.",
      "More grammar and edit controls now match the compact outlined style used across text details.",
    ],
    technicalNotes: [
      "Restyled `VocabularyItem` shell, sentence/grammar sections, grammar chips, and control buttons in the generated-text details route.",
      "No behavior, data, or translation changes.",
    ],
  },
  {
    id: "text-details-generate-new-text-cta-editorial-restyle",
    timestamp: "2026-03-01T19:47:05+01:00",
    title: "Generate-new-text action now matches editorial text-details style",
    summary:
      "The `Neuen Text aus Vokabeln generieren` action on text details now uses the same compact, newspaper-like visual language as the rest of the page.",
    userHighlights: [
      "Desktop action is now presented in a subtle bordered section with a compact pill button.",
      "Mobile sticky action now aligns with content gutters and uses the same editorial button styling.",
      "Action behavior is unchanged; only visual design was updated.",
    ],
    technicalNotes: [
      "Restyled both desktop and mobile `btn_Generate_new_Text` surfaces in generated text details route to a border-based, uppercase tracked editorial CTA pattern.",
      "No translation, backend, or routing logic changes.",
    ],
  },
  {
    id: "text-details-mobile-chapter-nav-side-padding",
    timestamp: "2026-03-01T19:46:14+01:00",
    title: "Mobile chapter navigation now aligns with page side gutters",
    summary:
      "The mobile row for `Vorheriges Kapitel` and `Nächstes Kapitel` now includes side padding so controls no longer sit flush against the viewport edges.",
    userHighlights: [
      "Chapter navigation buttons now align with the same horizontal gutters as text content.",
      "Touch targets keep the same behavior but feel better placed on small screens.",
      "Visual rhythm between nav controls and article body is now consistent.",
    ],
    technicalNotes: [
      "Added horizontal padding to the mobile-only chapter navigation wrapper in generated text details route.",
      "No behavior, routing, or state changes.",
    ],
  },
  {
    id: "text-details-chapter-nav-editorial-restyle",
    timestamp: "2026-03-01T19:45:03+01:00",
    title: "Chapter navigation now matches text-details editorial UI",
    summary:
      "The `Vorheriges Kapitel` and `Nächstes Kapitel` controls on text details now use the same compact, border-based visual language as the rest of the page.",
    userHighlights: [
      "Previous/next chapter controls now appear as subtle rounded pills instead of plain text rows.",
      "Active and disabled states are clearer and more consistent with surrounding metadata controls.",
      "The chapter navigation feels integrated with the article-like layout on mobile and desktop.",
    ],
    technicalNotes: [
      "Restyled `LastChapterNavigationTile` and `NextChapterNavigationTile` to use consistent compact border-pill classes and uppercase tracking.",
      "No behavior change to routing/search params; update is visual only.",
    ],
  },
  {
    id: "text-details-remove-published-label",
    timestamp: "2026-03-01T19:43:46+01:00",
    title: "Text details date row now hides the Published label",
    summary:
      "The text-details metadata row now shows only the date and time, without the extra `Published/Veröffentlicht` label.",
    userHighlights: [
      "Removed the `Veröffentlicht`/`Published` caption above text date metadata.",
      "Date and time remain visible in the same position under the title.",
      "The header area now looks cleaner and less repetitive.",
    ],
    technicalNotes: [
      "Removed rendering of `text_details_published_label` in `GeneratedTextDetailsTextPane` while keeping existing locale-aware date/time formatting.",
      "No backend or API changes.",
    ],
  },
  {
    id: "generated-text-strip-title-story-for-visual-and-audio",
    timestamp: "2026-03-01T19:43:06+01:00",
    title:
      "Generated texts now remove Title/Story wrappers before display and speech",
    summary:
      "Generated text content now strips leading `Title`/`Story` wrapper labels at save-time so the reading view and audio playback contain only the actual story body.",
    userHighlights: [
      "Text details no longer shows raw `Title:` or `Story:` wrapper labels.",
      "Audio playback no longer reads `Title` or `Story` out loud before the real text.",
      "Applies to plain labeled outputs in addition to JSON-style model responses.",
    ],
    technicalNotes: [
      "Backend model-output extraction now handles plain `Title:` + `Story:` formats and returns clean story body plus parsed title.",
      "Completion flow now persists cleaned `chatgpt_output` after wrapper removal and includes regression test coverage for plain labeled payloads.",
    ],
  },
  {
    id: "text-details-audio-timeline-knob",
    timestamp: "2026-03-01T19:41:28+01:00",
    title: "Text details audio timeline now has a draggable knob",
    summary:
      "The audio progress line on text details now includes a visible circular handle so users can scrub more precisely without changing the existing player layout.",
    userHighlights: [
      "A small circle now shows the current playback position on the timeline.",
      "You can drag the circle to replay or skip to exact moments more easily.",
      "The rest of the audio player design stays the same.",
    ],
    technicalNotes: [
      "Converted timeline wrapper to a relative track container and added a positioned handle tied to playback progress percentage.",
      "Wired handle mouse/touch start events into existing seek/scrub logic while preserving click-to-seek behavior on the line.",
    ],
  },
  {
    id: "text-details-audio-progress-line-scrub",
    timestamp: "2026-03-01T19:39:39+01:00",
    title: "Text details audio progress line is now interactive",
    summary:
      "The audio progress line on text details now supports click and drag scrubbing so listeners can quickly replay or skip sections without changing the player layout.",
    userHighlights: [
      "Click anywhere on the progress line to jump to that moment in the audio.",
      "Drag across the line to scrub backward or forward like modern streaming players.",
      "The existing compact editorial audio design stays unchanged.",
    ],
    technicalNotes: [
      "Added timeline seek handling based on pointer position against the rendered progress line bounds.",
      "Implemented mouse and touch scrubbing listeners that update `audio.currentTime` and keep UI progress state in sync.",
    ],
  },
  {
    id: "text-details-audio-editorial-player-layout",
    timestamp: "2026-03-01T18:11:52+01:00",
    title: "Text details audio player now uses a compact editorial layout",
    summary:
      "The text-details audio section has been redesigned from a large card into a slimmer, article-style metadata row for a cleaner reading experience.",
    userHighlights: [
      "Play controls now appear in a compact row that fits better with article typography.",
      "Playback now shows a subtle time readout and slim progress line while listening.",
      "Audio speed can be toggled inline without opening additional UI.",
    ],
    technicalNotes: [
      "Replaced oversized audio container/button styles with border-y editorial meta row styling in generated text details route.",
      "Added lightweight playback metadata state (`currentTime`, `duration`, `playbackRate`) and bound it to audio element events for time/progress/speed display.",
    ],
  },
  {
    id: "generated-title-headline-normalization-and-topic-echo-guard",
    timestamp: "2026-03-01T18:01:17+01:00",
    title:
      "Generated text titles now start uppercase and avoid topic-only headers",
    summary:
      "Generated text headers are now enforced as headline-style titles: they always start uppercase and are no longer allowed to just repeat the typed topic.",
    userHighlights: [
      "Generated titles now consistently begin with an uppercase letter.",
      "If the model returns a title that only mirrors your topic, the system rewrites it into a content-based title.",
      "Text headers better reflect the actual generated story content.",
    ],
    technicalNotes: [
      "Backend completion flow now checks title/topic similarity and treats exact topic echoes as invalid final titles.",
      "When needed, backend requests a story-based AI title and falls back to derived content titles, then applies headline normalization before persisting `topic`.",
    ],
  },
  {
    id: "ui-language-date-time-and-brand-copy-consistency",
    timestamp: "2026-03-01T17:29:29+01:00",
    title: "UI language now controls generated-text date/time formatting",
    summary:
      "Generated-text date/time displays now follow the app UI language, and the language-switch subtitle copy is fully translated.",
    userHighlights: [
      "When app UI is German, generated-text date/time metadata now formats in German style.",
      "Text-details publication metadata also follows app UI language selection.",
      "The `with Uxhulu` subtitle in language switcher is now translatable.",
    ],
    technicalNotes: [
      "Extended `useTranslation` return payload with current UI language and used it to map locale (`de-DE`/`en-US`) in generated-text surfaces.",
      "Replaced hardcoded `with Uxhulu` in app route with translation key `language_switch_with_brand`.",
    ],
  },
  {
    id: "generated-title-vs-prompt-separation",
    timestamp: "2026-03-01T17:23:27+01:00",
    title:
      "Text headers now use generated titles while prompt preview stays user-typed",
    summary:
      "Text card headers and prompt previews are now separated: headers use generated titles, while preview lines keep the original typed prompt.",
    userHighlights: [
      "If you type `rose` as topic, the preview can keep `rose` while header shows a generated title.",
      "Header no longer needs to reuse prompt text when no explicit title was returned.",
      "The same prompt preview behavior is available on text details.",
    ],
    technicalNotes: [
      "Backend now always derives a title from story content when no explicit title exists and persists it as `topic`.",
      "Frontend caches original create-form topic per generated text id and uses that cache for prompt preview lines.",
    ],
  },
  {
    id: "text-details-article-meta-date-time",
    timestamp: "2026-03-01T17:20:55+01:00",
    title: "Text details now shows article-style publication date and time",
    summary:
      "The text details header now includes an article-style publication metadata row with generation date and time.",
    userHighlights: [
      "Date and time now appear directly below the text title.",
      "Presentation uses a subtle editorial style to support an article-like reading vibe.",
      "Works for existing and new generated texts using stored `created_at`.",
    ],
    technicalNotes: [
      "Added formatted publication metadata block in `GeneratedTextDetailsTextPane` with locale-aware date/time formatting.",
      "Added translation key `text_details_published_label`.",
    ],
  },
  {
    id: "prompt-action-label-discreet",
    timestamp: "2026-03-01T17:19:37+01:00",
    title: "Prompt action label is now more discreet",
    summary:
      "The prompt expansion action now uses a shorter, less intrusive label.",
    userHighlights: [
      "Changed button wording from `Vollen Prompt` to `Mehr` (EN: `More`).",
      "This keeps the UI calmer while retaining access to full prompt content.",
      "No behavior changes to the full prompt modal.",
    ],
    technicalNotes: [
      "Updated translation key `text_card_open_full_prompt` only.",
      "No layout or backend changes.",
    ],
  },
  {
    id: "text-details-topic-preview-and-full-prompt",
    timestamp: "2026-03-01T17:18:51+01:00",
    title: "Text details now shows topic preview with full prompt option",
    summary:
      "The text details header now includes a compact topic preview line and lets you open the complete prompt when truncated.",
    userHighlights: [
      "Topic preview is now visible in text details below the header.",
      "Long topics are shortened to four words with `...` for readability.",
      "You can open a modal to read the full prompt text.",
    ],
    technicalNotes: [
      "Added topic-preview row and full-prompt modal behavior to `GeneratedTextDetailsTextPane`.",
      "Reused existing prompt action translation keys from text-card prompt UI.",
    ],
  },
  {
    id: "text-card-header-no-story-sentence-fallback",
    timestamp: "2026-03-01T17:17:28+01:00",
    title: "Text card header no longer falls back to first story sentence",
    summary:
      "The text card header now uses only actual title sources and no longer derives a heading from the story body.",
    userHighlights: [
      "First line/sentence of story text no longer appears in the card header.",
      "Header remains reserved for title content.",
      "Topic preview behavior remains unchanged.",
    ],
    technicalNotes: [
      "Removed story-based title derivation fallback in `ReepriseTextCard`.",
      "Header resolution now uses parsed title only, then existing fallback behavior.",
    ],
  },
  {
    id: "text-card-open-full-prompt-modal",
    timestamp: "2026-03-01T17:14:27+01:00",
    title: "Text cards now include an Open full prompt action",
    summary:
      "Long topic prompts can now be opened in full from the card via a dedicated action.",
    userHighlights: [
      "When a prompt is truncated in the topic line, you now get an `Open full prompt` button.",
      "Clicking it opens a modal with the complete prompt text.",
      "Works on both standard and podcast text cards.",
    ],
    technicalNotes: [
      "Added local prompt modal state to `ReepriseTextCard` and wired open actions where topic preview truncation is active.",
      "Introduced new translation keys for open/full/close prompt labels.",
    ],
  },
  {
    id: "text-card-topic-line-persistent",
    timestamp: "2026-03-01T17:13:01+01:00",
    title: "Text card topic line now stays visible after title resolves",
    summary:
      "The small topic/prompt line is now persistent and no longer disappears once the generated title appears.",
    userHighlights: [
      "Topic stays visible as secondary context at all times.",
      "Generated title still remains the primary header.",
      "Long topics continue to use 4-word ellipsis in the secondary line.",
    ],
    technicalNotes: [
      "Removed conditional hiding logic for topic line in `ReepriseTextCard`; topic now renders whenever present.",
      "No backend changes.",
    ],
  },
  {
    id: "text-card-topic-preview-word-truncation",
    timestamp: "2026-03-01T17:12:06+01:00",
    title: "Text card topic preview now truncates long prompts",
    summary:
      "Long topic prompts shown in the small secondary line are now shortened after four words and displayed with ellipsis.",
    userHighlights: [
      "Secondary topic text remains compact and readable for very long prompts.",
      "Prompt preview now shows first four words followed by `...` when needed.",
      "Full prompt is still available on hover via tooltip.",
    ],
    technicalNotes: [
      "Added shared topic-preview truncation helper in `ReepriseTextCard` and applied it to all secondary topic placements.",
      "No backend changes.",
    ],
  },
  {
    id: "text-card-stable-topic-placement-during-generation",
    timestamp: "2026-03-01T17:11:13+01:00",
    title: "Text cards keep prompt/topic in secondary line during generation",
    summary:
      "While a new text is still generating, the user prompt/topic now stays in the small secondary topic slot instead of temporarily appearing as the main header.",
    userHighlights: [
      "No visual jump from header to secondary line when the generated title appears.",
      "Header now shows a neutral `Generating title…` placeholder until a real title is available.",
      "Topic placement stays stable from first render.",
    ],
    technicalNotes: [
      "Adjusted card title-selection logic to use a generation placeholder when status is pending and no parsed/derived title exists.",
      "Added translation key `text_card_generating_title` for localized placeholder text.",
    ],
  },
  {
    id: "text-card-title-fallback-from-story",
    timestamp: "2026-03-01T17:08:47+01:00",
    title:
      "Text cards now derive a title from story when model title is absent",
    summary:
      "Newly generated texts now still show a strong header title even when the model output does not contain an explicit `title` field.",
    userHighlights: [
      "Cards no longer fall back to showing only the prompt as the main heading for new entries.",
      "The original prompt remains visible as secondary metadata when different from the displayed title.",
      "Title hierarchy remains consistent across standard and podcast cards.",
    ],
    technicalNotes: [
      "Added client-side title derivation from the first meaningful story lines as fallback after explicit title parsing.",
      "Display logic now uses: parsed model title -> derived story title -> prompt topic.",
    ],
  },
  {
    id: "text-card-ai-title-primary-prompt-secondary",
    timestamp: "2026-03-01T17:05:31+01:00",
    title: "Text cards now prioritize AI title over prompt",
    summary:
      "Generated text cards now show the AI-derived title as the main header and render the original user prompt/topic in a smaller secondary line.",
    userHighlights: [
      "Card header now emphasizes the generated title for clearer scanning.",
      "Your original prompt/topic is still visible but visually lighter and less dominant.",
      "Preview text stays visible under the new title hierarchy.",
    ],
    technicalNotes: [
      "Added title extraction from strict JSON, loose JSON-like payloads, and `Title:` lines directly in card rendering.",
      "Applied the same title/prompt hierarchy to both standard and podcast text-card layouts, including the tilted preview card.",
    ],
  },
  {
    id: "generated-text-json-wrapper-cleanup",
    timestamp: "2026-03-01T17:03:55+01:00",
    title: "Generated texts now strip JSON-like wrapper content",
    summary:
      "Texts that arrive in `title/story` JSON-like format are now normalized so only the story body is shown in the text view.",
    userHighlights: [
      'Raw payload fragments like `"title": ... "story": ...` no longer appear in generated text output.',
      "Existing affected texts also render cleaner in text details via frontend normalization.",
      "Title extraction still works when provided by the model.",
    ],
    technicalNotes: [
      "Backend completion now normalizes strict JSON, fenced JSON, and loose JSON-like key/value outputs before persisting `chatgpt_output`.",
      "Text-details stream normalization now extracts `story` from loose JSON-like payloads for resilient rendering.",
    ],
  },
  {
    id: "text-details-more-grammar-toggle-reliability",
    timestamp: "2026-03-01T16:58:35+01:00",
    title: "Text details: More grammar toggle now reacts reliably",
    summary:
      "The More grammar action on text-details vocabulary cards now toggles reliably and is only shown when extra grammar content exists.",
    userHighlights: [
      "The card no longer treats grammar-button clicks as jump-to-text clicks.",
      "More grammar appears only when there is additional grammar to expand.",
      "No-change clicks are avoided for entries that only have basic grammar fields.",
    ],
    technicalNotes: [
      "Added explicit click-control guards in the text-details vocabulary item to prevent parent row jump handling from interfering with control interactions.",
      "Conditionally render expanded grammar block and toggle based on actual advanced grammar availability.",
    ],
  },
  {
    id: "vocabulary-source-sentence-keeps-ending-punctuation",
    timestamp: "2026-03-01T16:55:19+01:00",
    title: "Vocabulary source sentence now keeps full ending punctuation",
    summary:
      "Saved source sentences now include terminal punctuation like `.`, `!`, or `?` when a word is added from text selection.",
    userHighlights: [
      "Source sentence lines now keep their full ending, including punctuation marks.",
      "This avoids clipped sentence context in vocabulary preview and detail cards.",
      "Existing sentence order and styling remain unchanged.",
    ],
    technicalNotes: [
      "Backend `add_vocabulary` sentence extraction now extends to terminal punctuation and trailing closing quote/bracket characters when needed.",
      "Added regression test to ensure `sentence_end_index` selections still persist the full sentence ending.",
    ],
  },
  {
    id: "text-details-vocab-card-layout-aligned",
    timestamp: "2026-03-01T16:52:29+01:00",
    title: "Text details vocabulary card now matches preview layout",
    summary:
      "The vocabulary card on text details now follows the same visual sectioning as the preview card for sentence and grammar content.",
    userHighlights: [
      "Sentence lines are shown in their own card section with the same spacing and typography pattern.",
      "Grammar is shown in a separate card section with consistent More grammar placement and styling.",
      "Existing behavior (prefix removal, source-word bolding, source-first order) remains intact.",
    ],
    technicalNotes: [
      "Restructured `VocabularyItem` in text details to use distinct sentence and grammar containers aligned with preview-card design language.",
      "No backend/API changes.",
    ],
  },
  {
    id: "srs-includes-manual-vocabulary",
    timestamp: "2026-03-01T16:49:02+01:00",
    title: "SRS trainer and stage cards now include manual vocabulary",
    summary:
      "Manual vocabulary is now counted in SRS trainer selection and stage cards, so language-space practice reflects your full vocabulary.",
    userHighlights: [
      "Vocabulary preview no longer says there is nothing to practice when manual words exist.",
      "Stage cards (Due/New/Learning/Reviewing/Mastered) now reflect manual words in the selected language space.",
      "Behavior is consistent with the vocabulary list users already see.",
    ],
    technicalNotes: [
      "Backend recent-vocabulary helper now includes manual-origin words in addition to usage-linked/photo-import words.",
      "Impacts both `/vocabulary/top_fifteen_priority/` and `/vocabulary/stage_counts/` via shared filtering.",
    ],
  },
  {
    id: "preview-sentences-below-self-rating",
    timestamp: "2026-03-01T16:39:40+01:00",
    title: "Preview card sentences moved below self-rating buttons",
    summary:
      "On the vocabulary preview card, source/translated sentence lines now appear under the Yes/Almost/No self-rating controls.",
    userHighlights: [
      "Sentence section is now positioned below the `Did you know the word?` button row.",
      "Sentence content and highlighting behavior remain unchanged.",
      "Grammar section still stays below the self-rating section.",
    ],
    technicalNotes: [
      "Reordered sentence block placement in `VocabularyPreviewCard` without API or schema changes.",
    ],
  },
  {
    id: "preview-vocabulary-pending-translation-refetch",
    timestamp: "2026-03-01T16:37:00+01:00",
    title:
      "Preview vocabulary now refreshes pending translations automatically",
    summary:
      "Newly added words in vocabulary preview no longer get stuck on the temporary generated placeholder.",
    userHighlights: [
      "If a word is still generating translation, preview auto-refreshes until final text arrives.",
      "Preview behavior now aligns with text-details vocabulary updates.",
      "The temporary `currently beeing generated` text no longer lingers after translation completes.",
    ],
    technicalNotes: [
      "Added conditional polling to the preview trainer vocabulary query while any entry is pending or still carries generation-placeholder text.",
      "No backend/API changes.",
    ],
  },
  {
    id: "vocabulary-sentence-left-align-no-source-bg",
    timestamp: "2026-03-01T16:33:23+01:00",
    title:
      "Vocabulary sentences are left-aligned and source background removed",
    summary:
      "Sentence lines are now explicitly left-aligned and the shaded background from the first sentence line has been removed.",
    userHighlights: [
      "Source sentence line no longer uses a gray background block.",
      "Both sentence lines are consistently left-aligned.",
      "Content and ordering of sentence lines stay the same.",
    ],
    technicalNotes: [
      "Updated sentence line classes in preview, trainer, generated-text card, and vocabulary progress (desktop + mobile).",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-source-sentence-word-highlight",
    timestamp: "2026-03-01T16:31:47+01:00",
    title: "Vocabulary word is now bold in source sentence",
    summary:
      "The vocabulary word is now visually emphasized in the source sentence line to improve recognition while reviewing.",
    userHighlights: [
      "In the first sentence line (source sentence), the vocabulary word is bold.",
      "Highlighting appears consistently in preview, trainer, generated-text vocabulary cards, and vocabulary progress.",
      "If source sentence is missing, fallback text remains unchanged.",
    ],
    technicalNotes: [
      "Added a shared sentence-highlighting utility and applied it to source sentence rendering across vocabulary surfaces.",
      "No backend/API changes.",
    ],
  },
  {
    id: "vocabulary-source-sentence-first-order",
    timestamp: "2026-03-01T16:30:05+01:00",
    title: "Vocabulary sentence section now always starts with source sentence",
    summary:
      "Sentence sections now use fixed order again: source sentence (or fallback) first, translated sentence second.",
    userHighlights: [
      "The source sentence slot is always shown first in the section.",
      "If source sentence is missing, `Kein Ausgangssatz verfügbar.` appears first.",
      "Translated sentence still appears as the second line when available.",
    ],
    technicalNotes: [
      "Replaced first-available sentence logic with strict source-first ordering across all vocabulary surfaces.",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-sentence-labels-removed",
    timestamp: "2026-03-01T16:28:39+01:00",
    title: "Vocabulary cards now show sentence text without prefixes",
    summary:
      "Sentence lines no longer display `Ausgangssatz:` or `Übersetzter Satz:` prefixes; only the sentence text is shown.",
    userHighlights: [
      "Cleaner sentence presentation across vocabulary cards and progress views.",
      "Primary and secondary sentence ordering remains unchanged.",
      "Fallback text still appears when no sentence is available.",
    ],
    technicalNotes: [
      "Removed sentence label rendering while keeping sentence-priority logic.",
      "Applied in preview, trainer, generated-text vocabulary card, and vocabulary progress (desktop + mobile).",
    ],
  },
  {
    id: "vocabulary-preview-grammar-below-self-rating",
    timestamp: "2026-03-01T16:26:33+01:00",
    title: "Vocabulary preview: grammar moved below self-rating actions",
    summary:
      "In the preview trainer card, the grammar block now appears below the `Did you know the word?` Yes/Almost/No actions.",
    userHighlights: [
      "Self-rating buttons now appear before grammar details in the preview flow.",
      "Sentence context remains visible above, so reading context still comes first.",
      "Grammar expand/collapse behavior is unchanged; only placement changed.",
    ],
    technicalNotes: [
      "Reordered the preview card layout so the grammar section renders after the self-rating action section.",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-primary-sentence-priority",
    timestamp: "2026-03-01T16:25:10+01:00",
    title: "Vocabulary cards prioritize the primary learning sentence",
    summary:
      "Sentence context now highlights the first available real sentence as primary, so placeholder text does not appear before useful content.",
    userHighlights: [
      "When `Ausgangssatz` is unavailable but a sentence exists in `Übersetzter Satz`, the real sentence is shown first.",
      "The primary sentence is visually emphasized to reflect higher learning importance.",
      "The unavailable placeholder is only shown when no sentence exists at all.",
    ],
    technicalNotes: [
      "Introduced primary/secondary sentence ordering logic in preview, trainer, generated-text vocabulary cards, and vocabulary progress (desktop + mobile).",
      "Applied stronger styling to the primary sentence row and kept existing grammar separation behavior.",
    ],
  },
  {
    id: "vocabulary-translated-sentence-double-label-fix",
    timestamp: "2026-03-01T16:20:50+01:00",
    title: "Vocabulary cards: duplicate translated-sentence label removed",
    summary:
      "Translated sentence values no longer show duplicated labels like `Übersetzter Satz: Satzübersetzung: ...`.",
    userHighlights: [
      "Translated sentence lines now render once with a single label.",
      "Legacy entries that stored `Satzübersetzung:` inside the value are cleaned automatically in the UI.",
      "No data migration required to see the fix.",
    ],
    technicalNotes: [
      "Added normalization that strips known translated-sentence prefixes (`translated sentence`, `übersetzter satz`, `satzübersetzung`) from the value.",
      "Applied to both structured `translation_details.translated_sentence` and legacy newline fallback parsing.",
    ],
  },
  {
    id: "vocabulary-more-grammar-plain-toggle",
    timestamp: "2026-03-01T16:19:58+01:00",
    title: "Vocabulary grammar toggle simplified to plain text",
    summary:
      "The More grammar control now uses a plain text button without chevron or outline styling.",
    userHighlights: [
      "Removed chevron icons from the grammar expand/collapse control.",
      "Removed outline/border styling from the same control for a cleaner look.",
      "Grammar content order and expand/collapse behavior remain unchanged.",
    ],
    technicalNotes: [
      "Dropped `ChevronDownIcon` and `ChevronUpIcon` imports/usages in all vocabulary surfaces.",
      "Adjusted button classes to plain text styling while preserving toggle logic.",
    ],
  },
  {
    id: "vocabulary-more-grammar-chevron",
    timestamp: "2026-03-01T16:18:29+01:00",
    title: "Vocabulary grammar toggle now shows chevron state",
    summary:
      "The More grammar toggle now includes a chevron icon that clearly indicates expand and collapse state.",
    userHighlights: [
      "A down chevron appears with `More grammar`; an up chevron appears with `Less grammar`.",
      "The icon behavior is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
      "Existing field order and grammar content remain unchanged.",
    ],
    technicalNotes: [
      "Added Heroicons `ChevronDownIcon`/`ChevronUpIcon` to all More grammar buttons.",
      "No backend or API changes; this is a frontend affordance update.",
    ],
  },
  {
    id: "vocabulary-more-grammar-collapsed-reset",
    timestamp: "2026-03-01T16:15:26+01:00",
    title: "Vocabulary cards: More grammar reset to collapsed state",
    summary:
      "More grammar now starts collapsed again across vocabulary surfaces while preserving the new sentence-first content order.",
    userHighlights: [
      "Expanded grammar no longer stays open from older saved preference.",
      "Source sentence and translated sentence remain above grammar fields.",
      "You can still open and close advanced grammar manually with the same button.",
    ],
    technicalNotes: [
      "Bumped the localStorage key for the grammar toggle from v1 to v2 to reset persisted expanded state.",
      "No API or schema changes; this is a frontend preference reset only.",
    ],
  },
  {
    id: "vocabulary-sentences-before-grammar",
    timestamp: "2026-03-01T16:13:53+01:00",
    title: "Vocabulary cards: sentence context moved above grammar",
    summary:
      "Vocabulary cards now show source and translated sentence context before grammar fields like part of speech and tense.",
    userHighlights: [
      "You now see `Ausgangssatz` and (if available) `Übersetzter Satz` before the grammar section.",
      "The fallback `Kein Ausgangssatz verfügbar.` remains in place when no source sentence exists.",
      "Order is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
    ],
    technicalNotes: [
      "Reordered sentence and grammar blocks in all vocabulary UI surfaces without changing data contracts.",
      "More grammar toggle behavior is unchanged; only content order was updated.",
    ],
  },
  {
    id: "vocabulary-more-grammar-toggle-bottom",
    timestamp: "2026-03-01T16:11:26+01:00",
    title: "Vocabulary cards: More grammar moved to the bottom",
    summary:
      "The More grammar button is now placed at the bottom of vocabulary detail blocks for a clearer expand/collapse flow.",
    userHighlights: [
      "More grammar/Less grammar now appears at the bottom of vocabulary detail sections instead of near the top.",
      "Expanded grammar details keep the same content and behavior; only placement changed for readability.",
      "The new placement is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
    ],
    technicalNotes: [
      "Reordered the grammar toggle markup to render after grammar and sentence details in all vocabulary surfaces.",
      "No API or data changes; this is a frontend layout adjustment only.",
    ],
  },
  {
    id: "source-sentence-unavailable-copy",
    timestamp: "2026-03-01T16:07:21+01:00",
    title: "Vocabulary source sentence fallback copy refined",
    summary:
      "The source sentence fallback label now uses neutral wording and no longer says “missing”.",
    userHighlights: [
      "Cards and lists now show “No source sentence available.” when no source sentence exists.",
      "The fallback remains clearly labeled as Source sentence for learning context.",
      "Wording is now consistent across preview, trainer, progress, and text-detail vocabulary cards.",
    ],
    technicalNotes: [
      "Renamed the translation key to `vocabulary_source_sentence_unavailable` and updated all frontend references.",
      "No behavioral change besides copy/key naming; fallback display logic remains the same.",
    ],
  },
  {
    id: "vocabulary-progress-grammar-separation",
    timestamp: "2026-03-01T16:01:30+01:00",
    title: "Vocabulary progress: separated grammar layout added",
    summary:
      "The vocabulary progress page now uses the same separated translation/grammar structure and More grammar toggle as other vocabulary surfaces.",
    userHighlights: [
      "Progress table and mobile cards now show translation and grammar as separate readable values.",
      "Core grammar remains visible by default; advanced grammar is shown with `More grammar`.",
      "Source and translated example sentences are shown as separate labeled lines.",
    ],
    technicalNotes: [
      "Vocabulary progress rows now normalize `translation_details` and safely fall back to legacy newline-based translation strings.",
      "Integrated shared localStorage-backed `More grammar` toggle key for consistent behavior across surfaces.",
    ],
  },
  {
    id: "vocabulary-grammar-separated-ui",
    timestamp: "2026-03-01T15:58:08+01:00",
    title: "Vocabulary cards: grammar fields split with a More grammar toggle",
    summary:
      "Vocabulary surfaces now separate translation details into readable fields and add a shared More grammar toggle for advanced details.",
    userHighlights: [
      "Preview card, text-detail vocabulary card, and vocabulary list now show translation and grammar in separate blocks.",
      "Core grammar stays visible by default; advanced grammar opens via `More grammar`.",
      "The toggle preference is remembered locally, so the view stays consistent while learning.",
    ],
    technicalNotes: [
      "Added a shared frontend translation-details normalizer that reads structured payloads and safely falls back to legacy newline translation strings.",
      "Extended `VocabularyDto` with optional `translation_details` and migrated UI rendering in three vocabulary surfaces to separated fields.",
    ],
  },
  {
    id: "generated-text-details-mobile-streaming-simplification",
    timestamp: "2026-07-27T17:51:21+0200",
    title: "Mobile generated text details now use a simpler streaming body until the text is complete",
    summary:
      "While a generated text is still streaming on phones, the details page now uses a stable plain reading body instead of rebuilding the full interactive span tree on every chunk.",
    userHighlights: [
      "On phones, partially generated text no longer visibly rewrites itself while new chunks arrive.",
      "The reading body stays calmer during generation, then switches back to the full interactive article once the text is complete.",
      "Desktop reading behavior stays unchanged.",
    ],
    technicalNotes: [
      "Added a mobile-only simplified streaming branch in `GeneratedTextDetailsTextPane.tsx` that renders incomplete text as one plain paragraph instead of the full interactive span layout.",
      "The full interactive body still renders once the text is complete, so vocabulary capture, previews, and pattern interactions remain unchanged after generation finishes.",
      "Added a focused unit regression for the new mobile/incomplete streaming decision in `GeneratedTextDetailsTextPane.test.tsx`.",
    ],
  },
  {
    id: "deferred-text-details-title-and-streaming",
    timestamp: "2026-07-07T15:43:30+0200",
    title: "Deferred text details: title stays visible and streaming starts on open",
    summary:
      "Daily texts and Rabbit Hole discoveries now keep their visible title when you open details, and generation starts streaming immediately instead of waiting for the full final text.",
    userHighlights: [
      "Opening a pending daily text now shows the same title you already saw on the card.",
      "Pending Rabbit Hole discoveries keep their resolved title across the route transition instead of looking blank.",
      "Text details begin receiving generated content as soon as generation starts, so the page no longer looks frozen for minutes.",
      "If the story has not started yet when details open, the page now shows a compact animated marker instead of an empty body area.",
      "On phones, the main reading text is now slightly larger for easier scanning and calmer reading.",
      "On phones, the Hören control is now one larger tap target, so icon and label open playback together.",
      "Live stream updates no longer scramble visible words while the text is still coming in.",
    ],
    technicalNotes: [
      "Frontend now persists already-resolved pending titles from feed, overview, and Rabbit Hole entry points into the shared generated-text title cache, and text details reuse that cache during pending hydration.",
      "Backend websocket payloads now include a `generation_attempt` discriminator so retries are distinguishable from the active stream instead of looking like unconditional replacements.",
      "The streaming integrator now keeps the visible body monotonic: stale resets are ignored, newer attempts buffer until they catch up, and shorter completed retries do not overwrite longer visible text.",
      "Generated text details now render the body directly from the live websocket chunks again, while keeping the monotonic no-regression protection in the shared stream integrator.",
      "Added a dedicated pending-generation body state in `GeneratedTextDetailsTextPane.tsx` for the pre-stream route-open gap, implemented as a compact animated dot marker, plus focused Playwright coverage that checks the indicator disappears once the first story chunk becomes visible.",
      "In `GeneratedTextDetailsTextPane.tsx`, increased the mobile-only reading text size for both continuous body text and dialogue lines while leaving desktop sizing unchanged.",
      "Updated `AudioTrigger` in `src/routes/app/reeprise/$generatedTextId.tsx` so the icon, label, and surrounding mobile padding form a single button with a larger touch target while desktop spacing stays compact.",
      "Updated `createTextSpans` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so streamed word nodes are keyed by both position and current word content, preventing React from reusing stale word nodes during live text mutations.",
      "Added regression coverage in `barkguide/reeprise/tests.py`, `src/hooks/useStreamingTextIntegrator.test.ts`, `src/utils/resolvePendingGeneratedTextTitle.test.ts`, and focused Playwright route-transition tests, including a browser-level websocket retry/reset sequence that proves visible body text does not blank or truncate mid-stream.",
    ],
  },
  {
    id: "generated-text-enqueue-failure-handling",
    timestamp: "2026-03-01T15:28:57+01:00",
    title:
      "Generate Text: failed queue jobs no longer create silent empty cards",
    summary:
      "When text generation cannot be queued, the request now completes safely and marks the new text as failed instead of leaving a silent empty pending card.",
    userHighlights: [
      "Creating a text no longer crashes when the background queue is temporarily unavailable.",
      "Failed generations are now clearly marked as failed instead of looking stuck.",
      "You can retry immediately instead of guessing whether generation is still running.",
    ],
    technicalNotes: [
      "Backend wraps `complete_generated_text.apply_async(...)` in guarded error handling during `GeneratedText` creation.",
      "On enqueue failure, the text is persisted with `status=FAILED` and a user-visible fallback message instead of returning a server error.",
    ],
  },
  {
    id: "playwright-e2e-make-targets",
    timestamp: "2026-02-27T21:17:53Z",
    title: "E2E tests: make targets for headed/headless runs",
    summary:
      "Playwright runs are now available via make targets, with a headed mode for interactive debugging.",
    userHighlights: [
      "Use `make e2e` to run tests with a visible browser.",
      "Use `make e2e-headless` for fast CI-style runs.",
    ],
    technicalNotes: [
      "Makefile now exposes two Playwright targets for headed and headless execution.",
    ],
  },
  {
    id: "secret-signup-inactive-users",
    timestamp: "2026-02-27T20:14:04Z",
    title: "Secret signup: inactive accounts by default",
    summary:
      "A private signup page lets invited users create accounts that remain inactive until manually approved.",
    userHighlights: [
      "Simple username + password signup at `/secretsignup`.",
      "Clear success screen explains activation within 24 hours.",
      "No login until the account is manually activated.",
    ],
    technicalNotes: [
      "New API endpoint creates users with `is_active = false` and runs Django password validation.",
      "Signup does not log the user in; activation remains manual.",
    ],
  },
  {
    id: "command-bar-smart-menu",
    timestamp: "2026-02-27T19:31:00Z",
    title: "Command bar: smart menu for fast actions",
    summary:
      "The burger menu is now a smart command bar that understands natural-language requests and still keeps the full side nav available.",
    userHighlights: [
      "Type what you need (e.g., change language, switch model, start daily text) and confirm in one tap.",
      "Direct actions first: change settings or open the photo picker without hunting through screens.",
      "Side navigation stays visible below the command bar so nothing is lost.",
    ],
    technicalNotes: [
      "Command bar parses German/English intents and executes settings updates, daily text creation, and profile photo uploads.",
      "Action-specific confirmations replace generic “Apply” for clearer UX.",
      "Mobile uses a full-width sheet with the command bar pinned at the top.",
    ],
  },
  {
    id: "text-details-vocab-focus-highlight",
    timestamp: "2026-02-22T20:57:55Z",
    title: "Text details: highlight selected vocab and jump to words",
    summary:
      "Words chosen for “new text from vocabulary” are now highlighted in the text, and tapping a vocab card scrolls to that word with a short flash.",
    userHighlights: [
      "Selected vocab words are highlighted directly in the generated text, matching the click-to-highlight style.",
      "Tapping a vocab card scrolls the text to that word and briefly highlights it.",
      "Unselecting a word removes its highlight so you can see what will be used next.",
    ],
    technicalNotes: [
      "Detail view now passes selected vocab words to the text renderer for highlight indexing.",
      "Text span rendering adds data attributes per word and supports a timed flash highlight with smooth scroll.",
    ],
  },
  {
    id: "daily-text-on-demand",
    timestamp: "2026-02-01T15:10:00Z",
    title: "Daily text: on-demand card with stable vocab picks",
    summary:
      "A 3pm callout lets you create a daily text per language using your due vocab and last generator settings. The text appears as a normal card; no clutter if you skip it.",
    userHighlights: [
      "At 15:00 you see a daily-text button; tapping creates today’s text for the current language and shows it in the list.",
      "Uses the same word queue you’re struggling with; if nothing is due, you’re told why.",
      "No duplicate cards: if you’ve already created today’s daily text, the callout stays hidden.",
    ],
    technicalNotes: [
      "Backend endpoint `/generated_texts/daily-practice/` now reuses any existing daily text per user/language/day and triggers generation on click.",
      "Frontend callout is 3pm-gated, language-space aware, CSRF-protected, and invalidates the paginated text list after creation.",
      "Translations consolidated into `dailyText*` keys; no inline fallbacks.",
    ],
  },
  {
    id: "knowledge-style-adult-tone",
    timestamp: "2026-02-05T09:30:00Z",
    title: "Knowledge text style: adult, factual output at all levels",
    summary:
      "Knowledge texts now stay factual and adult in tone, with A0–A2 guidance to avoid childlike language while keeping sentences simple.",
    userHighlights: [
      "Pick the new “Knowledge” style to get fact-based texts about your topic, not fiction.",
      "Beginner levels stay simple but avoid babyish wording; short sentences use clear connectors and adult contexts.",
      "Higher levels remain concise and factual, with no speculative or fluffy phrasing.",
    ],
    technicalNotes: [
      "Backend Knowledge prompt adds adult tone and factual constraints, plus A0–A2-specific guidance: short, connected sentences, no cutesy/filler language, everyday adult examples.",
      "No schema changes; frontend already exposes the Knowledge type in the generator.",
    ],
  },
  {
    id: "knowledge-no-main-character",
    timestamp: "2026-02-05T10:00:00Z",
    title: "Knowledge texts: no main character, narratives keep yours",
    summary:
      "Knowledge texts now ignore main characters, while Story/Dialog/Crime/Podcast remember your last protagonist per language. Switching to Knowledge won’t wipe it.",
    userHighlights: [
      "Knowledge text type disables the main-character field and sends none.",
      "When you return to other types, your last chosen character is prefilled (per language).",
      "You can still clear it; Knowledge won’t overwrite your saved choice.",
    ],
    technicalNotes: [
      "Frontend: main-character input is disabled for Knowledge; submissions send an empty value; stored defaults preserve the last non-Knowledge character per language.",
      "Backend: Knowledge texts force an empty main_character before generation.",
    ],
  },
  {
    id: "in-app-language-helper-mvp",
    timestamp: "2026-02-20T11:00:00Z",
    title: "In-app helper: ask, answer, and add vocab",
    summary:
      "A lightweight helper inside text details answers questions and lets you add words and example sentences directly to your vocabulary.",
    userHighlights: [
      "Chat-style helper with a single input; Enter sends on desktop.",
      "Asks for quick confirmation before adding to vocabulary so you see exactly what will be saved.",
      "Answers in the learning language for sentence/translation requests; explanations stay in your mother tongue.",
    ],
    technicalNotes: [
      "Helper uses first-question context from the generated text plus the last 5 clicked words.",
      "Agent responses follow intent rules (sentence, pronunciation, register, grammar).",
      "Uxhulu voice prompt enforces a calm editorial tone with bilingual few-shot examples.",
      "Intent routing keeps sentence/translation replies in the learning language; other answers stay in mother tongue.",
      "Agent can infer the vocab word from the last assistant phrase when the user asks to add it.",
      "New vocab additions go through the standard translation pipeline to preserve multi-line analysis.",
    ],
  },
  {
    id: "trainer-similar-words-spelling",
    timestamp: "2026-02-15T10:26:27+01:00",
    title: "Trainer: similar words by spelling",
    summary:
      "Expanded trainer cards now show spelling-similar words from your current deck, so you can jump to near matches without leaving the session.",
    userHighlights: [
      "See up to 5 near-spelling words in the expanded trainer card under “Ähnliche Wörter”.",
      "Tap a suggestion to switch the active card instantly.",
      "Only uses your current batch and language; no duplicate exact matches.",
    ],
    technicalNotes: [
      "Client-side accent-insensitive Levenshtein on the current deck; dist ≤ 2, normalized score ≥ 0.6; top 5.",
      "UI: single tab “Schreibweise”, scrollable list, reason label “sehr ähnlich geschrieben”.",
    ],
  },
  {
    id: "multi-language-spaces-stable-queues",
    timestamp: "2026-01-30T10:00:00Z",
    title: "Language spaces: per-language defaults and stable queues",
    summary:
      "Each language now has its own generator defaults and trainer queue. Switching spaces or refreshing keeps your current word and form selections intact.",
    userHighlights: [
      "Generate Text remembers level, tense, type, word count, and main character separately per language.",
      "Vocabulary preview shows a stable queue per language—even after switching spaces or refreshing, the same first word stays until you act on it.",
      "Language switcher supports all languages with distinct accents and mobile-friendly swipe/tap navigation.",
    ],
    technicalNotes: [
      "Per-language form defaults stored in localStorage keyed by language; generator hydrates on space change.",
      "Vocabulary preview persists batch snapshots and restores them per language before API data to prevent reorder on refresh.",
      "Language space selection options driven from TEXT_LANGUAGES; mobile pill bar uses per-language accent styling.",
    ],
  },
  {
    id: "trainer-session-size-picker",
    timestamp: "2026-01-20T15:20:00Z",
    title: "Trainer sessions: choose your round size",
    summary:
      "You can now pick how many words are in a round (5–50) from the “continue?” prompt, without changing your due list.",
    userHighlights: [
      "Rounds stay a fixed size; when you finish, tap the menu to choose a different size for the next round.",
      "Words remain stable within a round—no mid-session swaps even if you change the size later.",
      "Settings persist locally, so your preferred round size sticks when you come back.",
    ],
    technicalNotes: [
      "Frontend adds a session-size menu on the bundle-done prompt, stores selection in localStorage, and reinitializes batches with the new size.",
      "Batching continues to use frozen IDs per round; only the slice size changes for subsequent rounds.",
    ],
  },
  {
    id: "trainer-5-word-batches",
    timestamp: "2026-01-20T12:30:00Z",
    title: "Trainer: 5-word sessions with clear breaks",
    summary:
      "The vocabulary trainer now serves due words in fixed sets of five, then asks if you want to continue—no more endless queues without a breakpoint.",
    userHighlights: [
      "You always see a stable set of five due words; new words don’t slip in mid-session.",
      "After five answers, you get a calm “continue?” prompt to start the next five.",
      "If you leave and return, the trainer restarts at the first five due words instead of resuming mid-stream.",
    ],
    technicalNotes: [
      "Frontend freezes a per-session batch (SESSION_SIZE=5), tracks answeredCount, and shows the bundle-done state only after all five are answered.",
      "Batch state resets cleanly on refresh or language filter changes; continuation pulls the next due words without altering the server queue.",
    ],
  },
  {
    id: "podcast-audio-two-voices",
    timestamp: "2026-01-08T15:50:00Z",
    title: "Podcasts: two voices, intro/outro, reliable playback",
    summary:
      "Podcast episodes now sound like real shows: two distinct voices with a short intro/outro, leveled audio, cleaner titles, and playback that works on desktop and mobile.",
    userHighlights: [
      "Two distinct voices alternate naturally with a brief podcast-style welcome and wrap-up.",
      "Titles are generated from the episode content—no generic greetings or quotes.",
      "Podcast cards play reliably via the new speech endpoint on web and mobile.",
    ],
    technicalNotes: [
      "Backend: multi-voice TTS with loudness normalization, silence gaps, intro/outro prompt tweaks, and a dedicated /speech action; serializer returns absolute media URLs.",
      "Frontend: podcast card handles audio more robustly with hidden native controls and error handling.",
      "Title generation: prompt and post-process to produce concise, content-based headlines instead of first-line greetings.",
    ],
  },
  {
    id: "podcast-text-type",
    timestamp: "2025-12-12T09:00:00Z",
    title: "Podcast text type with audio",
    summary:
      "You can now pick “Podcast” when generating content: a 2-speaker, ~1-minute script is written from your topic and runs through text-to-speech so you can listen back.",
    userHighlights: [
      "New “Podcast” option alongside Story/Dialog/Krimi in the generator.",
      "Script uses your topic and supplied words, keeps two voices, and stays concise (~1 minute).",
      "Audio is generated so you can replay the episode from its card.",
    ],
    technicalNotes: [
      "Backend adds PODCAST text type, tailored prompt, and podcast-friendly TTS instructions.",
      "Frontend exposes the new type in the generator; choices and migrations updated.",
    ],
  },
  {
    id: "vocab-progress-fast-counts",
    timestamp: "2025-12-11T22:10:00Z",
    title: "Vocabulary progress shows live counts instantly",
    summary:
      "Stage cards now load their counts directly from the server instead of slowly accruing as you scroll, so you see the real numbers upfront.",
    userHighlights: [
      "Fällig/Lernen/Review counts appear immediately without waiting for list pagination.",
      "Photo-import vocab still has its own tab, but is included in the main counts when you’re on “Alle”.",
    ],
    technicalNotes: [
      "Backend adds a `stage_counts` action aligned to trainer filters (language/origin/max streak/text age) and SRS delays to return counts in one request.",
      "Frontend uses the stage-counts hook for stage badges (client reduction as fallback) and applies the correct language filter even for photo imports.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-4",
    timestamp: "2025-12-11T18:20:00Z",
    title: "Photo vocab import: sharper highlights, zero-drop fallback",
    summary:
      "Image preprocessing now boosts contrast/sharpness for faint marks, and the extractor is forced to return at least as many entries as highlights it counts.",
    userHighlights: [
      "Faint highlights are clearer to the model thanks to contrast and sharpness boosts.",
      "If the model is unsure, it still returns a low-confidence entry rather than skipping a highlight.",
      "One entry per distinct highlight remains—phrases stay intact, separate marks stay separate.",
    ],
    technicalNotes: [
      "Image pipeline adds autocontrast + mild contrast/sharpness enhancement before resize (2000px, LANCZOS).",
      "Prompt now requires the model to verify its entry count >= counted highlights and to over-include with low confidence if uncertain.",
    ],
  },
  {
    id: "photo-vocab-translation-grammar",
    timestamp: "2025-12-11T18:40:00Z",
    title: "Photo vocab translations: enforce grammatical sentences",
    summary:
      "Translations now ask the model to fix agreement/tense and avoid literal calques, with deterministic decoding to reduce odd phrasing.",
    userHighlights: [
      "Sentence translations aim to be grammatical and idiomatic instead of literal word-for-word copies.",
      "Lower randomness reduces quirky outputs when translating highlighted sentences.",
    ],
    technicalNotes: [
      "Translation prompt now explicitly repairs agreement/tense/prepositions while staying faithful to meaning.",
      "Temperature fixed at 0 and max_tokens capped for stable, deterministic outputs.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-3",
    timestamp: "2025-12-11T15:10:00Z",
    title: "Photo vocab import: one entry per highlight, no merges or drops",
    summary:
      "Each distinct highlight now returns exactly one entry in reading order—even multi-word spans stay intact—with added safeguards against accidental omissions.",
    userHighlights: [
      "One entry per mark: if you highlight a phrase, it stays together; separate highlights stay separate.",
      "Zero-drop policy remains: faint or hard-to-read marks still show up with low confidence instead of vanishing.",
      "Higher max tokens in the vision reply avoid truncated lists on dense pages.",
    ],
    technicalNotes: [
      "Highlight mode no longer splits spans; it keeps the marked text as-is with a deterministic token_id and ordered results, and asks the model to count highlights then return at least that many entries.",
      "Prompt explicitly forbids merging distinct highlights and keeps the over-inclusion safety net; vision max_tokens at 2600.",
      "Image preprocessing continues to use high-res autocontrast pipeline for clearer marks.",
    ],
  },
  {
    id: "photo-vocab-highlight-articles",
    timestamp: "2025-12-11T17:05:00Z",
    title: "Photo vocab import: keep articles inside highlights",
    summary:
      "Highlighted phrases are kept verbatim—including articles and prepositions—so marks like “le rétroviseur” stay intact instead of being shortened.",
    userHighlights: [
      "Marked spans are returned exactly as highlighted, preserving articles/determiners when they’re underlined or colored.",
      "Still one entry per distinct highlight with zero-drop behavior on faint marks.",
    ],
    technicalNotes: [
      "Highlight prompt now explicitly preserves the full marked span (no head-noun shortening) while maintaining the count-and-include safeguard.",
      "No changes to image pipeline or token limits; applies only to extraction rules.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-2",
    timestamp: "2025-12-11T12:30:00Z",
    title: "Photo vocab import: zero-drop for highlighted words",
    summary:
      "Highlighted-only imports now insist on returning every marked word, even tiny prepositions or faint strokes, and keep higher-resolution images for the model.",
    userHighlights: [
      "Marked words are no longer skipped: underlined/highlighted spans are split into individual words and included even when contrast is low.",
      "Tiny two-letter words that are marked are kept instead of being filtered out.",
      "Higher-res uploads (up to 2000px) give the model clearer text for consistent extractions.",
    ],
    technicalNotes: [
      "Prompt now forbids omission, requires per-word outputs for multi-word highlights, includes short words, and removes implicit count limits.",
      "Max tokens for the vision response raised to avoid truncation on longer sentences; temp remains 0 for determinism.",
      "Image preprocessing keeps aspect ratio and bumps max dimension to 2000px with LANCZOS to preserve fine annotations.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage",
    timestamp: "2025-12-11T08:00:00Z",
    title: "Photo vocab import keeps every highlighted word",
    summary:
      "Uploads that contain highlighted or underlined words now return the full set reliably, even when the handwriting is faint or the lighting is uneven.",
    userHighlights: [
      "Highlighted-only imports no longer drop random words; the extractor returns every marked token instead of a partial list.",
      "Faint or low-contrast marks are still included with a low-confidence flag instead of being skipped.",
      "Keeping the original aspect ratio and higher resolution preserves tiny annotations and pen underlines.",
    ],
    technicalNotes: [
      "Vision prompt now explicitly forbids skipping marked words and asks for best-effort guesses with low confidence instead of omission.",
      "Image preprocessing retains aspect ratio at up to 1400px using LANCZOS downscaling, removes destructive in-place overwrite, and encodes JPEG at higher quality.",
      "These changes make repeated uploads deterministic and keep all marked tokens available for selection.",
    ],
  },
  {
    id: "struggle-words-practice-texts",
    timestamp: "2025-12-07T09:10:00Z",
    title: "Practice texts for tricky words",
    summary:
      "Struggling vocabulary now triggers a dedicated practice text: we pick recent hard words, generate a short title and story, highlight the words in context, and surface a distinct card to revisit it.",
    userHighlights: [
      "A new banner offers an “Übungstext” when you have recent struggling words; after generation it hides for a day.",
      "The practice text card appears in your main list with a subtle badge, so you can always return to it.",
      "On the text detail page, the exact struggling words used are listed and highlighted in the text.",
    ],
    technicalNotes: [
      "Backend: `GeneratedText` stores `struggle_words`; generation pulls up to 8 low-streak vocab (last 30 days) in the selected language, injects them into the prompt, and derives a short title from the body.",
      "Async generation no longer hits the ORM; all struggle vocab is gathered synchronously, fixing SynchronousOnlyOperation errors and avoiding empty texts.",
      "Frontend: practice-text banner fetches struggle words daily and hides for 24h after use; detail view highlights the stored struggle words (filtered by text language); main cards show an “Übungstext” badge without special borders; form selections are remembered for the next practice text.",
    ],
  },
  {
    id: "photo-vocab-auto-translation",
    timestamp: "2025-12-05T13:00:00Z",
    title: "Photo vocab import: smarter translations & tags",
    summary:
      "Photo imports now auto-route translations per token, show the learning-language word with the mother-tongue translation, and land you on the photo tab after import.",
    userHighlights: [
      "Words extracted from photos show the learning-language term in “Wort” with the mother-tongue translation (and grammar info) in “Übersetzung”.",
      "Language badges reflect the actual source (e.g., French stays French); mixed-language imports can still be previewed before import.",
      "After finishing an import, the vocab list opens directly on the “Photo” tab.",
    ],
    technicalNotes: [
      "Finalize/preview auto-select targets per token (mother tongue vs. learning language), normalize language codes, and store target vs. source consistently.",
      "Vocabulary DTOs expose translation_language; the UI filters/badges via word_language for correct tagging.",
      "Success link carries ?tab=photo so the vocab list opens on the photo-import view.",
    ],
  },
  {
    id: "photo-vocabulary-import-ui",
    timestamp: "2024-05-28T12:00:00Z",
    title: "Photo vocabulary import (UX workflow)",
    summary:
      "Uploading a picture now launches a guided import flow: you choose whether to write a story or extract vocabulary, watch the AI read the photo, and cherry-pick the detected words before they land in a dedicated list.",
    userHighlights: [
      "Right after selecting a photo you decide between “Generate text” and “Import vocabulary,” so stories are never triggered by accident.",
      "The import view shows a real-time status card, each detected word with its sentence, and tools to select all, clear, or skip entries.",
      "Imported words appear in the new “Photo imports” tab inside the vocabulary list, making them easy to revisit.",
    ],
    technicalNotes: [
      "GenerateTextCard now polls the `/photo_vocabulary_imports/` endpoints, handles intent modals, status states, and lets users finalize token IDs.",
      "Result payloads are normalized client-side (deterministic token IDs, per-word sentence/language) before batching a finalize call.",
      "The vocabulary list API/UI supports an `origin=PHOTO_IMPORT` filter, exposed via a tab that reuses the infinite-query pipeline.",
    ],
  },
  {
    id: "photo-vocabulary-import-backend",
    timestamp: "2024-05-28T10:00:00Z",
    title: "Photo vocabulary import (backend groundwork)",
    summary:
      "Image uploads now branch into a dedicated vocabulary-import pipeline: the server extracts highlighted words or entire lists, tags the language, and serves them in a reviewable batch.",
    userHighlights: [
      "Uploading a picture asks whether you want to generate a story or import vocabulary, so no accidental text generations.",
      "Words imported from photos carry their original sentence context and appear in a dedicated list, making it easier to study book excerpts or word lists.",
      "Failed imports surface clear status/error messages so you know when the AI could not read a page.",
    ],
    technicalNotes: [
      "Backend adds `PhotoVocabularyImport` with Celery processing: OpenAI vision parses the photo, outputs JSON with word, sentence, language, and deterministic `token_id`.",
      "New REST endpoints let the client poll import status and finalize selected words; vocabulary entries now track `origin` and keep a pointer to the import batch.",
      "Translations work without `VocabularyUsage` thanks to a fallback that feeds the stored sentence directly into the translation task.",
    ],
  },
  {
    id: "spaced-repetition-response-time",
    timestamp: "2024-05-27T14:30:00Z",
    title: "Spaced Repetition respects thinking time",
    summary:
      "The trainer now records how long you think about a word before revealing it. Fast answers are rewarded; hesitant ones keep the word in a tougher state.",
    userHighlights: [
      "Only the time before the first tap is captured; you can take your time to pick Ja/Fast/Nein once the answer is visible.",
      "Ja: quick or steady answers advance the streak, slow clicks keep the word at the current difficulty.",
      "Fast and Nein push the word back sooner if you hesitated for a long time, focusing the queue on shaky vocabulary.",
    ],
    technicalNotes: [
      "Frontend: each card starts `responseTimer` on mount, locks it on the first tap, and sends `response_time_ms` with every Ja/Fast/Nein mutation.",
      "Backend: the DRF actions bucketize latency (≤2.5s, 2.5–8s, ≥8s) and mutate `winning_streak`/`last_tested` via helper functions.",
      "Effect: slow Ja responses stop incrementing the streak; slow Nein responses backdate `last_tested` more aggressively; fast Fast clicks leave streak untouched.",
    ],
  },
];

export const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(iso));

export const formatReleaseNumber = (entryNumber: number) =>
  entryNumber.toString().padStart(3, "0");

export const normalizeHeadlineText = (text: string) =>
  text.replace(/\b[A-Z]{2,}\b/g, (match) => {
    const [first, ...rest] = match;
    return `${first}${rest.join("").toLowerCase()}`;
  });

function ReleaseCard({
  translate,
  ...entry
}: ReleaseCardEntry & {
  translate: (key: string) => string;
}) {
  const releaseNumber = formatReleaseNumber(entry.entryNumber);
  return (
    <div
      className="rounded-[2rem] border border-slate-200 bg-white/95"
      data-testid="release-card"
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          <span>{formatDateTime(entry.timestamp)}</span>
          <span
            className="h-1 w-1 rounded-full bg-slate-300"
            aria-hidden="true"
          />
          <span data-testid="release-entry-number">
            {`${translate("product_updates_entry_label")} ${releaseNumber}`}
          </span>
        </div>
        <h3 className="mt-4 text-[1.35rem] font-bold leading-tight tracking-tight text-slate-950">
          {normalizeHeadlineText(entry.title)}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {normalizeHeadlineText(entry.summary)}
        </p>
        <div className="mt-5 grid gap-4">
          <ul className="space-y-1 text-sm text-slate-600 list-disc pl-5">
            {entry.userHighlights.map((bullet) => (
              <li key={bullet}>{normalizeHeadlineText(bullet)}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SpacedRepetitionLogPage() {
  const { translate } = useTranslation();
  const latestTimestamp =
    releaseEntries[0]?.timestamp ?? new Date().toISOString();
  const latestEntryNumber = releaseEntries.length;
  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-4xl space-y-10">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("product_updates_eyebrow")}
          </p>
          <div className="grid gap-4 rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/60 p-5 sm:grid-cols-[minmax(0,1fr)_11rem] sm:p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                  {translate("product_updates_title")}
                </h1>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
                  {translate("product_updates_description")}
                </p>
              </div>
              <div className="flex flex-wrap gap-5 text-sm text-slate-500">
                <p>{translate("product_updates_status_active")}</p>
                <p>
                  {`${translate("product_updates_last_update_label")} ${formatDateTime(
                    latestTimestamp,
                  )}`}
                </p>
              </div>
            </div>
            <div className="self-start rounded-[1.5rem] border border-slate-300 bg-white/90 p-4">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-slate-500">
                {translate("product_updates_current_label")}
              </p>
              <p
                className="mt-2 text-2xl font-semibold leading-none tracking-tight text-slate-900"
                data-testid="latest-release-number"
              >
                {`#${formatReleaseNumber(latestEntryNumber)}`}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {translate("product_updates_current_hint")}
              </p>
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            <h2 className="text-xl font-semibold text-slate-900">
              {translate("product_updates_section_title")}
            </h2>
          </div>
          <p className="text-sm text-slate-600">
            {translate("product_updates_section_description")}
          </p>
          <div className="space-y-4">
            {releaseEntries.map((entry, index) => (
              <ReleaseCard
                key={entry.id}
                {...entry}
                entryNumber={releaseEntries.length - index}
                translate={translate}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/product_logbook")({
  component: SpacedRepetitionLogPage,
});
